import MemberHeader from './MemberHeader';
import MemberBoard from './MemberBoard';
import DashboardScoreboard from './DashboardScoreboard';
import OwnerRuntimeVersion from './OwnerRuntimeVersion';
import TodaysPicksPage from './TodaysPicksPage';
import PublicLanding from './PublicLanding';
import {browserSupabaseUrl} from './stagingEnvironment';
import DrawingResultsPage from './DrawingResultsPage';
import MarketingShareCenter from './MarketingShareCenter';
import React, { useState, useEffect, useRef } from "react";
import LaunchDashboard from "./LaunchDashboard";
import FloridaPilotViewer from "./FloridaPilotViewer";
import FreeCustomerExperience from "./FreeCustomerExperience";
import QuickPicksTab from "./QuickPicksTab";
import CheckoutActivation from "./CheckoutActivation";
import { createPortal } from "react-dom";
import { NavLink, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import { BUILD_INFO } from "./buildVersion";
const { stableStrategyValue, strategyOutcomeSnapshot, strategyFingerprint } = require("./strategyFingerprint");
const { calculatePick2EntriesDraw } = require("./pick2Scoring");
const { calculatePick3EntriesDraw } = require("./pick3Scoring");
const { calculatePick5EntriesDraw } = require("./pick5Scoring");
const { renderCanonicalCardA } = require("./shareCardCanonical");
const { renderCanonicalCardB } = require("./shareResultCardCanonical");
const SUPABASE_URL = browserSupabaseUrl(process.env);
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const LEGACY_ONBOARDING_ENABLED = false;
const FREE_MAX_STRATEGIES = 3;
const STRATEGY_MAX_PLAYS = 2500;
const USER_PLAY_INSERT_CHUNK_SIZE = 500;
const BALL_COUNT = 5;
const MAX_NUM = 36;
const DEFAULT_SUBSCRIPTION={
  plan_tier:"free",
  subscription_status:"free",
  billing_status:"free",
  is_pro:false,
  is_premium:false,
  is_pool_organizer:false,
  trial_active:false,
  trial_ends_at:null,
  current_period_end:null,
  cancel_at_period_end:false,
  free_pick_game:null,
  referral_code:null,
  referral_slug:null,
  referral_slug_updated_at:null,
};
const PLAN_LEVELS={free:0,pro:1,premium:2,pool_organizer:3};
const PLAN_COPY={
  free:{label:"Free",price:"$0",short:"Try Today’s Picks"},
  pro:{label:"Pro",price:"$9.99",short:"Unlock Today’s Picks"},
  premium:{label:"Premium",price:"$19.99",short:"Coming Soon"},
  pool_organizer:{label:"Pool Organizer",price:"$29.99",short:"Coming Soon"},
};
function normalizePlanTier(plan){
  const key=String(plan||"free").toLowerCase().replace(/[\s-]+/g,"_");
  if(key==="pool"||key==="pool_manager"||key==="pool_organizer")return"pool_organizer";
  if(key==="premium")return"premium";
  if(key==="pro")return"pro";
  return"free";
}
function planRank(plan){return PLAN_LEVELS[normalizePlanTier(plan)]||0;}
function planAtLeast(plan,minimum){return planRank(plan)>=planRank(minimum);}
function subscriptionTier(subscription){
  const tier=normalizePlanTier(subscription?.plan_tier);
  return subscription?.trial_active&&tier==="free"?"pro":tier;
}
function subscriptionHas(subscription,minimum){
  return planAtLeast(subscriptionTier(subscription),minimum);
}
function normalizeUserPlayNumbers(numbers){
  return Array.isArray(numbers)?numbers.map(n=>Number(n)):[];
}
function userPlayInsertKey(row,includeStrategy=false){
  const nums=normalizeUserPlayNumbers(row?.numbers);
  const strategy=includeStrategy?(":"+(row?.strategy||"")):"";
  return(row?.game||"fantasy5")+strategy+":"+nums.join(",");
}
async function selectUserPlayRowsPaged(queryBuilder,pageSize=1000,maxRows=100000){
  const all=[];
  for(let from=0;from<maxRows;from+=pageSize){
    const to=from+pageSize-1;
    const{data,error}=await queryBuilder().range(from,to);
    if(error)return{data:all,error};
    all.push(...(data||[]));
    if(!data||data.length<pageSize)break;
  }
  return{data:all,error:null};
}
async function insertUserPlayRows(rows){
  const normalized=(rows||[])
    .filter(row=>row&&row.user_id&&Array.isArray(row.numbers)&&row.numbers.length)
    .map(row=>({...row,numbers:normalizeUserPlayNumbers(row.numbers)}));
  const saved=[];
  const skippedExisting=[];
  const reusedExisting=[];
  if(!normalized.length)return{data:saved,error:null,meta:{requested:0,inserted:0,reusedExisting,skippedExisting}};
  const userId=normalized[0].user_id;
  const games=[...new Set(normalized.map(row=>row.game||"fantasy5"))];
  const existingLookup=await selectUserPlayRowsPaged(()=>supabase.from("user_plays").select("*").eq("user_id",userId).in("game",games));
  if(existingLookup.error)return{data:saved,error:existingLookup.error,meta:{requested:normalized.length,inserted:0,reusedExisting,skippedExisting}};
  const existingByStrategy=new Map();
  (existingLookup.data||[]).forEach(row=>{
    if(!Array.isArray(row.numbers))return;
    const clean={...row,numbers:normalizeUserPlayNumbers(row.numbers)};
    existingByStrategy.set(userPlayInsertKey(clean,true),clean);
  });
  const insertable=[];
  const incomingByStrategy=new Set();
  normalized.forEach(row=>{
    const rowKey=userPlayInsertKey(row,true);
    if(incomingByStrategy.has(rowKey)){
      skippedExisting.push({game:row.game||"fantasy5",numbers:row.numbers,strategy:row.strategy,scope:"same_strategy_submission"});
      return;
    }
    incomingByStrategy.add(rowKey);
    const sameStrategy=existingByStrategy.get(userPlayInsertKey(row,true));
    if(sameStrategy){
      saved.push(sameStrategy);
      reusedExisting.push({game:row.game||"fantasy5",numbers:row.numbers,strategy:row.strategy});
      return;
    }
    insertable.push(row);
  });
  for(let i=0;i<insertable.length;i+=USER_PLAY_INSERT_CHUNK_SIZE){
    const chunk=insertable.slice(i,i+USER_PLAY_INSERT_CHUNK_SIZE);
    const{data,error}=await supabase.from("user_plays").insert(chunk).select();
    if(error)return{data:saved,error};
    saved.push(...(data||[]));
  }
  return{data:saved,error:null,meta:{requested:normalized.length,inserted:saved.length-reusedExisting.length,reusedExisting,skippedExisting}};
}
const APP_VERSION_LABEL="LottoTracker Launch";
const APP_BUILD_VERSION=BUILD_INFO?.version||"local-dev";
if(typeof window!=="undefined")window.__LOTTO_TRACKER_BUILD_VERSION__=APP_BUILD_VERSION;
const DATABASE_VERSION="1";
const SUPPORT_EMAIL="support@lottotracker.app";
const TRUST_COPY="Track your numbers. LottoTracker does not sell tickets, handle money, or distribute winnings.";
const LIVE_ROI_TRUST_COPY="LottoTracker calculates Live ROI from strategies you mark as Live Tracking. Only mark a strategy Live if you actually played it.";
const TRACKING_MODES={live:"live",watch:"watch"};
const BETA_CAPACITY_MODE_KEY="lt:betaCapacityMode";
const DASHBOARD_DRAW_LIMIT=60;
const FULL_DRAW_LIMIT=12000;
const WHAT_IF_DRAW_LIMIT=3000;
const DRAW_STATUS_TICK_MS=15000;
const DRAW_CACHE_TTL_MS=30000;
const OFFICIAL_POLL_MAX_ATTEMPTS=12;
const OFFICIAL_POLL_INTERVAL_MS=45000;
const SUMMARY_PERIOD_BY_WINDOW={
  "last_night":"last_night",
  "7":"7d",
  "30":"30d",
  "90":"90d",
  "this_year":"this_year",
  "last_year":"last_year",
};
const COMING_SOON_PLAN_IDS=new Set(["premium","pool_organizer"]);
// Feature flag: hide the ANCHORS tab from nav and the anchor3/anchor4 strategy
// cards from the Scoreboard for the initial public launch. The /anchors route
// still resolves so we can deep-link for internal testing, and existing
// user_plays rows with strategy='anchor3'/'anchor4' are preserved untouched --
// flip back to true to restore full visibility without a code change beyond
// this constant.
const SHOW_ANCHORS = false;
const GAME_CATALOG=[
  {id:"fantasy5",label:"Fantasy 5",status:"Launch Ready",roadmap:"launch",upcoming:"Midday and Evening draws",supportsStrategies:true},
  {id:"pick2",label:"Pick 2",status:"Launch Ready",roadmap:"launch",upcoming:"Daily draws",supportsStrategies:true},
  {id:"pick3",label:"Pick 3",status:"Launch Ready",roadmap:"launch",upcoming:"Daily draws",supportsStrategies:true},
  {id:"pick4",label:"Pick 4",status:"Launch Ready",roadmap:"launch",upcoming:"Daily draws",supportsStrategies:true},
  {id:"pick5",label:"Pick 5",status:"Launch Ready",roadmap:"launch",upcoming:"Daily draws",supportsStrategies:true},
  {id:"floridalotto",label:"Florida Lotto",status:"Coming Soon",roadmap:"soon",upcoming:"Wednesday and Saturday draws",supportsStrategies:false},
  {id:"jackpottripleplay",label:"Jackpot Triple Play",status:"Coming Soon",roadmap:"soon",upcoming:"Tuesday and Friday draws",supportsStrategies:false},
  {id:"powerball",label:"Powerball",status:"Coming Soon",roadmap:"soon",upcoming:"Monday, Wednesday, and Saturday draws",supportsStrategies:false},
  {id:"megamillions",label:"Mega Millions",status:"Coming Soon",roadmap:"soon",upcoming:"Tuesday and Friday draws",supportsStrategies:false},
  {id:"cashpop",label:"Cash Pop",status:"Research Later",roadmap:"research",upcoming:"Research later",supportsStrategies:false},
];
const GAME_ROADMAP_GROUPS=[
  {id:"launch",label:"Launch Ready",color:"#008577",note:"Trusted scoring rules and locked strategy tracking are launch-ready."},
  {id:"soon",label:"Coming Soon",color:"#2E6DA4",note:"Listed on the roadmap, but public scoring trust is not active yet."},
  {id:"research",label:"Research Later",color:"#c8860a",note:"Needs additional validation before active support."},
];
const ARCHIVED_GAME_LABELS={
  cash4life:"Cash4Life (Discontinued)",
};
const DEFAULT_MY_GAMES=["fantasy5"];
const PICK_GAME_IDS=["pick2","pick3","pick4","pick5"];
const FREE_DEFAULT_GAMES=["fantasy5"];
function gameLabel(id){return GAME_CATALOG.find(g=>g.id===id)?.label||ARCHIVED_GAME_LABELS[id]||id;}
function profileFirstName(user){
  const meta=user?.user_metadata||{};
  const raw=meta.first_name||meta.firstName||meta.given_name||meta.display_name||meta.full_name||meta.name||"";
  const first=String(raw||"").trim().split(/\s+/)[0]||"";
  const clean=first.replace(/[^a-z0-9'-]/gi,"").slice(0,32);
  return clean&&clean.indexOf("@")<0?clean:"";
}
function firstStrategyDefaultName(user,gameId){
  const first=profileFirstName(user);
  const owner=first?`${first}${/s$/i.test(first)?"'":"'s"}`:"My";
  return `${owner} ${gameLabel(gameId||"fantasy5")}`;
}
function gameRoadmapMeta(id){
  const game=GAME_CATALOG.find(g=>g.id===id);
  return GAME_ROADMAP_GROUPS.find(group=>group.id===(game?.roadmap||id))||GAME_ROADMAP_GROUPS[0];
}
function launchReadyGames(){return GAME_CATALOG.filter(g=>g.roadmap==="launch");}
function rowGame(row){return row?.game||"fantasy5";}
function normalizeGames(ids=[]){
  const valid=new Set(launchReadyGames().map(g=>g.id));
  const out=[];
  ids.forEach(id=>{if(valid.has(id)&&!out.includes(id))out.push(id);});
  return out.length?out:DEFAULT_MY_GAMES;
}
function freePickGame(ids=[]){
  return normalizeGames(ids).find(id=>PICK_GAME_IDS.includes(id))||null;
}
function freeGamesForPick(pickGame){
  return["fantasy5",...(PICK_GAME_IDS.includes(pickGame)?[pickGame]:[])];
}
function normalizeFreeGames(ids=[]){
  return freeGamesForPick(freePickGame(ids));
}
function enforceGameAccess(ids,planTier="free",trialActive=false){
  const normalized=normalizeGames(ids);
  if(planAtLeast(planTier,"pro")||trialActive)return normalized;
  return normalizeFreeGames(normalized);
}
function gameAllowedForPlan(gameId,myGames=[],planTier="free",trialActive=false){
  if(gameId==="fantasy5")return true;
  if(planAtLeast(planTier,"pro")||trialActive)return launchReadyGames().some(game=>game.id===gameId);
  const pick=freePickGame(myGames);
  return PICK_GAME_IDS.includes(gameId)&&(!pick||pick===gameId);
}
function savedStrategyIdsFromState(strategyTrust,plays=[]){
  return [...new Set([
    ...Object.keys(normalizeStrategyTrust(strategyTrust).records||{}),
    ...strategyIdsFromPlays(plays),
  ].filter(id=>id&&id!=="manual"))];
}
const DAILY=[0,1,2,3,4,5,6];
const NEXT_DRAW_SCHEDULE={
  fantasy5:[
    {label:"Midday",days:DAILY,draw:"13:05",close:"13:00"},
    {label:"Evening",days:DAILY,draw:"23:15",close:"23:00"},
  ],
  pick2:[
    {label:"Midday",days:DAILY,draw:"13:30",close:"13:20"},
    {label:"Evening",days:DAILY,draw:"21:45",close:"21:35"},
  ],
  pick3:[
    {label:"Midday",days:DAILY,draw:"13:30",close:"13:20"},
    {label:"Evening",days:DAILY,draw:"21:45",close:"21:35"},
  ],
  pick4:[
    {label:"Midday",days:DAILY,draw:"13:30",close:"13:20"},
    {label:"Evening",days:DAILY,draw:"21:45",close:"21:35"},
  ],
  pick5:[
    {label:"Midday",days:DAILY,draw:"13:30",close:"13:20"},
    {label:"Evening",days:DAILY,draw:"21:45",close:"21:35"},
  ],
  floridalotto:[
    {label:"Night",days:[3,6],draw:"23:15",close:"22:40"},
  ],
  jackpottripleplay:[
    {label:"Night",days:[2,5],draw:"23:15",close:"22:40"},
  ],
  powerball:[
    {label:"Night",days:[1,3,6],draw:"22:59",close:"22:00"},
  ],
  megamillions:[
    {label:"Night",days:[2,5],draw:"23:00",close:"22:00"},
  ],
};
const SALES_SOON_MS=60*60*1000;
const RESULTS_READY_MS=2*60*60*1000;
const DRAWING_IN_PROGRESS_MS=10*60*1000;
const EASTERN_TIME_ZONE="America/New_York";
const DAY_MS=24*60*60*1000;
function easternDateParts(d=new Date()){
  const date=d instanceof Date?d:new Date(d);
  return new Intl.DateTimeFormat("en-US",{
    timeZone:EASTERN_TIME_ZONE,
    year:"numeric",
    month:"2-digit",
    day:"2-digit",
  }).formatToParts(date).reduce((acc,part)=>{
    if(part.type!=="literal")acc[part.type]=part.value;
    return acc;
  },{});
}
function easternIsoDate(d=new Date()){
  const parts=easternDateParts(d);
  return parts.year+"-"+parts.month+"-"+parts.day;
}
function addDaysIsoDate(isoDate,offset=0){
  const m=String(isoDate||"").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if(!m)return easternIsoDate(new Date(Date.now()+Number(offset||0)*DAY_MS));
  const utc=Date.UTC(Number(m[1]),Number(m[2])-1,Number(m[3]))+Number(offset||0)*DAY_MS;
  return new Date(utc).toISOString().slice(0,10);
}
function easternWeekday(isoDate){
  const m=String(isoDate||"").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if(!m)return 0;
  return new Date(Date.UTC(Number(m[1]),Number(m[2])-1,Number(m[3]))).getUTCDay();
}
function easternOffsetForDate(isoDate){
  const month=Number(String(isoDate||"").slice(5,7));
  return month>=3&&month<=11?"-04:00":"-05:00";
}
function easternDateTime(isoDate,hhmm){
  return new Date(String(isoDate||easternIsoDate())+"T"+String(hhmm||"00:00")+":00"+easternOffsetForDate(isoDate));
}
function timeOnDate(base,hhmm){
  return easternDateTime(easternIsoDate(base),hhmm);
}
function formatDrawMoment(d,now){
  const nowIso=easternIsoDate(now);
  const targetIso=easternIsoDate(d);
  const diff=Math.round((Date.parse(targetIso+"T00:00:00Z")-Date.parse(nowIso+"T00:00:00Z"))/DAY_MS);
  const day=diff===0?"Today":diff===1?"Tomorrow":d.toLocaleDateString("en-US",{timeZone:EASTERN_TIME_ZONE,weekday:"short",month:"short",day:"numeric"});
  return day+", "+d.toLocaleTimeString("en-US",{timeZone:EASTERN_TIME_ZONE,hour:"numeric",minute:"2-digit"});
}
function formatCountdown(ms){
  if(ms<=0)return "Now";
  const totalM=Math.ceil(ms/60000);
  const days=Math.floor(totalM/(60*24));
  const hours=Math.floor((totalM-days*60*24)/60);
  const mins=totalM%60;
  if(days>0)return days+"d "+hours+"h";
  if(hours>0)return hours+"h "+mins+"m";
  return mins+"m";
}
function drawStatusColor(status){
  if(status==="Results Ready")return "#D4AF37";
  if(status==="Waiting for official results")return "#005bbb";
  if(status==="Official results delayed")return "#c8860a";
  if(status==="Drawing In Progress")return "#005bbb";
  if(status==="Sales Closed")return "#c8102e";
  if(status==="Sales Closing Soon")return "#c8860a";
  if(status==="Archived")return "#5d6675";
  return "#008577";
}
function localIsoDate(d){
  return easternIsoDate(d);
}
function drawTimeKey(value){
  const v=String(value||"").toLowerCase();
  if(v.includes("mid"))return"midday";
  if(v.includes("eve")||v.includes("night"))return"evening";
  return v;
}
function findOfficialDraw(officialDraws,game,drawDate,drawTime){
  return(officialDraws||[]).find(d=>
    rowGame(d)===game&&
    d.draw_date===drawDate&&
    drawTimeKey(d.draw_time)===drawTime&&
    (d.source==="official"||d.official_result_status==="official")&&
    Array.isArray(d.numbers)&&d.numbers.length>0
  )||null;
}
function buildDrawEntry(game,slot,drawAt,closeAt,now,officialDraws=[]){
  const drawingDoneAt=new Date(drawAt.getTime()+DRAWING_IN_PROGRESS_MS);
  const archiveAt=new Date(drawAt.getTime()+RESULTS_READY_MS);
  const delayedAt=new Date(drawAt.getTime()+15*60*1000);
  const drawDate=localIsoDate(drawAt);
  const drawKey=drawTimeKey(slot.label);
  const officialDraw=findOfficialDraw(officialDraws,game,drawDate,drawKey);
  let status="Upcoming";
  let countdown=formatCountdown(drawAt-now);
  let countdownTarget=drawAt;
  if(officialDraw&&now>=archiveAt){
    status="Archived";
    countdown="Archived";
    countdownTarget=archiveAt;
  }else if(officialDraw&&now>=drawingDoneAt){
    status="Results Ready";
    countdown="Results ready";
    countdownTarget=archiveAt;
  }else if(now>=delayedAt){
    status="Official results delayed";
    countdown="Delayed";
    countdownTarget=delayedAt;
  }else if(now>=drawingDoneAt){
    status="Waiting for official results";
    countdown="Waiting";
    countdownTarget=delayedAt;
  }else if(now>=drawAt){
    status="Drawing In Progress";
    countdown="Drawing now";
    countdownTarget=drawingDoneAt;
  }else if(now>=closeAt){
    status="Sales Closed";
    countdown=formatCountdown(drawAt-now);
    countdownTarget=drawAt;
  }else if(closeAt-now<=SALES_SOON_MS){
    status="Sales Closing Soon";
    countdown=formatCountdown(closeAt-now);
    countdownTarget=closeAt;
  }
  return{
    game,
    gameLabel:gameLabel(game),
    drawLabel:slot.label,
    draw_date:drawDate,
    draw_time:drawKey,
    drawAt,
    closeAt,
    archiveAt,
    delayedAt,
    officialDraw,
    drawTime:formatDrawMoment(drawAt,now),
    closeTime:formatDrawMoment(closeAt,now),
    countdown,
    status,
    statusColor:drawStatusColor(status),
    sortAt:drawAt.getTime(),
    countdownMs:Math.max(0,countdownTarget-now),
    isArchived:status==="Archived",
  };
}
function getDrawStatusService(gameIds,now=new Date(),officialDraws=[]){
  const draws=[];
  normalizeGames(gameIds).forEach(game=>{
    const schedules=NEXT_DRAW_SCHEDULE[game]||[];
    const nowIso=easternIsoDate(now);
    for(let offset=-14;offset<8;offset++){
      const drawDate=addDaysIsoDate(nowIso,offset);
      for(const slot of schedules){
        if(!slot.days.includes(easternWeekday(drawDate)))continue;
        draws.push(buildDrawEntry(game,slot,easternDateTime(drawDate,slot.draw),easternDateTime(drawDate,slot.close),now,officialDraws));
      }
    }
  });
  const entries=draws.filter(d=>!d.isArchived).sort((a,b)=>a.sortAt-b.sortAt);
  const archivedEntries=draws.filter(d=>d.isArchived).sort((a,b)=>b.sortAt-a.sortAt);
  const next=entries.find(d=>d.sortAt>=now.getTime())
    ||entries.find(d=>d.status==="Results Ready")
    ||entries[0]
    ||null;
  const latestArchived=archivedEntries[0]||null;
  return{
    now,
    entries,
    next,
    archivedEntries,
    latestArchived,
    status:next?.status||latestArchived?.status||"Archived",
    statusColor:next?.statusColor||latestArchived?.statusColor||drawStatusColor("Archived"),
  };
}
function useCentralDrawStatus(gameIds,officialDraws=[]){
  const[now,setNow]=useState(()=>new Date());
  useEffect(()=>{
    const id=setInterval(()=>setNow(new Date()),DRAW_STATUS_TICK_MS);
    return()=>clearInterval(id);
  },[]);
  return getDrawStatusService(gameIds,now,officialDraws);
}
function getNextDrawEntries(gameIds,now=new Date()){
  return getDrawStatusService(gameIds,now).entries;
}
function getDashboardSchedule(gameIds,now=new Date()){
  const status=getDrawStatusService(gameIds,now);
  return normalizeGames(gameIds).map(game=>{
    const gameEntries=status.entries.filter(e=>e.game===game);
    return gameEntries.find(e=>e.sortAt>=now.getTime())||gameEntries[0]||null;
  }).filter(Boolean).map((e,i)=>{
    let icon="⏳";
    let detail=e.countdown;
    if(e.status==="Results Ready"){icon="🟢";detail="Results Ready";}
    else if(e.status==="Drawing In Progress"){icon="🔵";detail="Drawing";}
    else if(e.status==="Sales Closed"){icon="✓";detail="Completed";}
    else if(i===0){icon="⏳";detail=e.countdown;}
    const dayLabel=e.drawTime.split(",")[0];
    if(!["Today","Tomorrow"].includes(dayLabel)&&e.status==="Upcoming"){icon="📅";detail=dayLabel;}
    return{...e,icon,detail};
  });
}
function normalizeLiveDraw(row,now=new Date()){
  const game=rowGame(row);
  const drawDate=row?.draw_date||row?.date||"";
  const drawTime=String(row?.draw_time||"").toLowerCase();
  const slot=(NEXT_DRAW_SCHEDULE[game]||[]).find(s=>s.label.toLowerCase()===drawTime);
  const validDrawDate=/^\d{4}-\d{2}-\d{2}$/.test(drawDate);
  const drawAt=slot&&validDrawDate?easternDateTime(drawDate,slot.draw):null;
  const closeAt=slot&&validDrawDate?easternDateTime(drawDate,slot.close):null;
  const normalizedDate=/^\d{4}-\d{2}-\d{2}$/.test(drawDate)
    ?easternDateTime(drawDate,"12:00").toLocaleDateString("en-US",{timeZone:EASTERN_TIME_ZONE,weekday:"short",month:"short",day:"numeric",year:"numeric"})
    :row?.date||drawDate;
  return{
    ...row,
    game,
    gameLabel:gameLabel(game),
    date:normalizedDate,
    draw_date:row?.draw_date||drawDate,
    draw_time:row?.draw_time||"",
    numbers:Array.isArray(row?.numbers)?row.numbers:[],
    isEvening:typeof row?.isEvening==="boolean"?row.isEvening:drawTime==="evening"||drawTime==="night",
    drawAt,
    closeAt,
    drawTime:drawAt?formatDrawMoment(drawAt,now):row?.draw_time||"",
    closeTime:closeAt?formatDrawMoment(closeAt,now):"",
    statusColor:drawStatusColor("Archived"),
    archived:true,
    status:"Archived",
  };
}
function createDrawDataService(gameIds,rawDraws=[],now=new Date()){
  const selectedGames=normalizeGames(gameIds);
  const allDraws=(rawDraws||[]).map(row=>normalizeLiveDraw(row,now));
  const selectedDraws=allDraws.filter(d=>selectedGames.includes(rowGame(d)));
  const completedSelectedDraws=selectedDraws.filter(d=>Array.isArray(d.numbers)&&d.numbers.length===gameNumberConfig(rowGame(d)).count);
  const scheduledStatus=getDrawStatusService(selectedGames,now,selectedDraws);
  const status={
    ...scheduledStatus,
    archivedEntries:completedSelectedDraws.length?completedSelectedDraws:scheduledStatus.archivedEntries,
    latestArchived:completedSelectedDraws[0]||scheduledStatus.latestArchived,
  };
  return{
    now,
    games:selectedGames,
    allDraws,
    draws:selectedDraws,
    archivedDraws:selectedDraws,
    status,
    entries:status.entries,
    next:status.next,
    latestArchived:completedSelectedDraws[0]||status.latestArchived,
  };
}
function useDrawDataService(gameIds,rawDraws=[]){
  const[now,setNow]=useState(()=>new Date());
  useEffect(()=>{
    const id=setInterval(()=>setNow(new Date()),DRAW_STATUS_TICK_MS);
    return()=>clearInterval(id);
  },[]);
  return createDrawDataService(gameIds,rawDraws,now);
}
const MOCK_ACTIVITY=[
  {id:"first-numbers-saved",group:"Today",icon:"🎉",title:"First numbers saved",body:"Nice start. LottoTracker will now check those numbers against every tracked draw.",meta:"Today · Milestone",tone:"#008577"},
  {id:"next-draw-2h",group:"Today",icon:"🎯",title:"Next draw begins in 2 hours",body:"Fantasy 5 Evening is the next selected game on your dashboard.",meta:"Today · Fantasy 5",tone:"#008577"},
  {id:"sales-close-15m",group:"Today",icon:"🔒",title:"Ticket sales close in 15 minutes",body:"Your next selected draw is nearing its ticket sales cutoff.",meta:"Today · Reminder",tone:"#c8860a"},
  {id:"fantasy5-results",group:"Today",icon:"🎉",title:"Fantasy 5 results posted",body:"Latest results are ready to review against your saved plays.",meta:"Today · Results",tone:"#D4AF37"},
  {id:"strategy-3-match",group:"Today",icon:"🏆",title:"Your strategy hit 3 matches",body:"One saved strategy matched 3 numbers in the latest simulated draw.",meta:"Today · Scoreboard",tone:"#c8102e"},
  {id:"pool-lucky-10",group:"Yesterday",icon:"👥",title:"Pool \"Lucky 10\" results are available",body:"The shared pool simulation has finished scoring the latest draw.",meta:"Yesterday · Pool Simulation",tone:"#2E6DA4"},
  {id:"roi-new-high",group:"Yesterday",icon:"📈",title:"First profitable strategy",body:"Quick Picks moved into your best simulated ROI position.",meta:"Yesterday · Strategy",tone:"#008577"},
  {id:"tracked-100-draws",group:"Earlier",icon:"⭐",title:"First 100 tracked draws",body:"Your dashboard history now includes 100 tracked draw results.",meta:"Earlier · Milestone",tone:"#002855"},
];
function buildActivityNotifications(base,drawStatus){
  const next=drawStatus?.next;
  const latestArchived=drawStatus?.latestArchived;
  if(!next&&latestArchived){
    return[
      {id:"draw-status-archived",group:"Today",icon:"📚",title:latestArchived.gameLabel+" draw moved to History",body:"The completed draw is archived with your tracked results.",meta:"Today · History",tone:"#5d6675",route:"/history"},
      ...base,
    ];
  }
  if(!next)return base;
  let item=null;
  if(next.status==="Results Ready"){
    item={id:"draw-status-results-ready",group:"Today",icon:"🎉",title:next.gameLabel+" Results Ready",body:"Your saved plays are ready to review for the "+next.drawLabel+" draw.",meta:"Today · Results Center",tone:"#D4AF37",route:"/results"};
  }else if(next.status==="Waiting for official results"){
    item={id:"draw-status-official-wait",group:"Today",icon:"⏳",title:"Waiting for official results",body:next.gameLabel+" "+next.drawLabel+" is complete. LottoTracker is checking the official Florida Lottery results before scoring.",meta:"Today · Official Results",tone:"#005bbb",route:"/results"};
  }else if(next.status==="Official results delayed"){
    item={id:"draw-status-official-delayed",group:"Today",icon:"⏳",title:"Official results delayed",body:"Florida Lottery has not posted the official "+next.gameLabel+" "+next.drawLabel+" results yet. Scoring will stay paused until they are saved.",meta:"Today · Official Results",tone:"#c8860a",route:"/results"};
  }else if(next.status==="Sales Closing Soon"){
    item={id:"draw-status-sales-closing",group:"Today",icon:"🔒",title:"Ticket sales close soon",body:next.gameLabel+" "+next.drawLabel+" closes at "+next.closeTime+". Add numbers before the window closes.",meta:"Today · Reminder",tone:"#c8860a",route:"/my-strategies"};
  }else if(next.status==="Sales Closed"||next.status==="Drawing In Progress"){
    item={id:"draw-status-sales-closed",group:"Today",icon:"⏳",title:next.gameLabel+" draw is underway",body:"Sales are closed. LottoTracker will surface results when they are ready.",meta:"Today · Draw Status",tone:"#005bbb",route:"/results"};
  }else{
    item={id:"draw-status-upcoming",group:"Today",icon:"🎯",title:next.gameLabel+" next draw is "+next.countdown,body:next.drawLabel+" draw is coming up. Save numbers or check your plays before sales close.",meta:"Today · "+next.drawLabel,tone:"#008577",route:"/my-strategies"};
  }
  return[item,...base.filter(n=>n.id!==item.id)];
}
// Tier metadata (label + color) — does NOT include amounts. Prize amounts
// are pari-mutuel and per-draw; read from draw.prize_Nmatch, not from here.
const TIER_META={
  5:{label:"Jackpot",color:"#D4AF37"},
  4:{label:"4 Match",color:"#8B4513"},
  3:{label:"3 Match",color:"#2E6DA4"},
  2:{label:"Free Play",color:"#555555"},
};
function cntMatch(play,draw){if(!play||!draw)return 0;return play.filter(n=>draw.includes(n)).length}
function getTier(m){return TIER_META[m]||null}

// Amount-aware prize lookup. Returns:
//   null              — matches < 2 (no tier)
//   { amount: null }  — price not yet entered (display "Pending")
//   { amount: 0 }     — either free Quick Pick (m=2) or rolldown (m=5)
//   { amount: n }     — real pari-mutuel amount
// `display` is the formatted string suitable for direct rendering.
function getDrawPrize(draw,m){
  if(m<2||!draw) return null;
  const meta=TIER_META[m];
  if(!meta) return null;
  const raw=draw["prize_"+m+"match"];
  if(raw===null||raw===undefined){
    return{amount:null,display:"Pending",label:meta.label,color:meta.color};
  }
  const amount=Number(raw);
  if(!Number.isFinite(amount)){
    return{amount:null,display:"\u2014",label:meta.label,color:meta.color};
  }
  if(m===5&&amount===0){
    return{amount:0,display:"Rolldown",label:"Rolldown",color:"#8B4513"};
  }
  if(m===2){
    return{amount:0,display:"Free Quick Pick",label:meta.label,color:meta.color};
  }
  return{amount,display:"$"+amount.toLocaleString(),label:meta.label,color:meta.color};
}
function drawTimeLabel(draw){
  const raw=String(draw?.draw_time||"").toLowerCase();
  if(raw==="midday")return "Midday";
  if(raw==="evening"||raw==="night")return "Evening";
  return draw?.isEvening?"Evening":"Midday";
}
function isOfficialDraw(draw){
  return !!draw&&(draw.source==="official"||draw.official_result_status==="official");
}
function jackpotPrizeTableEntry(draw){
  const table=Array.isArray(draw?.prize_table)?draw.prize_table:[];
  return table.find(row=>Number(row?.match)===5||Number(row?.tier)===5)||null;
}
function jackpotWinnerText(draw){
  const entry=jackpotPrizeTableEntry(draw);
  const winners=Number(entry?.winners ?? entry?.winnerCount ?? entry?.count);
  const amount=Number(draw?.prize_5match);
  if(Number.isFinite(winners)&&winners>0){
    const prize=Number.isFinite(amount)&&amount>0?("$"+amount.toLocaleString(undefined,{maximumFractionDigits:2})):"Prize posted";
    return winners+" Jackpot Winner"+(winners===1?"":"s")+" · "+prize;
  }
  if(Number.isFinite(amount)&&amount>0){
    return "$"+amount.toLocaleString(undefined,{maximumFractionDigits:2})+" Jackpot";
  }
  if(isOfficialDraw(draw))return Number.isFinite(winners)&&winners===0?"No Jackpot Winner":"Jackpot Rolled Over";
  return "Waiting for official jackpot";
}
function jackpotAmountText(draw){
  const amount=Number(draw?.prize_5match);
  if(Number.isFinite(amount)&&amount>0)return "$"+amount.toLocaleString(undefined,{maximumFractionDigits:2});
  if(Number.isFinite(amount)&&amount===0)return "Jackpot Rolled Over";
  return isOfficialDraw(draw)?"No Jackpot Winner":"Waiting for official results";
}
const RESULTS_HISTORY_WINDOWS=[
  {id:"last_night",label:"Yesterday",kind:"yesterday"},
  {id:"7",label:"Last 7 Days",days:7},
  {id:"30",label:"Last 30 Days",days:30},
  {id:"90",label:"Last 90 Days",days:90},
  {id:"this_year",label:"This Year",kind:"this_year"},
  {id:"last_year",label:"Last Year",kind:"last_year"},
];
function resultsWindowOption(windowId){
  return RESULTS_HISTORY_WINDOWS.find(w=>w.id===windowId)||RESULTS_HISTORY_WINDOWS.find(w=>w.id==="30")||RESULTS_HISTORY_WINDOWS[0];
}
function filterDrawsByWindow(draws,windowId){
  const option=resultsWindowOption(windowId);
  if(option.kind==="yesterday"||option.kind==="last_night"){
    const today=new Date();
    today.setHours(0,0,0,0);
    const yesterday=new Date(today);
    yesterday.setDate(today.getDate()-1);
    const yesterdayIso=yesterday.toISOString().slice(0,10);
    return(draws||[]).filter(d=>{
      const key=d.draw_date||String(d.date||"").slice(0,10);
      return key===yesterdayIso;
    });
  }
  const today=new Date();
  today.setHours(0,0,0,0);
  const endDate=new Date(today);
  if(option.days!==0&&option.kind!=="this_year")endDate.setDate(today.getDate()-1);
  const cutoff=new Date(today);
  if(option.kind==="this_year"){
    cutoff.setMonth(0,1);
  }else if(option.kind==="last_year"){
    cutoff.setFullYear(today.getFullYear()-1,0,1);
    endDate.setFullYear(today.getFullYear()-1,11,31);
  }else{
    if(option.days==null)return draws||[];
    cutoff.setDate(today.getDate()-option.days);
  }
  const cutoffIso=cutoff.toISOString().slice(0,10);
  const todayIso=today.toISOString().slice(0,10);
  const endIso=endDate.toISOString().slice(0,10);
  return(draws||[]).filter(d=>{
    const key=d.draw_date||String(d.date||"").slice(0,10);
    return option.days===0?key===todayIso:key>=cutoffIso&&key<=endIso;
  });
}
function resultsWindowBounds(windowId){
  const option=resultsWindowOption(windowId);
  const today=new Date();
  today.setHours(0,0,0,0);
  const todayIso=today.toISOString().slice(0,10);
  if(option.kind==="yesterday"||option.kind==="last_night"){
    const yesterday=new Date(today);
    yesterday.setDate(today.getDate()-1);
    const yesterdayIso=yesterday.toISOString().slice(0,10);
    return{from:yesterdayIso,to:yesterdayIso};
  }
  if(option.kind==="this_year"){
    const from=new Date(today);
    from.setMonth(0,1);
    return{from:from.toISOString().slice(0,10),to:todayIso};
  }
  if(option.kind==="last_year"){
    const from=new Date(today);
    from.setFullYear(today.getFullYear()-1,0,1);
    const to=new Date(today);
    to.setFullYear(today.getFullYear()-1,11,31);
    return{from:from.toISOString().slice(0,10),to:to.toISOString().slice(0,10)};
  }
  if(option.days==null)return{from:null,to:todayIso};
  if(option.days===0)return{from:todayIso,to:todayIso};
  const cutoff=new Date(today);
  cutoff.setDate(today.getDate()-option.days);
  const endDate=new Date(today);
  endDate.setDate(today.getDate()-1);
  return{from:cutoff.toISOString().slice(0,10),to:endDate.toISOString().slice(0,10)};
}
function summaryPeriodForWindow(windowId){
  return SUMMARY_PERIOD_BY_WINDOW[windowId]||"30d";
}
function summaryNumber(value){
  const n=Number(value);
  return Number.isFinite(n)?n:0;
}
function watchRoiText(value,totalCost){
  if(!(Number(totalCost)>0)||value===null||value===undefined)return"Unavailable";
  const n=Number(value);
  if(!Number.isFinite(n))return"Unavailable";
  return(n>=0?"+":"")+n.toFixed(2)+"%";
}
function normalizeTrackingMode(value){
  const key=String(value||"").toLowerCase();
  return key==="watch"?"watch":"live";
}
function normalizeResultsGame(value){
  const key=String(value||"").toLowerCase();
  return launchReadyGames().some(game=>game.id===key)?key:null;
}
function normalizeResultsPeriod(value){
  const key=String(value||"");
  return RESULTS_HISTORY_WINDOWS.some(option=>option.id===key)?key:null;
}
function normalizeResultsDrawFilter(value){
  const key=String(value||"both").toLowerCase();
  return["both","midday","evening"].includes(key)?key:"both";
}
function strategyPerformancePath(strategyId,game="fantasy5",period="90",drawFilter="both"){
  const qs=new URLSearchParams({
    mode:"watch",
    game:normalizeResultsGame(game)||"fantasy5",
    strategy:String(strategyId||"all"),
    period:normalizeResultsPeriod(period)||"90",
    draw:normalizeResultsDrawFilter(drawFilter),
  });
  return"/results?"+qs.toString();
}
function strategyEditPath(strategyId,options={}){
  const qs=new URLSearchParams({edit:String(strategyId||"")});
  if(options.onboarding)qs.set("onboarding","1");
  return"/strategy-builder?"+qs.toString();
}
function trackingModeLabel(value){
  return normalizeTrackingMode(value)==="watch"?"Saved Playslip":"Playing Today";
}
function trackingModeColor(value){
  return normalizeTrackingMode(value)==="watch"?"#2E6DA4":"#008577";
}
function drawsFingerprint(rows=[]){
  if(!Array.isArray(rows)||!rows.length)return"empty";
  return rows.map(row=>[
    row.id||"",
    row.game||"",
    row.draw_date||"",
    row.draw_time||"",
    row.official_result_status||row.source||"",
    Array.isArray(row.numbers)?row.numbers.join("-"):"",
    row.prize_5match??"",
    row.prize_4match??"",
    row.prize_3match??"",
    row.prize_2match??"",
    Array.isArray(row.prize_table)?row.prize_table.length:"",
  ].join(":")).join("|");
}
function drawRowKey(row){
  return String(row?.id||[row?.game||"",row?.draw_date||"",row?.draw_time||""].join(":"));
}
function mergeDrawRows(existing=[],incoming=[]){
  const map=new Map();
  [...(existing||[]),...(incoming||[])].forEach(row=>{
    if(!row)return;
    map.set(drawRowKey(row),row);
  });
  return [...map.values()].sort((a,b)=>{
    const aKey=String(a?.draw_date||"")+" "+String(a?.draw_time||"");
    const bKey=String(b?.draw_date||"")+" "+String(b?.draw_time||"");
    return bKey.localeCompare(aKey);
  });
}
function summarizePerformanceRows(rows=[]){
  const clean=Array.isArray(rows)?rows:[];
  const totalCost=clean.reduce((sum,row)=>sum+summaryNumber(row.total_cost),0);
  const cashWon=clean.reduce((sum,row)=>sum+summaryNumber(row.cash_won),0);
  const net=cashWon-totalCost;
  const roi=totalCost>0?(net/totalCost)*100:null;
  const bestMatch=clean.reduce((best,row)=>Math.max(best,summaryNumber(row.best_match)),0);
  const largestPrize=clean.reduce((best,row)=>Math.max(best,summaryNumber(row.largest_prize)),0);
  const freeTickets=clean.reduce((sum,row)=>sum+summaryNumber(row.free_tickets),0);
  const drawsTracked=clean.reduce((sum,row)=>sum+summaryNumber(row.draws_tracked),0);
  const winningDraws=clean.reduce((sum,row)=>sum+summaryNumber(row.winning_draws),0);
  const latest=clean
    .filter(row=>row.last_completed_draw_date)
    .sort((a,b)=>String((b.last_completed_draw_date||"")+"-"+(b.last_completed_draw_time||"")).localeCompare(String((a.last_completed_draw_date||"")+"-"+(a.last_completed_draw_time||""))))[0]||null;
  return{total_cost:totalCost,cash_won:cashWon,net,roi,best_match:bestMatch,largest_prize:largestPrize,free_tickets:freeTickets,draws_tracked:drawsTracked,winning_draws:winningDraws,latest};
}
function withDrawsTracked(summary,drawCount){
  return{...(summary||{}),draws_tracked:summaryNumber(drawCount)};
}
function performanceSummaryRowsFor(summaries=[],filters={}){
  const periodKey=filters.periodKey||"all_time";
  const drawFilter=filters.drawFilter||"both";
  const trackingMode=normalizeTrackingMode(filters.trackingMode||"live");
  return(Array.isArray(summaries)?summaries:[]).filter(row=>
    (!filters.game||row.game===filters.game)&&
    (!filters.strategyId||row.strategy_id===filters.strategyId)&&
    normalizeTrackingMode(row.tracking_mode)===(filters.trackingMode?trackingMode:"live")&&
    row.period_key===periodKey&&
    row.draw_filter===drawFilter
  );
}
function strategyPerformanceRow(summaries=[],strategyId,game,periodKey="all_time",drawFilter="both"){
  return performanceSummaryRowsFor(summaries,{strategyId,game,periodKey,drawFilter,trackingMode:"live"})[0]||null;
}
function roiText(value,totalCost){
  if(!(Number(totalCost)>0)||value===null||value===undefined)return"Pending";
  const n=Number(value);
  if(!Number.isFinite(n))return"Pending";
  return(n>=0?"+":"")+n.toFixed(0)+"%";
}
function netText(value){
  const n=Number(value)||0;
  return n>=0?"+"+fmtMoney(n):"-"+fmtMoney(Math.abs(n));
}
function trendLabel(value){
  if(value==="up")return"Trending up";
  if(value==="down")return"Trending down";
  if(value==="flat")return"Flat";
  return"Not enough data";
}
function trendSymbol(value){
  if(value==="up")return"\u2191";
  if(value==="down")return"\u2193";
  if(value==="flat")return"\u2192";
  return"-";
}
function refreshReasonText(reason){
  const copy={
    official_draw_import:"Updated after official results were imported.",
    scheduled_draw_completed:"Updated after a scheduled draw was completed.",
    backfill:"Updated during live performance backfill.",
    manual_verify:"Updated during performance verification.",
    manual_recalculate:"Updated after manual recalculation.",
  };
  return copy[reason]||"Live performance summary is ready.";
}
function drawFilterLabel(filter){
  if(filter==="midday")return"Midday";
  if(filter==="evening")return"Evening";
  return"All Draws";
}
function completedSummaryRowsForSelection(summaries,game,periodKey,drawFilter,strategyId="all",trackingMode="live"){
  return performanceSummaryRowsFor(summaries,{
    game,
    strategyId:strategyId==="all"?null:strategyId,
    periodKey,
    drawFilter,
    trackingMode,
  });
}
function strategyLiveRowFromSummaries(strategyId,records,allRows,periodKey,drawFilter,trackingMode="live"){
  const rowFor=(game,filter)=>performanceSummaryRowsFor(allRows,{strategyId,game,periodKey,drawFilter:filter,trackingMode})[0]||null;
  const both=rowFor(records?.[strategyId]?.game,drawFilter);
  const game=both?.game||records?.[strategyId]?.game||(allRows.find(r=>r.strategy_id===strategyId)?.game)||"fantasy5";
  const row=both||rowFor(game,drawFilter)||null;
  const midday=rowFor(game,"midday");
  const evening=rowFor(game,"evening");
  const name=strategyDisplayName(strategyId,records?.[strategyId]||{id:strategyId});
  const stronger=betterDrawTime(midday,evening);
  return{strategy_id:strategyId,id:strategyId,game,name,row,midday,evening,stronger};
}
function strategyRequiresFireball(record){
  const game=record?.game||"";
  if(!isPickGame(game))return false;
  const entries=strategySnapshotPlayEntries(record);
  if(entries.length)return entries.some(entry=>!!entry?.pickRules?.fireball);
  return !!pickLineRulesFromItem(null,record?.pickRules||record?.outcomeSnapshot?.pickRules||record?.snapshot?.pickRules||{}).fireball;
}
function watchEligibleOfficialDraws(draws=[],game="fantasy5",windowId="7",drawFilter="both",strategyRecord=null){
  const cfg=gameNumberConfig(game);
  const requiresFireball=strategyRequiresFireball(strategyRecord);
  return filterDrawsByWindow(draws,windowId)
    .filter(draw=>{
      if(
        rowGame(draw)!==game||
        !drawHasExpectedNumbers(draw)||
        !isOfficialDraw(draw)||
        (drawFilter&&drawFilter!=="both"&&String(draw.draw_time||"").toLowerCase()!==drawFilter)||
        !Array.isArray(draw.numbers)||
        draw.numbers.length!==cfg.count
      )return false;
      if(game==="fantasy5"){
        return(
          (draw.prize_3match!==null&&draw.prize_3match!==undefined)&&
          (draw.prize_4match!==null&&draw.prize_4match!==undefined)&&
          (draw.prize_5match!==null&&draw.prize_5match!==undefined)
        );
      }
      if(game==="pick2"||game==="pick3"||game==="pick4"||game==="pick5"){
        return requiresFireball?drawFireballDigit(draw)!==null:true;
      }
      return false;
    })
    .sort((a,b)=>{
      const ad=(a.draw_date||"")+"-"+(a.draw_time==="evening"?"2":"1");
      const bd=(b.draw_date||"")+"-"+(b.draw_time==="evening"?"2":"1");
      return bd.localeCompare(ad);
    });
}
function drawTimeSortRank(drawTime){
  return String(drawTime||"").toLowerCase()==="evening"?2:1;
}
function compareOfficialDrawRecency(a,b){
  const dateCmp=String(b?.draw_date||"").localeCompare(String(a?.draw_date||""));
  if(dateCmp)return dateCmp;
  const timeCmp=drawTimeSortRank(b?.draw_time)-drawTimeSortRank(a?.draw_time);
  if(timeCmp)return timeCmp;
  return String(b?.official_result_at||b?.fetched_at||b?.official_checked_at||"").localeCompare(String(a?.official_result_at||a?.fetched_at||a?.official_checked_at||""));
}
function latestOfficialDrawForSelection(draws=[],game,drawFilter="both"){
  return [...(draws||[])]
    .filter(row=>rowGame(row)===game)
    .filter(row=>drawFilter==="both"||String(row.draw_time||"").toLowerCase()===drawFilter)
    .filter(row=>isOfficialDraw(row)&&drawHasExpectedNumbers(row))
    .sort(compareOfficialDrawRecency)[0]||null;
}
function fantasy5PrizeForWatchDraw(draw,match){
  if(match===5)return summaryNumber(draw?.prize_5match);
  if(match===4)return summaryNumber(draw?.prize_4match);
  if(match===3)return summaryNumber(draw?.prize_3match);
  return 0;
}
function fantasy5MatchedNumbers(play=[],official=[]){
  const officialSet=new Set((official||[]).map(Number));
  return (play||[]).map(Number).filter(n=>officialSet.has(n)).sort((a,b)=>a-b);
}
function fantasy5PrizeType(match){
  if(match===5)return "5 of 5 cash prize";
  if(match===4)return "4 of 5 cash prize";
  if(match===3)return "3 of 5 cash prize";
  if(match===2)return "Free ticket";
  return "No prize";
}
function pickRuleValue(...values){
  return values.find(value=>value!==undefined&&value!==null&&value!=="");
}
function pickLineRulesFromItem(item,fallbackRules=null){
  const fallback=fallbackRules&&typeof fallbackRules==="object"?fallbackRules:{};
  if(!item||typeof item!=="object"||Array.isArray(item)){
    return{
      playType:fallback.playType||fallback.play_type||"Straight",
      wager:Number(pickRuleValue(fallback.wager,fallback.amount,1))||1,
      multiple:Number(pickRuleValue(fallback.multiple,fallback.multiplier,1))||1,
      fireball:!!pickRuleValue(fallback.fireball,fallback.fireballEnabled,false),
    };
  }
  const nested=item.pickRules||item.rules||item.playRules||item.rule||{};
  const rule={
    playType:pickRuleValue(item.playType,item.play_type,item.type,nested.playType,nested.play_type,nested.type,fallback.playType,fallback.play_type,"Straight"),
    wager:Number(pickRuleValue(item.wager,item.amount,item.ticketAmount,nested.wager,nested.amount,nested.ticketAmount,fallback.wager,fallback.amount,1))||1,
    multiple:Number(pickRuleValue(item.multiple,item.multiplier,item.playCount,item.times,nested.multiple,nested.multiplier,nested.playCount,nested.times,fallback.multiple,fallback.multiplier,1))||1,
    fireball:!!pickRuleValue(item.fireball,item.fireballEnabled,nested.fireball,nested.fireballEnabled,fallback.fireball,fallback.fireballEnabled,false),
  };
  const cost=Number(pickRuleValue(item.cost,item.estimatedCost,item.ticketCost,nested.cost,nested.estimatedCost,nested.ticketCost));
  if(Number.isFinite(cost)&&cost>=0)rule.cost=cost;
  return rule;
}
function pickLineCost(game,numbers=[],rules={}){
  const stored=Number(rules?.cost);
  if(Number.isFinite(stored)&&stored>=0)return stored;
  if(!isPickGame(game))return 1;
  const type=rules.playType||"Straight";
  const wager=Number(rules.wager)||1;
  const multiple=Math.max(1,Number(rules.multiple)||1);
  const ways=permutationCount(numbers);
  const ticketWager=type==="Straight/Box"?Math.max(1,wager):wager;
  const units=type==="Combo"?Math.max(1,ways):1;
  return ticketWager*units*multiple*(rules.fireball?2:1);
}
function pickLineRepresentedTickets(game,numbers=[],rules={}){
  if(!isPickGame(game))return 1;
  const type=rules.playType||"Straight";
  const multiple=Math.max(1,Number(rules.multiple)||1);
  const ways=permutationCount(numbers);
  return (type==="Combo"?Math.max(1,ways):1)*multiple;
}
function emptyMatchCounts(count=5){
  const out={};
  for(let i=0;i<=count;i++)out[String(i)]=0;
  return out;
}
function uniqueOrderedVariants(drawNumbers=[],fireball=null){
  if(!Array.isArray(drawNumbers)||fireball==null)return[];
  const seen=new Set();
  const variants=[];
  for(let i=0;i<drawNumbers.length;i++){
    const next=[...drawNumbers];
    next[i]=fireball;
    const key=next.join("-");
    if(seen.has(key))continue;
    seen.add(key);
    variants.push(next);
  }
  return variants;
}
function uniqueBoxVariantKeys(variants=[]){
  return new Set((variants||[]).map(variant=>[...variant].sort((a,b)=>a-b).join("-")));
}
function pick4DisplayWinPath(path){
  const raw=String(path?.win_path||"");
  const isFireball=path?.category==="fireball"||raw.toLowerCase().includes("fireball");
  if(raw.includes("Straight/Box Box portion"))return isFireball?"Fireball Box win":"Base Box win";
  if(raw.includes("Straight/Box Straight portion"))return isFireball?"Fireball Straight win":"Base Straight win";
  if(raw==="Base Box")return"Base Box win";
  if(raw==="Fireball Box")return"Fireball Box win";
  if(raw==="Base Straight")return"Base Straight win";
  if(raw==="Fireball Straight")return"Fireball Straight win";
  return raw;
}
function pick4WinPathDetails(entry,draw,record,index=0){
  const numbers=Array.isArray(entry)?entry:(entry?.numbers||[]);
  const drawNumbers=draw?.numbers||[];
  if(!Array.isArray(numbers)||numbers.length!==4||!Array.isArray(drawNumbers)||drawNumbers.length!==4)return[];
  const rules=(entry&&!Array.isArray(entry)&&entry.pickRules)||record?.pickRules||{};
  const type=rules.playType||"Straight";
  const rawWager=Number(rules.wager);
  const wager=Number.isFinite(rawWager)&&rawWager>0?rawWager:1;
  const rawMultiple=Number(rules.multiple);
  const multiple=Number.isFinite(rawMultiple)&&rawMultiple>0?rawMultiple:1;
  const ticketWager=type==="Straight/Box"?Math.max(1,wager):wager;
  const splitWager=type==="Straight/Box"?ticketWager/2:ticketWager;
  const exact=numbers.every((n,i)=>Number(n)===Number(drawNumbers[i]));
  const boxed=sameMultiset(numbers,drawNumbers);
  const ways=permutationCount(numbers);
  const validBox=ways>1;
  const fireballEnabled=!!rules.fireball;
  const fireball=fireballEnabled?drawFireballDigit(draw):null;
  const variants=uniqueOrderedVariants(drawNumbers,fireball);
  const variantBoxKeys=uniqueBoxVariantKeys(variants);
  const playKey=numbers.join("-");
  const playBoxKey=[...numbers].sort((a,b)=>a-b).join("-");
  const fireballExact=variants.some(variant=>variant.join("-")===playKey);
  const fireballBoxed=validBox&&variantBoxKeys.has(playBoxKey);
  const straightBase=PICK_STRAIGHT_PRIZES.pick4;
  const boxBase=validBox?(PICK_BOX_PRIZES.pick4?.[ways]||0):0;
  const fireballStraightBase=PICK_FIREBALL_STRAIGHT_PRIZES.pick4;
  const fireballBoxBase=validBox?(PICK_FIREBALL_BOX_PRIZES.pick4?.[ways]||0):0;
  const common={
    saved_digits:numbers,
    saved_line_index:index+1,
    play_type:type,
    wager,
    multiple,
    fireball_enabled:fireballEnabled,
    official_base_digits:drawNumbers,
    official_fireball_digit:drawFireballDigit(draw),
    permutation_count:ways,
  };
  const makePath=(winPath,payoutBeforeMultiplier,category)=>{
    const before=summaryNumber(payoutBeforeMultiplier);
    return{
      ...common,
      win_path:winPath,
      category,
      payout_before_multiplier:before,
      multiplier:multiple,
      final_payout:before*multiple,
    };
  };
  const paths=[];
  if(type==="Straight"){
    if(exact)paths.push(makePath("Base Straight",straightBase*wager,"straight"));
    if(fireballExact)paths.push(makePath("Fireball Straight",fireballStraightBase*wager,"fireball"));
  }else if(type==="Box"){
    if(boxed&&validBox)paths.push(makePath("Base Box",boxBase*wager,"box"));
    if(fireballBoxed&&validBox)paths.push(makePath("Fireball Box",fireballBoxBase*wager,"fireball"));
  }else if(type==="Straight/Box"){
    if(exact)paths.push(makePath("Straight/Box Straight portion",straightBase*splitWager,"straight_box"));
    if(boxed&&validBox)paths.push(makePath("Straight/Box Box portion",boxBase*splitWager,"straight_box"));
    if(fireballExact)paths.push(makePath("Fireball Straight/Box Straight portion",fireballStraightBase*splitWager,"fireball"));
    if(fireballBoxed&&validBox)paths.push(makePath("Fireball Straight/Box Box portion",fireballBoxBase*splitWager,"fireball"));
  }else if(type==="Combo"){
    if(boxed&&validBox)paths.push(makePath("Combo exact-order leg",straightBase*ticketWager,"combo"));
    if(fireballBoxed&&validBox)paths.push(makePath("Fireball Combo",fireballStraightBase*ticketWager,"fireball"));
  }else if(exact){
    paths.push(makePath("Base Straight",straightBase*wager,"straight"));
  }
  return paths.filter(path=>summaryNumber(path.final_payout)>0);
}
function pick4WatchPrizeDetails(entry,draw,record,index=0){
  const numbers=Array.isArray(entry)?entry:(entry?.numbers||[]);
  const drawNumbers=draw?.numbers||[];
  if(!Array.isArray(numbers)||numbers.length!==4||!Array.isArray(drawNumbers)||drawNumbers.length!==4){
    return{amount:0,baseAmount:0,fireballAmount:0,bestMatch:0,baseWin:false,fireballWin:false,labels:[],paths:[]};
  }
  const paths=pick4WinPathDetails(entry,draw,record,index);
  const baseAmount=paths.filter(path=>path.category!=="fireball").reduce((sum,path)=>sum+summaryNumber(path.final_payout),0);
  const fireballAmount=paths.filter(path=>path.category==="fireball").reduce((sum,path)=>sum+summaryNumber(path.final_payout),0);
  const labels=paths.map(path=>path.win_path);
  const positionMatch=numbers.reduce((sum,n,i)=>sum+(Number(n)===Number(drawNumbers[i])?1:0),0);
  const bestMatch=baseAmount>0||fireballAmount>0?4:positionMatch;
  return{
    amount:baseAmount+fireballAmount,
    baseAmount,
    fireballAmount,
    bestMatch,
    baseWin:baseAmount>0,
    fireballWin:fireballAmount>0,
    labels,
    paths,
  };
}
function calculatePick4WatchDrawResult(strategyId,record,draw){
  const game="pick4";
  const entries=strategySnapshotPlayEntries(record);
  const numbers=entries.map(entry=>entry.numbers);
  const cost=entries.reduce((sum,entry)=>sum+pickLineCost(game,entry.numbers,entry.pickRules),0);
  const representedPlays=entries.reduce((sum,entry)=>sum+pickLineRepresentedTickets(game,entry.numbers,entry.pickRules),0);
  const matchCounts=emptyMatchCounts(4);
  const prizeBreakdown={
    base_wins:{label:"Base wins",plays:0,cash:0},
    fireball_wins:{label:"Fireball wins",plays:0,cash:0},
  };
  let cashWon=0;
  let winningPlaysCount=0;
  let bestMatch=0;
  let largestPrize=0;
  const winningPlayDetails=[];
  entries.forEach((entry,index)=>{
    const details=pick4WatchPrizeDetails(entry,draw,record,index);
    const key=String(Math.max(0,Math.min(4,details.bestMatch)));
    matchCounts[key]=(matchCounts[key]||0)+1;
    bestMatch=Math.max(bestMatch,details.bestMatch);
    if(details.amount>0){
      cashWon+=details.amount;
      largestPrize=Math.max(largestPrize,details.amount);
      winningPlaysCount+=1;
      winningPlayDetails.push(...details.paths);
    }
    if(details.baseWin){
      prizeBreakdown.base_wins.plays+=1;
      prizeBreakdown.base_wins.cash+=details.baseAmount;
    }
    if(details.fireballWin){
      prizeBreakdown.fireball_wins.plays+=1;
      prizeBreakdown.fireball_wins.cash+=details.fireballAmount;
    }
  });
  const net=cashWon-cost;
  return{
    id:["watch",strategyId,draw?.id||draw?.draw_date,draw?.draw_time].join(":"),
    user_id:null,
    strategy_id:strategyId,
    scheduled_draw_id:null,
    official_draw_id:draw?.id!=null?String(draw.id):null,
    official_draw_key:[game,draw?.draw_date,draw?.draw_time].join(":"),
    game,
    draw_date:draw?.draw_date,
    draw_time:String(draw?.draw_time||"").toLowerCase(),
    official_numbers:draw?.numbers||[],
    strategy_name:strategyDisplayName(strategyId,record),
    play_type:formatPickRulesSummary(game,record?.pickRules),
    tracking_mode:"watch",
    play_count:numbers.length,
    represented_plays:representedPlays,
    total_cost:summaryNumber(cost),
    cash_won:cashWon,
    net,
    roi:cost>0?(net/cost)*100:null,
    best_match:bestMatch,
    free_tickets:0,
    largest_prize:largestPrize,
    winning_plays_count:winningPlaysCount,
    match_counts:matchCounts,
    prize_breakdown:prizeBreakdown,
    winning_play_details:winningPlayDetails,
    result_source:"watch_snapshot_pick4",
    calculated_at:new Date().toISOString(),
  };
}
function calculatePick3WatchDrawResult(strategyId,record,draw){
  const game="pick3";
  const entries=strategySnapshotPlayEntries(record);
  const scored=calculatePick3EntriesDraw(entries,draw,record?.pickRules||{});
  const cost=summaryNumber(scored.total_cost);
  const net=summaryNumber(scored.cash_won)-cost;
  return{
    id:["watch",strategyId,draw?.id||draw?.draw_date,draw?.draw_time].join(":"),
    user_id:null,
    strategy_id:strategyId,
    scheduled_draw_id:null,
    official_draw_id:draw?.id!=null?String(draw.id):null,
    official_draw_key:[game,draw?.draw_date,draw?.draw_time].join(":"),
    game,
    draw_date:draw?.draw_date,
    draw_time:String(draw?.draw_time||"").toLowerCase(),
    strategy_name:strategyDisplayName(strategyId,record),
    play_type:formatPickRulesSummary(game,record?.pickRules),
    tracking_mode:"watch",
    play_count:summaryNumber(scored.play_count),
    represented_plays:summaryNumber(scored.represented_plays),
    total_cost:cost,
    cash_won:summaryNumber(scored.cash_won),
    net,
    roi:cost>0?(net/cost)*100:null,
    best_match:summaryNumber(scored.best_match),
    free_tickets:0,
    largest_prize:summaryNumber(scored.largest_prize),
    winning_plays_count:summaryNumber(scored.winning_plays_count),
    match_counts:scored.match_counts||emptyMatchCounts(3),
    prize_breakdown:scored.prize_breakdown||{},
    winning_play_details:scored.winning_play_details||[],
    result_source:"watch_snapshot_pick3",
    calculated_at:new Date().toISOString(),
    error:scored.error||null,
  };
}
function calculatePick2WatchDrawResult(strategyId,record,draw){
  const game="pick2";
  const entries=strategySnapshotPlayEntries(record);
  const scored=calculatePick2EntriesDraw(entries,{...draw,fireball_digit:drawFireballDigit(draw)},record?.pickRules||{});
  const cost=summaryNumber(scored.total_cost);
  const net=summaryNumber(scored.cash_won)-cost;
  return{
    id:["watch",strategyId,draw?.id||draw?.draw_date,draw?.draw_time].join(":"),
    user_id:null,
    strategy_id:strategyId,
    scheduled_draw_id:null,
    official_draw_id:draw?.id!=null?String(draw.id):null,
    official_draw_key:[game,draw?.draw_date,draw?.draw_time].join(":"),
    game,
    draw_date:draw?.draw_date,
    draw_time:String(draw?.draw_time||"").toLowerCase(),
    strategy_name:strategyDisplayName(strategyId,record),
    play_type:formatPickRulesSummary(game,record?.pickRules),
    tracking_mode:"watch",
    play_count:summaryNumber(scored.play_count),
    represented_plays:summaryNumber(scored.represented_plays),
    total_cost:cost,
    cash_won:summaryNumber(scored.cash_won),
    net,
    roi:cost>0?(net/cost)*100:null,
    best_match:summaryNumber(scored.best_match),
    free_tickets:0,
    largest_prize:summaryNumber(scored.largest_prize),
    winning_plays_count:summaryNumber(scored.winning_plays_count),
    match_counts:scored.match_counts||emptyMatchCounts(2),
    prize_breakdown:scored.prize_breakdown||{},
    winning_play_details:scored.winning_play_details||[],
    result_source:"watch_snapshot_pick2",
    calculated_at:new Date().toISOString(),
    error:scored.error||null,
  };
}
function calculatePick5WatchDrawResult(strategyId,record,draw){
  const game="pick5";
  const entries=strategySnapshotPlayEntries(record);
  const scored=calculatePick5EntriesDraw(entries,{...draw,fireball_digit:drawFireballDigit(draw)},record?.pickRules||{});
  const cost=summaryNumber(scored.total_cost);
  const net=summaryNumber(scored.cash_won)-cost;
  return{
    id:["watch",strategyId,draw?.id||draw?.draw_date,draw?.draw_time].join(":"),
    user_id:null,
    strategy_id:strategyId,
    scheduled_draw_id:null,
    official_draw_id:draw?.id!=null?String(draw.id):null,
    official_draw_key:[game,draw?.draw_date,draw?.draw_time].join(":"),
    game,
    draw_date:draw?.draw_date,
    draw_time:String(draw?.draw_time||"").toLowerCase(),
    strategy_name:strategyDisplayName(strategyId,record),
    play_type:formatPickRulesSummary(game,record?.pickRules),
    tracking_mode:"watch",
    play_count:summaryNumber(scored.play_count),
    represented_plays:summaryNumber(scored.represented_plays),
    total_cost:cost,
    cash_won:summaryNumber(scored.cash_won),
    net,
    roi:cost>0?(net/cost)*100:null,
    best_match:summaryNumber(scored.best_match),
    free_tickets:0,
    largest_prize:summaryNumber(scored.largest_prize),
    winning_plays_count:summaryNumber(scored.winning_plays_count),
    match_counts:scored.match_counts||emptyMatchCounts(5),
    prize_breakdown:scored.prize_breakdown||{},
    winning_play_details:scored.winning_play_details||[],
    result_source:"watch_snapshot_pick5",
    calculated_at:new Date().toISOString(),
    error:scored.error||null,
  };
}
function calculateWatchDrawResult(strategyId,record,draw){
  const game=record?.game||"fantasy5";
  if(game==="pick2")return calculatePick2WatchDrawResult(strategyId,record,draw);
  if(game==="pick3")return calculatePick3WatchDrawResult(strategyId,record,draw);
  if(game==="pick4")return calculatePick4WatchDrawResult(strategyId,record,draw);
  if(game==="pick5")return calculatePick5WatchDrawResult(strategyId,record,draw);
  const numbers=strategySnapshotNumbers(record);
  const cost=strategyEstimatedCost(record,numbers);
  const matchCounts=emptyMatchCounts(5);
  const prizeBreakdown={
    "5":{label:"Jackpot",plays:0,cash:0},
    "4":{label:"4 Match",plays:0,cash:0},
    "3":{label:"3 Match",plays:0,cash:0},
    "2":{label:"Free Ticket",plays:0,cash:0,free_tickets:0},
  };
  let cashWon=0;
  let freeTickets=0;
  let winningPlaysCount=0;
  let bestMatch=0;
  let largestPrize=0;
  const winningPlayDetails=[];
  numbers.forEach((play,index)=>{
    const match=countWhatIfMatch(game,play,draw?.numbers||[]);
    const key=String(Math.max(0,Math.min(5,match)));
    matchCounts[key]=(matchCounts[key]||0)+1;
    bestMatch=Math.max(bestMatch,match);
    if(match===2){
      freeTickets+=1;
      winningPlaysCount+=1;
      prizeBreakdown["2"].plays+=1;
      prizeBreakdown["2"].free_tickets+=1;
      winningPlayDetails.push({
        saved_line_index:index+1,
        saved_digits:play,
        official_base_digits:draw?.numbers||[],
        matched_numbers:fantasy5MatchedNumbers(play,draw?.numbers||[]),
        match_count:match,
        prize_type:fantasy5PrizeType(match),
        payout:0,
        final_payout:0,
        free_ticket:true,
        category:"free_ticket",
        draw_date:draw?.draw_date,
        draw_time:String(draw?.draw_time||"").toLowerCase(),
      });
      return;
    }
    if(match>=3){
      const prize=fantasy5PrizeForWatchDraw(draw,match);
      cashWon+=prize;
      largestPrize=Math.max(largestPrize,prize);
      winningPlaysCount+=1;
      prizeBreakdown[String(match)].plays+=1;
      prizeBreakdown[String(match)].cash+=prize;
      winningPlayDetails.push({
        saved_line_index:index+1,
        saved_digits:play,
        official_base_digits:draw?.numbers||[],
        matched_numbers:fantasy5MatchedNumbers(play,draw?.numbers||[]),
        match_count:match,
        prize_type:fantasy5PrizeType(match),
        payout:prize,
        final_payout:prize,
        free_ticket:false,
        category:"cash",
        draw_date:draw?.draw_date,
        draw_time:String(draw?.draw_time||"").toLowerCase(),
      });
    }
  });
  const net=cashWon-cost;
  return{
    id:["watch",strategyId,draw?.id||draw?.draw_date,draw?.draw_time].join(":"),
    user_id:null,
    strategy_id:strategyId,
    scheduled_draw_id:null,
    official_draw_id:draw?.id!=null?String(draw.id):null,
    official_draw_key:[game,draw?.draw_date,draw?.draw_time].join(":"),
    game,
    draw_date:draw?.draw_date,
    draw_time:String(draw?.draw_time||"").toLowerCase(),
    official_numbers:draw?.numbers||[],
    strategy_name:strategyDisplayName(strategyId,record),
    play_type:"Watch Results",
    tracking_mode:"watch",
    play_count:numbers.length,
    total_cost:summaryNumber(cost),
    cash_won:cashWon,
    net,
    roi:cost>0?(net/cost)*100:null,
    best_match:bestMatch,
    free_tickets:freeTickets,
    largest_prize:largestPrize,
    winning_plays_count:winningPlaysCount,
    match_counts:matchCounts,
    prize_breakdown:prizeBreakdown,
    winning_play_details:winningPlayDetails,
    result_source:"watch_snapshot",
    calculated_at:new Date().toISOString(),
  };
}
function summarizeWatchResultRows(rows=[]){
  const clean=Array.isArray(rows)?rows:[];
  const totalCost=clean.reduce((sum,row)=>sum+summaryNumber(row.total_cost),0);
  const cashWon=clean.reduce((sum,row)=>sum+summaryNumber(row.cash_won),0);
  const net=cashWon-totalCost;
  return{
    total_cost:totalCost,
    cash_won:cashWon,
    net,
    roi:totalCost>0?(net/totalCost)*100:null,
    best_match:clean.reduce((best,row)=>Math.max(best,summaryNumber(row.best_match)),0),
    largest_prize:clean.reduce((best,row)=>Math.max(best,summaryNumber(row.largest_prize)),0),
    free_tickets:clean.reduce((sum,row)=>sum+summaryNumber(row.free_tickets),0),
    draws_tracked:new Set(clean.map(row=>[row.draw_date,row.draw_time].join(":"))).size,
    winning_draws:clean.filter(row=>summaryNumber(row.cash_won)>0||summaryNumber(row.free_tickets)>0).length,
    latest:clean.sort((a,b)=>String((b.draw_date||"")+"-"+(b.draw_time||"")).localeCompare(String((a.draw_date||"")+"-"+(a.draw_time||""))))[0]||null,
  };
}
function buildWatchStrategyRows(strategyRecords=[],officialDraws=[],options={}){
  const windowId=options.windowId||"7";
  const drawFilter=options.drawFilter||"both";
  return strategyRecords.map(record=>{
    const game=record.game||"fantasy5";
    const eligibleDraws=watchEligibleOfficialDraws(officialDraws,game,windowId,drawFilter,record);
    const middayDraws=watchEligibleOfficialDraws(officialDraws,game,windowId,"midday",record);
    const eveningDraws=watchEligibleOfficialDraws(officialDraws,game,windowId,"evening",record);
    const rows=eligibleDraws.map(draw=>calculateWatchDrawResult(record.id,record,draw));
    const middayRows=middayDraws.map(draw=>calculateWatchDrawResult(record.id,record,draw));
    const eveningRows=eveningDraws.map(draw=>calculateWatchDrawResult(record.id,record,draw));
    const row=summarizeWatchResultRows(rows);
    const midday=summarizeWatchResultRows(middayRows);
    const evening=summarizeWatchResultRows(eveningRows);
    return{
      strategy_id:record.id,
      id:record.id,
      game,
      name:strategyDisplayName(record.id,record),
      row,
      rows,
      midday,
      evening,
      stronger:betterDrawTime(midday,evening),
    };
  });
}
function betterDrawTime(midday,evening){
  const mCost=summaryNumber(midday?.total_cost);
  const eCost=summaryNumber(evening?.total_cost);
  if(!mCost&&!eCost)return{label:"Needs live data",text:"Not enough Midday or Evening live results yet."};
  if(mCost&&!eCost)return{label:"Midday only",text:"Evening comparison will appear after this strategy tracks an Evening draw."};
  if(eCost&&!mCost)return{label:"Evening only",text:"Midday comparison will appear after this strategy tracks a Midday draw."};
  const mRoi=Number(midday?.roi);
  const eRoi=Number(evening?.roi);
  if(Number.isFinite(mRoi)&&Number.isFinite(eRoi)){
    if(eRoi>mRoi)return{label:"Evening",text:"Historically stronger in Evening based on your tracked results."};
    if(mRoi>eRoi)return{label:"Midday",text:"Historically stronger in Midday based on your tracked results."};
  }
  return{label:"Even",text:"Midday and Evening are currently even based on your tracked results."};
}
function latestResultStatus({latestRows=[],pendingEntries=[]}){
  if(latestRows.length)return"Your Results Are Ready";
  if((pendingEntries||[]).some(e=>e.status==="Waiting for official results"||e.status==="Official results delayed"))return"Awaiting Results";
  if((pendingEntries||[]).some(e=>e.status==="Drawing In Progress"||e.status==="Sales Closed"))return"Processing Results";
  return"No Scheduled Strategies";
}
function resultRowsSummary(rows=[]){
  const clean=Array.isArray(rows)?rows:[];
  return{
    strategies:new Set(clean.map(r=>r.strategy_id).filter(Boolean)).size,
    plays:clean.reduce((sum,r)=>sum+summaryNumber(r.play_count),0),
    cashWon:clean.reduce((sum,r)=>sum+summaryNumber(r.cash_won),0),
    freeTickets:clean.reduce((sum,r)=>sum+summaryNumber(r.free_tickets),0),
    bestMatch:clean.reduce((best,r)=>Math.max(best,summaryNumber(r.best_match)),0),
    largestPrize:clean.reduce((best,r)=>Math.max(best,summaryNumber(r.largest_prize)),0),
    net:clean.reduce((sum,r)=>sum+summaryNumber(r.net),0),
  };
}
function rowWinningValue(row){
  return summaryNumber(row?.cash_won)+summaryNumber(row?.free_tickets);
}
function winningResultRows(rows=[]){
  return(Array.isArray(rows)?rows:[])
    .filter(row=>rowWinningValue(row)>0)
    .sort(compareOfficialDrawRecency);
}
function formatWinningDate(row){
  return row?fmtShortDate(row.draw_date)+" "+drawTimeLabel(row):"";
}
function bestWinningDrawRow(rows=[]){
  return winningResultRows(rows).sort((a,b)=>{
    const cashDiff=summaryNumber(b.cash_won)-summaryNumber(a.cash_won);
    if(cashDiff)return cashDiff;
    const prizeDiff=summaryNumber(b.largest_prize)-summaryNumber(a.largest_prize);
    if(prizeDiff)return prizeDiff;
    const ticketDiff=summaryNumber(b.free_tickets)-summaryNumber(a.free_tickets);
    if(ticketDiff)return ticketDiff;
    const matchDiff=summaryNumber(b.best_match)-summaryNumber(a.best_match);
    if(matchDiff)return matchDiff;
    return compareOfficialDrawRecency(a,b);
  })[0]||null;
}
function bestWinningDrawLabel(rows=[]){
  const winners=winningResultRows(rows);
  const best=bestWinningDrawRow(rows);
  if(best)return"Best Winning Draw: "+formatWinningDate(best);
  return winners.length?"Winning Draw Summary":"No winning draws in this period";
}
function officialDrawForResultRow(row,officialRows=[]){
  if(!row)return null;
  if(Array.isArray(row.official_numbers)&&row.official_numbers.length){
    return{...row,numbers:row.official_numbers};
  }
  return(officialRows||[]).find(draw=>
    rowGame(draw)===rowGame(row)
    && String(draw.draw_date||"")===String(row.draw_date||"")
    && String(draw.draw_time||"").toLowerCase()===String(row.draw_time||"").toLowerCase()
  )||null;
}
function winningDatesLabel(rows=[],limit=3){
  const winners=winningResultRows(rows);
  if(!winners.length)return"No winning dates in this period";
  const labels=[...winners].reverse().slice(0,limit).map(formatWinningDate);
  return labels.join(", ")+(winners.length>limit?" + "+(winners.length-limit)+" more":"");
}
function watchInsightSummary(rows=[]){
  const allRows=[...(Array.isArray(rows)?rows:[])].sort((a,b)=>String((a.draw_date||"")+"-"+(a.draw_time||"")).localeCompare(String((b.draw_date||"")+"-"+(b.draw_time||""))));
  const winners=allRows.filter(row=>rowWinningValue(row)>0);
  const months=winners.reduce((acc,row)=>{
    const d=new Date(String(row.draw_date||"")+"T00:00:00");
    const key=Number.isFinite(d.getTime())?d.toLocaleString("en-US",{month:"short",year:"numeric"}):"Unknown";
    acc[key]=(acc[key]||0)+1;
    return acc;
  },{});
  const winningMonths=Object.entries(months).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([month,count])=>month+" ("+count+")").join(", ")||"No winning months";
  let longestLosingStreak=0;
  let current=0;
  allRows.forEach(row=>{
    if(rowWinningValue(row)>0){
      longestLosingStreak=Math.max(longestLosingStreak,current);
      current=0;
    }else{
      current+=1;
    }
  });
  longestLosingStreak=Math.max(longestLosingStreak,current);
  const winnerDates=[...new Set(winners.map(row=>row.draw_date).filter(Boolean))].sort();
  const gaps=[];
  for(let i=1;i<winnerDates.length;i++){
    const prev=new Date(winnerDates[i-1]+"T00:00:00");
    const next=new Date(winnerDates[i]+"T00:00:00");
    const gap=Math.round((next-prev)/86400000);
    if(Number.isFinite(gap)&&gap>=0)gaps.push(gap);
  }
  const averageGap=gaps.length?Math.round(gaps.reduce((sum,gap)=>sum+gap,0)/gaps.length)+" days":"Not enough wins";
  return{
    winningDates:winningDatesLabel(allRows,4),
    winningMonths,
    longestLosingStreak,
    averageGap,
  };
}
function resultLineCheckText(row,isWatchMode=false){
  const savedLines=summaryNumber(row?.play_count);
  if(!isWatchMode)return savedLines+" plays checked";
  const represented=summaryNumber(row?.represented_plays);
  const cost=summaryNumber(row?.total_cost);
  if(represented&&represented!==savedLines){
    return savedLines+" saved lines checked · "+represented+" represented plays · "+fmtMoney(cost)+" cost";
  }
  return savedLines+" saved lines checked · "+fmtMoney(cost)+" represented cost";
}
function resultCardTimestampLabel(value){
  if(!value)return "";
  const d=new Date(value);
  if(!Number.isFinite(d.getTime()))return "";
  return d.toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"});
}
function resultCardVerifiedInfo(row){
  const scheduledAt=Date.parse(row?.scheduled_at||"");
  const cutoffAt=Date.parse(row?.cutoff_at||"");
  if(!Number.isFinite(scheduledAt)||!Number.isFinite(cutoffAt))return null;
  if(scheduledAt>=cutoffAt)return null;
  const savedAt=scheduledAt;
  if(!Number.isFinite(savedAt))return null;
  return{label:"Verified before draw",savedAt:resultCardTimestampLabel(row?.scheduled_at)};
}
function snapshotActivationTimestamp(snapshot={}){
  const candidates=[
    snapshot?.versionActivatedAt,
    snapshot?.versionCreatedAt,
    snapshot?.activatedAt,
    snapshot?.createdAt,
    snapshot?.created_at,
  ];
  for(const value of candidates){
    const parsed=Date.parse(value||"");
    if(Number.isFinite(parsed))return new Date(parsed).toISOString();
  }
  return null;
}
function resultShareActivationInfo(row){
  const scheduledAt=Date.parse(row?.scheduled_at||"");
  const cutoffAt=Date.parse(row?.cutoff_at||"");
  if(!Number.isFinite(scheduledAt)||!Number.isFinite(cutoffAt))return{ok:false,reason:"missing commitment timing"};
  if(scheduledAt>=cutoffAt)return{ok:false,reason:"scheduled after cutoff"};
  const activationSource=row?.strategy_version_activated_at||row?.version_activated_at||snapshotActivationTimestamp(row?.frozen_snapshot||{})||row?.scheduled_at;
  const activationAt=Date.parse(activationSource||"");
  if(!Number.isFinite(activationAt))return{ok:false,reason:"missing version activation"};
  if(activationAt>=cutoffAt)return{ok:false,reason:"version activated after cutoff"};
  return{ok:true,activationAt:new Date(activationAt).toISOString(),scheduledAt:new Date(scheduledAt).toISOString(),cutoffAt:new Date(cutoffAt).toISOString()};
}
function resultOfficialDrawKey(game,drawDate,drawTime){
  const g=String(game||"").toLowerCase();
  const d=String(drawDate||"").slice(0,10);
  const rawTime=String(drawTime||"").toLowerCase();
  const t=rawTime?rawTime==="evening"?"evening":"midday":"";
  return g&&d&&t?[g,d,t].join(":"):"";
}
function officialDrawMatchesResult(row,official){
  if(!row||!official)return false;
  const rowTime=String(row.draw_time||"").toLowerCase().includes("eve")?"evening":"midday";
  const officialTime=String(official.draw_time||"").toLowerCase().includes("eve")?"evening":"midday";
  return String(rowGame(official)||official.game||"").toLowerCase()===String(rowGame(row)||row.game||"").toLowerCase()
    && String(official.draw_date||"").slice(0,10)===String(row.draw_date||"").slice(0,10)
    && officialTime===rowTime;
}
function resultHasExactOfficial(row){
  const expectedKey=resultOfficialDrawKey(rowGame(row)||row?.game,row?.draw_date,row?.draw_time);
  const officialKey=String(row?.official_draw_key||"").toLowerCase();
  return !!(
    row?.official_draw_id
    && expectedKey
    && officialKey===expectedKey
    && Array.isArray(row?.official_numbers)
    && row.official_numbers.length>0
  );
}
function resultFrozenRawEntries(snapshot){
  return Array.isArray(snapshot?.numbers)?snapshot.numbers:Array.isArray(snapshot?.plays)?snapshot.plays:[];
}
function resultFrozenNumberEntries(snapshot){
  const raw=resultFrozenRawEntries(snapshot);
  return raw.map(item=>{
    if(Array.isArray(item))return item;
    if(Array.isArray(item?.numbers))return item.numbers;
    return null;
  }).filter(Boolean);
}
function resultFrozenDeclaredCount(snapshot){
  const candidates=[snapshot?.playCount,snapshot?.play_count,snapshot?.totalPlays,snapshot?.total_plays];
  for(const value of candidates){
    const count=Number(value);
    if(Number.isInteger(count)&&count>=0)return count;
  }
  return null;
}
function resultShareIneligibleReason(row){
  if(!row?.scheduled_draw_id)return"missing scheduled draw";
  if(!row?.frozen_snapshot)return"missing frozen snapshot";
  const rawFrozen=resultFrozenRawEntries(row.frozen_snapshot);
  const frozenNumbers=resultFrozenNumberEntries(row.frozen_snapshot);
  if(!rawFrozen.length||!frozenNumbers.length)return"frozen lineup missing";
  if(rawFrozen.length!==frozenNumbers.length)return"frozen lineup contains invalid plays";
  const declared=resultFrozenDeclaredCount(row.frozen_snapshot);
  if(declared!==null&&declared!==frozenNumbers.length)return"frozen count mismatch";
  const scoredCount=Number(row?.play_count);
  if(Number.isFinite(scoredCount)&&scoredCount>=0&&scoredCount!==frozenNumbers.length)return"scored count mismatch";
  const frozenCost=Number(row?.frozen_snapshot?.cost);
  if(!Number.isFinite(frozenCost)||frozenCost<0)return"frozen cost invalid";
  const activation=resultShareActivationInfo(row);
  if(!activation.ok)return activation.reason;
  if(!resultHasExactOfficial(row))return"official result pending";
  return"";
}
function resultShareEligible(row){
  return !resultShareIneligibleReason(row);
}
function resultShareStatusText(row){
  const reason=resultShareIneligibleReason(row);
  if(!reason)return"";
  if(reason==="official result pending")return"Official result pending";
  if(reason.includes("frozen"))return"Result unavailable - "+reason;
  return"Result unavailable - "+reason;
}
function sameNumberMultiset(a=[],b=[]){
  if(!Array.isArray(a)||!Array.isArray(b)||a.length!==b.length)return false;
  const left=a.map(n=>String(Number(n))).sort().join("|");
  const right=b.map(n=>String(Number(n))).sort().join("|");
  return left===right;
}
function exactNumberOrder(a=[],b=[]){
  return Array.isArray(a)&&Array.isArray(b)&&a.length===b.length&&a.every((n,i)=>Number(n)===Number(b[i]));
}
function nearMissExplanationForFrozenResult(row){
  const game=rowGame(row);
  if(!isPickGame(game))return "";
  const official=Array.isArray(row?.official_numbers)?row.official_numbers.map(n=>Number(n)):[];
  if(!official.length)return "";
  const record={game,snapshot:row?.frozen_snapshot||{}};
  const entries=strategySnapshotPlayEntries(record);
  const detailLines=pick4WinningDetailsForRow(row);
  const paidLineKeys=new Set(detailLines.map(path=>String(path.saved_line_index)));
  const found=entries.find((entry,index)=>{
    const rules=entry?.pickRules||{};
    const playType=String(rules.playType||rules.play_type||"").toLowerCase();
    if(playType!=="straight")return false;
    if(paidLineKeys.has(String(index+1)))return false;
    const nums=entry.numbers||[];
    return sameNumberMultiset(nums,official)&&!exactNumberOrder(nums,official);
  });
  if(!found)return "";
  return "I played "+digitsText(found.numbers)+" straight. It came "+digitsText(official)+". The digits matched, but not in exact order, so this play paid $0.";
}
function resultOutcomeDisplay(state){
  const key=String(state||"").toLowerCase();
  if(key==="up"||key==="positive")return{label:"Came out ahead",color:"#008577"};
  if(key==="hit_but_down"||key==="hit-but-down")return{label:"Cash hit",color:"#c8860a"};
  if(key==="break_even"||key==="break-even")return{label:"Broke even",color:"#002855"};
  if(key==="near_miss"||key==="near-miss")return{label:"So close",color:"#c8860a"};
  if(key==="win")return{label:"Came out ahead",color:"#008577"};
  return{label:"Nothing",color:"#002855"};
}
function publicSnapshotOutcomeState(snapshot={}){
  const cash=summaryNumber(snapshot.amountWon);
  const cost=summaryNumber(snapshot.amountPlayed);
  const net=cash-cost;
  const cents=Math.round(net*100);
  if(cents>0)return"up";
  if(cents===0)return"nothing";
  if(cash>0&&cents<0)return"hit_but_down";
  if(String(snapshot.verdictState||"").toLowerCase()==="near_miss")return"near_miss";
  return"nothing";
}
function publicSnapshotOutcomeText(snapshot={}){
  const state=publicSnapshotOutcomeState(snapshot);
  if(state==="up")return"Came out ahead.";
  if(state==="nothing"&&Math.round((summaryNumber(snapshot.amountWon)-summaryNumber(snapshot.amountPlayed))*100)===0)return"Broke even.";
  if(state==="hit_but_down"){
    const best=summaryNumber(snapshot.bestMatch);
    const total=Array.isArray(snapshot.officialNumbers)&&snapshot.officialNumbers.length?snapshot.officialNumbers.length:(gameNumberConfig(snapshot.game||"fantasy5").count||5);
    const hit=best>0?"Hit "+best+"/"+total:"Cash hit";
    return hit+" - down "+fmtMoney(Math.abs(summaryNumber(snapshot.amountWon)-summaryNumber(snapshot.amountPlayed)))+".";
  }
  if(state==="near_miss")return snapshot.verdictText||"So close.";
  return"Not this one. I post the misses too.";
}
function resultNetValue(row){
  const net=Number(row?.net);
  if(Number.isFinite(net))return net;
  return summaryNumber(row?.cash_won)-summaryNumber(row?.total_cost);
}
function resultOutcomeState(row){
  const cash=summaryNumber(row?.cash_won);
  const net=resultNetValue(row);
  const cents=Math.round(net*100);
  if(cents>0)return"up";
  if(cents===0)return"nothing";
  if(cash>0&&cents<0)return"hit_but_down";
  if(nearMissExplanationForFrozenResult(row))return"near_miss";
  return"nothing";
}
function resultOutcomeText(row){
  const state=resultOutcomeState(row);
  if(state==="up")return"Came out ahead.";
  if(state==="nothing"&&Math.round(resultNetValue(row)*100)===0)return"Broke even.";
  if(state==="hit_but_down"){
    const game=rowGame(row);
    const total=gameNumberConfig(game).count||5;
    const best=summaryNumber(row?.best_match);
    const hit=best>0?"Hit "+best+"/"+total:"Cash hit";
    return hit+" - down "+fmtMoney(Math.abs(resultNetValue(row)))+".";
  }
  if(state==="near_miss")return"So close. "+nearMissExplanationForFrozenResult(row);
  return"Not this one. I post the misses too.";
}
function resultCardVerdict(row){
  const state=resultOutcomeState(row);
  if(state==="near_miss"){
    const game=rowGame(row);
    const best=summaryNumber(row?.best_match);
    const total=gameNumberConfig(game).count||5;
    return best>0?"Best match "+best+" of "+total:"So close";
  }
  if(state==="nothing")return Math.round(resultNetValue(row)*100)===0?"Broke even":"Not this one";
  return resultOutcomeDisplay(state).label;
}
function publicSnapshotCardVerdict(snapshot={}){
  const state=publicSnapshotOutcomeState(snapshot);
  if(state==="near_miss"){
    const best=summaryNumber(snapshot.bestMatch);
    const total=gameNumberConfig(snapshot.game||"fantasy5").count||5;
    return best>0?"Best match "+best+" of "+total:"So close";
  }
  if(state==="nothing")return Math.round((summaryNumber(snapshot.amountWon)-summaryNumber(snapshot.amountPlayed))*100)===0?"Broke even":"Not this one";
  return resultOutcomeDisplay(state).label;
}
function resultCardSharePayload(row,gameConfig,publicLink=""){
  const game=rowGame(row);
  const snapshot=row?.frozen_snapshot||{};
  const record={game,snapshot};
  const numbers=strategySnapshotNumbers(record);
  const entries=isPickGame(game)?strategySnapshotPlayEntries(record):[];
  const firstRules=entries[0]?.pickRules||snapshot?.pickRules||{};
  const mixedRules=isPickGame(game)&&entries.some(entry=>{
    const rules=entry?.pickRules||{};
    return String(rules.playType||"")!==String(firstRules.playType||"")
      || Number(rules.wager||0)!==Number(firstRules.wager||0)
      || Number(rules.multiple||1)!==Number(firstRules.multiple||1)
      || !!rules.fireball!==!!firstRules.fireball;
  });
  const verified=resultCardVerifiedInfo(row);
  const verdict=resultCardVerdict(row);
  const verdictText=resultOutcomeText(row);
  const officialNumbers=Array.isArray(row?.official_numbers)?row.official_numbers:[];
  const drawLabel=[fmtShortDate(row?.draw_date),drawFilterLabel(row?.draw_time)].filter(Boolean).join(" ");
  const playTypeLabel=isPickGame(game)
    ?(mixedRules?"Mixed Pick rules":formatPickRulesSummary(game,firstRules))
    :"$1 Fantasy 5 play";
  const fireballSelected=isPickGame(game)&&(mixedRules?entries.some(entry=>entry?.pickRules?.fireball):!!firstRules.fireball);
  const fireballStatus=row?.fireball_status||row?.prize_breakdown?.fireball_status||"";
  const rowFireballs=isPickGame(game)?entries.map(entry=>!!entry?.pickRules?.fireball):[];
  const uniformFireball=rowFireballs.length>0&&rowFireballs.every(value=>value===rowFireballs[0]);
  const firstMultiple=Math.max(1,Number(firstRules.multiple)||1);
  const firstWager=Number(firstRules.wager)||1;
  const fireballDetail=uniformFireball?(rowFireballs[0]?"Fireball included":"Fireball not included"):(rowFireballs.some(Boolean)?"Mixed Fireball":"Fireball not included");
  const resultRulesLine=isPickGame(game)
    ?"Play details: "+(mixedRules?"Mixed Pick rules":moneyLabel(firstWager)+" "+(firstRules.playType||"Straight"))+" · "+(mixedRules?"line-level multiples":"each number x"+firstMultiple)+" · "+fireballDetail
    :playTypeLabel;
  return shareCardPayload({
    type:"result",
    title:"LottoTracker Result Card",
    strategyName:snapshot?.strategyName||snapshot?.name||row?.strategy_name||row?.strategy_id||"Strategy",
    game:gameLabel(game),
    status:verdict,
    afterDraw:true,
    resultCard:true,
    watchDraw:false,
    drawTime:drawLabel||"Completed draw",
    playCount:summaryNumber(row?.play_count)||numbers.length,
    numbersPreview:numbers,
    numbersTotal:numbers.length,
    numbersShown:Math.min(50,numbers.length),
    representedPlayCount:row?.represented_plays||row?.play_count||numbers.length,
    playTypeLabel,
    rulesSummaryLine:resultRulesLine,
    rowFireballs,
    estimatedCost:fmtMoney(row?.total_cost),
    won:fmtMoney(row?.cash_won),
    net:netText(row?.net),
    bestMatch:(summaryNumber(row?.best_match)||0)+" / "+(gameConfig?.count||officialNumbers.length||5),
    freeTickets:summaryNumber(row?.free_tickets),
    winningPlayCount:summaryNumber(row?.winning_plays_count),
    largestPrize:fmtMoney(row?.largest_prize),
    officialNumbers,
    officialFireball:row?.official_fireball_digit,
    verdict,
    nearMiss:verdictText,
    verificationLabel:verified?.label||"",
    verificationTime:verified?.savedAt||"",
    link:publicLink||shareBaseUrl()+"/results?mode=live&game="+encodeURIComponent(game)+"&strategy="+encodeURIComponent(row?.strategy_id||""),
    fireballNote:shareFireballNote(game,fireballSelected,fireballStatus),
    fireballStatus,
    note:"I post the misses too.",
    ctaLabel:"View the result on LottoTracker",
  });
}
function publicResultSnapshotToCardPayload(snapshot={}){
  const game=snapshot.game||"fantasy5";
  const numbers=Array.isArray(snapshot.numbersPlayed)?snapshot.numbersPlayed:[];
  const rules=Array.isArray(snapshot.playRules)?snapshot.playRules:[];
  const firstRules=rules[0]||{};
  const mixedRules=isPickGame(game)&&rules.some(rule=>
    String(rule.playType||"")!==String(firstRules.playType||"")
    || Number(rule.wager||0)!==Number(firstRules.wager||0)
    || Number(rule.multiple||1)!==Number(firstRules.multiple||1)
    || !!rule.fireball!==!!firstRules.fireball
  );
  const playTypeLabel=isPickGame(game)
    ?(mixedRules?"Mixed Pick rules":formatPickRulesSummary(game,firstRules))
    :"$1 Fantasy 5 play";
  const hasFireball=isPickGame(game)&&(mixedRules?rules.some(rule=>!!rule.fireball):!!firstRules.fireball);
  const fireballStatus=snapshot.fireballStatus||snapshot.fireball_status||snapshot.prizeBreakdown?.fireball_status||"";
  const rowFireballs=isPickGame(game)?rules.map(rule=>!!rule?.fireball):[];
  const uniformFireball=rowFireballs.length>0&&rowFireballs.every(value=>value===rowFireballs[0]);
  const firstMultiple=Math.max(1,Number(firstRules.multiple)||1);
  const firstWager=Number(firstRules.wager)||1;
  const fireballDetail=uniformFireball?(rowFireballs[0]?"Fireball included":"Fireball not included"):(rowFireballs.some(Boolean)?"Mixed Fireball":"Fireball not included");
  const resultRulesLine=isPickGame(game)
    ?"Play details: "+(mixedRules?"Mixed Pick rules":moneyLabel(firstWager)+" "+(firstRules.playType||"Straight"))+" · "+(mixedRules?"line-level multiples":"each number x"+firstMultiple)+" · "+fireballDetail
    :playTypeLabel;
  const link=snapshot.link||shareBaseUrl()+"/s/result/"+encodeURIComponent(snapshot.shareId||"");
  const snapshotOutcomeState=publicSnapshotOutcomeState(snapshot);
  const verdictDisplay=resultOutcomeDisplay(snapshotOutcomeState);
  const verdictLabel=publicSnapshotCardVerdict(snapshot);
  return shareCardPayload({
    type:"result",
    title:"LottoTracker Result Card",
    strategyName:snapshot.strategyName||"Strategy",
    game:snapshot.gameLabel||gameLabel(game),
    status:verdictLabel||verdictDisplay.label,
    afterDraw:true,
    resultCard:true,
    drawTime:[fmtShortDate(snapshot.drawDate),drawFilterLabel(snapshot.drawTime)].filter(Boolean).join(" "),
    playCount:snapshot.totalPlays||numbers.length,
    numbersPreview:numbers,
    numbersTotal:numbers.length,
    numbersShown:Math.min(50,numbers.length),
    representedPlayCount:snapshot.totalPlays||numbers.length,
    playTypeLabel,
    rulesSummaryLine:resultRulesLine,
    rowFireballs,
    estimatedCost:fmtMoney(snapshot.amountPlayed),
    won:fmtMoney(snapshot.amountWon),
    net:netText((Number(snapshot.amountWon)||0)-(Number(snapshot.amountPlayed)||0)),
    bestMatch:(Number(snapshot.bestMatch)||0)+" / "+(gameNumberConfig(game).count||5),
    freeTickets:Number(snapshot.freeTickets)||0,
    winningPlayCount:Number(snapshot.winningPlayCount)||0,
    largestPrize:fmtMoney(snapshot.largestPrize),
    officialNumbers:snapshot.officialNumbers||[],
    officialFireball:snapshot.officialFireballDigit,
    verdict:verdictLabel||verdictDisplay.label,
    nearMiss:publicSnapshotOutcomeText(snapshot),
    verificationLabel:snapshot.validPreDrawProofAt?"Verified before draw":"",
    verificationTime:snapshot.validPreDrawProofAt?resultCardTimestampLabel(snapshot.validPreDrawProofAt):"",
    fireballNote:shareFireballNote(game,hasFireball,fireballStatus),
    fireballStatus,
    note:"I post the misses too.",
    ctaLabel:"View the result on LottoTracker",
    link,
  });
}
function formatOfficialTimestamp(value){
  if(!value)return "Not recorded";
  const d=new Date(value);
  if(!Number.isFinite(d.getTime()))return "Not recorded";
  return d.toLocaleString("en-US",{timeZone:EASTERN_TIME_ZONE,month:"short",day:"numeric",hour:"numeric",minute:"2-digit"});
}
function officialSourceLabel(draw){
  const value=draw?.official_payload_source||draw?.source||draw?.official_source_url||"official";
  if(String(value).includes("florida_lottery"))return "Florida Lottery";
  if(String(value).includes("floridalottery.com"))return "Florida Lottery";
  if(value==="api")return "Florida Lottery";
  return String(value||"Official source");
}
function latestOfficialDrawForGame(draws=[],game){
  return latestOfficialDrawForSelection(draws,game,"both");
}
function officialNumbersText(draw){
  if(!draw||!Array.isArray(draw.numbers)||!draw.numbers.length)return "Waiting";
  return draw.numbers.join("-");
}
function mergeLatestOfficialRows(draws=[],fallbackRows=[]){
  const byGame=new Map();
  [...(draws||[]),...(fallbackRows||[])].forEach(row=>{
    const game=rowGame(row);
    if(!game||!isOfficialDraw(row)||!drawHasExpectedNumbers(row))return;
    const current=byGame.get(game);
    if(!current||compareOfficialDrawRecency(row,current)<0)byGame.set(game,row);
  });
  return byGame;
}
function latestStatusRowForEntry(rows=[],entry=null){
  if(!entry)return null;
  return(rows||[]).find(row=>
    rowGame(row)===entry.game&&
    row?.draw_date===entry.draw_date&&
    drawTimeKey(row?.draw_time)===drawTimeKey(entry.draw_time)
  )||null;
}
function nextOfficialCheckTime(lastChecked){
  const parsed=Date.parse(lastChecked||"");
  const base=Number.isFinite(parsed)?parsed:Date.now();
  return new Date(Math.max(Date.now()+OFFICIAL_POLL_INTERVAL_MS,base+OFFICIAL_POLL_INTERVAL_MS)).toISOString();
}
function officialHealthNextCheck(row,lastChecked){
  return row?.official_next_check_at||nextOfficialCheckTime(lastChecked);
}
function drawInstantForRow(row){
  const game=rowGame(row);
  const drawDate=String(row?.draw_date||"").slice(0,10);
  const drawTime=drawTimeKey(row?.draw_time);
  const slot=(NEXT_DRAW_SCHEDULE[game]||[]).find(item=>drawTimeKey(item.label)===drawTime);
  if(!slot||!/^\d{4}-\d{2}-\d{2}$/.test(drawDate))return NaN;
  return easternDateTime(drawDate,slot.draw).getTime();
}
function dashboardEntryForGame(entries=[],game,row=null,now=new Date()){
  const nowMs=now instanceof Date?now.getTime():Date.parse(now);
  const latestOfficialAt=drawInstantForRow(row);
  const today=easternIsoDate(now instanceof Date?now:new Date(now));
  const sorted=(entries||[]).filter(entry=>entry.game===game).sort((a,b)=>a.sortAt-b.sortAt);
  const activeStatuses=new Set(["Drawing In Progress","Waiting for official results","Official results delayed"]);
  const active=sorted
    .filter(entry=>activeStatuses.has(entry.status)&&entry.sortAt<=nowMs)
    .filter(entry=>Number.isFinite(latestOfficialAt)?entry.sortAt>latestOfficialAt:entry.draw_date===today)
    .sort((a,b)=>b.sortAt-a.sortAt)[0]||null;
  const upcoming=sorted.find(entry=>entry.sortAt>nowMs)||null;
  return active||upcoming||sorted[0]||null;
}
function resultCardStatusLabel(entry,row){
  if(entry?.status==="Official results delayed")return"Verification delayed";
  if(entry&&["Drawing In Progress","Waiting for official results"].includes(entry.status))return"Confirming result";
  if(row)return"Official";
  return"Import issue";
}
function nextDrawLine(entry){
  if(!entry)return"Next draw unavailable";
  return"Next: "+drawTimeLabel(entry)+" draw in "+entry.countdown;
}
function LatestOfficialResultsCard({draws=[],drawStatus}){
  const games=launchReadyGames().filter(game=>PICK_GAME_IDS.includes(game.id)||game.id==="fantasy5");
  const[latestFallbackRows,setLatestFallbackRows]=useState([]);
  useEffect(()=>{
    let cancelled=false;
    async function loadLatestOfficialFallbacks(){
      try{
        const rows=(await Promise.all(games.map(async game=>{
          const r=await fetch(`/api/draws?game=${encodeURIComponent(game.id)}&limit=20`,{cache:"no-store"});
          if(!r.ok)return[];
          const data=await r.json();
          return Array.isArray(data)?data:[];
        }))).flat();
        if(!cancelled)setLatestFallbackRows(rows);
      }catch{
        if(!cancelled)setLatestFallbackRows([]);
      }
    }
    loadLatestOfficialFallbacks();
    return()=>{cancelled=true;};
  },[]);
  const latestByGame=mergeLatestOfficialRows(draws,latestFallbackRows);
  const statusRows=[...(draws||[]),...(latestFallbackRows||[])];
  const allGameStatus=getDrawStatusService(games.map(game=>game.id),drawStatus?.now||new Date(),statusRows);
  const pendingEntries=allGameStatus.entries||[];
  return(
    <div className="panel" style={{marginBottom:18}}>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:10,marginBottom:12}}>
        <div>
          <span className="sec-title" style={{fontSize:22}}>Latest Official Results</span>
          <div style={{fontSize:13,color:"#555",marginTop:4}}>Read-only import status from the official Florida Lottery source.</div>
        </div>
        <span className="sec-badge">Official source</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:10}}>
        {games.map(game=>{
          const row=latestByGame.get(game.id)||null;
          const pending=dashboardEntryForGame(pendingEntries,game.id,row,allGameStatus.now||new Date());
          const pendingRow=latestStatusRowForEntry(statusRows,pending);
          const lastChecked=pendingRow?.official_checked_at||pendingRow?.fetched_at||pendingRow?.official_result_at||null;
          const nextPoll=pending&&["Drawing In Progress","Waiting for official results","Official results delayed"].includes(pending.status)?officialHealthNextCheck(pendingRow,lastChecked):null;
          const confirming=!!(pending&&["Drawing In Progress","Waiting for official results","Official results delayed"].includes(pending.status));
          const statusLabel=resultCardStatusLabel(confirming?pending:null,row);
          return(
            <div key={game.id} style={{border:"1px solid #edf1f0",borderRadius:10,padding:11,background:"#fff",display:"grid",gap:7}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"center"}}>
                <strong style={{color:"#002855",fontSize:15}}>{game.label}</strong>
                <span className="sec-badge" style={{background:statusLabel==="Official"?"#008577":statusLabel==="Verification delayed"?"#c8860a":statusLabel==="Import issue"?"#c8102e":"#005bbb"}}>{statusLabel}</span>
              </div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:900,color:"#002855",lineHeight:1}}>
                {officialNumbersText(row)}
              </div>
              {PICK_GAME_IDS.includes(game.id)&&<div style={{fontSize:12,color:"#555",fontWeight:800}}>Fireball: {row?.fireball_digit??"Waiting"}</div>}
              <div style={{display:"grid",gap:4,fontSize:12,color:"#555",lineHeight:1.35}}>
                <div><strong style={{color:"#002855"}}>Draw:</strong> {row?fmtShortDate(row.draw_date)+" "+drawTimeLabel(row):"No confirmed official result stored yet"}</div>
                <div><strong style={{color:"#002855"}}>Source:</strong> {row?officialSourceLabel(row):"Florida Lottery"}</div>
                {confirming?(
                  <div style={{background:"#f7fbff",border:"1px solid #d7e9ff",borderRadius:8,padding:"8px 9px",display:"grid",gap:3}}>
                    <div style={{fontWeight:900,color:"#002855"}}>{statusLabel==="Verification delayed"?"Verification delayed for":"Confirming the"} {fmtShortDate(pending.draw_date)} {drawTimeLabel(pending)} result...</div>
                    <div>
                      <strong style={{color:"#002855"}}>Last checked:</strong> {lastChecked?formatOfficialTimestamp(lastChecked):"Not checked yet"}{" "}
                      <strong style={{color:"#002855"}}>Next check:</strong> {formatOfficialTimestamp(nextPoll)}
                    </div>
                    {pendingRow?.official_last_error&&<div><strong style={{color:"#c8102e"}}>Import issue:</strong> {pendingRow.official_last_error}</div>}
                    {pendingRow?.official_stale_warning_at&&<div><strong style={{color:"#c8102e"}}>Stale warning:</strong> Missing beyond expected publication window</div>}
                  </div>
                ):(
                  <div><strong style={{color:"#002855"}}>{nextDrawLine(pending).split(":")[0]}:</strong>{nextDrawLine(pending).slice(nextDrawLine(pending).indexOf(":")+1)}</div>
                )}
                {!confirming&&row&&(
                  <div><strong style={{color:"#002855"}}>Imported:</strong> {formatOfficialTimestamp(row?.fetched_at||row?.official_result_at)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function digitsText(value=[]){
  return Array.isArray(value)?value.join("-"):"";
}
function pick4WinningDetailsForRow(row){
  return Array.isArray(row?.winning_play_details)?row.winning_play_details:[];
}
function summarizePick4WinningDetails(rows=[]){
  const details=(rows||[]).flatMap(row=>pick4WinningDetailsForRow(row).map(path=>({
    ...path,
    draw_date:row.draw_date,
    draw_time:row.draw_time,
  })));
  const playTypes={};
  const topLines={};
  let fireballWins=0;
  let fireballCash=0;
  details.forEach(path=>{
    const type=path.play_type||"Unknown";
    if(!playTypes[type])playTypes[type]={play_type:type,wins:0,cash:0};
    playTypes[type].wins+=1;
    playTypes[type].cash+=summaryNumber(path.final_payout);
    if(path.category==="fireball"||String(path.win_path||"").toLowerCase().includes("fireball")){
      fireballWins+=1;
      fireballCash+=summaryNumber(path.final_payout);
    }
    const lineKey=[path.saved_line_index,digitsText(path.saved_digits),type].join("|");
    if(!topLines[lineKey]){
      topLines[lineKey]={
        saved_line_index:path.saved_line_index,
        saved_digits:path.saved_digits,
        play_type:type,
        wins:0,
        cash:0,
        best_payout:0,
        best_draw:null,
      };
    }
    topLines[lineKey].wins+=1;
    topLines[lineKey].cash+=summaryNumber(path.final_payout);
    if(summaryNumber(path.final_payout)>summaryNumber(topLines[lineKey].best_payout)){
      topLines[lineKey].best_payout=summaryNumber(path.final_payout);
      topLines[lineKey].best_draw=[path.draw_date,drawFilterLabel(path.draw_time)].filter(Boolean).join(" ");
    }
  });
  return{
    playTypes:Object.values(playTypes).sort((a,b)=>summaryNumber(b.cash)-summaryNumber(a.cash)||summaryNumber(b.wins)-summaryNumber(a.wins)),
    fireballWins,
    fireballCash,
    topLines:Object.values(topLines).sort((a,b)=>summaryNumber(b.cash)-summaryNumber(a.cash)||summaryNumber(b.wins)-summaryNumber(a.wins)).slice(0,5),
    winningDraws:new Set((rows||[]).filter(row=>summaryNumber(row.cash_won)>0).map(row=>[row.draw_date,row.draw_time].join(":"))).size,
    totalWinningTickets:details.length,
    totalPathCash:details.reduce((sum,path)=>sum+summaryNumber(path.final_payout),0),
  };
}
function fantasy5WinningDetailsForRow(row){
  return Array.isArray(row?.winning_play_details)?row.winning_play_details:[];
}
function summarizeFantasy5WinningDetails(rows=[]){
  const details=(rows||[]).flatMap(row=>fantasy5WinningDetailsForRow(row).map(path=>({
    ...path,
    draw_date:row.draw_date,
    draw_time:row.draw_time,
  })));
  const tiers={};
  const topLines={};
  details.forEach(path=>{
    const match=summaryNumber(path.match_count);
    const key=String(match);
    if(!tiers[key])tiers[key]={match_count:match,label:match+" of 5",wins:0,cash:0,free_tickets:0};
    tiers[key].wins+=1;
    tiers[key].cash+=summaryNumber(path.final_payout||path.payout);
    if(path.free_ticket)tiers[key].free_tickets+=1;
    const lineKey=[path.saved_line_index,digitsText(path.saved_digits)].join("|");
    if(!topLines[lineKey]){
      topLines[lineKey]={
        saved_line_index:path.saved_line_index,
        saved_digits:path.saved_digits,
        wins:0,
        cash:0,
        free_tickets:0,
        best_match:0,
        best_cash:0,
        best_draw:"",
      };
    }
    topLines[lineKey].wins+=1;
    topLines[lineKey].cash+=summaryNumber(path.final_payout||path.payout);
    if(path.free_ticket)topLines[lineKey].free_tickets+=1;
    const cash=summaryNumber(path.final_payout||path.payout);
    const matchCount=summaryNumber(path.match_count);
    if(matchCount>topLines[lineKey].best_match||cash>topLines[lineKey].best_cash){
      topLines[lineKey].best_match=matchCount;
      topLines[lineKey].best_cash=Math.max(topLines[lineKey].best_cash,cash);
      topLines[lineKey].best_draw=[path.draw_date,drawFilterLabel(path.draw_time)].filter(Boolean).join(" ");
    }
  });
  const cashDetails=details.filter(path=>summaryNumber(path.final_payout||path.payout)>0);
  const freeDetails=details.filter(path=>path.free_ticket);
  return{
    tiers:Object.values(tiers).sort((a,b)=>summaryNumber(b.cash)-summaryNumber(a.cash)||summaryNumber(b.wins)-summaryNumber(a.wins)),
    topLines:Object.values(topLines).sort((a,b)=>summaryNumber(b.cash)-summaryNumber(a.cash)||summaryNumber(b.wins)-summaryNumber(a.wins)).slice(0,8),
    winningDraws:new Set((rows||[]).filter(row=>summaryNumber(row.cash_won)>0||summaryNumber(row.free_tickets)>0).map(row=>[row.draw_date,row.draw_time].join(":"))).size,
    cashWinningTickets:cashDetails.length,
    freeTicketWins:freeDetails.length,
    totalCash:cashDetails.reduce((sum,path)=>sum+summaryNumber(path.final_payout||path.payout),0),
    bestMatch:details.reduce((max,path)=>Math.max(max,summaryNumber(path.match_count)),0),
    largestPrize:cashDetails.reduce((max,path)=>Math.max(max,summaryNumber(path.final_payout||path.payout)),0),
  };
}
function simpleBarPercent(value,max){
  const n=Math.abs(Number(value)||0);
  const m=Math.max(1,Math.abs(Number(max)||0));
  return Math.max(4,Math.min(100,(n/m)*100));
}
function readableBreakdown(value,label){
  if(!value||typeof value!=="object")return label+" will appear after this draw has winning activity.";
  const entries=Object.entries(value).filter(([,v])=>{
    if(v&&typeof v==="object")return summaryNumber(v.cash)>0||summaryNumber(v.plays)>0||summaryNumber(v.free_tickets)>0;
    return summaryNumber(v)>0;
  });
  if(!entries.length)return "No "+label.toLowerCase()+" recorded for this draw.";
  return entries.map(([key,v])=>{
    const cleanKey=String(key).replace(/_/g," ");
    if(v&&typeof v==="object"){
      const pieces=[];
      if(summaryNumber(v.plays)>0)pieces.push(summaryNumber(v.plays)+" plays");
      if(summaryNumber(v.cash)>0)pieces.push(fmtMoney(summaryNumber(v.cash)));
      if(summaryNumber(v.free_tickets)>0)pieces.push(summaryNumber(v.free_tickets)+" free tickets");
      return cleanKey+" "+pieces.join(", ");
    }
    return cleanKey+" "+v;
  }).join(" - ");
}
const WHAT_IF_HISTORY_WINDOWS=[
  {id:"today",label:"Today",days:0},
  {id:"7",label:"Last 7 Days",days:7},
  {id:"30",label:"Last 30 Days",days:30},
  {id:"90",label:"Last 90 Days",days:90},
  {id:"365",label:"Last 365 Days",days:365},
  {id:"1095",label:"Pro: Last 3 Years",days:1095,pro:true},
];
function drawHasExpectedNumbers(draw){
  const cfg=gameNumberConfig(rowGame(draw));
  return Array.isArray(draw?.numbers)&&draw.numbers.length===cfg.count;
}
function filterDrawsByDays(draws,days){
  if(days==null)return draws||[];
  const today=new Date();
  today.setHours(0,0,0,0);
  const cutoff=new Date(today);
  cutoff.setDate(today.getDate()-days);
  const cutoffIso=cutoff.toISOString().slice(0,10);
  const todayIso=today.toISOString().slice(0,10);
  return(draws||[]).filter(d=>{
    const key=d.draw_date||String(d.date||"").slice(0,10);
    return days===0?key===todayIso:key>=cutoffIso;
  });
}
function filterDrawsByCadence(draws,cadence="twice"){
  const rows=[...(draws||[])].sort((a,b)=>{
    const ad=(a.draw_date||"")+"-"+(a.draw_time==="evening"?"2":"1");
    const bd=(b.draw_date||"")+"-"+(b.draw_time==="evening"?"2":"1");
    return bd.localeCompare(ad);
  });
  if(cadence!=="once")return rows;
  const byDate=new Map();
  rows.forEach(d=>{
    const key=d.draw_date||String(d.date||"").slice(0,10);
    const existing=byDate.get(key);
    if(!existing||d.draw_time==="evening")byDate.set(key,d);
  });
  return[...byDate.values()];
}
function sameMultiset(a=[],b=[]){
  if(a.length!==b.length)return false;
  const left=[...a].sort((x,y)=>x-y).join("-");
  const right=[...b].sort((x,y)=>x-y).join("-");
  return left===right;
}
function countWhatIfMatch(game,numbers,drawNumbers){
  if(!Array.isArray(numbers)||!Array.isArray(drawNumbers))return 0;
  if(isPickGame(game)){
    if(numbers.length!==drawNumbers.length)return 0;
    if(sameMultiset(numbers,drawNumbers))return numbers.length;
    return numbers.reduce((sum,n,i)=>sum+(n===drawNumbers[i]?1:0),0);
  }
  return cntMatch(numbers,drawNumbers);
}
function drawFireballDigit(draw){
  const direct=draw?.fireball_digit ?? draw?.fireballDigit ?? draw?.pick_result_summary?.fireball_digit ?? draw?.pick_result_summary?.fireball;
  if(direct!==undefined&&direct!==null&&direct!==""){
    const n=Number(direct);
    if(Number.isInteger(n)&&n>=0&&n<=9)return n;
  }
  const table=Array.isArray(draw?.prize_table)?draw.prize_table:[];
  for(const row of table){
    const label=String(row?.tier||row?.label||"").toLowerCase();
    const raw=row?.fireball ?? row?.fire_ball ?? row?.bonus ?? row?.number;
    if(label.includes("fireball")&&raw!==undefined&&raw!==null&&raw!==""){
      const n=Number(String(raw).match(/\d/)?.[0]);
      if(Number.isInteger(n)&&n>=0&&n<=9)return n;
    }
  }
  return null;
}
function fireballDrawVariants(drawNumbers,fireball){
  return uniqueOrderedVariants(drawNumbers,fireball);
}
function pickWhatIfPrize(game,numbers,draw,record){
  const drawNumbers=draw?.numbers||[];
  if(!isPickGame(game)||!Array.isArray(numbers)||!Array.isArray(drawNumbers))return 0;
  const cfg=gameNumberConfig(game);
  if(numbers.length!==cfg.count||drawNumbers.length!==cfg.count)return 0;
  const rules=record?.pickRules||{};
  const type=rules.playType||"Straight";
  const wager=Number(rules.wager)||1;
  const multiple=Number(rules.multiple)||1;
  const fireballEnabled=!!rules.fireball;
  const fireball=fireballEnabled?drawFireballDigit(draw):null;
  const exact=numbers.every((n,i)=>n===drawNumbers[i]);
  const boxed=sameMultiset(numbers,drawNumbers);
  const fireballVariants=fireballDrawVariants(drawNumbers,fireball);
  const fireballExact=fireballVariants.some(variant=>numbers.every((n,i)=>n===variant[i]));
  const fireballBoxed=fireballVariants.some(variant=>sameMultiset(numbers,variant));
  const ways=permutationCount(numbers);
  const validBox=ways>1;
  const straightBase=PICK_STRAIGHT_PRIZES[game]||0;
  const boxBase=validBox?(PICK_BOX_PRIZES[game]?.[ways]||0):0;
  const fireballStraightBase=PICK_FIREBALL_STRAIGHT_PRIZES[game]||0;
  const fireballBoxBase=validBox?(PICK_FIREBALL_BOX_PRIZES[game]?.[ways]||0):0;
  let amount=0;
  if(type==="Straight"){
    if(exact)amount+=straightBase*wager*multiple;
    if(fireballExact)amount+=fireballStraightBase*wager*multiple;
    return amount;
  }
  if(type==="Box"){
    if(boxed&&validBox)amount+=boxBase*wager*multiple;
    if(fireballBoxed&&validBox)amount+=fireballBoxBase*wager*multiple;
    return amount;
  }
  if(type==="Straight/Box"){
    const split=Math.max(1,wager)/2;
    if(exact)amount+=straightBase*split*multiple;
    if(boxed&&validBox)amount+=boxBase*split*multiple;
    if(fireballExact)amount+=fireballStraightBase*split*multiple;
    if(fireballBoxed&&validBox)amount+=fireballBoxBase*split*multiple;
    return amount;
  }
  if(type==="Combo"){
    if(boxed&&validBox)amount+=straightBase*wager*multiple;
    if(fireballBoxed&&validBox)amount+=fireballStraightBase*wager*multiple;
    return amount;
  }
  return exact?straightBase*wager*multiple:0;
}
function whatIfOutcomeForPlay(game,numbers,draw,record){
  const match=countWhatIfMatch(game,numbers,draw?.numbers||[]);
  if(isPickGame(game)){
    const amount=pickWhatIfPrize(game,numbers,draw,record);
    return{match,amount,freeTicket:false,winning:amount>0};
  }
  const prize=getDrawPrize(draw,match);
  const amount=Number(prize?.amount)||0;
  return{match,amount,freeTicket:match===2,winning:!!prize};
}
function whatIfCostPerDraw(strategyNumbers,record){
  const rules=record?.pickRules;
  if(rules&&Number.isFinite(Number(rules.cost)))return Number(rules.cost);
  return(strategyNumbers||[]).length;
}
function calculateWhatIfHistory({draws,plays,strategyId,record,windowId="365",cadence="twice",isPro=false}){
  const game=record?.game||"fantasy5";
  const requested=WHAT_IF_HISTORY_WINDOWS.find(w=>w.id===windowId)||WHAT_IF_HISTORY_WINDOWS.find(w=>w.id==="365");
  const option=requested?.pro&&!isPro?WHAT_IF_HISTORY_WINDOWS.find(w=>w.id==="365"):requested;
  const snapshotEntries=isPickGame(game)?strategySnapshotPlayEntries(record):[];
  const durableNumbers=strategyNumbersForRecord(strategyId,record,plays);
  const strategyNumbers=durableNumbers.length
    ?durableNumbers
    :strategyRecordUsesDurableSnapshot(record)
      ?[]
      :(plays||[])
      .filter(p=>strategyIdForPlay(p)===strategyId&&(p.game||"fantasy5")===game&&Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(game).count)
      .map(p=>p.numbers);
  const officialDraws=(draws||[])
    .filter(d=>rowGame(d)===game&&isOfficialDraw(d)&&drawHasExpectedNumbers(d));
  const windowed=filterDrawsByCadence(filterDrawsByDays(officialDraws,option.days),cadence);
  if((game==="pick2"||game==="pick3"||game==="pick5")&&snapshotEntries.length){
    let wins=0,freeTickets=0,cashWins=0,winnings=0,bestMatch=0,bestStrategyWon=0,totalCost=0,scoredDraws=0;
    windowed.forEach(draw=>{
      const scoringDraw={...draw,fireball_digit:drawFireballDigit(draw)};
      const scored=game==="pick2"
        ?calculatePick2EntriesDraw(snapshotEntries,scoringDraw,record?.pickRules||{})
        :game==="pick3"
          ?calculatePick3EntriesDraw(snapshotEntries,scoringDraw,record?.pickRules||{})
          :calculatePick5EntriesDraw(snapshotEntries,scoringDraw,record?.pickRules||{});
      if(scored?.error)return;
      scoredDraws+=1;
      totalCost+=Number(scored.total_cost)||0;
      winnings+=Number(scored.cash_won)||0;
      wins+=Number(scored.winning_plays_count)||0;
      cashWins+=Number(scored.winning_plays_count)||0;
      freeTickets+=Number(scored.free_tickets)||0;
      bestMatch=Math.max(bestMatch,Number(scored.best_match)||0);
      bestStrategyWon=Math.max(bestStrategyWon,Number(scored.cash_won)||0);
    });
    const net=winnings-totalCost;
    const roi=totalCost>0?(net/totalCost)*100:null;
    return{
      window:option,
      cadence,
      game,
      strategyNumbers,
      draws:scoredDraws,
      playsPerDraw:snapshotEntries.length,
      cost:totalCost,
      wins,
      freeTickets,
      cashWins,
      winnings,
      playedWinnings:winnings,
      roi,
      bestMatch,
      bestStrategy:strategyNumbers.length?strategyDisplayName(strategyId,record):"Create a strategy",
      bestStrategyWon,
      cappedWindow:requested?.pro&&!isPro,
    };
  }
  const costPerDraw=whatIfCostPerDraw(strategyNumbers,record);
  let wins=0,freeTickets=0,cashWins=0,winnings=0,bestMatch=0,bestStrategyWon=0;
  windowed.forEach(draw=>{
    let drawWon=0;
    strategyNumbers.forEach(nums=>{
      const outcome=whatIfOutcomeForPlay(game,nums,draw,record);
      bestMatch=Math.max(bestMatch,outcome.match);
      if(outcome.freeTicket)freeTickets++;
      if(outcome.winning)wins++;
      if(outcome.amount>0){cashWins++;drawWon+=outcome.amount;}
    });
    winnings+=drawWon;
    bestStrategyWon=Math.max(bestStrategyWon,drawWon);
  });
  const cost=windowed.length*costPerDraw;
  const net=winnings-cost;
  const roi=cost>0?(net/cost)*100:null;
  return{
    window:option,
    cadence,
    game,
    strategyNumbers,
    draws:windowed.length,
    playsPerDraw:strategyNumbers.length,
    cost,
    wins,
    freeTickets,
    cashWins,
    winnings,
    playedWinnings:winnings,
    roi,
    bestMatch,
    bestStrategy:strategyNumbers.length?strategyDisplayName(strategyId,record):"Create a strategy",
    bestStrategyWon,
    cappedWindow:requested?.pro&&!isPro,
  };
}
function rndPick(excl=[]){const pool=[];for(let i=1;i<=MAX_NUM;i++)if(!excl.includes(i))pool.push(i);const r=[];while(r.length<BALL_COUNT&&pool.length>0){const i=Math.floor(Math.random()*pool.length);r.push(pool.splice(i,1)[0])}return r.sort((a,b)=>a-b)}
function rndGamePick(game="fantasy5"){
  const cfg=gameNumberConfig(game);
  if(cfg.allowDuplicates)return Array.from({length:cfg.count},()=>Math.floor(Math.random()*(cfg.max-cfg.min+1))+cfg.min);
  const pool=[];
  for(let i=cfg.min;i<=cfg.max;i++)pool.push(i);
  const r=[];
  while(r.length<cfg.count&&pool.length>0){
    const i=Math.floor(Math.random()*pool.length);
    r.push(pool.splice(i,1)[0]);
  }
  return r.sort((a,b)=>a-b);
}
function genAllCombos(anchors,size){const remaining=[];for(let i=1;i<=MAX_NUM;i++)if(!anchors.includes(i))remaining.push(i);const need=size-anchors.length;const combos=[];function pick(start,cur){if(cur.length===need){combos.push([...anchors,...cur].sort((a,b)=>a-b));return}for(let i=start;i<remaining.length;i++){cur.push(remaining[i]);pick(i+1,cur);cur.pop()}}pick(0,[]);return combos}
const STRAT_DEFS=[
  {id:"anchor3",name:"Anchor 3",desc:"3 fixed + 2 random = 528 plays covers all",color:"#1e3a5f",ac:3},
  {id:"anchor4",name:"Anchor 4",desc:"4 fixed + 1 random = 32 plays covers all",color:"#008577",ac:4},
];

// Display metadata for every strategy label in user_plays.strategy.
// Order here drives card display order when ROIs tie.
const STRATEGY_META={
  manual:    {label:"Manual",     color:"#002855",icon:"\u270F\uFE0F"},
  quick_pick:{label:"Quick Pick", color:"#c8860a",icon:"\uD83C\uDFB2"},
  anchor3:   {label:"Anchor 3",   color:"#1e3a5f",icon:"\uD83C\uDFAF"},
  anchor4:   {label:"Anchor 4",   color:"#008577",icon:"\uD83C\uDFAF"},
  hotcold:   {label:"Hot / Cold", color:"#c8102e",icon:"\uD83D\uDD25"},
  custom:    {label:"Custom",     color:"#555555",icon:"\u2699\uFE0F"},
};
function titleFromStrategyId(id){
  return String(id||"Strategy")
    .replace(/^builder[_-]/,"")
    .split(/[_-]+/)
    .filter(Boolean)
    .map(part=>part.charAt(0).toUpperCase()+part.slice(1))
    .join(" ")||"Strategy";
}
function getStrategyMeta(id){
  return STRATEGY_META[id]||{...STRATEGY_META.custom,label:titleFromStrategyId(id)};
}

// Combinatorics helper — integer C(n, k). We never exceed C(36, 2) = 630 so
// the accumulator stays safely within JS Number precision.
function binom(n,k){
  if(k<0||k>n) return 0;
  if(k===0||k===n) return 1;
  k=Math.min(k,n-k);
  let c=1;
  for(let i=0;i<k;i++) c=c*(n-i)/(i+1);
  return Math.round(c);
}
// Expansion size per recipe: C(36-a, 5-a) where a = anchor count. Anchor 3
// yields 528, Anchor 4 yields 32.
function anchorExpansionSize(anchorCount){
  return binom(MAX_NUM-anchorCount,BALL_COUNT-anchorCount);
}
// Match-count distribution for a single draw against an anchor recipe.
// Returns {matches → number_of_combinations_with_that_match_count}.
//
// Math: partition winning numbers into anchor-hits (A) and non-anchor winners
// (W). For each combo, total matches = A + k where k is how many of the
// 5-a fill slots picked a non-anchor winner. Count of combos with exactly k
// winners among fill slots = C(W, k) * C(poolSize - W, fill - k).
function anchorMatchDistribution(anchors,drawNumbers){
  const fill=BALL_COUNT-anchors.length;
  const pool=MAX_NUM-anchors.length;
  const anchorSet=new Set(anchors);
  const drawSet=new Set(drawNumbers||[]);
  let A=0;
  for(const n of anchors) if(drawSet.has(n)) A++;
  let W=0;
  for(const n of (drawNumbers||[])) if(!anchorSet.has(n)) W++;
  const nonW=pool-W;
  const dist={};
  for(let k=0;k<=fill;k++){
    const combos=binom(W,k)*binom(nonW,fill-k);
    if(combos===0) continue;
    const matches=A+k;
    dist[matches]=(dist[matches]||0)+combos;
  }
  return dist;
}

// Aggregate simulated ROI metrics per strategy across all scored draws.
// Returns [] when either plays or draws are empty.
//
// Two kinds of rows contribute:
//   - Materialized: concrete 5-number plays, $1/draw each.
//   - Recipe (anchor_numbers set, numbers=null): expands to 528 or 32
//     combinations via anchorMatchDistribution per draw. Per Eric's
//     disclaimer framing, each recipe counts as ONE simulated play for
//     cost accounting ($1/draw for the whole anchor strategy), but its
//     hit counts accumulate the FULL expansion — so the card discloses
//     that real-world play would cost 528× or 32× what the sim shows.
function aggregateStrategies(plays,draws,options={}){
  if(!plays?.length||!draws?.length) return [];
  const PLAY_COST=1;
  const eligibleForDraw=typeof options.eligibleForDraw==="function"?options.eligibleForDraw:()=>true;
  const groups={};
  for(const p of plays){
    const isMat=Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count;
    const isRec=Array.isArray(p.anchor_numbers)&&p.anchor_numbers.length>0;
    if(!isMat&&!isRec) continue;
    const s=p.strategy||"manual";
    (groups[s]=groups[s]||[]).push(p);
  }
  // Assume server already sorted draws newest-first — we use that for streak.
  return Object.entries(groups).map(([stratId,stratPlays])=>{
    let spent=0,realSpent=0,realTrials=0;
    let won=0,m5=0,m4=0,m3=0,m2=0,hits2plus=0,bestDraw=0;
    let streak=0,streakActive=true;
    let pricesPendingDraws=0;

    const materialized=stratPlays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count);
    const recipes=stratPlays.filter(p=>Array.isArray(p.anchor_numbers)&&p.anchor_numbers.length>0);
    const totalExpansion=recipes.reduce((sum,r)=>sum+anchorExpansionSize(r.anchor_numbers.length),0);

    for(const d of draws){
      const p5=Number(d.prize_5match)||0;
      const p4=Number(d.prize_4match)||0;
      const p3=Number(d.prize_3match)||0;
      const pending=d.prize_5match==null||d.prize_4match==null||d.prize_3match==null;
      if(pending) pricesPendingDraws++;
      let drawWon=0,drawHas3plus=false;
      const drawMaterialized=materialized.filter(pl=>rowGame(pl)===rowGame(d)&&eligibleForDraw(pl,d,stratId));
      const drawRecipes=recipes.filter(r=>eligibleForDraw(r,d,stratId));
      const drawExpansion=drawRecipes.reduce((sum,r)=>sum+anchorExpansionSize(r.anchor_numbers.length),0);
      spent+=(drawMaterialized.length+drawRecipes.length)*PLAY_COST;
      realSpent+=(drawMaterialized.length+drawExpansion)*PLAY_COST;
      realTrials+=drawMaterialized.length+drawExpansion;

      // Materialized plays.
      for(const pl of drawMaterialized){
        const matches=cntMatch(pl.numbers,d.numbers);
        if(matches>=2) hits2plus++;
        if(matches>=3) drawHas3plus=true;
        if(matches===5){m5++;drawWon+=p5;}
        else if(matches===4){m4++;drawWon+=p4;}
        else if(matches===3){m3++;drawWon+=p3;}
        else if(matches===2){m2++;}
      }
      // Recipe expansion — combinatorial, avoids materializing 528 plays.
      for(const r of drawRecipes){
        const dist=anchorMatchDistribution(r.anchor_numbers,d.numbers);
        const n5=dist[5]||0,n4=dist[4]||0,n3=dist[3]||0,n2=dist[2]||0;
        m5+=n5; m4+=n4; m3+=n3; m2+=n2;
        hits2plus+=n5+n4+n3+n2;
        if(n3+n4+n5>0) drawHas3plus=true;
        drawWon+=n5*p5+n4*p4+n3*p3;
      }

      won+=drawWon;
      if(drawWon>bestDraw) bestDraw=drawWon;
      if(streakActive){
        if(drawHas3plus) streakActive=false;
        else streak++;
      }
    }

    // Eric's disclaimer framing: one recipe = $1/draw simulated, not per-combo.
    const simPlayCount=materialized.length+recipes.length;
    const net=won-spent;
    const roi=spent>0?(net/spent)*100:null;
    // Hit rate uses real-world trials so the percentage stays bounded 0-100%
    // even when recipes balloon the numerator.
    const hitRate=realTrials>0?(hits2plus/realTrials)*100:0;

    return{
      id:stratId,
      meta:getStrategyMeta(stratId),
      playCount:simPlayCount,
      spent,won,net,roi,
      realSpent,
      m5,m4,m3,m2,
      bestDraw,
      hitRate,
      streak,
      pricesPendingDraws,
      recipeCount:recipes.length,
      anchorCount:recipes[0]?recipes[0].anchor_numbers.length:null,
      totalExpansion,
    };
  });
}
const CSS="\n@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=Barlow:wght@400;500;600&display=swap');\n*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}\n:root{--red:#c8102e;--teal:#008577;--gold:#D4AF37;--navy:#002855;--cream:#F5F0E8}\nbody{font-family:'Barlow',sans-serif;background:var(--cream);color:#1a1a1a}\n.hdr{background:var(--navy);padding:0 24px;display:flex;align-items:center;justify-content:space-between;height:60px;position:sticky;top:0;z-index:100;box-shadow:0 2px 8px rgba(0,0,0,.3)}\n.logo{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:22px;color:#fff}.logo span{color:var(--gold)}\n.logo-sub{font-size:11px;color:rgba(255,255,255,.6);letter-spacing:2px;text-transform:uppercase}\n.nav{display:flex;gap:4px}.nav-btn{background:transparent;border:none;color:rgba(255,255,255,.75);font-family:'Barlow',sans-serif;font-size:13px;font-weight:500;padding:8px 14px;border-radius:6px;cursor:pointer;text-transform:uppercase}\n.nav-btn:hover{background:rgba(255,255,255,.1);color:#fff}.nav-btn.active{background:var(--teal);color:#fff}\n.upill{background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);border-radius:20px;padding:4px 12px;color:rgba(255,255,255,.85);font-size:12px;cursor:pointer}\n.page{max-width:1200px;margin:0 auto;padding:28px 20px 60px}\n.hero{background:var(--navy);border-radius:16px;padding:36px 40px 28px;margin-bottom:28px;position:relative;overflow:hidden}\n.hero::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse at 80% 50%,rgba(0,133,119,.3) 0%,transparent 60%);pointer-events:none}\n.hero-title{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:42px;color:#fff;text-transform:uppercase;letter-spacing:2px;line-height:1;margin-bottom:6px}\n.hero-sub{font-size:12px;font-weight:600;color:var(--teal);text-transform:uppercase;letter-spacing:3px;margin-bottom:24px}\n.draws-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:16px;margin-bottom:24px}\n.draw-card{background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,.08);cursor:pointer;transition:transform .15s}\n.draw-card:hover{transform:translateY(-2px)}.draw-card.sel{outline:3px solid var(--teal)}\n.dc-head{background:var(--navy);padding:10px 14px 8px}.dc-date{font-size:11px;color:rgba(255,255,255,.6)}\n.dc-type{font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:700;color:#fff;text-transform:uppercase}\n.dc-body{padding:14px 12px 10px}.dc-balls{display:flex;gap:5px;flex-wrap:wrap;margin-bottom:10px;justify-content:center}\n.dc-prize{font-size:11px;text-align:center;color:#555}.dc-prize strong{display:block;font-size:18px;font-weight:700;color:#1a1a1a}\n.dc-badge{text-align:center;font-size:11px;font-weight:700;padding:4px 10px;border-radius:10px;margin-top:6px}\n.ball{width:36px;height:36px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-family:'Barlow Condensed',sans-serif;font-weight:700;font-size:15px;flex-shrink:0;color:#fff;background:radial-gradient(circle at 35% 35%,#4a7db5,#1e3a5f);box-shadow:inset 0 1px 2px rgba(255,255,255,.25)}\n.ball-m{background:radial-gradient(circle at 35% 35%,#f5c842,#c8860a) !important}\n.ball-sm{width:28px !important;height:28px !important;font-size:12px !important}.ball-lg{width:44px !important;height:44px !important;font-size:18px !important}\n.sec-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px}\n.sec-title{font-family:'Barlow Condensed',sans-serif;font-weight:700;font-size:22px;color:var(--navy);text-transform:uppercase}\n.sec-badge{background:var(--teal);color:#fff;font-size:11px;padding:3px 10px;border-radius:10px;text-transform:uppercase}\n.panel{background:#fff;border-radius:14px;padding:20px 24px;box-shadow:0 2px 10px rgba(0,0,0,.06);margin-bottom:20px}\n.add-row{display:flex;gap:10px;align-items:center;margin-bottom:16px;flex-wrap:wrap;padding-bottom:16px;border-bottom:2px solid #f0f0f0}\n.add-inp{flex:1;min-width:180px;border:2px solid #e0e0e0;border-radius:10px;padding:10px 14px;font-family:'Barlow',sans-serif;font-size:14px;outline:none}\n.add-inp:focus{border-color:var(--teal)}\n.btn{border:none;border-radius:10px;padding:10px 20px;font-family:'Barlow',sans-serif;font-size:13px;font-weight:600;cursor:pointer;text-transform:uppercase}\n.btn-p{background:var(--teal);color:#fff}.btn-p:hover{background:#006a5e}\n.btn-s{background:#f0f0f0;color:#1a1a1a}.btn-s:hover{background:#e0e0e0}\n.btn-sm{padding:6px 14px;font-size:12px}.btn:disabled{opacity:.5;cursor:not-allowed}\n.play-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid #f0f0f0}.play-row:last-child{border-bottom:none}\n.play-num{font-size:12px;color:#999;width:36px;flex-shrink:0}.play-balls{display:flex;gap:4px;flex:1}\n.play-mi{font-size:11px;min-width:100px;text-align:right;font-weight:600}\n.play-del{background:none;border:none;color:#ccc;font-size:16px;cursor:pointer;padding:4px}.play-del:hover{color:var(--red)}\n.auth-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--navy)}\n.auth-card{background:#fff;border-radius:20px;padding:40px;width:380px;max-width:95vw}\n.auth-logo{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:32px;color:var(--navy);text-align:center;margin-bottom:4px}\n.auth-logo span{color:var(--teal)}.auth-sub{text-align:center;font-size:12px;color:#555;margin-bottom:28px;text-transform:uppercase;letter-spacing:2px}\n.auth-fld{margin-bottom:14px}.auth-fld label{display:block;font-size:12px;font-weight:600;color:#555;margin-bottom:5px;text-transform:uppercase}\n.auth-fld input{width:100%;border:1.5px solid #ddd;border-radius:8px;padding:10px 14px;font-family:'Barlow',sans-serif;font-size:14px;outline:none}\n.auth-fld input:focus{border-color:var(--teal)}.auth-btn{width:100%;background:var(--navy);color:#fff;border:none;border-radius:10px;padding:13px;font-family:'Barlow Condensed',sans-serif;font-size:18px;font-weight:700;text-transform:uppercase;cursor:pointer;margin-top:6px}\n.auth-btn:hover{background:var(--teal)}.auth-tog{text-align:center;margin-top:16px;font-size:13px;color:#555}\n.auth-tog a{color:var(--teal);cursor:pointer;font-weight:600}.auth-err{background:#fff0f0;border:1px solid #ffcccc;border-radius:8px;padding:10px 14px;font-size:13px;color:var(--red);margin-bottom:14px}\n.strat-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}\n.strat-card{background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,.07)}\n.strat-head{padding:14px 18px 12px;color:#fff}.strat-name{font-family:'Barlow Condensed',sans-serif;font-weight:700;font-size:18px;text-transform:uppercase}\n.strat-desc{font-size:11px;opacity:.8;margin-top:2px}.strat-body{padding:14px 18px}\n.strat-plays{max-height:240px;overflow-y:auto}.strat-row{display:flex;gap:4px;padding:5px 0;border-bottom:1px solid #f5f5f5;align-items:center}\n.strat-row:last-child{border-bottom:none}\n.sc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:16px;margin-bottom:24px}\n.sc-card{background:#fff;border-radius:14px;padding:20px;box-shadow:0 2px 10px rgba(0,0,0,.07);position:relative}\n.sc-name{font-family:'Barlow Condensed',sans-serif;font-size:20px;font-weight:700;text-transform:uppercase;margin-bottom:4px}\n.sc-roi{font-size:36px;font-weight:700;font-family:'Barlow Condensed',sans-serif;margin-bottom:4px}\n.sc-stats{display:flex;gap:12px;flex-wrap:wrap;margin-top:10px;padding-top:10px;border-top:1px solid #f0f0f0}\n.sc-num{font-size:18px;font-weight:700;display:block}.sc-lbl{font-size:10px;color:#555;text-transform:uppercase}\n.sc-trophy{position:absolute;top:12px;right:14px;font-size:20px}\n.hc-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:6px;margin-bottom:20px}\n.hc-ball{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Barlow Condensed',sans-serif;font-weight:700;font-size:15px;margin:0 auto 3px;color:#fff}\n.hc-count{font-size:10px;color:#555;text-align:center}\n.hc-section{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}\n.hc-panel{background:#fff;border-radius:14px;padding:18px 20px;box-shadow:0 2px 10px rgba(0,0,0,.06)}\n.hc-hdr{font-family:'Barlow Condensed',sans-serif;font-size:20px;font-weight:700;text-transform:uppercase;margin-bottom:14px}\n.hc-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid #f0f0f0}.hc-row:last-child{border-bottom:none}\n.hc-bar-wrap{flex:1}.hc-bar{height:6px;border-radius:3px}.hc-freq{font-size:12px;color:#555;min-width:30px;text-align:right}\n.hist-tbl{width:100%;border-collapse:collapse;font-size:13px}\n.hist-tbl th{background:var(--navy);color:#fff;padding:10px 12px;text-align:left;font-family:'Barlow Condensed',sans-serif;font-size:14px;text-transform:uppercase}\n.hist-tbl td{padding:10px 12px;border-bottom:1px solid #f0f0f0;vertical-align:middle}\n.hist-tbl tr:nth-child(even) td{background:#fafafa}.hist-tbl tr:hover td{background:#f0f8f6}\n.match-panel{background:var(--navy);border-radius:14px;padding:20px 24px;margin-bottom:20px;color:#fff}\n.match-title{font-family:'Barlow Condensed',sans-serif;font-size:18px;font-weight:700;text-transform:uppercase;margin-bottom:14px;color:var(--gold)}\n.spin-wrap{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;color:#555;gap:14px}\n.spinner{width:32px;height:32px;border:3px solid #e0e0e0;border-top-color:var(--teal);border-radius:50%;animation:spin .8s linear infinite}\n@keyframes spin{to{transform:rotate(360deg)}}.empty{text-align:center;padding:40px 20px;color:#555}\n.toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#222;color:#fff;padding:10px 20px;border-radius:20px;font-size:13px;z-index:999;pointer-events:none}\n.tabs{background:#fff;border-radius:12px;padding:6px;display:flex;gap:4px;margin-bottom:20px;overflow-x:auto}\n.tab-btn{background:none;border:none;padding:8px 18px;border-radius:8px;font-family:'Barlow',sans-serif;font-size:13px;font-weight:600;cursor:pointer;text-transform:uppercase;color:#555}\n.tab-btn:hover{background:#f5f5f5}.tab-btn.active{background:var(--navy);color:#fff}\n@media(max-width:640px){.hero-title{font-size:28px}.draws-grid{grid-template-columns:1fr 1fr}.hc-section{grid-template-columns:1fr}.nav{display:none}}\n";
const POLISH_CSS=`
.page{animation:none}
.route-fade{animation:none}
@keyframes ltPageIn{from{opacity:.001;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
.btn,.auth-btn,.nav-btn,.tab-btn,.upill{transition:transform .14s ease,box-shadow .14s ease,filter .14s ease,background-color .14s ease,opacity .14s ease}
.btn:hover:not(:disabled),.auth-btn:hover:not(:disabled),.nav-btn:hover,.tab-btn:hover,.upill:hover{transform:translateY(-1px);box-shadow:0 8px 18px rgba(0,0,0,.12);filter:brightness(1.02)}
.btn:active:not(:disabled),.auth-btn:active:not(:disabled),.nav-btn:active,.tab-btn:active,.upill:active{transform:translateY(1px) scale(.98);box-shadow:0 3px 8px rgba(0,0,0,.1)}
.btn:disabled,.auth-btn:disabled{opacity:.52;cursor:not-allowed;filter:saturate(.75)}
.btn.loading,.auth-btn.loading{position:relative;color:transparent !important;pointer-events:none}
.btn.loading::after,.auth-btn.loading::after{content:"";position:absolute;left:50%;top:50%;width:16px;height:16px;margin:-8px 0 0 -8px;border-radius:50%;border:2px solid rgba(255,255,255,.55);border-top-color:#fff;animation:spin .8s linear infinite}
.panel,.sc-card,.strat-card,.draw-card,.auth-card{transition:transform .16s ease,box-shadow .16s ease}
.panel:hover,.sc-card:hover,.strat-card:hover,.draw-card:hover{transform:translateY(-2px);box-shadow:0 10px 24px rgba(0,0,0,.10)}
.lt-countdown-warn{animation:ltWarnPulse 1s ease-in-out infinite}
.lt-countdown-danger{animation:ltDangerPulse .72s ease-in-out infinite}
@keyframes ltWarnPulse{0%,100%{color:#fff;text-shadow:0 0 0 rgba(212,175,55,0)}50%{color:#FFD54F;text-shadow:0 0 16px rgba(212,175,55,.5)}}
@keyframes ltDangerPulse{0%,100%{color:#fff;text-shadow:0 0 0 rgba(200,16,46,0)}50%{color:#ff8797;text-shadow:0 0 18px rgba(200,16,46,.72)}}
@keyframes ltUnreadPulse{0%,100%{box-shadow:0 2px 10px rgba(0,0,0,.06)}50%{box-shadow:0 0 0 4px rgba(0,133,119,.12),0 8px 24px rgba(0,0,0,.1)}}
.activity-unread{animation:ltUnreadPulse 2.4s ease-in-out infinite}
.activity-fading{opacity:0 !important;transform:translateY(-4px);transition:opacity .24s ease,transform .24s ease}
.skeleton{position:relative;overflow:hidden;background:#eef1f3;border-radius:12px}
.skeleton::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent);animation:ltSkeleton 1.05s ease-in-out infinite}
@keyframes ltSkeleton{to{transform:translateX(100%)}}
.hdr{gap:14px}
.nav{min-width:0;overflow-x:auto;scrollbar-width:none}
.nav::-webkit-scrollbar,.tabs::-webkit-scrollbar{display:none}
.nav-btn{white-space:nowrap;flex:0 0 auto}
.tabs{scrollbar-width:none}
.launch-note{background:#fffaf0;border:1px solid #ffd54f;border-radius:10px;padding:10px 12px;font-size:12px;color:#5a4a00;font-weight:700;line-height:1.45}
.settings-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px}
.results-center-grid{display:grid;grid-template-columns:minmax(0,1.25fr) minmax(260px,.75fr);gap:16px;align-items:start}
.results-center-compact{--rc-gap:7px;margin-top:-24px}
.results-center-compact .panel{margin-bottom:var(--rc-gap)}
.results-topline{display:flex;justify-content:space-between;gap:12px;align-items:center;margin-bottom:5px}
.results-topline p{font-size:12px;color:#555;line-height:1.2;margin-top:1px;max-width:740px}
.results-trust-strip{margin-bottom:var(--rc-gap);padding:6px 9px;font-size:11px}
.results-filter-bar{display:grid;grid-template-columns:repeat(5,minmax(120px,1fr));gap:8px;align-items:end;padding:9px 12px}
.results-filter-bar label{font-size:10px;font-weight:900;color:#555;text-transform:uppercase}
.results-filter-bar .add-inp{width:100%;min-width:0;margin-top:3px;padding:7px 9px;border-radius:9px}
.results-mode-control{display:grid;gap:3px}
.results-mode-label{font-size:10px;font-weight:900;color:#555;text-transform:uppercase}
.results-mode-buttons{display:grid;grid-template-columns:1fr 1fr;gap:5px;margin-top:3px}
.results-mode-buttons button{border:1px solid #dbeee9;background:#fff;color:#002855;border-radius:10px;padding:7px 8px;text-align:left;cursor:pointer;font-family:'Barlow',sans-serif}
.results-mode-buttons button.active{background:#002855;color:#fff;border-color:#002855}
.results-mode-buttons span{display:block;font-size:12px;font-weight:900;line-height:1.1}
.results-mode-buttons small{display:block;font-size:10px;font-weight:800;line-height:1.15;color:inherit;opacity:.74;margin-top:2px}
.results-refresh-note{grid-column:1/-1;font-size:11px;color:#666;line-height:1.25}
.results-refresh-note strong{color:#002855}
.results-hero-card{background:#002855;color:#fff;padding:11px 14px}
.results-hero-top{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:7px}
.results-eyebrow{font-size:10px;color:rgba(255,255,255,.62);font-weight:900;text-transform:uppercase;letter-spacing:1.4px}
.results-status-title{font-family:'Barlow Condensed',sans-serif;font-size:30px;font-weight:900;line-height:.92;text-transform:uppercase;margin-top:2px}
.results-draw-meta{font-size:12px;color:rgba(255,255,255,.75);font-weight:800;margin-top:5px}
.results-winning-row{display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-bottom:8px}
.results-best-draw-card{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.16);border-radius:12px;padding:10px 11px;margin-bottom:9px;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:center}
.results-best-draw-card strong{display:block;font-size:18px;color:#fff;margin-top:3px}
.results-best-draw-card span{display:block;font-size:12px;color:rgba(255,255,255,.78);font-weight:800;margin-top:2px}
.results-best-draw-card .results-winning-row{justify-content:flex-end;margin-bottom:0}
.results-winning-placeholder{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.16);border-radius:10px;padding:10px;margin-bottom:10px;font-size:12px;color:rgba(255,255,255,.82)}
.results-empty-hero{margin-top:8px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.16);border-radius:10px;padding:8px 10px;display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.results-empty-hero strong{font-size:13px;color:#fff}
.results-empty-hero span{font-size:12px;color:rgba(255,255,255,.78);flex:1;min-width:220px}
.results-metric-strip{display:grid;grid-template-columns:repeat(8,minmax(0,1fr));gap:6px}
.results-metric-pill{background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.15);border-radius:9px;padding:6px 8px;min-width:0}
.results-metric-pill div{font-size:9px;color:rgba(255,255,255,.58);font-weight:900;text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.results-metric-pill strong{display:block;font-family:'Barlow Condensed',sans-serif;font-size:20px;line-height:1;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.results-dashboard-grid{display:grid;grid-template-columns:minmax(0,1.45fr) minmax(300px,.72fr);gap:8px;align-items:start}
.results-rankings-panel{padding:11px 13px}
.results-section-head{margin-bottom:6px;gap:10px;flex-wrap:wrap}
.results-section-head .sec-title,.results-split-card .sec-title{font-size:21px}
.results-section-head label{font-size:10px;font-weight:900;color:#555;text-transform:uppercase;display:flex;gap:7px;align-items:center}
.results-section-head .add-inp{min-width:132px;padding:7px 9px}
.results-rank-list{display:grid;gap:6px;max-height:224px;overflow:auto;padding-right:2px}
.results-rank-row{width:100%;text-align:left;border:1px solid #e8eee9;background:#fff;border-radius:10px;padding:7px 9px;cursor:pointer;display:grid;grid-template-columns:minmax(0,1.15fr) minmax(315px,.9fr);gap:9px;align-items:center}
.results-rank-row.top{box-shadow:0 9px 20px rgba(0,133,119,.1)}
.results-rank-main{display:grid;grid-template-columns:auto minmax(0,1fr);gap:9px;align-items:center;min-width:0}
.results-rank-num{width:28px;height:28px;border-radius:999px;background:#008577;color:#fff;display:grid;place-items:center;font-weight:900;font-size:13px}
.results-rank-name{font-weight:900;color:#002855;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.results-rank-note{font-size:11px;color:#666;font-weight:800;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.results-rank-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:6px}
.results-rank-stats div{background:#f8fbfa;border:1px solid #edf1f0;border-radius:8px;padding:5px 7px;min-width:0}
.results-rank-stats span{display:block;font-size:9px;color:#555;font-weight:900;text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.results-rank-stats strong{display:block;font-size:12px;color:#002855;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.results-side-stack{display:grid;gap:8px}
.results-split-card{padding:11px 13px}
.results-split-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-top:8px}
.results-split-grid>div{border-radius:10px;padding:8px;border:1px solid #dbe7f7}
.results-split-grid .midday{background:#fffaf0;border-color:#ffd54f;color:#5a4a00}
.results-split-grid .evening{background:#eef4ff;border-color:#d9e6ff;color:#002855}
.results-split-grid span{font-size:9px;font-weight:900;text-transform:uppercase;letter-spacing:.8px}
.results-split-grid strong{display:block;font-family:'Barlow Condensed',sans-serif;font-size:30px;line-height:1;margin-top:2px}
.results-split-grid small{font-size:11px;font-weight:700;line-height:1.25}
.results-split-card p{font-size:12px;color:#555;font-weight:800;margin-top:9px;line-height:1.35}
.results-detail-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:10px}
.results-detail-actions .btn{width:100%;padding:9px 10px}
.results-info-modal{position:fixed;inset:0;z-index:990;background:rgba(0,20,45,.62);display:grid;place-items:center;padding:20px}
.results-info-modal>.panel{width:min(760px,100%);max-height:min(760px,90vh);overflow:auto;margin:0}
.results-modal-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:16px}
.results-mini-title{font-size:13px;font-weight:900;color:#002855;margin-bottom:8px}
.results-trust-copy{display:grid;gap:8px;font-size:13px;color:#555;line-height:1.45}
.results-trust-copy strong{color:#002855}
.results-refresh-box{background:#f8fbfa;border:1px solid #dbeee9;border-radius:9px;padding:8px 9px;font-weight:800;color:#002855}
.results-play-row{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:10px;align-items:center;border:1px solid #eee;border-radius:12px;padding:10px 12px;background:#fff}
.results-play-row.best{border-color:#008577;background:#f0f8f6}
.results-play-row.win{border-color:#dbeee9}
.results-play-row.near{border-color:#f2d88b;background:#fffaf0}
.results-celebrate{animation:resultsPop .52s ease-out}
.strategy-builder-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(300px,.8fr);gap:18px;align-items:start}
.playslip-panel{position:sticky;top:78px}
.mobile-playslip-summary{display:none}
@keyframes resultsPop{0%{opacity:.001;transform:translateY(10px) scale(.98)}100%{opacity:1;transform:translateY(0) scale(1)}}
@media(max-width:900px){.hdr{padding:0 12px}.upill{max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.nav-btn{padding:8px 10px;font-size:12px}.page{padding:20px 14px 52px}}
@media(max-width:640px){.panel,.sc-card{padding:16px}.sec-hdr{align-items:flex-start;gap:10px;flex-wrap:wrap}.btn{min-height:42px}.strategy-actions{grid-template-columns:repeat(2,minmax(0,1fr)) !important}.strategy-actions>.btn{width:100%}.sc-card [style*="repeat(2"]{grid-template-columns:1fr !important}}
.today-hub{background:#fff;border-radius:16px;padding:20px 22px;box-shadow:0 2px 12px rgba(0,0,0,.07);margin-bottom:20px;border:1px solid rgba(0,40,85,.06)}
.today-hub-top{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:16px;align-items:start;margin-bottom:18px}
.today-hub-label{font-size:11px;color:#008577;font-weight:900;text-transform:uppercase;letter-spacing:2px;margin-bottom:4px}
.today-hub-date{font-family:'Barlow Condensed',sans-serif;font-size:38px;font-weight:900;color:#002855;text-transform:uppercase;line-height:1}
.today-next{background:#002855;color:#fff;border-radius:14px;padding:16px;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:14px;align-items:center}
.today-countdown{font-family:'Barlow Condensed',sans-serif;font-size:clamp(34px,7vw,56px);font-weight:900;line-height:.9;text-align:right;white-space:nowrap}
.today-hub-grid{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(260px,.9fr);gap:16px;margin-top:16px}
.today-games{display:grid;gap:8px}
.today-game-row{width:100%;border:1px solid #edf1f0;background:#fbfdfc;border-radius:12px;padding:11px 12px;display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:10px;align-items:center;text-align:left;cursor:pointer}
.today-game-dot{width:11px;height:11px;border-radius:50%;box-shadow:0 0 0 3px rgba(0,133,119,.1)}
.today-check-row{border:none;background:#f7f8f8;border-radius:10px;padding:9px 10px;display:flex;align-items:center;gap:9px;text-align:left;font-weight:800;color:#002855;cursor:pointer}
.today-check-box{width:18px;height:18px;border-radius:5px;border:2px solid #cfd8d5;display:inline-flex;align-items:center;justify-content:center;color:#fff;background:#fff;flex-shrink:0}
.today-check-row.done .today-check-box{background:#008577;border-color:#008577}
.today-actions{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:9px;margin-top:14px}
.today-luck-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.today-luck-item{background:#fff8e6;border:1px solid #f2df9b;border-radius:10px;padding:10px}
@media(max-width:760px){.today-hub{padding:16px}.today-hub-top,.today-next,.today-hub-grid{grid-template-columns:1fr}.today-countdown{text-align:left}.today-hub-date{font-size:31px}.today-actions{grid-template-columns:1fr 1fr}.today-luck-grid{grid-template-columns:1fr}}
@media(max-width:760px){.results-topline{display:grid}.results-filter-bar{grid-template-columns:1fr;padding:14px}.results-mode-buttons{grid-template-columns:1fr}.results-hero-card{padding:14px}.results-status-title{font-size:32px}.results-best-draw-card{grid-template-columns:1fr}.results-best-draw-card .results-winning-row{justify-content:flex-start}.results-metric-strip{grid-template-columns:repeat(2,minmax(0,1fr))}.results-dashboard-grid{grid-template-columns:1fr}.results-rank-list{max-height:none;overflow:visible}.results-rank-row{grid-template-columns:1fr;padding:12px}.results-rank-stats{grid-template-columns:repeat(2,minmax(0,1fr))}.results-split-grid{grid-template-columns:1fr}.results-detail-actions{grid-template-columns:1fr}.results-info-modal{padding:12px;place-items:end stretch}.results-info-modal>.panel{width:100%;max-height:86vh;border-radius:14px 14px 0 0}}
@media(max-width:760px){.results-center-grid,.strategy-builder-grid{grid-template-columns:1fr}.builder-main-panel{order:1}.playslip-panel{position:static;order:2}.playslip-list{max-height:132px!important}.playslip-save-panel{margin-top:10px!important;padding-top:10px!important;gap:8px!important}.playslip-cost-card{padding:8px 10px!important}.playslip-cost-card strong{font-size:30px!important}.mobile-playslip-summary{position:sticky;bottom:10px;z-index:30;display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:12px;background:#002855;color:#fff;border-radius:12px;padding:8px 10px;box-shadow:0 8px 22px rgba(0,0,0,.18)}.mobile-playslip-summary span{font-size:12px;font-weight:900;color:#ffd54f}.mobile-playslip-summary .btn{min-height:34px;padding:6px 12px}.results-play-row{grid-template-columns:1fr}.results-play-row .dc-badge{justify-self:start}}
`;

function Ball({n,matched,size,locked,matchKind="",title=""}){
  const sz=size==="sm"?"28px":size==="lg"?"44px":"36px";
  const fs=size==="sm"?"12px":size==="lg"?"18px":"15px";
  const exact=matchKind==="exact"||matched;
  const wrongPosition=matchKind==="wrong-position";
  const bg=locked?"radial-gradient(circle at 35% 35%,#6ee7b7,#059669)":exact?"radial-gradient(circle at 35% 35%,#f5c842,#c8860a)":"radial-gradient(circle at 35% 35%,#4a7db5,#1e3a5f)";
  const shadow=locked
    ?"0 0 0 2px #059669,inset 0 1px 2px rgba(255,255,255,.25)"
    :wrongPosition
      ?"0 0 0 2px #D4AF37,inset 0 1px 2px rgba(255,255,255,.25)"
      :"inset 0 1px 2px rgba(255,255,255,.25)";
  const mark=exact?"✓":wrongPosition?"○":"";
  const aria=title||(
    exact?"correct digit in the correct position":
    wrongPosition?"correct digit in a different position":
    "no official match"
  );
  return <span title={aria} aria-label={`${n}: ${aria}`} style={{width:sz,height:sz,fontSize:fs,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,flexShrink:0,color:"#fff",background:bg,boxShadow:shadow,position:"relative"}}>
    {n}
    {mark&&<span aria-hidden="true" style={{position:"absolute",right:-2,bottom:-4,width:size==="sm"?12:14,height:size==="sm"?12:14,borderRadius:"50%",background:exact?"#002855":"#fffaf0",color:exact?"#D4AF37":"#c8860a",border:"1px solid #D4AF37",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:size==="sm"?8:9,fontFamily:"Arial, sans-serif",fontWeight:900,lineHeight:1}}>{mark}</span>}
  </span>;
}
function Toast({msg}){return msg?<div className="toast">{msg}</div>:null;}

const VERSION_CHECK_INTERVAL_MS=60*1000;
function currentMainBundleName(){
  try{
    const scripts=Array.from(document.scripts||[]).map(script=>script.src||"").filter(Boolean);
    const main=scripts.reverse().find(src=>/\/static\/js\/main\.[^/]+\.js/.test(src));
    return main?main.split("/").pop():"";
  }catch{
    return"";
  }
}
function hasUnsavedLottoTrackerWork(){
  try{return !!window.__LOTTO_TRACKER_UNSAVED_WORK__;}catch{return false;}
}
function loadRemoteBuildInfo(reason){
  return new Promise(resolve=>{
    if(typeof document==="undefined"){
      resolve(null);
      return;
    }
    const script=document.createElement("script");
    const done=remote=>{
      script.remove();
      resolve(remote||null);
    };
    const timeout=setTimeout(()=>done(null),8000);
    script.async=true;
    script.src="/version.js?check="+Date.now()+"&reason="+encodeURIComponent(reason);
    script.onload=()=>{
      clearTimeout(timeout);
      done(window.__LOTTO_TRACKER_LATEST_BUILD__);
    };
    script.onerror=()=>{
      clearTimeout(timeout);
      done(null);
    };
    try{
      window.__LOTTO_TRACKER_LATEST_BUILD__=null;
      document.head.appendChild(script);
    }catch{
      clearTimeout(timeout);
      done(null);
    }
  });
}
function VersionRefreshGuard(){
  const[update,setUpdate]=useState(null);
  const[confirmUnsaved,setConfirmUnsaved]=useState(false);
  const checkingRef=useRef(false);
  async function checkForUpdate(reason="periodic"){
    if(typeof window==="undefined"||checkingRef.current)return;
    checkingRef.current=true;
    try{
      let remote=await loadRemoteBuildInfo(reason);
      if(!remote?.version){
        const response=await fetch("/version.json?check="+Date.now()+"&reason="+encodeURIComponent(reason),{
          cache:"no-store",
          headers:{"Cache-Control":"no-cache"},
        });
        if(!response.ok)return;
        remote=await response.json().catch(()=>null);
      }
      if(!remote?.version||remote.app!=="lottotracker")return;
      if(remote.version!==APP_BUILD_VERSION){
        setUpdate({
          ...remote,
          currentVersion:APP_BUILD_VERSION,
          currentBundle:currentMainBundleName(),
        });
      }
    }catch{
      // Version checks should never interrupt the app.
    }finally{
      checkingRef.current=false;
    }
  }
  useEffect(()=>{
    checkForUpdate("startup");
    const startupTimer=setTimeout(()=>checkForUpdate("startup-delay"),5000);
    const timer=setInterval(()=>checkForUpdate("interval"),VERSION_CHECK_INTERVAL_MS);
    function onFocus(){checkForUpdate("focus");}
    function onVisibility(){
      if(document.visibilityState==="visible")checkForUpdate("visible");
    }
    window.addEventListener("focus",onFocus);
    document.addEventListener("visibilitychange",onVisibility);
    return()=>{
      clearTimeout(startupTimer);
      clearInterval(timer);
      window.removeEventListener("focus",onFocus);
      document.removeEventListener("visibilitychange",onVisibility);
    };
  },[]);
  if(!update)return null;
  function refreshNow(){
    if(hasUnsavedLottoTrackerWork()&&!confirmUnsaved){
      setConfirmUnsaved(true);
      return;
    }
    window.location.reload();
  }
  return(
    <div role="dialog" aria-live="polite" aria-label="LottoTracker update ready" style={{position:"fixed",right:16,bottom:16,zIndex:2000,width:"min(390px,calc(100vw - 32px))",background:"#fff",border:"2px solid #008577",borderRadius:14,boxShadow:"0 16px 42px rgba(0,0,0,.22)",padding:16}}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:"#002855",lineHeight:1,textTransform:"uppercase",marginBottom:6}}>A new LottoTracker update is ready.</div>
      <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:12}}>
        Refresh to use the newest version. Your sign-in stays in place.
        {confirmUnsaved&&<div style={{marginTop:8,color:"#8B4513",fontWeight:800}}>You may have unsaved strategy edits. Save them first, or refresh when you're ready.</div>}
      </div>
      <button type="button" className="btn btn-p" style={{width:"100%"}} onClick={refreshNow}>Refresh Now</button>
      {confirmUnsaved&&(
        <button type="button" className="btn btn-sm btn-s" style={{width:"100%",marginTop:8}} onClick={()=>setConfirmUnsaved(false)}>Keep Editing</button>
      )}
      <div style={{fontSize:10,color:"#888",marginTop:9,lineHeight:1.35}}>
        Current bundle: {update.currentBundle||"unknown"}
      </div>
    </div>
  );
}

function LoadingSkeleton({kind="dashboard"}){
  if(kind==="app"){
    return(
      <><style>{CSS}{POLISH_CSS}</style>
        <div className="page">
          <div className="skeleton" style={{height:60,marginBottom:18}}/>
          <div className="skeleton" style={{height:210,marginBottom:20}}/>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14}}>
            {[1,2,3,4].map(i=><div key={i} className="skeleton" style={{height:118}}/> )}
          </div>
        </div>
      </>
    );
  }
  return(
    <div style={{display:"grid",gap:14}}>
      <div className="skeleton" style={{height:190}}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14}}>
        {[1,2,3,4].map(i=><div key={i} className="skeleton" style={{height:110}}/> )}
      </div>
    </div>
  );
}

function ErrorFallback({onReset}){
  const navigate=useNavigate();
  return(
    <div className="panel" style={{textAlign:"center",padding:"42px 20px",border:"2px solid #ffd4d4"}}>
      <div style={{fontSize:42,marginBottom:10}}>{"\u26A0\uFE0F"}</div>
      <div className="sec-title" style={{fontSize:30,marginBottom:8}}>We hit a snag.</div>
      <div style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto 18px"}}>
        Something on this page did not load correctly. Refresh the page, return home, or report the issue from Settings.
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
        <button type="button" className="btn btn-p" onClick={()=>{if(onReset)onReset();window.location.reload();}}>Refresh</button>
        <button type="button" className="btn btn-s" onClick={()=>{if(onReset)onReset();navigate("/");}}>Return Home</button>
        <button type="button" className="btn btn-s" onClick={()=>{if(onReset)onReset();navigate("/settings");}}>Report Bug</button>
      </div>
    </div>
  );
}

class PageErrorBoundary extends React.Component{
  constructor(props){
    super(props);
    this.state={hasError:false};
  }
  static getDerivedStateFromError(){
    return{hasError:true};
  }
  componentDidCatch(error,info){
    try{
      console.error("LottoTracker page error",error,info);
      const log=JSON.parse(localStorage.getItem("lt:errorReports")||"[]");
      localStorage.setItem("lt:errorReports",JSON.stringify([{message:error?.message||"Unknown error",stack:error?.stack||"",componentStack:info?.componentStack||"",at:new Date().toISOString()},...log].slice(0,10)));
    }catch{}
  }
  render(){
    if(this.state.hasError)return <ErrorFallback onReset={()=>this.setState({hasError:false})}/>;
    return this.props.children;
  }
}

function DeferredPage({children,kind="dashboard"}){
  return children;
}

function SafePage({children,defer=false,kind="dashboard"}){
  const body=defer?<DeferredPage kind={kind}>{children}</DeferredPage>:children;
  return <PageErrorBoundary>{body}</PageErrorBoundary>;
}

function AnimatedValue({value,prefix="",suffix="",duration=650}){
  const numeric=Number(value);
  const[start,setStart]=useState(numeric||0);
  const[display,setDisplay]=useState(numeric||0);
  useEffect(()=>{
    if(!Number.isFinite(numeric)){setDisplay(value);return undefined;}
    const from=Number(start)||0;
    const to=numeric;
    const began=performance.now();
    let raf=0;
    function step(t){
      const p=Math.min(1,(t-began)/duration);
      const eased=1-Math.pow(1-p,3);
      setDisplay(from+(to-from)*eased);
      if(p<1)raf=requestAnimationFrame(step);
      else setStart(to);
    }
    raf=requestAnimationFrame(step);
    return()=>cancelAnimationFrame(raf);
  },[numeric]);
  if(!Number.isFinite(numeric))return <>{value}</>;
  const rounded=Math.abs(numeric)>=100?Math.round(display):Number(display.toFixed(0));
  return <>{prefix}{rounded.toLocaleString()}{suffix}</>;
}

function AnimatedMetric({value}){
  if(typeof value==="number")return <AnimatedValue value={value}/>;
  const m=String(value).match(/^([+\u2212-]?\$?)(-?\d+(?:\.\d+)?)(%?)$/);
  if(!m)return <>{value}</>;
  return <AnimatedValue prefix={m[1]} value={Number(m[2])} suffix={m[3]}/>;
}

function FirstWinExperience({open,myGames,onDashboard,onAddMore,drawStatus}) {
  useEffect(()=>{
    if(!open)return undefined;
    const done=setTimeout(()=>onDashboard(),9000);
    return()=>clearTimeout(done);
  },[open,onDashboard]);
  if(!open)return null;
  const next=drawStatus?.next||getNextDrawEntries(myGames)[0];
  const countdownClass=next&&next.status!=="Results Ready"?(next.countdownMs<=5*60000?"lt-countdown-danger":next.countdownMs<=30*60000?"lt-countdown-warn":""):"";
  return(
    <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,40,85,.78)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
      <style>{`
        @keyframes firstWinPop{0%{opacity:0;transform:translateY(18px) scale(.96)}100%{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes firstWinBurst{0%,100%{transform:rotate(-7deg) scale(1)}50%{transform:rotate(7deg) scale(1.12)}}
      `}</style>
      <div style={{width:"min(760px,100%)",maxHeight:"92vh",overflowY:"auto",background:"#fff",borderRadius:18,boxShadow:"0 24px 70px rgba(0,0,0,.38)",padding:"26px 28px",animation:"firstWinPop .32s ease-out"}}>
        <div style={{textAlign:"center",marginBottom:18}}>
          <div style={{fontSize:52,lineHeight:1,marginBottom:8,animation:"firstWinBurst 1.2s ease-in-out infinite"}}>{"\uD83C\uDF89"}</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:44,fontWeight:900,color:"#008577",textTransform:"uppercase",lineHeight:1}}>Nice!</div>
          <div style={{fontSize:18,fontWeight:800,color:"#002855",marginTop:6}}>Your numbers are now being tracked.</div>
        </div>

        <div style={{background:"#002855",borderRadius:14,padding:16,color:"#fff",marginBottom:16}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,.65)",textTransform:"uppercase",letterSpacing:2,marginBottom:10}}>Your Next Draw</div>
          {next?(
            <>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap",marginBottom:12}}>
                <div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:800,textTransform:"uppercase",lineHeight:1}}>{next.gameLabel}</div>
                  <div style={{fontSize:12,color:"#D4AF37",fontWeight:800,textTransform:"uppercase",marginTop:4}}>{next.drawLabel} Draw</div>
                </div>
                <div className={countdownClass} style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:42,fontWeight:900,lineHeight:.9,textAlign:"right"}}>{next.countdown}</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:10}}>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:800,textTransform:"uppercase"}}>Game</div><strong>{next.gameLabel}</strong></div>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:800,textTransform:"uppercase"}}>Draw Time</div><strong>{next.drawTime}</strong></div>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:800,textTransform:"uppercase"}}>Sales Close</div><strong>{next.closeTime}</strong></div>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:800,textTransform:"uppercase"}}>Countdown</div><strong>{next.countdown}</strong></div>
              </div>
            </>
          ):(
            <div style={{fontSize:13,color:"rgba(255,255,255,.78)",lineHeight:1.45}}>Choose a game in My Games to see your next draw.</div>
          )}
        </div>

        <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:14,padding:16,marginBottom:18}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:800,color:"#002855",textTransform:"uppercase",marginBottom:10}}>What happens now?</div>
          <div style={{display:"grid",gap:8,fontSize:14,color:"#555",lineHeight:1.4}}>
            {[
              "We'll automatically check every draw.",
              "We'll compare every strategy.",
              "We'll notify you when results are ready.",
              "You'll never have to remember your numbers again.",
            ].map(item=>(
              <div key={item} style={{display:"flex",gap:9,alignItems:"flex-start"}}>
                <span style={{color:"#008577",fontWeight:900}}>{"\u2713"}</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          <button type="button" className="btn btn-p" onClick={onDashboard}>View Dashboard</button>
          <button type="button" className="btn btn-s" onClick={onAddMore}>Add More Numbers</button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({icon,title,text,actions=[]}){
  return(
    <div className="empty" style={{background:"#fff",borderRadius:14,boxShadow:"0 2px 10px rgba(0,0,0,.06)",padding:"34px 22px"}}>
      <div style={{fontSize:38,marginBottom:10}}>{icon}</div>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>{title}</div>
      {text&&<p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto 18px"}}>{text}</p>}
      {actions.length>0&&(
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          {actions.map((a,i)=>(
            <button key={a.label} type="button" className={"btn "+(i===0?"btn-p":"btn-s")} onClick={a.onClick}>{a.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function NavItemLabel({label,badge}){
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
      <span>{label}</span>
      {!!badge&&<span style={{background:"#c8102e",color:"#fff",borderRadius:10,minWidth:18,height:18,padding:"0 5px",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,lineHeight:1}}>{badge}</span>}
    </span>
  );
}

function GameSupportRoadmap(){
  return(
    <div className="panel" style={{marginBottom:18}}>
      <div className="sec-title" style={{fontSize:22,marginBottom:12}}>Game Support Roadmap</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
        {GAME_ROADMAP_GROUPS.map(group=>(
          <div key={group.id} style={{border:"1px solid #edf1f0",borderLeft:"5px solid "+group.color,borderRadius:10,padding:"12px 14px",background:"#fafafa"}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:"#002855",textTransform:"uppercase",marginBottom:4}}>{group.label}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
              {GAME_CATALOG.filter(game=>game.roadmap===group.id).map(game=>(
                <span key={game.id} className="dc-badge" style={{background:group.color+"20",color:group.color}}>{game.label}</span>
              ))}
            </div>
            <div style={{fontSize:12,color:"#555",lineHeight:1.4}}>{group.note}</div>
          </div>
        ))}
      </div>
      <div style={{fontSize:12,color:"#5a4a00",lineHeight:1.45,background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px",marginTop:12}}>
        Historical Cash4Life records stay archived as discontinued compatibility data and are not active game selections.
      </div>
    </div>
  );
}

function TrustedScoringRules(){
  return(
    <div className="panel" style={{marginBottom:18,border:"1px solid #dbeee9",background:"#f8fbfa"}}>
      <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Trusted Scoring Rules</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:12}}>
        <div style={{border:"1px solid #dbeee9",borderRadius:10,padding:"12px",background:"#fff"}}>
          <span className="sec-badge" style={{background:"#008577"}}>Trusted locked strategy</span>
          <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginTop:8}}>
            Locked strategies count toward public Scoreboard, Profile, Community, ROI, and achievements.
          </div>
        </div>
        <div style={{border:"1px solid #eee",borderRadius:10,padding:"12px",background:"#fff"}}>
          <span className="sec-badge" style={{background:"#5d6675"}}>What-if simulation only</span>
          <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginTop:8}}>
            What-if simulations do not count toward public Scoreboard, Profile, Community, ROI, or achievements.
          </div>
        </div>
      </div>
    </div>
  );
}

function AddToHomeScreenPrompt({user}) {
  const storageKey="lt:pwaInstallDismissed:"+(user?.id||user?.email||"local");
  const[installPrompt,setInstallPrompt]=useState(null);
  const[dismissed,setDismissed]=useState(()=>localStorage.getItem(storageKey)==="true");
  const[installed,setInstalled]=useState(false);
  const[busy,setBusy]=useState(false);
  useEffect(()=>{
    const standalone=window.matchMedia?.("(display-mode: standalone)")?.matches||window.navigator.standalone===true;
    setInstalled(!!standalone);
    setDismissed(localStorage.getItem(storageKey)==="true");
  },[storageKey]);
  useEffect(()=>{
    function onBeforeInstallPrompt(event){
      event.preventDefault();
      setInstallPrompt(event);
      setDismissed(localStorage.getItem(storageKey)==="true");
    }
    function onInstalled(){
      setInstalled(true);
      setInstallPrompt(null);
    }
    window.addEventListener("beforeinstallprompt",onBeforeInstallPrompt);
    window.addEventListener("appinstalled",onInstalled);
    return()=>{
      window.removeEventListener("beforeinstallprompt",onBeforeInstallPrompt);
      window.removeEventListener("appinstalled",onInstalled);
    };
  },[storageKey]);
  if(installed||dismissed)return null;
  const ua=window.navigator.userAgent||"";
  const isiOS=/iphone|ipad|ipod/i.test(ua);
  const isSafari=isiOS&&/safari/i.test(ua)&&!/crios|fxios|edgios/i.test(ua);
  async function install(){
    if(!installPrompt)return;
    setBusy(true);
    installPrompt.prompt();
    await installPrompt.userChoice.catch(()=>null);
    setInstallPrompt(null);
    setBusy(false);
  }
  function dismiss(){
    localStorage.setItem(storageKey,"true");
    setDismissed(true);
  }
  if(!installPrompt&&!isSafari)return null;
  return(
    <div className="panel" style={{border:"1px solid #dbeee9",background:"#f8fbfa",display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,flexWrap:"wrap",marginBottom:18}}>
      <div style={{minWidth:240,flex:"1 1 320px"}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:"#002855",textTransform:"uppercase",marginBottom:4}}>Install LottoTracker</div>
        <div style={{fontSize:13,color:"#555",lineHeight:1.45}}>
          {installPrompt
            ?"Add LottoTracker to your phone home screen for a faster app-like experience."
            :"On iPhone, open Safari's Share button, then choose Add to Home Screen."}
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        {installPrompt&&<button type="button" className={"btn btn-p "+(busy?"loading":"")} disabled={busy} onClick={install}>{busy?"Installing":"Add to Home Screen"}</button>}
        <button type="button" className="btn btn-s" onClick={dismiss}>Maybe later</button>
      </div>
    </div>
  );
}

function NextDrawWidget({myGames,activePlays,onViewResults,drawStatus}){
  const entries=drawStatus?.entries||getNextDrawEntries(myGames);
  const next=drawStatus?.next||entries.find(e=>e.sortAt>=Date.now())||entries[0];
  if(!next){
    return(
      <div style={{background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.14)",borderRadius:14,padding:16,width:"100%"}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,.65)",textTransform:"uppercase",letterSpacing:2,marginBottom:8}}>Your Next Draw</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.75)",lineHeight:1.45}}>Choose a game in My Games to see your next draw.</div>
      </div>
    );
  }
  const countdownClass=next.status==="Results Ready"?"":next.countdownMs<=5*60000?"lt-countdown-danger":next.countdownMs<=30*60000?"lt-countdown-warn":"";
  return(
    <div style={{background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.14)",borderRadius:14,padding:16,width:"100%"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:10}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,.65)",textTransform:"uppercase",letterSpacing:2}}>Your Next Draw</div>
        <span style={{background:next.statusColor,color:"#fff",fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:10,textTransform:"uppercase",whiteSpace:"nowrap"}}>{next.status}</span>
      </div>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#fff",textTransform:"uppercase",lineHeight:1,marginBottom:4}}>{next.gameLabel}</div>
      <div style={{fontSize:12,color:"#D4AF37",fontWeight:700,textTransform:"uppercase",marginBottom:10}}>{next.drawLabel} Draw</div>
      <div className={countdownClass} style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:next.status==="Results Ready"?44:54,fontWeight:900,color:"#fff",lineHeight:.95,marginBottom:12}}>
        {next.status==="Results Ready"?"Results Ready":next.countdown}
      </div>
      {next.status==="Results Ready"&&<button type="button" className="btn btn-p" onClick={onViewResults} style={{marginBottom:14}}>View Results</button>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(128px,1fr))",gap:10}}>
        <div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.5)",textTransform:"uppercase",fontWeight:700,marginBottom:3}}>Draw Time</div>
          <div style={{fontSize:13,color:"#fff",fontWeight:600}}>{next.drawTime}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.5)",textTransform:"uppercase",fontWeight:700,marginBottom:3}}>Sales Close Time</div>
          <div style={{fontSize:13,color:"#fff",fontWeight:600}}>{next.closeTime}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.5)",textTransform:"uppercase",fontWeight:700,marginBottom:3}}>Time Remaining</div>
          <div style={{fontSize:13,color:"#fff",fontWeight:600}}>{next.status==="Results Ready"?"Ready now":next.countdown}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.5)",textTransform:"uppercase",fontWeight:700,marginBottom:3}}>Active Plays</div>
          <div style={{fontSize:13,color:"#fff",fontWeight:600}}>{activePlays}</div>
        </div>
      </div>
    </div>
  );
}

function MilestoneStrip({plays,draws,recentWins,topStrategy,storageKey}){
  const milestones=[
    {id:"first-numbers",icon:"🎉",label:"First numbers saved",done:(plays||[]).length>0},
    {id:"first-3-match",icon:"🏆",label:"First 3-match",done:(recentWins||[]).some(w=>w.best>=3)},
    {id:"profitable-strategy",icon:"📈",label:"First profitable strategy",done:!!topStrategy&&Number(topStrategy.roi)>0},
    {id:"tracked-100",icon:"⭐",label:"First 100 tracked draws",done:(draws||[]).length>=100},
  ];
  const[seen,setSeen]=useState(()=>{
    if(!storageKey)return [];
    try{return JSON.parse(localStorage.getItem(storageKey)||"[]");}catch{return [];}
  });
  const visible=milestones.filter(m=>m.done&&!seen.includes(m.id));
  useEffect(()=>{
    if(!storageKey||!visible.length)return;
    const next=[...new Set([...seen,...visible.map(m=>m.id)])];
    localStorage.setItem(storageKey,JSON.stringify(next));
    const t=setTimeout(()=>setSeen(next),4200);
    return()=>clearTimeout(t);
  },[storageKey,visible.map(m=>m.id).join(","),seen.join(",")]);
  if(!visible.length)return null;
  return(
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:10,marginBottom:20}}>
      {visible.map(m=>(
        <div key={m.id} style={{background:"#fff",border:"1px solid #dbeee9",borderLeft:"5px solid #008577",borderRadius:10,padding:"12px 14px",boxShadow:"0 2px 10px rgba(0,0,0,.05)"}}>
          <div style={{fontSize:20,marginBottom:4}}>{m.icon}</div>
          <div style={{fontSize:13,fontWeight:700,color:"#002855"}}>{m.label}</div>
          <div style={{fontSize:11,color:"#008577",fontWeight:700,textTransform:"uppercase",marginTop:3}}>Milestone reached</div>
        </div>
      ))}
    </div>
  );
}

function TodaySchedule({myGames,drawStatus}){
  const rows=getDashboardSchedule(myGames,drawStatus?.now||new Date());
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div className="sec-title" style={{fontSize:20,marginBottom:12}}>Today's Schedule</div>
      <div style={{display:"grid",gap:9}}>
        {rows.map(r=>(
          <div key={r.game} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:10,alignItems:"center",borderBottom:"1px solid #f0f0f0",paddingBottom:8}}>
            <span style={{fontSize:20}}>{r.icon}</span>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#002855"}}>{r.gameLabel}{r.drawLabel!=="Night"?" "+r.drawLabel:""}</div>
              <div style={{fontSize:11,color:"#777"}}>{r.closeTime}</div>
            </div>
            <div style={{fontSize:12,fontWeight:800,color:r.statusColor,textAlign:"right",textTransform:r.detail.length<10?"uppercase":"none"}}>{r.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TodayLotteryHub({user,myGames,plays,latestReady,drawStatus,onAddNumbers,onQuickPick,onViewResults,onPool,onScoreboard,onMyNumbers,onStrategyLibrary,onCommunity,onCreatePool,onStrategyBuilder,simpleMode=false}){
  const now=drawStatus?.now||new Date();
  const dateKey=now.getFullYear()+"-"+String(now.getMonth()+1).padStart(2,"0")+"-"+String(now.getDate()).padStart(2,"0");
  const userKey=(user?.id||user?.email||"local").replace(/[^a-zA-Z0-9_-]/g,"_");
  const checklistKey="lt:todayChecklist:"+userKey+":"+dateKey;
  const selectedGames=normalizeGames(myGames);
  const entries=drawStatus?.entries||getNextDrawEntries(selectedGames,now);
  const next=drawStatus?.next||entries.find(entry=>entry.sortAt>=now.getTime())||entries[0];
  const gameEntries=selectedGames.map(game=>{
    const options=entries.filter(entry=>entry.game===game);
    return options.find(entry=>entry.sortAt>=now.getTime())||options[0]||null;
  }).filter(Boolean);
  const todayLabel=now.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
  const[checked,setChecked]=useState(()=>{
    try{return JSON.parse(localStorage.getItem(checklistKey)||"{}");}catch{return {};}
  });
  useEffect(()=>{
    try{setChecked(JSON.parse(localStorage.getItem(checklistKey)||"{}"));}catch{setChecked({});}
  },[checklistKey]);
  function toggleCheck(id){
    setChecked(prev=>{
      const nextState={...prev,[id]:!prev[id]};
      localStorage.setItem(checklistKey,JSON.stringify(nextState));
      return nextState;
    });
  }
  function markThen(id,fn){
    setChecked(prev=>{
      const nextState={...prev,[id]:true};
      localStorage.setItem(checklistKey,JSON.stringify(nextState));
      return nextState;
    });
    if(fn)fn();
  }
  const checklist=simpleMode?[
    {id:"numbers",label:"Added today's numbers",done:checked.numbers||((plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).length>0)},
    {id:"strategy",label:"Created a strategy",done:checked.strategy||((plays||[]).some(p=>p.strategy&&p.strategy!=="manual"))},
    {id:"watching",label:"Chose what to watch today",done:checked.watching},
    {id:"results",label:"Checked today's results",done:checked.results||latestReady},
  ]:[
    {id:"numbers",label:"Added today's numbers",done:checked.numbers||((plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).length>0)},
    {id:"results",label:"Checked today's results",done:checked.results||latestReady},
    {id:"scoreboard",label:"Viewed scoreboard",done:checked.scoreboard},
    {id:"simulation",label:"Ran one simulation",done:checked.simulation},
    {id:"community",label:"Checked community",done:checked.community},
  ];
  const doneCount=checklist.filter(item=>item.done).length;
  const progress=Math.round((doneCount/checklist.length)*100);
  const hasNumbers=(plays||[]).some(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT);
  const flowSteps=simpleMode?[
    {label:"Numbers Saved",done:hasNumbers},
    {label:"Strategy Created",done:!!checked.strategy||((plays||[]).some(p=>p.strategy&&p.strategy!=="manual"))},
    {label:"Watching Today",done:!!checked.watching},
    {label:"Results Reviewed",done:!!checked.results||latestReady},
  ]:[
    {label:"Numbers Saved",done:hasNumbers},
    {label:"Waiting for Draw",done:!!next&&next.status!=="Results Ready"},
    {label:"Results Posted",done:latestReady||next?.status==="Results Ready"},
    {label:"Results Reviewed",done:!!checked.results},
    {label:"Strategy Compared",done:!!checked.scoreboard},
    {label:"Community Checked",done:!!checked.community},
  ];
  let smartActions=[
    {label:"Browse Strategies",onClick:onStrategyLibrary},
    {label:"Join Community",onClick:()=>markThen("community",onCommunity)},
    {label:"Create Pool",onClick:onCreatePool},
  ];
  if(simpleMode){
    smartActions=next?.status==="Results Ready"?[
      {label:"View Results",onClick:()=>markThen("results",onViewResults)},
      {label:"Create Strategy",onClick:()=>markThen("strategy",onStrategyBuilder)},
      {label:"Browse Strategies",onClick:onStrategyLibrary},
    ]:next?.status==="Sales Closed"||next?.status==="Drawing In Progress"?[
      {label:"View Today's Draw",onClick:onViewResults},
      {label:"View My Playslips",onClick:onMyNumbers},
      {label:"Browse Strategies",onClick:onStrategyLibrary},
    ]:[
      {label:"Create Strategy",onClick:onAddNumbers},
      {label:"Create Strategy",onClick:()=>markThen("strategy",onStrategyBuilder)},
      {label:"View Results",onClick:onViewResults},
    ];
  }else
  if(next?.status==="Results Ready"){
    smartActions=[
      {label:"View Results",onClick:()=>markThen("results",onViewResults)},
      {label:"View Scoreboard",onClick:()=>markThen("scoreboard",onScoreboard)},
      {label:"Compare Strategies",onClick:()=>markThen("scoreboard",onScoreboard)},
    ];
  }else if(next?.status==="Sales Closed"||next?.status==="Drawing In Progress"){
    smartActions=[
      {label:"View Today's Draw",onClick:onViewResults},
      {label:"Open Pool",onClick:onPool},
      {label:"Strategy Library",onClick:onStrategyLibrary},
    ];
  }else if(next){
    smartActions=[
      {label:"Create Strategy",onClick:onAddNumbers},
      {label:"Quick Pick",onClick:onQuickPick},
      {label:"View My Playslips",onClick:onMyNumbers},
    ];
  }
  const seed=(dateKey+":"+userKey).split("").reduce((sum,ch)=>sum+ch.charCodeAt(0),0);
  const colors=["Teal","Gold","Ruby","Sky Blue","Forest Green","Silver"];
  const times=["11:15 PM","7:30 PM","4:44 PM","12:12 PM","8:08 PM","9:21 PM"];
  const luck={
    color:colors[seed%colors.length],
    number:String((seed%36)+1).padStart(2,"0"),
    pair:String(((seed*3)%36)+1).padStart(2,"0")+" + "+String(((seed*7)%36)+1).padStart(2,"0"),
    time:times[seed%times.length],
  };
  const countdownClass=next&&next.status!=="Results Ready"?(next.countdownMs<=5*60000?"lt-countdown-danger":next.countdownMs<=30*60000?"lt-countdown-warn":""):"";
  function displayStatus(status){
    if(status==="Sales Closing Soon")return "Closing Soon";
    if(status==="Drawing In Progress")return "Drawing";
    return status||"Upcoming";
  }
  function shortTime(label){
    return String(label||"").replace(/^Today, /,"").replace(/^Tomorrow, /,"Tomorrow ");
  }
  function gameDetail(entry){
    const day=String(entry.drawTime||"").split(",")[0];
    if(entry.status==="Results Ready")return {main:"Results Ready",sub:"Tap to view results"};
    if(day==="Tomorrow")return {main:"Tomorrow",sub:shortTime(entry.drawTime)};
    if(day&&day!=="Today")return {main:day,sub:shortTime(entry.drawTime)};
    return {main:shortTime(entry.drawTime),sub:entry.countdown};
  }
  function dotColor(entry){
    if(!entry)return "#2E6DA4";
    if(String(entry.drawTime||"").startsWith("Tomorrow"))return "#2E6DA4";
    if(entry.status==="Results Ready")return "#D4AF37";
    if(entry.status==="Sales Closing Soon")return "#c8860a";
    if(entry.status==="Drawing In Progress")return "#005bbb";
    if(entry.status==="Sales Closed")return "#c8102e";
    return "#008577";
  }
  return(
    <section className="today-hub" aria-label="Today's Lottery Hub">
      <div className="today-hub-top">
        <div>
          <div className="today-hub-label">Today</div>
          <div className="today-hub-date">{todayLabel}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:5}}>Daily Command Center</div>
          <div style={{fontSize:13,color:"#555"}}>{progress}% checklist complete</div>
        </div>
      </div>

      <div className="today-next">
        <div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.62)",fontWeight:900,textTransform:"uppercase",letterSpacing:2,marginBottom:9}}>Next Draw</div>
          {next?(
            <>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,textTransform:"uppercase",lineHeight:1}}>{next.gameLabel} {next.drawLabel}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginTop:12}}>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:900,textTransform:"uppercase"}}>Game</div><strong>{next.gameLabel} {next.drawLabel}</strong></div>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:900,textTransform:"uppercase"}}>Draw Time</div><strong>{shortTime(next.drawTime)}</strong></div>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:900,textTransform:"uppercase"}}>Sales Close</div><strong>{shortTime(next.closeTime)}</strong></div>
                <div><div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:900,textTransform:"uppercase"}}>Status</div><strong style={{color:next.statusColor}}>{displayStatus(next.status)}</strong></div>
              </div>
            </>
          ):(
            <div style={{fontSize:14,color:"rgba(255,255,255,.78)",lineHeight:1.45}}>Choose a game in My Games to see today's next draw.</div>
          )}
        </div>
        <div className={countdownClass+" today-countdown"}>
          {next?(next.status==="Results Ready"?"Results Ready":next.countdown):"--"}
        </div>
      </div>

      <div style={{marginTop:16}}>
        <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Today's Lottery Progress</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:8}}>
          {flowSteps.map(step=>(
            <div key={step.label} style={{background:step.done?"#f0f8f6":"#f7f8f8",border:"1px solid "+(step.done?"#cfe9e3":"#eee"),borderRadius:10,padding:"9px 10px",display:"flex",gap:8,alignItems:"center"}}>
              <span style={{width:20,height:20,borderRadius:"50%",background:step.done?"#008577":"#ddd",color:step.done?"#fff":"#777",display:"inline-flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:12}}>{step.done?"\u2713":"-"}</span>
              <strong style={{fontSize:12,color:step.done?"#002855":"#666"}}>{step.label}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="today-hub-grid">
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Today's Games</div>
          <div className="today-games">
            {gameEntries.length?gameEntries.map(entry=>{
              const detail=gameDetail(entry);
              return(
                <button key={entry.game} type="button" className="today-game-row" onClick={entry.status==="Results Ready"?()=>markThen("results",onViewResults):undefined}>
                  <span className="today-game-dot" style={{background:dotColor(entry)}}/>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:900,color:"#002855"}}>{entry.gameLabel}{entry.drawLabel!=="Night"?" "+entry.drawLabel:""}</div>
                    <div style={{fontSize:12,color:"#666",marginTop:2}}>{detail.sub}</div>
                  </div>
                  <div style={{fontSize:13,fontWeight:900,color:entry.statusColor,textAlign:"right",whiteSpace:"nowrap"}}>{detail.main}</div>
                </button>
              );
            }):(
              <div style={{background:"#fbfdfc",border:"1px solid #edf1f0",borderRadius:12,padding:14,fontSize:13,color:"#555",lineHeight:1.45}}>Choose at least one game in My Games to fill your daily schedule.</div>
            )}
          </div>
        </div>

        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Today's Checklist</div>
          <div style={{height:9,background:"#edf1f0",borderRadius:99,overflow:"hidden",marginBottom:10}}>
            <div style={{height:"100%",width:progress+"%",background:"#008577",transition:"width .22s ease"}}/>
          </div>
          <div style={{display:"grid",gap:7,marginBottom:14}}>
            {checklist.map(item=>(
              <button key={item.id} type="button" className={"today-check-row"+(item.done?" done":"")} onClick={()=>toggleCheck(item.id)}>
                <span className="today-check-box">{item.done?"\u2713":""}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="today-hub-grid">
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Today's Quick Actions</div>
          <div className="today-actions">
            {smartActions.map((action,i)=>(
              <button key={action.label} type="button" className={"btn "+(i===0?"btn-p":"btn-s")} onClick={action.onClick}>{action.label}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Today's Luck</div>
          <div style={{fontSize:12,color:"#666",lineHeight:1.4,marginBottom:10}}>Random inspiration only. No calculations.</div>
          <div className="today-luck-grid">
            <div className="today-luck-item"><div style={{fontSize:10,color:"#7b6500",fontWeight:900,textTransform:"uppercase"}}>Lucky Color</div><strong>{luck.color}</strong></div>
            <div className="today-luck-item"><div style={{fontSize:10,color:"#7b6500",fontWeight:900,textTransform:"uppercase"}}>Lucky Number</div><strong>{luck.number}</strong></div>
            <div className="today-luck-item"><div style={{fontSize:10,color:"#7b6500",fontWeight:900,textTransform:"uppercase"}}>Lucky Pair</div><strong>{luck.pair}</strong></div>
            <div className="today-luck-item"><div style={{fontSize:10,color:"#7b6500",fontWeight:900,textTransform:"uppercase"}}>Lucky Time</div><strong>{luck.time}</strong></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveHeader({user,myGames}){
  const now=new Date();
  const hour=now.getHours();
  const greeting=hour<12?"Good Morning":hour<17?"Good Afternoon":"Good Evening";
  const name=(user?.user_metadata?.name||"Eric").split(" ")[0]||"Eric";
  const next=getNextDrawEntries(myGames,now)[0];
  return(
    <div className="panel" style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:14,alignItems:"center",marginBottom:18}}>
      <div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#002855",textTransform:"uppercase",lineHeight:1}}>{greeting} {name}</div>
        <div style={{fontSize:13,color:"#555",marginTop:5}}>{now.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
      </div>
      <div style={{textAlign:"right"}}>
        <div style={{fontSize:10,color:"#555",fontWeight:800,textTransform:"uppercase",letterSpacing:1}}>Today's Next Draw</div>
        <div style={{fontSize:15,fontWeight:800,color:"#002855"}}>{next?next.gameLabel+" "+next.drawLabel:"Choose a game"}</div>
        <div style={{fontSize:12,color:next?.statusColor||"#555",fontWeight:800}}>{next?next.drawTime:"No draw selected"}</div>
      </div>
    </div>
  );
}

function DailyStreakCard({streak}){
  const tier=streak>=100?"100 Day":streak>=30?"30 Day":streak>=7?"7 Day":(streak||1)+" Day";
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <div style={{fontSize:36,lineHeight:1}}>{"\uD83D\uDD25"}</div>
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:3}}>Daily Streak</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#c8102e",lineHeight:1}}><AnimatedValue value={streak||1}/> Day</div>
          <div style={{fontSize:12,color:"#555",marginTop:4}}>Current badge: {tier}</div>
        </div>
      </div>
    </div>
  );
}

function DailyGoalCard({plays,latestReady,featureUnlocks,onDraw,onNumbers,onScoreboard,onPool}){
  const goals=[
    {id:"draw",label:"Check today's draw",done:!!latestReady,onClick:onDraw},
    {id:"save",label:"Save one new play",done:(plays||[]).some(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT),onClick:onNumbers},
    {id:"scoreboard",label:"View Scoreboard",done:!!featureUnlocks?.scoreboard,onClick:onScoreboard},
    {id:"pool",label:"Run one Pool Simulation",done:!!featureUnlocks?.poolSimulation,onClick:onPool},
  ];
  const complete=goals.filter(g=>g.done).length;
  const pct=Math.round((complete/goals.length)*100);
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div className="sec-hdr" style={{marginBottom:10}}>
        <span className="sec-title" style={{fontSize:20}}>Today's Goal</span>
        <span className="sec-badge">{pct}%</span>
      </div>
      <div style={{height:8,background:"#edf1f0",borderRadius:8,overflow:"hidden",marginBottom:12}}>
        <div style={{height:"100%",width:pct+"%",background:"#008577",transition:"width .25s ease"}}/>
      </div>
      <div style={{display:"grid",gap:8}}>
        {goals.map(g=>(
          <button key={g.id} type="button" onClick={g.onClick} className="btn btn-s" style={{display:"flex",justifyContent:"space-between",alignItems:"center",textAlign:"left",textTransform:"none",padding:"8px 10px"}}>
            <span>{g.done?"\u2713":"\u25CB"} {g.label}</span>
            <span style={{fontSize:11,color:g.done?"#008577":"#777",fontWeight:800,textTransform:"uppercase"}}>{g.done?"Done":"Open"}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProgressBadges({plays,draws,featureUnlocks}) {
  const playCount=(plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).length;
  const drawCount=(draws||[]).length;
  const badges=[
    {label:"Beginner",done:playCount>0},
    {label:"Tracker",done:drawCount>=5},
    {label:"Play Builder",done:!!featureUnlocks?.scoreboard},
    {label:"Simulation Expert",done:!!featureUnlocks?.poolSimulation},
    {label:"Pool Manager",done:!!featureUnlocks?.poolManager},
    {label:"Legend",done:drawCount>=1000},
  ];
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div className="sec-title" style={{fontSize:20,marginBottom:12}}>Progress Badges</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8}}>
        {badges.map(b=>(
          <div key={b.label} style={{border:"1px solid "+(b.done?"#cfe9e3":"#eee"),background:b.done?"#f0f8f6":"#fafafa",borderRadius:10,padding:"10px 9px"}}>
            <div style={{fontSize:20,marginBottom:4}}>{b.done?"\u2B50":"\u25CB"}</div>
            <div style={{fontSize:12,fontWeight:800,color:b.done?"#008577":"#777"}}>{b.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardTip(){
  const tips=[
    "Try comparing your top strategies.",
    "Quick Picks sometimes outperform favorites.",
    "Fantasy 5 has two drawings every day.",
    "Track one strategy for 30 draws before judging it.",
  ];
  const day=Math.floor(Date.now()/86400000);
  const tip=tips[day%tips.length];
  return(
    <div className="panel" style={{marginBottom:0,borderLeft:"5px solid #D4AF37"}}>
      <div style={{fontSize:22,marginBottom:6}}>{"\uD83D\uDCA1"}</div>
      <div className="sec-title" style={{fontSize:20,marginBottom:6}}>Dashboard Tip</div>
      <div style={{fontSize:13,color:"#555",lineHeight:1.45}}>{tip}</div>
    </div>
  );
}

function WeeklySummary({draws,topStrategy,recentWins}) {
  const bestMatch=recentWins?.length?Math.max(...recentWins.map(w=>w.best)):0;
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div className="sec-title" style={{fontSize:20,marginBottom:12}}>This Week</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,fontSize:12}}>
        <div><span style={{color:"#555"}}>Draws Tracked</span><br/><strong style={{fontSize:24,color:"#002855",fontFamily:"'Barlow Condensed',sans-serif"}}><AnimatedValue value={Math.min((draws||[]).length,14)}/></strong></div>
        <div><span style={{color:"#555"}}>Best Strategy</span><br/><strong>{topStrategy?.meta?.label||"Keep tracking"}</strong></div>
        <div><span style={{color:"#555"}}>Best Match</span><br/><strong style={{fontSize:24,color:"#008577",fontFamily:"'Barlow Condensed',sans-serif"}}><AnimatedValue value={bestMatch}/></strong></div>
        <div><span style={{color:"#555"}}>ROI</span><br/><strong>{topStrategy?.roi!=null?(topStrategy.roi>=0?"+":"")+topStrategy.roi.toFixed(0)+"%":"Demo pending"}</strong></div>
        <div><span style={{color:"#555"}}>Pool Simulations</span><br/><strong style={{fontSize:24,color:"#c8860a",fontFamily:"'Barlow Condensed',sans-serif"}}><AnimatedValue value={2}/></strong></div>
      </div>
    </div>
  );
}

function AchievementTimeline({plays,draws,recentWins}) {
  const playCount=(plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).length;
  const drawCount=(draws||[]).length;
  const achievements=[
    {label:"First Saved Numbers",done:playCount>0},
    {label:"First Match",done:(recentWins||[]).some(w=>w.best>=1)||playCount>0},
    {label:"First 3 Match",done:(recentWins||[]).some(w=>w.best>=3)},
    {label:"Tracked 100 Draws",done:drawCount>=100},
    {label:"Tracked 500 Draws",done:drawCount>=500},
    {label:"Tracked 1000 Draws",done:drawCount>=1000},
  ];
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div className="sec-title" style={{fontSize:20,marginBottom:12}}>Achievement Timeline</div>
      <div style={{display:"grid",gap:9}}>
        {achievements.map((a,i)=>(
          <div key={a.label} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr)",gap:10,alignItems:"center"}}>
            <span style={{width:28,height:28,borderRadius:"50%",background:a.done?"#008577":"#e7e7e7",color:a.done?"#fff":"#777",display:"inline-flex",alignItems:"center",justifyContent:"center",fontWeight:900}}>{a.done?"\u2713":i+1}</span>
            <span style={{fontSize:13,fontWeight:800,color:a.done?"#002855":"#777"}}>{a.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function shareBaseUrl(){
  return typeof window!=="undefined"&&window.location?.origin?window.location.origin:"https://lottotracker.vercel.app";
}
function slugifyShare(value){
  return String(value||"lottotracker").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"").slice(0,48)||"lottotracker";
}
function shareIdSegment(value){
  return String(value||"lottotracker").toLowerCase().replace(/[^a-z0-9_-]+/g,"-").replace(/^-|-$/g,"").slice(0,120)||"lottotracker";
}
function shortShareLink(type,id,isPublic=false){
  if(type==="strategy"&&!isPublic)return "";
  return shareBaseUrl()+"/s/"+slugifyShare(type)+"/"+shareIdSegment(id);
}
function compactShareValue(value,max=34){
  const text=String(value||"").replace(/\s+/g," ").trim();
  if(text.length<=max)return text;
  return text.slice(0,Math.max(0,max-1)).trim()+"...";
}
function shareMetricFontSize(value,base=34){
  const len=String(value||"").length;
  if(len>38)return Math.max(16,base-14);
  if(len>26)return Math.max(18,base-10);
  if(len>18)return Math.max(20,base-6);
  return base;
}
function shareDrawTime(entry){
  if(!entry)return "Next draw";
  return (entry.gameLabel?entry.gameLabel+" "+entry.drawLabel:"Draw")+" · "+(entry.drawTime||"Time pending");
}
function formatShareDrawTime(entry){
  if(!entry)return "Next draw";
  return (entry.drawLabel||"Draw")+" / "+(entry.drawDate||entry.drawTime||"date pending");
}
function sharePlayTypeLabel(game,playType){
  const gameId=String(game||"").toLowerCase().replace(/\s+/g,"");
  const base=PICK_GAME_IDS.some(id=>id===gameId||gameId===gameLabel(id).toLowerCase().replace(/\s+/g,""))
    ?"$1 Straight Box"
    :gameId==="fantasy5"?"$1 Fantasy 5 play":"$1 play";
  return playType==="Real Play"?base:base+" · simulation";
}
function sharePossibleWinLabel(game){
  return shareTopPrizeLabel(game);
}
function normalizeShareGame(game){
  const gameId=String(game||"").toLowerCase().replace(/\s+/g,"");
  if(gameId==="fantasy5"||gameId==="fantasyfive")return "fantasy5";
  return PICK_GAME_IDS.find(id=>id===gameId||gameId===gameLabel(id).toLowerCase().replace(/\s+/g,""))||gameId;
}
function shareTopPrizeAmount(game){
  const gameId=normalizeShareGame(game);
  if(gameId==="fantasy5")return "$100,000";
  if(gameId==="pick5")return "$50,000";
  if(gameId==="pick4")return "$5,000";
  if(gameId==="pick3")return "$500";
  if(gameId==="pick2")return "$50";
  return "the top prize";
}
function shareTopPrizeLabel(game){
  const gameId=normalizeShareGame(game);
  if(gameId==="fantasy5")return "Estimated $100,000 top prize";
  if(gameId==="pick5")return "$50,000 top prize";
  if(gameId==="pick4")return "$5,000 top prize";
  if(gameId==="pick3")return "$500 top prize";
  if(gameId==="pick2")return "$50 top prize";
  return "Top prize";
}
function normalizeShareStrategyName(name){
  const text=String(name||"").replace(/\s+/g," ").trim();
  if(!text||/^manual$/i.test(text))return "Eric's Strategy";
  return text;
}
function shareOwnerName(name){
  const text=normalizeShareStrategyName(name);
  const possessive=text.match(/^(.+?)'s\b/i);
  if(possessive?.[1])return possessive[1];
  return text.split(/\s+/)[0]||"This player";
}
function shareMoneyNumber(value){
  const parsed=Number(String(value||"").replace(/[^0-9.-]/g,""));
  return Number.isFinite(parsed)?parsed:0;
}
function shareNetLabel(won,cost){
  const net=shareMoneyNumber(won)-shareMoneyNumber(cost);
  return (net<0?"-":"")+fmtMoney(Math.abs(net));
}
function shareBestMatchLabel(value,game){
  if(String(value||"").includes("out of"))return value;
  const match=String(value||"").match(/\d+/);
  const count=match?Number(match[0]):0;
  const gameId=normalizeShareGame(game);
  const total=gameId==="pick2"?2:gameId==="pick3"?3:gameId==="pick4"?4:5;
  return count?count+" out of "+total:"Pending";
}
function publicDigitMatchKinds(numbers=[],official=[],game=""){
  const row=Array.isArray(numbers)?numbers.map(Number):[];
  const draw=Array.isArray(official)?official.map(Number):[];
  if(!row.length||!draw.length)return row.map(()=>"");
  if(!String(game||"").toLowerCase().startsWith("pick")){
    const drawSet=new Set(draw);
    return row.map(n=>drawSet.has(Number(n))?"exact":"");
  }
  const kinds=row.map((n,index)=>Number(n)===Number(draw[index])?"exact":"");
  const remaining=new Map();
  draw.forEach((n,index)=>{
    if(kinds[index]==="exact")return;
    remaining.set(Number(n),(remaining.get(Number(n))||0)+1);
  });
  row.forEach((n,index)=>{
    if(kinds[index])return;
    const key=Number(n);
    const count=remaining.get(key)||0;
    if(count>0){
      kinds[index]="wrong-position";
      remaining.set(key,count-1);
    }
  });
  return kinds;
}
function shareFireballNote(game,selected=false,status=""){
  const gameId=String(game||"").toLowerCase().replace(/\s+/g,"");
  if(!PICK_GAME_IDS.some(id=>id===gameId||gameId===gameLabel(id).toLowerCase().replace(/\s+/g,"")))return "No extra play selected";
  if(String(status||"").toLowerCase()==="unavailable")return "Fireball not scored (result unavailable)";
  return selected?"Fireball/extra play selected":"No Fireball/extra play selected";
}
function shareFireballPhrase(selected=false){
  return selected?"Fireball included":"Fireball not included";
}
function shareLineRulesDigest(record){
  const game=record?.game||"fantasy5";
  if(!isPickGame(game))return null;
  const entries=strategySnapshotPlayEntries(record);
  if(!entries.length)return null;
  const rules=entries.map(entry=>entry.pickRules||pickLineRulesFromItem(null,record?.pickRules||{}));
  const first=rules[0]||{};
  const uniform=rules.every(rule=>
    String(rule.playType||"Straight")===String(first.playType||"Straight")
    &&Number(rule.wager||1)===Number(first.wager||1)
    &&Number(rule.multiple||1)===Number(first.multiple||1)
    &&!!rule.fireball===!!first.fireball
  );
  const multiples=rules.map(rule=>Math.max(1,Number(rule.multiple)||1));
  const uniformMultiple=multiples.every(value=>value===multiples[0]);
  const fireballs=rules.map(rule=>!!rule.fireball);
  const uniformFireball=fireballs.every(value=>value===fireballs[0]);
  const playType=first.playType||"Straight";
  const wager=Number(first.wager)||1;
  const multiple=Math.max(1,Number(first.multiple)||1);
  const baseLabel=moneyLabel(wager)+" "+playType;
  const fireballLabel=uniformFireball?shareFireballPhrase(!!first.fireball):(fireballs.some(Boolean)?"Mixed Fireball":"No Fireball");
  const summaryLine=uniform
    ?[baseLabel,multiple>1?"Each number ×"+multiple:"Each number ×1",fireballLabel].filter(Boolean).join(" · ")
    :["Mixed Pick rules",uniformMultiple&&multiple>1?"Each number ×"+multiple:"Multiples shown by line",fireballLabel].filter(Boolean).join(" · ");
  return{
    entries,
    rules,
    uniform,
    uniformMultiple,
    multiple,
    rowSuffixes:multiples.map(value=>value>1?"×"+value:""),
    rowFireballs:fireballs,
    summaryLine,
    playTypeLabel:uniform?baseLabel:"Mixed Pick rules",
    fireballNote:fireballLabel,
    combinationsLabel:String(entries.length)+" combinations",
    representedPlayCount:entries.reduce((sum,entry)=>sum+pickLineRepresentedTickets(game,entry.numbers,entry.pickRules||{}),0),
  };
}
function normalizeShareNumbers(rows,limit=50){
  return (Array.isArray(rows)?rows:[])
    .map(row=>Array.isArray(row)?row:previewEntryNumbers(row))
    .filter(row=>Array.isArray(row)&&row.length)
    .slice(0,limit)
    .map(row=>row.map(n=>String(n).trim()).filter(Boolean));
}
function shareNumbersLine(row,pad=true){
  return (Array.isArray(row)?row:[]).map(n=>pad?String(n).padStart(2,"0"):String(n)).join("-");
}
function shareNumbersSvgRenderForGame(numbers,game,options={}){
  const maxRows=Number(options.maxRows||50);
  const rows=normalizeShareNumbers(numbers,maxRows);
  const rowSuffixes=Array.isArray(options.rowSuffixes)?options.rowSuffixes:[];
  const isPick=String(normalizeShareGame(game)||"").startsWith("pick");
  const requestedColumns=Number(options.columns);
  const columns=Number.isFinite(requestedColumns)&&requestedColumns>0?Math.max(1,Math.min(4,Math.floor(requestedColumns))):(rows.length>25?2:1);
  const xStart=Number(options.xStart||126);
  const yStart=Number(options.yStart||388);
  const maxY=Number(options.maxY||0);
  const colWidth=Number(options.colWidth||(columns===3?282:columns===2?430:860));
  const rowHeight=Number(options.rowHeight||(columns===3?38:rows.length>36?20:rows.length>24?22:26));
  const balanced=!!options.balanceColumns;
  const naturalPerColumn=balanced?Math.ceil(rows.length/columns):(columns===2?25:rows.length);
  const maxPerColumn=maxY>yStart?Math.min(naturalPerColumn,Math.max(1,Math.floor((maxY-yStart)/rowHeight)+1)):naturalPerColumn;
  const renderedRows=rows.slice(0,columns*maxPerColumn);
  const indexFill=options.indexFill||"#D4AF37";
  const indexTextFill=options.indexTextFill||"#002855";
  const numberFill=options.numberFill||"#002855";
  const fontSize=Number(options.fontSize||(rowHeight>=24?21:18));
  const badgeWidth=Number(options.badgeWidth||46);
  const badgeHeight=Number(options.badgeHeight||Math.max(18,Math.min(24,rowHeight)));
  const svg=renderedRows.map((row,i)=>{
    const col=columns>1?Math.floor(i/maxPerColumn):0;
    const idx=columns>1?i-(col*maxPerColumn):i;
    const x=xStart+col*colWidth;
    const y=yStart+idx*rowHeight;
    const label="#"+String(i+1);
    const suffix=rowSuffixes[i]?" "+String(rowSuffixes[i]):"";
    const numberLine=shareNumbersLine(row,!isPick)+suffix;
    return `<rect x="${x}" y="${y-badgeHeight+5}" width="${badgeWidth}" height="${badgeHeight}" rx="9" fill="${escapeSvgText(indexFill)}"/><text x="${x+badgeWidth/2}" y="${y}" text-anchor="middle" font-family="Arial, sans-serif" font-size="${Math.max(12,fontSize-4)}" font-weight="900" fill="${escapeSvgText(indexTextFill)}">${escapeSvgText(label)}</text><text x="${x+badgeWidth+12}" y="${y}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="900" fill="${escapeSvgText(numberFill)}">${escapeSvgText(numberLine)}</text>`;
  }).join("");
  return{svg,count:renderedRows.length};
}
function shareNumbersSvgRowsForGame(numbers,game,options={}){
  return shareNumbersSvgRenderForGame(numbers,game,options).svg;
}
function sharePoolStatus(entry,locked=false){
  if(locked||entry?.status==="Sales Closed"||entry?.status==="Drawing In Progress")return "Locked";
  if(entry?.status==="Sales Closing Soon"||entry?.countdownMs<=60*60*1000)return "Drawing Soon";
  return "Open";
}
function shareCardPayload(overrides={}){
  const savedAfter=overrides.status==="Saved After Draw"||overrides.isWhatIfOnly;
  const joinDraw=!!overrides.joinDraw&&!savedAfter;
  const strategyName=normalizeShareStrategyName(overrides.strategyName);
  const topPrize=overrides.topPrize||shareTopPrizeLabel(overrides.game);
  const estimatedCost=overrides.estimatedCost||"$0";
  const won=overrides.won||"$0";
  const numbersPreview=normalizeShareNumbers(overrides.numbersPreview||overrides.playsPreview||[],50);
  const numbersTotal=Number(overrides.numbersTotal||overrides.totalPlays||overrides.playCount||numbersPreview.length)||numbersPreview.length;
  const numbersShown=Number(overrides.numbersShown||numbersPreview.length)||numbersPreview.length;
  return{
    type:overrides.type||"strategy",
    title:overrides.title||"LottoTracker Share Card",
    strategyName,
    game:overrides.game||"Fantasy 5",
    status:savedAfter?"What-if only":overrides.status||"Watching tonight's draw",
    roi:overrides.roi||"Pending",
    drawsTracked:overrides.drawsTracked||"0",
    createdDate:overrides.createdDate||"Today",
    link:overrides.link||"",
    isPublic:!!overrides.isPublic,
    accent:overrides.accent||"#008577",
    note:overrides.note||"Track the draw, compare the result, and share your lineup.",
    watchDraw:!!overrides.watchDraw,
    drawTime:overrides.drawTime||"Next draw",
    playCount:overrides.playCount||overrides.drawsTracked||"0",
    numbersPreview,
    numbersShown,
    numbersTotal,
    representedPlayCount:overrides.representedPlayCount||overrides.totalPlays||overrides.playCount||overrides.drawsTracked||"0",
    playTypeLabel:overrides.playTypeLabel||sharePlayTypeLabel(overrides.game,overrides.playType),
    rulesSummaryLine:overrides.rulesSummaryLine||"",
    rowSuffixes:Array.isArray(overrides.rowSuffixes)?overrides.rowSuffixes:[],
    rowFireballs:Array.isArray(overrides.rowFireballs)?overrides.rowFireballs:[],
    combinationsLabel:overrides.combinationsLabel||"",
    estimatedCost,
    possibleWin:overrides.possibleWin||topPrize,
    topPrize,
    afterDraw:!!(overrides.afterDraw||overrides.resultsReady),
    resultCard:!!overrides.resultCard,
    bestMatch:shareBestMatchLabel(overrides.bestMatch,overrides.game),
    won,
    net:overrides.net||shareNetLabel(won,estimatedCost),
    freeTickets:overrides.freeTickets||0,
    winningPlayCount:overrides.winningPlayCount||0,
    largestPrize:overrides.largestPrize||"$0",
    officialNumbers:normalizeShareNumbers([overrides.officialNumbers||[]],1)[0]||[],
    officialFireball:overrides.officialFireball,
    verdict:overrides.verdict||"",
    nearMiss:overrides.nearMiss||"",
    verificationLabel:overrides.verificationLabel||"",
    verificationTime:overrides.verificationTime||"",
    fireballNote:overrides.fireballNote||shareFireballNote(overrides.game,false),
    fireballStatus:overrides.fireballStatus||"",
    ctaLabel:overrides.ctaLabel||(savedAfter?"Watch Strategy":"Watch this draw on LottoTracker"),
    joinDraw,
    isWhatIfOnly:savedAfter,
    poolMode:overrides.poolMode||"Group Play",
    openSpots:overrides.openSpots||"5 spots available",
    suggestedBuyIn:overrides.suggestedBuyIn||"$20 each",
    possiblePayout:overrides.possiblePayout||overrides.possibleWin||sharePossibleWinLabel(overrides.game),
    poolStatus:overrides.poolStatus||overrides.status||"Open",
    joinCtaLabel:overrides.joinCtaLabel||"Request to Join",
    organizerDisclaimer:overrides.organizerDisclaimer||"Organizer handles tickets and payments outside LottoTracker.",
  };
}
function shareSmsText(p){
  const owner=shareOwnerName(p.strategyName);
  if(p.afterDraw){
    return[
      "How did "+owner+" do last night?",
      p.playCount+" numbers played",
      "Best match: "+p.bestMatch,
      "Won: "+p.won,
      "Cost: "+p.estimatedCost,
      "Net: "+p.net,
      "Top prize chased: "+shareTopPrizeAmount(p.game),
      "Watch here: "+(p.link||shareBaseUrl()),
      "LottoTracker tracks numbers only. No tickets sold. No money handled.",
    ].filter(Boolean).join("\n");
  }
  const topPrizePhrase=String(p.topPrize||shareTopPrizeLabel(p.game)).replace(/^Estimated /,"estimated ");
  return[
    owner+" is playing "+p.playCount+" "+p.game+" numbers tonight for a shot at the "+topPrizePhrase+".",
    "Strategy: "+p.strategyName,
    "Draw: "+(p.drawTime||"Evening / date pending"),
    "Plays: "+p.playCount,
    "Cost: "+p.estimatedCost,
    "Estimated top prize: "+shareTopPrizeAmount(p.game),
    "Status: "+p.status,
    "Watch here: "+(p.link||shareBaseUrl()),
    p.joinDraw?p.organizerDisclaimer:"LottoTracker tracks numbers only. No tickets sold. No money handled.",
  ].filter(Boolean).join("\n");
}
function shareShortMessage(p){
  const link=p.link||shareBaseUrl();
  if(p.resultCard||p.afterDraw){
    return[
      "\uD83C\uDFAF Here's what happened.",
      "",
      "Strategy: "+p.strategyName,
      "",
      "Tracked from a saved LottoTracker lineup.",
      "",
      "View the result or build your own:",
      "",
      link,
    ].join("\n");
  }
  if(p.watchDraw){
    return[
      "\uD83C\uDFAF Here's what I'm playing today.",
      "",
      "Strategy: "+p.strategyName,
      "",
      "Tested against historical lottery draws with LottoTracker.",
      "",
      "View the full strategy or build your own:",
      "",
      link,
    ].join("\n");
  }
  if(p.joinDraw){
    return[
      "\uD83C\uDFAF Join this LottoTracker draw.",
      "",
      "View the lineup details:",
      "",
      link,
    ].join("\n");
  }
  const owner=shareOwnerName(p.strategyName);
  return[
    "\uD83C\uDFAF "+owner+" is tracking this "+p.game+" strategy on LottoTracker.",
    "",
    "Follow this strategy after every draw.",
    "",
    link,
  ].filter(line=>line!==null&&line!==undefined).join("\n");
}
function escapeSvgText(value){
  return String(value??"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
}
function qrBitsFromLink(link){
  const seed=String(link||"lottotracker").split("").reduce((sum,ch,i)=>sum+ch.charCodeAt(0)*(i+3),97);
  return Array.from({length:49},(_,i)=>((seed+i*17+Math.floor(i/7)*31)%5)<2);
}
function shareNumbersSvgRows(numbers){
  const rows=normalizeShareNumbers(numbers,50);
  const columns=rows.length>25?2:1;
  const colWidth=columns===2?430:860;
  const rowHeight=rows.length>36?20:rows.length>24?22:26;
  return rows.map((row,i)=>{
    const col=columns===2&&i>=25?1:0;
    const idx=columns===2&&i>=25?i-25:i;
    const x=126+col*colWidth;
    const y=388+idx*rowHeight;
    const line="#"+String(i+1).padStart(2,"0")+"  "+shareNumbersLine(row);
    return `<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="${rowHeight>=24?21:18}" font-weight="900" fill="#002855">${escapeSvgText(line)}</text>`;
  }).join("");
}
function shareCardSvg(payload){
  const p=shareCardPayload(payload);
  const owner=shareOwnerName(p.strategyName);
  const headline=p.afterDraw?"How did "+owner+" do last night?":owner+" is playing "+p.playCount+" "+p.game+" numbers tonight";
  const metricOne=p.afterDraw?["BEST MATCH",compactShareValue(p.bestMatch,18)]:["PLAYS",compactShareValue(p.playCount,18)];
  const metricTwo=p.afterDraw?["WON",compactShareValue(p.won,22)]:["COST",compactShareValue(p.estimatedCost,22)];
  const metricThree=p.afterDraw?["NET",compactShareValue(p.net,28)]:["TOP PRIZE",compactShareValue(p.topPrize,28)];
  const metricThreeSize=shareMetricFontSize(metricThree[1],30);
  const subLine=compactShareValue(p.joinDraw?p.poolMode+" • "+p.openSpots+" • "+p.suggestedBuyIn:p.playTypeLabel+" • "+p.fireballNote,68);
  const noteLine=compactShareValue(p.joinDraw?p.organizerDisclaimer:p.watchDraw?p.note:"Track your numbers. Compare strategies. Share the journey.",82);
  if(p.afterDraw&&p.resultCard){
    const resultColor=p.verdict==="Came out ahead"?"#008577":(/^Hit\b|^Cash hit\b|^Best match\b|^So close\b/.test(String(p.verdict||""))?"#c8860a":"#002855");
    return renderCanonicalCardB({
      strategyName:p.strategyName,
      game:p.game,
      gameLine:p.game+" | "+p.drawTime,
      verdict:p.verdict||"Result",
      verdictColor:resultColor,
      officialNumbers:p.officialNumbers,
      officialFireball:p.officialFireball,
      amountPlayed:p.estimatedCost,
      amountWon:p.won,
      bestMatch:p.bestMatch,
      rulesLine:p.rulesSummaryLine||"Play details: "+p.playTypeLabel+" · "+p.fireballNote,
      fireballStatus:p.fireballStatus,
      rows:p.numbersPreview,
      rowFireballs:p.rowFireballs,
      numbersTotal:p.numbersTotal||p.playCount,
      explanation:p.nearMiss||"I post the misses too.",
      verificationLabel:p.verificationLabel,
      verificationTime:p.verificationTime,
      footerLink:(p.link||shareBaseUrl()).replace(/^https?:\/\//,""),
      accent:p.accent,
    });
  }
  if(p.watchDraw&&p.numbersPreview.length&&!p.afterDraw&&!p.joinDraw){
    const rulesLine=compactShareValue(p.rulesSummaryLine||p.playTypeLabel+" | "+p.fireballNote,72);
    const combinationsLabel=compactShareValue(p.combinationsLabel||String(p.numbersTotal||p.numbersPreview.length)+" combinations",20);
    const representedLabel=compactShareValue(String(p.representedPlayCount||p.playCount)+" total plays",20);
    const costBase=String(p.estimatedCost||"$0").replace(/\s+(estimated|cost)$/i,"").trim();
    const costLabel=compactShareValue((costBase||"$0")+" cost",18);
    const footerLink=compactShareValue((p.link||shareBaseUrl()).replace(/^https?:\/\//,""),42);
    return renderCanonicalCardA({
      strategyName:p.strategyName,
      game:p.game,
      gameLine:p.game+" | "+p.drawTime,
      rulesLine,
      rows:p.numbersPreview,
      rowSuffixes:p.rowSuffixes,
      rowFireballs:p.rowFireballs,
      numbersTotal:p.numbersTotal||p.playCount,
      combinationsLabel,
      representedLabel,
      costLabel,
      footerLink,
      accent:p.accent,
    });
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1080" viewBox="0 0 1080 1080">
  <defs><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#002855"/><stop offset=".58" stop-color="#073b68"/><stop offset="1" stop-color="${escapeSvgText(p.accent)}"/></linearGradient></defs>
  <rect width="1080" height="1080" rx="54" fill="url(#bg)"/>
  <rect x="74" y="74" width="932" height="932" rx="42" fill="#fffaf0" opacity=".98"/>
  <text x="110" y="150" font-family="Arial, sans-serif" font-size="34" font-weight="800" fill="#002855">Lotto<tspan fill="#D4AF37">Tracker</tspan></text>
  <text x="110" y="195" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#008577" letter-spacing="4">${p.afterDraw?"RESULTS CARD":"WATCH MY DRAW"}</text>
  <text x="110" y="315" font-family="Arial, sans-serif" font-size="64" font-weight="900" fill="#002855">${escapeSvgText(p.strategyName)}</text>
  <text x="110" y="366" font-family="Arial, sans-serif" font-size="30" font-weight="800" fill="${escapeSvgText(p.accent)}">${escapeSvgText(p.game)} • ${escapeSvgText(p.status)}</text>
  <text x="110" y="408" font-family="Arial, sans-serif" font-size="24" font-weight="800" fill="#002855">${escapeSvgText(p.drawTime)}</text>
  <text x="110" y="446" font-family="Arial, sans-serif" font-size="21" font-weight="800" fill="#555">${escapeSvgText(subLine)}</text>
  <rect x="110" y="500" width="250" height="154" rx="26" fill="#ffffff"/><text x="140" y="553" font-family="Arial" font-size="22" font-weight="800" fill="#667">${escapeSvgText(metricOne[0])}</text><text x="140" y="620" font-family="Arial" font-size="52" font-weight="900" fill="${escapeSvgText(p.accent)}">${escapeSvgText(metricOne[1])}</text>
  <rect x="394" y="500" width="250" height="154" rx="26" fill="#ffffff"/><text x="424" y="553" font-family="Arial" font-size="22" font-weight="800" fill="#667">${escapeSvgText(metricTwo[0])}</text><text x="424" y="620" font-family="Arial" font-size="46" font-weight="900" fill="#002855">${escapeSvgText(metricTwo[1])}</text>
  <rect x="678" y="500" width="250" height="154" rx="26" fill="#ffffff"/><text x="708" y="553" font-family="Arial" font-size="22" font-weight="800" fill="#667">${escapeSvgText(metricThree[0])}</text><text x="708" y="613" font-family="Arial" font-size="${metricThreeSize}" font-weight="900" fill="#002855">${escapeSvgText(metricThree[1])}</text>
  <text x="110" y="735" font-family="Arial, sans-serif" font-size="28" font-weight="800" fill="#002855">${escapeSvgText(p.joinDraw?"Watch Draw  •  "+p.joinCtaLabel:p.watchDraw?p.ctaLabel:p.note)}</text>
  <text x="110" y="787" font-family="Arial, sans-serif" font-size="22" fill="#555">${escapeSvgText(noteLine)}</text>
  <text x="110" y="925" font-family="Arial, sans-serif" font-size="24" font-weight="800" fill="#008577">${escapeSvgText(p.link||"lottotracker.vercel.app")}</text>
  <text x="110" y="964" font-family="Arial, sans-serif" font-size="18" font-weight="700" fill="#5a4a00">No ticket sales. No gambling. No money handled.</text>
</svg>`;
}
function shareCardSvgDataUrl(payload){
  return "data:image/svg+xml;charset=utf-8,"+encodeURIComponent(shareCardSvg(payload));
}
function shareCardImageBlob(payload){
  return new Promise((resolve,reject)=>{
    if(typeof document==="undefined"||typeof Image==="undefined"){
      reject(new Error("Share card image export is not supported."));
      return;
    }
    const svg=shareCardSvg(payload);
    const widthMatch=svg.match(/<svg[^>]*\swidth="(\d+)"/);
    const heightMatch=svg.match(/<svg[^>]*\sheight="(\d+)"/);
    const exportWidth=Math.max(1,Number(widthMatch?.[1])||1080);
    const exportHeight=Math.max(1,Number(heightMatch?.[1])||1080);
    const blob=new Blob([svg],{type:"image/svg+xml;charset=utf-8"});
    const url=URL.createObjectURL(blob);
    const image=new Image();
    image.onload=()=>{
      try{
        const canvas=document.createElement("canvas");
        canvas.width=exportWidth;
        canvas.height=exportHeight;
        const context=canvas.getContext("2d");
        if(!context)throw new Error("Canvas is not available.");
        context.drawImage(image,0,0,exportWidth,exportHeight);
        canvas.toBlob((pngBlob)=>{
          URL.revokeObjectURL(url);
          if(!pngBlob){
            reject(new Error("Unable to generate share card image."));
            return;
          }
          resolve(pngBlob);
        },"image/png",0.92);
      }catch(error){
        URL.revokeObjectURL(url);
        reject(error);
      }
    };
    image.onerror=()=>{
      URL.revokeObjectURL(url);
      reject(new Error("Unable to render share card image."));
    };
    image.src=url;
  });
}
async function downloadShareCard(payload){
  const filename=slugifyShare(payload.strategyName||"lottotracker-share-card")+".png";
  try{
    const blob=await shareCardImageBlob(payload);
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url;
    a.download=filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return;
  }catch{}
  const svg=shareCardSvg(payload);
  const blob=new Blob([svg],{type:"image/svg+xml"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;
  a.download=slugifyShare(payload.strategyName||"lottotracker-share-card")+".svg";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function shareCardImageFile(payload){
  return new Promise((resolve,reject)=>{
    if(typeof File!=="function"){
      reject(new Error("Share card image export is not supported."));
      return;
    }
    shareCardImageBlob(payload)
      .then(pngBlob=>resolve(new File([pngBlob],slugifyShare(payload.strategyName||"lottotracker-share-card")+".png",{type:"image/png"})))
      .catch(reject);
  });
}
function ShareCardModal({payload,onClose}){
  const p=shareCardPayload(payload);
  const isPortraitCard=!!((p.watchDraw&&p.numbersPreview.length&&!p.afterDraw&&!p.joinDraw)||(p.resultCard&&p.afterDraw));
  const[shareStatus,setShareStatus]=useState("");
  const shareUrl=p.link||shareBaseUrl();
  const shareText=shareShortMessage(p);
  const canCopyImage=!!(navigator.clipboard?.write&&typeof ClipboardItem!=="undefined"&&window.isSecureContext);
  function setTemporaryShareStatus(message){
    setShareStatus(message);
    if(message)setTimeout(()=>setShareStatus(""),2200);
  }
  async function copyText(){
    try{
      await navigator.clipboard?.writeText(shareText);
      setTemporaryShareStatus("Message copied.");
    }catch{
      setTemporaryShareStatus("Copy was not available.");
    }
  }
  function copyTextFallback(text){
    if(typeof document==="undefined")return false;
    const textarea=document.createElement("textarea");
    textarea.value=String(text||"");
    textarea.setAttribute("readonly","");
    textarea.style.position="fixed";
    textarea.style.left="-9999px";
    textarea.style.top="0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let copied=false;
    try{copied=document.execCommand("copy");}
    catch{copied=false;}
    textarea.remove();
    return copied;
  }
  async function copyLink(eventOrMessage,maybeMessage){
    const isEvent=eventOrMessage&&typeof eventOrMessage==="object"&&(typeof eventOrMessage.preventDefault==="function"||typeof eventOrMessage.stopPropagation==="function");
    if(isEvent){
      eventOrMessage.preventDefault?.();
      eventOrMessage.stopPropagation?.();
    }
    const successMessage=typeof eventOrMessage==="string"
      ?eventOrMessage
      :typeof maybeMessage==="string"
      ?maybeMessage
      :"Link copied";
    try{
      if(navigator.clipboard?.writeText){
        await navigator.clipboard.writeText(shareUrl);
      }else if(!copyTextFallback(shareUrl)){
        throw new Error("Clipboard copy failed.");
      }
      setTemporaryShareStatus(successMessage);
    }catch{
      setTemporaryShareStatus("Couldn't copy automatically. Press and hold the share link above to copy it.");
    }
  }
  async function copyImage(){
    if(!canCopyImage){
      await downloadImage("Your phone does not support Copy Image. Tap Save Image instead.");
      return;
    }
    try{
      const blob=await shareCardImageBlob(p);
      await navigator.clipboard.write([new ClipboardItem({"image/png":blob})]);
      setTemporaryShareStatus("Image copied.");
    }catch{
      await downloadImage("Your phone does not support Copy Image. Save Image started instead.");
    }
  }
  async function downloadImage(message="Image save started."){
    await downloadShareCard(p);
    setTemporaryShareStatus(message);
  }
  function isMobileShareDevice(){
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent||"");
  }
  function openPopupOrCopy(url,statusMessage){
    const popup=window.open(url,"_blank","width=720,height=720,noreferrer");
    try{if(popup)popup.opener=null;}catch{}
    if(popup){
      if(statusMessage)setTemporaryShareStatus(statusMessage);
      return true;
    }
    setTemporaryShareStatus("Popup blocked. Link copied instead.");
    copyLink("Popup blocked. Link copied instead.");
    return false;
  }
  function resultShareCardUrl(){
    const clean=String(shareUrl||"").split("#")[0].split("?")[0].replace(/\/$/,"");
    return /\/s\/result\/[^/]+$/i.test(clean)?clean+"/card.png":"";
  }
  function resultShareIdFromUrl(){
    const clean=String(shareUrl||"").split("#")[0].split("?")[0].replace(/\/$/,"");
    const match=clean.match(/\/s\/result\/([^/]+)$/i);
    return match?decodeURIComponent(match[1]):"";
  }
  function strategyShareLookupFromUrl(){
    const clean=String(shareUrl||"").split("#")[0].split("?")[0].replace(/\/$/,"");
    const legacy=clean.match(/\/s\/strategy\/([^/]+)$/i);
    if(legacy)return{shareId:decodeURIComponent(legacy[1]),slugPath:""};
    const slug=clean.match(/\/s\/([^/]+)\/([^/]+)$/i);
    return slug?{shareId:"",slugPath:decodeURIComponent(slug[1])+"/"+decodeURIComponent(slug[2])}:{shareId:"",slugPath:""};
  }
  async function verifyPublicResultShare(){
    if(!p.resultCard)return true;
    const cardUrl=resultShareCardUrl();
    const shareId=resultShareIdFromUrl();
    if(!cardUrl)throw new Error("Public result share is not ready yet.");
    const page=await fetch(shareUrl,{cache:"no-store"});
    if(!page.ok)throw new Error("Public result page is not ready yet.");
    const ogUrl="/api/public?action=result-og&share="+encodeURIComponent(shareId);
    const og=await fetch(ogUrl,{cache:"no-store"});
    if(!og.ok)throw new Error("Public result preview metadata is not ready yet.");
    const html=await og.text();
    const required=['property="og:image"','property="og:title"','property="og:description"','property="og:image:width"','property="og:image:height"'];
    if(!required.every(token=>html.includes(token)))throw new Error("Public result preview metadata is not ready yet.");
    if(!html.includes(cardUrl.split("?")[0]))throw new Error("Public result card preview is not ready yet.");
    const image=await fetch(cardUrl,{cache:"no-store"});
    if(!image.ok)throw new Error("Public result card image is not ready yet.");
    const type=image.headers.get("content-type")||"";
    if(type&&!type.includes("image/png"))throw new Error("Public result card image is not ready yet.");
    return true;
  }
  async function verifyPublicStrategyShare(){
    if(p.resultCard)return true;
    const lookup=strategyShareLookupFromUrl();
    if(!lookup.shareId&&!lookup.slugPath)return true;
    const ogUrl=lookup.slugPath
      ?"/api/public?action=strategy-og&strategyPath="+encodeURIComponent(lookup.slugPath)
      :"/api/public?action=strategy-og&strategy="+encodeURIComponent(lookup.shareId);
    const og=await fetch(ogUrl,{cache:"no-store"});
    if(!og.ok)throw new Error("Public strategy preview metadata is not ready yet.");
    const html=await og.text();
    const required=['property="og:image"','property="og:title"','property="og:description"','property="og:image:width"','property="og:image:height"'];
    if(!required.every(token=>html.includes(token)))throw new Error("Public strategy preview metadata is not ready yet.");
    const imageUrl=(html.match(/property="og:image"\s+content="([^"]+)"/)||[])[1]||"";
    if(!/^https:\/\//i.test(imageUrl))throw new Error("Public strategy image URL is not ready yet.");
    const image=await fetch(imageUrl,{cache:"no-store"});
    if(!image.ok)throw new Error("Public strategy card image is not ready yet.");
    const type=image.headers.get("content-type")||"";
    if(type&&!type.includes("image/png"))throw new Error("Public strategy card image is not ready yet.");
    return true;
  }
  function openExternalShare(url,statusMessage){
    if(isMobileShareDevice()){
      window.location.href=url;
      if(statusMessage)setTemporaryShareStatus(statusMessage);
      return true;
    }
    return openPopupOrCopy(url,statusMessage);
  }
  async function openShareTarget(target){
    const encodedUrl=encodeURIComponent(shareUrl);
    const encodedText=encodeURIComponent(shareText);
    if(target==="facebook"){
      try{
        setTemporaryShareStatus(p.resultCard?"Checking public Card B preview...":"Checking public share preview...");
        await verifyPublicResultShare();
        await verifyPublicStrategyShare();
        if(isMobileShareDevice()&&navigator.clipboard?.writeText){
          await navigator.clipboard.writeText(shareUrl).catch(()=>{});
        }
        openExternalShare("https://www.facebook.com/sharer/sharer.php?u="+encodedUrl+"&quote="+encodedText,"Facebook opened with the public LottoTracker link.");
        if(isMobileShareDevice())setTemporaryShareStatus("Facebook opened. If the composer is blank, the link is copied - paste it into Facebook.");
      }catch(e){
        setTemporaryShareStatus(e?.message||"Public share preview is not ready yet.");
      }
      return;
    }
    if(target==="messenger"){
      if(isMobileShareDevice()){
        window.location.href="fb-messenger://share?link="+encodedUrl;
        setTemporaryShareStatus("Opening Messenger. If it does not open, use Copy Link or Text Message.");
      }else{
        copyLink("Messenger does not offer a reliable prefilled desktop composer here. Link copied; paste it into Messenger with the image if you want.");
      }
      return;
    }
    if(target==="whatsapp"){
      openExternalShare("https://wa.me/?text="+encodedText,"WhatsApp opened with the public link.");
      return;
    }
    if(target==="x"){
      openExternalShare("https://twitter.com/intent/tweet?text="+encodedText,"X opened with the public link.");
      return;
    }
    setTemporaryShareStatus("That share option is not available.");
  }
  function emailShare(){
    const subject=encodeURIComponent(p.strategyName+" on LottoTracker");
    const body=encodeURIComponent(shareText);
    window.location.href="mailto:?subject="+subject+"&body="+body;
  }
  function textMessageShare(){
    const encodedText=encodeURIComponent(shareText);
    const ios=/iPhone|iPad|iPod/i.test(navigator.userAgent||"");
    const smsUrl=ios?"sms:&body="+encodedText:"sms:?body="+encodedText;
    window.location.href=smsUrl;
    setTemporaryShareStatus("Opening text message with the public link. Download Image if you want to attach the card too.");
  }
  async function nativeShare(){
    const text=p.joinDraw
      ?p.strategyName+" · "+p.game+" · "+p.drawTime+" · "+p.openSpots+" · "+p.poolStatus+". Watch Draw or Request to Join. "+p.organizerDisclaimer
      :p.watchDraw
      ?p.strategyName+" · "+p.game+" · "+p.drawTime+" · "+p.playCount+" plays · "+p.status+". "+p.ctaLabel+". "+TRUST_COPY
      :p.strategyName+" · "+p.game+" · "+p.status+" · ROI "+p.roi+". "+TRUST_COPY;
    const nativeShareText=shareText||text;
    try{
      if(navigator.share){
        const imageFile=await shareCardImageFile(p).catch(()=>null);
        const shareWithImageAndUrl={title:p.title,text:nativeShareText,url:shareUrl,files:imageFile?[imageFile]:[]};
        if(imageFile&&navigator.canShare?.(shareWithImageAndUrl)){
          await navigator.share(shareWithImageAndUrl);
          setTemporaryShareStatus("Share opened with the image and public link.");
        }else{
          await navigator.share({title:p.title,text:nativeShareText,url:shareUrl});
          setTemporaryShareStatus("Shared the public link. Use Download Image if you also want the card image.");
        }
      }else await copyText();
    }catch{
      setTemporaryShareStatus("Native share was not available.");
    }
  }
  return(
    <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,20,45,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
      <div style={{width:"min(980px,100%)",maxHeight:"92vh",overflowY:"auto",background:"#fff",borderRadius:18,boxShadow:"0 28px 80px rgba(0,0,0,.35)",padding:18}}>
        <div className="sec-hdr" style={{marginBottom:14}}>
          <span className="sec-title">Share Card</span>
          <button type="button" className="btn btn-sm btn-s" onClick={onClose}>Close</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:isPortraitCard?"repeat(auto-fit,minmax(300px,1fr))":"repeat(auto-fit,minmax(240px,1fr))",gap:18,alignItems:"start"}}>
          <div style={{background:"#fffaf0",borderRadius:20,padding:0,color:"#fff",minHeight:0,aspectRatio:isPortraitCard?"4 / 5":"1 / 1",position:"relative",overflow:"hidden",boxShadow:"inset 0 0 0 1px rgba(0,0,0,.08)",width:"100%",maxWidth:isPortraitCard?520:"none",justifySelf:"center"}}>
            <img
              alt={p.strategyName+" share card preview"}
              src={shareCardSvgDataUrl(p)}
              style={{position:"absolute",inset:0,zIndex:2,width:"100%",height:"100%",objectFit:"contain",display:"block"}}
            />
          </div>
          <div style={{display:"grid",gap:10,alignContent:"start"}}>
            <div className="panel" style={{marginBottom:0,padding:14}}>
              <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",marginBottom:6}}>Share Link</div>
              <div style={{fontSize:13,color:"#002855",fontWeight:800,lineHeight:1.35,wordBreak:"break-word"}}>{shareUrl}</div>
            </div>
            <div className="panel" style={{marginBottom:0,padding:14}}>
              <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",marginBottom:10}}>Share to</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8}}>
                <button type="button" className="btn btn-p" onClick={()=>openShareTarget("facebook")}>Share to Facebook</button>
                <button type="button" className="btn btn-p" onClick={()=>openShareTarget("messenger")}>Share to Messenger</button>
                <button type="button" className="btn btn-p" onClick={()=>openShareTarget("whatsapp")}>Share to WhatsApp</button>
                <button type="button" className="btn btn-p" onClick={()=>openShareTarget("x")}>Share to X</button>
                <button type="button" className="btn btn-p" onClick={textMessageShare}>Text Message</button>
              </div>
              <div style={{fontSize:12,color:"#555",lineHeight:1.35,marginTop:10}}>
                Some services share the link first. Use Save Image when you want to attach the card manually.
              </div>
            </div>
            <div className="panel" style={{marginBottom:0,padding:14}}>
              <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",marginBottom:10}}>Card actions</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8}}>
                <button type="button" className="btn btn-s" onClick={copyImage}>{canCopyImage?"Copy Image":"Save Image"}</button>
                <button type="button" className="btn btn-s" onClick={(event)=>copyLink(event)}>Copy Link</button>
                <button type="button" className="btn btn-s" onClick={()=>downloadImage("Image save started.")}>Save Image</button>
                <button type="button" className="btn btn-s" onClick={emailShare}>Email</button>
              </div>
            </div>
            <button type="button" className="btn btn-s" onClick={copyText}>Copy Message</button>
            <button type="button" className="btn btn-s" onClick={nativeShare}>Share with Recent Contacts & Apps</button>
            {shareStatus&&<div className="launch-note" style={{margin:0,padding:"10px 12px",fontSize:13}}>{shareStatus}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
function ShareCardButton({payload,label="Share",className="btn btn-s",onBeforeOpen}) {
  const[open,setOpen]=useState(false);
  const[busy,setBusy]=useState(false);
  const[modalPayload,setModalPayload]=useState(payload);
  const[error,setError]=useState("");
  async function handleOpen(){
    if(busy)return;
    setBusy(true);
    setError("");
    try{
      const result=typeof onBeforeOpen==="function"?await onBeforeOpen():null;
      if(result?.cancel)return;
      setModalPayload(result?.payload?{...payload,...result.payload}:payload);
      setOpen(true);
    }catch(e){
      setError(e?.message||"Could not prepare share.");
    }finally{
      setBusy(false);
    }
  }
  return(
    <>
      <button type="button" className={className+" "+(busy?"loading":"")} onClick={handleOpen} disabled={busy}>{busy?"Preparing...":label}</button>
      {error&&<div className="auth-err" style={{margin:"8px 0 0",fontSize:12}}>{error}</div>}
      {open&&createPortal(<ShareCardModal payload={modalPayload||payload} onClose={()=>setOpen(false)}/>,document.body)}
    </>
  );
}

function FeedbackModal({user,onClose}){
  const[name,setName]=useState(user?.user_metadata?.name||"");
  const[contact,setContact]=useState(user?.email||"");
  const[trying,setTrying]=useState("");
  const[feedback,setFeedback]=useState("");
  const[file,setFile]=useState(null);
  const[busy,setBusy]=useState(false);
  const[msg,setMsg]=useState("");
  const[err,setErr]=useState("");
  function readScreenshot(selected){
    return new Promise(resolve=>{
      if(!selected)return resolve(null);
      if(selected.size>2*1024*1024){
        setErr("Screenshots must be 2 MB or smaller.");
        return resolve(null);
      }
      const reader=new FileReader();
      reader.onload=()=>resolve({name:selected.name,type:selected.type,size:selected.size,dataUrl:String(reader.result||"")});
      reader.onerror=()=>resolve(null);
      reader.readAsDataURL(selected);
    });
  }
  async function submitFeedback(){
    setErr("");
    setMsg("");
    if(!trying.trim()||!feedback.trim()){
      setErr("Tell us what you were trying to do and what happened.");
      return;
    }
    setBusy(true);
    try{
      const screenshot=await readScreenshot(file);
      const res=await fetch("/api/feedback",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          name,
          contact,
          trying_to_do:trying,
          feedback,
          page_url:typeof window!=="undefined"?window.location.href:"",
          user_agent:typeof navigator!=="undefined"?navigator.userAgent:"",
          screenshot,
        }),
      });
      const data=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(data.error||"Could not send feedback.");
      setMsg("Thank you. Your feedback was sent.");
      setTrying("");
      setFeedback("");
      setFile(null);
      setTimeout(onClose,1200);
    }catch(e){
      setErr(e?.message||"Could not send feedback.");
    }finally{
      setBusy(false);
    }
  }
  return(
    <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:1100,background:"rgba(0,20,45,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
      <div className="panel" style={{width:"min(640px,100%)",maxHeight:"92vh",overflowY:"auto",margin:0}}>
        <div className="sec-hdr" style={{marginBottom:10}}>
          <span className="sec-title">Feedback / Contact</span>
          <button type="button" className="btn btn-sm btn-s" onClick={onClose}>Close</button>
        </div>
        <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:12,padding:"10px 12px",fontSize:13,color:"#5a4a00",fontWeight:800,lineHeight:1.4,marginBottom:14}}>
          LottoTracker is in soft launch. Your feedback helps us improve the app.
        </div>
        {err&&<div className="auth-err">{err}</div>}
        {msg&&<div style={{background:"#eef8f6",border:"1px solid #cfe9e3",borderRadius:10,padding:"10px 12px",fontSize:13,color:"#008577",fontWeight:800,marginBottom:12}}>{msg}</div>}
        <div style={{display:"grid",gap:11}}>
          <label style={{fontSize:11,fontWeight:900,color:"#555",textTransform:"uppercase"}}>
            Name
            <input className="add-inp" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" style={{marginTop:5,width:"100%"}}/>
          </label>
          <label style={{fontSize:11,fontWeight:900,color:"#555",textTransform:"uppercase"}}>
            Email or phone
            <input className="add-inp" value={contact} onChange={e=>setContact(e.target.value)} placeholder="you@example.com or phone number" style={{marginTop:5,width:"100%"}}/>
          </label>
          <label style={{fontSize:11,fontWeight:900,color:"#555",textTransform:"uppercase"}}>
            What were you trying to do?
            <textarea className="add-inp" value={trying} onChange={e=>setTrying(e.target.value)} placeholder="Example: I was trying to create a Pick 4 strategy." rows={3} style={{marginTop:5,width:"100%",resize:"vertical"}}/>
          </label>
          <label style={{fontSize:11,fontWeight:900,color:"#555",textTransform:"uppercase"}}>
            What went wrong or what feedback do you have?
            <textarea className="add-inp" value={feedback} onChange={e=>setFeedback(e.target.value)} placeholder="Tell us what happened, what felt confusing, or what would make LottoTracker better." rows={5} style={{marginTop:5,width:"100%",resize:"vertical"}}/>
          </label>
          <label style={{fontSize:11,fontWeight:900,color:"#555",textTransform:"uppercase"}}>
            Screenshot optional
            <input className="add-inp" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={e=>{setErr("");setFile(e.target.files?.[0]||null);}} style={{marginTop:5,width:"100%"}}/>
          </label>
          {file&&<div style={{fontSize:12,color:"#555",fontWeight:800}}>Attached: {file.name}</div>}
          <button type="button" className={"btn btn-p "+(busy?"loading":"")} onClick={submitFeedback} disabled={busy}>
            {busy?"Sending...":"Send Feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PublicSharePreview({path,user=null}){
  const parts=String(path||"").split("/").filter(Boolean);
  const legacyShareType=parts[1]||"strategy";
  const isSlugStrategyPath=parts[0]==="s"&&parts.length>=3&&!["strategy","result","results"].includes(String(parts[1]||"").toLowerCase());
  const shareType=isSlugStrategyPath?"strategy":legacyShareType;
  const shareId=isSlugStrategyPath?parts.slice(1,3).join("/"):(parts[2]||"lottotracker");
  const strategyLookupParam=isSlugStrategyPath?"strategyPath":"strategy";
  const title=shareId.split("-").filter(Boolean).map(part=>part.charAt(0).toUpperCase()+part.slice(1)).join(" ")||"LottoTracker Strategy";
  const isResults=shareType==="results";
  const[publicData,setPublicData]=useState(null);
  const[loading,setLoading]=useState(shareType==="strategy");
  const[loadError,setLoadError]=useState("");
  const[now,setNow]=useState(()=>new Date());
  const[feedbackOpen,setFeedbackOpen]=useState(false);
  const[showPublicNumbers,setShowPublicNumbers]=useState(false);
  const[publicPlays,setPublicPlays]=useState([]);
  const[publicResults,setPublicResults]=useState([]);
  const[playsLoading,setPlaysLoading]=useState(false);
  const[playsHasMore,setPlaysHasMore]=useState(false);
  const[copyBusy,setCopyBusy]=useState(false);
  const[copyMsg,setCopyMsg]=useState("");
  useEffect(()=>{
    if(shareType!=="strategy")return;
    let alive=true;
    setLoading(true);
    setLoadError("");
    setPublicPlays([]);
    setPublicResults([]);
    setPlaysHasMore(false);
    fetch("/api/public-strategy?"+strategyLookupParam+"="+encodeURIComponent(shareId))
      .then(res=>res.ok?res.json():Promise.reject(new Error("Strategy not found")))
      .then(data=>{
        if(!alive)return;
        setPublicData(data);
        if(data?.canonicalPath&&data.canonicalPath!==window.location.pathname){
          window.history.replaceState(null,"",data.canonicalPath+window.location.search+window.location.hash);
        }
      })
      .catch(err=>{
        if(alive){
          setLoadError(err?.message||"Strategy preview unavailable");
          setPublicData(null);
        }
      })
      .finally(()=>{if(alive)setLoading(false);});
    return()=>{alive=false;};
  },[shareType,shareId,strategyLookupParam]);
  useEffect(()=>{
    if(shareType!=="result")return;
    let alive=true;
    setLoading(true);
    setLoadError("");
    setPublicData(null);
    fetch("/api/public?action=result-share&share="+encodeURIComponent(shareId),{cache:"no-store"})
      .then(res=>res.ok?res.json():Promise.reject(new Error("Shared result not found")))
      .then(data=>{if(alive)setPublicData(data);})
      .catch(err=>{
        if(alive){
          setLoadError(err?.message||"Shared result unavailable");
          setPublicData(null);
        }
      })
      .finally(()=>{if(alive)setLoading(false);});
    return()=>{alive=false;};
  },[shareType,shareId]);
  useEffect(()=>{
    const id=setInterval(()=>setNow(new Date()),DRAW_STATUS_TICK_MS);
    return()=>clearInterval(id);
  },[]);
  const signedIn=!!user;
  const accountLabel=user?.email?user.email.split("@")[0]:"Your account";
  function rememberPublicReturn(copyId=shareId){
    try{
      if(copyId)localStorage.setItem("lt:pendingPublicStrategy",copyId);
      localStorage.setItem("lt:pendingReferralCode",publicData?.creatorReferralCode||"");
      localStorage.setItem("lt:returnToAfterAuth",window.location.pathname+window.location.search+window.location.hash);
    }catch{}
  }
  function goAuth(mode="signup"){
    const copyId=publicData?.strategyShareId||shareId;
    rememberPublicReturn(copyId);
    const params=new URLSearchParams();
    params.set("auth",mode==="login"?"login":"signup");
    params.set("returnTo",window.location.pathname+window.location.search+window.location.hash);
    if(copyId)params.set("copyStrategy",copyId);
    if(publicData?.creatorReferralCode)params.set("ref",publicData.creatorReferralCode);
    window.location.href="/?"+params.toString();
  }
  function goSignup(){
    goAuth("signup");
  }
  function goLogin(){
    goAuth("login");
  }
  async function loadPublicPlays(reset=false){
    if(playsLoading)return;
    setPlaysLoading(true);
    setCopyMsg("");
    const offset=reset?0:publicPlays.length;
    try{
      const res=await fetch("/api/public-strategy?"+strategyLookupParam+"="+encodeURIComponent(shareId)+"&include=plays&offset="+offset+"&limit=100");
      if(!res.ok)throw new Error("Numbers are not available right now.");
      const data=await res.json();
      const nextPlays=Array.isArray(data.plays)?data.plays:[];
      const nextResults=Array.isArray(data.results)?data.results:[];
      setPublicPlays(prev=>reset?nextPlays:[...prev,...nextPlays]);
      setPublicResults(prev=>reset?nextResults:[...prev,...nextResults]);
      setPlaysHasMore(!!data.playsPage?.hasMore);
      setShowPublicNumbers(true);
    }catch(err){
      setCopyMsg(err?.message||"Could not load numbers.");
    }finally{
      setPlaysLoading(false);
    }
  }
  async function addSharedStrategyToVault(){
    if(copyBusy)return;
    setCopyBusy(true);
    setCopyMsg("");
    try{
      const sessionResult=await supabase.auth.getSession();
      const token=sessionResult?.data?.session?.access_token;
      if(!token){
        goSignup();
        return;
      }
      const res=await fetch("/api/public?action=copy-public-strategy",{
        method:"POST",
        headers:{"Content-Type":"application/json",Authorization:"Bearer "+token},
        body:JSON.stringify({strategyId:publicData?.strategyShareId||shareId,referralCode:publicData?.creatorReferralCode||""}),
      });
      const payload=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(payload.error||"Could not add this strategy.");
      setCopyMsg("Added to My Playslips.");
      setTimeout(()=>{window.location.href="/my-strategies";},700);
    }catch(err){
      setCopyMsg(err?.message||"Could not add this strategy.");
    }finally{
      setCopyBusy(false);
    }
  }
  if(shareType==="result"){
    if(loadError&&!publicData&&!loading){
      return(
        <main className="page public-share-page" style={{minHeight:"100vh",padding:"24px 16px 60px"}}>
          <style>{CSS}{POLISH_CSS}</style>
          <div className="panel" style={{maxWidth:720,margin:"40px auto",textAlign:"center"}}>
            <div className="sec-title" style={{fontSize:26}}>This shared result is no longer available</div>
            <p style={{color:"#555",lineHeight:1.5}}>The owner may have revoked this result share. Private completed results remain visible only inside the owner account.</p>
            <button type="button" className="btn btn-p" onClick={()=>{window.location.href="/";}}>Go to LottoTracker</button>
          </div>
        </main>
      );
    }
    const data=publicData||{};
    const game=data.game||"fantasy5";
    const numbers=Array.isArray(data.numbersPlayed)?data.numbersPlayed:[];
    const rules=Array.isArray(data.playRules)?data.playRules:[];
    const publicOutcomeState=publicSnapshotOutcomeState(data);
    const publicOutcomeText=publicSnapshotOutcomeText(data);
    const verdictDisplay=resultOutcomeDisplay(publicOutcomeState);
    const verdict=verdictDisplay.label;
    const proof=data.validPreDrawProofAt?resultCardTimestampLabel(data.validPreDrawProofAt):"";
    const officialNumbers=Array.isArray(data.officialNumbers)?data.officialNumbers:[];
    const fireball=data.officialFireballDigit;
    const strategyLink=data.strategyShareId?"/s/strategy/"+encodeURIComponent(data.strategyShareId):"";
    return(
      <><style>{CSS}{POLISH_CSS}</style>
        <main className="page public-share-page" style={{minHeight:"100vh",padding:"24px 16px 60px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:18}}>
            <div>
              <div className="logo" style={{color:"#002855"}}>Lotto<span>Tracker</span></div>
              <div style={{fontSize:12,color:"#555",fontWeight:800,textTransform:"uppercase",letterSpacing:2}}>Public Result Snapshot</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className="btn btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback</button>
              <a className="btn btn-p" href="/">Build my own strategy</a>
            </div>
          </div>
          <div className="launch-note" style={{marginBottom:18}}>
            This is a frozen public result snapshot. LottoTracker tracks numbers only. No tickets sold. No money handled.
          </div>
          <div className="panel" style={{border:"1px solid #dbeee9",marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:14,alignItems:"start"}}>
              <div>
                <div className="sec-badge" style={{marginBottom:10}}>What Happened</div>
                <div className="sec-title" style={{fontSize:38,lineHeight:1,marginBottom:8}}>{data.strategyName||title}</div>
                <p style={{fontSize:15,color:"#555",lineHeight:1.5,maxWidth:760}}>
                  {gameLabel(game)} {fmtShortDate(data.drawDate)} {drawFilterLabel(data.drawTime)} result, shared from the exact lineup that was marked as played.
                </p>
              </div>
              <span className="sec-badge" style={{background:verdictDisplay.color}}>{verdict}</span>
            </div>
            {loading&&<div style={{marginTop:14}}><LoadingSkeleton kind="card"/></div>}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16,marginBottom:16}}>
            <div className="panel" style={{marginBottom:0}}>
              <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Official Result</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                {officialNumbers.length?officialNumbers.map((n,i)=><Ball key={i} n={n}/>):<strong style={{color:"#002855"}}>Official numbers unavailable</strong>}
              </div>
              {isPickGame(game)&&<div style={{fontSize:13,color:"#555",fontWeight:900}}>Fireball: {fireball??"Not stored"}</div>}
              <div style={{display:"grid",gap:7,marginTop:13,fontSize:13,color:"#555"}}>
                <div><strong style={{color:"#002855"}}>Draw:</strong> {fmtShortDate(data.drawDate)} {drawFilterLabel(data.drawTime)}</div>
                <div><strong style={{color:"#002855"}}>Verdict:</strong> {publicOutcomeText||verdict}</div>
                {proof&&<div><strong style={{color:"#008577"}}>Verified before draw:</strong> Lineup saved at {proof}</div>}
              </div>
            </div>
            <div className="panel" style={{marginBottom:0}}>
              <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Result Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10}}>
                {[
                  ["Amount Played",fmtMoney(data.amountPlayed),"#002855"],
                  ["Amount Won",fmtMoney(data.amountWon),"#008577"],
                  ["Free Tickets",data.freeTickets||0,"#c8860a"],
                  ["Best Match",(data.bestMatch||0)+" / "+gameNumberConfig(game).count,"#002855"],
                  ["Winning Plays",data.winningPlayCount||0,"#008577"],
                  ["Largest Prize",fmtMoney(data.largestPrize),"#c8860a"],
                ].map(([label,value,color])=>(
                  <div key={label} style={{background:"#f8fbfa",border:"1px solid #edf1f0",borderRadius:10,padding:"10px 11px"}}>
                    <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>{label}</div>
                    <strong style={{display:"block",color,fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,lineHeight:1.05,overflowWrap:"anywhere"}}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="panel" style={{marginBottom:16}}>
            <div className="sec-hdr" style={{marginBottom:12}}>
              <span className="sec-title" style={{fontSize:22}}>Exact Numbers Played</span>
              <span className="sec-badge">{numbers.length} saved lines</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8,maxHeight:360,overflow:"auto"}}>
              {numbers.map((nums,index)=>(
                <div key={index} style={{background:"#f8fbfa",border:"1px solid #edf1f0",borderRadius:9,padding:"7px 8px",fontSize:12,fontWeight:900,color:"#002855"}}>
                  #{index+1} {digitsText(nums)}{rules[index]?.playType?` · ${rules[index].playType}`:""}
                </div>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button type="button" className={"btn btn-p "+(copyBusy?"loading":"")} onClick={addSharedStrategyToVault} disabled={copyBusy||!data.strategyShareId}>
              {copyBusy?"Adding...":"Add This Playslip to My Playslips"}
            </button>
            {strategyLink&&<a className="btn btn-s" href={strategyLink}>View Public Strategy</a>}
            <a className="btn btn-s" href="/">Build My Own Strategy</a>
          </div>
          {copyMsg&&<div style={{fontSize:12,color:copyMsg.startsWith("Added")?"#008577":"#c8102e",fontWeight:800,marginTop:10}}>{copyMsg}</div>}
          <footer style={{marginTop:26,borderTop:"1px solid #e8e2d6",paddingTop:16,display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"center",fontSize:12,color:"#555"}}>
            <span>I post the misses too.</span>
            <button type="button" className="btn btn-sm btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback / Contact</button>
          </footer>
          {feedbackOpen&&<FeedbackModal onClose={()=>setFeedbackOpen(false)}/>}
        </main>
      </>
    );
  }
  if(shareType==="strategy"){
    if(loadError&&!publicData&&!loading){
      return(
        <main className="page public-share-page" style={{minHeight:"100vh",padding:"24px 16px 60px"}}>
          <style>{CSS}{POLISH_CSS}</style>
          <div className="panel" style={{maxWidth:720,margin:"40px auto",textAlign:"center"}}>
            <div className="sec-title" style={{fontSize:26}}>This shared strategy is not available</div>
            <p style={{color:"#555",lineHeight:1.5}}>Ask the owner to share it again from My Playslips. Private playslips are only visible after the owner creates a public share link.</p>
            <button type="button" className="btn btn-p" onClick={()=>{window.location.href="/";}}>Go to LottoTracker</button>
          </div>
        </main>
      );
    }
    const data=publicData||publicStrategyFallback(shareId);
    const game=data.game||"fantasy5";
    const drawStatus=getDrawStatusService([game],now,[]);
    const next=drawStatus.next;
    const latest=data.latestDraw;
    const results=publicResults.length?publicResults:(Array.isArray(data.results)?data.results:[]);
    const stats=data.stats||{};
    const roi=Number.isFinite(Number(stats.roi))?(Number(stats.roi)>=0?"+":"")+Number(stats.roi).toFixed(0)+"%":"Pending";
    const bestMatch=stats.bestMatch ?? (results.length?Math.max(0,...results.map(row=>Number(row.matches)||0)):0);
    const playCount=Number(data.playCount)||((data.plays||[]).length)||0;
    const costLabel=fmtMoney(Number(data.estimatedCost)||Number(stats.currentCost)||playCount);
    const totalWon=Number(stats.totalWon)||results.reduce((sum,row)=>sum+(Number(row.won)||0),0);
    const net=totalWon-(Number(data.estimatedCost)||Number(stats.currentCost)||playCount);
    const netLabel=(net<0?"-":"")+fmtMoney(Math.abs(net));
    const topPrizeLabel=shareTopPrizeLabel(game);
    const loadedRows=publicPlays.length?publicPlays:(Array.isArray(data.plays)?data.plays:[]);
    const publicRows=results.length?results:loadedRows.map(play=>({...play,matches:0,won:0,winner:false}));
    const publicTitle=data.title||title;
    return(
      <><style>{CSS}{POLISH_CSS}</style>
        <main className="page public-share-page" style={{minHeight:"100vh",padding:"24px 16px 60px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:18}}>
            <div>
              <div className="logo" style={{color:"#002855"}}>Lotto<span>Tracker</span></div>
              <div style={{fontSize:12,color:"#555",fontWeight:800,textTransform:"uppercase",letterSpacing:2}}>{signedIn?`Signed in as ${accountLabel}`:"Public Strategy Viewer"}</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className="btn btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback</button>
              {signedIn?(
                <>
                  <a className="btn btn-s" href="/">View My Dashboard</a>
                  <a className="btn btn-p" href="/my-strategies">Open in My Account</a>
                </>
              ):(
                <>
                  <button type="button" className="btn btn-s" onClick={goLogin}>Log In</button>
                  <button type="button" className="btn btn-p" onClick={goSignup}>Create Free Account</button>
                </>
              )}
            </div>
          </div>

          <div className="launch-note" style={{marginBottom:18}}>
            {signedIn?"You are viewing a public shared playslip while signed in. You can add it to My Playslips or jump back to your account anytime.":"Watch first. Join when you are ready. LottoTracker tracks numbers only. No tickets sold. No money handled."}
          </div>

          <div className="panel" style={{border:"1px solid #dbeee9",marginBottom:16,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:14,alignItems:"start"}}>
              <div>
                <div className="sec-badge" style={{marginBottom:10}}>Public Watch Page</div>
                <div className="sec-title" style={{fontSize:38,lineHeight:1,marginBottom:8}}>{publicTitle}</div>
                <p style={{fontSize:15,color:"#555",lineHeight:1.5,maxWidth:760}}>
                  {signedIn
                    ?`${data.creator||"A LottoTracker player"} shared this playslip. You can add this frozen public copy to My Playslips without signing in again.`
                    :`${data.creator||"A LottoTracker player"} shared this strategy so you can watch the draw, see the results, and decide if you want to build your own.`}
                </p>
              </div>
              <span className="sec-badge" style={{background:"#008577"}}>{data.status||"Watching"}</span>
            </div>
            {loading&&<div style={{marginTop:14}}><LoadingSkeleton kind="card"/></div>}
            {loadError&&!loading&&(
              <div style={{marginTop:14,background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:12,padding:12,fontSize:13,color:"#5a4a00",fontWeight:700}}>
                Live strategy data was not available, so this link is showing a public watch preview.
              </div>
            )}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16,marginBottom:16}}>
            <div className="panel" style={{marginBottom:0}}>
              <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Live Draw</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#002855",lineHeight:1}}>{data.gameLabel||gameLabel(game)}</div>
              <div style={{display:"grid",gap:8,marginTop:14,fontSize:13,color:"#555"}}>
                <div><strong style={{color:"#002855"}}>Draw:</strong> {next?.drawLabel||"Next draw"}</div>
                <div><strong style={{color:"#002855"}}>Draw time:</strong> {next?.drawTime||"Time pending"}</div>
                <div><strong style={{color:"#002855"}}>Sales close:</strong> {next?.closeTime||"Pending"}</div>
                <div><strong style={{color:"#002855"}}>Status:</strong> <span style={{color:next?.statusColor||"#008577",fontWeight:900}}>{next?.status||"Upcoming"}</span></div>
              </div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:48,fontWeight:900,color:next?.statusColor||"#008577",lineHeight:1,marginTop:16}}>{next?.countdown||"Live"}</div>
              <button type="button" className="btn btn-s" style={{marginTop:14}} onClick={()=>{if(signedIn)window.location.href="/";else goLogin();}}>{signedIn?"View My Dashboard":"Watch in your account"}</button>
            </div>

            <div className="panel" style={{marginBottom:0}}>
              <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Strategy Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10}}>
                {[
                  ["Game",data.gameLabel||gameLabel(game),"#002855"],
                  ["Plays",playCount,"#008577"],
                  ["Cost",costLabel,"#c8860a"],
                  ["Top Prize",topPrizeLabel,"#2E6DA4"],
                  ["History ROI",roi,"#c8860a"],
                  ["Best Match",latest?.numbers?.length?shareBestMatchLabel(bestMatch,game):"Pending","#008577"],
                  ["History Winnings",latest?.numbers?.length?fmtMoney(totalWon):"Pending","#2E7D32"],
                  ["Net",latest?.numbers?.length?netLabel:"Pending",net<0?"#c8102e":"#2E7D32"],
                ].map(([label,value,color])=>(
                  <div key={label} style={{background:"#f8fbfa",border:"1px solid #edf1f0",borderRadius:10,padding:"10px 11px"}}>
                    <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>{label}</div>
                    <strong style={{display:"block",color,fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,lineHeight:1.05,overflowWrap:"anywhere"}}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
            <div className="panel" style={{marginBottom:0}}>
              <div className="sec-hdr" style={{marginBottom:12}}>
                <span className="sec-title" style={{fontSize:22}}>Results & Matches</span>
                <span className="sec-badge">{latest?.official?"Official results":"Watch mode"}</span>
              </div>
              {latest?.numbers?.length?(
                <>
                  <div style={{fontSize:13,color:"#555",fontWeight:800,marginBottom:8}}>
                    {data.gameLabel||gameLabel(game)} {latest.drawTime||""} - {latest.drawDate||"Latest draw"}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                    {latest.numbers.map((n,i)=><Ball key={i} n={n}/>)}
                  </div>
                </>
              ):(
                <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:12,padding:14,marginBottom:14}}>
                  <strong style={{color:"#002855"}}>Waiting for official results.</strong>
                  <div style={{fontSize:13,color:"#555",marginTop:4}}>Once the Florida Lottery posts the draw, this page can show matches and results.</div>
                </div>
              )}
              <div style={{display:"grid",gap:10,marginBottom:12}}>
                <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:12,padding:12,fontSize:13,color:"#5a4a00",fontWeight:800,lineHeight:1.45}}>
                  {latest?.numbers?.length
                    ?"Best match: "+shareBestMatchLabel(bestMatch,game)+" - Won: "+fmtMoney(totalWon)+" - Net: "+netLabel
                    :playCount+" plays are waiting for the official draw results."}
                </div>
                <button type="button" className={"btn btn-s "+(playsLoading?"loading":"")} onClick={()=>showPublicNumbers?setShowPublicNumbers(false):loadPublicPlays(true)} disabled={playsLoading}>
                  {playsLoading?"Loading...":showPublicNumbers?"Hide numbers":"View all "+playCount+" numbers"}
                </button>
              </div>
              {showPublicNumbers&&(
                <div style={{display:"grid",gap:8}}>
                  {latest?.numbers?.length&&(
                    <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:12,padding:"10px 12px",display:"grid",gap:8,fontSize:12,color:"#555",fontWeight:800,lineHeight:1.35}}>
                      <div style={{fontWeight:900,color:"#002855"}}>Number match legend</div>
                      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:6}}><Ball n="4" size="sm" matchKind="exact"/> Gold fill + check = correct digit in the correct position</span>
                        {isPickGame(game)&&<span style={{display:"inline-flex",alignItems:"center",gap:6}}><Ball n="4" size="sm" matchKind="wrong-position"/> Gold outline + dot = correct digit, wrong position</span>}
                        <span style={{display:"inline-flex",alignItems:"center",gap:6}}><Ball n="4" size="sm"/> Blue = no match</span>
                      </div>
                      {isPickGame(game)&&<div>Match counts for Pick games use exact-position matches. Box-order digits are shown separately with a gold outline so they are not confused with exact hits or Fireball.</div>}
                    </div>
                  )}
                  {publicRows.map((row,index)=>(
                    (()=>{const kinds=publicDigitMatchKinds(row.numbers||[],latest?.numbers||[],game);return(
                    <div key={(row.id||row.index||index)+"-public"} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:9,alignItems:"center",borderBottom:"1px solid #f0f0f0",padding:"8px 0"}}>
                      <span style={{fontSize:11,color:"#777",fontWeight:900}}>#{row.index||index+1}</span>
                      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                        {(row.numbers||[]).map((n,i)=><Ball key={i} n={n} size="sm" matchKind={kinds[i]||""}/>)}
                      </div>
                      <span style={{background:Number(row.matches)>=2?"#eef8f6":"#f4f4f4",color:Number(row.matches)>=2?"#008577":"#666",borderRadius:9,padding:"4px 8px",fontSize:11,fontWeight:900,whiteSpace:"nowrap"}}>
                        {latest?.numbers?.length?(row.matches+" Match"+(Number(row.won)>0?" - "+fmtMoney(row.won):"")):"Pending"}
                      </span>
                    </div>
                    );})()
                  ))}
                  {playsHasMore&&(
                    <button type="button" className={"btn btn-s "+(playsLoading?"loading":"")} onClick={()=>loadPublicPlays(false)} disabled={playsLoading}>
                      {playsLoading?"Loading...":"Load more numbers"}
                    </button>
                  )}
                </div>
              )}
              {!results.length&&latest?.numbers?.length&&showPublicNumbers&&(
                <div style={{fontSize:13,color:"#555",marginTop:12}}>No loaded plays matched this draw yet.</div>
              )}
            </div>

            <div style={{display:"grid",gap:16,alignContent:"start"}}>
              <div className="panel" style={{marginBottom:0}}>
                <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Watch First</div>
                <p style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:12}}>
                  {signedIn?"You are already signed in. Add this public playslip to My Playslips or open your LottoTracker account.":"You can view this playslip, live draw status, results, matches, ROI, and stats without logging in."}
                </p>
                <div style={{display:"grid",gap:8}}>
                  <button type="button" className={"btn btn-p "+(copyBusy?"loading":"")} onClick={addSharedStrategyToVault} disabled={copyBusy}>
                    {copyBusy?"Adding...":"Add This Playslip to My Playslips"}
                  </button>
                  {signedIn?(
                    <>
                      <button type="button" className="btn btn-s" onClick={()=>{window.location.href="/my-strategies";}}>Open in My Account</button>
                      <button type="button" className="btn btn-s" onClick={()=>{window.location.href="/";}}>View My Dashboard</button>
                    </>
                  ):(
                    <>
                      <button type="button" className="btn btn-s" onClick={goLogin}>Log In to add it</button>
                      <button type="button" className="btn btn-s" onClick={goSignup}>Create your own strategy</button>
                    </>
                  )}
                </div>
                {copyMsg&&<div style={{fontSize:12,color:copyMsg.startsWith("Added")?"#008577":"#c8102e",fontWeight:800,marginTop:10}}>{copyMsg}</div>}
              </div>

              <div className="panel" style={{marginBottom:0}}>
                <div className="sec-title" style={{fontSize:22,marginBottom:10}}>Strategy Details</div>
                <div style={{display:"grid",gap:8,fontSize:13,color:"#555",lineHeight:1.4}}>
                  <div><strong style={{color:"#002855"}}>Creator:</strong> {data.creator||"LottoTracker Player"}</div>
                  <div><strong style={{color:"#002855"}}>Version:</strong> v{data.version||1}</div>
                  <div><strong style={{color:"#002855"}}>Pick rules:</strong> {formatPickRulesSummary(game,data.pickRules)}</div>
                  <div><strong style={{color:"#002855"}}>Performance:</strong> live and historical numbers are labeled separately before you copy anything.</div>
                  <div><strong style={{color:"#002855"}}>{signedIn?"Account status":"Signup required"}:</strong> {signedIn?"signed in - My Playslips actions are available.":"copy this playslip, create your own playslip, or save anything to My Playslips."}</div>
                  <div><strong style={{color:"#002855"}}>Trust:</strong> no tickets sold, no gambling, no money handled.</div>
                </div>
              </div>
            </div>
          </div>
          <footer style={{marginTop:26,borderTop:"1px solid #e8e2d6",paddingTop:16,display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"center",fontSize:12,color:"#555"}}>
            <span>LottoTracker is in soft launch.</span>
            <button type="button" className="btn btn-sm btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback / Contact</button>
          </footer>
          {feedbackOpen&&<FeedbackModal onClose={()=>setFeedbackOpen(false)}/>}
        </main>
      </>
    );
  }
  return(
    <><style>{CSS}{POLISH_CSS}</style>
      <main className="page" style={{minHeight:"100vh",display:"grid",placeItems:"center",padding:20}}>
        <div className="panel" style={{width:"min(680px,100%)",border:"1px solid #dbeee9"}}>
          <div className="logo" style={{marginBottom:8}}>Lotto<span>Tracker</span></div>
          <div className="sec-badge" style={{marginBottom:18}}>Public Preview</div>
          <div className="sec-title" style={{fontSize:34,marginBottom:8}}>{isResults?"Watch these results":title}</div>
          <p style={{fontSize:15,color:"#555",lineHeight:1.55,marginBottom:18}}>
            A LottoTracker user shared this {isResults?"results card":"strategy"} so you can follow the draw, save the idea, or create your own free account.
          </p>
          <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:12,padding:14,marginBottom:16}}>
            <strong style={{color:"#002855"}}>Simulation only.</strong>
            <div style={{fontSize:13,color:"#555",marginTop:5}}>No tickets sold. No money collected. No winnings distributed.</div>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <a className="btn btn-p" href="/">Create free account</a>
            <a className="btn btn-s" href="/results">View LottoTracker</a>
            <button type="button" className="btn btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback</button>
          </div>
          <footer style={{marginTop:22,borderTop:"1px solid #e8e2d6",paddingTop:14,display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"center",fontSize:12,color:"#555"}}>
            <span>LottoTracker is in soft launch. Your feedback helps us improve the app.</span>
            <button type="button" className="btn btn-sm btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback / Contact</button>
          </footer>
        </div>
        {feedbackOpen&&<FeedbackModal onClose={()=>setFeedbackOpen(false)}/>}
      </main>
    </>
  );
}

function publicStrategyFallback(shareId){
  const title=String(shareId||"lottotracker-strategy").split("-").filter(Boolean).map(part=>part.charAt(0).toUpperCase()+part.slice(1)).join(" ")||"LottoTracker Strategy";
  const plays=Array.from({length:122},(_,index)=>{
    const nums=[];
    let seed=index*7+3;
    while(nums.length<5){
      const n=(seed%36)+1;
      if(!nums.includes(n))nums.push(n);
      seed+=11;
    }
    return nums.sort((a,b)=>a-b);
  });
  return{
    id:shareId,
    title:/^manual$/i.test(title)?"Eric's Strategy":title,
    creator:"LottoTracker Player",
    game:"fantasy5",
    gameLabel:"Fantasy 5",
    status:"Watching",
    playCount:plays.length,
    plays:plays.map((numbers,index)=>({index:index+1,numbers})),
    latestDraw:null,
    results:[],
    stats:{drawsTracked:0,bestMatch:0,totalWon:0,totalSpent:0,roi:null,winners:0},
  };
}

function UserProfileTab({user,subscription,onSignOut}) {
  const navigate=useNavigate();
  const displayName=user?.user_metadata?.name||user?.email?.split("@")[0]||"LottoTracker Member";
  const initials=displayName.split(/[\s._-]+/).filter(Boolean).slice(0,2).map(part=>part[0]?.toUpperCase()).join("")||"LT";
  const memberDate=user?.created_at?new Date(user.created_at):null;
  const memberSince=memberDate&&!Number.isNaN(memberDate.getTime())?memberDate.toLocaleDateString("en-US",{month:"long",year:"numeric"}):null;
  const plan=PLAN_COPY[subscriptionTier(subscription)]?.label||"Free";
  const paid=subscriptionHas(subscription,"pro");
  const endDate=subscription?.current_period_end?new Date(subscription.current_period_end):null;
  const periodEnd=endDate&&!Number.isNaN(endDate.getTime())?endDate.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}):null;
  return <div>
    <section className="panel" style={{background:"#002855",color:"white",display:"flex",alignItems:"center",gap:18,flexWrap:"wrap"}}>
      <div aria-hidden="true" style={{width:64,height:64,borderRadius:"50%",background:"#008577",display:"grid",placeItems:"center",fontSize:28,fontWeight:900}}>{initials}</div>
      <div style={{minWidth:0}}><div style={{fontSize:12,letterSpacing:1,textTransform:"uppercase",opacity:.75}}>My Profile</div>
        <h1 style={{margin:"4px 0",fontSize:30,overflowWrap:"anywhere"}}>{displayName}</h1>
        {memberSince&&<div style={{fontSize:13,opacity:.8}}>Member since {memberSince}</div>}
      </div>
    </section>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,280px),1fr))",gap:16}}>
      <section className="panel" style={{marginBottom:0}}><h2 className="sec-title" style={{fontSize:22}}>My Membership</h2>
        <div style={{fontSize:28,fontWeight:900,color:"#008577",margin:"12px 0"}}>{plan}</div>
        {paid&&periodEnd&&<p>{subscription?.cancel_at_period_end?"Access through ":"Renews on "}{periodEnd}</p>}
        {paid&&subscription?.cancel_at_period_end&&<p style={{fontSize:13,color:"#555"}}>Your membership ends at the end of your paid period.</p>}
        <button type="button" className="btn btn-p" onClick={()=>navigate("/pro")}>{paid?"Manage Membership":"Explore Pro"}</button>
      </section>
      <section className="panel" style={{marginBottom:0}}><h2 className="sec-title" style={{fontSize:22}}>My Account</h2>
        <div style={{fontSize:12,color:"#555",marginTop:16}}>Email</div><p style={{overflowWrap:"anywhere",marginTop:5}}>{user?.email||"Email unavailable"}</p>
        <button type="button" className="btn btn-s" onClick={onSignOut}>Sign Out</button>
      </section>
      <section className="panel" style={{marginBottom:0}}><h2 className="sec-title" style={{fontSize:22}}>Need Help?</h2>
        <p style={{fontSize:14,color:"#555"}}>Questions about your account or membership? Get in touch.</p>
        <a className="btn btn-s" href={"mailto:"+SUPPORT_EMAIL} style={{textDecoration:"none",display:"inline-flex",marginTop:10}}>Contact Support</a>
      </section>
    </div>
  </div>;
}

function LegacyUserProfileTab({user,draws,plays,myGames,dailyStreak,featureUnlocks,strategyTrust,todaysLineup,subscription,performanceSummaries=[]}) {
  const navigate=useNavigate();
  const[tab,setTab]=useState("Overview");
  const[following,setFollowing]=useState(false);
  const[shared,setShared]=useState(false);
  const scoredDraws=(draws||[]).filter(d=>Array.isArray(d.numbers)&&d.numbers.length===gameNumberConfig(rowGame(d)).count);
  const savedPlays=(plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count);
  const publicPlays=filterPlaysForLineup(plays,todaysLineup);
  const publicSavedPlays=publicPlays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count);
  const publicEligibility=publicEligibilityForLineup(todaysLineup,strategyTrust);
  const userTrackedDraws=scoredDraws.filter(d=>
    publicSavedPlays.some(pl=>rowGame(pl)===rowGame(d)&&publicEligibility(pl,d))
  );
  const recentWins=scoredDraws.reduce((acc,d)=>{
    if(!publicSavedPlays.length)return acc;
    const eligiblePlays=publicSavedPlays.filter(pl=>rowGame(pl)===rowGame(d)&&publicEligibility(pl,d));
    const best=eligiblePlays.length?Math.max(0,...eligiblePlays.map(pl=>countWhatIfMatch(rowGame(d),pl.numbers,d.numbers))):0;
    if(best>=3)acc.push({d,best});
    return acc;
  },[]);
  const liveSummaryRows=performanceSummaryRowsFor(performanceSummaries,{periodKey:"all_time",drawFilter:"both"})
    .filter(r=>SHOW_ANCHORS||(r.strategy_id!=="anchor3"&&r.strategy_id!=="anchor4"));
  const fallbackStrategyRows=liveSummaryRows.length?[]:aggregateStrategies(publicPlays,scoredDraws,{eligibleForDraw:publicEligibility}).filter(r=>SHOW_ANCHORS||(r.id!=="anchor3"&&r.id!=="anchor4"));
  const strategyRows=liveSummaryRows.length
    ?liveSummaryRows.map(row=>({
      id:row.strategy_id,
      roi:row.roi==null?null:Number(row.roi),
      playCount:Number(row.total_cost)||0,
      m3:0,
      m4:0,
      meta:{label:strategyDisplayName(row.strategy_id,strategyTrust?.records?.[row.strategy_id])},
      draws_tracked:Number(row.draws_tracked)||0,
      best_match:Number(row.best_match)||0,
    }))
    :fallbackStrategyRows;
  const trustCounts=lockedStrategyCounts(strategyTrust);
  const publicStrategyRows=strategyRows.filter(row=>strategyTrust?.records?.[row.id]?.status==="Locked");
  const createdStrategyCount=strategyRows.filter(r=>r.id!=="manual").length;
  const topStrategy=[...(publicStrategyRows.length?publicStrategyRows:strategyRows)].sort((a,b)=>((b.roi??-Infinity)-(a.roi??-Infinity)))[0];
  const displayName=user?.user_metadata?.name||user?.email?.split("@")[0]||"LottoTracker User";
  const profilePlan=PLAN_COPY[subscriptionTier(subscription)]?.label||"Free";
  const initials=displayName.split(/[\s._-]+/).filter(Boolean).slice(0,2).map(x=>x[0]?.toUpperCase()).join("")||"LT";
  const memberSince=user?.created_at
    ?new Date(user.created_at).toLocaleDateString("en-US",{month:"long",year:"numeric"})
    :"Launch Member";
  const favoriteGame=gameLabel(normalizeGames(myGames)[0]);
  const bestRoi=topStrategy?.roi!=null?(topStrategy.roi>=0?"+":"")+topStrategy.roi.toFixed(0)+"%":"Pending";
  const latestAchievement=savedPlays.length?"First Numbers Saved":"Profile Created";
  const stats=[
    ["Member Since",memberSince],
    ["Current Plan",profilePlan],
    ["Favorite Game",favoriteGame],
    ["Current Streak",(dailyStreak||1)+" Day"],
    ["Lifetime ROI",bestRoi],
    ["Draws Tracked",userTrackedDraws.length],
    ["Lineup Strategies",createdStrategyCount],
    ["Locked Strategies",trustCounts.locked],
    ["Simulation Strategies",trustCounts.simulation],
    ["Active Strategy",trustCounts.active],
    ["Retired Strategies",trustCounts.retired],
    ["Pools Managed",0],
    ["Followers",0],
    ["Following",0],
  ];
  const achievements=[
    {label:"First Numbers Saved",done:publicSavedPlays.length>0},
    {label:"First 3 Match",done:recentWins.some(w=>w.best>=3)},
    {label:"100 Draws",done:userTrackedDraws.length>=100},
    {label:"500 Draws",done:userTrackedDraws.length>=500},
    {label:"1000 Draws",done:userTrackedDraws.length>=1000},
    {label:"Simulation Expert",done:!!featureUnlocks?.poolSimulation},
    {label:"Pool Manager",done:!!featureUnlocks?.poolManager},
    {label:"Community Contributor",done:createdStrategyCount>0||publicSavedPlays.length>0},
    {label:"First Locked Strategy",done:trustCounts.locked>=1},
    {label:"10 Locked Strategies",done:trustCounts.locked>=10},
    {label:"100 Locked Strategies",done:trustCounts.locked>=100},
    {label:"365 Locked Strategies",done:trustCounts.locked>=365},
  ];
  function shareProfile(){
    setShared(true);
    setTimeout(()=>setShared(false),1800);
  }
  function renderTab(){
    if(tab==="Strategies"){
      return(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:14}}>
          {publicStrategyRows.length?publicStrategyRows.map(row=>(
            <div key={row.id} className="sc-card">
              {(() => {
                const record=strategyTrust?.records?.[row.id];
                const status=strategyTrustStatusMeta(record?.status);
                return(
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                    <div className="sc-name" style={{color:"#002855"}}>{strategyDisplayName(row.id,record)||row.meta.label}</div>
                    <span className="sec-badge" style={{background:status.color}}>{status.icon} {status.label}</span>
                  </div>
                );
              })()}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,fontSize:12,marginTop:10}}>
                <div><span style={{color:"#555"}}>ROI</span><br/><strong>{row.roi==null?"Pending":(row.roi>=0?"+":"")+row.roi.toFixed(0)+"%"}</strong></div>
                <div><span style={{color:"#555"}}>Plays</span><br/><strong>{row.playCount}</strong></div>
                <div><span style={{color:"#555"}}>3 Matches</span><br/><strong>{row.m3}</strong></div>
                <div><span style={{color:"#555"}}>4 Matches</span><br/><strong>{row.m4}</strong></div>
              </div>
            </div>
          )):<EmptyState icon="*" title="No public lineup strategies yet." text="Add a strategy to Today's Lineup and lock it before a draw to make it eligible for public profiles, leaderboards, Community, and Strategy Library."/>}
        </div>
      );
    }
    if(tab==="Pools"){
      return(
        <div className="panel" style={{marginBottom:0}}>
          <div className="sec-title" style={{fontSize:20,marginBottom:8}}>Pools Managed</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:12}}>No public pools are connected to this profile yet. Pool Manager remains simulation-only: no money, tickets, or payment handling.</div>
          <button type="button" className="btn btn-p" onClick={()=>navigate("/pools")}>View Pools</button>
        </div>
      );
    }
    if(tab==="Achievements"){
      return(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
          {achievements.map(a=>(
            <div key={a.label} className="panel" style={{marginBottom:0,padding:"16px 18px",borderLeft:"5px solid "+(a.done?"#008577":"#ddd")}}>
              <div style={{fontSize:24,marginBottom:6}}>{a.done?"\u2713":"\u25CB"}</div>
              <div style={{fontWeight:900,color:a.done?"#002855":"#777"}}>{a.label}</div>
              <div style={{fontSize:11,color:a.done?"#008577":"#888",fontWeight:800,textTransform:"uppercase",marginTop:5}}>{a.done?"Unlocked":"Locked"}</div>
            </div>
          ))}
        </div>
      );
    }
    if(tab==="Activity"){
      const lifecycleEvents=(strategyTrust?.events||[]).slice(0,8);
      return(
        <div style={{display:"grid",gap:10}}>
          {lifecycleEvents.length?lifecycleEvents.map(item=>(
            <div key={item.id} className="panel" style={{marginBottom:0,padding:"14px 16px",borderLeft:"5px solid #008577"}}>
              <div style={{fontWeight:900,color:"#002855",marginBottom:3}}>{item.type}</div>
              <div style={{fontSize:12,color:"#666",lineHeight:1.4}}>{item.label} · {formatLockTimestamp(item.at)}{item.detail?" · "+item.detail:""}</div>
            </div>
          )):MOCK_ACTIVITY.slice(0,5).map(item=>(
            <div key={item.id} className="panel" style={{marginBottom:0,padding:"14px 16px",borderLeft:"5px solid "+item.tone}}>
              <div style={{fontWeight:900,color:"#002855",marginBottom:3}}>{item.icon} {item.title}</div>
              <div style={{fontSize:12,color:"#666"}}>{item.meta}</div>
            </div>
          ))}
        </div>
      );
    }
    return(
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16}}>
        <div className="panel" style={{marginBottom:0}}>
          <div className="sec-title" style={{fontSize:20,marginBottom:12}}>Overview</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,fontSize:13}}>
            <div><span style={{color:"#555"}}>Current favorite strategy</span><br/><strong>{topStrategy?.meta?.label||"Keep tracking"}</strong></div>
            <div><span style={{color:"#555"}}>Best ROI</span><br/><strong>{bestRoi}</strong></div>
            <div><span style={{color:"#555"}}>Latest achievement</span><br/><strong>{latestAchievement}</strong></div>
            <div><span style={{color:"#555"}}>Favorite games</span><br/><strong>{normalizeGames(myGames).map(gameLabel).join(", ")}</strong></div>
          </div>
        </div>
        <div className="panel" style={{marginBottom:0}}>
          <div className="sec-title" style={{fontSize:20,marginBottom:12}}>Recent Results</div>
          <div style={{display:"grid",gap:9}}>
            {scoredDraws.slice(0,4).map((d,i)=>{
              const best=savedPlays.length?Math.max(0,...savedPlays.map(pl=>cntMatch(pl.numbers,d.numbers))):0;
              return(
                <div key={(d.draw_date||d.date||"draw")+i} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:13,borderBottom:"1px solid #f0f0f0",paddingBottom:7}}>
                  <strong style={{color:"#002855"}}>{d.date||d.draw_date}</strong>
                  <span style={{color:best>=3?"#008577":"#777",fontWeight:800}}>{best?best+" match":"No match"}</span>
                </div>
              );
            })}
            {!scoredDraws.length&&<div style={{fontSize:13,color:"#555"}}>Results will appear after tracked draws are available.</div>}
          </div>
        </div>
      </div>
    );
  }
  return(
    <div>
      <div className="panel" style={{background:"#002855",color:"#fff",overflow:"hidden",position:"relative"}}>
        <div style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr)",gap:18,alignItems:"center"}}>
          <div style={{width:84,height:84,borderRadius:"50%",background:"#008577",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,boxShadow:"0 0 0 5px rgba(255,255,255,.12)"}}>{initials}</div>
          <div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.62)",fontWeight:900,textTransform:"uppercase",letterSpacing:2,marginBottom:6}}>Public Profile</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:42,fontWeight:900,lineHeight:1,textTransform:"uppercase"}}>{displayName}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,.72)",marginTop:7}}>Member since {memberSince} - Favorite game: {favoriteGame}</div>
          </div>
          <div style={{gridColumn:"1 / -1",display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-start"}}>
            <button type="button" className={"btn "+(following?"btn-p":"btn-s")} onClick={()=>setFollowing(v=>!v)}>{following?"Following":"Follow"}</button>
            <button type="button" className="btn btn-s" onClick={shareProfile}>{shared?"Shared":"Share Profile"}</button>
            <button type="button" className="btn btn-s" onClick={()=>navigate("/my-strategies")}>View Strategies</button>
            <button type="button" className="btn btn-s" onClick={()=>navigate("/pools")}>View Pools</button>
            <button type="button" className="btn btn-p" onClick={()=>navigate("/pro")}>{subscriptionHas(subscription,"pro")?"Manage Plan":"Start Pro"}</button>
          </div>
        </div>
      </div>

      <div className="sc-grid">
        {stats.map(([label,value])=>(
          <div key={label} className="sc-card">
            <div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:800,letterSpacing:1,marginBottom:6}}>{label}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:label==="Lifetime ROI"&&String(value).startsWith("-")?"#c8102e":"#002855",lineHeight:1}}>{value}</div>
          </div>
        ))}
      </div>

      <div className="tabs" style={{marginBottom:18}}>
        {["Overview","Strategies","Pools","Achievements","Activity"].map(name=>(
          <button key={name} type="button" className={"tab-btn "+(tab===name?"active":"")} onClick={()=>setTab(name)}>{name}</button>
        ))}
      </div>
      {renderTab()}
    </div>
  );
}

function NextBestStep({plays,latestReady,isFree,hasStrategy,onAdd,onResults,onUpgrade,onStrategy}){
  let action={icon:"🎲",title:"Add Your Numbers",text:"Save your first play so LottoTracker can start checking every draw.",button:"Add Your Numbers",onClick:onAdd};
  if(latestReady) action={icon:"🟢",title:"View Results",text:"Your latest selected draw is ready to review.",button:"View Results",onClick:onResults};
  else if(!plays.length) action={icon:"🎲",title:"Add Your Numbers",text:"Start with your favorite numbers or a Quick Pick.",button:"Add Your Numbers",onClick:onAdd};
  else if(isFree) action={icon:"⭐",title:"Unlock Pro",text:"Track every game, compare more strategies, and unlock advanced simulations.",button:"Unlock Pro",onClick:onUpgrade};
  else if(!hasStrategy) action={icon:"📊",title:"Create Strategy",text:"Tag or save a strategy so your Scoreboard has something to compare.",button:"Create Strategy",onClick:onStrategy};
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
        <div style={{fontSize:34,lineHeight:1}}>{action.icon}</div>
        <div style={{flex:1}}>
          <div className="sec-title" style={{fontSize:20,marginBottom:6}}>Next Best Step</div>
          <div style={{fontSize:16,fontWeight:700,color:"#002855",marginBottom:4}}>{action.title}</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:12}}>{action.text}</div>
          <button type="button" className="btn btn-p" onClick={action.onClick}>{action.button}</button>
        </div>
      </div>
    </div>
  );
}

function NextStepCard({title="Next Step",text,actions=[]}){
  if(!text&&!actions.length)return null;
  return(
    <div className="panel" style={{marginBottom:18,borderLeft:"5px solid #008577"}}>
      <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:14,alignItems:"center"}}>
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:6}}>{title}</div>
          {text&&<div style={{fontSize:13,color:"#555",lineHeight:1.5}}>{text}</div>}
        </div>
        {actions.length>0&&(
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
            {actions.map((action,i)=>(
              <button key={action.label} type="button" className={"btn "+(i===0?"btn-p":"btn-s")} onClick={action.onClick} disabled={action.disabled}>
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RecentActivityPreview({onViewAll,notifications=MOCK_ACTIVITY}){
  return(
    <div className="panel" style={{marginBottom:0}}>
      <div className="sec-hdr" style={{marginBottom:12}}>
        <span className="sec-title" style={{fontSize:20}}>Recent Activity</span>
        <button type="button" className="btn btn-sm btn-s" onClick={onViewAll}>View All</button>
      </div>
      <div style={{display:"grid",gap:10}}>
        {notifications.slice(0,3).map(n=>(
          <div key={n.id} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr)",gap:10,alignItems:"start"}}>
            <div style={{width:34,height:34,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",background:n.tone+"18",fontSize:20}}>{n.icon}</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#002855",lineHeight:1.25}}>{n.title}</div>
              <div style={{fontSize:12,color:"#666",lineHeight:1.35}}>{n.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuthPage({onAuth}){
  const[isUp,setIsUp]=useState(()=>{
    try{
      const mode=new URLSearchParams(window.location.search||"").get("auth");
      return String(mode||"").toLowerCase()==="signup";
    }catch{return false;}
  });
  const[email,setEmail]=useState("");
  const[pw,setPw]=useState("");
  const[signupGames,setSignupGames]=useState(FREE_DEFAULT_GAMES);
  const[loading,setLoading]=useState(false);
  const[err,setErr]=useState("");
  const[info,setInfo]=useState("");
  function toggleSignupGame(id){
    if(!PICK_GAME_IDS.includes(id))return;
    setSignupGames(freeGamesForPick(id));
  }
  async function go(){
    setErr("");setInfo("");setLoading(true);
    try{
      const authKey=(isUp?"signup":"login")+":"+email.trim().toLowerCase();
      const limit=isUp?rateLimitAction(authKey,3,60*60*1000):rateLimitAction(authKey,8,15*60*1000);
      if(!limit.allowed)throw new Error((isUp?"Too many signup attempts. ":"Too many login attempts. ")+"Please try again in about "+limit.retryText+".");
      const r=isUp?await supabase.auth.signUp({email,password:pw}):await supabase.auth.signInWithPassword({email,password:pw});
      if(r.error)throw r.error;
      if(r.data?.user&&isUp){
        const selected=enforceGameAccess(signupGames,"free",false);
        localStorage.setItem("lt:myGames:"+r.data.user.id,JSON.stringify(selected));
        localStorage.setItem("lt:trialStarted:"+r.data.user.id,new Date().toISOString());
      }
      if(isUp&&r.data?.user&&!r.data?.session){
        const immediate=await supabase.auth.signInWithPassword({email,password:pw});
        if(immediate.data?.user){
          if(immediate.data?.session)recordSignupAlert().catch(()=>{});
          onAuth(immediate.data.user);
          return;
        }
        setInfo("Check your email to confirm your account, then return here to sign in.");
        return;
      }
      if(r.data?.user){
        if(isUp&&r.data?.session)recordSignupAlert().catch(()=>{});
        onAuth(r.data.user);
      }
    }catch(e){setErr(e.message||"Failed");}
    finally{setLoading(false);}
  }
  async function recordSignupAlert(){
    const {data:{session}}=await supabase.auth.getSession();
    const token=session?.access_token;
    if(!token)return;
    const params=new URLSearchParams(window.location.search||"");
    const referralCode=String(params.get("ref")||localStorage.getItem("lt:pendingReferralCode")||"").trim();
    const referralSourceStrategyId=String(params.get("copyStrategy")||localStorage.getItem("lt:pendingPublicStrategy")||"").trim();
    const selectedPickGame=freePickGame(signupGames);
    await fetch("/api/public?action=signup-alert",{
      method:"POST",
      headers:{Authorization:"Bearer "+token,"Content-Type":"application/json"},
      body:JSON.stringify({email:email.trim().toLowerCase(),referralCode,referralSourceStrategyId,selectedPickGame}),
    });
  }
  async function resetPassword(){
    const clean=email.trim();
    if(!clean){setErr("Enter your email first, then tap Forgot password.");return;}
    setErr("");setInfo("");setLoading(true);
    try{
      const {error}=await supabase.auth.resetPasswordForEmail(clean,{redirectTo:window.location.origin});
      if(error)throw error;
      setInfo("Password reset sent. Check your email for the secure link.");
    }catch(e){setErr(e.message||"Password reset failed.");}
    finally{setLoading(false);}
  }
  return(
    <div className="auth-wrap"><div className="auth-card">
      <div className="auth-logo">Lotto<span>Tracker</span></div>
      <div className="auth-sub">Today's Picks &middot; Follow the numbers. Track the results.</div>
      <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap",margin:"10px 0 14px"}}>
        <span className="sec-badge" style={{background:"#008577"}}>{APP_VERSION_LABEL}</span>
      </div>
      <div style={{fontSize:12,color:"#555",lineHeight:1.45,textAlign:"center",margin:"0 auto 14px",maxWidth:360}}>{TRUST_COPY}</div>
      {err&&<div className="auth-err">{err}</div>}
      {info&&<div style={{background:"#f0f8f6",border:"1px solid #b8ded7",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#006a5e",marginBottom:14}}>{info}</div>}
      <div className="auth-fld"><label>Email</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com"/></div>
      <div className="auth-fld"><label>Password</label><input type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()}/></div>
      {LEGACY_ONBOARDING_ENABLED&&isUp&&(
        <div style={{border:"1px solid #e0e0e0",borderRadius:10,padding:"12px 14px",marginBottom:14,background:"#fafafa"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#002855",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Choose Your Free Pick Game</div>
          <div style={{fontSize:12,color:"#002855",fontWeight:800,marginBottom:8}}>Fantasy 5 is always included.</div>
          <div style={{display:"grid",gap:8}}>
            {launchReadyGames().filter(g=>PICK_GAME_IDS.includes(g.id)).map(g=>(
              <label key={g.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer"}}>
                <input type="radio" name="signup-game" checked={signupGames.includes(g.id)} onChange={()=>toggleSignupGame(g.id)}/>
                <span style={{fontWeight:600}}>{g.label}</span>
                <span style={{marginLeft:"auto",fontSize:10,color:gameRoadmapMeta(g.id).color,textTransform:"uppercase"}}>{g.status}</span>
              </label>
            ))}
          </div>
          <div style={{fontSize:11,color:"#777",marginTop:8,lineHeight:1.35}}>Free includes Fantasy 5 plus one Pick game. Upgrade anytime for every launch-ready game.</div>
        </div>
      )}
      <button className={"auth-btn "+(loading?"loading":"")} onClick={go} disabled={loading}>{loading?"...":isUp?"Create Account":"Sign In"}</button>
      {!isUp&&<div className="auth-tog" style={{marginTop:10}}><a onClick={resetPassword}>Forgot password?</a></div>}
      <div className="auth-tog">{isUp?<>Have account? <a onClick={()=>setIsUp(false)}>Sign in</a></>:<>New? <a onClick={()=>setIsUp(true)}>Create account</a></>}</div>
    </div></div>
  );
}

function parseNumberEntry(v){
  return String(v||"").trim().split(/[\s,\-]+/).map(x=>parseInt(x,10)).filter(n=>!isNaN(n));
}
function validPlayNumbers(nums){
  return nums.length===BALL_COUNT&&new Set(nums).size===BALL_COUNT&&nums.every(n=>n>=1&&n<=MAX_NUM);
}
function formatPlayNumbers(nums){
  return nums.map(n=>String(n).padStart(2,"0")).join(" ");
}
function gameNumberConfig(game="fantasy5"){
  if(game==="pick2")return{count:2,min:0,max:9,allowDuplicates:true,label:"Pick 2 numbers",maxDigits:1};
  if(game==="pick3")return{count:3,min:0,max:9,allowDuplicates:true,label:"Pick 3 numbers",maxDigits:1};
  if(game==="pick4")return{count:4,min:0,max:9,allowDuplicates:true,label:"Pick 4 numbers",maxDigits:1};
  if(game==="pick5")return{count:5,min:0,max:9,allowDuplicates:true,label:"Pick 5 numbers",maxDigits:1};
  return{count:5,min:1,max:36,allowDuplicates:false,label:"Fantasy 5 numbers",maxDigits:2};
}
function numberBoxValuesFromNumbers(nums,count=BALL_COUNT){
  const values=Array.from({length:count},(_,i)=>nums?.[i]!==undefined&&nums?.[i]!==null?String(nums[i]):"");
  return values;
}
function validateNumberBoxes(values,config=gameNumberConfig()){
  const count=config.count||BALL_COUNT;
  const min=config.min??1;
  const max=config.max??MAX_NUM;
  const raw=Array.from({length:count},(_,i)=>String(values?.[i]||"").trim());
  if(raw.some(v=>!v))return{error:"Enter all "+count+" numbers."};
  const nums=raw.map(v=>parseInt(v,10));
  if(nums.some(n=>!Number.isInteger(n)))return{error:"Use numbers only."};
  if(nums.some(n=>n<min||n>max))return{error:"Each number must be between "+min+" and "+max+"."};
  if(!config.allowDuplicates&&new Set(nums).size!==count)return{error:"Numbers must be unique."};
  return{numbers:config.allowDuplicates?nums:[...nums].sort((a,b)=>a-b),error:""};
}
function NumberBoxes({values,onChange,disabled=false,label="Fantasy 5 numbers",count=BALL_COUNT,maxDigits=2}){
  const refs=useRef([]);
  const current=Array.from({length:count},(_,i)=>String(values?.[i]||""));
  function setAt(index,value){
    const digits=String(value||"").replace(/\D/g,"");
    const next=[...current];
    if(digits.length>maxDigits&&maxDigits===1){
      digits.split("").slice(0,count-index).forEach((digit,offset)=>{next[index+offset]=digit;});
      onChange(next);
      refs.current[Math.min(count-1,index+digits.length-1)]?.focus();
      return;
    }
    const clean=digits.slice(0,maxDigits);
    next[index]=clean;
    onChange(next);
    if(clean.length>=maxDigits&&index<count-1)refs.current[index+1]?.focus();
  }
  return(
    <div role="group" aria-label={label} style={{display:"grid",gridTemplateColumns:"repeat("+count+",minmax(44px,58px))",gap:8,maxWidth:Math.max(220,count*66)}}>
      {current.map((value,index)=>(
        <input
          key={index}
          ref={el=>{refs.current[index]=el;}}
          className="add-inp"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={maxDigits}
          value={value}
          disabled={disabled}
          onChange={e=>setAt(index,e.target.value)}
          onKeyDown={e=>{
            if(e.key==="Backspace"&&!current[index]&&index>0)refs.current[index-1]?.focus();
          }}
          aria-label={"Number "+(index+1)}
          style={{textAlign:"center",fontSize:20,fontWeight:900,padding:"11px 8px",minWidth:0,width:"100%"}}
        />
      ))}
    </div>
  );
}

function firstRunRevealTier(game){
  const count=gameNumberConfig(game).count;
  return{top:count,near:Math.max(0,count-1)};
}
function firstRunPickMultisetMatch(play=[],draw=[]){
  const counts=new Map();
  (draw||[]).forEach(n=>counts.set(Number(n),(counts.get(Number(n))||0)+1));
  return(play||[]).reduce((sum,n)=>{
    const key=Number(n);
    const available=counts.get(key)||0;
    if(available<=0)return sum;
    counts.set(key,available-1);
    return sum+1;
  },0);
}
function firstRunPickPositionalMatch(play=[],draw=[]){
  return(play||[]).reduce((sum,n,index)=>sum+(Number(n)===Number(draw?.[index])?1:0),0);
}
function firstRunMatchCount(game,entry,drawNumbers=[]){
  const nums=previewEntryNumbers(entry);
  if(!nums.length||!Array.isArray(drawNumbers))return 0;
  if(!isPickGame(game))return cntMatch(nums,drawNumbers);
  const playType=String(entry?.pickRules?.playType||"Straight").toLowerCase();
  if(playType.includes("box")){
    return Math.max(firstRunPickPositionalMatch(nums,drawNumbers),firstRunPickMultisetMatch(nums,drawNumbers));
  }
  return firstRunPickPositionalMatch(nums,drawNumbers);
}
function summarizeFirstRunReveal(game,entries=[],drawRows=[]){
  const tier=firstRunRevealTier(game);
  let topHits=0;
  let nearMisses=0;
  let freeTickets=0;
  let mostRecentNearMiss=null;
  (drawRows||[]).forEach(draw=>{
    let drawTop=false;
    let drawNear=false;
    (entries||[]).forEach(entry=>{
      const match=firstRunMatchCount(game,entry,draw?.numbers||[]);
      if(match>=tier.top)drawTop=true;
      if(match===tier.near)drawNear=true;
      if(game==="fantasy5"&&match===2)freeTickets+=1;
    });
    if(drawTop)topHits+=1;
    if(drawNear){
      nearMisses+=1;
      mostRecentNearMiss=draw;
    }
  });
  return{drawsChecked:(drawRows||[]).length,topHits,nearMisses,mostRecentNearMiss,freeTickets};
}
function firstRunRevealOddsContext(game){
  return"Past appearances do not increase future odds. This is a plain record of what happened in completed official draws.";
}
function firstRunRevealDate(row){
  if(!row)return"";
  const date=row.draw_date?new Date(row.draw_date+"T12:00:00"):null;
  const label=date&&!Number.isNaN(date.getTime())?date.toLocaleDateString("en-US",{month:"long",day:"numeric"}):row.draw_date;
  return label+" "+drawTimeLabel(row).toLowerCase();
}
function lastCompleteCalendarYearET(reference=new Date()){
  const parts=new Intl.DateTimeFormat("en-US",{timeZone:EASTERN_TIME_ZONE,year:"numeric"}).formatToParts(reference);
  return Number(parts.find(part=>part.type==="year")?.value||reference.getFullYear())-1;
}
function lastCompleteYearBoundsET(reference=new Date()){
  const year=lastCompleteCalendarYearET(reference);
  return{year,from:year+"-01-01",to:year+"-12-31"};
}
function occurrenceWord(count){
  if(count===1)return"once";
  if(count===2)return"twice";
  return count.toLocaleString()+" times";
}
function FirstRunRevealContent({user,strategyId,game,onThisYear,onImprove,onKeep,onAnother,saving=false}){
  const[status,setStatus]=useState({loading:true,error:"",record:null,summary:null});
  useEffect(()=>{
    let cancelled=false;
    async function loadReveal(){
      if(!user?.id||!strategyId){
        setStatus({loading:false,error:"Strategy was saved, but I could not find the reveal target yet.",record:null,summary:null});
        return;
      }
      setStatus({loading:true,error:"",record:null,summary:null});
      try{
        const versionResult=await supabase
          .from("strategy_versions")
          .select("strategy_id,strategy_name,game,snapshot,play_count,estimated_cost,version_number,is_active,retired_at")
          .eq("user_id",user.id)
          .eq("strategy_id",strategyId)
          .eq("is_active",true)
          .is("retired_at",null)
          .maybeSingle();
        if(versionResult.error)throw versionResult.error;
        if(!versionResult.data)throw new Error("No active saved version found.");
        const strategyGame=versionResult.data.game||game||"fantasy5";
        const bounds=lastCompleteYearBoundsET();
        const drawResult=await supabase
          .from("draws")
          .select("id,game,draw_date,draw_time,numbers,official_result_status,source")
          .eq("game",strategyGame)
          .eq("official_result_status","official")
          .gte("draw_date",bounds.from)
          .lte("draw_date",bounds.to)
          .order("draw_date",{ascending:true})
          .order("draw_time",{ascending:true})
          .limit(1000);
        if(drawResult.error)throw drawResult.error;
        const snapshot=parseStrategySnapshot(versionResult.data.snapshot)||{};
        const record=strategyBuilderRecord(versionResult.data.strategy_id,{
          id:versionResult.data.strategy_id,
          baseName:versionResult.data.strategy_name,
          game:strategyGame,
          version:versionResult.data.version_number,
          playSize:versionResult.data.play_count,
          estimatedCost:versionResult.data.estimated_cost,
          outcomeSnapshot:snapshot,
          pickRules:snapshot.pickRules||null,
          sourceOfTruth:"strategy_versions",
        });
        const entries=isPickGame(strategyGame)?strategySnapshotPlayEntries(record):strategySnapshotNumbers(record);
        const rows=(Array.isArray(drawResult.data)?drawResult.data:[])
          .filter(row=>rowGame(row)===strategyGame)
          .filter(row=>isOfficialDraw(row))
          .filter(row=>Array.isArray(row.numbers)&&row.numbers.length===gameNumberConfig(strategyGame).count);
        const summary=summarizeFirstRunReveal(strategyGame,entries,rows);
        if(!cancelled)setStatus({loading:false,error:"",record,summary});
      }catch(error){
        if(!cancelled)setStatus({loading:false,error:error.message||"Could not load completed official draws.",record:null,summary:null});
      }
    }
    loadReveal();
    return()=>{cancelled=true;};
  },[user?.id,strategyId,game]);
  if(status.loading){
    return(
      <div style={{border:"1px solid #e8edf2",borderRadius:14,padding:18,background:"#f8fbfb",marginBottom:18}}>
        <strong style={{color:"#002855"}}>Checking your strategy against completed official results...</strong>
      </div>
    );
  }
  if(status.error){
    return(
      <div style={{border:"1px solid #f1d0d0",borderRadius:14,padding:18,background:"#fff6f6",marginBottom:18}}>
        <strong style={{color:"#8b1e1e"}}>Your strategy is saved.</strong>
        <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginTop:5}}>The first-run reveal could not load yet: {status.error}</div>
      </div>
    );
  }
  const record=status.record||{};
  const summary=status.summary||{};
  const strategyGame=record.game||game||"fantasy5";
  const entries=isPickGame(strategyGame)?strategySnapshotPlayEntries(record):strategySnapshotNumbers(record);
  const primaryNumbers=previewEntryNumbers(entries[0]||[]);
  const primaryText=digitsText(primaryNumbers);
  const revealYear=lastCompleteCalendarYearET();
  const resultLine=summary.topHits>0
    ?"Great news! "+primaryText+" came out "+summary.topHits.toLocaleString()+" time"+(summary.topHits===1?"":"s")+" last year."
    :primaryText+" did not appear last year. Now you know - check this year or add more numbers.";
  const nearLine=summary.nearMisses>0
    ?(isPickGame(strategyGame)
      ?"But you were one digit away "+summary.nearMisses.toLocaleString()+" times. Closest: "+firstRunRevealDate(summary.mostRecentNearMiss)+"."
      :"But you were one number away "+summary.nearMisses.toLocaleString()+" times. Closest: "+firstRunRevealDate(summary.mostRecentNearMiss)+".")
    :"";
  return(
    <div style={{marginBottom:18}}>
      <div style={{border:"1px solid #dfe9e7",borderRadius:16,padding:18,background:"linear-gradient(180deg,#ffffff,#f7fbfa)"}}>
        <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>First Saved Number Check</div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:31,fontWeight:900,color:"#002855",lineHeight:1,marginBottom:8}}>
          {strategyDisplayName(strategyId,record)}
        </div>
        <div style={{fontSize:13,color:"#555",fontWeight:800,marginBottom:14}}>{gameLabel(strategyGame)} - {revealYear} completed official draws</div>
        {summary.drawsChecked>0?(
          <>
            <p style={{fontSize:16,color:"#002855",fontWeight:900,lineHeight:1.45,margin:"0 0 12px"}}>{resultLine}</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:10,marginBottom:14}}>
              <div style={{background:"#f3f7fb",borderRadius:12,padding:12}}>
                <div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>Appeared last year</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#002855"}}>
                  {summary.topHits.toLocaleString()} <span style={{fontSize:18,color:"#555"}}>/ {summary.drawsChecked.toLocaleString()} draws</span>
                </div>
              </div>
              <div style={{background:"#f4fbf8",borderRadius:12,padding:12}}>
                <div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>One-away draws</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#008577"}}>{summary.nearMisses.toLocaleString()}</div>
              </div>
              {strategyGame==="fantasy5"&&(
                <div style={{background:"#fffaf0",borderRadius:12,padding:12}}>
                  <div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>Free tickets</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#c8860a"}}>{summary.freeTickets.toLocaleString()}</div>
                </div>
              )}
            </div>
            {nearLine&&<p style={{fontSize:15,color:"#002855",fontWeight:800,lineHeight:1.45,margin:"0 0 10px"}}>{nearLine}</p>}
            {strategyGame==="fantasy5"&&summary.freeTickets>0&&<p style={{fontSize:14,color:"#555",lineHeight:1.45,margin:"0 0 10px"}}>Your numbers would have earned {summary.freeTickets.toLocaleString()} free ticket{summary.freeTickets===1?"":"s"} during this window.</p>}
            <p style={{fontSize:13,color:"#555",lineHeight:1.5,margin:0}}>{firstRunRevealOddsContext(strategyGame)}</p>
          </>
        ):(
          <div style={{background:"#f8f8f8",borderRadius:12,padding:14,color:"#555",fontWeight:800}}>No completed draws in range.</div>
        )}
      </div>
      <div style={{border:"1px solid #e6e6e6",borderRadius:14,padding:14,marginTop:12,background:"#fff"}}>
        <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:10}}>
          A playslip is something you can reuse, test, and share.
        </div>
        <button type="button" className={"btn btn-p "+(saving?"loading":"")} style={{width:"100%",fontSize:18,padding:"14px 18px",marginBottom:10}} onClick={onThisYear} disabled={saving}>
          See how it is doing this year
        </button>
        <button type="button" className={"btn btn-p "+(saving?"loading":"")} style={{width:"100%",fontSize:18,padding:"14px 18px",marginBottom:10}} onClick={onImprove} disabled={saving}>
          Add more numbers to my playslip
        </button>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:10}}>
          <button type="button" className={"btn btn-s "+(saving?"loading":"")} onClick={onKeep} disabled={saving}>Keep it saved in My Playslips</button>
          {onAnother&&<button type="button" className={"btn btn-s "+(saving?"loading":"")} onClick={onAnother} disabled={saving}>Build Another Playslip</button>}
        </div>
      </div>
    </div>
  );
}

function SetupWizard({initialGames,planTier,user,initialStep=1,onProgress,onComplete}){
  const[step,setStep]=useState(Math.max(1,Math.min(5,Number(initialStep)||1)));
  const[initialFreePick]=useState(()=>freePickGame(initialGames));
  const[selectedGames,setSelectedGames]=useState(()=>enforceGameAccess(initialGames,planTier,false));
  const[strategyName,setStrategyName]=useState("My First Strategy");
  const[numberMode,setNumberMode]=useState("Manual");
  const[numberBoxes,setNumberBoxes]=useState(()=>numberBoxValuesFromNumbers([],gameNumberConfig(freePickGame(initialGames)||"fantasy5").count));
  const[quickPick,setQuickPick]=useState(()=>rndGamePick(freePickGame(initialGames)||"fantasy5"));
  const[err,setErr]=useState("");
  const[saving,setSaving]=useState(false);
  const[createdStrategyId,setCreatedStrategyId]=useState("");
  const isPro=planAtLeast(planTier,"pro");
  const selectedPick=freePickGame(selectedGames)||initialFreePick;
  const strategyGame=selectedPick||"fantasy5";
  const strategyGameConfig=gameNumberConfig(strategyGame);
  const numberOptions=[
    {id:"manual",label:"Manual",desc:"Type the exact numbers you want LottoTracker to track."},
    {id:"quick",label:"Quick Picks",desc:"Let LottoTracker create a random starter set."},
    {id:"birthday",label:"Birthday Numbers",desc:"Use meaningful dates and favorite family numbers."},
    {id:"favorite",label:"Favorites",desc:"Save the numbers you already like to play."},
  ];
  function toggleGame(id){
    setSelectedGames(current=>{
      if(isPro){
        const next=current.includes(id)?current.filter(x=>x!==id):[...current,id];
        return normalizeGames(next);
      }
      return freeGamesForPick(id);
    });
  }
  function fillQuickPick(){
    const next=rndGamePick(strategyGame);
    setQuickPick(next);
    setNumberBoxes(numberBoxValuesFromNumbers(next,strategyGameConfig.count));
    setNumberMode("Quick Picks");
    setErr("");
  }
  useEffect(()=>{
    setNumberBoxes(numberBoxValuesFromNumbers([],strategyGameConfig.count));
    setQuickPick(rndGamePick(strategyGame));
    setErr("");
  },[strategyGame,strategyGameConfig.count]);
  useEffect(()=>{
    onProgress?.(step);
  },[step]);
  const setupHasUnsavedWork=step>=3&&!createdStrategyId;
  useEffect(()=>{
    window.__LOTTO_TRACKER_UNSAVED_WORK__=setupHasUnsavedWork;
    return()=>{window.__LOTTO_TRACKER_UNSAVED_WORK__=false;};
  },[setupHasUnsavedWork]);
  async function saveDraftStrategy(){
    setErr("");
    const name=(strategyName||"").trim();
    if(!name){setErr("Name your strategy before saving.");setStep(3);return;}
    const checked=validateNumberBoxes(numberBoxes,strategyGameConfig);
    if(checked.error){setErr(checked.error);setStep(4);return;}
    setSaving(true);
    try{
      const strategyId=await onComplete({games:selectedGames,strategyGame,strategyName:name,source:numberMode,numbers:checked.numbers,intent:"draft"});
      if(strategyId){
        setCreatedStrategyId(strategyId);
        setErr("");
        setStep(5);
      }
    }finally{setSaving(false);}
  }
  async function finishStrategy(intent){
    setErr("");
    if(!createdStrategyId){
      setErr("Your playslip needs to be saved first. Tap Back, then Save to My Vault again.");
      return;
    }
    setSaving(true);
    try{
      await onComplete({
        games:selectedGames,
        strategyId:createdStrategyId,
        intent,
        path:intent==="results"
          ?strategyPerformancePath(createdStrategyId,strategyGame,"365","both")
          :intent==="improve"
          ?strategyEditPath(createdStrategyId,{onboarding:true})
          :intent==="later"?"/my-strategies":intent==="another"?"/strategy-builder":"/"
      });
    }finally{setSaving(false);}
  }
  const shellStyle={minHeight:"100vh",background:"var(--cream)",display:"flex",alignItems:"center",justifyContent:"center",padding:"28px 18px"};
  const cardStyle={width:"min(860px,100%)",background:"#fff",borderRadius:16,boxShadow:"0 14px 40px rgba(0,0,0,.14)",overflow:"hidden"};
  const headStyle={background:"#002855",color:"#fff",padding:"28px 32px"};
  const bodyStyle={padding:"28px 32px"};
  const titleStyle={fontFamily:"'Barlow Condensed',sans-serif",fontSize:38,fontWeight:900,textTransform:"uppercase",lineHeight:1,color:"#002855",marginBottom:8};
  const optionGrid={display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12,marginTop:18,marginBottom:20};
  const progress=(step/5)*100;
  return(
    <div style={shellStyle}>
      <div style={cardStyle}>
        <div style={headStyle}>
          <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:2,color:"#D4AF37",fontWeight:700,marginBottom:10}}>Simple Mode Setup</div>
          <div style={{height:8,background:"rgba(255,255,255,.14)",borderRadius:8,overflow:"hidden"}}>
            <div style={{height:"100%",width:progress+"%",background:"#008577",transition:"width .2s ease"}}/>
          </div>
        </div>
        <div style={bodyStyle}>
          {step===1&&(
            <div>
              <div style={{fontSize:14,color:"#008577",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Step 1</div>
              <div style={titleStyle}>Welcome to LottoTracker</div>
              <div style={{fontSize:15,color:"#555",lineHeight:1.65,maxWidth:680,marginBottom:4}}>
                <div style={{fontWeight:800,color:"#002855",marginBottom:8}}>This app helps you:</div>
                {[
                  "Save your lottery numbers",
                  "Track every draw automatically",
                  "See what your numbers would have won",
                  "Build and compare strategies",
                  "Never lose track of your favorite numbers again",
                ].map(item=><div key={item}><strong style={{color:"#008577"}}>{"\u2713"}</strong> {item}</div>)}
              </div>
              <div className="launch-note" style={{margin:"18px 0"}}>{TRUST_COPY}</div>
              <button type="button" className="btn btn-p" style={{fontSize:18,padding:"13px 22px"}} onClick={()=>setStep(2)}>Let's Get Started</button>
            </div>
          )}

          {step===2&&(
            <div>
              <div style={{fontSize:14,color:"#008577",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Step 2</div>
              <div style={titleStyle}>Your Free games</div>
              <p style={{fontSize:15,color:"#555",lineHeight:1.55,maxWidth:640,marginBottom:4}}>
                Fantasy 5 is included automatically. Choose one Pick game to build your first strategy around.
              </p>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",margin:"12px 0 16px"}}>
                <span className="sec-badge" style={{background:"#008577"}}>Fantasy 5 included</span>
                {selectedPick?<span className="sec-badge" style={{background:"#002855"}}>{gameLabel(selectedPick)} included</span>:<span className="sec-badge" style={{background:"#c8860a"}}>Choose one Pick game</span>}
              </div>
              <div style={optionGrid}>
                {launchReadyGames().filter(g=>PICK_GAME_IDS.includes(g.id)).map(g=>{
                  const checked=selectedGames.includes(g.id);
                  const meta=gameRoadmapMeta(g.id);
                  return(
                    <button
                      key={g.id}
                      type="button"
                      onClick={()=>toggleGame(g.id)}
                      style={{
                        textAlign:"left",background:checked?"#f0f8f6":"#fff",border:"2px solid "+(checked?"#008577":"#e5e5e5"),
                        borderRadius:10,padding:"14px 16px",cursor:"pointer",minHeight:94
                      }}
                    >
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:21,fontWeight:700,color:checked?"#008577":"#002855",textTransform:"uppercase"}}>{g.label}</div>
                      <div style={{fontSize:11,color:meta.color,fontWeight:700,textTransform:"uppercase",marginTop:3}}>{g.status}</div>
                      <div style={{fontSize:12,color:"#555",marginTop:6,lineHeight:1.3}}>{g.upcoming}</div>
                    </button>
                  );
                })}
              </div>
              <button type="button" className="btn btn-p" onClick={()=>setStep(3)} disabled={!selectedPick}>Continue</button>
            </div>
          )}

          {step===3&&(
            <div>
              <div style={{fontSize:14,color:"#008577",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Step 3</div>
              <div style={titleStyle}>Create your first strategy</div>
              <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:650}}>
                Name the set of numbers you want LottoTracker to watch for {gameLabel(strategyGame)}. Fantasy 5 stays included too.
              </p>
              <div className="auth-fld" style={{maxWidth:460,marginTop:18}}>
                <label>Strategy Name</label>
                <input value={strategyName} onChange={e=>{setStrategyName(e.target.value);setErr("");}} placeholder="Birthday Numbers"/>
              </div>
              {err&&<div className="auth-err" style={{maxWidth:520}}>{err}</div>}
              <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-s" onClick={()=>setStep(2)}>Back</button>
                <button type="button" className="btn btn-p" onClick={()=>{if(!(strategyName||"").trim()){setErr("Name your strategy before continuing.");return;}setErr("");setStep(4);}}>Continue</button>
              </div>
            </div>
          )}

          {step===4&&(
            <div>
              <div style={{fontSize:14,color:"#008577",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Step 4</div>
              <div style={titleStyle}>Add numbers</div>
              <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:650,marginBottom:16}}>
                Add one {gameLabel(strategyGame)} play to your strategy. {strategyGame==="fantasy5"?"Each box must be a unique number from 1 to 36.":"Each box is one digit from 0 to 9."}
              </p>
              <div style={{display:"grid",gap:12,marginBottom:16}}>
                <NumberBoxes values={numberBoxes} onChange={values=>{setNumberBoxes(values);setNumberMode("Manual");setErr("");}} label={strategyGameConfig.label} count={strategyGameConfig.count} maxDigits={strategyGameConfig.maxDigits}/>
                <button type="button" className="btn btn-s btn-sm" style={{justifySelf:"start"}} onClick={fillQuickPick}>Quick Pick</button>
              </div>
              {err&&<div className="auth-err" style={{maxWidth:520}}>{err}</div>}
              <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-s" onClick={()=>setStep(3)}>Back</button>
                <button type="button" className={"btn btn-p "+(saving?"loading":"")} onClick={saveDraftStrategy} disabled={saving}>Save to My Vault</button>
              </div>
            </div>
          )}

          {step===5&&(
            <div>
              <div style={{fontSize:14,color:"#008577",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Step 5</div>
              <div style={titleStyle}>Congratulations! You built your first strategy.</div>
              <div style={{fontSize:14,color:"#555",lineHeight:1.55,maxWidth:650,marginBottom:18}}>
                Now see how your strategy would have performed using completed official lottery results.
              </div>
              <FirstRunRevealContent
                user={user}
                strategyId={createdStrategyId}
                game={strategyGame}
                saving={saving}
                onThisYear={()=>finishStrategy("results")}
                onImprove={()=>finishStrategy("improve")}
                onKeep={()=>finishStrategy("later")}
                onAnother={()=>finishStrategy("another")}
              />
              <button type="button" className="btn btn-sm btn-s" onClick={()=>setStep(4)} disabled={saving}>Back</button>
              {err&&<div className="auth-err" style={{maxWidth:520,marginTop:12}}>{err}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SetupWizardOld({initialGames,planTier,onComplete}){
  const[step,setStep]=useState(1);
  const[selectedGames,setSelectedGames]=useState(normalizeGames(initialGames).slice(0,planAtLeast(planTier,"pro")?launchReadyGames().length:1));
  const[strategy,setStrategy]=useState("");
  const isPro=planAtLeast(planTier,"pro");
  const strategyOptions=[
    {id:"own",label:"My Own Numbers",desc:"Enter the numbers you already like to play."},
    {id:"quick",label:"Quick Picks",desc:"Generate random sets to start tracking right away."},
    {id:"hot",label:"Hot Numbers",desc:"Start with numbers appearing most often in recent draws."},
    {id:"cold",label:"Cold Numbers",desc:"Explore numbers that have been quieter lately."},
  ];
  function toggleGame(id){
    setSelectedGames(current=>{
      if(isPro){
        const next=current.includes(id)?current.filter(x=>x!==id):[...current,id];
        return normalizeGames(next);
      }
      return [id];
    });
  }
  function finish(path){
    onComplete(selectedGames,strategy,path);
  }
  const shellStyle={minHeight:"100vh",background:"var(--cream)",display:"flex",alignItems:"center",justifyContent:"center",padding:"28px 18px"};
  const cardStyle={width:"min(860px,100%)",background:"#fff",borderRadius:16,boxShadow:"0 14px 40px rgba(0,0,0,.14)",overflow:"hidden"};
  const headStyle={background:"#002855",color:"#fff",padding:"28px 32px"};
  const bodyStyle={padding:"28px 32px"};
  const titleStyle={fontFamily:"'Barlow Condensed',sans-serif",fontSize:38,fontWeight:900,textTransform:"uppercase",lineHeight:1,color:"#002855",marginBottom:8};
  const optionGrid={display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12,marginTop:18,marginBottom:20};
  const progress=(step/4)*100;
  return(
    <div style={shellStyle}>
      <div style={cardStyle}>
        <div style={headStyle}>
          <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:2,color:"#D4AF37",fontWeight:700,marginBottom:10}}>First-Time Setup</div>
          <div style={{height:8,background:"rgba(255,255,255,.14)",borderRadius:8,overflow:"hidden"}}>
            <div style={{height:"100%",width:progress+"%",background:"#008577",transition:"width .2s ease"}}/>
          </div>
        </div>
        <div style={bodyStyle}>
          {step===1&&(
            <div>
              <div style={{fontSize:14,color:"#008577",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Welcome to LottoTracker.</div>
              <div style={titleStyle}>Let's set up your Lottery Dashboard.</div>
              <p style={{fontSize:15,color:"#555",lineHeight:1.55,maxWidth:600,marginBottom:24}}>
                Choose the lottery games you want LottoTracker to track for you.
              </p>
              <button type="button" className="btn btn-p" onClick={()=>setStep(2)}>Get Started</button>
            </div>
          )}

          {step===2&&(
            <div>
              <div style={titleStyle}>Choose Your Games</div>
              <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:650}}>
                Free users may select one game. Pro users will unlock all games later.
              </p>
              <div style={optionGrid}>
                {launchReadyGames().map(g=>{
                  const checked=selectedGames.includes(g.id);
                  const meta=gameRoadmapMeta(g.id);
                  return(
                    <button
                      key={g.id}
                      type="button"
                      onClick={()=>toggleGame(g.id)}
                      style={{
                        textAlign:"left",background:checked?"#f0f8f6":"#fff",border:"2px solid "+(checked?"#008577":"#e5e5e5"),
                        borderRadius:10,padding:"14px 16px",cursor:"pointer",minHeight:94
                      }}
                    >
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:21,fontWeight:700,color:checked?"#008577":"#002855",textTransform:"uppercase"}}>{g.label}</div>
                      <div style={{fontSize:11,color:meta.color,fontWeight:700,textTransform:"uppercase",marginTop:3}}>{g.status}</div>
                      <div style={{fontSize:12,color:"#555",marginTop:6,lineHeight:1.3}}>{g.upcoming}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-s" onClick={()=>setStep(1)}>Back</button>
                <button type="button" className="btn btn-p" onClick={()=>setStep(3)} disabled={!selectedGames.length}>Continue</button>
              </div>
            </div>
          )}

          {step===3&&(
            <div>
              <div style={titleStyle}>Choose Your First Strategy</div>
              <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:650}}>
                Pick a starting point for your tracking. You can skip this and decide later.
              </p>
              <div style={optionGrid}>
                {strategyOptions.map(opt=>{
                  const checked=strategy===opt.id;
                  return(
                    <button
                      key={opt.id}
                      type="button"
                      onClick={()=>setStrategy(opt.id)}
                      style={{
                        textAlign:"left",background:checked?"#f0f8f6":"#fff",border:"2px solid "+(checked?"#008577":"#e5e5e5"),
                        borderRadius:10,padding:"16px",cursor:"pointer",minHeight:118
                      }}
                    >
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:21,fontWeight:700,color:checked?"#008577":"#002855",textTransform:"uppercase",marginBottom:6}}>{opt.label}</div>
                      <div style={{fontSize:13,color:"#555",lineHeight:1.35}}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-s" onClick={()=>setStep(2)}>Back</button>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <button type="button" className="btn btn-s" onClick={()=>{setStrategy("");setStep(4);}}>Skip</button>
                  <button type="button" className="btn btn-p" onClick={()=>setStep(4)}>Continue</button>
                </div>
              </div>
            </div>
          )}

          {step===4&&(
            <div style={{textAlign:"center",padding:"12px 0"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:44,fontWeight:900,color:"#008577",textTransform:"uppercase",lineHeight:1,marginBottom:10}}>Success</div>
              <div style={{fontSize:18,fontWeight:700,color:"#002855",marginBottom:8}}>Your dashboard is ready.</div>
              <div style={{fontSize:13,color:"#555",marginBottom:24}}>
                Tracking {selectedGames.map(gameLabel).join(", ")}{strategy?(" with "+strategyOptions.find(o=>o.id===strategy)?.label+" as your first strategy."):"."}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-p" onClick={()=>finish("/")}>Go To Dashboard</button>
                <button type="button" className="btn btn-s" onClick={()=>finish("/my-strategies")}>Add My Playslips</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function summarizeResults(draw,plays,options={}){
  const game=rowGame(draw);
  const expectedCount=gameNumberConfig(game).count||BALL_COUNT;
  const materialized=(plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===expectedCount&&rowGame(p)===game);
  const rows=materialized.map((pl,index)=>{
    const match=cntMatch(pl.numbers,draw?.numbers);
    const prize=getDrawPrize(draw,match);
    const amount=prize&&Number.isFinite(Number(prize.amount))?Number(prize.amount):0;
    const eligibility=draw&&options.lineup?classifyPlayForDraw(pl,draw,options.lineup,options.strategyTrust):{countsPublic:true,label:"Tracked",tone:"#008577"};
    return{play:pl,numbers:pl.numbers,match,prize,amount,index,eligibility};
  }).sort((a,b)=>{
    const aw=a.match>=2,bw=b.match>=2;
    if(aw!==bw)return aw?-1:1;
    if(b.match!==a.match)return b.match-a.match;
    return a.index-b.index;
  });
  const countedRows=rows.filter(r=>r.eligibility?.countsPublic);
  const bestMatch=countedRows.length?Math.max(0,...countedRows.map(r=>r.match)):0;
  return{
    rows,
    countedRows,
    totalPlays:materialized.length,
    trackedPlays:countedRows.length,
    whatIfPlays:rows.length-countedRows.length,
    bestMatch,
    m3:countedRows.filter(r=>r.match===3).length,
    m4:countedRows.filter(r=>r.match===4).length,
    m5:countedRows.filter(r=>r.match===5).length,
    freeTickets:countedRows.filter(r=>r.match===2).length,
    estimatedWinnings:countedRows.reduce((sum,r)=>sum+r.amount,0),
    winners:countedRows.filter(r=>r.match>=2),
    whatIfWinners:rows.filter(r=>!r.eligibility?.countsPublic&&r.match>=2),
  };
}

function ResultsSummary({draw,plays,lineup,strategyTrust,onViewPlays}) {
  const summary=summarizeResults(draw,plays,{lineup,strategyTrust});
  const jackpot=jackpotAmountText(draw);
  const drawDate=draw?.date||draw?.draw_date||"Latest draw";
  const hasMajor=summary.m5>0||summary.m4>0||summary.estimatedWinnings>0;
  const hasThree=summary.m3>0;
  const hasFree=summary.freeTickets>0;
  const celebration=hasMajor
    ?{icon:"\uD83E\uDD73",title:"Congratulations!",text:"Your saved plays produced simulated winnings this draw.",bg:"#fff4cc",color:"#8B4513"}
    :hasThree
      ?{icon:"\uD83C\uDF89",title:"Nice!",text:"You matched 3 numbers!",bg:"#eef8f6",color:"#008577"}
      :hasFree
        ?{icon:"\uD83C\uDF9F",title:"You earned a Free Ticket!",text:"One of your saved plays matched 2 numbers in this simulation.",bg:"#f5f5f5",color:"#002855"}
        :null;
  const sharePayload=shareCardPayload({
    type:"results",
    title:"LottoTracker Results",
    strategyName:"Results Summary",
    game:gameLabel(draw?.game||"fantasy5"),
    status:"Results Ready",
    roi:summary.estimatedWinnings?fmtMoney(summary.estimatedWinnings):"Best "+summary.bestMatch+" Match",
    drawsTracked:"1",
    createdDate:drawDate,
    link:shortShareLink("results",draw?.id||drawDate,true),
    isPublic:true,
    accent:"#D4AF37",
    note:"Winning numbers checked against saved plays. Simulation only.",
  });
  return(
    <div style={{background:"#fff",borderRadius:16,padding:18,boxShadow:"0 14px 30px rgba(0,0,0,.18)",width:"100%"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:14}}>
        <div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:"#002855",textTransform:"uppercase",lineHeight:1}}>{"\uD83C\uDF89 Fantasy 5 Results"}</div>
          <div style={{fontSize:12,color:"#555",marginTop:5}}>Draw Date: <strong>{drawDate}</strong></div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:10,color:"#555",fontWeight:700,textTransform:"uppercase",letterSpacing:1}}>Jackpot Amount</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:900,color:"#c8860a"}}>{jackpot}</div>
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
        <span style={{fontSize:11,color:"#555",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginRight:2}}>Winning Numbers</span>
        {(draw?.numbers||[]).map((n,i)=><Ball key={i} n={n}/>)}
      </div>

      {celebration&&(
        <div style={{background:celebration.bg,border:"1px solid #e8dcc0",borderRadius:12,padding:"12px 14px",display:"flex",gap:12,alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:34,lineHeight:1}}>{celebration.icon}</div>
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:celebration.color,textTransform:"uppercase",lineHeight:1}}>{celebration.title}</div>
            <div style={{fontSize:13,color:"#555",fontWeight:600}}>{celebration.text}</div>
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(118px,1fr))",gap:10,marginBottom:16}}>
        {[
          ["Total Plays Checked",summary.trackedPlays,"#002855"],
          ["Best Match",summary.bestMatch,"#008577"],
          ["3 Matches",summary.m3,"#2E6DA4"],
          ["4 Matches",summary.m4,"#8B4513"],
          ["Jackpots",summary.m5,"#D4AF37"],
          ["Free Tickets",summary.freeTickets,"#555"],
          ["Estimated Winnings",fmtMoney(summary.estimatedWinnings),"#2E7D32"],
        ].map(([label,value,color])=>(
          <div key={label} style={{background:"#f8f8f8",borderRadius:10,padding:"10px 11px"}}>
            <div style={{fontSize:10,color:"#555",fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>{label}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:25,fontWeight:900,color}}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
        <button type="button" className="btn btn-p" onClick={onViewPlays}>View My Plays</button>
        <ShareCardButton payload={sharePayload} label="Share Results" className="btn btn-s"/>
      </div>

      {summary.winners.length===0?(
        <div style={{background:"#f8f8f8",borderRadius:12,padding:16}}>
          <div style={{fontWeight:800,color:"#002855",marginBottom:5}}>No winning plays this draw.</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:12}}>{summary.whatIfWinners.length?"Some saved plays would have matched, but they were saved in My Playslips or saved after sales close. They are labeled What-if only.":"Keep going. Try another playslip or review What-If from My Playslips."}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button type="button" className="btn btn-sm btn-p" onClick={onViewPlays}>Try another strategy</button>
          </div>
        </div>
      ):(
        <div>
          <div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Recent Winning Plays</div>
          <div style={{display:"grid",gap:7,maxHeight:240,overflowY:"auto",paddingRight:2}}>
            {summary.rows.slice(0,8).map((r,i)=>(
              <div key={(r.play.id||i)+"-"+r.match} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:8,alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f0f0f0"}}>
                <span style={{fontSize:11,color:"#888",fontWeight:700}}>#{i+1}</span>
                <div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{r.numbers.map((n,j)=><Ball key={j} n={n} size="sm" matched={(draw?.numbers||[]).includes(n)}/>)}</div>
                  {!r.eligibility?.countsPublic&&<div style={{fontSize:11,color:r.eligibility?.tone||"#2E6DA4",fontWeight:800,textTransform:"uppercase",marginTop:4}}>{r.eligibility?.label||"What-if only"}</div>}
                </div>
                <span style={{background:r.match>=2?(r.prize?.color||"#008577")+"22":"#f0f0f0",color:r.match>=2?(r.prize?.color||"#008577"):"#777",borderRadius:9,padding:"3px 8px",fontSize:11,fontWeight:800,whiteSpace:"nowrap"}}>
                  {r.match} Match{r.prize?" · "+r.prize.display:""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MatchPanel({draw,plays,lineup,strategyTrust}){
  // Recipe rows (numbers=null) don't render individually — they're anchor
  // sets, scored via expansion in aggregateStrategies. Keep this view tight
  // on materialized plays only.
  const results=plays
    .filter(p=>rowGame(p)===rowGame(draw)&&Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count)
    .map(pl=>({play:pl.numbers,m:cntMatch(pl.numbers,draw.numbers),eligibility:lineup?classifyPlayForDraw(pl,draw,lineup,strategyTrust):{countsPublic:true,label:"Tracked"}}))
    .sort((a,b)=>b.m-a.m);
  return(
    <div className="match-panel">
      <div className="match-title">{draw.date} {draw.isEvening?"Evening":"Midday"} — Simulated Results</div>
      <div style={{display:"flex",gap:8,marginBottom:14}}>{draw.numbers.map((n,i)=><Ball key={i} n={n}/>)}</div>
      {results.map((r,i)=>{
        const pr=getDrawPrize(draw,r.m);
        return(
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,.08)"}}>
            <span style={{color:"rgba(255,255,255,.4)",fontSize:12,width:22}}>#{i+1}</span>
            <div style={{display:"flex",gap:3}}>{r.play.map((n,j)=><Ball key={j} n={n} size="sm" matched={draw.numbers.includes(n)}/>)}</div>
            {pr
              ?<span style={{background:r.eligibility?.countsPublic?pr.color:"#5d6675",color:"#fff",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:700,marginLeft:"auto"}}>{r.m} Match &middot; {r.eligibility?.countsPublic?pr.display:(r.eligibility?.label||"What-if only")}</span>
              :<span style={{color:"rgba(255,255,255,.3)",fontSize:11,marginLeft:"auto"}}>{r.m} match</span>
            }
          </div>
        );
      })}
    </div>
  );
}

function ResultsCenterTab({draws,plays,subscription,onUpgrade,billingBusy,drawStatus,strategyTrust,todaysLineup,performanceSummaries=[],performanceLoading=false,isAdmin=false,onCreatePublicShare,onCreatePublicResultShare}) {
  const navigate=useNavigate();
  const[searchParams]=useSearchParams();
  const defaultResultsGame=(plays||[]).find(p=>p?.game)?.game||"fantasy5";
  const initialResultsGame=normalizeResultsGame(searchParams.get("game"))||defaultResultsGame;
  const initialResultMode=normalizeTrackingMode(searchParams.get("mode")||searchParams.get("results")||"watch");
  const initialPeriod=normalizeResultsPeriod(searchParams.get("period"))||"30";
  const initialDrawFilter=normalizeResultsDrawFilter(searchParams.get("draw")||searchParams.get("draw_time"));
  const initialStrategy=searchParams.get("strategy")||"all";
  const[selectedGame,setSelectedGame]=useState(initialResultsGame);
  const[resultMode,setResultMode]=useState(initialResultMode);
  const[period,setPeriod]=useState(initialPeriod);
  const[drawFilter,setDrawFilter]=useState(initialDrawFilter);
  const[selectedStrategy,setSelectedStrategy]=useState(initialStrategy);
  const[sortBy,setSortBy]=useState("roi");
  const[latestRows,setLatestRows]=useState([]);
  const[latestLoading,setLatestLoading]=useState(false);
  const[latestError,setLatestError]=useState("");
  const[detailStrategy,setDetailStrategy]=useState(null);
  const[detailRows,setDetailRows]=useState([]);
  const[detailLoading,setDetailLoading]=useState(false);
  const[detailError,setDetailError]=useState("");
  const[watchDraws,setWatchDraws]=useState([]);
  const[watchDrawsKey,setWatchDrawsKey]=useState("");
  const[watchLoading,setWatchLoading]=useState(false);
  const[watchError,setWatchError]=useState("");
  const[liveDrawCounts,setLiveDrawCounts]=useState({both:0,midday:0,evening:0});
  const[expandedDraw,setExpandedDraw]=useState("");
  const[infoPanel,setInfoPanel]=useState(null);
  const detailDrawerRef=useRef(null);
  const periodKey=summaryPeriodForWindow(period);
  const watchRequestKey=[selectedGame,period].join("|");
  const selectedGameConfig=gameNumberConfig(selectedGame);
  const isWatchMode=resultMode==="watch";
  const selectedGameStatusEntries=(drawStatus?.entries||[]).filter(entry=>entry.game===selectedGame);
  const pendingEntries=selectedGameStatusEntries.filter(entry=>["Sales Closed","Drawing In Progress","Waiting for official results","Official results delayed"].includes(entry.status));
  const completedDraws=(draws||[]).filter(d=>rowGame(d)===selectedGame&&Array.isArray(d.numbers)&&d.numbers.length===selectedGameConfig.count);
  const strategyRecordMap=strategyTrust?.records||{};
  useEffect(()=>{
    const nextGame=normalizeResultsGame(searchParams.get("game"));
    const nextMode=searchParams.has("mode")||searchParams.has("results")
      ?normalizeTrackingMode(searchParams.get("mode")||searchParams.get("results"))
      :null;
    const nextPeriod=normalizeResultsPeriod(searchParams.get("period"));
    const nextDraw=searchParams.has("draw")||searchParams.has("draw_time")
      ?normalizeResultsDrawFilter(searchParams.get("draw")||searchParams.get("draw_time"))
      :null;
    const nextStrategy=searchParams.get("strategy");
    if(nextGame&&nextGame!==selectedGame)setSelectedGame(nextGame);
    if(nextMode&&nextMode!==resultMode)setResultMode(nextMode);
    if(nextPeriod&&nextPeriod!==period)setPeriod(nextPeriod);
    if(nextDraw&&nextDraw!==drawFilter)setDrawFilter(nextDraw);
    if(nextStrategy&&nextStrategy!==selectedStrategy)setSelectedStrategy(nextStrategy);
  },[searchParams.toString()]);
  function closeDetailDrawer(){
    setDetailStrategy(null);
    setDetailRows([]);
  }
  useEffect(()=>{
    if(!detailStrategy)return undefined;
    function onKeyDown(event){
      if(event.key==="Escape")closeDetailDrawer();
    }
    window.addEventListener("keydown",onKeyDown);
    return()=>window.removeEventListener("keydown",onKeyDown);
  },[detailStrategy]);
  const durableStrategyOptions=Object.keys(strategyRecordMap)
    .filter(id=>id&&id!=="manual")
    .map(id=>strategyBuilderRecord(id,strategyRecordMap[id]))
    .filter(record=>record.sourceOfTruth==="strategy_versions"&&strategySnapshotNumbers(record).length>0);
  const strategyIds=isWatchMode
    ?durableStrategyOptions.map(record=>record.id)
    :[...new Set([
      ...Object.keys(strategyRecordMap),
      ...strategyIdsFromPlays(plays),
      ...(performanceSummaries||[]).filter(r=>r.game===selectedGame&&normalizeTrackingMode(r.tracking_mode)===resultMode).map(r=>r.strategy_id),
    ])].filter(id=>id&&id!=="manual");
  const strategyOptions=(isWatchMode?durableStrategyOptions:strategyIds.map(id=>strategyBuilderRecord(id,strategyRecordMap[id])))
    .filter(record=>(record.game||"fantasy5")===selectedGame||(!isWatchMode&&(performanceSummaries||[]).some(row=>row.strategy_id===record.id&&row.game===selectedGame&&normalizeTrackingMode(row.tracking_mode)===resultMode)))
    .sort((a,b)=>strategyDisplayName(a.id,a).localeCompare(strategyDisplayName(b.id,b)));
  const allStrategiesLabel="All "+gameLabel(selectedGame)+" Strategies";
  async function enrichLiveResultRows(rows=[],bounds={}){
    const baseRows=Array.isArray(rows)?rows:[];
    if(!baseRows.length)return[];
    const scheduledIds=[...new Set(baseRows.map(row=>row.scheduled_draw_id).filter(Boolean))];
    const scheduleMap=new Map();
    const versionMap=new Map();
    const officialMapById=new Map();
    const officialMapByKey=new Map();
    if(scheduledIds.length){
      const{data,error}=await supabase
        .from("scheduled_strategy_draws")
        .select("id,strategy_id,game,draw_date,draw_time,strategy_version,strategy_name,play_type,tracking_mode,scheduled_at,cutoff_at,locked_at,completed_at,official_draw_id,official_draw_key,official_result_at,frozen_snapshot")
        .in("id",scheduledIds);
      if(error)throw error;
      (data||[]).forEach(row=>scheduleMap.set(row.id,row));
    }
    const versionStrategies=[...new Set(baseRows.map(row=>row.strategy_id).filter(Boolean))];
    if(versionStrategies.length){
      const{data,error}=await supabase
        .from("strategy_versions")
        .select("strategy_id,version_number,created_at")
        .in("strategy_id",versionStrategies);
      if(!error){
        (data||[]).forEach(row=>versionMap.set([row.strategy_id,Number(row.version_number)||1].join(":"),row.created_at));
      }
    }
    let drawQuery=supabase
      .from("draws")
      .select("id,game,draw_date,draw_time,numbers,fireball_digit,source,official_result_status,official_result_at,official_checked_at,fetched_at,official_payload_source,official_provider_game_id,official_provider_result_id")
      .eq("game",selectedGame)
      .order("draw_date",{ascending:false})
      .order("draw_time",{ascending:false})
      .limit(200);
    if(bounds.from)drawQuery=drawQuery.gte("draw_date",bounds.from);
    if(bounds.to)drawQuery=drawQuery.lte("draw_date",bounds.to);
    const{data:drawRows,error:drawError}=await drawQuery;
    if(drawError)throw drawError;
    (drawRows||[]).forEach(draw=>{
      if(draw?.id)officialMapById.set(String(draw.id),draw);
      officialMapByKey.set(resultOfficialDrawKey(draw.game,draw.draw_date,draw.draw_time),draw);
    });
    return baseRows.map(row=>{
      const scheduled=scheduleMap.get(row.scheduled_draw_id)||{};
      const versionNumber=Number(scheduled.strategy_version||row.strategy_version)||Number(scheduled.frozen_snapshot?.version)||1;
      const rowGameId=row.game||scheduled.game||selectedGame;
      const rowDrawDate=row.draw_date||scheduled.draw_date;
      const rowDrawTime=row.draw_time||scheduled.draw_time;
      const expectedKey=resultOfficialDrawKey(rowGameId,rowDrawDate,rowDrawTime);
      const storedKey=String(row.official_draw_key||scheduled.official_draw_key||"").toLowerCase();
      const storedOfficialId=String(row.official_draw_id||scheduled.official_draw_id||"");
      const candidate=(storedOfficialId&&officialMapById.get(storedOfficialId))
        ||(storedKey===expectedKey&&officialMapByKey.get(expectedKey))
        ||(!storedOfficialId&&!storedKey&&officialMapByKey.get(expectedKey))
        ||null;
      const official=officialDrawMatchesResult({...row,game:rowGameId,draw_date:rowDrawDate,draw_time:rowDrawTime},candidate)?candidate:{};
      return{
        ...row,
        frozen_snapshot:scheduled.frozen_snapshot||row.frozen_snapshot||null,
        strategy_version:versionNumber||null,
        strategy_version_activated_at:versionMap.get([row.strategy_id||scheduled.strategy_id,versionNumber].join(":"))||snapshotActivationTimestamp(scheduled.frozen_snapshot||row.frozen_snapshot||{})||null,
        scheduled_at:scheduled.scheduled_at||row.scheduled_at||null,
        cutoff_at:scheduled.cutoff_at||row.cutoff_at||null,
        locked_at:scheduled.locked_at||row.locked_at||null,
        completed_at:scheduled.completed_at||row.completed_at||null,
        official_draw_id:official.id?String(official.id):null,
        official_draw_key:official.id?expectedKey:null,
        official_result_at:official.id?(official.official_result_at||scheduled.official_result_at||row.official_result_at||null):null,
        official_checked_at:official.id?(official.official_checked_at||row.official_checked_at||null):null,
        official_fetched_at:official.id?(official.fetched_at||row.official_fetched_at||null):null,
        official_source:official.id?officialSourceLabel(official):"Official result pending",
        official_numbers:Array.isArray(official.numbers)?official.numbers:row.official_numbers,
        official_fireball_digit:official.fireball_digit??row.official_fireball_digit,
      };
    });
  }
  async function prepareResultShare(row){
    if(!resultShareEligible(row)){
      const reason=resultShareIneligibleReason(row);
      setDetailError(reason==="official result pending"?"Official result pending. This completed result cannot be shared until the exact official draw is imported.":"This result cannot be shared yet: "+reason+".");
      return{cancel:true};
    }
    const payload=resultCardSharePayload(row,selectedGameConfig);
    if(row?.scheduled_draw_id&&typeof onCreatePublicResultShare==="function"){
      try{
        const data=await onCreatePublicResultShare(row.scheduled_draw_id,row.id);
        if(data?.snapshot){
          const snapshotPayload={...data.snapshot,shareId:data.shareId,link:data.link};
          return{payload:publicResultSnapshotToCardPayload(snapshotPayload)};
        }
      }catch(e){
        setDetailError(e?.message||"Could not prepare this result share.");
        return{cancel:true};
      }
    }
    const strategyId=row?.strategy_id;
    if(!strategyId||typeof onCreatePublicShare!=="function")return{payload};
    try{
      const data=await onCreatePublicShare(strategyId,{strategyName:payload.strategyName});
      if(data?.link)return{payload:{...payload,link:data.link,isPublic:true}};
    }catch{}
    return{payload};
  }
  useEffect(()=>{
    if(!selectedGame&&defaultResultsGame)setSelectedGame(defaultResultsGame);
  },[defaultResultsGame,selectedGame]);
  useEffect(()=>{
    if(selectedStrategy!=="all"&&strategyOptions.length>0&&!strategyOptions.some(record=>record.id===selectedStrategy))setSelectedStrategy("all");
  },[selectedGame,selectedStrategy,strategyOptions.map(r=>r.id).join("|")]);
  useEffect(()=>{
    if(!isWatchMode)return;
    let cancelled=false;
    const requestKey=[selectedGame,period].join("|");
    async function loadWatchDraws(){
      setWatchLoading(true);
      setWatchError("");
      try{
        const bounds=resultsWindowBounds(period);
        const qs=new URLSearchParams({limit:String(FULL_DRAW_LIMIT),game:selectedGame});
        if(bounds.from)qs.set("date_from",bounds.from);
        if(bounds.to)qs.set("date_to",bounds.to);
        const r=await monitoredFetch("/api/draws?"+qs.toString(),{cache:"no-store"},"watch-draws");
        if(!r.ok)throw new Error("Official draw history is unavailable right now.");
        const data=await r.json();
        const rows=(Array.isArray(data)?data:[])
          .filter(row=>rowGame(row)===selectedGame)
          .filter(row=>isOfficialDraw(row))
          .filter(row=>drawHasExpectedNumbers(row));
        if(!cancelled){
          setWatchDraws(rows);
          setWatchDrawsKey(requestKey);
        }
      }catch(e){
        if(!cancelled){
          setWatchDraws([]);
          setWatchDrawsKey(requestKey);
          setWatchError(e?.message||"Watch Results are unavailable right now.");
        }
      }finally{
        if(!cancelled)setWatchLoading(false);
      }
    }
    loadWatchDraws();
    return()=>{cancelled=true;};
  },[isWatchMode,selectedGame,period]);
  useEffect(()=>{
    if(isWatchMode){
      setLiveDrawCounts({both:0,midday:0,evening:0});
      return;
    }
    let cancelled=false;
    async function loadLiveDrawCounts(){
      try{
        const bounds=resultsWindowBounds(period);
        let query=supabase
          .from("strategy_draw_results")
          .select("draw_date,draw_time")
          .eq("game",selectedGame)
          .eq("tracking_mode",resultMode)
          .limit(2000);
        if(selectedStrategy!=="all")query=query.eq("strategy_id",selectedStrategy);
        if(bounds.from)query=query.gte("draw_date",bounds.from);
        if(bounds.to)query=query.lte("draw_date",bounds.to);
        const{data,error}=await query;
        if(error)throw error;
        const rows=Array.isArray(data)?data:[];
        const counts={
          both:new Set(rows.map(row=>[row.draw_date,row.draw_time].join(":"))).size,
          midday:new Set(rows.filter(row=>row.draw_time==="midday").map(row=>[row.draw_date,row.draw_time].join(":"))).size,
          evening:new Set(rows.filter(row=>row.draw_time==="evening").map(row=>[row.draw_date,row.draw_time].join(":"))).size,
        };
        if(!cancelled)setLiveDrawCounts(counts);
      }catch(e){
        if(!cancelled)setLiveDrawCounts({both:0,midday:0,evening:0});
      }
    }
    loadLiveDrawCounts();
    return()=>{cancelled=true;};
  },[isWatchMode,selectedGame,resultMode,period,selectedStrategy]);
  useEffect(()=>{
    if(isWatchMode){
      setLatestRows([]);
      setLatestLoading(false);
      setLatestError("");
      return;
    }
    let cancelled=false;
    async function loadLatest(){
      setLatestLoading(true);
      setLatestError("");
      try{
        const bounds=resultsWindowBounds(period);
        let query=supabase
          .from("strategy_draw_results")
          .select("id,user_id,strategy_id,scheduled_draw_id,official_draw_id,official_draw_key,game,draw_date,draw_time,strategy_name,play_type,tracking_mode,play_count,total_cost,cash_won,net,roi,best_match,free_tickets,largest_prize,winning_plays_count,match_counts,prize_breakdown,result_source,calculated_at")
          .eq("game",selectedGame)
          .eq("tracking_mode",resultMode)
          .order("draw_date",{ascending:false})
          .order("draw_time",{ascending:false})
          .limit(50);
        if(selectedStrategy!=="all")query=query.eq("strategy_id",selectedStrategy);
        if(drawFilter!=="both")query=query.eq("draw_time",drawFilter);
        if(bounds.from)query=query.gte("draw_date",bounds.from);
        if(bounds.to)query=query.lte("draw_date",bounds.to);
        const{data,error}=await query;
        if(error)throw error;
        const enriched=await enrichLiveResultRows(data||[],bounds);
        if(!cancelled)setLatestRows(enriched);
      }catch(e){
        if(!cancelled){
          setLatestRows([]);
          setLatestError(e?.message||"Latest results are unavailable right now.");
        }
      }finally{
        if(!cancelled)setLatestLoading(false);
      }
    }
    loadLatest();
    return()=>{cancelled=true;};
  },[selectedGame,selectedStrategy,resultMode,isWatchMode,period,drawFilter]);
  useEffect(()=>{
    if(!detailStrategy)return;
    if(isWatchMode){
      setDetailRows([]);
      setDetailLoading(false);
      setDetailError("");
      setExpandedDraw("");
      return;
    }
    let cancelled=false;
    async function loadDetail(){
      setDetailLoading(true);
      setDetailError("");
      setExpandedDraw("");
      try{
        const bounds=resultsWindowBounds(period);
        let query=supabase
          .from("strategy_draw_results")
          .select("id,strategy_id,scheduled_draw_id,official_draw_id,official_draw_key,game,draw_date,draw_time,strategy_name,play_type,tracking_mode,play_count,total_cost,cash_won,net,roi,best_match,free_tickets,largest_prize,winning_plays_count,match_counts,prize_breakdown,result_source,calculated_at")
          .eq("strategy_id",detailStrategy)
          .eq("game",selectedGame)
          .eq("tracking_mode",resultMode)
          .order("draw_date",{ascending:false})
          .order("draw_time",{ascending:false})
          .limit(30);
        if(drawFilter!=="both")query=query.eq("draw_time",drawFilter);
        if(bounds.from)query=query.gte("draw_date",bounds.from);
        if(bounds.to)query=query.lte("draw_date",bounds.to);
        const{data,error}=await query;
        if(error)throw error;
        const enriched=await enrichLiveResultRows(data||[],bounds);
        if(!cancelled)setDetailRows(enriched);
      }catch(e){
        if(!cancelled){
          setDetailRows([]);
          setDetailError(e?.message||"Strategy details are unavailable right now.");
        }
      }finally{
        if(!cancelled)setDetailLoading(false);
      }
    }
    loadDetail();
    return()=>{cancelled=true;};
  },[detailStrategy,selectedGame,drawFilter,resultMode,isWatchMode,period]);
  const activeWatchDraws=watchDrawsKey===watchRequestKey?watchDraws:[];
  const watchSelectedDraws=watchEligibleOfficialDraws(activeWatchDraws,selectedGame,period,drawFilter);
  const watchMiddayDraws=watchEligibleOfficialDraws(activeWatchDraws,selectedGame,period,"midday");
  const watchEveningDraws=watchEligibleOfficialDraws(activeWatchDraws,selectedGame,period,"evening");
  const watchRankingRows=buildWatchStrategyRows(strategyOptions,activeWatchDraws,{windowId:period,drawFilter})
    .filter(item=>selectedStrategy==="all"||item.id===selectedStrategy);
  const watchMiddayRows=watchRankingRows.map(item=>item.midday);
  const watchEveningRows=watchRankingRows.map(item=>item.evening);
  const liveSelectedRows=completedSummaryRowsForSelection(performanceSummaries,selectedGame,periodKey,drawFilter,selectedStrategy,resultMode);
  const selectedRows=isWatchMode?watchRankingRows.map(item=>({
    ...item.row,
    strategy_id:item.id,
    game:item.game,
    tracking_mode:"watch",
  })):liveSelectedRows;
  const selectedWatchDrawCount=selectedStrategy==="all"
    ?Math.max(0,...watchRankingRows.map(item=>summaryNumber(item.row?.draws_tracked)))
    :summaryNumber(watchRankingRows[0]?.row?.draws_tracked);
  const selectedDrawCount=isWatchMode?selectedWatchDrawCount:liveDrawCounts[drawFilter]||0;
  const totals=isWatchMode
    ?withDrawsTracked(summarizeWatchResultRows(selectedRows),selectedDrawCount)
    :withDrawsTracked(summarizePerformanceRows(selectedRows),selectedDrawCount);
  const rankingIds=isWatchMode?[]:[...new Set([
    ...strategyOptions.map(r=>r.id),
    ...performanceSummaryRowsFor(performanceSummaries,{game:selectedGame,periodKey,drawFilter,trackingMode:resultMode}).map(r=>r.strategy_id),
  ])].filter(Boolean);
  const liveRankingRows=isWatchMode?[]:rankingIds.map(id=>strategyLiveRowFromSummaries(id,strategyRecordMap,performanceSummaries,periodKey,drawFilter,resultMode))
    .filter(item=>selectedStrategy==="all"||item.id===selectedStrategy)
    .sort((a,b)=>{
      const aHas=summaryNumber(a.row?.draws_tracked)>0||summaryNumber(a.row?.total_cost)>0;
      const bHas=summaryNumber(b.row?.draws_tracked)>0||summaryNumber(b.row?.total_cost)>0;
      if(aHas!==bHas)return bHas-aHas;
      const fieldMap={roi:"roi",cash:"cash_won",net:"net",largest:"largest_prize",best:"best_match",draws:"draws_tracked"};
      const field=fieldMap[sortBy]||"roi";
      const av=field==="roi"?(Number.isFinite(Number(a.row?.roi))?Number(a.row?.roi):-Infinity):summaryNumber(a.row?.[field]);
      const bv=field==="roi"?(Number.isFinite(Number(b.row?.roi))?Number(b.row?.roi):-Infinity):summaryNumber(b.row?.[field]);
      return bv-av;
    });
  const rankingRows=(isWatchMode?watchRankingRows:liveRankingRows).sort((a,b)=>{
    const fieldMap={roi:"roi",cash:"cash_won",net:"net",largest:"largest_prize",best:"best_match",draws:"draws_tracked"};
    const field=fieldMap[sortBy]||"roi";
    const av=field==="roi"?(Number.isFinite(Number(a.row?.roi))?Number(a.row?.roi):-Infinity):summaryNumber(a.row?.[field]);
    const bv=field==="roi"?(Number.isFinite(Number(b.row?.roi))?Number(b.row?.roi):-Infinity):summaryNumber(b.row?.[field]);
    return bv-av;
  });
  const watchResultRows=watchRankingRows.flatMap(item=>item.rows||[]);
  const watchInsights=watchInsightSummary(watchResultRows);
  const singleStrategySelected=selectedStrategy!=="all"||rankingRows.length<=1;
  const displayLatestRows=isWatchMode?watchResultRows:latestRows;
  const latestOfficialPool=isWatchMode?watchSelectedDraws:completedDraws;
  const latestOfficialDraw=isWatchMode
    ?latestOfficialDrawForSelection(watchSelectedDraws,selectedGame,drawFilter)
    :(displayLatestRows[0]?latestOfficialPool.find(d=>d.draw_date===displayLatestRows[0].draw_date&&d.draw_time===displayLatestRows[0].draw_time):null);
  const latestKey=latestOfficialDraw
    ?[latestOfficialDraw.draw_date,latestOfficialDraw.draw_time].join(":")
    :(displayLatestRows[0]?[displayLatestRows[0].draw_date,displayLatestRows[0].draw_time].join(":"):"");
  const latestDrawRows=latestKey?displayLatestRows.filter(row=>[row.draw_date,row.draw_time].join(":")===latestKey):[];
  const bestWinningRow=bestWinningDrawRow(displayLatestRows);
  const bestWinningOfficialDraw=officialDrawForResultRow(bestWinningRow,latestOfficialPool);
  const latestSummary=resultRowsSummary(latestDrawRows);
  const latestStatus=isWatchMode
    ?(watchLoading?"Processing Results":watchError?"Processing Results":latestOfficialDraw?"Your Results Are Ready":"No completed draws in range")
    :(latestLoading?"Processing Results":latestError?"Processing Results":latestResultStatus({latestRows:latestDrawRows,pendingEntries}));
  const prizeDataPending=latestOfficialDraw&&isOfficialDraw(latestOfficialDraw)&&latestDrawRows.length&&latestDrawRows.some(row=>row.cash_won==null||row.total_cost==null);
  const statusLabel=prizeDataPending?"Prize Data Pending":latestStatus;
  const refreshRow=totals.latest||selectedRows.sort((a,b)=>String(b.refreshed_at||"").localeCompare(String(a.refreshed_at||"")))[0]||null;
  const latestRefresh=isWatchMode
    ?"Calculated on demand from active strategy snapshots"
    :(refreshRow?.refreshed_at?new Date(refreshRow.refreshed_at).toLocaleString():"Not generated yet");
  const summaryMissing=!isWatchMode&&!performanceLoading&&!selectedRows.length&&strategyOptions.length>0;
  function MetricCard({label,value,sub,tone="#002855"}){
    const valueText=typeof value==="string"?value:String(value??"");
    const valueSize=valueText.includes("$")||valueText.length>6?27:31;
    return(
      <div style={{background:"#fff",border:"1px solid #e8eee9",borderRadius:12,padding:"14px 15px",boxShadow:"0 10px 24px rgba(0,40,85,.06)"}}>
        <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.7,marginBottom:5}}>{label}</div>
        <strong style={{display:"block",fontFamily:"'Barlow Condensed',sans-serif",fontSize:valueSize,lineHeight:1,color:tone,overflowWrap:"normal",wordBreak:"normal"}}>{value}</strong>
        {sub&&<div style={{fontSize:12,color:"#666",lineHeight:1.35,marginTop:3}}>{sub}</div>}
      </div>
    );
  }
  function MiniBar({label,value,max,color="#008577"}){
    return(
      <div>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:12,fontWeight:800,color:"#002855",marginBottom:5}}>
          <span>{label}</span><span>{value}</span>
        </div>
        <div style={{height:9,background:"#edf1f0",borderRadius:999,overflow:"hidden"}}>
          <div style={{width:simpleBarPercent(value,max)+"%",height:"100%",background:color,borderRadius:999}}/>
        </div>
      </div>
    );
  }
  function EmptyResultsPanel({title,text,action}){
    return(
      <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:12,padding:18}}>
        <div style={{fontWeight:900,color:"#002855",fontSize:18,marginBottom:5}}>{title}</div>
        <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:action?12:0}}>{text}</div>
        {action&&<button type="button" className="btn btn-sm btn-p" onClick={action.onClick}>{action.label}</button>}
      </div>
    );
  }
  const maxNet=Math.max(1,...rankingRows.map(item=>Math.abs(summaryNumber(item.row?.net))));
  const totalMidday=isWatchMode
    ?withDrawsTracked(summarizeWatchResultRows(watchMiddayRows),Math.max(0,...watchRankingRows.map(item=>summaryNumber(item.midday?.draws_tracked))))
    :withDrawsTracked(summarizePerformanceRows(performanceSummaryRowsFor(performanceSummaries,{game:selectedGame,periodKey,drawFilter:"midday",trackingMode:resultMode}).filter(row=>selectedStrategy==="all"||row.strategy_id===selectedStrategy)),liveDrawCounts.midday||0);
  const totalEvening=isWatchMode
    ?withDrawsTracked(summarizeWatchResultRows(watchEveningRows),Math.max(0,...watchRankingRows.map(item=>summaryNumber(item.evening?.draws_tracked))))
    :withDrawsTracked(summarizePerformanceRows(performanceSummaryRowsFor(performanceSummaries,{game:selectedGame,periodKey,drawFilter:"evening",trackingMode:resultMode}).filter(row=>selectedStrategy==="all"||row.strategy_id===selectedStrategy)),liveDrawCounts.evening||0);
  const middayEveningSame=summaryNumber(totalMidday.cash_won)===summaryNumber(totalEvening.cash_won)
    &&summaryNumber(totalMidday.total_cost)===summaryNumber(totalEvening.total_cost)
    &&summaryNumber(totalMidday.draws_tracked)===summaryNumber(totalEvening.draws_tracked);
  const detailItem=detailStrategy?rankingRows.find(item=>item.id===detailStrategy)||strategyLiveRowFromSummaries(detailStrategy,strategyRecordMap,performanceSummaries,periodKey,drawFilter,resultMode):null;
  const displayDetailRows=isWatchMode?(detailItem?.rows||[]):detailRows;
  const detailTotals=detailItem?.row
    ?(isWatchMode
      ?detailItem.row
      :withDrawsTracked(summarizePerformanceRows([detailItem.row]),new Set(displayDetailRows.map(row=>[row.draw_date,row.draw_time].join(":"))).size||summaryNumber(detailItem.row.draws_tracked)))
    :(isWatchMode?summarizeWatchResultRows([]):summarizePerformanceRows([]));
  const displayDetailLoading=isWatchMode?watchLoading:detailLoading;
  const displayDetailError=isWatchMode?watchError:detailError;
  const detailRecentSummary=resultRowsSummary(displayDetailRows);
  const pickDetailBreakdown=(isWatchMode&&["pick2","pick3","pick4","pick5"].includes(selectedGame))?summarizePick4WinningDetails(displayDetailRows):null;
  const fantasy5DetailBreakdown=(isWatchMode&&selectedGame==="fantasy5")?summarizeFantasy5WinningDetails(displayDetailRows):null;
  const selectedLatestOfficialDraw=latestOfficialDrawForSelection(completedDraws,selectedGame,drawFilter);
  const latestTrackedLiveRow=!isWatchMode?[...(latestRows||[])].sort(compareOfficialDrawRecency)[0]||null:null;
  const latestTrackedLiveKey=latestTrackedLiveRow?[latestTrackedLiveRow.draw_date,latestTrackedLiveRow.draw_time].join(":"):"";
  const selectedLatestOfficialKey=selectedLatestOfficialDraw?[selectedLatestOfficialDraw.draw_date,selectedLatestOfficialDraw.draw_time].join(":"):"";
  const latestOfficialIsUntrackedLive=!isWatchMode&&selectedLatestOfficialDraw&&selectedLatestOfficialKey&&selectedLatestOfficialKey!==latestTrackedLiveKey;
  const latestTrackedLiveLabel=latestTrackedLiveRow
    ?fmtShortDate(latestTrackedLiveRow.draw_date)+" "+drawTimeLabel(latestTrackedLiveRow)
    :"No tracked Live draw yet";
  const selectedStrategyRecord=selectedStrategy==="all"?null:(strategyOptions.find(record=>record.id===selectedStrategy)||strategyRecordMap[selectedStrategy]||null);
  const performanceSubject=selectedStrategy==="all"?allStrategiesLabel:strategyDisplayName(selectedStrategy,selectedStrategyRecord);
  const performanceTitle=isWatchMode
    ?performanceSubject+" Performance"
    :"Live Plays: "+performanceSubject;
  const modeDescription=isWatchMode
    ?"Strategy Performance uses official historical draws to show how the saved strategy would have performed."
    :"Live Plays uses only strategies you actually marked as played before the draw.";
  const periodLabel=RESULTS_HISTORY_WINDOWS.find(option=>option.id===period)?.label||"Selected period";
  const drawWindowLabel=drawFilter==="both"?"All Draws":drawFilter==="evening"?"Evening":"Midday";
  const performanceSharePath=strategyPerformancePath(selectedStrategy,selectedGame,period,drawFilter);
  const performanceShareLink=referralOrigin()+performanceSharePath;
  const performanceSharePayload=shareCardPayload({
    type:isWatchMode?"strategy-performance":"live-performance",
    title:isWatchMode?"Strategy Performance":"Live Play Performance",
    strategyName:performanceTitle,
    game:gameLabel(selectedGame),
    status:isWatchMode?"Historical simulation":"Verified live tracking",
    roi:isWatchMode?watchRoiText(totals.roi,totals.total_cost):roiText(totals.roi,totals.total_cost),
    drawsTracked:String(summaryNumber(totals.draws_tracked)),
    createdDate:periodLabel,
    link:performanceShareLink,
    isPublic:true,
    accent:isWatchMode?"#2E6DA4":"#008577",
    note:(isWatchMode
      ?"HISTORICAL SIMULATION. Numbers may have been selected after these drawings. Not actual tickets played and not a verified prediction record."
      :"Verified tracked performance from lineups frozen before their draws.")+" "+drawWindowLabel+" - "+periodLabel+".",
    watchDraw:false,
    drawTime:drawWindowLabel+" - "+periodLabel,
    playCount:String(summaryNumber(totals.draws_tracked)),
    estimatedCost:fmtMoney(totals.total_cost),
    won:fmtMoney(totals.cash_won),
    net:netText(totals.net),
    bestMatch:summaryNumber(totals.best_match)+" / "+selectedGameConfig.count,
    freeTickets:summaryNumber(totals.free_tickets),
    winningPlayCount:summaryNumber(totals.winning_draws),
    largestPrize:fmtMoney(totals.largest_prize),
    ctaLabel:"View Strategy Performance",
  });
  function LatestSelectedOfficialCard(){
    const row=selectedLatestOfficialDraw;
    return(
      <div className="panel" style={{border:"1px solid #dbeee9",background:"linear-gradient(135deg,#ffffff,#f7fffc)",display:"grid",gap:12,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
          <div>
            <div className="results-eyebrow">Latest Official Result - {gameLabel(selectedGame)}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:31,fontWeight:900,color:"#002855",lineHeight:1.05}}>
              {row?fmtShortDate(row.draw_date)+" "+drawTimeLabel(row):(isWatchMode?"Historical official draws only":"No official result stored yet")}
            </div>
            <div style={{fontSize:13,color:"#555",marginTop:4}}>
              {isWatchMode
                ?"Watch Results are historical performance, not live tracking status. Upcoming or incomplete draws are excluded."
                :"Newest completed lottery draw for the selected game and draw time. This is separate from Live Results."}
            </div>
          </div>
          <span className="sec-badge" style={{background:row?"#008577":"#777"}}>{row?.official_result_status||(isWatchMode?"Watch history":"No official row")}</span>
        </div>
        {row?.numbers?.length?(
          <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
            {row.numbers.map((n,i)=><Ball key={i} n={n} size="lg"/>)}
            {PICK_GAME_IDS.includes(selectedGame)&&(
              <span className="sec-badge" style={{background:"#002855"}}>Fireball {row.fireball_digit??"Waiting"}</span>
            )}
          </div>
        ):(
          <div className="results-winning-placeholder">
            {isWatchMode?"Choose a completed historical period to review saved-strategy performance.":"Official winning numbers will appear here after the Florida Lottery source publishes the draw."}
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:8,fontSize:12,color:"#555",lineHeight:1.35}}>
          <div><strong style={{color:"#002855"}}>Imported:</strong> {formatOfficialTimestamp(row?.fetched_at||row?.official_result_at)}</div>
          <div><strong style={{color:"#002855"}}>Source:</strong> {officialSourceLabel(row)}</div>
          <div><strong style={{color:"#002855"}}>Last checked:</strong> {formatOfficialTimestamp(row?.official_checked_at||row?.fetched_at||row?.official_result_at)}</div>
        </div>
      </div>
    );
  }
  return(
    <div className="results-center-compact">
      <div className="results-topline">
        <div>
          <span className="sec-title">Results Center</span>
          <p>{modeDescription}</p>
        </div>
        <span className="sec-badge" style={{background:statusLabel==="Your Results Are Ready"?"#008577":statusLabel==="No Scheduled Strategies"?"#5d6675":"#c8860a"}}>
          {statusLabel==="Your Results Are Ready"?"Completed Official Results":statusLabel}
        </span>
      </div>

      <div className="launch-note results-trust-strip">{LIVE_ROI_TRUST_COPY}</div>
      {isWatchMode?(
        <div className="launch-note" style={{borderColor:"#ffd54f",background:"#fffaf0",color:"#5a4a00",fontWeight:900,lineHeight:1.45}}>
          HISTORICAL SIMULATION. Numbers may have been selected after these drawings. Not actual tickets played and not a verified prediction record.
        </div>
      ):(
        <div className="launch-note" style={{borderColor:"#bfe8db",background:"#eefaf6",color:"#006a5e",fontWeight:900,lineHeight:1.45}}>
          Verified tracked performance uses only lineups frozen before their official draws.
        </div>
      )}
      <LatestSelectedOfficialCard/>
      <div className="panel results-filter-bar">
        <div className="results-mode-control">
          <div className="results-mode-label">Results</div>
          <div className="results-mode-buttons" role="radiogroup" aria-label="Results mode">
            {[
              {id:"watch",label:"Strategy Performance",sub:"historical simulation"},
              {id:"live",label:"Live Plays",sub:"actual tracked plays"},
            ].map(option=>(
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={resultMode===option.id}
                className={resultMode===option.id?"active":""}
                onClick={()=>setResultMode(option.id)}
              >
                <span>{option.label}</span>
                <small>{option.sub}</small>
              </button>
            ))}
          </div>
        </div>
        <label>
          Game
          <select className="add-inp" value={selectedGame} onChange={e=>setSelectedGame(e.target.value)}>
            {launchReadyGames().map(g=><option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
        </label>
        <label>
          Strategy
          <select className="add-inp" value={selectedStrategy} onChange={e=>setSelectedStrategy(e.target.value)}>
            <option value="all">{allStrategiesLabel}</option>
            {strategyOptions.map(record=><option key={record.id} value={record.id}>{strategyDisplayName(record.id,record)}</option>)}
          </select>
        </label>
        <label>
          Period
          <select className="add-inp" value={period} onChange={e=>setPeriod(e.target.value)}>
            {RESULTS_HISTORY_WINDOWS.map(option=><option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>
        <label>
          Draw Time
          <select className="add-inp" value={drawFilter} onChange={e=>setDrawFilter(e.target.value)}>
            <option value="both">All Draws</option>
            <option value="midday">Midday</option>
            <option value="evening">Evening</option>
          </select>
        </label>
        <div className="results-refresh-note">
          Last updated: <strong>{latestRefresh}</strong>
          {refreshRow?.refresh_reason&&<> - {refreshReasonText(refreshRow.refresh_reason)}</>}
        </div>
      </div>

      {summaryMissing&&(
        <div className="panel" style={{border:"1px solid #ffd54f",background:"#fffaf0"}}>
          <strong style={{color:"#5a4a00"}}>{isAdmin?"Admin notice: summaries need backfill or verification.":"Live summaries are being prepared."}</strong>
          <div style={{fontSize:13,color:"#5a4a00",lineHeight:1.45,marginTop:5}}>
            {isAdmin?"Run Strategy Performance Backfill from Admin Tools, then verify totals.":"Your completed live results will appear here after LottoTracker finishes processing official draws."}
          </div>
        </div>
      )}

      <div className="panel results-hero-card">
        <div className="results-hero-top">
          <div>
            <div className="results-eyebrow">{isWatchMode?"Strategy Performance":"Live Plays"} - {gameLabel(selectedGame)}</div>
            <div className="results-status-title">
              {performanceTitle}
            </div>
            {isWatchMode&&(
              <div style={{fontSize:13,color:"rgba(255,255,255,.8)",fontWeight:800,lineHeight:1.35,marginTop:6,maxWidth:760}}>
                If you had played this strategy for every eligible draw during the selected period, here is how it would have performed.
              </div>
            )}
            <div className="results-draw-meta">
              {bestWinningDrawLabel(displayLatestRows)}
            </div>
            {isWatchMode&&<div style={{fontSize:13,color:"#555",fontWeight:800,marginTop:5}}>{winningDatesLabel(watchResultRows,4)}</div>}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"flex-end",flexWrap:"wrap"}}>
            <span className="sec-badge" style={{background:statusLabel==="Your Results Are Ready"?"#008577":"#c8860a"}}>{statusLabel}</span>
            <ShareCardButton payload={performanceSharePayload} label={isWatchMode?"Share Performance":"Share Live Results"} className="btn btn-sm btn-p"/>
          </div>
        </div>
        {bestWinningRow?(
          <div className="results-best-draw-card">
            <div>
              <div className="results-eyebrow">Best Winning Draw</div>
              <strong>{fmtShortDate(bestWinningRow.draw_date)} · {drawTimeLabel(bestWinningRow)}</strong>
              <span>{fmtMoney(summaryNumber(bestWinningRow.cash_won))} cash won · {summaryNumber(bestWinningRow.free_tickets)} free tickets</span>
            </div>
            <div className="results-winning-row" aria-label="Winning numbers for best draw">
              {(bestWinningOfficialDraw?.numbers||[]).map((n,i)=><Ball key={i} n={n} size="lg"/>)}
              {PICK_GAME_IDS.includes(selectedGame)&&bestWinningOfficialDraw&&(
                <span className="sec-badge" style={{background:"#002855"}}>{formatFireballDigit(bestWinningOfficialDraw.fireball_digit,"Fireball unavailable")}</span>
              )}
            </div>
          </div>
        ):isWatchMode?(
          <div className="results-winning-placeholder">
            No winning draws during this period. Try another date range to explore more history.
          </div>
        ):(
          <div className="results-winning-placeholder">
            No winning Live Play draw in this selected period. Schedule a strategy before a draw to build Live Play history.
          </div>
        )}
        <div className="results-metric-strip">
          {[
            ["Total Cost",fmtMoney(totals.total_cost)],
            ["Cash Won",fmtMoney(totals.cash_won)],
            ["Net Result",totals.net<0?"Loss "+fmtMoney(Math.abs(totals.net)):netText(totals.net)],
            ["ROI",isWatchMode?watchRoiText(totals.roi,totals.total_cost):roiText(totals.roi,totals.total_cost)],
            ["Winning Draws",summaryNumber(totals.winning_draws)],
            ["Best Match",totals.best_match+" / "+selectedGameConfig.count],
            ["Draws Tracked",totals.draws_tracked],
            ["Free Tickets",totals.free_tickets],
          ].map(([label,value])=>(
            <div key={label} className="results-metric-pill">
              <div>{label}</div>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        {!latestDrawRows.length&&!latestLoading&&!latestError&&!pendingEntries.length&&(
          <div className="results-empty-hero">
            <strong>{isWatchMode?"No completed draws in range":"No scheduled live results yet."}</strong>
            <span>{isWatchMode?"Choose a different period or draw time. Upcoming or incomplete draws are excluded from Watch Results.":"Schedule a strategy for an upcoming draw and completed official results will appear here."}</span>
            <button type="button" className="btn btn-sm btn-p" onClick={()=>navigate("/my-strategies")}>My Playslips</button>
          </div>
        )}
        {latestError&&<div style={{marginTop:12,fontSize:13,color:"#ffd54f",fontWeight:800}}>Latest results are still processing.</div>}
        {latestOfficialIsUntrackedLive&&(
          <div style={{marginTop:12,background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:10,padding:12,fontSize:13,color:"#365",lineHeight:1.45}}>
            <strong style={{color:"#002855"}}>Official results are available, but you did not track a strategy for this draw.</strong>
            <div>Latest tracked Live draw: {latestTrackedLiveLabel}</div>
          </div>
        )}
      </div>

      <div className="results-dashboard-grid">
        <section className="panel results-rankings-panel">
          <div className="sec-hdr results-section-head">
            <span className="sec-title">{resultMode==="live"?"Live Strategy Rankings":"Watch Strategy Rankings"}</span>
            <label>
                Sort
              <select className="add-inp" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                <option value="roi">ROI</option>
                <option value="cash">Cash won</option>
                <option value="net">Net result</option>
                <option value="largest">Largest single-draw prize</option>
                <option value="best">Best match</option>
                <option value="draws">Draws tracked</option>
              </select>
            </label>
          </div>
          <div className="results-rank-list">
              {rankingRows.length?rankingRows.map((item,index)=>{
                const row=item.row;
                const enough=summaryNumber(row?.draws_tracked)>0||summaryNumber(row?.total_cost)>0;
                return(
                  <button key={item.id} type="button" onClick={()=>setDetailStrategy(item.id)} className={"results-rank-row "+(index===0&&enough?"top":"")}>
                    <div className="results-rank-main">
                      <div className="results-rank-num">{enough?index+1:"-"}</div>
                      <div>
                        <div className="results-rank-name">{item.name}</div>
                        <div className="results-rank-note">{enough?item.stronger.text:"Not enough live data yet."}</div>
                      </div>
                    </div>
                    <div className="results-rank-stats">
                      {[
                        ["ROI",isWatchMode?watchRoiText(row?.roi,row?.total_cost):roiText(row?.roi,row?.total_cost)],
                        ["Cash",fmtMoney(row?.cash_won||0)],
                        ["Net",netText(row?.net||0)],
                        ["Best",summaryNumber(row?.best_match)+" / "+selectedGameConfig.count],
                        ["Draws",summaryNumber(row?.draws_tracked)],
                      ].map(([label,value])=>(
                        <div key={label}>
                          <span>{label}</span>
                          <strong>{value}</strong>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              }):(
                <EmptyResultsPanel title={performanceLoading?"Loading live strategy results...":"No completed live strategy data yet."} text={performanceLoading?"LottoTracker is loading your live performance summaries.":"Schedule a strategy for a draw. Completed official results will create live performance rankings here."} action={{label:"Schedule Strategy",onClick:()=>navigate("/my-strategies")}}/>
              )}
          </div>
        </section>

        <aside className="results-side-stack">
          <div className="panel results-split-card">
            <div className="sec-title">Midday vs Evening</div>
            <div className="results-split-grid">
              <div className="midday">
                <span>Midday</span>
                <strong>{isWatchMode?watchRoiText(totalMidday.roi,totalMidday.total_cost):roiText(totalMidday.roi,totalMidday.total_cost)}</strong>
                <small>Cash {fmtMoney(totalMidday.cash_won)} - Cost {fmtMoney(totalMidday.total_cost)} - {totalMidday.draws_tracked} draws</small>
              </div>
              <div className="evening">
                <span>Evening</span>
                <strong>{isWatchMode?watchRoiText(totalEvening.roi,totalEvening.total_cost):roiText(totalEvening.roi,totalEvening.total_cost)}</strong>
                <small>Cash {fmtMoney(totalEvening.cash_won)} - Cost {fmtMoney(totalEvening.total_cost)} - {totalEvening.draws_tracked} draws</small>
              </div>
            </div>
            <p>{isWatchMode&&middayEveningSame?"Midday and Evening are identical for this filter, so the period insights below are more useful.":betterDrawTime(totalMidday,totalEvening).text}</p>
            {isWatchMode&&middayEveningSame&&(
              <div style={{display:"grid",gap:6,marginTop:10,fontSize:12,color:"#555",lineHeight:1.35}}>
                <div><strong style={{color:"#002855"}}>Winning dates:</strong> {watchInsights.winningDates}</div>
                <div><strong style={{color:"#002855"}}>Winning months:</strong> {watchInsights.winningMonths}</div>
                <div><strong style={{color:"#002855"}}>Longest losing streak:</strong> {watchInsights.longestLosingStreak} completed draws</div>
                <div><strong style={{color:"#002855"}}>Average days between wins:</strong> {watchInsights.averageGap}</div>
              </div>
            )}
          </div>

          <div className="panel results-detail-actions">
            <button type="button" className="btn btn-s" onClick={()=>setInfoPanel("charts")}>View Charts</button>
            <button type="button" className="btn btn-s" onClick={()=>setInfoPanel("trust")}>How These Numbers Work</button>
          </div>
        </aside>
      </div>

      {infoPanel&&(
        <div role="dialog" aria-modal="true" className="results-info-modal">
          <div className="panel">
            <div className="sec-hdr">
              <span className="sec-title">{infoPanel==="charts"?"Charts":"How these numbers work"}</span>
              <button type="button" className="btn btn-sm btn-s" onClick={()=>setInfoPanel(null)}>Close</button>
            </div>
            {infoPanel==="charts"?(
              <div className="results-modal-grid">
                {!singleStrategySelected&&(
                  <div>
                    <div className="results-mini-title">Net performance by strategy</div>
                    {rankingRows.slice(0,5).map(item=><MiniBar key={item.id} label={item.name} value={summaryNumber(item.row?.net)} max={maxNet} color={summaryNumber(item.row?.net)>=0?"#008577":"#c8102e"}/>)}
                    {!rankingRows.length&&<div style={{fontSize:13,color:"#555"}}>Charts appear after live summaries are generated.</div>}
                  </div>
                )}
                <div>
                  <div className="results-mini-title">Cost versus cash won</div>
                  <MiniBar label="Cost" value={summaryNumber(totals.total_cost)} max={Math.max(totals.total_cost,totals.cash_won)} color="#002855"/>
                  <MiniBar label="Cash won" value={summaryNumber(totals.cash_won)} max={Math.max(totals.total_cost,totals.cash_won)} color="#008577"/>
                </div>
                {isWatchMode&&middayEveningSame?(
                  <div>
                    <div className="results-mini-title">Historical insights</div>
                    <div style={{display:"grid",gap:7,fontSize:13,color:"#555",lineHeight:1.4}}>
                      <div><strong style={{color:"#002855"}}>Winning dates:</strong> {watchInsights.winningDates}</div>
                      <div><strong style={{color:"#002855"}}>Winning months:</strong> {watchInsights.winningMonths}</div>
                      <div><strong style={{color:"#002855"}}>Longest losing streak:</strong> {watchInsights.longestLosingStreak} completed draws</div>
                      <div><strong style={{color:"#002855"}}>Average days between wins:</strong> {watchInsights.averageGap}</div>
                    </div>
                  </div>
                ):(
                  <div>
                    <div className="results-mini-title">Midday versus Evening cash</div>
                    <MiniBar label="Midday" value={summaryNumber(totalMidday.cash_won)} max={Math.max(totalMidday.cash_won,totalEvening.cash_won)} color="#c8860a"/>
                    <MiniBar label="Evening" value={summaryNumber(totalEvening.cash_won)} max={Math.max(totalMidday.cash_won,totalEvening.cash_won)} color="#002855"/>
                  </div>
                )}
              </div>
            ):(
              <div className="results-trust-copy">
                <div><strong>{resultMode==="live"?"Live Tracking":"Saved Playslip"}</strong> uses the frozen numbers saved when a draw was scheduled.</div>
                <div>Only completed official draws count. Pending draws are excluded.</div>
                <div>Cash winnings exclude free-ticket value.</div>
                <div>Net = cash won minus cost.</div>
                <div>ROI = net divided by cost x 100.</div>
                <div>Live, Watch, and What-If results are separate and never combine into one ROI.</div>
                <div className="results-refresh-box">
                  {refreshRow?.last_completed_draw_date
                    ?"Last completed draw: "+fmtShortDate(refreshRow.last_completed_draw_date)+" "+drawFilterLabel(refreshRow.last_completed_draw_time)+". "+refreshReasonText(refreshRow.refresh_reason)
                    :"No completed live summary has been generated for this filter yet."}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {detailStrategy&&(
        <div role="dialog" aria-modal="true" onMouseDown={event=>{if(event.currentTarget===event.target)closeDetailDrawer();}} style={{position:"fixed",inset:0,zIndex:980,background:"rgba(0,20,45,.62)",display:"flex",justifyContent:"flex-end"}}>
          <div ref={detailDrawerRef} className="panel" style={{width:"min(760px,100%)",height:"100%",overflow:"auto",borderRadius:0,margin:0}}>
            <div className="sec-hdr" style={{gap:12,alignItems:"flex-start",flexWrap:"wrap",position:"sticky",top:0,zIndex:2,background:"#fff",borderBottom:"1px solid #e8eee9",paddingBottom:12}}>
              <div>
                <span className="sec-title">{detailItem?.name||"Strategy Details"}</span>
              <p style={{fontSize:13,color:"#555",lineHeight:1.45,marginTop:5}}>{resultMode==="live"?"Live tracked performance from recent completed draws.":"Watch performance from recent completed draws. These results do not count toward Live ROI."}</p>
              </div>
              <button type="button" className="btn btn-sm btn-s" onClick={closeDetailDrawer}>Close</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:16}}>
              <MetricCard label="Total Cost" value={fmtMoney(detailTotals.total_cost)}/>
              <MetricCard label="Cash Won" value={fmtMoney(detailTotals.cash_won)} tone="#008577"/>
              <MetricCard label="Net" value={netText(detailTotals.net)} tone={detailTotals.net>=0?"#008577":"#c8102e"}/>
              <MetricCard label="ROI" value={isWatchMode?watchRoiText(detailTotals.roi,detailTotals.total_cost):roiText(detailTotals.roi,detailTotals.total_cost)} tone={detailTotals.roi>=0?"#008577":"#c8102e"}/>
              <MetricCard label="Best Match" value={detailTotals.best_match+" / "+selectedGameConfig.count}/>
              <MetricCard label="Largest Single-Draw Prize" value={fmtMoney(detailTotals.largest_prize)} tone="#c8860a"/>
              <MetricCard label="Free Tickets" value={detailTotals.free_tickets}/>
              <MetricCard label="Draws Tracked" value={detailTotals.draws_tracked}/>
            </div>
            <div className="panel" style={{background:"#f8fbfa",marginBottom:16}}>
              <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Midday vs Evening</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
                <div><strong>Midday:</strong> {isWatchMode?watchRoiText(detailItem?.midday?.roi,detailItem?.midday?.total_cost):roiText(detailItem?.midday?.roi,detailItem?.midday?.total_cost)} - {fmtMoney(detailItem?.midday?.cash_won||0)} cash - {summaryNumber(detailItem?.midday?.draws_tracked)} draws</div>
                <div><strong>Evening:</strong> {isWatchMode?watchRoiText(detailItem?.evening?.roi,detailItem?.evening?.total_cost):roiText(detailItem?.evening?.roi,detailItem?.evening?.total_cost)} - {fmtMoney(detailItem?.evening?.cash_won||0)} cash - {summaryNumber(detailItem?.evening?.draws_tracked)} draws</div>
              </div>
              <div style={{fontSize:13,color:"#555",fontWeight:800,marginTop:8}}>{detailItem?.stronger?.text}</div>
            </div>
            {pickDetailBreakdown&&(
              <div className="panel" style={{background:"#fff",border:"1px solid #e8eee9",marginBottom:16}}>
                <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Selected Period Breakdown</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginBottom:12}}>
                  <MetricCard label="Winning Draws" value={pickDetailBreakdown.winningDraws}/>
                  <MetricCard label="Winning Tickets" value={pickDetailBreakdown.totalWinningTickets}/>
                  <MetricCard label="Fireball Wins" value={pickDetailBreakdown.fireballWins}/>
                  <MetricCard label="Fireball Cash" value={fmtMoney(pickDetailBreakdown.fireballCash)} tone="#c8860a"/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:12}}>
                  <div>
                    <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.6,marginBottom:7}}>Most wins during this period</div>
                    <div style={{display:"grid",gap:7}}>
                      {pickDetailBreakdown.playTypes.length?pickDetailBreakdown.playTypes.map(item=>(
                        <div key={item.play_type} style={{display:"flex",justifyContent:"space-between",gap:10,border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px",fontSize:12}}>
                          <strong style={{color:"#002855"}}>{item.play_type}</strong>
                          <span>{item.wins} wins</span>
                        </div>
                      )):<div style={{fontSize:13,color:"#666"}}>No winning saved lines in this selected period.</div>}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.6,marginBottom:7}}>Most cash during this period</div>
                    <div style={{display:"grid",gap:7}}>
                      {pickDetailBreakdown.playTypes.length?pickDetailBreakdown.playTypes.map(item=>(
                        <div key={item.play_type+"-cash"} style={{display:"flex",justifyContent:"space-between",gap:10,border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px",fontSize:12}}>
                          <strong style={{color:"#002855"}}>{item.play_type}</strong>
                          <span>{fmtMoney(item.cash)}</span>
                        </div>
                      )):<div style={{fontSize:13,color:"#666"}}>Cash totals appear when a saved line wins in this selected period.</div>}
                    </div>
                  </div>
                </div>
                <div style={{marginTop:12}}>
                  <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.6,marginBottom:7}}>Highest-earning saved lines</div>
                  <div style={{display:"grid",gap:7}}>
                    {pickDetailBreakdown.topLines.length?pickDetailBreakdown.topLines.map(line=>(
                      <div key={[line.saved_line_index,digitsText(line.saved_digits),line.play_type].join("-")} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:10,border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px",fontSize:12}}>
                        <div>
                          <strong style={{color:"#002855"}}>#{line.saved_line_index} {digitsText(line.saved_digits)}</strong>
                          <div style={{color:"#666",fontWeight:800}}>{line.play_type} - {line.wins} wins - best {line.best_draw||"draw"}</div>
                        </div>
                        <strong style={{color:"#008577"}}>{fmtMoney(line.cash)} period total</strong>
                      </div>
                    )):<div style={{fontSize:13,color:"#666"}}>No highest-earning saved lines in this selected period.</div>}
                  </div>
                </div>
              </div>
            )}
            {fantasy5DetailBreakdown&&(
              <div className="panel" style={{background:"#fff",border:"1px solid #e8eee9",marginBottom:16}}>
                <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Selected Period Breakdown</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:10,marginBottom:12}}>
                  <MetricCard label="Winning Draws" value={fantasy5DetailBreakdown.winningDraws}/>
                  <MetricCard label="Cash-Winning Tickets" value={fantasy5DetailBreakdown.cashWinningTickets}/>
                  <MetricCard label="Free-Ticket Wins" value={fantasy5DetailBreakdown.freeTicketWins}/>
                  <MetricCard label="Total Cash Won" value={fmtMoney(fantasy5DetailBreakdown.totalCash)} tone="#008577"/>
                  <MetricCard label="Best Match" value={fantasy5DetailBreakdown.bestMatch+" / 5"}/>
                  <MetricCard label="Largest Single-Line Prize" value={fmtMoney(fantasy5DetailBreakdown.largestPrize)} tone="#c8860a"/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:12}}>
                  <div>
                    <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.6,marginBottom:7}}>Most wins during this period</div>
                    <div style={{display:"grid",gap:7}}>
                      {fantasy5DetailBreakdown.tiers.length?fantasy5DetailBreakdown.tiers.map(item=>(
                        <div key={item.label} style={{display:"flex",justifyContent:"space-between",gap:10,border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px",fontSize:12}}>
                          <strong style={{color:"#002855"}}>{item.label}</strong>
                          <span>{item.wins} wins{item.free_tickets?` · ${item.free_tickets} free tickets`:""}</span>
                        </div>
                      )):<div style={{fontSize:13,color:"#666"}}>No winning saved lines in this selected period.</div>}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.6,marginBottom:7}}>Most cash during this period</div>
                    <div style={{display:"grid",gap:7}}>
                      {fantasy5DetailBreakdown.tiers.filter(item=>summaryNumber(item.cash)>0).length?fantasy5DetailBreakdown.tiers.filter(item=>summaryNumber(item.cash)>0).map(item=>(
                        <div key={item.label+"-cash"} style={{display:"flex",justifyContent:"space-between",gap:10,border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px",fontSize:12}}>
                          <strong style={{color:"#002855"}}>{item.label}</strong>
                          <span>{fmtMoney(item.cash)}</span>
                        </div>
                      )):<div style={{fontSize:13,color:"#666"}}>Cash totals appear when a saved line wins a cash tier in this selected period.</div>}
                    </div>
                  </div>
                </div>
                <div style={{marginTop:12}}>
                  <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.6,marginBottom:7}}>Highest-earning saved lines</div>
                  <div style={{display:"grid",gap:7}}>
                    {fantasy5DetailBreakdown.topLines.length?fantasy5DetailBreakdown.topLines.map(line=>(
                      <div key={[line.saved_line_index,digitsText(line.saved_digits)].join("-")} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:10,border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px",fontSize:12}}>
                        <div>
                          <strong style={{color:"#002855"}}>#{line.saved_line_index} - {digitsText(line.saved_digits)}</strong>
                          <div style={{color:"#666",fontWeight:800}}>
                            {line.wins} winning draws · {fmtMoney(line.cash)} cash · {line.free_tickets} free tickets · Best: {line.best_match}/5{line.best_draw?" on "+line.best_draw:""}
                          </div>
                        </div>
                        <strong style={{color:"#008577"}}>{fmtMoney(line.cash)}</strong>
                      </div>
                    )):<div style={{fontSize:13,color:"#666"}}>No highest-earning saved lines in this selected period.</div>}
                  </div>
                </div>
              </div>
            )}
            <div className="sec-title" style={{fontSize:22,marginBottom:12}}>Recent Completed Draws</div>
            {displayDetailLoading&&<LoadingSkeleton/>}
            {displayDetailError&&<div className="auth-err">{displayDetailError}</div>}
            {!displayDetailLoading&&!displayDetailError&&!displayDetailRows.length&&<EmptyResultsPanel title={isWatchMode?"No completed draws in range":"No recent completed draws for this strategy."} text={isWatchMode?"This Watch Results period has no eligible official completed draws.":"This strategy may have pending draws, or live summaries may still need to be generated."}/>}
            <div style={{display:"grid",gap:9}}>
              {displayDetailRows.map(row=>(
                <div key={row.id} style={{border:"1px solid #e8eee9",borderRadius:12,padding:12,background:"#fff"}}>
                  <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:10,alignItems:"start"}}>
                    <div>
                      <strong style={{color:"#002855"}}>{fmtShortDate(row.draw_date)} - {drawFilterLabel(row.draw_time)}</strong>
                      <div style={{fontSize:12,color:"#666",fontWeight:800,marginTop:3}}>Official status: completed - {resultLineCheckText(row,isWatchMode)}</div>
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      {!isWatchMode&&resultShareEligible(row)&&(
                        <ShareCardButton
                          payload={resultCardSharePayload(row,selectedGameConfig)}
                          label="Share Result"
                          className="btn btn-sm btn-p"
                          onBeforeOpen={()=>prepareResultShare(row)}
                        />
                      )}
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>setExpandedDraw(expandedDraw===row.id?"":row.id)}>View Draw Details</button>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:8,marginTop:10}}>
                    {[
                      ["Cash Won",fmtMoney(row.cash_won)],
                      ["Free Tickets",row.free_tickets],
                      ["Best Match",row.best_match+" / "+selectedGameConfig.count],
                      ["Net",netText(row.net)],
                      ["Largest Saved-Line Prize",fmtMoney(row.largest_prize)],
                    ].map(([label,value])=>(
                      <div key={label} style={{background:"#f8fbfa",border:"1px solid #edf1f0",borderRadius:9,padding:"8px 9px"}}>
                        <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>{label}</div>
                        <strong style={{color:"#002855",fontSize:13}}>{value}</strong>
                      </div>
                    ))}
                  </div>
                  {expandedDraw===row.id&&(
                    isWatchMode&&selectedGame==="fantasy5"?(()=>{
                      const winningDetails=fantasy5WinningDetailsForRow(row);
                      const cashSum=winningDetails.reduce((sum,path)=>sum+summaryNumber(path.final_payout||path.payout),0);
                      const freeCount=winningDetails.filter(path=>path.free_ticket).length;
                      const cashReconciles=Math.abs(cashSum-summaryNumber(row.cash_won))<0.01;
                      const freeReconciles=freeCount===summaryNumber(row.free_tickets);
                      const groupedByTier=Object.values(winningDetails.reduce((groups,path)=>{
                        const key=String(path.match_count||0);
                        if(!groups[key])groups[key]={match_count:summaryNumber(path.match_count),label:fantasy5PrizeType(summaryNumber(path.match_count)),lines:[],cash:0,free_tickets:0};
                        groups[key].lines.push(path);
                        groups[key].cash+=summaryNumber(path.final_payout||path.payout);
                        if(path.free_ticket)groups[key].free_tickets+=1;
                        return groups;
                      },{})).sort((a,b)=>summaryNumber(b.match_count)-summaryNumber(a.match_count));
                      return(
                        <div style={{marginTop:10,background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:10,fontSize:12,color:"#5a4a00",lineHeight:1.45}}>
                          <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:8}}>
                            <strong>Winning Play Explanation</strong>
                            <span style={{fontWeight:900,color:cashReconciles&&freeReconciles?"#008577":"#c8102e"}}>
                              Reconciled: {cashReconciles&&freeReconciles?"Yes":"No - review required"}
                            </span>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:7,marginBottom:10}}>
                            <div><strong>Official numbers:</strong> {digitsText(row.official_numbers||winningDetails[0]?.official_base_digits||[])||"Unavailable"}</div>
                            <div><strong>Saved lines checked:</strong> {row.play_count}</div>
                            <div><strong>Winning saved lines:</strong> {row.winning_plays_count}</div>
                            <div><strong>Free tickets earned:</strong> {row.free_tickets}</div>
                            <div><strong>Cash won:</strong> {fmtMoney(row.cash_won)}</div>
                            <div><strong>Draw cost:</strong> {fmtMoney(row.total_cost)}</div>
                            <div><strong>Net result:</strong> {netText(row.net)}</div>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:7,marginBottom:10}}>
                            <div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:8}}>
                              <strong>Cash line sum:</strong> {fmtMoney(cashSum)} {cashReconciles?"matches draw cash":"does not match draw cash"}
                            </div>
                            <div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:8}}>
                              <strong>Free-ticket line count:</strong> {freeCount} {freeReconciles?"matches total":"does not match total"}
                            </div>
                          </div>
                          {groupedByTier.length?(
                            <div style={{display:"grid",gap:8}}>
                              {groupedByTier.map(group=>(
                                <details key={group.match_count} open={group.match_count>=4} style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:9,color:"#002855"}}>
                                  <summary style={{cursor:"pointer",fontWeight:900}}>
                                    {group.match_count} of 5 - {group.lines.length} saved lines - {group.free_tickets?group.free_tickets+" free tickets - ":""}{fmtMoney(group.cash)} cash
                                  </summary>
                                  <div style={{display:"grid",gap:6,marginTop:8}}>
                                    {group.lines.map(path=>(
                                      <div key={[path.saved_line_index,digitsText(path.saved_digits),path.match_count].join("-")} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:10,borderTop:"1px solid #f3ead0",paddingTop:7,fontSize:12}}>
                                        <div>
                                          <strong>Saved line #{path.saved_line_index}: {digitsText(path.saved_digits)}</strong>
                                          <div style={{color:"#555",fontWeight:800}}>
                                            Match: {path.match_count} of 5 - {path.prize_type} - Matched: {digitsText(path.matched_numbers)}
                                          </div>
                                        </div>
                                        <strong style={{color:path.free_ticket?"#c8860a":"#008577"}}>
                                          {path.free_ticket?"Free ticket":fmtMoney(path.final_payout||path.payout)}
                                        </strong>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              ))}
                            </div>
                          ):(
                            <div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:9}}>No winning saved lines for this completed draw.</div>
                          )}
                        </div>
                      );
                    })():isWatchMode&&["pick2","pick3","pick4","pick5"].includes(selectedGame)?(()=>{
                      const winningDetails=pick4WinningDetailsForRow(row);
                      const payoutSum=winningDetails.reduce((sum,path)=>sum+summaryNumber(path.final_payout),0);
                      const reconciles=Math.abs(payoutSum-summaryNumber(row.cash_won))<0.01;
                      const groupedWinningDetails=Object.values(winningDetails.reduce((groups,path)=>{
                        const key=[path.saved_line_index,digitsText(path.saved_digits),path.play_type,path.wager,path.multiple,path.fireball_enabled,path.permutation_count].join("|");
                        if(!groups[key])groups[key]={...path,paths:[],total:0};
                        groups[key].paths.push(path);
                        groups[key].total+=summaryNumber(path.final_payout);
                        return groups;
                      },{}));
                      return(
                        <div style={{marginTop:10,background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:10,fontSize:12,color:"#5a4a00",lineHeight:1.45}}>
                          <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:8}}>
                            <strong>Winning play explanation</strong>
                            <span style={{fontWeight:900,color:reconciles?"#008577":"#c8102e"}}>
                              Displayed payouts: {fmtMoney(payoutSum)} · Draw cash: {fmtMoney(row.cash_won)}
                            </span>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:7,marginBottom:10}}>
                            <div><strong>Official base:</strong> {digitsText(winningDetails[0]?.official_base_digits)||"Unavailable"}</div>
                            <div><strong>Fireball:</strong> {winningDetails[0]?.official_fireball_digit??"Unavailable"}</div>
                            <div><strong>Winning saved lines:</strong> {row.winning_plays_count}</div>
                            <div><strong>Reconciled:</strong> {reconciles?"Yes":"No - review required"}</div>
                          </div>
                          {groupedWinningDetails.length?(
                            <div style={{display:"grid",gap:8}}>
                              {groupedWinningDetails.map((group,index)=>(
                                <div key={index} style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:9,color:"#002855"}}>
                                  <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:6}}>
                                    <strong>Saved line #{group.saved_line_index}: {digitsText(group.saved_digits)}</strong>
                                    <strong style={{color:"#008577"}}>Saved-line total: {fmtMoney(group.total)}</strong>
                                  </div>
                                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(135px,1fr))",gap:"5px 10px",fontSize:12,color:"#555",fontWeight:800}}>
                                    <span>{group.play_type} · {moneyLabel(group.wager)} · x{group.multiple} · {group.fireball_enabled?"Fireball ON":"Fireball OFF"}</span>
                                    <span>{group.permutation_count}-way</span>
                                    <span>Official draw: {digitsText(group.official_base_digits)}</span>
                                    <span>Fireball: {group.official_fireball_digit}</span>
                                  </div>
                                  <div style={{display:"grid",gap:5,marginTop:8,fontSize:12,color:"#334"}}>
                                    {group.paths.map((path,pathIndex)=>(
                                      <div key={pathIndex} style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",borderTop:pathIndex===0?"1px solid #f3ead0":"none",paddingTop:pathIndex===0?7:0}}>
                                        <span>{pick4DisplayWinPath(path)}: {fmtMoney(path.payout_before_multiplier)} x{path.multiplier}</span>
                                        <strong>{fmtMoney(path.final_payout)}</strong>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ):(
                            <div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:9}}>No winning saved lines for this completed draw.</div>
                          )}
                        </div>
                      );
                    })():!isWatchMode&&row.frozen_snapshot?(()=>{
                      const record={game:selectedGame,snapshot:row.frozen_snapshot};
                      const frozenNumbers=strategySnapshotNumbers(record);
                      const verification=resultCardVerifiedInfo(row);
                      const nearMiss=nearMissExplanationForFrozenResult(row);
                      const verdict=resultCardVerdict(row);
                      return(
                        <div style={{marginTop:10,background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:10,fontSize:12,color:"#5a4a00",lineHeight:1.45}}>
                          <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:8}}>
                            <strong>Frozen played result</strong>
                            <span style={{fontWeight:900,color:resultOutcomeDisplay(resultOutcomeState(row)).color}}>{verdict}</span>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:7,marginBottom:10}}>
                            <div><strong>Official numbers:</strong> {digitsText(row.official_numbers)||"Unavailable"}</div>
                            {isPickGame(selectedGame)&&<div><strong>Fireball:</strong> {row.official_fireball_digit??"Unavailable"}</div>}
                            <div><strong>Frozen saved lines:</strong> {frozenNumbers.length}</div>
                            <div><strong>Cost:</strong> {fmtMoney(row.total_cost)}</div>
                            <div><strong>Cash won:</strong> {fmtMoney(row.cash_won)}</div>
                            <div><strong>Free tickets:</strong> {row.free_tickets}</div>
                            <div><strong>Best match:</strong> {row.best_match} / {selectedGameConfig.count}</div>
                            <div><strong>Winning plays:</strong> {row.winning_plays_count}</div>
                          </div>
                          {verification?(
                            <div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:8,marginBottom:8,color:"#008577",fontWeight:900}}>
                              {verification.label}: Lineup saved at {verification.savedAt}
                            </div>
                          ):(
                            <div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:8,marginBottom:8}}>
                              No pre-draw verification badge is shown for this row because the stored timestamp does not prove the claim.
                            </div>
                          )}
                          {nearMiss&&<div style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:8,marginBottom:8}}>{nearMiss}</div>}
                          <details style={{background:"#fff",border:"1px solid #f1df8a",borderRadius:9,padding:9,color:"#002855"}}>
                            <summary style={{cursor:"pointer",fontWeight:900}}>View frozen numbers played</summary>
                            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(105px,1fr))",gap:6,marginTop:8,maxHeight:220,overflow:"auto"}}>
                              {frozenNumbers.map((nums,index)=>(
                                <div key={index} style={{fontFamily:"monospace",fontSize:12,fontWeight:900,color:"#002855"}}>#{index+1} {digitsText(nums)}</div>
                              ))}
                            </div>
                          </details>
                        </div>
                      );
                    })():(
                      <div style={{marginTop:10,background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:10,fontSize:12,color:"#5a4a00",lineHeight:1.45}}>
                        <strong>Draw details:</strong> {row.winning_plays_count} winning plays. {readableBreakdown(row.match_counts,"Match counts")}. {readableBreakdown(row.prize_breakdown,"Prize breakdown")}.
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DASHBOARD_WIDGETS=[
  {id:"nextDraw",label:"Your Next Draw"},
  {id:"todaysSchedule",label:"Today's Schedule"},
  {id:"myNumbers",label:"My Playslips"},
  {id:"recentResults",label:"Recent Results"},
  {id:"recentHits",label:"Recent Hits"},
  {id:"strategyScoreboard",label:"Strategy Scoreboard"},
  {id:"activity",label:"Activity"},
  {id:"communityFeed",label:"Community Feed"},
  {id:"poolManager",label:"Pool Manager"},
  {id:"achievements",label:"Achievements"},
  {id:"dailyGoal",label:"Daily Goal"},
  {id:"weeklySummary",label:"Weekly Summary"},
  {id:"progressBadges",label:"Progress Badges"},
  {id:"recentStrategies",label:"Recent Strategies"},
  {id:"recentPools",label:"Recent Pools"},
  {id:"hotCold",label:"Hot & Cold"},
  {id:"historySnapshot",label:"History Snapshot"},
];
const DASHBOARD_SIMPLE_IDS=["nextDraw","todaysSchedule","myNumbers","recentResults","activity"];
const DASHBOARD_ALL_IDS=DASHBOARD_WIDGETS.map(w=>w.id);
function normalizeDashboardConfig(saved){
  const cleanOrder=Array.isArray(saved?.order)
    ?[...saved.order.filter(id=>DASHBOARD_ALL_IDS.includes(id)),...DASHBOARD_ALL_IDS.filter(id=>!saved.order.includes(id))]
    :DASHBOARD_ALL_IDS;
  const visible=Array.isArray(saved?.visible)
    ?saved.visible.filter(id=>DASHBOARD_ALL_IDS.includes(id))
    :(saved?.mode==="advanced"?DASHBOARD_ALL_IDS:DASHBOARD_SIMPLE_IDS);
  return{mode:saved?.mode==="advanced"?"advanced":"simple",order:cleanOrder,visible:visible.length?visible:DASHBOARD_SIMPLE_IDS};
}

function CustomizeDashboard({config,onChange,onClose}){
  const[dragId,setDragId]=useState(null);
  function setMode(mode){
    onChange({
      mode,
      order:config.order,
      visible:mode==="advanced"?DASHBOARD_ALL_IDS:DASHBOARD_SIMPLE_IDS,
    });
  }
  function toggleVisible(id){
    const visible=config.visible.includes(id)
      ?config.visible.filter(x=>x!==id)
      :[...config.visible,id];
    onChange({...config,visible:visible.length?visible:[id]});
  }
  function dropOn(targetId){
    if(!dragId||dragId===targetId)return;
    const next=config.order.filter(id=>id!==dragId);
    const idx=next.indexOf(targetId);
    next.splice(idx,0,dragId);
    onChange({...config,order:next});
    setDragId(null);
  }
  return(
    <div className="panel" style={{border:"2px solid #008577",marginBottom:18}}>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Customize Dashboard</span>
          <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginTop:5}}>Choose what appears on Home, then drag items into your preferred order. Saved locally for now.</div>
        </div>
        <button type="button" className="btn btn-sm btn-s" onClick={onClose}>Done</button>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
        <button type="button" className={"btn "+(config.mode==="simple"?"btn-p":"btn-s")} onClick={()=>setMode("simple")}>Simple Mode</button>
        <button type="button" className={"btn "+(config.mode==="advanced"?"btn-p":"btn-s")} onClick={()=>setMode("advanced")}>Advanced Mode</button>
      </div>
      <div style={{display:"grid",gap:8}}>
        {config.order.map(id=>{
          const widget=DASHBOARD_WIDGETS.find(w=>w.id===id);
          if(!widget)return null;
          const checked=config.visible.includes(id);
          return(
            <div
              key={id}
              draggable
              onDragStart={()=>setDragId(id)}
              onDragOver={e=>e.preventDefault()}
              onDrop={()=>dropOn(id)}
              style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:10,alignItems:"center",border:"1px solid #eee",borderRadius:10,padding:"10px 12px",background:checked?"#f0f8f6":"#fafafa",cursor:"grab"}}
            >
              <span style={{fontSize:18,color:"#888"}}>↕</span>
              <strong style={{color:"#002855"}}>{widget.label}</strong>
              <label style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:12,fontWeight:800,color:checked?"#008577":"#777",textTransform:"uppercase"}}>
                <input type="checkbox" checked={checked} onChange={()=>toggleVisible(id)} style={{accentColor:"#008577"}}/>
                {checked?"Shown":"Hidden"}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HomeTab({draws,plays,loading,user,myGames,subscription,onUpgrade,featureUnlocks,dailyStreak,drawStatus,activityNotifications,strategyTrust,todaysLineup,performanceSummaries=[],onRemoveFromLineup}){
  const navigate=useNavigate();
  const[sel,setSel]=useState(null);
  const[customizing,setCustomizing]=useState(false);
  const dashboardStorageKey="lt:dashboardConfig:"+(user?.id||user?.email||"local");
  const[dashboardConfig,setDashboardConfig]=useState(()=>normalizeDashboardConfig(JSON.parse(localStorage.getItem(dashboardStorageKey)||"null")));
  useEffect(()=>{
    const next=normalizeDashboardConfig(JSON.parse(localStorage.getItem(dashboardStorageKey)||"null"));
    setDashboardConfig(next);
  },[dashboardStorageKey]);
  function saveDashboardConfig(next){
    const clean=normalizeDashboardConfig(next);
    setDashboardConfig(clean);
    localStorage.setItem(dashboardStorageKey,JSON.stringify(clean));
  }
  const firstName=(user?.email||"player").split("@")[0];
  const selectedGameLabels=normalizeGames(myGames).map(gameLabel).join(", ");
  const materializedPlays=plays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count);
  const strategyPlays=plays.filter(p=>p.strategy&&p.strategy!=="manual");
  const scoredDraws=draws.filter(d=>Array.isArray(d.numbers)&&d.numbers.length===gameNumberConfig(rowGame(d)).count);
  const publicEligibility=publicEligibilityForLineup(todaysLineup,strategyTrust);
  const livePlays=liveTrackingPlays(plays,todaysLineup);
  const liveMaterializedPlays=livePlays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count);
  const recentWins=scoredDraws.reduce((acc,d)=>{
    if(!liveMaterializedPlays.length)return acc;
    const eligiblePlays=liveMaterializedPlays.filter(pl=>publicEligibility(pl,d));
    const best=eligiblePlays.length?Math.max(0,...eligiblePlays.map(pl=>cntMatch(pl.numbers,d.numbers))):0;
    if(best>=3)acc.push({d,best});
    return acc;
  },[]).slice(0,6);
  const bestRecent=recentWins[0];
  const liveSummaryRows=performanceSummaryRowsFor(performanceSummaries,{periodKey:"all_time",drawFilter:"both"})
    .filter(r=>SHOW_ANCHORS||(r.strategy_id!=="anchor3"&&r.strategy_id!=="anchor4"));
  const strategyRows=liveSummaryRows.length
    ?liveSummaryRows.map(row=>({
      id:row.strategy_id,
      roi:row.roi==null?null:Number(row.roi),
      meta:{label:strategyDisplayName(row.strategy_id,strategyTrust?.records?.[row.strategy_id])},
    }))
    :(scoredDraws.length?aggregateStrategies(livePlays,scoredDraws,{eligibleForDraw:publicEligibility}).filter(r=>SHOW_ANCHORS||(r.id!=="anchor3"&&r.id!=="anchor4")):[]);
  const lineupIds=lineupIdSet(todaysLineup);
  const publicStrategyRows=strategyRows.filter(r=>lineupIds.has(r.id));
  const publicPlays=filterPlaysForLineup(plays,todaysLineup);
  const topStrategy=[...publicStrategyRows].sort((a,b)=>((b.roi??-Infinity)-(a.roi??-Infinity)))[0];
  const lineupStats=lineupSummary(todaysLineup,plays,drawStatus);
  const nextLockedStrategy=strategyRows.find(r=>strategyTrust?.records?.[r.id]?.status==="Locked");
  const pendingPrizes=scoredDraws.filter(d=>d.prize_5match==null||d.prize_4match==null||d.prize_3match==null).length;
  const nextDraw=drawStatus?.next||getNextDrawEntries(myGames)[0];
  const latestReady=nextDraw?.status==="Results Ready";
  const resultDraw=latestReady?(scoredDraws[0]||null):null;
  const resultSummary=resultDraw?summarizeResults(resultDraw,plays,{lineup:todaysLineup,strategyTrust}):null;
  const hasStrategy=plays.some(p=>p.strategy&&p.strategy!=="manual");
  const hasProAccess=subscriptionHas(subscription,"pro");
  const beginnerDashboard=!hasProAccess;
  const beginnerWidgetIds=["nextDraw","todaysSchedule","recentResults","recentStrategies"];
  const visibleDashboardIds=dashboardConfig.order.filter(id=>dashboardConfig.visible.includes(id)&&(!beginnerDashboard||beginnerWidgetIds.includes(id)));
  function toggle(key){setSel(s=>s===key?null:key);}
  function renderDashboardWidget(id){
    switch(id){
      case "nextDraw":
        return resultDraw?(
          <ResultsSummary draw={resultDraw} plays={plays} lineup={todaysLineup} strategyTrust={strategyTrust} onViewPlays={()=>navigate("/my-strategies")}/>
        ):(
          <NextDrawWidget myGames={myGames} activePlays={materializedPlays.length} onViewResults={()=>navigate("/results")} drawStatus={drawStatus}/>
        );
      case "todaysSchedule":
        return <TodaySchedule myGames={myGames} drawStatus={drawStatus}/>;
      case "myNumbers":
        return(
          <div className="panel" style={{marginBottom:0}}>
            <div className="sec-hdr" style={{marginBottom:12}}><span className="sec-title" style={{fontSize:20}}>My Playslips</span><button type="button" className="btn btn-sm btn-p" onClick={()=>navigate("/my-strategies")}>Add More</button></div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10}}>
              {statCards.map(card=>(
                <div key={card.label} style={{background:"#fafafa",borderRadius:10,padding:"10px 11px"}}>
                  <div style={{fontSize:10,color:"#555",textTransform:"uppercase",fontWeight:800,letterSpacing:.5}}>{card.label}</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:card.color,lineHeight:1.05}}><AnimatedMetric value={card.value}/></div>
                  <div style={{fontSize:11,color:"#666",lineHeight:1.25}}>{card.sub}</div>
                </div>
              ))}
            </div>
          </div>
        );
      case "recentResults":
        return(
          <div>
            <div className="sec-hdr"><span className="sec-title">Recent Results</span><span className="sec-badge">Latest draws</span></div>
            <div className="draws-grid">
              {draws.slice(0,6).map((d,i)=>{
                const eligiblePlays=materializedPlays.filter(pl=>publicEligibility(pl,d));
                const best=eligiblePlays.length?Math.max(0,...eligiblePlays.map(pl=>cntMatch(pl.numbers,d.numbers))):0;
                const pr=best>=3?getDrawPrize(d,best):null;
                return(
                  <div key={i} className={"draw-card"+(sel==="r"+i?" sel":"")} onClick={()=>toggle("r"+i)}>
                    <div className="dc-head"><div className="dc-date">{d.date}</div><div className="dc-type">{d.isEvening?"Evening":"Midday"}</div></div>
                    <div className="dc-body">
                      <div className="dc-balls">{d.numbers.map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div>
                      <div className="dc-prize"><span>Jackpot</span><strong>{jackpotAmountText(d)}</strong></div>
                      {pr&&<div className="dc-badge" style={{background:pr.color+"25",color:pr.color}}>{best} Match &middot; {pr.display}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {typeof sel==="string"&&sel.startsWith("r")&&draws[parseInt(sel.slice(1))]&&<MatchPanel draw={draws[parseInt(sel.slice(1))]} plays={plays} lineup={todaysLineup} strategyTrust={strategyTrust}/>}
          </div>
        );
      case "recentHits":
        return(
          <div>
            <div className="sec-hdr"><span className="sec-title">Recent Hits</span><span className="sec-badge">Simulated 3+</span></div>
            {recentWins.length?(
              <>
                <div className="draws-grid">
                  {recentWins.map(({d,best},i)=>{
                    const pr=getDrawPrize(d,best);
                    return(
                      <div key={i} className={"draw-card"+(sel==="w"+i?" sel":"")} onClick={()=>toggle("w"+i)}>
                        <div className="dc-head"><div className="dc-date">{d.date}</div><div className="dc-type">{d.isEvening?"Evening":"Midday"}</div></div>
                        <div className="dc-body">
                          <div className="dc-balls">{d.numbers.map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div>
                          {pr&&<div className="dc-badge" style={{background:pr.color+"25",color:pr.color}}>{best} Match &middot; {pr.display}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {sel&&sel.startsWith("w")&&recentWins[parseInt(sel.slice(1))]&&<MatchPanel draw={recentWins[parseInt(sel.slice(1))].d} plays={plays} lineup={todaysLineup} strategyTrust={strategyTrust}/>}
              </>
            ):<EmptyState icon="ðŸŽ¯" title="No recent hits yet." text="Keep your plays saved and LottoTracker will surface 3-match or better results here."/>}
          </div>
        );
      case "strategyScoreboard":
        return null;
      case "activity":
        return null;
      case "communityFeed":
        return(
          <div className="panel" style={{marginBottom:0}}>
            <div className="sec-hdr" style={{marginBottom:12}}><span className="sec-title" style={{fontSize:20}}>Community Feed</span><button type="button" className="btn btn-sm btn-s" onClick={()=>navigate("/community")}>Open</button></div>
            <div style={{display:"grid",gap:10}}>{COMMUNITY_FEED.slice(0,3).map(item=><div key={item.id} style={{fontSize:13,color:"#555",lineHeight:1.35}}><strong style={{color:"#002855"}}>{item.icon} {item.title}</strong><br/>{item.meta}</div>)}</div>
          </div>
        );
      case "poolManager":
        return null;
      case "achievements":
        return <AchievementTimeline plays={publicPlays} draws={scoredDraws} recentWins={recentWins}/>;
      case "dailyGoal":
        return <DailyGoalCard plays={plays} latestReady={latestReady} featureUnlocks={featureUnlocks} onDraw={()=>navigate("/results")} onNumbers={()=>navigate("/my-strategies")} onScoreboard={()=>navigate("/scoreboard")} onPool={()=>navigate("/pool-simulation")}/>;
      case "weeklySummary":
        return <WeeklySummary draws={scoredDraws} topStrategy={topStrategy} recentWins={recentWins}/>;
      case "progressBadges":
        return <ProgressBadges plays={publicPlays} draws={scoredDraws} featureUnlocks={featureUnlocks}/>;
      case "recentStrategies":
        return(
          <div className="panel" style={{marginBottom:0}}>
            <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Scheduled Strategies</div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:12}}>Strategies you play today appear here until their draws complete.</div>
            <div style={{display:"grid",gap:8}}>{(publicStrategyRows.slice(0,3).length?publicStrategyRows.slice(0,3):[{meta:{label:"No strategy marked for today"},roi:null}]).map((r,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:13}}><strong style={{color:"#002855"}}>{r.meta.label}</strong><span>{r.roi==null?"Watching":(r.roi>=0?"+":"")+r.roi.toFixed(0)+"%"}</span></div>)}</div>
            <button type="button" className="btn btn-sm btn-s" style={{marginTop:12}} onClick={()=>navigate("/strategy-builder")}>Choose Strategy</button>
          </div>
        );
      case "recentPools":
        return null;
      case "hotCold":
        return null;
      case "historySnapshot":
        return null;
      default:
        return null;
    }
  }
  const statCards=[
    {label:"Saved plays",value:plays.length,sub:materializedPlays.length+" manual"+(strategyPlays.length?" · "+strategyPlays.length+" strategies":""),color:"#002855"},
    {label:"Draws tracked",value:scoredDraws.length,sub:pendingPrizes?pendingPrizes+" pending prize update":selectedGameLabels,color:"#008577"},
    {label:"Recent hits",value:recentWins.length,sub:"3+ matches in recent history",color:"#c8860a"},
    {label:"Today's Lineup",value:lineupStats.count,sub:"$"+lineupStats.ticketCost+" est. · "+lineupStats.simulation+" simulation / "+lineupStats.real+" real",color:"#c8102e"},
    {label:"Next Locked Strategy",value:nextLockedStrategy?"Locked":"—",sub:nextLockedStrategy?strategyDisplayName(nextLockedStrategy.id,strategyTrust?.records?.[nextLockedStrategy.id]):"Lock one for tonight",color:"#008577"},
  ];
  if(loading)return <LoadingSkeleton/>;
  const dashboardLineupItems=normalizeLineup(todaysLineup).items.filter(item=>strategyExists(item.id,strategyTrust?.records||{},plays));
  const todayPlannedCost=dashboardLineupItems.reduce((sum,item)=>{
    const record=strategyRecordForId(item.id,strategyTrust?.records,plays);
    const nums=strategyNumbersForRecord(item.id,record,plays);
    return sum+strategyEstimatedCost(record,nums);
  },0);
  return(
    <div>
      <AddToHomeScreenPrompt user={user}/>
      <div className="sec-hdr" style={{alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Today's Lineup</span>
          <div style={{fontSize:14,color:"#555",marginTop:4}}>
            {dashboardLineupItems.length} strateg{dashboardLineupItems.length===1?"y":"ies"} • {moneyLabel(todayPlannedCost)} total planned cost
          </div>
        </div>
        <button type="button" className="btn btn-p" onClick={()=>navigate("/my-strategies")}>My Playslips</button>
      </div>
      <div className="launch-note" style={{marginBottom:18}}>Review your total before playing. LottoTracker tracks strategies and does not sell tickets or handle wagers.</div>
      <LatestOfficialResultsCard draws={draws} drawStatus={drawStatus}/>
      {!dashboardLineupItems.length?(
        <div className="panel" style={{textAlign:"center",padding:"48px 24px",border:"2px solid #dbeee9"}}>
          <div className="sec-title" style={{fontSize:34,marginBottom:8}}>Nothing in today's lineup yet.</div>
          <div style={{fontSize:15,color:"#555",lineHeight:1.55,maxWidth:620,margin:"0 auto 20px"}}>
            Choose a strategy to play today.
          </div>
          <button type="button" className="btn btn-p" onClick={()=>navigate("/my-strategies")}>Go to My Playslips</button>
        </div>
      ):(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
          {dashboardLineupItems.map(item=>{
            const record=strategyRecordForId(item.id,strategyTrust?.records,plays);
            const service=getDrawStatusService([record.game||"fantasy5"],new Date(),draws);
            const gameEntries=[...(service.entries||[]),...(service.archivedEntries||[])].filter(e=>e.game===(record.game||"fantasy5"));
            const selectedDrawIds=(Array.isArray(item.draws)?item.draws:[]).map(draw=>String(draw.drawId)).filter(Boolean);
            let selectedEntries=selectedDrawIds.length
              ?gameEntries.filter(entry=>selectedDrawIds.includes(String(drawLockIdForDraw(entry))))
              :gameEntries.filter(entry=>item.drawId&&String(drawLockIdForDraw(entry))===String(item.drawId));
            if(!selectedEntries.length)selectedEntries=gameEntries.filter(e=>e.sortAt>=Date.now()).slice(0,1);
            selectedEntries=selectedEntries.sort((a,b)=>(Number(a.sortAt)||0)-(Number(b.sortAt)||0));
            const pendingEntry=selectedEntries.find(e=>["Sales Closed","Drawing In Progress","Waiting for official results","Official results delayed"].includes(e.status));
            const nextOpenEntry=selectedEntries.find(e=>["Upcoming","Sales Closing Soon"].includes(e.status));
            const readyEntries=selectedEntries.filter(e=>e.status==="Results Ready"||e.status==="Archived");
            const primary=pendingEntry||nextOpenEntry||readyEntries[readyEntries.length-1]||selectedEntries[0]||drawStatus?.next;
            const statusLabel=pendingEntry
              ?(pendingEntry.status==="Waiting for official results"||pendingEntry.status==="Official results delayed"?"Results Pending":"Drawing Now")
              :readyEntries.length&&nextOpenEntry
                ?drawTimeLabel(readyEntries[readyEntries.length-1])+" Results Complete"
                :readyEntries.length&&readyEntries.length===selectedEntries.length
                  ?"Results Available"
                  :selectedEntries.length>1
                    ?"Playing Today ("+selectedEntries.map(drawTimeLabel).join(" + ")+")"
                    :"Playing Today";
            const countdownDisplay=pendingEntry
              ?(pendingEntry.status==="Waiting for official results"||pendingEntry.status==="Official results delayed"?"Results Pending":pendingEntry.countdown||"Drawing now")
              :nextOpenEntry
                ?nextOpenEntry.countdown
                :readyEntries.length?"Results Available":primary?.countdown||"Waiting";
            const resultsDisplay=selectedEntries.length
              ?selectedEntries.map(e=>drawTimeLabel(e)+": "+(e.status==="Waiting for official results"?"Results Pending":e.status==="Official results delayed"?"Results delayed":e.status==="Archived"?"Results Complete":e.status)).join(" · ")
              :primary?.status||"Upcoming";
            const strategyNumbers=strategyNumbersForRecord(item.id,record,plays);
            const count=strategyEstimatedCost(record,strategyNumbers);
            const canRemove=selectedEntries.some(e=>["Upcoming","Sales Closing Soon"].includes(e.status));
            return(
              <div key={item.id} className="sc-card">
                <div className="sec-badge" style={{background:primary?.status==="Results Ready"||primary?.status==="Archived"?"#D4AF37":statusLabel==="Drawing Now"||statusLabel==="Results Pending"?"#005bbb":"#008577",marginBottom:10}}>{statusLabel}</div>
                <div className="sc-name" style={{color:"#002855",marginBottom:8}}>{strategyDisplayName(item.id,record)}</div>
                <div style={{fontSize:13,color:"#555",fontWeight:900,textTransform:"uppercase",marginBottom:4}}>{gameLabel(record.game||"fantasy5")} • Today's Draw</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:42,fontWeight:900,color:"#002855",lineHeight:1,marginBottom:8}}>{countdownDisplay}</div>
                <div style={{display:"grid",gap:8,fontSize:13,color:"#555",lineHeight:1.35}}>
                  <div><strong style={{color:"#002855"}}>Game and draw:</strong> {selectedEntries.length?selectedEntries.map(e=>gameLabel(record.game||"fantasy5")+" "+drawTimeLabel(e)).join(" + "):gameLabel(record.game||"fantasy5")}</div>
                  <div><strong style={{color:"#002855"}}>Cost:</strong> {moneyLabel(count)} planned</div>
                  <div><strong style={{color:"#002855"}}>Results:</strong> {resultsDisplay}</div>
                  {selectedEntries.length>1&&selectedEntries.map(entry=>(
                    <div key={drawLockIdForDraw(entry)||entry.draw_date+"-"+entry.draw_time} style={{background:"#f8fbfa",border:"1px solid #edf1f0",borderRadius:8,padding:"7px 8px"}}>
                      <strong style={{color:"#002855"}}>{drawTimeLabel(entry)}:</strong> {entry.status==="Waiting for official results"?"Results Pending":entry.status==="Archived"?"Results Complete":entry.status} · {entry.status==="Upcoming"||entry.status==="Sales Closing Soon"?entry.countdown:entry.drawTime}
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:14}}>
                  <button type="button" className="btn btn-sm btn-p" onClick={()=>navigate("/results")}>Results</button>
                  {canRemove&&<button type="button" className="btn btn-sm btn-s" onClick={()=>onRemoveFromLineup?.(item.id)}>Remove From Today</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  if(beginnerDashboard&&lineupStats.count===0){
    return(
      <div>
        <AddToHomeScreenPrompt user={user}/>
        <div className="panel" style={{textAlign:"center",padding:"46px 24px",border:"2px solid #dbeee9",background:"linear-gradient(180deg,#ffffff,#f8fbfa)"}}>
          <div style={{width:150,height:150,borderRadius:"50%",background:"#e8f6f2",display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:18,boxShadow:"inset 0 0 0 12px #f8fbfa"}}>
            <div style={{fontSize:64}}>{"\uD83C\uDFB2"}</div>
          </div>
          <div className="sec-title" style={{fontSize:34,marginBottom:8}}>Nothing in today's lineup yet.</div>
          <div style={{fontSize:15,color:"#555",lineHeight:1.55,maxWidth:620,margin:"0 auto 20px"}}>
            Choose a strategy to watch or play today.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12,maxWidth:860,margin:"0 auto 22px",textAlign:"left"}}>
            {[
              ["1","Create a strategy","Name the numbers you want LottoTracker to follow.","/strategy-builder"],
              ["2","Add numbers","Use five simple boxes so spacing never gets in the way.","/strategy-builder"],
              ["3","Choose next step","Play today or keep it in My Playslips.","/strategy-builder"],
              ["4","View results","Come back after the draw to see what would have happened.","/results"],
            ].map(step=>(
              <button key={step[0]} type="button" onClick={()=>navigate(step[3])} style={{textAlign:"left",border:"1px solid #dbeee9",background:"#fff",borderRadius:12,padding:14,cursor:"pointer",boxShadow:"0 10px 24px rgba(0,40,85,.07)"}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:"#008577",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,marginBottom:10}}>{step[0]}</div>
                <div style={{fontWeight:900,color:"#002855",marginBottom:4}}>{step[1]}</div>
                <div style={{fontSize:12,color:"#555",lineHeight:1.35}}>{step[2]}</div>
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-p" style={{fontSize:18,padding:"13px 22px"}} onClick={()=>navigate("/my-strategies")}>Go to My Playslips</button>
        </div>
        {nextDraw&&(
          <div style={{marginTop:18}}>
            <NextDrawWidget myGames={myGames} activePlays={0} onViewResults={()=>navigate("/results")} drawStatus={drawStatus}/>
          </div>
        )}
      </div>
    );
  }
  return(
    <div>
      <TodayLotteryHub
        user={user}
        myGames={myGames}
        plays={plays}
        latestReady={latestReady}
        drawStatus={drawStatus}
        onAddNumbers={()=>navigate("/strategy-builder")}
        onQuickPick={()=>navigate("/strategy-builder")}
        onViewResults={()=>navigate("/results")}
        onPool={()=>navigate("/pool-simulation")}
        onScoreboard={()=>navigate("/scoreboard")}
        onMyNumbers={()=>navigate("/strategy-builder")}
        onStrategyLibrary={()=>navigate("/my-strategies")}
        onStrategyBuilder={()=>navigate("/strategy-builder")}
        onCommunity={()=>navigate("/community")}
        onCreatePool={()=>navigate("/pools")}
        simpleMode={beginnerDashboard}
      />
      <AddToHomeScreenPrompt user={user}/>
      <div className="sec-hdr" style={{alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span className="sec-title">My Dashboard</span>
        <button type="button" className="btn btn-s" onClick={()=>setCustomizing(v=>!v)}>⚙ Customize</button>
      </div>
      {customizing&&<CustomizeDashboard config={dashboardConfig} onChange={saveDashboardConfig} onClose={()=>setCustomizing(false)}/>}
      <div className="hero">
        <div style={{position:"relative",zIndex:1,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:24,alignItems:"center"}}>
          <div>
            <div className="hero-sub">Your LottoTracker Dashboard</div>
            <div className="hero-title">Welcome back, {firstName}</div>
            <p style={{color:"rgba(255,255,255,.78)",fontSize:15,lineHeight:1.55,maxWidth:680,marginBottom:20}}>
              Save your numbers once. LottoTracker watches every draw for you.
            </p>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button type="button" className="btn btn-p" onClick={()=>navigate("/strategy-builder")}>Create Strategy</button>
              <button type="button" className="btn btn-s" onClick={()=>navigate("/my-strategies")}>My Playslips</button>
              <button type="button" className="btn btn-s" onClick={()=>navigate("/results")}>View Results</button>
              {!beginnerDashboard&&<button type="button" className="btn btn-s" onClick={()=>navigate("/lineup")}>Today's Lineup</button>}
            </div>
          </div>
        </div>
      </div>
      <div style={{
        background:"#fff7e6",border:"1px solid #ffd54f",borderRadius:10,
        padding:"10px 14px",marginBottom:20,fontSize:12,color:"#5a4a00",
        display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"
      }}>
        <span style={{fontSize:18}}>💡</span>
        <span><strong>This is a simulation — not a real lottery.</strong> LottoTracker records actual lottery draws for your selected games and shows what your saved numbers <em>would have won</em>. No tickets are purchased, no money changes hands.</span>
      </div>
      <MilestoneStrip plays={publicPlays} draws={scoredDraws} recentWins={recentWins} topStrategy={topStrategy} storageKey={"lt:milestones:"+(user?.id||user?.email||"local")}/>
      {loading&&<LoadingSkeleton/>}
      {!loading&&draws.length===0&&(
        <div className="panel" style={{textAlign:"center",padding:"48px 20px"}}>
          <div style={{fontSize:40,marginBottom:10}}>🎟️</div>
          <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>Your dashboard is ready</div>
          <div style={{fontSize:13,color:"#555"}}>Results will appear automatically after your first tracked draw. Create a strategy and LottoTracker will keep watch.</div>
          <button type="button" className="btn btn-p" style={{marginTop:16}} onClick={()=>navigate("/strategy-builder")}>Create Strategy</button>
        </div>
      )}
      {!loading&&draws.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:24}}>
          {visibleDashboardIds.map(id=>(
            <div key={id} style={{minWidth:0}}>
              {renderDashboardWidget(id)}
            </div>
          ))}
        </div>
      )}
      {!loading&&draws.length>0&&false&&<>
        <div className="sc-grid" style={{marginBottom:24}}>
          {statCards.map(card=>(
            <div key={card.label} className="sc-card">
              <div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:600,letterSpacing:1,marginBottom:6}}>{card.label}</div>
              <div className="sc-roi" style={{color:card.color,marginBottom:2}}><AnimatedMetric value={card.value}/></div>
              <div style={{fontSize:12,color:"#555"}}>{card.sub}</div>
            </div>
          ))}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:24}}>
          <DailyStreakCard streak={dailyStreak}/>
          <DailyGoalCard
            plays={plays}
            latestReady={latestReady}
            featureUnlocks={featureUnlocks}
            onDraw={()=>navigate("/results")}
            onNumbers={()=>navigate("/my-strategies")}
            onScoreboard={()=>navigate("/scoreboard")}
            onPool={()=>navigate("/pool-simulation")}
          />
          <ProgressBadges plays={plays} draws={scoredDraws} featureUnlocks={featureUnlocks}/>
          <DashboardTip/>
          <WeeklySummary draws={scoredDraws} topStrategy={topStrategy} recentWins={recentWins}/>
          <AchievementTimeline plays={plays} draws={scoredDraws} recentWins={recentWins}/>
          <div className="panel" style={{marginBottom:0}}>
            <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Today's Snapshot</div>
            {bestRecent?(
              <div>
                <div style={{fontSize:13,color:"#555",marginBottom:10}}>Your latest simulated 3+ match:</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>{bestRecent.d.numbers.map((n,i)=><Ball key={i} n={n} size="sm"/>)}</div>
                <div style={{fontWeight:700,color:"#002855"}}>{bestRecent.best} Match · {bestRecent.d.date}</div>
              </div>
            ):(
              <div style={{fontSize:13,color:"#555",lineHeight:1.45}}>No 3-match milestone yet. Keep your numbers saved and LottoTracker will check every draw for you.</div>
            )}
          </div>
          <TodaySchedule myGames={myGames} drawStatus={drawStatus}/>
          <NextBestStep
            plays={plays}
            latestReady={latestReady}
            isFree={!subscriptionHas(subscription,"pro")}
            hasStrategy={hasStrategy}
            onAdd={()=>navigate("/my-strategies")}
            onResults={()=>navigate("/results")}
            onUpgrade={onUpgrade}
            onStrategy={()=>navigate("/strategy-builder")}
          />
          <RecentActivityPreview onViewAll={()=>navigate("/activity")} notifications={activityNotifications}/>
        </div>

        {recentWins.length>0&&(
          <>
            <div className="sec-hdr"><span className="sec-title">Recent Hits</span><span className="sec-badge">Simulated · Matched 3+</span></div>
            <div className="draws-grid">
              {recentWins.map(({d,best},i)=>{
                const pr=getDrawPrize(d,best);
                return(
                  <div key={i} className={"draw-card"+(sel==="w"+i?" sel":"")} onClick={()=>toggle("w"+i)}>
                    <div className="dc-head"><div className="dc-date">{d.date}</div><div className="dc-type">{d.isEvening?"Evening":"Midday"}</div></div>
                    <div className="dc-body">
                      <div className="dc-balls">{d.numbers.map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div>
                      {pr&&<div className="dc-badge" style={{background:pr.color+"25",color:pr.color}}>{best} Match &middot; {pr.display}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {sel&&sel.startsWith("w")&&recentWins[parseInt(sel.slice(1))]&&<MatchPanel draw={recentWins[parseInt(sel.slice(1))].d} plays={plays} lineup={todaysLineup} strategyTrust={strategyTrust}/>}
          </>
        )}
        <div className="sec-hdr"><span className="sec-title">Recent Draws</span><span className="sec-badge">Fantasy 5</span></div>
        <div className="draws-grid">
          {draws.slice(0,14).map((d,i)=>{
            const best=materializedPlays.length?Math.max(0,...materializedPlays.map(pl=>cntMatch(pl.numbers,d.numbers))):0;
            const pr=best>=3?getDrawPrize(d,best):null;
            return(
              <div key={i} className={"draw-card"+(sel===i?" sel":"")} onClick={()=>toggle(i)}>
                <div className="dc-head"><div className="dc-date">{d.date}</div><div className="dc-type">{d.isEvening?"Evening":"Midday"}</div></div>
                <div className="dc-body">
                  <div className="dc-balls">{d.numbers.map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div>
                  <div className="dc-prize"><span>Jackpot</span><strong>{jackpotAmountText(d)}</strong></div>
                  {pr&&<div className="dc-badge" style={{background:pr.color+"25",color:pr.color}}>{best} Match &middot; {pr.display}</div>}
                </div>
              </div>
            );
          })}
        </div>
        {typeof sel==="number"&&draws[sel]&&<MatchPanel draw={draws[sel]} plays={plays} lineup={todaysLineup} strategyTrust={strategyTrust}/>}
      </>}
    </div>
  );
}

function MyNumbersTab({plays,onAdd,onAddBatch,onDelete,onDeleteAll,latestDraw,toast,planTier,onUpgrade,drawStatus}){
  const navigate=useNavigate();
  const inputRef=useRef(null);
  const[numberBoxes,setNumberBoxes]=useState(()=>numberBoxValuesFromNumbers([]));
  const[err,setErr]=useState("");
  const[confirmAll,setConfirmAll]=useState(false);
  // Play-Lock + Target Count state. Checkboxes gate which existing plays
  // the user wants to keep; the fill button tops the selected set up to
  // the target with fresh quick picks. Additive -- unchecked plays remain
  // in the list untouched, so final row count can exceed the target.
  const[selectedIds,setSelectedIds]=useState(()=>new Set());
  const[targetFill,setTargetFill]=useState("100");
  const[fillErr,setFillErr]=useState("");
  const[filling,setFilling]=useState(false);
  const limitLabel="Unlimited";
  function doAdd(){
    setErr("");
    const checked=validateNumberBoxes(numberBoxes);
    if(checked.error){setErr(checked.error);return;}
    const sorted=checked.numbers;
    if(plays.some(p=>Array.isArray(p.numbers)&&p.numbers.join(",")===sorted.join(","))){setErr("That play already exists");return;}
    onAdd(sorted);
    setNumberBoxes(numberBoxValuesFromNumbers([]));
  }
  function doQP(){
    onAdd(rndPick([]));
  }
  // --- Play-Lock + Fill handlers --------------------------------------------
  function toggleSelected(id){
    setSelectedIds(prev=>{
      const next=new Set(prev);
      if(next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setFillErr("");
  }
  async function doFill(){
    setFillErr("");
    let target=parseInt(targetFill,10);
    if(!Number.isInteger(target)||target<1){
      setFillErr("Target must be a number between 1 and 500");return;
    }
    let capped=false;
    if(target>500){target=500;capped=true;toast("Capped at 500 -- that's the max fill size.");}
    // Only count checked plays that actually exist (materialized 5-number rows).
    const validSelected=[...selectedIds].filter(id=>plays.some(p=>p.id===id&&Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT));
    const checkedCount=validSelected.length;
    if(target<=checkedCount){
      setFillErr("Target must be greater than number of locked plays ("+checkedCount+")");return;
    }
    let need=target-checkedCount;
    // Collision avoidance: serialize existing plays into a Set keyed on sorted
    // number tuple. Rejection-sample quick picks against it. C(36,5)=376,992
    // so with ~100s of existing plays collisions are vanishingly rare; the
    // attempt cap + "allow" fallback per spec covers the pathological case.
    const existing=new Set(
      plays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT)
        .map(p=>[...p.numbers].sort((a,b)=>a-b).join(","))
    );
    const additions=[];
    const maxTries=need*50+100;
    for(let t=0;t<maxTries&&additions.length<need;t++){
      const candidate=rndPick([]);
      const key=candidate.join(","); // rndPick already returns sorted
      if(existing.has(key)) continue;
      existing.add(key);
      additions.push(candidate);
    }
    // Fallback per spec: if the unique-QP search exhausted, allow duplicates.
    while(additions.length<need){
      additions.push(rndPick([]));
    }
    setFilling(true);
    try{
      const msg="Added "+additions.length+" Quick Pick play"+(additions.length===1?"":"s")+
        (capped?" (target capped at 500)":"")+" -- "+checkedCount+" checked + "+additions.length+" new";
      await onAddBatch(additions,msg);
    }finally{setFilling(false);}
  }
  return(
    <div>
      <div className="panel">
        <div className="sec-hdr">
          <span className="sec-title">My Playslips</span>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:13,color:"#555"}}>{plays.length} / {limitLabel}</span>
            {!isPro&&<button className="btn btn-sm btn-p" onClick={onUpgrade}>Upgrade</button>}
            {plays.length>0&&<button className="btn btn-sm" style={{background:"#fee2e2",color:"#c8102e",border:"none",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}} onClick={()=>setConfirmAll(true)}>Delete All</button>}
          </div>
        </div>
        {confirmAll&&(
          <div style={{background:"#fff3cd",border:"1px solid #ffc107",borderRadius:10,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontWeight:600,fontSize:13}}>⚠️ Delete ALL {plays.length} plays? This cannot be undone.</span>
            <button className="btn btn-sm" style={{background:"#c8102e",color:"#fff",border:"none",borderRadius:8,padding:"5px 14px",cursor:"pointer"}} onClick={()=>{onDeleteAll();setConfirmAll(false);}}>Yes, Delete All</button>
            <button className="btn btn-sm" style={{background:"#f0f0f0",color:"#333",border:"none",borderRadius:8,padding:"5px 14px",cursor:"pointer"}} onClick={()=>setConfirmAll(false)}>Cancel</button>
          </div>
        )}
        {drawStatus?.next?.status==="Results Ready"&&(
          <NextStepCard
            title="Results Ready"
            text="Your saved plays are highlighted against the latest draw. Open Results Center to review matches from best to worst."
            actions={[{label:"View Results",onClick:()=>navigate("/results")},{label:"My Playslips",onClick:()=>navigate("/my-strategies")}]}
          />
        )}
        {drawStatus?.next?.status==="Sales Closing Soon"&&(
          <NextStepCard
            title="Sales Closing Soon"
            text={drawStatus.next.gameLabel+" "+drawStatus.next.drawLabel+" closes soon. Add numbers or quick pick before the window closes."}
            actions={[{label:"Create Strategy",onClick:()=>inputRef.current?.querySelector("input")?.focus()},{label:"Quick Pick",onClick:doQP}]}
          />
        )}
        <div className="add-row">
          <div ref={inputRef}>
            <NumberBoxes values={numberBoxes} onChange={values=>{setNumberBoxes(values);setErr("");}}/>
          </div>
          <button className="btn btn-p" onClick={doAdd}>Add</button>
          <button className="btn btn-s" onClick={doQP}>Quick Pick</button>
        </div>

        {/* Fill with Quick Pick — select existing plays via the row checkboxes
            below, then fill to a target count with fresh quick picks. Additive:
            unchecked plays stay in the list, so the final total can exceed the
            target. The per-row checkboxes live on each .play-row further down. */}
        {(() => {
          const materializedCount=plays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).length;
          const validSelected=[...selectedIds].filter(id=>plays.some(p=>p.id===id&&Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT));
          const checkedCount=validSelected.length;
          return (
            <div style={{border:"1px solid #e0e0e0",borderRadius:10,padding:"14px 16px",marginBottom:14,background:"#fafcfb"}}>
              <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:6}}>
                <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1}}>Fill with Quick Pick</div>
                <div style={{fontSize:11,color:"#888"}}>{checkedCount} of {materializedCount} play{materializedCount===1?"":"s"} selected</div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                <label style={{fontSize:12,color:"#555"}}>Fill to</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={targetFill}
                  onChange={e=>{setTargetFill(e.target.value);setFillErr("");}}
                  onKeyDown={e=>e.key==="Enter"&&doFill()}
                  style={{width:80,border:"1.5px solid #ccc",borderRadius:8,padding:"8px 10px",fontSize:14,fontFamily:"inherit",textAlign:"center"}}
                  aria-label="Target number of plays"
                />
                <span style={{fontSize:12,color:"#555"}}>plays</span>
                <button
                  type="button"
                  className={"btn btn-sm btn-p "+(filling?"loading":"")}
                  onClick={doFill}
                  disabled={filling||!onAddBatch}
                >
                  {filling?"Adding…":"Fill with Quick Pick"}
                </button>
                {checkedCount>0&&(
                  <button
                    type="button"
                    className="btn btn-sm btn-s"
                    onClick={()=>setSelectedIds(new Set())}
                    style={{marginLeft:"auto"}}
                  >
                    Clear selection
                  </button>
                )}
              </div>
              {fillErr&&<p style={{color:"var(--red)",fontSize:12,marginTop:8,fontWeight:600}}>{fillErr}</p>}
            </div>
          );
        })()}

        {err&&<p style={{color:"var(--red)",fontSize:12,marginBottom:12,fontWeight:600}}>{err}</p>}
        {latestDraw&&(
          <div style={{background:"#f0f8f6",borderRadius:10,padding:"8px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:12,fontWeight:600,color:"var(--teal)",textTransform:"uppercase"}}>Latest:</span>
            {latestDraw.numbers.map((n,i)=><Ball key={i} n={n} size="sm"/>)}
            <span style={{fontSize:11,color:"#555"}}>{latestDraw.date}</span>
          </div>
        )}
        {plays.length===0&&(
          <EmptyState
            icon="🎲"
            title="You haven't added any numbers yet."
            text="Start with your own favorite numbers, or let Quick Pick create your first saved play."
            actions={[
              {label:"Create Strategy",onClick:()=>inputRef.current?.querySelector("input")?.focus()},
              {label:"Quick Pick",onClick:doQP},
            ]}
          />
        )}
        {/* MyNumbers only renders materialized 5-number plays. Anchor recipe
            rows live on the Anchors tab (shown under "Saved sets") — surfacing
            them here without their expanded combos would just look broken. */}
        {plays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).map((pl,i)=>{
          const m=latestDraw?cntMatch(pl.numbers,latestDraw.numbers):0;
          const pr=latestDraw?getDrawPrize(latestDraw,m):null;
          const checked=selectedIds.has(pl.id);
          return(
            <div key={pl.id||i} className="play-row">
              <input
                type="checkbox"
                checked={checked}
                onChange={()=>toggleSelected(pl.id)}
                style={{marginRight:4,cursor:"pointer",width:16,height:16,accentColor:"var(--teal)"}}
                aria-label={`Select play #${i+1}`}
              />
              <span className="play-num">#{i+1}</span>
              <div className="play-balls">
                {pl.numbers.map((n,j)=>(
                  <Ball key={j} n={n} size="sm" matched={latestDraw&&latestDraw.numbers.includes(n)}/>
                ))}
              </div>
              <span className="play-mi" style={{color:pr?pr.color:"#999"}}>{pr?m+" Match · "+pr.display:m>0?m+" match":""}</span>
              <button className="play-del" onClick={()=>onDelete(pl.id)}>✕</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StratCard({def,draws,data,savedAnchors,onSave,onPersist,onDeleteSaved,trustRecord,drawStatus,onLock}){
  const[anchors,setAnchors]=useState(data.anchors||[]);
  const[ainp,setAinp]=useState((data.anchors||[]).join(" "));
  const[gen,setGen]=useState(data.generated||[]);
  const[generating,setGenerating]=useState(false);
  const[saving,setSaving]=useState(false);
  const[saveErr,setSaveErr]=useState("");

  useEffect(()=>{ if(data.anchors?.length) setAinp(data.anchors.join(" ")); },[]);

  function parseAnchors(v){
    return v.split(/[\s,]+/).map(x=>parseInt(x,10)).filter(n=>!isNaN(n)&&n>=1&&n<=MAX_NUM).slice(0,def.ac);
  }
  function generate(){
    const parsed=parseAnchors(ainp);
    if(parsed.length!==def.ac){return;}
    setAnchors(parsed);
    setGenerating(true);
    setSaveErr("");
    setTimeout(()=>{
      const plays=genAllCombos(parsed,BALL_COUNT);
      setGen(plays);
      onSave({anchors:parsed,generated:plays});
      setGenerating(false);
    },50);
  }
  async function save(){
    if(anchors.length!==def.ac) return;
    setSaving(true);
    setSaveErr("");
    const r=await onPersist(anchors);
    if(r&&r.error) setSaveErr(r.error);
    setSaving(false);
  }

  const latest=draws[0];
  const anchorSet=new Set(anchors);
  const currentKey=[...anchors].sort((a,b)=>a-b).join(",");
  const alreadySaved=(savedAnchors||[]).some(s=>s.anchors.join(",")===currentKey);
  const statusMeta=strategyTrustStatusMeta(trustRecord?.status);
  const lockedActive=!strategyCanModify(trustRecord,drawStatus);
  return(
    <div className="strat-card">
      <div className="strat-head" style={{background:def.color}}>
        <div className="strat-name">{def.name}</div>
        <div className="strat-desc">{def.desc}</div>
      </div>
      <div className="strat-body">
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:10}}>
            <span className="sec-badge" style={{background:statusMeta.color}}>{statusMeta.icon} {statusMeta.label}</span>
            {trustRecord?.status==="Locked"&&<span style={{fontSize:11,color:"#555",fontWeight:800}}>{trustRecord.lockedBeforeClose?"Locked before draw":"Sales Closed"}</span>}
          </div>
          <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",marginBottom:6}}>Your {def.ac} Anchor Numbers</div>
          {anchors.length>0&&<div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap"}}>
            {anchors.map((n,i)=><Ball key={i} n={n} size="sm" locked={true}/>)}
          </div>}
          <div style={{display:"flex",gap:6}}>
            <input style={{flex:1,border:"1.5px solid #ddd",borderRadius:8,padding:"6px 10px",fontSize:13}} placeholder={"Enter "+def.ac+" numbers e.g. 3 9 14"} value={ainp} onChange={e=>setAinp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&generate()}/>
          </div>
        </div>
        <button className="btn btn-sm btn-p" onClick={generate} disabled={generating} style={{width:"100%",marginBottom:8}}>
          {generating?"Generating...":"Generate All "+(def.ac===3?"528":"32")+" Plays"}
        </button>
        <button
          className={"btn btn-sm "+(alreadySaved?"btn-s":"btn-p")}
          onClick={save}
          disabled={saving||alreadySaved||anchors.length!==def.ac||lockedActive}
          style={{width:"100%",marginBottom:10,background:alreadySaved?"#e8f5e9":undefined,color:alreadySaved?"#2E7D32":undefined}}
        >
          {alreadySaved?"✓ Saved to Scoreboard":saving?"Saving...":"Save to Scoreboard"}
        </button>
        {savedAnchors?.length>0&&drawIsBeforeClose(drawStatus)&&trustRecord?.status!=="Locked"&&(
          <button type="button" className="btn btn-sm btn-s" onClick={()=>onLock(def.id,"tonight")} style={{width:"100%",marginBottom:10}}>Lock For Tonight</button>
        )}
        {lockedActive&&<div style={{fontSize:11,color:"#008577",fontWeight:800,marginBottom:8}}>Strategy Locked until this draw finishes.</div>}
        {saveErr&&<div style={{fontSize:11,color:"#c8102e",marginBottom:8}}>{saveErr}</div>}

        {/* Previously-saved anchor sets for this strategy, with quick remove. */}
        {savedAnchors&&savedAnchors.length>0&&(
          <div style={{background:"#f0f8f6",borderRadius:8,padding:"8px 10px",marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#0a4f3d",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Saved sets</div>
            {savedAnchors.map(s=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0"}}>
                <div style={{display:"flex",gap:3,flex:1,flexWrap:"wrap"}}>
                  {s.anchors.map((n,i)=><Ball key={i} n={n} size="sm" locked={true}/>)}
                </div>
                <button onClick={()=>onDeleteSaved(s.id)} title="Remove saved set" style={{background:"none",border:"none",color:"#999",fontSize:14,cursor:"pointer",padding:"2px 6px"}}>✕</button>
              </div>
            ))}
          </div>
        )}

        {gen.length>0&&<div style={{fontSize:11,color:"#555",marginBottom:8}}>{gen.length} plays — anchor numbers 🔒 appear on every slip</div>}
        <div className="strat-plays">
          {!gen.length&&<p style={{fontSize:12,color:"#aaa"}}>Enter your {def.ac} anchor numbers above, then click Generate.</p>}
          {gen.map((pl,i)=>{
            const m=latest?cntMatch(pl,latest.numbers):0;
            return(
              <div key={i} className="strat-row">
                {pl.map((n,j)=><Ball key={j} n={n} size="sm" matched={latest&&latest.numbers.includes(n)} locked={anchorSet.has(n)}/>)}
                {m>=3&&<span style={{marginLeft:"auto",fontSize:10,fontWeight:700,color:def.color,flexShrink:0}}>{m}✓</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StratsTab({draws,strats,plays,onSave,onPersistAnchors,onDeleteAnchor,strategyTrust,drawStatus,onLockStrategy}){
  // Saved anchor recipes, keyed by strategy, so each StratCard knows whether
  // its current input has already been persisted. Matches by sorted anchor
  // set (order-invariant).
  const savedByStrategy={};
  for(const p of (plays||[])){
    if(!Array.isArray(p.anchor_numbers)||p.anchor_numbers.length===0) continue;
    if(!savedByStrategy[p.strategy]) savedByStrategy[p.strategy]=[];
    savedByStrategy[p.strategy].push({id:p.id,anchors:[...p.anchor_numbers].sort((a,b)=>a-b)});
  }
  return(
    <div>
      <div className="sec-hdr"><span className="sec-title">Anchors</span><span className="sec-badge">2 Strategies</span></div>
      <div className="strat-grid">
        {STRAT_DEFS.map(def=>(
          <StratCard
            key={def.id}
            def={def}
            draws={draws}
            data={strats[def.id]||{anchors:[],generated:[]}}
            savedAnchors={savedByStrategy[def.id]||[]}
            onSave={dt=>onSave(def.id,dt)}
            onPersist={anchors=>onPersistAnchors(def.id,anchors)}
            onDeleteSaved={id=>onDeleteAnchor(id)}
            trustRecord={strategyTrust?.records?.[def.id]}
            drawStatus={drawStatus}
            onLock={onLockStrategy}
          />
        ))}
      </div>
    </div>
  );
}

const STRATEGY_SOURCE_OPTIONS=["Manual","Quick Pick"];
const STRATEGY_NAME_EXAMPLES=["Eric Favorites","Birthday Numbers","Quick Pick Test","4 Wheel","Summer Strategy","Lucky 7","Vacation Numbers"];
const STRATEGY_PLAY_SIZES=[5,10,25,50,100];
function strategySlug(name){
  const slug=String(name||"strategy").toLowerCase().replace(/[^a-z0-9]+/g,"_").replace(/^_+|_+$/g,"");
  return "builder_"+(slug||"strategy");
}
function uniqueStrategyId(name,records){
  const base=strategySlug(name);
  let id=base;
  let i=2;
  while(records?.[id]){id=base+"_v"+i;i++;}
  return id;
}
function buildStrategyPreview(source,count,plays,draws){
  const total=Math.max(1,Math.min(100,Number(count)||5));
  const recent=(draws||[]).filter(d=>Array.isArray(d.numbers)).slice(0,20);
  const freq={};
  recent.forEach(d=>d.numbers.forEach(n=>{freq[n]=(freq[n]||0)+1;}));
  const ranked=Array.from({length:MAX_NUM},(_,i)=>i+1).sort((a,b)=>(freq[b]||0)-(freq[a]||0)||a-b);
  const hot=ranked.slice(0,12);
  const cold=[...ranked].reverse().slice(0,12);
  const favorites=(plays||[]).filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT).flatMap(p=>p.numbers).slice(0,18);
  const birthday=[1,2,3,4,5,7,8,9,10,11,12,14,16,18,21,23,24,28,31].filter(n=>n<=MAX_NUM);
  function pickFrom(seed){
    const preferred=seed.filter((n,i,a)=>n>=1&&n<=MAX_NUM&&a.indexOf(n)===i);
    const nums=[];
    preferred.forEach(n=>{if(nums.length<BALL_COUNT&&!nums.includes(n))nums.push(n);});
    while(nums.length<BALL_COUNT){
      const n=rndPick(nums)[0];
      if(!nums.includes(n))nums.push(n);
    }
    return nums.sort((a,b)=>a-b);
  }
  return Array.from({length:total},(_,i)=>{
    if(source==="Birthday Numbers")return pickFrom([...birthday.slice(i,i+5),...birthday]);
    if(source==="Favorites")return favorites.length?pickFrom([...favorites.slice(i,i+5),...favorites]):rndPick([]);
    if(source==="Hot Numbers")return pickFrom([...hot.slice(i%hot.length),...hot]);
    if(source==="Cold Numbers")return pickFrom([...cold.slice(i%cold.length),...cold]);
    if(source==="Mixed")return pickFrom([...hot.slice(0,3),...cold.slice(0,3),...favorites.slice(0,3),...birthday.slice(i,i+3)]);
    return rndPick([]);
  });
}
const PICK_PLAY_TYPES={
  pick2:["Straight","Box","Straight/Box","Front Number","Back Number"],
  pick3:["Straight","Box","Straight/Box","Combo","Front Pair","Mid Pair","Back Pair"],
  pick4:["Straight","Box","Straight/Box","Combo"],
  pick5:["Straight","Box","Straight/Box"],
};
const PICK_STRAIGHT_PRIZES={pick2:50,pick3:500,pick4:5000,pick5:50000};
const PICK_BOX_PRIZES={
  pick2:{2:25},
  pick3:{3:160,6:80},
  pick4:{4:1200,6:800,12:400,24:200},
  pick5:{5:10000,10:5000,20:2500,30:1660,60:830,120:416},
};
const PICK_FIREBALL_STRAIGHT_PRIZES={pick2:30,pick3:200,pick4:1500,pick5:12000};
const PICK_FIREBALL_BOX_PRIZES={
  pick2:{2:15},
  pick3:{3:68,6:34},
  pick4:{4:390,6:260,12:126,24:60},
  pick5:{5:2400,10:1200,20:600,30:400,60:200,120:100},
};
const PICK_FRONT_BACK_PRIZES={pick2:5};
const PICK_FIREBALL_FRONT_BACK_PRIZES={pick2:3};
const PICK_PAIR_PRIZES={pick3:50};
const PICK_FIREBALL_PAIR_PRIZES={pick3:20};
function isPickGame(game){return PICK_GAME_IDS.includes(game);}
function factorial(n){let out=1;for(let i=2;i<=n;i++)out*=i;return out;}
function permutationCount(nums){
  const counts={};
  (nums||[]).forEach(n=>{counts[n]=(counts[n]||0)+1;});
  return Object.values(counts).reduce((ways,count)=>ways/factorial(count),factorial((nums||[]).length));
}
function moneyLabel(value){
  const n=Number(value)||0;
  return "$"+n.toLocaleString(undefined,{minimumFractionDigits:n%1?2:0,maximumFractionDigits:2});
}
function pickGameRules(game,numbers,playType,wager,playCount,fireball){
  if(!isPickGame(game))return null;
  const cfg=gameNumberConfig(game);
  const clean=(numbers||[]).map(n=>Number(n));
  const complete=clean.length===cfg.count&&clean.every(n=>Number.isInteger(n)&&n>=0&&n<=9);
  const ways=complete?permutationCount(clean):0;
  const validBox=ways>1;
  const baseWager=Number(wager)||1;
  const type=playType||"Straight";
  const ticketWager=type==="Straight/Box"?Math.max(1,baseWager):baseWager;
  const splitWager=type==="Straight/Box"?ticketWager/2:ticketWager;
  const plays=Math.max(1,Math.min(100,Number(playCount)||1));
  let units=1;
  if(type==="Combo")units=Math.max(1,ways);
  const baseCost=ticketWager*units*plays;
  const cost=baseCost*(fireball?2:1);
  const straightPrize=(PICK_STRAIGHT_PRIZES[game]||0)*splitWager*plays;
  const boxBase=validBox?(PICK_BOX_PRIZES[game]?.[ways]||0):0;
  const boxPrize=boxBase*splitWager*plays;
  const comboPrize=(PICK_STRAIGHT_PRIZES[game]||0)*ticketWager*plays;
  const fireballStraightPrize=(PICK_FIREBALL_STRAIGHT_PRIZES[game]||0)*splitWager*plays;
  const fireballBoxPrize=(validBox?(PICK_FIREBALL_BOX_PRIZES[game]?.[ways]||0):0)*splitWager*plays;
  const fireballComboPrize=(PICK_FIREBALL_STRAIGHT_PRIZES[game]||0)*ticketWager*plays;
  const frontBackPrize=(PICK_FRONT_BACK_PRIZES[game]||0)*ticketWager*plays;
  const fireballFrontBackPrize=(PICK_FIREBALL_FRONT_BACK_PRIZES[game]||0)*ticketWager*plays;
  const pairPrize=(PICK_PAIR_PRIZES[game]||0)*ticketWager*plays;
  const fireballPairPrize=(PICK_FIREBALL_PAIR_PRIZES[game]||0)*ticketWager*plays;
  const payouts=[];
  if(type==="Straight")payouts.push({label:"Straight",value:straightPrize});
  if(type==="Box")payouts.push({label:validBox?ways+"-Way Box":"Box",value:validBox?boxPrize:0,disabled:!validBox});
  if(type==="Straight/Box"){
    payouts.push({label:"Straight",value:straightPrize});
    payouts.push({label:validBox?ways+"-Way Box":"Box",value:validBox?boxPrize:0,disabled:!validBox});
  }
  if(type==="Combo")payouts.push({label:validBox?ways+" Exact Orders":"Combo",value:validBox?comboPrize:0,disabled:!validBox});
  if(type==="Front Number")payouts.push({label:"Front Number",value:frontBackPrize});
  if(type==="Back Number")payouts.push({label:"Back Number",value:frontBackPrize});
  if(type==="Front Pair")payouts.push({label:"Front Pair",value:pairPrize});
  if(type==="Mid Pair")payouts.push({label:"Mid Pair",value:pairPrize});
  if(type==="Back Pair")payouts.push({label:"Back Pair",value:pairPrize});
  if(fireball){
    if(type==="Straight")payouts.push({label:"Fireball Straight",value:fireballStraightPrize,fireball:true});
    if(type==="Box")payouts.push({label:validBox?"Fireball "+ways+"-Way Box":"Fireball Box",value:validBox?fireballBoxPrize:0,disabled:!validBox,fireball:true});
    if(type==="Straight/Box"){
      payouts.push({label:"Fireball Straight",value:fireballStraightPrize,fireball:true});
      payouts.push({label:validBox?"Fireball "+ways+"-Way Box":"Fireball Box",value:validBox?fireballBoxPrize:0,disabled:!validBox,fireball:true});
    }
    if(type==="Combo")payouts.push({label:validBox?"Fireball "+ways+" Exact Orders":"Fireball Combo",value:validBox?fireballComboPrize:0,disabled:!validBox,fireball:true});
    if(type==="Front Number")payouts.push({label:"Fireball Front Number",value:fireballFrontBackPrize,fireball:true});
    if(type==="Back Number")payouts.push({label:"Fireball Back Number",value:fireballFrontBackPrize,fireball:true});
    if(type==="Front Pair")payouts.push({label:"Fireball Front Pair",value:fireballPairPrize,fireball:true});
    if(type==="Mid Pair")payouts.push({label:"Fireball Mid Pair",value:fireballPairPrize,fireball:true});
    if(type==="Back Pair")payouts.push({label:"Fireball Back Pair",value:fireballPairPrize,fireball:true});
  }
  return{
    game,
    complete,
    ways,
    playType:type,
    wager:ticketWager,
    playCount:plays,
    fireball:!!fireball,
    cost,
    costLabel:moneyLabel(cost),
    baseCostLabel:moneyLabel(baseCost),
    payouts,
    topPotential:Math.max(0,...payouts.map(p=>p.value||0)),
    note:!complete
      ?"Enter all "+cfg.count+" digits to calculate this Pick play."
      :!validBox&&["Box","Straight/Box","Combo"].includes(type)
        ?"Box and Combo need at least two different digits."
        :fireball
          ?"Fireball doubles the ticket cost and adds official Fireball winning combinations after the draw."
          :"Estimated from Florida Lottery fixed Pick payout tables.",
  };
}
function pickStrategyRulesFromPreview(game,preview,playType,wager,fireball,multiple=1){
  if(!isPickGame(game))return null;
  const plays=(preview||[])
    .map(entry=>previewEntryNumbers(entry))
    .filter(nums=>Array.isArray(nums)&&nums.length===gameNumberConfig(game).count);
  const times=Math.max(1,Math.min(100,Number(multiple)||1));
  const parts=(preview||[])
    .map(entry=>{
      const nums=previewEntryNumbers(entry);
      if(!Array.isArray(nums)||nums.length!==gameNumberConfig(game).count)return null;
      const rules=previewEntryPickRules(game,entry,{playType,wager,multiple:times,fireball});
      return pickGameRules(game,nums,rules.playType,rules.wager,rules.multiple,rules.fireball);
    })
    .filter(Boolean);
  const cost=parts.reduce((sum,part)=>sum+(Number(part.cost)||0),0);
  const topPotential=Math.max(0,...parts.map(part=>Number(part.topPotential)||0));
  const ruleKeys=new Set(parts.map(part=>[part.playType,part.wager,part.playCount,part.fireball].join("|")));
  const mixed=ruleKeys.size>1;
  return{
    game,
    playType:mixed?"Mixed":(parts[0]?.playType||playType||"Straight"),
    wager:mixed?null:(Number(parts[0]?.wager??wager)||1),
    playCount:parts.reduce((sum,part)=>sum+(Number(part.playCount)||1),0),
    multiple:mixed?null:times,
    fireball:mixed?parts.some(part=>part.fireball):!!fireball,
    mixed,
    cost,
    costLabel:moneyLabel(cost),
    payouts:topPotential?[{label:"Best single-play payout",value:topPotential}]:[],
    topPotential,
    note:"Estimated from Florida Lottery fixed Pick payout tables.",
  };
}
function previewEntryNumbers(entry){
  if(Array.isArray(entry))return entry.map(n=>Number(n));
  if(entry&&typeof entry==="object"&&Array.isArray(entry.numbers))return entry.numbers.map(n=>Number(n));
  return[];
}
function previewEntryPickRules(game,entry,fallback={}){
  const nums=previewEntryNumbers(entry);
  const rules=pickLineRulesFromItem(entry,fallback);
  const calc=pickGameRules(game,nums,rules.playType,rules.wager,rules.multiple,rules.fireball);
  return{
    playType:calc?.playType||rules.playType||"Straight",
    wager:Number(calc?.wager??rules.wager)||1,
    multiple:Math.max(1,Number(calc?.playCount??rules.multiple)||1),
    fireball:!!(calc?.fireball??rules.fireball),
    permutationCount:Number(calc?.ways)||permutationCount(nums),
    representedTickets:pickLineRepresentedTickets(game,nums,rules),
    cost:Number(calc?.cost)||pickLineCost(game,nums,rules),
  };
}
function makePickPreviewEntry(game,numbers,playType,wager,fireball,multiple=1){
  const nums=(numbers||[]).map(n=>Number(n));
  return{
    numbers:nums,
    pickRules:previewEntryPickRules(game,{numbers:nums},{playType,wager,fireball,multiple}),
  };
}
function makePreviewEntryForGame(game,numbers,playType,wager,fireball,multiple=1){
  const nums=(numbers||[]).map(n=>Number(n));
  return isPickGame(game)?makePickPreviewEntry(game,nums,playType,wager,fireball,multiple):nums;
}
function previewEntryKey(game,entry){
  const nums=previewEntryNumbers(entry);
  return(game||"fantasy5")+":"+nums.join("-");
}
function previewEntryLabel(game,entry){
  const nums=previewEntryNumbers(entry);
  if(!isPickGame(game))return nums.join(" ");
  const rules=previewEntryPickRules(game,entry);
  return[
    nums.join(""),
    rules.playType||"Straight",
    moneyLabel(Number(rules.wager)||1),
    "x"+(Number(rules.multiple)||1),
    rules.fireball?"Fireball":"No Fireball",
  ].join(" - ");
}
function parseFantasy5Bulk(text){
  const lines=String(text||"").split(/\r?\n/).map(line=>line.trim()).filter(Boolean);
  const valid=[];
  const errors=[];
  const seen=new Set();
  lines.forEach((line,index)=>{
    const nums=parseNumberEntry(line);
    const label="Line "+(index+1);
    if(!validPlayNumbers(nums)){errors.push(label+": enter 5 unique numbers from 1 to 36.");return;}
    const sorted=[...nums].sort((a,b)=>a-b);
    const key=sorted.join("-");
    if(seen.has(key)){errors.push(label+": duplicate play.");return;}
    seen.add(key);
    valid.push(sorted);
  });
  return{valid,errors};
}
function importLinesFromText(text){
  return String(text||"")
    .split(/\r?\n/)
    .map(line=>line.trim())
    .filter(Boolean);
}
function parseImportLineForGame(line,game){
  const cfg=gameNumberConfig(game);
  const raw=String(line||"").trim();
  if(!raw)return{numbers:null,error:"Blank row."};
  let parts=raw.split(/[\s,\t;\-|]+/).map(part=>part.trim()).filter(Boolean);
  if(parts.length===1&&isPickGame(game)&&/^\d+$/.test(parts[0])&&parts[0].length===cfg.count){
    parts=parts[0].split("");
  }
  const nums=parts.map(part=>Number(part));
  if(nums.length!==cfg.count)return{numbers:null,error:"Expected "+cfg.count+" number"+(cfg.count===1?"":"s")+"."};
  if(nums.some(n=>!Number.isInteger(n)))return{numbers:null,error:"Use numbers only."};
  if(nums.some(n=>n<cfg.min||n>cfg.max))return{numbers:null,error:"Numbers must be between "+cfg.min+" and "+cfg.max+"."};
  if(!cfg.allowDuplicates&&new Set(nums).size!==cfg.count)return{numbers:null,error:"Duplicate numbers are not allowed for "+gameLabel(game)+"."};
  return{numbers:cfg.allowDuplicates?nums:[...nums].sort((a,b)=>a-b),error:""};
}
function parseImportLinesForGame(lines,game,existing=[]){
  const accepted=[];
  const invalid=[];
  let duplicatesRemoved=0;
  const seen=new Set((existing||[])
    .map(entry=>previewEntryNumbers(entry))
    .filter(nums=>Array.isArray(nums)&&nums.length)
    .map(nums=>nums.join("-")));
  (lines||[]).forEach((line,index)=>{
    const parsed=parseImportLineForGame(line,game);
    if(parsed.error){invalid.push({line:index+1,text:line,error:parsed.error});return;}
    const key=parsed.numbers.join("-");
    if(seen.has(key)){duplicatesRemoved++;return;}
    seen.add(key);
    accepted.push(parsed.numbers);
  });
  return{
    game,
    accepted,
    duplicatesRemoved,
    invalid,
    total:(lines||[]).length,
  };
}
function detectImportGame(lines,existingByGame={}){
  const games=launchReadyGames().map(g=>g.id);
  const scored=games.map(game=>{
    const parsed=parseImportLinesForGame(lines,game,existingByGame[game]||[]);
    return{game,accepted:parsed.accepted.length,invalid:parsed.invalid.length,duplicatesRemoved:parsed.duplicatesRemoved,parsed};
  }).sort((a,b)=>b.accepted-a.accepted||a.invalid-b.invalid);
  const best=scored[0];
  if(!best||best.accepted<=0)return{game:null,needsGame:true,reason:"No valid plays found.",scored};
  const tied=scored.filter(item=>item.accepted===best.accepted&&item.invalid===best.invalid);
  if(tied.length>1)return{game:null,needsGame:true,reason:"Those rows match more than one game. Choose the game to continue.",scored};
  return{game:best.game,needsGame:false,reason:"Detected "+gameLabel(best.game)+".",scored,parsed:best.parsed};
}
function formatStrategyRoi(row,isWhatIf=false,eligibleDraws=0){
  if(isWhatIf)return"What-if only";
  if(!row||row.roi==null||eligibleDraws<=0)return"Pending";
  const roi=Number(row.roi);
  if(!Number.isFinite(roi))return"Pending";
  if(Math.abs(roi)>9999)return"Tracking";
  return(roi>=0?"+":"")+roi.toFixed(0)+"%";
}
function strategyBuilderRecord(id,record){
  const safe=record&&typeof record==="object"?record:{};
  const snapshot=parseStrategySnapshot(safe.outcomeSnapshot)||parseStrategySnapshot(safe.snapshot)||{};
  const normalizedSafe={...safe,outcomeSnapshot:snapshot};
  const snapshotNumbers=strategySnapshotNumbers(normalizedSafe);
  return{
    ...safe,
    outcomeSnapshot:snapshot,
    id,
    status:safe.status||"Draft",
    baseName:safe.baseName||strategyBaseLabel(id),
    version:Number(safe.version)||1,
    game:safe.game||snapshot.game||"fantasy5",
    source:safe.source||snapshot.source||"Manual",
    playSize:snapshotNumbers.length||Number(safe.playSize)||0,
    pickRules:safe.pickRules||snapshot.pickRules||null,
    createdAt:safe.createdAt||null,
    updatedAt:safe.updatedAt||safe.lockedAt||safe.createdAt||null,
    previousVersions:Array.isArray(safe.previousVersions)?safe.previousVersions:[],
  };
}
function strategyRecordForId(id,records,plays=[]){
  const playRows=(plays||[]).filter(p=>strategyIdForPlay(p)===id);
  const firstGame=playRows.find(p=>p?.game)?.game;
  const base=strategyBuilderRecord(id,records?.[id]);
  return{
    ...base,
    game:records?.[id]?.game||firstGame||base.game||"fantasy5",
    playSize:base.playSize||playRows.filter(p=>Array.isArray(p.numbers)).length,
  };
}
function normalizeSnapshotPlayNumbers(value,gameId){
  const cfg=gameNumberConfig(gameId||"fantasy5");
  if(Array.isArray(value)){
    const raw=value.every(part=>typeof part==="object"&&!Array.isArray(part))
      ?(value.map(part=>part?.numbers).filter(Boolean))
      :value;
    if(raw.every(n=>typeof n!=="object")){
      const nums=raw.map(n=>Number(n));
      return nums.length===cfg.count&&nums.every(n=>Number.isFinite(n))?nums:null;
    }
    return null;
  }
  if(value&&typeof value==="object"&&Array.isArray(value.numbers)){
    const nums=value.numbers.map(n=>Number(n));
    return nums.length===cfg.count&&nums.every(n=>Number.isFinite(n))?nums:null;
  }
  return null;
}
function parseStrategySnapshot(value){
  if(!value)return null;
  if(typeof value==="string"){
    try{
      const parsed=JSON.parse(value);
      return parsed&&typeof parsed==="object"?parsed:null;
    }catch{
      return null;
    }
  }
  return value&&typeof value==="object"?value:null;
}
function strategyRecordUsesDurableSnapshot(record){
  return record?.sourceOfTruth==="strategy_versions"||record?.durableSource==="strategy_versions";
}
function strategySnapshotNumbers(record){
  const snapshot=parseStrategySnapshot(record?.outcomeSnapshot)||parseStrategySnapshot(record?.snapshot)||record;
  const gameId=snapshot?.game||record?.game||"fantasy5";
  const source=Array.isArray(snapshot?.numbers)?snapshot.numbers:Array.isArray(snapshot?.plays)?snapshot.plays:[];
  const out=[];
  source.forEach(item=>{
    const nums=normalizeSnapshotPlayNumbers(item,gameId);
    if(nums)out.push(nums);
  });
  return out;
}
function strategySnapshotPlayEntries(record){
  const snapshot=parseStrategySnapshot(record?.outcomeSnapshot)||parseStrategySnapshot(record?.snapshot)||record;
  const gameId=snapshot?.game||record?.game||"fantasy5";
  const fallbackRules=record?.pickRules||snapshot?.pickRules||null;
  const source=Array.isArray(snapshot?.numbers)?snapshot.numbers:Array.isArray(snapshot?.plays)?snapshot.plays:[];
  const out=[];
  source.forEach(item=>{
    const nums=normalizeSnapshotPlayNumbers(item,gameId);
    if(!nums)return;
    out.push({
      numbers:nums,
      pickRules:isPickGame(gameId)?pickLineRulesFromItem(item,fallbackRules):null,
      raw:item,
    });
  });
  const grouped=[...(Array.isArray(snapshot?.groups)?snapshot.groups:[]),...(Array.isArray(snapshot?.ruleGroups)?snapshot.ruleGroups:[]),...(Array.isArray(snapshot?.playGroups)?snapshot.playGroups:[])];
  if(!out.length&&grouped.length){
    grouped.forEach(group=>{
      const groupRules=group?.pickRules||group?.rules||group?.playRules||fallbackRules;
      const plays=Array.isArray(group?.numbers)?group.numbers:Array.isArray(group?.plays)?group.plays:[];
      plays.forEach(item=>{
        const nums=normalizeSnapshotPlayNumbers(item,gameId);
        if(!nums)return;
        out.push({
          numbers:nums,
          pickRules:isPickGame(gameId)?pickLineRulesFromItem(item,groupRules):null,
          raw:item,
        });
      });
    });
  }
  return out;
}
function strategyNumbersForRecord(strategyId,record,plays=[]){
  const fromSnapshot=strategySnapshotNumbers(record);
  if(fromSnapshot.length)return fromSnapshot;
  if(strategyRecordUsesDurableSnapshot(record))return[];
  const gameId=record?.game||"fantasy5";
  return(plays||[])
    .filter(p=>strategyIdForPlay(p)===strategyId&&(p.game||gameId)===gameId&&Array.isArray(p.numbers))
    .map(p=>p.numbers.map(n=>Number(n)));
}
function strategyEstimatedCost(record,numbers=[]){
  const explicit=Number(record?.estimatedCost);
  if(Number.isFinite(explicit)&&explicit>0)return explicit;
  const rules=record?.pickRules||record?.outcomeSnapshot?.pickRules||null;
  const ruleCost=Number(rules?.cost);
  if(Number.isFinite(ruleCost)&&ruleCost>0)return ruleCost;
  const snapshotCost=Number(record?.outcomeSnapshot?.estimatedCost);
  if(Number.isFinite(snapshotCost)&&snapshotCost>0)return snapshotCost;
  return(numbers||[]).length;
}
function formatPickRulesSummary(game,rules){
  if(!isPickGame(game)||!rules)return"Standard play";
  if(rules.mixed)return"Mixed Pick rules - see each saved line";
  const parts=[
    rules.playType||"Straight",
    rules.wager?moneyLabel(Number(rules.wager)):"",
    Number(rules.multiple)>1?Number(rules.multiple)+"x":"1 play",
    rules.fireball?"Fireball":"No Fireball",
  ].filter(Boolean);
  return parts.join(" · ");
}
function WhatIfHistoryPanel({draws,plays,item,isPro,onUpgrade,onLoadHistory,historyLoading=false,historyLoaded=false}) {
  const[windowId,setWindowId]=useState("365");
  const[cadence,setCadence]=useState("twice");
  const readyForHistory=!!historyLoaded;
  const result=calculateWhatIfHistory({
    draws:readyForHistory?draws:[],
    plays,
    strategyId:item.id,
    record:item.record,
    windowId,
    cadence,
    isPro,
  });
  const roiText=result.roi==null?"Pending":(result.roi>=0?"+":"")+result.roi.toFixed(0)+"%";
  return(
    <div style={{border:"1px solid #dbeee9",background:"#f8fbfa",borderRadius:12,padding:12,margin:"12px 0"}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",flexWrap:"wrap",marginBottom:10}}>
        <div>
          <div style={{fontSize:10,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1}}>What-If History</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.4}}>
            Build your numbers and see how they would have performed over the last 365 days.
          </div>
        </div>
        {!isPro&&<button type="button" className="btn btn-sm btn-p" onClick={()=>onUpgrade?.("pro")}>Upgrade for 3 Years</button>}
      </div>
      {!readyForHistory&&(
        <div style={{background:"#fff",border:"1px solid #dbeee9",borderRadius:10,padding:"10px 11px",marginBottom:10}}>
          <div style={{fontSize:13,color:"#555",lineHeight:1.4,marginBottom:8}}>
            Historical testing loads only when you ask for it so the app stays fast while you browse.
          </div>
          <button type="button" className={"btn btn-sm btn-s "+(historyLoading?"loading":"")} onClick={onLoadHistory} disabled={historyLoading}>
            {historyLoading?"Loading history...":"Load What-If History"}
          </button>
        </div>
      )}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:9}}>
        {WHAT_IF_HISTORY_WINDOWS.map(option=>{
          const locked=option.pro&&!isPro;
          return(
            <button
              key={option.id}
              type="button"
              className={"btn btn-sm "+(windowId===option.id?"btn-p":"btn-s")}
              onClick={()=>locked?onUpgrade?.("pro"):setWindowId(option.id)}
              title={locked?"Upgrade to Pro to test three years of draw history.":""}
            >
              {option.label}{locked?" 🔒":""}
            </button>
          );
        })}
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
        <button type="button" className={"btn btn-sm "+(cadence==="once"?"btn-p":"btn-s")} onClick={()=>setCadence("once")}>Once per day</button>
        <button type="button" className={"btn btn-sm "+(cadence==="twice"?"btn-p":"btn-s")} onClick={()=>setCadence("twice")}>Twice per day</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8}}>
        {[
          ["Draws Tested",result.draws],
          ["Cost",fmtMoney(result.cost)],
          ["Played Winnings",fmtMoney(result.playedWinnings)],
          ["ROI",roiText],
          ["Winning Plays",result.wins],
          ["Free Tickets",result.freeTickets],
          ["Best Match",result.bestMatch+" / "+gameNumberConfig(result.game).count],
          ["Best Strategy",result.bestStrategy],
        ].map(([label,value])=>(
          <div key={label} style={{background:"#fff",border:"1px solid #edf1f0",borderRadius:10,padding:"8px 9px"}}>
            <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>{label}</div>
            <strong style={{fontFamily:label==="ROI"?"'Barlow Condensed',sans-serif":"inherit",fontSize:label==="ROI"?22:13,color:label==="ROI"&&String(value).startsWith("-")?"#c8102e":"#002855"}}>{value}</strong>
          </div>
        ))}
      </div>
      {!result.draws&&(
        <div style={{fontSize:12,color:"#5a4a00",background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"8px 9px",marginTop:9}}>
          No official {gameLabel(result.game)} draws are loaded for this window yet. Results appear here as official draw history is saved.
        </div>
      )}
      {result.cappedWindow&&(
        <div style={{fontSize:12,color:"#555",lineHeight:1.4,marginTop:9}}>
          Free users can test up to 1 year. Upgrade to Pro to test 3 years.
        </div>
      )}
    </div>
  );
}
function StrategyBuilderTab({plays,draws,drawStatus,strategyTrust,todaysLineup,scheduledDraws=[],performanceSummaries=[],onLoadHistoricalDraws,historyLoading=false,historyLoaded=false,onCreateStrategy,onUpdateStrategy,onLockStrategy,onArchiveStrategy,onReplaceStrategy,onRenameStrategy,onDuplicateStrategy,onPublishStrategy,onCreatePublicShare,onCreatePublicResultShare,onRevokePublicShare,onAddToLineup,onRemoveFromLineup,onDeleteStrategy,onUpgrade,onLimit,billingBusy=false,showAdvancedTools=false,libraryOnly=false,maxPreviewPlays=STRATEGY_MAX_PLAYS,myGames=DEFAULT_MY_GAMES,planTier="free",trialActive=false,user=null}) {
  const navigate=useNavigate();
  const[params,setParams]=useSearchParams();
  const editId=!libraryOnly?params.get("edit"):null;
  const onboardingContinuation=!libraryOnly&&editId&&params.get("onboarding")==="1";
  const[createStep,setCreateStep]=useState(1);
  const[name,setName]=useState("Eric Favorites");
  const[nameWasEdited,setNameWasEdited]=useState(false);
  const[game,setGame]=useState("fantasy5");
  const[source,setSource]=useState("Manual");
  const[playSize,setPlaySize]=useState(5);
  const[quickPickCount,setQuickPickCount]=useState(1);
  const[pickPlayType,setPickPlayType]=useState("Straight");
  const[pickWager,setPickWager]=useState(1);
  const[pickMultiple,setPickMultiple]=useState(1);
  const[pickFireball,setPickFireball]=useState(false);
  const[playslipDraw,setPlayslipDraw]=useState("evening");
  const[manualBoxes,setManualBoxes]=useState(()=>numberBoxValuesFromNumbers([]));
  const[preview,setPreview]=useState([]);
  const[editingPreviewIndex,setEditingPreviewIndex]=useState(null);
  const[previewFeedback,setPreviewFeedback]=useState("");
  const[entryFeedback,setEntryFeedback]=useState(null);
  const[highlightPreviewIndex,setHighlightPreviewIndex]=useState(null);
  const[manualAddFocusTick,setManualAddFocusTick]=useState(0);
  const[saveBusy,setSaveBusy]=useState(false);
  const[pendingEditSave,setPendingEditSave]=useState(null);
  const[bulkText,setBulkText]=useState("");
  const[sharedId,setSharedId]=useState("");
  const[exportedId,setExportedId]=useState("");
  const[whatIfOpenId,setWhatIfOpenId]=useState("");
  const[vaultResultRows,setVaultResultRows]=useState([]);
  const[vaultResultLoading,setVaultResultLoading]=useState(false);
  const[vaultResultError,setVaultResultError]=useState("");
  const[resultChoice,setResultChoice]=useState(null);
  const[vaultResultSharePayload,setVaultResultSharePayload]=useState(null);
  const[vaultResultShareBusyId,setVaultResultShareBusyId]=useState("");
  const[vaultResultShareError,setVaultResultShareError]=useState("");
  const[builderErr,setBuilderErr]=useState("");
  const[playTodayChoice,setPlayTodayChoice]=useState(null);
  const[playTodayMode,setPlayTodayMode]=useState("live");
  const[selectedPlayTodayDraws,setSelectedPlayTodayDraws]=useState([]);
  const[postSaveChoice,setPostSaveChoice]=useState(null);
  const[saveConfirmation,setSaveConfirmation]=useState(null);
  const[renameChoice,setRenameChoice]=useState(null);
  const[moreChoice,setMoreChoice]=useState(null);
  const[renameValue,setRenameValue]=useState("");
  const[importText,setImportText]=useState("");
  const[importMode,setImportMode]=useState("auto");
  const[importTargetId,setImportTargetId]=useState("");
  const[importSummary,setImportSummary]=useState(null);
  const[importPendingLines,setImportPendingLines]=useState([]);
  const[importFileName,setImportFileName]=useState("");
  const[importBusy,setImportBusy]=useState(false);
  const manualEntryRef=useRef(null);
  const manualFocusRestoreRef=useRef(null);
  const cfg=gameNumberConfig(game);
  const stagedValidation=validateNumberBoxes(manualBoxes,cfg);
  const currentPickNumbers=stagedValidation.numbers||[];
  const hasStagedDigits=manualBoxes.some(value=>String(value||"").trim());
  const stagedReady=!stagedValidation.error&&currentPickNumbers.length===cfg.count;
  const pickCalc=pickGameRules(game,currentPickNumbers,pickPlayType,pickWager,pickMultiple,pickFireball);
  const savedLineupIds=lineupIdSet(todaysLineup);
  const proLocked=!showAdvancedTools;
  const scoredDraws=(draws||[]).filter(d=>Array.isArray(d.numbers)&&d.numbers.length===gameNumberConfig(rowGame(d)).count);
  const liveEligibility=publicEligibilityForLineup(todaysLineup,strategyTrust);
  const livePlays=liveTrackingPlays(plays,todaysLineup);
  const summaryRows=performanceSummaryRowsFor(performanceSummaries,{periodKey:"all_time",drawFilter:"both",trackingMode:"live"});
  const watchSummaryRows=performanceSummaryRowsFor(performanceSummaries,{periodKey:"all_time",drawFilter:"both",trackingMode:"watch"});
  const rows=summaryRows.length
    ?summaryRows.map(row=>({
      id:row.strategy_id,
      roi:row.roi==null?null:Number(row.roi),
      best_match:Number(row.best_match)||0,
      free_tickets:Number(row.free_tickets)||0,
      cash_won:Number(row.cash_won)||0,
      draws_tracked:Number(row.draws_tracked)||0,
      winning_draws:Number(row.winning_draws)||0,
    }))
    :(scoredDraws.length?aggregateStrategies(livePlays,scoredDraws,{eligibleForDraw:liveEligibility}):[]);
  const rowById=Object.fromEntries(rows.map(r=>[r.id,r]));
  const watchRowById=Object.fromEntries(watchSummaryRows.map(row=>[row.strategy_id,row]));
  const trust=normalizeStrategyTrust(strategyTrust);
  const strategyIds=[...new Set([...Object.keys(trust.records),...strategyIdsFromPlays(plays)])];
  function nextEntryForStrategyGame(gameId){
    const central=(drawStatus?.entries||[]).filter(e=>e.game===gameId);
    const entries=central.length?central:getDrawStatusService([gameId],new Date(),draws).entries;
    return entries.find(e=>e.sortAt>=Date.now())||entries[0]||drawStatus?.next;
  }
  const saved=strategyIds.map(id=>{
    const record=strategyRecordForId(id,trust.records,plays);
    const row=rowById[id];
    const watchRow=watchRowById[id];
    const next=nextEntryForStrategyGame(record.game||"fantasy5");
    return{id,record,row,watchRow,next};
  }).filter(item=>item.id!=="manual"||item.row);
  const savedStrategyKey=saved.map(item=>item.id).filter(Boolean).sort().join("|");
  function sortVaultResultRows(rows=[]){
    return[...(rows||[])].sort((a,b)=>{
      const recency=compareOfficialDrawRecency(a,b);
      if(recency)return recency;
      return String(b?.calculated_at||b?.completed_at||"").localeCompare(String(a?.calculated_at||a?.completed_at||""));
    });
  }
  function vaultResultsForStrategy(itemOrId,options={}){
    const item=typeof itemOrId==="object"?itemOrId:null;
    const strategyId=item?item.id:itemOrId;
    const eligibleOnly=options.eligibleOnly!==false;
    const limit=Number.isFinite(Number(options.limit))?Math.max(0,Number(options.limit)):4;
    const ids=new Set([strategyId,item?.record?.id,item?.record?.strategy_id,item?.record?.strategyId].filter(Boolean).map(String));
    const displayName=item?strategyDisplayName(item.id,item.record):"";
    const normalizedName=displayName.replace(/\s+/g," ").trim().toLowerCase();
    const rows=sortVaultResultRows(vaultResultRows.filter(row=>{
      const rowName=String(row?.strategy_name||"").replace(/\s+/g," ").trim().toLowerCase();
      const idMatch=ids.has(String(row?.strategy_id||""));
      const nameMatch=normalizedName&&rowName===normalizedName;
      return (idMatch||nameMatch)&&(!eligibleOnly||resultShareEligible(row));
    }));
    return limit?rows.slice(0,limit):rows;
  }
  function scheduledRowsForStrategy(item){
    if(!item?.id)return[];
    const displayName=strategyDisplayName(item.id,item.record).replace(/\s+/g," ").trim().toLowerCase();
    return (scheduledDraws||[]).filter(row=>{
      const rowName=String(row?.strategy_name||"").replace(/\s+/g," ").trim().toLowerCase();
      return String(row?.strategy_id||"")===String(item.id)||(displayName&&rowName===displayName);
    }).sort(compareOfficialDrawRecency);
  }
  function frozenSnapshotLooksValid(snapshot){
    const raw=resultFrozenRawEntries(snapshot);
    const numbers=resultFrozenNumberEntries(snapshot);
    const declared=resultFrozenDeclaredCount(snapshot);
    const cost=Number(snapshot?.cost);
    return !!raw.length&&raw.length===numbers.length&&(declared===null||declared===numbers.length)&&Number.isFinite(cost)&&cost>0;
  }
  function vaultResultStateForStrategy(item,eligibleRows=[]){
    const allResults=vaultResultsForStrategy(item,{eligibleOnly:false,limit:0});
    if(eligibleRows.length){
      return{state:"share",label:"Share Results",tone:"#008577",detail:eligibleRows.length+" eligible completed frozen result"+(eligibleRows.length===1?"":"s")+" found."};
    }
    const ineligibleReasons=allResults.map(resultShareIneligibleReason).filter(Boolean);
    const missingFrozen=ineligibleReasons.find(reason=>/frozen lineup missing|missing frozen snapshot|frozen count mismatch|frozen lineup contains invalid|frozen cost invalid/i.test(reason));
    if(missingFrozen){
      return{state:"unavailable",label:"Result unavailable",tone:"#c8860a",detail:resultShareStatusText(allResults.find(row=>resultShareIneligibleReason(row)===missingFrozen))||("Result unavailable - "+missingFrozen)};
    }
    const scoringIssue=ineligibleReasons.find(reason=>/scored count mismatch|missing scheduled draw|activation|scheduled after cutoff|version activated after cutoff/i.test(reason));
    if(scoringIssue){
      return{state:"scoring_failed",label:"Scoring failed",tone:"#c8102e",detail:"Review required - "+scoringIssue+"."};
    }
    const activeScheduled=scheduledRowsForStrategy(item).filter(row=>["upcoming","cutoff_passed","awaiting_results"].includes(String(row?.status||"").toLowerCase()));
    const validFrozenPending=activeScheduled.find(row=>frozenSnapshotLooksValid(row?.frozen_snapshot));
    if(validFrozenPending){
      return{state:"pending",label:"Results pending",tone:"#2E6DA4",detail:"Frozen before "+fmtShortDate(validFrozenPending.draw_date)+" "+drawFilterLabel(validFrozenPending.draw_time)+". Result share unlocks after exact official scoring."};
    }
    const invalidFrozenPending=activeScheduled.find(row=>row?.frozen_snapshot&&!frozenSnapshotLooksValid(row.frozen_snapshot));
    if(invalidFrozenPending){
      return{state:"unavailable",label:"Result unavailable",tone:"#c8860a",detail:"Result unavailable - frozen lineup missing."};
    }
    return{state:"no_freeze",label:"Play or share before a draw to unlock results",tone:"#5d6675",detail:"No pre-draw frozen lineup exists for this strategy yet."};
  }
  async function enrichVaultLiveResultRows(rows=[]){
    const baseRows=Array.isArray(rows)?rows:[];
    if(!baseRows.length)return[];
    const scheduledIds=[...new Set(baseRows.map(row=>row.scheduled_draw_id).filter(Boolean))];
    const scheduleMap=new Map();
    const versionMap=new Map();
    if(scheduledIds.length){
      const{data,error}=await supabase
        .from("scheduled_strategy_draws")
        .select("id,strategy_id,game,draw_date,draw_time,strategy_version,strategy_name,play_type,tracking_mode,scheduled_at,cutoff_at,locked_at,completed_at,official_draw_id,official_draw_key,official_result_at,frozen_snapshot,status")
        .in("id",scheduledIds);
      if(error)throw error;
      (data||[]).forEach(row=>scheduleMap.set(row.id,row));
    }
    const versionStrategies=[...new Set(baseRows.map(row=>row.strategy_id).filter(Boolean))];
    if(versionStrategies.length){
      const{data,error}=await supabase
        .from("strategy_versions")
        .select("strategy_id,version_number,created_at")
        .eq("user_id",user.id)
        .in("strategy_id",versionStrategies);
      if(error)throw error;
      (data||[]).forEach(row=>versionMap.set([row.strategy_id,Number(row.version_number)||1].join(":"),row.created_at));
    }
    const officialIds=[...new Set(baseRows.map(row=>row.official_draw_id||scheduleMap.get(row.scheduled_draw_id)?.official_draw_id).filter(Boolean).map(String))];
    const games=[...new Set(baseRows.map(row=>row.game).filter(Boolean))];
    const dates=baseRows.map(row=>row.draw_date).filter(Boolean).sort();
    const officialMapById=new Map();
    const officialMapByKey=new Map();
    if(games.length&&dates.length){
      const drawQuery=supabase
        .from("draws")
        .select("id,game,draw_date,draw_time,numbers,fireball_digit,source,official_result_status,official_result_at,official_checked_at,fetched_at,official_payload_source,official_provider_game_id,official_provider_result_id")
        .in("game",games)
        .gte("draw_date",dates[0])
        .lte("draw_date",dates[dates.length-1])
        .limit(1000);
      const{data,error}=await drawQuery;
      if(error)throw error;
      (data||[]).forEach(draw=>{
        if(draw?.id)officialMapById.set(String(draw.id),draw);
        officialMapByKey.set(resultOfficialDrawKey(draw.game,draw.draw_date,draw.draw_time),draw);
      });
    }
    if(officialIds.length){
      const missingIds=officialIds.filter(id=>!officialMapById.has(id));
      if(missingIds.length){
        const{data,error}=await supabase
          .from("draws")
          .select("id,game,draw_date,draw_time,numbers,fireball_digit,source,official_result_status,official_result_at,official_checked_at,fetched_at,official_payload_source,official_provider_game_id,official_provider_result_id")
          .in("id",missingIds);
        if(error)throw error;
        (data||[]).forEach(draw=>{
          if(draw?.id)officialMapById.set(String(draw.id),draw);
          officialMapByKey.set(resultOfficialDrawKey(draw.game,draw.draw_date,draw.draw_time),draw);
        });
      }
    }
    return baseRows.map(row=>{
      const scheduled=scheduleMap.get(row.scheduled_draw_id)||{};
      const versionNumber=Number(scheduled.strategy_version||row.strategy_version)||Number(scheduled.frozen_snapshot?.version)||1;
      const rowGameId=row.game||scheduled.game||"fantasy5";
      const rowDrawDate=row.draw_date||scheduled.draw_date;
      const rowDrawTime=row.draw_time||scheduled.draw_time;
      const expectedKey=resultOfficialDrawKey(rowGameId,rowDrawDate,rowDrawTime);
      const storedKey=String(row.official_draw_key||scheduled.official_draw_key||"").toLowerCase();
      const storedOfficialId=String(row.official_draw_id||scheduled.official_draw_id||"");
      const candidate=(storedOfficialId&&officialMapById.get(storedOfficialId))
        ||(storedKey===expectedKey&&officialMapByKey.get(expectedKey))
        ||(!storedOfficialId&&!storedKey&&officialMapByKey.get(expectedKey))
        ||null;
      const official=officialDrawMatchesResult({...row,game:rowGameId,draw_date:rowDrawDate,draw_time:rowDrawTime},candidate)?candidate:{};
      return{
        ...row,
        frozen_snapshot:scheduled.frozen_snapshot||row.frozen_snapshot||null,
        strategy_version:versionNumber||null,
        strategy_version_activated_at:versionMap.get([row.strategy_id||scheduled.strategy_id,versionNumber].join(":"))||snapshotActivationTimestamp(scheduled.frozen_snapshot||row.frozen_snapshot||{})||null,
        strategy_name:scheduled.strategy_name||row.strategy_name,
        scheduled_at:scheduled.scheduled_at||row.scheduled_at||null,
        cutoff_at:scheduled.cutoff_at||row.cutoff_at||null,
        locked_at:scheduled.locked_at||row.locked_at||null,
        completed_at:scheduled.completed_at||row.completed_at||null,
        official_draw_id:official.id?String(official.id):null,
        official_draw_key:official.id?expectedKey:null,
        official_result_at:official.id?(official.official_result_at||scheduled.official_result_at||row.official_result_at||null):null,
        official_checked_at:official.id?(official.official_checked_at||row.official_checked_at||null):null,
        official_fetched_at:official.id?(official.fetched_at||row.official_fetched_at||null):null,
        official_source:official.id?officialSourceLabel(official):"Official result pending",
        official_numbers:Array.isArray(official.numbers)?official.numbers:row.official_numbers,
        official_fireball_digit:official.fireball_digit??row.official_fireball_digit,
      };
    });
  }
  async function prepareVaultResultShare(row){
    if(!resultShareEligible(row)){
      const reason=resultShareIneligibleReason(row);
      setBuilderErr(reason==="official result pending"?"Official result pending. This completed result cannot be shared until the exact official draw is imported.":"This result cannot be shared yet: "+reason+".");
      return{cancel:true};
    }
    const fallbackPayload=resultCardSharePayload(row,gameNumberConfig(row.game||"fantasy5"));
    if(typeof onCreatePublicResultShare!=="function")return{payload:fallbackPayload};
    try{
      const data=await onCreatePublicResultShare(row.scheduled_draw_id,row.id);
      if(data?.snapshot){
        return{payload:publicResultSnapshotToCardPayload({...data.snapshot,shareId:data.shareId,link:data.link})};
      }
      if(data?.link)return{payload:{...fallbackPayload,link:data.link,isPublic:true}};
      return{payload:fallbackPayload};
    }catch(e){
      setBuilderErr(e?.message||"Could not prepare this result share.");
      return{cancel:true};
    }
  }
  async function openVaultResultShare(row){
    const rowId=String(row?.id||row?.scheduled_draw_id||"");
    if(vaultResultShareBusyId)return;
    setVaultResultShareBusyId(rowId);
    setVaultResultShareError("");
    try{
      const prepared=await prepareVaultResultShare({...row});
      if(prepared?.cancel){
        const reason=resultShareIneligibleReason(row);
        setVaultResultShareError(reason==="official result pending"?"Official result pending for "+fmtShortDate(row?.draw_date)+" "+drawFilterLabel(row?.draw_time)+".":"Could not prepare "+fmtShortDate(row?.draw_date)+" "+drawFilterLabel(row?.draw_time)+": "+(reason||"share failed")+".");
        return;
      }
      setResultChoice(null);
      setVaultResultSharePayload(prepared?.payload||resultCardSharePayload(row,gameNumberConfig(row.game||"fantasy5")));
    }catch(e){
      setVaultResultShareError(e?.message||"Could not prepare this result share.");
    }finally{
      setVaultResultShareBusyId("");
    }
  }
  function vaultResultSummaryText(row){
    const verdict=resultCardVerdict(row);
    const won=fmtMoney(row?.cash_won);
    const played=fmtMoney(row?.total_cost);
    const total=gameNumberConfig(row?.game||"fantasy5").count||5;
    const bestMatch="Best match "+(summaryNumber(row?.best_match)||0)+" of "+total;
    return[verdict,"Played "+played,"Won "+won,bestMatch].filter(Boolean).join(" · ");
  }
  const firstStrategyName=firstStrategyDefaultName(user,game);
  const editRecord=editId?strategyRecordForId(editId,trust.records,plays):null;
  const editPlays=editId?(plays||[]).filter(p=>strategyIdForPlay(p)===editId&&Array.isArray(p.numbers)):[];
  const activeEditId=editId||importTargetId;
  const hasBuilderUnsavedWork=!libraryOnly&&(
    preview.length>0||
    editingPreviewIndex!==null||
    !!pendingEditSave||
    manualBoxes.some(value=>String(value||"").trim())||
    !!String(bulkText||"").trim()||
    !!String(importText||"").trim()||
    importPendingLines.length>0||
    (nameWasEdited&&!!String(name||"").trim())
  );
  const totalPotential=game==="fantasy5"
    ?sharePossibleWinLabel(game)
    :pickStrategyRulesFromPreview(game,preview,pickPlayType,pickWager,pickFireball,pickMultiple)?.payouts?.[0]?.value
      ?moneyLabel(pickStrategyRulesFromPreview(game,preview,pickPlayType,pickWager,pickFireball,pickMultiple).payouts[0].value)
      :sharePossibleWinLabel(game);
  const playslipDrawLabel=playslipDraw==="both"?"Both":playslipDraw==="midday"?"Midday":"Evening";
  const previewFireballValues=isPickGame(game)
    ?preview.map(entry=>!!previewEntryPickRules(game,entry,{fireball:pickFireball}).fireball)
    :[];
  const playslipFireballLabel=!isPickGame(game)
    ?"Not used"
    :previewFireballValues.length
      ?(previewFireballValues.every(Boolean)?"Yes":previewFireballValues.every(value=>!value)?"No":"Mixed")
      :(pickFireball?"Yes":"No");
  const stagedNumberText=manualBoxes.map(value=>String(value||"").trim()||"_").join(game==="fantasy5"?" ":"");
  const addPlayButtonText=editingPreviewIndex!==null?"Update Play":"Add Play";
  const createPlayslipButtonText=activeEditId?"Save Changes to My Playslips":"Save to My Vault";
  const builderDisplayName=(name||"").trim();
  const builderHeading=libraryOnly
    ?"My Playslips"
    :activeEditId
      ?"Edit: "+(builderDisplayName||strategyBaseLabel(activeEditId))
      :builderDisplayName
        ?"Building: "+builderDisplayName
        :"Play Builder";
  useEffect(()=>{
    if(libraryOnly||editId||importTargetId||saved.length>0||nameWasEdited)return;
    setName(firstStrategyName);
  },[libraryOnly,editId,importTargetId,saved.length,nameWasEdited,firstStrategyName]);
  useEffect(()=>{
    if(!libraryOnly||!user?.id||!savedStrategyKey){
      setVaultResultRows([]);
      setVaultResultLoading(false);
      setVaultResultError("");
      return undefined;
    }
    let cancelled=false;
    async function loadVaultCompletedResults(){
      setVaultResultLoading(true);
      setVaultResultError("");
      try{
        const strategyIds=[...new Set([
          ...savedStrategyKey.split("|").filter(Boolean),
          ...summaryRows.map(row=>row.strategy_id).filter(Boolean),
        ].map(String))];
        const selectFields="id,user_id,strategy_id,scheduled_draw_id,official_draw_id,official_draw_key,game,draw_date,draw_time,strategy_name,play_type,tracking_mode,play_count,total_cost,cash_won,net,roi,best_match,free_tickets,largest_prize,winning_plays_count,match_counts,prize_breakdown,result_source,calculated_at";
        const batches=await Promise.all(strategyIds.map(strategyId=>
          supabase
            .from("strategy_draw_results")
            .select(selectFields)
            .eq("user_id",user.id)
            .in("tracking_mode",["live","watch"])
            .eq("strategy_id",strategyId)
            .order("draw_date",{ascending:false})
            .order("draw_time",{ascending:false})
            .limit(8)
        ));
        const failed=batches.find(result=>result.error);
        if(failed?.error)throw failed.error;
        const data=batches.flatMap(result=>result.data||[]);
        const enriched=await enrichVaultLiveResultRows(data||[]);
        if(!cancelled)setVaultResultRows(sortVaultResultRows(enriched));
      }catch(e){
        if(!cancelled){
          setVaultResultRows([]);
          setVaultResultError(e?.message||"Completed tracked results are unavailable right now.");
        }
      }finally{
        if(!cancelled)setVaultResultLoading(false);
      }
    }
    loadVaultCompletedResults();
    return()=>{cancelled=true;};
  },[libraryOnly,user?.id,savedStrategyKey,performanceSummaries]);
  useEffect(()=>{
    if(libraryOnly)return undefined;
    window.__LOTTO_TRACKER_UNSAVED_WORK__=hasBuilderUnsavedWork;
    return()=>{window.__LOTTO_TRACKER_UNSAVED_WORK__=false;};
  },[libraryOnly,hasBuilderUnsavedWork]);
  useEffect(()=>{
    if(!editId)return;
    setImportTargetId("");
    const record=strategyRecordForId(editId,trust.records,plays);
    const strategyEntries=isPickGame(record.game||"fantasy5")
      ?strategySnapshotPlayEntries(record).map(entry=>makePreviewEntryForGame(record.game||"fantasy5",entry.numbers,entry.pickRules?.playType,entry.pickRules?.wager,entry.pickRules?.fireball,entry.pickRules?.multiple))
      :strategyNumbersForRecord(editId,record,plays);
    setCreateStep(3);
    setName(record.baseName||strategyBaseLabel(editId));
    setNameWasEdited(true);
    setGame(record.game||"fantasy5");
    setSource(record.source||"Manual");
    setPreview(strategyEntries);
    setManualBoxes(numberBoxValuesFromNumbers([],gameNumberConfig(record.game||"fantasy5").count));
    setManualAddFocusTick(tick=>tick+1);
    setPlayslipDraw(record.preferredDraw||record.preferred_draw||"evening");
    const firstRules=isPickGame(record.game||"fantasy5")&&strategyEntries[0]?previewEntryPickRules(record.game||"fantasy5",strategyEntries[0]):record.pickRules;
    setPickPlayType((PICK_PLAY_TYPES[record.game||"pick4"]||[]).includes(firstRules?.playType)?firstRules.playType:"Straight");
    setPickWager(Number(firstRules?.wager)||1);
    setPickMultiple(Number(firstRules?.multiple)||1);
    setPickFireball(!!firstRules?.fireball);
    setEditingPreviewIndex(null);
    setBuilderErr(strategyEntries.length?"":"This strategy's saved version is incomplete. I did not load a blank editor because that could overwrite the strategy. Try refreshing, or contact support before saving.");
  },[
    editId,
    plays.map(p=>[p.id,p.strategy,Array.isArray(p.numbers)?p.numbers.join("-"):""].join(":")).join("|"),
    JSON.stringify(trust.records?.[editId]?.outcomeSnapshot||null)
  ]);
  useEffect(()=>{
    if(!manualAddFocusTick||libraryOnly)return;
    const section=manualEntryRef.current;
    if(!section)return;
    const restore=manualFocusRestoreRef.current;
    manualFocusRestoreRef.current=null;
    window.requestAnimationFrame(()=>{
      window.setTimeout(()=>{
        const firstInput=section.querySelector('input[inputmode="numeric"], input[pattern="[0-9]*"], input');
        firstInput?.focus?.({preventScroll:true});
        if(restore&&Number.isFinite(restore.y)){
          const restoreScroll=()=>window.scrollTo(restore.x||0,restore.y);
          restoreScroll();
          window.requestAnimationFrame(restoreScroll);
          window.setTimeout(restoreScroll,120);
          window.setTimeout(restoreScroll,260);
        }
      },40);
    });
  },[manualAddFocusTick,libraryOnly]);
  function previewCostLabel(entries){
    const rows=Array.isArray(entries)?entries:[];
    if(isPickGame(game))return pickStrategyRulesFromPreview(game,rows,pickPlayType,pickWager,pickFireball,pickMultiple)?.costLabel||"$0";
    return "$"+rows.length;
  }
  function previewCountCostText(entries){
    const rows=Array.isArray(entries)?entries:[];
    return rows.length+" play"+(rows.length===1?"":"s")+" on My Playslip - Estimated cost: "+previewCostLabel(rows);
  }
  function firstSavedNumberInsight(record){
    const gameId=record?.game||game;
    const savedNumbers=Array.isArray(record?.primaryNumbers)&&record.primaryNumbers.length
      ?record.primaryNumbers
      :previewEntryNumbers(preview[0]||[]);
    if(!savedNumbers.length)return"A strategy is a saved playslip you can reuse, test and share.";
    const nowET=new Date(new Date().toLocaleString("en-US",{timeZone:EASTERN_TIME_ZONE}));
    const lastCompleteYear=nowET.getFullYear()-1;
    const matches=(draws||[]).filter(draw=>{
      if(rowGame(draw)!==gameId)return false;
      if(String(draw.draw_date||draw.date||"").slice(0,4)!==String(lastCompleteYear))return false;
      if(!Array.isArray(draw.numbers)||draw.numbers.length!==savedNumbers.length)return false;
      return isPickGame(gameId)
        ?draw.numbers.every((n,index)=>Number(n)===Number(savedNumbers[index]))
        :sameNumberMultiset(draw.numbers,savedNumbers);
    }).length;
    const numberText=digitsText(savedNumbers);
    if(matches>0)return"Great news! "+numberText+" came out "+(matches===1?"1 time":matches+" times")+" last year.";
    return numberText+" did not appear last year. Now you know - check this year or add more numbers.";
  }
  function showEntryFeedback(kind,message,entries,index=null,detail=null){
    setEntryFeedback({kind,message,detail:detail===null?previewCountCostText(entries):detail});
    setHighlightPreviewIndex(Number.isInteger(index)?index:null);
    window.clearTimeout?.(showEntryFeedback._timer);
    showEntryFeedback._timer=window.setTimeout(()=>{
      setEntryFeedback(null);
      setHighlightPreviewIndex(null);
    },4200);
  }
  const existingImportTargets=saved.filter(item=>item.id&&item.id!=="manual");
  function strategyTradingCardStats(item,strategyNumbers,lineupItem){
    const pickRules=item.record?.pickRules;
    const durableCount=Number(item.record?.playSize)||0;
    const playCount=Number(pickRules?.playCount)
      ||(strategyRecordUsesDurableSnapshot(item.record)&&durableCount?durableCount:0)
      ||strategyNumbers.length
      ||lineupCostForStrategy(plays,item.id)
      ||durableCount
      ||0;
    const row=item.row;
    const currentDraw=item.next||drawStatus?.next;
    const savedAfter=currentDraw?strategyNumbers.some((_,index)=>{
      const strategyPlays=(plays||[]).filter(p=>strategyIdForPlay(p)===item.id&&Array.isArray(p.numbers));
      return strategyPlays[index]?playSavedAfterDraw(strategyPlays[index],currentDraw):false;
    }):false;
    const drawsTracked=savedAfter?0:Math.max(0,Number(row?.draws_tracked)||0);
    const bestMatch=Number(row?.best_match)||0;
    const bestHit=savedAfter?"What-if only":drawsTracked<=0?"Pending":bestMatch>0?bestMatch+" Match":"No hits yet";
    const totalWins=savedAfter?"What-if only":drawsTracked<=0?"Pending":Number(row?.winning_draws)||Number(row?.free_tickets)||0;
    const isPlayingToday=!!lineupItem&&normalizeTrackingMode(lineupItem.trackingMode)==="live";
    const currentStatus=item.record?.status==="Archived"
      ?"Archived"
      :savedAfter
        ?"Saved After Draw"
        :isPlayingToday
          ?"Playing Today"
          :"Saved Playslip";
    return{
      playCount,
      bestHit,
      totalWins,
      drawsTracked,
      isWhatIfOnly:savedAfter,
      estimatedCost:pickRules?.costLabel||"$"+(Number(item.record?.estimatedCost)||playCount),
      potentialWin:pickRules?.payouts?.filter(p=>!p.disabled).map(p=>p.label+" "+moneyLabel(p.value)).join(" / ")||sharePossibleWinLabel(item.record?.game||"fantasy5"),
      currentStatus,
      statusColor:currentStatus==="Archived"?"#5d6675":currentStatus==="Saved After Draw"?"#c8860a":isPlayingToday?"#008577":"#5d6675",
    };
  }
  function appendPreviewPlays(nextPlays){
    const incoming=(nextPlays||[])
      .map(entry=>Array.isArray(entry)||entry?.numbers?entry:makePreviewEntryForGame(game,entry,pickPlayType,pickWager,pickFireball,pickMultiple))
      .filter(entry=>previewEntryNumbers(entry).length===cfg.count);
    if(!incoming.length)return;
    setPreview(current=>{
      const headroom=Math.max(0,maxPreviewPlays-current.length);
      if(headroom<=0){
        setBuilderErr("Strategies can include up to "+STRATEGY_MAX_PLAYS.toLocaleString()+" plays.");
        return current;
      }
      const keys=new Set(current.map(entry=>previewEntryKey(game,entry)));
      const clean=[];
      incoming.forEach(entry=>{
        const key=previewEntryKey(game,entry);
        if(!keys.has(key)){keys.add(key);clean.push(entry);}
      });
      if(clean.length<incoming.length)setBuilderErr("Duplicate plays were skipped.");
      return[...current,...clean.slice(0,headroom)];
    });
  }
  function applyImportedPlays(imported,resultGame,baseSummary={}){
    const rows=(imported||[])
      .filter(nums=>Array.isArray(nums)&&nums.length===gameNumberConfig(resultGame).count)
      .map(nums=>makePreviewEntryForGame(resultGame,nums,pickPlayType,pickWager,pickFireball,pickMultiple));
    setPreview(current=>{
      const baseCurrent=resultGame===game?current:[];
      const headroom=Math.max(0,maxPreviewPlays-baseCurrent.length);
      const keys=new Set(baseCurrent.map(entry=>previewEntryKey(resultGame,entry)));
      const accepted=[];
      let duplicateExisting=0;
      rows.forEach(entry=>{
        const key=previewEntryKey(resultGame,entry);
        if(keys.has(key)){duplicateExisting++;return;}
        keys.add(key);
        if(accepted.length<headroom)accepted.push(entry);
      });
      const capped=Math.max(0,rows.length-duplicateExisting-accepted.length);
      setImportSummary({
        fileName:importFileName||"Pasted numbers",
        game:resultGame,
        accepted:accepted.length,
        duplicatesRemoved:(baseSummary.duplicatesRemoved||0)+duplicateExisting,
        invalid:(baseSummary.invalid||[]).length,
        invalidRows:(baseSummary.invalid||[]).slice(0,8),
        capped,
        total:baseSummary.total||rows.length,
        needsGame:false,
      });
      if(accepted.length)setPreviewFeedback("Added "+accepted.length+" imported play"+(accepted.length===1?"":"s")+" to My Playslip.");
      return[...baseCurrent,...accepted];
    });
  }
  function applyImportedEntries(imported,resultGame,baseSummary={}){
    const entries=(imported||[])
      .map(entry=>makePreviewEntryForGame(
        resultGame,
        previewEntryNumbers(entry),
        pickLineRulesFromItem(entry)?.playType,
        pickLineRulesFromItem(entry)?.wager,
        pickLineRulesFromItem(entry)?.fireball,
        pickLineRulesFromItem(entry)?.multiple
      ))
      .filter(entry=>previewEntryNumbers(entry).length===gameNumberConfig(resultGame).count);
    setPreview(current=>{
      const baseCurrent=resultGame===game?current:[];
      const headroom=Math.max(0,maxPreviewPlays-baseCurrent.length);
      const keys=new Set(baseCurrent.map(entry=>previewEntryKey(resultGame,entry)));
      const accepted=[];
      let duplicateExisting=0;
      entries.forEach(entry=>{
        const key=previewEntryKey(resultGame,entry);
        if(keys.has(key)){duplicateExisting++;return;}
        keys.add(key);
        if(accepted.length<headroom)accepted.push(entry);
      });
      setImportSummary({
        fileName:importFileName||"Imported strategy JSON",
        game:resultGame,
        accepted:accepted.length,
        duplicatesRemoved:(baseSummary.duplicatesRemoved||0)+duplicateExisting,
        invalid:baseSummary.invalid||0,
        invalidRows:[],
        capped:Math.max(0,entries.length-duplicateExisting-accepted.length),
        total:baseSummary.total||entries.length,
        needsGame:false,
      });
      if(accepted.length)setPreviewFeedback("Added "+accepted.length+" imported play"+(accepted.length===1?"":"s")+" to My Playslip.");
      return[...baseCurrent,...accepted];
    });
  }
  function importExportedStrategyPayload(payload,label="Imported strategy JSON"){
    const exported=payload&&typeof payload==="object"?payload:null;
    const resultGame=exported?.game||game;
    const sourcePlays=Array.isArray(exported?.plays)?exported.plays:Array.isArray(exported?.numbers)?exported.numbers:[];
    if(!sourcePlays.length)return false;
    setImportFileName(label);
    setGame(resultGame);
    setSource("Import");
    if(exported?.name&&!name){setName(exported.name);setNameWasEdited(true);}
    applyImportedEntries(sourcePlays,resultGame,{total:sourcePlays.length,duplicatesRemoved:0,invalid:0});
    return true;
  }
  function existingForGameMap(){
    const map={};
    launchReadyGames().forEach(g=>{map[g.id]=game===g.id?preview.map(entry=>previewEntryNumbers(entry)):[];});
    return map;
  }
  function importLines(lines,label="Pasted numbers"){
    const clean=(lines||[]).map(line=>String(line||"").trim()).filter(Boolean);
    setImportFileName(label);
    setBuilderErr("");
    if(!clean.length){
      setImportSummary({fileName:label,game:null,accepted:0,duplicatesRemoved:0,invalid:0,invalidRows:[],total:0,needsGame:false});
      setBuilderErr("Paste numbers or choose a file to import.");
      return;
    }
    const targetGame=importTargetId
      ?(strategyRecordForId(importTargetId,trust.records,plays).game||game)
      :importMode==="auto"
        ?null
        :importMode;
    if(!targetGame){
      const detected=detectImportGame(clean,existingForGameMap());
      if(detected.needsGame){
        setImportPendingLines(clean);
        setImportSummary({
          fileName:label,
          game:null,
          accepted:0,
          duplicatesRemoved:0,
          invalid:clean.length,
          invalidRows:[],
          total:clean.length,
          needsGame:true,
          reason:detected.reason,
          scored:detected.scored,
        });
        setBuilderErr(detected.reason+" Pick a game in Import As, then run the import again.");
        return;
      }
      setGame(detected.game);
      setSource("Import");
      applyImportedPlays(detected.parsed.accepted,detected.game,detected.parsed);
      setImportPendingLines([]);
      return;
    }
    const parsed=parseImportLinesForGame(clean,targetGame,targetGame===game?preview:[]);
    if(parsed.accepted.length>0){
      setGame(targetGame);
      setSource("Import");
      applyImportedPlays(parsed.accepted,targetGame,parsed);
    }else{
      setImportSummary({
        fileName:label,
        game:targetGame,
        accepted:0,
        duplicatesRemoved:parsed.duplicatesRemoved,
        invalid:parsed.invalid.length,
        invalidRows:parsed.invalid.slice(0,8),
        total:parsed.total,
        needsGame:false,
      });
      setBuilderErr("No valid "+gameLabel(targetGame)+" plays found.");
    }
    setImportPendingLines([]);
  }
  function loadImportTarget(strategyId){
    setImportTargetId(strategyId);
    setImportSummary(null);
    setBuilderErr("");
    if(!strategyId)return;
    const record=strategyRecordForId(strategyId,trust.records,plays);
    const strategyEntries=isPickGame(record.game||"fantasy5")
      ?strategySnapshotPlayEntries(record).map(entry=>makePreviewEntryForGame(record.game||"fantasy5",entry.numbers,entry.pickRules?.playType,entry.pickRules?.wager,entry.pickRules?.fireball,entry.pickRules?.multiple))
      :strategyNumbersForRecord(strategyId,record,plays);
    setName(record.baseName||strategyBaseLabel(strategyId));
    setNameWasEdited(true);
    setGame(record.game||"fantasy5");
    setSource(record.source||"Manual");
    setPreview(strategyEntries);
    setManualBoxes(numberBoxValuesFromNumbers([],gameNumberConfig(record.game||"fantasy5").count));
    setManualAddFocusTick(tick=>tick+1);
    setPlayslipDraw(record.preferredDraw||record.preferred_draw||"evening");
    const firstRules=isPickGame(record.game||"fantasy5")&&strategyEntries[0]?previewEntryPickRules(record.game||"fantasy5",strategyEntries[0]):record.pickRules;
    setPickPlayType((PICK_PLAY_TYPES[record.game||"pick4"]||[]).includes(firstRules?.playType)?firstRules.playType:"Straight");
    setPickWager(Number(firstRules?.wager)||1);
    setPickMultiple(Number(firstRules?.multiple)||1);
    setPickFireball(!!firstRules?.fireball);
  }
  async function handleImportFile(e){
    const file=e.target.files?.[0];
    if(!file)return;
    setImportBusy(true);
    setBuilderErr("");
    try{
      const lower=file.name.toLowerCase();
      if(lower.endsWith(".json")){
        const payload=JSON.parse(await file.text());
        if(!importExportedStrategyPayload(payload,file.name))setBuilderErr("That JSON file does not include strategy plays.");
      }else
      if(lower.endsWith(".xlsx")){
        const XLSX=await import("xlsx");
        const buffer=await file.arrayBuffer();
        const workbook=XLSX.read(buffer,{type:"array"});
        const rows=[];
        workbook.SheetNames.forEach(name=>{
          const sheet=workbook.Sheets[name];
          XLSX.utils.sheet_to_json(sheet,{header:1,raw:false,blankrows:false}).forEach(row=>{
            const line=(row||[]).map(cell=>String(cell??"").trim()).filter(Boolean).join(" ");
            if(line)rows.push(line);
          });
        });
        importLines(rows,file.name);
      }else{
        const text=await file.text();
        const trimmed=String(text||"").trim();
        if((trimmed.startsWith("{")||trimmed.startsWith("["))){
          try{
            const payload=JSON.parse(trimmed);
            if(importExportedStrategyPayload(Array.isArray(payload)?{plays:payload,game}:payload,file.name))return;
          }catch{}
        }
        importLines(importLinesFromText(text),file.name);
      }
    }catch(err){
      setBuilderErr("Could not read that file. Try TXT, CSV, or XLSX.");
    }finally{
      setImportBusy(false);
      e.target.value="";
    }
  }
  function addManualPlay(){
    setBuilderErr("");
    if(source==="Manual"||source==="Quick Pick"){
      const checked=validateNumberBoxes(manualBoxes,cfg);
      if(checked.error){setBuilderErr(checked.error);return;}
      const entry=makePreviewEntryForGame(game,checked.numbers,pickPlayType,pickWager,pickFireball,pickMultiple);
      if(editingPreviewIndex!==null){
        manualFocusRestoreRef.current={x:window.scrollX||0,y:window.scrollY||0};
        const next=preview.map((item,index)=>index===editingPreviewIndex?entry:item);
        setPreview(next);
        setPreviewFeedback("Updated Play #"+(editingPreviewIndex+1)+" on My Playslip.");
        showEntryFeedback("success","Play updated.",next,editingPreviewIndex);
        setEditingPreviewIndex(null);
        setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));
        setManualAddFocusTick(tick=>tick+1);
        return;
      }
      if(preview.some(item=>previewEntryKey(game,item)===previewEntryKey(game,entry))){
        setBuilderErr("Duplicate plays were skipped.");
        showEntryFeedback("warning","Duplicate skipped: "+digitsText(checked.numbers)+".",preview,null);
        return;
      }
      if(preview.length>=maxPreviewPlays){
        setBuilderErr("Strategies can include up to "+STRATEGY_MAX_PLAYS.toLocaleString()+" plays.");
        return;
      }
      const next=[...preview,entry];
      manualFocusRestoreRef.current={x:window.scrollX||0,y:window.scrollY||0};
      setPreview(next);
      const typedDigits=Array.isArray(checked.numbers)?checked.numbers.join(""):digitsText(checked.numbers);
      const addedMessage="Added "+typedDigits+" - "+next.length+" play"+(next.length===1?"":"s")+" - "+previewCostLabel(next)+" estimated";
      setPreviewFeedback(addedMessage);
      showEntryFeedback("success",addedMessage,next,next.length-1,"");
      setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));
      setManualAddFocusTick(tick=>tick+1);
      return;
    }
    setBuilderErr(source+" is coming soon.");
  }
  function editPreviewPlay(index){
    const entry=preview[index];
    const nums=previewEntryNumbers(entry);
    if(!nums.length)return;
    setManualBoxes(numberBoxValuesFromNumbers(nums,cfg.count));
    if(isPickGame(game)){
      const rules=previewEntryPickRules(game,entry);
      setPickPlayType(rules.playType||"Straight");
      setPickWager(Number(rules.wager)||1);
      setPickMultiple(Number(rules.multiple)||1);
      setPickFireball(!!rules.fireball);
    }
    setSource("Manual");
    setEditingPreviewIndex(index);
    setPreviewFeedback("Editing Play #"+(index+1)+".");
    setBuilderErr("");
  }
  function deletePreviewPlay(index){
    const next=preview.filter((_,i)=>i!==index);
    setPreview(next);
    setPreviewFeedback("Removed Play #"+(index+1)+".");
    showEntryFeedback("success","Play deleted.",next,null);
    if(editingPreviewIndex===index){
      setEditingPreviewIndex(null);
      setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));
    }else if(editingPreviewIndex!==null&&index<editingPreviewIndex){
      setEditingPreviewIndex(editingPreviewIndex-1);
    }
  }
  function chooseGame(nextGame){
    if(!gameAllowedForPlan(nextGame,myGames,planTier,trialActive)){
      onLimit?.("pick_game");
      return;
    }
    setGame(nextGame);
    const nextCfg=gameNumberConfig(nextGame);
    setManualBoxes(numberBoxValuesFromNumbers([],nextCfg.count));
    setPreview([]);
    setBulkText("");
    setPickPlayType("Straight");
    setPickWager(1);
    setPickMultiple(1);
    setPickFireball(false);
    setPlayslipDraw("evening");
    setPlaySize(1);
    setQuickPickCount(1);
    setEditingPreviewIndex(null);
    setPreviewFeedback("");
    setEntryFeedback(null);
    setHighlightPreviewIndex(null);
    setBuilderErr("");
    setImportSummary(null);
  }
  function fillQuickPickForGame(){
    setSource("Quick Pick");
    const count=Math.max(1,Math.min(25,Number(quickPickCount)||1));
    appendPreviewPlays(Array.from({length:count},()=>makePreviewEntryForGame(game,rndGamePick(game),pickPlayType,pickWager,pickFireball,pickMultiple)));
    setPreviewFeedback("Added "+count+" Quick Pick"+(count===1?"":"s")+" to My Playslip.");
    setBuilderErr("");
  }
  function addBulkFantasy5(){
    setBuilderErr("");
    if(game!=="fantasy5"){setBuilderErr("Bulk paste is available for Fantasy 5.");return;}
    const parsed=parseFantasy5Bulk(bulkText);
    if(parsed.errors.length){setBuilderErr(parsed.errors.slice(0,5).join(" ")+(parsed.errors.length>5?" ...":""));return;}
    if(!parsed.valid.length){setBuilderErr("Paste one Fantasy 5 play per line.");return;}
    appendPreviewPlays(parsed.valid.map(nums=>makePreviewEntryForGame(game,nums,pickPlayType,pickWager,pickFireball,pickMultiple)));
    setPreviewFeedback("Added "+parsed.valid.length+" pasted Fantasy 5 play"+(parsed.valid.length===1?"":"s")+" to My Playslip.");
    setBulkText("");
  }
  async function saveStrategy(){
    if(saveBusy)return;
    setBuilderErr("");
    if(!(name||"").trim()){setBuilderErr("Name your strategy before saving.");setCreateStep(2);return;}
    if(source==="Upload"||source==="Import Later"){setBuilderErr(source+" is coming soon. Use Manual or Quick Pick for now.");setCreateStep(3);return;}
    let draft=preview;
    if(!draft.length){
      setBuilderErr("Tap Add Play before saving to My Playslips.");
      setCreateStep(3);
      return;
    }
    const pickRules=isPickGame(game)?pickStrategyRulesFromPreview(game,draft,pickPlayType,pickWager,pickFireball,pickMultiple):null;
    const form={name,game,source,playSize:draft.length,pickRules,preferredDraw:playslipDraw,onboardingContinuation};
    if(activeEditId){
      if(onboardingContinuation){
        setSaveBusy(true);
        try{
          const id=await onUpdateStrategy?.(activeEditId,form,draft);
          if(id){
            setPreview([]);
            setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));
            setImportTargetId("");
            navigate("/my-strategies");
          }
        }finally{
          setSaveBusy(false);
        }
        return;
      }
      setPendingEditSave({strategyId:activeEditId,form,draft});
      return;
    }
    setSaveBusy(true);
    try{
      const created=await onCreateStrategy(form,draft,false,{returnMeta:true});
      const id=typeof created==="object"&&created?created.id:created;
      if(id){
        setPreview([]);
        setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));
        setImportTargetId("");
        const savedRecord={
          id,
          baseName:name,
          game,
          source,
          version:1,
          playSize:draft.length,
          pickRules,
          preferredDraw:playslipDraw,
          primaryNumbers:previewEntryNumbers(draft[0]||[]),
          createdAt:new Date().toISOString(),
        };
        if(created?.firstStrategy){
          setPostSaveChoice({id,record:savedRecord});
        }else{
          setSaveConfirmation({id,record:savedRecord});
        }
      }
    }finally{
      setSaveBusy(false);
    }
  }
  async function confirmEditSave(){
    if(!pendingEditSave||saveBusy)return;
    setSaveBusy(true);
    try{
      const id=await onUpdateStrategy?.(pendingEditSave.strategyId,pendingEditSave.form,pendingEditSave.draft);
      if(id){
        setPreview([]);
        setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));
        setImportTargetId("");
        setPendingEditSave(null);
        navigate("/my-strategies");
      }
    }finally{
      setSaveBusy(false);
    }
  }
  function autoPreviewForSource(option){
    setSource(option);
    setBuilderErr("");
  }
  function exportStrategy(item){
    const entries=isPickGame(item.record?.game||"fantasy5")?strategySnapshotPlayEntries(item.record):strategyNumbersForRecord(item.id,item.record,plays);
    const numbers=entries.map(entry=>previewEntryNumbers(entry));
    const payload={
      name:strategyDisplayName(item.id,item.record),
      strategyId:item.id,
      game:item.record?.game||"fantasy5",
      status:item.record?.status||"Draft",
      version:item.record?.version||1,
      playCount:numbers.length,
      estimatedCost:strategyEstimatedCost(item.record,numbers),
      pickRules:item.record?.pickRules||null,
      fingerprint:item.record?.fingerprint||null,
      plays:isPickGame(item.record?.game||"fantasy5")
        ?entries.map(entry=>({numbers:previewEntryNumbers(entry),pickRules:previewEntryPickRules(item.record?.game||"fantasy5",entry)}))
        :numbers,
      exportedAt:new Date().toISOString(),
      notice:"Strategy data export. This recreates numbers and wager rules; it does not include the owner's account, ROI, schedule, or live history.",
    };
    const json=JSON.stringify(payload,null,2);
    const blob=new Blob([json],{type:"application/json"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url;
    a.download=slugifyShare((payload.name||"lottotracker-strategy")+"-strategy")+".json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    if(navigator.clipboard)navigator.clipboard.writeText(json).catch(()=>{});
    setExportedId(item.id);
    setTimeout(()=>setExportedId(""),1600);
  }
  async function shareStrategy(item){
    try{
      const choices=drawChoicesForStrategy(item);
      const nextOpenDraw=choices[0]||null;
      if(!nextOpenDraw){
        setBuilderErr("Choose an open draw before sharing today's picks. Results unlock only from a lineup frozen before the drawing.");
        return{cancel:true};
      }
      const frozen=await onAddToLineup?.(item.id,{draws:[nextOpenDraw],trackingMode:"watch",source:"share_today"});
      if(frozen===false){
        setBuilderErr("We could not freeze this playslip before the draw. Try again before sales close.");
        return{cancel:true};
      }
      const data=await onCreatePublicShare?.(item.id,{strategyName:strategyDisplayName(item.id,item.record)});
      setSharedId(item.id);
      setTimeout(()=>setSharedId(""),1600);
      if(data?.link)return{payload:{link:data.link,isPublic:true}};
    }catch(e){
      setBuilderErr(e?.message||"We could not prepare that share link.");
      return{cancel:true};
    }
    setBuilderErr("We could not prepare that share link.");
    return{cancel:true};
  }
  function proMetricButton(label){
    return(
      <button type="button" className={"btn btn-sm btn-p "+(billingBusy?"loading":"")} onClick={()=>onUpgrade?.("pro")} disabled={billingBusy} style={{width:"100%",marginTop:4,textAlign:"left",whiteSpace:"normal",lineHeight:1.2}}>
        {label==="ROI"?"Upgrade to Pro to see ROI":"Upgrade to Pro to see Best Hit"}
      </button>
    );
  }
  function toggleWhatIf(strategyId){
    const opening=whatIfOpenId!==strategyId;
    setWhatIfOpenId(opening?strategyId:"");
    if(opening){
      const record=strategyRecordForId(strategyId,trust.records,plays);
      onLoadHistoricalDraws?.(record?.game||"fantasy5");
    }
  }
  function drawChoiceClosed(entry){
    return drawEntryIsClosedOrLater(entry);
  }
  function drawChoiceKey(entry){
    return drawLockIdForDraw(entry)||[entry?.game,entry?.draw_date,entry?.draw_time].join(":");
  }
  function drawChoicesForStrategy(item){
    const savedGame=(plays||[]).find(p=>strategyIdForPlay(p)===item?.id&&p.game)?.game;
    const gameId=item?.record?.game||savedGame||"fantasy5";
    const statusEntries=(drawStatus?.entries||[]).filter(entry=>entry.game===gameId);
    const directEntries=statusEntries.length?statusEntries:getDrawStatusService([gameId],new Date(),draws).entries;
    return directEntries
      .filter(entry=>entry.game===gameId&&!drawChoiceClosed(entry))
      .sort((a,b)=>(Number(a.sortAt)||0)-(Number(b.sortAt)||0))
      .slice(0,4);
  }
  function openPlayToday(item,mode="live"){
    setPlayTodayChoice(item);
    setPlayTodayMode(normalizeTrackingMode(mode));
    setSelectedPlayTodayDraws([]);
    setPostSaveChoice(null);
    setBuilderErr("");
  }
  function togglePlayTodayDraw(key){
    setSelectedPlayTodayDraws(prev=>prev.includes(key)?prev.filter(id=>id!==key):[...prev,key]);
  }
  async function confirmPlayToday(){
    if(!playTodayChoice)return;
    const choices=drawChoicesForStrategy(playTodayChoice);
    const selected=choices.filter(entry=>selectedPlayTodayDraws.includes(drawChoiceKey(entry)));
    if(!selected.length){
      setBuilderErr("Choose at least one open draw or keep the playslip in My Playslips.");
      return;
    }
    const added=await onAddToLineup?.(playTodayChoice.id,{draws:selected,trackingMode:playTodayMode});
    if(added){
      setPlayTodayChoice(null);
      setPlayTodayMode("live");
      setSelectedPlayTodayDraws([]);
      setPostSaveChoice(null);
      navigate("/my-strategies");
    }
  }
  function openRename(item){
    setRenameChoice(item);
    setRenameValue(strategyDisplayName(item.id,item.record));
    setBuilderErr("");
  }
  async function saveRename(){
    const clean=renameValue.trim();
    if(!renameChoice||!clean){setBuilderErr("Enter a strategy name.");return;}
    const renamed=await onRenameStrategy?.(renameChoice.id,clean);
    if(renamed!==false){
      setRenameChoice(null);
      setRenameValue("");
    }
  }
  const builderPreviewPanel=(
    <div className="panel playslip-panel">
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:10,marginBottom:10}}>
        <div>
          <div className="sec-title" style={{fontSize:22}}>My Playslip</div>
          <div style={{fontSize:12,color:"#555",lineHeight:1.4,marginTop:4}}>
            {preview.length} play{preview.length===1?"":"s"} on My Playslip. {editingPreviewIndex!==null?"Play #"+(editingPreviewIndex+1)+" is open for editing. Tap Update Play to update this playslip.":"Type your numbers or generate a Quick Pick. New plays appear here immediately."}
          </div>
        </div>
        {previewFeedback&&<span className="sec-badge" style={{background:"#008577"}}>{previewFeedback}</span>}
      </div>
      <div className="playslip-list" style={{display:"grid",gap:8,maxHeight:300,overflowY:"auto",paddingRight:4}}>
        {(preview.length?preview:((source==="Manual"||source==="Quick Pick")?[]:buildStrategyPreview(source,Math.min(playSize,5),plays,draws))).map((entry,i)=>{
          const nums=previewEntryNumbers(entry);
          return(
          <div key={i} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:8,alignItems:"center",padding:"7px 8px",borderBottom:"1px solid #f0f0f0",borderRadius:8,background:editingPreviewIndex===i||highlightPreviewIndex===i?"#fffaf0":"transparent",outline:editingPreviewIndex===i||highlightPreviewIndex===i?"1px solid #ffd54f":"none"}}>
            <span style={{fontSize:11,color:editingPreviewIndex===i||highlightPreviewIndex===i?"#c8860a":"#888",fontWeight:900}}>{editingPreviewIndex===i?"Editing #"+(i+1):"#"+(i+1)}</span>
            <div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{nums.map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div>
              {isPickGame(game)&&<div style={{fontSize:11,color:"#555",fontWeight:800,marginTop:4}}>{previewEntryLabel(game,entry)}</div>}
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",justifyContent:"end"}}>
              <button type="button" className={"btn btn-sm "+(editingPreviewIndex===i?"btn-p":"btn-s")} onClick={()=>editPreviewPlay(i)}>{editingPreviewIndex===i?"Editing":"Edit"}</button>
              <button type="button" className="btn btn-sm btn-s" onClick={()=>deletePreviewPlay(i)} aria-label={"Delete Play #"+(i+1)} title="Delete play" style={{minWidth:38}}>x</button>
            </div>
          </div>
        )})}
        {(source==="Manual"||source==="Quick Pick")&&!preview.length&&<EmptyState icon="+" title="No plays added yet." text="Enter numbers or tap Quick Pick, then Add Play."/>}
      </div>
      <div className="playslip-save-panel" style={{marginTop:14,borderTop:"1px solid #eee",paddingTop:14,display:"grid",gap:14}}>
        <div className="playslip-cost-card" style={{border:"2px solid #008577",borderRadius:8,padding:"12px 14px",background:"#eefaf6",display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>Estimated Cost</div>
            <strong style={{fontFamily:"'Barlow Condensed',sans-serif",color:"#002855",fontSize:38,lineHeight:1}}>{previewCostLabel(preview)}</strong>
          </div>
          <div style={{fontSize:12,color:"#006a5e",fontWeight:900,lineHeight:1.3,textAlign:"right"}}>{preview.length?preview.length+" play"+(preview.length===1?"":"s")+" saved on this playslip":"Add a play to calculate the ticket cost"}</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(135px,1fr))",gap:10}}>
          <div style={{border:"1px solid #edf1f0",borderRadius:8,padding:"9px 10px",background:"#f8fbfa"}}>
            <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>Draw</div>
            <strong style={{color:"#002855",fontSize:18}}>{playslipDrawLabel}</strong>
          </div>
          <div style={{border:"1px solid #edf1f0",borderRadius:8,padding:"9px 10px",background:"#f8fbfa"}}>
            <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>Fireball</div>
            <strong style={{color:isPickGame(game)&&playslipFireballLabel==="Yes"?"#c8102e":"#002855",fontSize:18}}>{playslipFireballLabel}</strong>
          </div>
        </div>
        {preview.length>0&&(
          <div style={{fontSize:13,color:"#555",lineHeight:1.45}}>
            <strong style={{color:"#002855"}}>Maximum potential payout:</strong> {totalPotential}
          </div>
        )}
        <button type="button" className={"btn btn-p "+(saveBusy?"loading":"")} onClick={saveStrategy} disabled={saveBusy||!preview.length}>
          {saveBusy?"Saving...":createPlayslipButtonText}
        </button>
      </div>
      <div className="mobile-playslip-summary" aria-live="polite">
        <strong>{preview.length} play{preview.length===1?"":"s"}</strong>
        <span>{previewCostLabel(preview)}</span>
        <button type="button" className="btn btn-sm btn-p" onClick={saveStrategy} disabled={saveBusy||!preview.length}>
          {saveBusy?"Saving...":"Create"}
        </button>
      </div>
    </div>
  );
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">{builderHeading}</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:780}}>
            {libraryOnly?"Build, organize, and play your saved lottery strategies in one place.":"Fill out a playslip, save it, then reuse, test and share it."}
          </p>
        </div>
        <span className="sec-badge">Ready to save</span>
      </div>

      {libraryOnly&&proLocked&&(
        <div className="panel" style={{border:"2px solid #ffd54f",background:"#fffaf0"}}>
          <div className="sec-hdr" style={{marginBottom:8}}>
            <span className="sec-title" style={{fontSize:22}}>Unlock Pro</span>
            <span className="sec-badge" style={{background:"#c8860a"}}>$9.99/mo</span>
          </div>
          <div style={{fontSize:14,color:"#5a4a00",lineHeight:1.5,marginBottom:12}}>
            Pro unlocks ROI, Best Hit, unlimited saved strategies, and every launch-ready Pick game.
          </div>
          <button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} onClick={()=>onUpgrade?.("pro")} disabled={billingBusy}>
            {billingBusy?"Opening...":"Upgrade to Pro"}
          </button>
        </div>
      )}

      {!libraryOnly&&<div className="strategy-builder-grid">
        {createStep===3&&builderPreviewPanel}
        <div className="panel builder-main-panel">
          <div className="sec-title" style={{fontSize:24,marginBottom:14}}>{activeEditId?"Edit Playslip":"Play Builder"}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
            {[1,2,3].map(n=><span key={n} className="sec-badge" style={{background:createStep===n?"#008577":"#5d6675"}}>Step {n}</span>)}
          </div>
          {createStep===1&&(
            <div>
              <div style={{fontSize:11,fontWeight:900,color:"#555",textTransform:"uppercase",marginBottom:8}}>Choose Game</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(135px,1fr))",gap:8,marginBottom:14}}>
                {launchReadyGames().map(g=>{
                  const unlocked=gameAllowedForPlan(g.id,myGames,planTier,trialActive);
                  return(
                    <button
                      key={g.id}
                      type="button"
                      className={"btn btn-sm "+(game===g.id?"btn-p":"btn-s")}
                      onClick={()=>chooseGame(g.id)}
                      style={!unlocked?{opacity:.58,borderStyle:"dashed"}:null}
                      title={!unlocked?"Pro unlocks "+g.label:""}
                    >
                      {g.label}{!unlocked&&<span style={{marginLeft:6,fontSize:10,fontWeight:900,color:"#c8860a"}}>PRO</span>}
                    </button>
                  );
                })}
              </div>
              <button type="button" className="btn btn-p" onClick={()=>setCreateStep(2)}>Continue</button>
            </div>
          )}
          {createStep===2&&(
            <div>
              <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                Strategy Name
                <input className="add-inp" value={name} onChange={e=>{setName(e.target.value);setNameWasEdited(true);setBuilderErr("");}} placeholder={firstStrategyName} style={{marginTop:6}}/>
              </label>
              <div style={{display:"flex",gap:7,flexWrap:"wrap",margin:"12px 0 16px"}}>
                {STRATEGY_NAME_EXAMPLES.map(example=><button key={example} type="button" className="btn btn-sm btn-s" onClick={()=>{setName(example);setNameWasEdited(true);}}>{example}</button>)}
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"space-between",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-s" onClick={()=>setCreateStep(1)}>Back</button>
                <button type="button" className="btn btn-p" onClick={()=>{if(!(name||"").trim()){setBuilderErr("Name your strategy before continuing.");return;}setCreateStep(3);}}>Continue</button>
              </div>
            </div>
          )}
          {createStep===3&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:8}}>Fill out your playslip</div>
                <div style={{fontWeight:900,color:"#002855"}}>{gameLabel(game)} - {cfg.count} box{cfg.count===1?"":"es"}</div>
              </div>
              <div style={{border:"1px solid #dbeee9",borderRadius:12,padding:14,background:"#fff",order:3}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",flexWrap:"wrap",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Import Strategy</div>
                    <div style={{fontSize:12,color:"#555",lineHeight:1.35}}>Paste plays or upload TXT, CSV, or Excel. LottoTracker removes duplicates and adds valid plays to My Playslip.</div>
                  </div>
                  <span className="sec-badge" style={{background:"#002855"}}>{importSummary?.accepted||0} added</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10,marginBottom:10}}>
                  <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                    Import Into
                    {editId?(
                      <div className="add-inp" style={{marginTop:5,width:"100%",background:"#f8fbfa",color:"#002855",fontWeight:800}}>
                        Current strategy
                      </div>
                    ):(
                      <select className="add-inp" value={importTargetId} onChange={e=>loadImportTarget(e.target.value)} style={{marginTop:5,width:"100%"}}>
                        <option value="">New strategy draft</option>
                        {existingImportTargets.map(item=><option key={item.id} value={item.id}>{strategyDisplayName(item.id,item.record)} ({gameLabel(item.record?.game||"fantasy5")})</option>)}
                      </select>
                    )}
                  </label>
                  <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                    Import As
                    <select className="add-inp" value={importMode} onChange={e=>{setImportMode(e.target.value);setBuilderErr("");}} disabled={!!importTargetId} style={{marginTop:5,width:"100%"}}>
                      <option value="auto">Auto detect</option>
                      {launchReadyGames().map(g=><option key={g.id} value={g.id}>{g.label}</option>)}
                    </select>
                  </label>
                </div>
                {isPickGame(importTargetId?(strategyRecordForId(importTargetId,trust.records,plays).game||game):(importMode==="auto"?game:importMode))&&(
                  <div style={{border:"1px solid #dbeee9",borderRadius:12,background:"#f8fbfa",padding:12,marginBottom:10}}>
                    <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Apply These Rules To Imported Plays</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10}}>
                      <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                        Play Type
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(112px,1fr))",gap:7,marginTop:6}}>
                          {(PICK_PLAY_TYPES[importTargetId?(strategyRecordForId(importTargetId,trust.records,plays).game||game):(importMode==="auto"?game:importMode)]||PICK_PLAY_TYPES.pick4).map(type=>(
                            <button key={type} type="button" className={"btn btn-sm "+(pickPlayType===type?"btn-p":"btn-s")} onClick={()=>setPickPlayType(type)}>{type}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:6}}>Amount</div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:7}}>
                          {[0.5,1].map(amount=>(
                            <button key={amount} type="button" className={"btn btn-sm "+(Number(pickWager)===amount?"btn-p":"btn-s")} onClick={()=>setPickWager(amount)}>{amount===0.5?"$0.50":"$1.00"}</button>
                          ))}
                        </div>
                      </div>
                      <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                        Multiple Plays
                        <input className="add-inp" type="number" min="1" max="100" value={pickMultiple} onChange={e=>setPickMultiple(Math.max(1,Math.min(100,Number(e.target.value)||1)))} style={{marginTop:5}}/>
                      </label>
                      <div>
                        <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:6}}>Fireball</div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:7}}>
                          {[true,false].map(value=>(
                            <button key={String(value)} type="button" className={"btn btn-sm "+(pickFireball===value?"btn-p":"btn-s")} onClick={()=>setPickFireball(value)}>{value?"Yes":"No"}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <textarea
                  className="add-inp"
                  value={importText}
                  onChange={e=>setImportText(e.target.value)}
                  placeholder={"Paste one play per line:\n1 2 3 4 5\n6,7,8,9,10\n2-5-8"}
                  rows={5}
                  style={{width:"100%",resize:"vertical",marginBottom:10}}
                />
                <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                  <button type="button" className="btn btn-sm btn-p" onClick={()=>importLines(importLinesFromText(importText),"Pasted numbers")}>Import Paste</button>
                  {importPendingLines.length>0&&importMode!=="auto"&&<button type="button" className="btn btn-sm btn-p" onClick={()=>importLines(importPendingLines,importFileName||"Pending import")}>Import As {gameLabel(importMode)}</button>}
                  <label className={"btn btn-sm btn-s "+(importBusy?"loading":"")} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:importBusy?"not-allowed":"pointer"}}>
                    {importBusy?"Reading...":"Upload TXT / CSV / XLSX"}
                    <input type="file" accept=".txt,.csv,.xlsx,.json,text/plain,text/csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleImportFile} disabled={importBusy} style={{display:"none"}}/>
                  </label>
                </div>
                {importSummary&&(
                  <div style={{marginTop:12,display:"grid",gap:8}}>
                    {importSummary.needsGame&&<div className="auth-err" style={{margin:0}}>{importSummary.reason||"Choose a game to continue."}</div>}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8}}>
                      {[
                        ["Total Generated",importSummary.total||0,"#002855"],
                        ["Duplicates Removed",importSummary.duplicatesRemoved||0,"#c8860a"],
                        ["Unique Plays Added",importSummary.accepted||0,"#008577"],
                        ["Invalid Plays",importSummary.invalid||0,"#c8102e"],
                        ["Over Limit",importSummary.capped||0,"#5d6675"],
                      ].map(([label,value,color])=>(
                        <div key={label} style={{border:"1px solid #edf1f0",background:"#f8fbfa",borderRadius:10,padding:"8px 10px"}}>
                          <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>{label}</div>
                          <strong style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,color}}>{value}</strong>
                        </div>
                      ))}
                    </div>
                    {(Number(importSummary.total||0)!==Number(importSummary.accepted||0))&&(
                      <div className="auth-err" style={{margin:0,lineHeight:1.45}}>
                        Integrity warning: imported source count is {Number(importSummary.total||0).toLocaleString()}, but only {Number(importSummary.accepted||0).toLocaleString()} play{Number(importSummary.accepted||0)===1?"":"s"} are on My Playslip.
                        Review duplicates, invalid rows, and limits before creating the playslip. LottoTracker will not treat this as complete unless the playslip count matches what you expected.
                      </div>
                    )}
                    {importSummary.game&&<div style={{fontSize:12,color:"#555",fontWeight:800}}>Imported as {gameLabel(importSummary.game)} from {importSummary.fileName}.</div>}
                    {importSummary.invalidRows?.length>0&&(
                      <div style={{fontSize:12,color:"#7a1b1b",background:"#fff0f0",border:"1px solid #ffcccc",borderRadius:10,padding:"8px 10px",lineHeight:1.35}}>
                        {importSummary.invalidRows.map(row=><div key={row.line}>Line {row.line}: {row.error}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {(source==="Manual"||source==="Quick Pick")&&(
                <div ref={manualEntryRef} style={{border:"1px solid #dbeee9",borderRadius:12,padding:16,background:"#f8fbfa",order:1}}>
                  <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>
                    Type My Numbers
                  </div>
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:6}}>Draw</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:9}}>
                      {[
                        ["midday","Midday"],
                        ["evening","Evening"],
                        ["both","Both"],
                      ].map(([value,label])=>(
                        <button key={value} type="button" className={"btn btn-sm "+(playslipDraw===value?"btn-p":"btn-s")} onClick={()=>{setPlayslipDraw(value);setBuilderErr("");}}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {editingPreviewIndex!==null&&(
                    <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px",marginBottom:12,color:"#5a4a00",fontSize:12,lineHeight:1.4,fontWeight:800}}>
                      Editing Play #{editingPreviewIndex+1}. Change the boxes or Pick rules, then tap <strong>Update Play</strong> to update My Playslip before saving.
                    </div>
                  )}
                  <NumberBoxes values={manualBoxes} count={cfg.count} maxDigits={cfg.maxDigits} label={cfg.label} onChange={values=>{setManualBoxes(values);setSource("Manual");setBuilderErr("");}}/>
                  <div aria-live="polite" style={{marginTop:12,background:"#fff",border:"1px solid #dbeee9",borderRadius:10,padding:"10px 12px"}}>
                    <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>Current Play</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#002855",lineHeight:1}}>{stagedNumberText}</div>
                    <div style={{marginTop:7,fontSize:12,fontWeight:900,color:stagedReady?"#008577":hasStagedDigits?"#c8860a":"#5d6675"}}>
                      {stagedReady?"Ready to Add \u2713":hasStagedDigits?"Finish the boxes to add this play":"Tap the boxes to start a play"}
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginTop:10}}>
                      <button type="button" className="btn btn-sm btn-p" onClick={addManualPlay}>{addPlayButtonText}</button>
                      {editingPreviewIndex!==null&&<button type="button" className="btn btn-sm btn-s" onClick={()=>{setEditingPreviewIndex(null);setManualBoxes(numberBoxValuesFromNumbers([],cfg.count));setBuilderErr("");}}>Cancel Edit</button>}
                      {preview.length>0&&<span style={{fontSize:12,color:"#002855",fontWeight:900}}>{preview.length} play{preview.length===1?"":"s"} - {previewCostLabel(preview)} estimated</span>}
                    </div>
                    {entryFeedback&&(
                      <div style={{marginTop:10,background:entryFeedback.kind==="warning"?"#fffaf0":"#eefaf6",border:"1px solid "+(entryFeedback.kind==="warning"?"#ffd54f":"#bfe8db"),borderRadius:10,padding:"9px 10px",fontSize:13,color:entryFeedback.kind==="warning"?"#5a4a00":"#006a5e",fontWeight:900,lineHeight:1.35}}>
                        <div>{entryFeedback.message}</div>
                        {entryFeedback.detail&&<div style={{fontSize:12,color:"#555",fontWeight:800,marginTop:3}}>{entryFeedback.detail}</div>}
                      </div>
                    )}
                  </div>
                  <div style={{fontSize:12,color:"#555",lineHeight:1.35,marginTop:8}}>
                    {editingPreviewIndex!==null
                      ?"Tap Update Play to apply the staged changes to My Playslip. The saved playslip changes only after you save."
                      :game==="fantasy5"?"Fantasy 5 uses five unique numbers from 1 to 36.":gameLabel(game)+" uses "+cfg.count+" digit boxes from "+cfg.min+" to "+cfg.max+"."}
                  </div>
                  {isPickGame(game)&&(
                    <div style={{marginTop:18,border:"1px solid #dbeee9",borderRadius:12,background:"#fff",padding:16}}>
                      <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Live Pick Game Calculator</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:14,marginBottom:14}}>
                        <div style={{gridColumn:"1 / -1"}}>
                          <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:6}}>Play Type</div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(112px,1fr))",gap:9}}>
                            {(PICK_PLAY_TYPES[game]||["Straight"]).map(type=>(
                              <button key={type} type="button" className={"btn btn-sm "+(pickPlayType===type?"btn-p":"btn-s")} onClick={()=>setPickPlayType(type)}>
                                {type}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:6}}>Amount</div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:9}}>
                            {[0.5,1].map(amount=>(
                              <button key={amount} type="button" className={"btn btn-sm "+(Number(pickWager)===amount?"btn-p":"btn-s")} onClick={()=>setPickWager(amount)}>
                                {amount===0.5?"$0.50":"$1.00"}
                              </button>
                            ))}
                          </div>
                        </div>
                        <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                          Multiple Plays
                          <input className="add-inp" type="number" min="1" max="100" value={pickMultiple} onChange={e=>setPickMultiple(Math.max(1,Math.min(100,Number(e.target.value)||1)))} style={{marginTop:5}}/>
                        </label>
                        <div>
                          <div style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase",marginBottom:6}}>Fireball</div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:9}}>
                            {[true,false].map(value=>(
                              <button key={String(value)} type="button" className={"btn btn-sm "+(pickFireball===value?"btn-p":"btn-s")} onClick={()=>setPickFireball(value)}>
                                {value?"Yes":"No"}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
                        <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:10,padding:"10px 12px"}}>
                          <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>Cost</div>
                          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:"#002855"}}>{pickCalc?.costLabel||"$0"}</div>
                          {pickFireball&&<div style={{fontSize:11,color:"#c8102e",fontWeight:800}}>Fireball included</div>}
                        </div>
                        <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px"}}>
                          <div style={{fontSize:10,color:"#5a4a00",fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>Maximum Potential Win</div>
                          <div style={{display:"grid",gap:4,marginTop:5}}>
                            {(pickCalc?.payouts||[]).map(p=>(
                              <div key={p.label} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:13,color:p.disabled?"#999":"#002855"}}>
                                <strong>{p.label}</strong>
                                <span>{p.disabled?"Not available":moneyLabel(p.value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div style={{fontSize:11,color:"#555",lineHeight:1.35,marginTop:10}}>{pickCalc?.note}</div>
                    </div>
                  )}
                  {game==="fantasy5"&&(
                    <div style={{marginTop:14,border:"1px solid #dbeee9",borderRadius:12,background:"#fff",padding:14}}>
                      <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Bulk Paste Fantasy 5</div>
                      <textarea className="add-inp" value={bulkText} onChange={e=>setBulkText(e.target.value)} placeholder={"1 2 3 4 5\n6 7 8 9 10"} rows={4} style={{width:"100%",resize:"vertical",marginBottom:8}}/>
                      <button type="button" className="btn btn-sm btn-s" onClick={addBulkFantasy5}>Add Pasted Plays</button>
                    </div>
                  )}
                </div>
              )}
              <div style={{border:"1px solid #dbeee9",borderRadius:12,padding:14,background:"#fff",order:2}}>
                <div style={{fontSize:11,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Quick Pick</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,alignItems:"end"}}>
                  <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                    How many quick picks?
                    <input className="add-inp" type="number" min="1" max="25" value={quickPickCount} onChange={e=>setQuickPickCount(Math.max(1,Math.min(25,Number(e.target.value)||1)))} style={{marginTop:5}}/>
                  </label>
                  <button type="button" className="btn btn-sm btn-s" onClick={fillQuickPickForGame}>Add Quick Pick Play{quickPickCount>1?"s":""}</button>
                </div>
                {isPickGame(game)&&<div style={{fontSize:12,color:"#555",lineHeight:1.35,marginTop:8}}>Quick Picks use the play type, amount, multiple, and Fireball settings shown above.</div>}
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"space-between",flexWrap:"wrap",order:4}}>
                <button type="button" className="btn btn-s" onClick={()=>activeEditId?navigate("/my-strategies"):setCreateStep(2)}>Back</button>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button type="button" className={"btn btn-p "+(saveBusy?"loading":"")} onClick={saveStrategy} disabled={saveBusy||!preview.length}>{saveBusy?"Saving...":createPlayslipButtonText}</button>
                </div>
              </div>
            </div>
          )}
          {builderErr&&<div className="auth-err" style={{marginBottom:12}}>{builderErr}</div>}
        </div>
      </div>}

      {postSaveChoice&&(
        <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:960,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div className="panel" style={{width:"min(560px,100%)",margin:0}}>
            <div className="sec-title" style={{fontSize:28,marginBottom:8}}>Congratulations — you built your first strategy.</div>
            <p style={{fontSize:14,color:"#555",lineHeight:1.45,marginBottom:14}}>
              <strong>{strategyDisplayName(postSaveChoice.id,postSaveChoice.record)}</strong> is saved in My Playslips.
            </p>
            <div style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:12,padding:"12px 14px",fontSize:15,color:"#002855",fontWeight:900,lineHeight:1.4,marginBottom:10}}>
              {firstSavedNumberInsight(postSaveChoice.record)}
            </div>
            <p style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:14}}>
              A strategy is a saved playslip you can reuse, test and share. Past appearances do not increase future odds.
            </p>
            <div style={{display:"grid",gap:10,marginBottom:14}}>
              <button type="button" className="btn btn-p" style={{justifyContent:"flex-start",whiteSpace:"normal",lineHeight:1.35,textAlign:"left"}} onClick={()=>{setPostSaveChoice(null);navigate(strategyPerformancePath(postSaveChoice.id,postSaveChoice.record?.game||"fantasy5","365","both"));}}>
                View Historical Results
              </button>
              <button type="button" className="btn btn-p" style={{justifyContent:"flex-start",whiteSpace:"normal",lineHeight:1.35,textAlign:"left"}} onClick={()=>{setPostSaveChoice(null);navigate(strategyEditPath(postSaveChoice.id,{onboarding:true}));}}>
                Add More Plays
              </button>
              <button type="button" className="btn btn-s" style={{justifyContent:"flex-start",whiteSpace:"normal",lineHeight:1.35,textAlign:"left"}} onClick={()=>{setPostSaveChoice(null);navigate("/my-strategies");}}>
                Keep it saved in My Playslips
              </button>
            </div>
          </div>
        </div>
      )}

      {saveConfirmation&&(
        <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:960,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div className="panel" style={{width:"min(500px,100%)",margin:0}}>
            <div className="sec-title" style={{fontSize:28,marginBottom:8}}>Saved to My Playslips</div>
            <p style={{fontSize:14,color:"#555",lineHeight:1.45,marginBottom:14}}>
              <strong>{strategyDisplayName(saveConfirmation.id,saveConfirmation.record)}</strong> is ready to share, play today, or edit later.
            </p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className="btn btn-p" onClick={()=>{setSaveConfirmation(null);navigate("/my-strategies");}}>View My Playslips</button>
              <button type="button" className="btn btn-s" onClick={()=>{setSaveConfirmation(null);openPlayToday({id:saveConfirmation.id,record:saveConfirmation.record},"live");}}>Play Today</button>
              <button type="button" className="btn btn-s" onClick={()=>{setSaveConfirmation(null);navigate("/strategy-builder");}}>Build Another</button>
            </div>
          </div>
        </div>
      )}

      {pendingEditSave&&(
        <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:950,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div className="panel" style={{width:"min(480px,100%)",margin:0}}>
            <div className="sec-title" style={{fontSize:26,marginBottom:8}}>Save Changes?</div>
            <p style={{fontSize:14,color:"#555",lineHeight:1.45,marginBottom:14}}>
              Outcome-changing edits create a new version. Existing scheduled draws and completed results keep their original version.
            </p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className={"btn btn-p "+(saveBusy?"loading":"")} onClick={confirmEditSave} disabled={saveBusy}>{saveBusy?"Saving...":"Continue"}</button>
              <button type="button" className="btn btn-s" onClick={()=>setPendingEditSave(null)} disabled={saveBusy}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {libraryOnly&&<>
      <div className="sec-hdr" style={{marginTop:24}}><span className="sec-title">My Playslips</span><span className="sec-badge">{saved.length} total</span></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:16}}>
        {saved.map(item=>{
          const row=item.row;
          const lineupItem=normalizeLineup(todaysLineup).items.find(lineupEntry=>lineupEntry.id===item.id);
          const strategyNumbers=strategyNumbersForRecord(item.id,item.record,plays);
          const cardStats=strategyTradingCardStats(item,strategyNumbers,lineupItem);
          const playCount=cardStats.playCount;
          const shareRules=shareLineRulesDigest(item.record);
          const cardStatus=cardStats.currentStatus;
          const isPublicStrategy=!!item.record?.published||item.record?.status==="Locked";
          const itemDrawOpen=item.next&&!drawChoiceClosed(item.next);
          const completedResults=vaultResultsForStrategy(item);
          const latestCompletedResult=completedResults[0]||null;
          const resultState=vaultResultStateForStrategy(item,completedResults);
          const sharePayload=shareCardPayload({
            type:"strategy",
            title:cardStats.isWhatIfOnly?"Watch Strategy on LottoTracker":"Watch My Draw on LottoTracker",
            strategyName:strategyDisplayName(item.id,item.record),
            game:gameLabel(item.record?.game||"fantasy5"),
            status:cardStats.isWhatIfOnly?"What-if only":"Watching tonight's draw",
            roi:"View Results",
            drawsTracked:String(playCount),
            createdDate:fmtShortDate(item.record?.createdAt),
            link:shortShareLink("strategy",item.id,isPublicStrategy),
            isPublic:true,
            accent:cardStats.statusColor,
            note:cardStats.isWhatIfOnly?"Saved after this draw. This is What-if only and does not count toward ROI, wins, or rankings.":isPublicStrategy?"Locked before the draw. Ready to share.":"Watch card. Track the draw without affecting public rankings.",
            watchDraw:true,
            joinDraw:false,
            isWhatIfOnly:cardStats.isWhatIfOnly,
            drawTime:formatShareDrawTime(item.next),
            playCount:String(shareRules?.representedPlayCount||playCount),
            numbersPreview:strategyNumbers.slice(0,50),
            numbersShown:Math.min(50,strategyNumbers.length),
            numbersTotal:strategyNumbers.length,
            representedPlayCount:String(shareRules?.representedPlayCount||playCount),
            playType:lineupItem?.playType||"Simulation",
            playTypeLabel:shareRules?.playTypeLabel||sharePlayTypeLabel(item.record?.game||"fantasy5",lineupItem?.playType),
            rulesSummaryLine:shareRules?.summaryLine||"",
            rowSuffixes:shareRules?.rowSuffixes||[],
            rowFireballs:shareRules?.rowFireballs||[],
            combinationsLabel:shareRules?.combinationsLabel||"",
            estimatedCost:cardStats.estimatedCost,
            possibleWin:shareTopPrizeLabel(item.record?.game||"fantasy5"),
            poolMode:"Group Play",
            openSpots:"5 spots available",
            suggestedBuyIn:"$20 each",
            possiblePayout:shareTopPrizeLabel(item.record?.game||"fantasy5"),
            poolStatus:sharePoolStatus(item.next,false),
            fireballNote:shareRules?.fireballNote||shareFireballNote(item.record?.game||"fantasy5",false),
            ctaLabel:cardStats.isWhatIfOnly?"Watch Strategy":"Watch this draw on LottoTracker",
            joinCtaLabel:"Request to Join",
            organizerDisclaimer:"Organizer handles tickets and payments outside LottoTracker.",
          });
          return(
            <div key={item.id} className="sc-card">
              <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                <div>
                  <div className="sc-name" style={{color:"#002855"}}>{strategyDisplayName(item.id,item.record)}</div>
                  <div style={{fontSize:12,color:"#555"}}>{gameLabel(item.record?.game||"fantasy5")} · Created {fmtShortDateTimeET(item.record?.createdAt)}</div>
                </div>
                <div style={{display:"grid",gap:6,justifyItems:"end"}}>
                  <span className="sec-badge" style={{background:cardStats.statusColor}}>{cardStatus}</span>
                  {lineupItem&&<span className="sec-badge" style={{background:trackingModeColor(lineupItem?.trackingMode)}}>{trackingModeLabel(lineupItem.trackingMode)}</span>}
                </div>
              </div>
              <div style={{fontSize:10,color:"#008577",fontWeight:900,textTransform:"uppercase",letterSpacing:1,margin:"2px 0 8px"}}>Strategy Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10,fontSize:12,marginBottom:12}}>
                {[
                  ["Numbers Included",playCount],
                  ["Cost Per Draw",cardStats.estimatedCost],
                  ["Status",lineupItem?trackingModeLabel(lineupItem.trackingMode):"Saved Playslip"],
                  ["Next Draw",item.next?item.next.gameLabel+" "+item.next.drawLabel:"Choose Draw"],
                  ["Version","v"+(Number(item.record?.version)||1)],
                  ["Created",fmtShortDateTimeET(item.record?.createdAt)],
                ].map(([label,value])=>(
                  <div key={label} style={{background:"#f8fbfa",border:"1px solid #edf1f0",borderRadius:10,padding:"9px 10px"}}>
                    <span style={{color:"#555",fontSize:10,fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>{label}</span><br/>
                    <strong style={{color:"#002855",fontFamily:"inherit",fontSize:13}}>{value}</strong>
                  </div>
                ))}
                <div style={{gridColumn:"1 / -1",background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"9px 10px"}}>
                  <span style={{color:"#5a4a00",fontSize:10,fontWeight:900,textTransform:"uppercase",letterSpacing:.5}}>Current Status</span><br/>
                  <strong style={{color:cardStats.statusColor}}>{cardStatus}</strong>
                  <div style={{fontSize:11,color:"#5a4a00",marginTop:3}}>Next draw: {item.next?item.next.gameLabel+" "+item.next.drawLabel+" · "+item.next.countdown:"Choose game"}</div>
                </div>
              </div>
              <div style={{display:"none"}}>
                <div><span style={{color:"#555"}}>Game</span><br/><strong>{gameLabel(item.record?.game||"fantasy5")}</strong></div>
                <div><span style={{color:"#555"}}>ROI</span><br/><strong>{row?.roi==null?"Pending":(row.roi>=0?"+":"")+row.roi.toFixed(0)+"%"}</strong></div>
                <div><span style={{color:"#555"}}>Draws Tracked</span><br/><strong>{scoredDraws.length}</strong></div>
                <div><span style={{color:"#555"}}>Created</span><br/><strong>{fmtShortDateTimeET(item.record?.createdAt)}</strong></div>
                <div><span style={{color:"#555"}}>Last Updated</span><br/><strong>{fmtShortDate(item.record?.updatedAt||item.record?.lockedAt||item.record?.createdAt)}</strong></div>
                <div><span style={{color:"#555"}}>Status</span><br/><strong>{cardStatus}</strong></div>
                <div><span style={{color:"#555"}}>Plays</span><br/><strong>{playCount||item.record?.playSize||0}</strong></div>
                <div style={{gridColumn:"1 / -1"}}><span style={{color:"#555"}}>Next Draw</span><br/><strong>{item.next?item.next.gameLabel+" "+item.next.drawLabel+" · "+item.next.countdown:"Choose game"}</strong></div>
              </div>
              {strategyNumbers.length>0&&(
                <div style={{display:"grid",gap:7,marginBottom:12}}>
                  {strategyNumbers.slice(0,3).map((nums,i)=>(
                    <div key={i} style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                      <span style={{fontSize:11,color:"#777",fontWeight:900,minWidth:28}}>#{i+1}</span>
                      {nums.map((n,j)=><Ball key={j} n={n} size="sm"/>)}
                    </div>
                  ))}
                  {strategyNumbers.length>3&&<div style={{fontSize:12,color:"#555",fontWeight:800}}>+ {strategyNumbers.length-3} more play{strategyNumbers.length-3===1?"":"s"}</div>}
                </div>
              )}
              <div className="strategy-actions" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7}}>
                <button type="button" className="btn btn-sm btn-p" onClick={()=>navigate(strategyPerformancePath(item.id,item.record?.game||"fantasy5","90","both"))}>View Strategy Performance</button>
                <ShareCardButton payload={sharePayload} label={sharedId===item.id?"Shared":"Share Today's Picks"} className="btn btn-sm btn-s" onBeforeOpen={()=>shareStrategy(item)}/>
                {resultState.state==="share"?(
                  <button type="button" className="btn btn-sm btn-p" onClick={()=>{setVaultResultShareError("");setResultChoice({item,rows:completedResults});}}>Share Results</button>
                ):(
                  <button type="button" className="btn btn-sm btn-s" disabled title={resultState.detail}>{resultState.label}</button>
                )}
                <button type="button" className="btn btn-sm btn-s" onClick={()=>openPlayToday(item,"live")}>{lineupItem&&normalizeTrackingMode(lineupItem.trackingMode)==="live"?"Playing Today":"Mark as Playing Today"}</button>
                {lineupItem&&<button type="button" className="btn btn-sm btn-s" onClick={()=>onRemoveFromLineup?.(item.id)}>Remove From Today</button>}
                <button type="button" className="btn btn-sm btn-s" onClick={()=>navigate("/strategy-builder?edit="+encodeURIComponent(item.id))}>Edit</button>
                <button type="button" className="btn btn-sm btn-s" onClick={()=>setMoreChoice(item)}>More</button>
              </div>
              {latestCompletedResult&&(
                <div style={{fontSize:11,color:"#555",fontWeight:800,marginTop:8}}>
                  Latest tracked result: {vaultResultSummaryText(latestCompletedResult)}
                </div>
              )}
              <div style={{fontSize:11,color:resultState.tone,fontWeight:900,marginTop:8,lineHeight:1.35}}>
                {resultState.detail}
              </div>
              {whatIfOpenId===item.id&&(
                <WhatIfHistoryPanel
                  draws={draws}
                  plays={plays}
                  item={item}
                  isPro={showAdvancedTools}
                  onUpgrade={onUpgrade}
                  onLoadHistory={()=>onLoadHistoricalDraws?.(item.record?.game||"fantasy5")}
                  historyLoading={historyLoading}
                  historyLoaded={historyLoaded===true||!!historyLoaded?.[item.record?.game||"fantasy5"]}
                />
              )}
            </div>
          );
        })}
      </div>
      {!saved.length&&<EmptyState icon="*" title="You don't have any saved playslips yet." text="Build one playslip, add numbers, then choose whether to play it today or save it for later." actions={[{label:"Build First Play",onClick:()=>navigate("/strategy-builder")}]} />}
      {vaultResultLoading&&saved.length>0&&(
        <div style={{fontSize:12,color:"#555",fontWeight:800,marginTop:10}}>Checking completed tracked results...</div>
      )}
      {vaultResultError&&(
        <div style={{display:"none"}}>{vaultResultError}</div>
      )}
      {resultChoice&&(
        <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:950,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div className="panel" style={{width:"min(620px,100%)",maxHeight:"86vh",overflow:"auto",margin:0}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div className="sec-title" style={{fontSize:26,marginBottom:4}}>Share Results</div>
                <p style={{fontSize:14,color:"#555",lineHeight:1.45,margin:0}}>
                  Choose one of the latest completed tracked draws for {strategyDisplayName(resultChoice.item?.id,resultChoice.item?.record)}. Each card uses the frozen lineup that was actually marked as played.
                </p>
              </div>
              <button type="button" className="btn btn-sm btn-s" onClick={()=>setResultChoice(null)}>Close</button>
            </div>
            <div style={{display:"grid",gap:9}}>
              {vaultResultShareError&&(
                <div className="auth-err" style={{marginBottom:8}}>{vaultResultShareError}</div>
              )}
              {(resultChoice.rows||[]).map((row,index)=>{
                const rowId=String(row.id||row.scheduled_draw_id||index);
                const rowBusy=vaultResultShareBusyId===rowId;
                const ineligibleReason=resultShareIneligibleReason(row);
                const canShare=!ineligibleReason;
                const unavailableText=resultShareStatusText(row);
                const shareThisResult=(event)=>{
                  event.preventDefault();
                  event.stopPropagation();
                  if(vaultResultShareBusyId)return;
                  if(!canShare){
                    setVaultResultShareError(unavailableText||"This result is not shareable.");
                    return;
                  }
                  openVaultResultShare({...row});
                };
                return(
                <div
                  key={row.id||row.scheduled_draw_id||index}
                  role="button"
                  tabIndex={0}
                  onClick={shareThisResult}
                  onKeyDown={(event)=>{
                    if((event.key==="Enter"||event.key===" ")&&!vaultResultShareBusyId){
                      shareThisResult(event);
                    }
                  }}
                  style={{border:"1px solid #e8eee9",borderRadius:12,padding:12,background:index===0?"#fffaf0":"#fff",cursor:vaultResultShareBusyId?"wait":canShare?"pointer":"not-allowed",opacity:!canShare?0.74:vaultResultShareBusyId&&!rowBusy?0.92:1}}
                >
                  <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:10,alignItems:"center"}}>
                    <div>
                      <strong style={{color:"#002855"}}>{fmtShortDate(row.draw_date)} · {drawFilterLabel(row.draw_time)}</strong>
                      {index===0&&<span className="sec-badge" style={{marginLeft:8,background:"#008577"}}>Most recent</span>}
                      <div style={{fontSize:12,color:"#555",fontWeight:800,marginTop:4}}>{vaultResultSummaryText(row)}</div>
                      {!canShare&&<div style={{fontSize:12,color:"#5a4a00",fontWeight:900,marginTop:4}}>{unavailableText}</div>}
                    </div>
                    <button type="button" className={"btn btn-sm btn-p "+(rowBusy?"loading":"")} disabled={!!vaultResultShareBusyId||!canShare} onClick={shareThisResult}>{rowBusy?"Preparing...":canShare?"Share":"Unavailable"}</button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {vaultResultSharePayload&&createPortal(<ShareCardModal payload={vaultResultSharePayload} onClose={()=>setVaultResultSharePayload(null)}/>,document.body)}
      {playTodayChoice&&(
        <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:950,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div className="panel" style={{width:"min(520px,100%)",margin:0}}>
            <div className="sec-title" style={{fontSize:28,marginBottom:8}}>Play Today</div>
            <p style={{fontSize:14,color:"#555",lineHeight:1.45,marginBottom:14}}>
              Choose open draws for <strong>{strategyDisplayName(playTodayChoice.id,playTodayChoice.record)}</strong>. Review your total before playing. LottoTracker tracks strategies and does not sell tickets or handle wagers.
            </p>
            <div style={{display:"grid",gap:9,marginBottom:14}}>
              {drawChoicesForStrategy(playTodayChoice).length===0&&(
                <div style={{fontSize:13,color:"#5a4a00",background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px"}}>
                  All draws for this game are closed right now. Save it in My Playslips and choose the next open draw when it appears.
                </div>
              )}
              {drawChoicesForStrategy(playTodayChoice).map(entry=>{
                const closed=drawChoiceClosed(entry);
                const key=drawChoiceKey(entry);
                const selected=selectedPlayTodayDraws.includes(key);
                return(
                  <label key={entry.draw_date+"-"+entry.draw_time} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:10,alignItems:"center",border:"1px solid "+(selected?"#008577":"#dbeee9"),borderRadius:12,padding:"10px 12px",background:selected?"#f0fbf8":"#fff",opacity:closed?0.7:1}}>
                    <input type="checkbox" checked={selected} disabled={closed} onChange={()=>togglePlayTodayDraw(key)} style={{width:18,height:18,accentColor:"#008577"}}/>
                    <span style={{fontWeight:900,color:"#002855"}}>{entry.gameLabel} {entry.drawLabel}<br/><small style={{fontWeight:700,color:"#555"}}>{entry.drawTime} · Sales close {entry.closeTime}</small></span>
                    <span style={{fontWeight:900,color:closed?"#c8102e":"#008577"}}>{closed?entry.drawLabel+" closed":entry.countdown}</span>
                  </label>
                );
              })}
            </div>
            <div style={{fontSize:12,color:"#5a4a00",background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"9px 10px",marginBottom:12}}>
              If Midday is closed, choose Evening or save the playslip for later.
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className="btn btn-s" onClick={()=>{setPlayTodayChoice(null);setSelectedPlayTodayDraws([]);setPlayTodayMode("live");setPostSaveChoice(null);navigate("/my-strategies");}}>Keep in My Playslips</button>
              <button type="button" className="btn btn-p" onClick={confirmPlayToday} disabled={!selectedPlayTodayDraws.length}>Play Selected Draws</button>
              <button type="button" className="btn btn-s" onClick={()=>{setPlayTodayChoice(null);setSelectedPlayTodayDraws([]);setPlayTodayMode("live");}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {moreChoice&&(()=>{
        const item=moreChoice;
        const itemDrawOpen=item.next&&!drawChoiceClosed(item.next);
        return(
          <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:950,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
            <div className="panel" style={{width:"min(500px,100%)",margin:0}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div className="sec-title" style={{fontSize:28,marginBottom:4}}>More Options</div>
                  <div style={{fontSize:13,color:"#555",fontWeight:800}}>{strategyDisplayName(item.id,item.record)} · {gameLabel(item.record?.game||"fantasy5")}</div>
                </div>
                <button type="button" className="btn btn-sm btn-s" onClick={()=>setMoreChoice(null)}>Close</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:8}}>
                <button type="button" className="btn btn-s" onClick={()=>{setRenameChoice(item);setRenameValue(strategyDisplayName(item.id,item.record));setMoreChoice(null);}}>Rename</button>
                <button type="button" className="btn btn-s" onClick={()=>{onDuplicateStrategy?.(item.id);setMoreChoice(null);}}>Duplicate</button>
                <button type="button" className="btn btn-s" onClick={()=>{toggleWhatIf(item.id);setMoreChoice(null);}}>{whatIfOpenId===item.id?"Hide What-If":"View What-If"}</button>
                <button type="button" className="btn btn-s" onClick={()=>{exportStrategy(item);setMoreChoice(null);}}>{exportedId===item.id?"Exported":"Export Strategy"}</button>
                <button type="button" className="btn btn-s" onClick={async()=>{try{await onRevokePublicShare?.(item.id);setSharedId("");setBuilderErr("");}catch(e){setBuilderErr(e?.message||"Could not stop sharing this strategy.");}setMoreChoice(null);}}>Stop Sharing</button>
                <button type="button" className="btn btn-s" onClick={()=>{onArchiveStrategy?.(item.id);setMoreChoice(null);}}>Archive</button>
                <button type="button" className="btn btn-s" style={{color:"#c8102e",borderColor:"#f0c6cf"}} onClick={()=>{onDeleteStrategy?.(item.id);setMoreChoice(null);}}>Delete</button>
              </div>
              <div style={{fontSize:12,color:"#555",lineHeight:1.4,marginTop:12}}>
                Results and performance stay in Results Center. My Playslips keeps the saved version, plays, cost, and scheduling controls simple.
              </div>
            </div>
          </div>
        );
      })()}
      {renameChoice&&(
        <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:950,background:"rgba(0,20,45,.66)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div className="panel" style={{width:"min(480px,100%)",margin:0}}>
            <div className="sec-title" style={{fontSize:28,marginBottom:8}}>Rename Strategy</div>
            <p style={{fontSize:14,color:"#555",lineHeight:1.45,marginBottom:14}}>
              Give this saved strategy a clearer name.
            </p>
            <input className="add-inp" value={renameValue} onChange={e=>setRenameValue(e.target.value)} style={{width:"100%",marginBottom:12}} autoFocus/>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className="btn btn-p" onClick={saveRename}>Save Name</button>
              <button type="button" className="btn btn-s" onClick={()=>{setRenameChoice(null);setRenameValue("");}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

// Scoreboard toggles. Time windows compare against draw_date (YYYY-MM-DD
// strings lex-compare correctly). Pool sizes include 1 ("Solo") so toggling
// back to unpooled math is just another button, not a hidden default.
const WINDOW_OPTIONS=[
  {id:"30", label:"30 days",  days:30  },
  {id:"60", label:"60 days",  days:60  },
  {id:"90", label:"90 days",  days:90  },
  {id:"180",label:"6 months", days:180 },
  {id:"365",label:"1 year",   days:365 },
  {id:"all",label:"All time", days:null},
];
const SB_POOL_SIZES=[1,5,10,25,50,100];
const SCORE_SORT_OPTIONS=[
  {id:"roi",label:"ROI"},
  {id:"profit",label:"Profit"},
  {id:"hitRate",label:"Hit Rate"},
  {id:"m5",label:"Jackpots"},
  {id:"m4",label:"4 Matches"},
  {id:"m3",label:"3 Matches"},
  {id:"plays",label:"Play Count"},
  {id:"newest",label:"Newest"},
  {id:"oldest",label:"Oldest"},
];

function daysAgoIso(n){
  const d=new Date();
  d.setDate(d.getDate()-n);
  return d.toISOString().slice(0,10);
}

function strategyIdForPlay(p){
  return p.strategy||"manual";
}

function emptyStrategyTrust(){
  return{records:{},events:[]};
}
function normalizeStrategyTrust(data){
  const safe=data&&typeof data==="object"?data:{};
  return{
    records:safe.records&&typeof safe.records==="object"?safe.records:{},
    events:Array.isArray(safe.events)?safe.events:[],
  };
}
function strategyBaseLabel(id){
  const meta=getStrategyMeta(id);
  if(meta?.label)return meta.label;
  const clean=String(id||"Strategy").replace(/^builder_/,"").replace(/_v\d+$/,"");
  return clean.split(/[_-]+/).filter(Boolean).map(part=>part.charAt(0).toUpperCase()+part.slice(1)).join(" ")||"Strategy";
}
function strategyDisplayName(id,record){
  const rawBase=record?.baseName||strategyBaseLabel(id);
  const base=String(rawBase||"").toLowerCase().startsWith("builder_")||String(rawBase||"").toLowerCase().startsWith("builder-")
    ?strategyBaseLabel(rawBase)
    :rawBase;
  return record?.version&&record.version>1?base+" v"+record.version:base;
}
function strategyStatusMeta(status){
  if(status==="Locked")return{icon:"🔒",label:"Locked",color:"#008577"};
  if(status==="Archived")return{icon:"📦",label:"Archived",color:"#5d6675"};
  if(status==="Replaced")return{icon:"✏️",label:"Replaced",color:"#c8860a"};
  return{icon:"🟢",label:"Draft",color:"#2E6DA4"};
}
function strategyTrustStatusMeta(status){
  if(status==="Locked")return{icon:"\uD83D\uDD12",label:"Trusted locked strategy",color:"#008577"};
  if(status==="Archived")return{icon:"\uD83D\uDCE6",label:"Archived",color:"#5d6675"};
  if(status==="Replaced")return{icon:"\u270F\uFE0F",label:"Replaced",color:"#c8860a"};
  return{icon:"\uD83D\uDFE2",label:"What-if simulation only",color:"#2E6DA4"};
}
function strategyIdsFromPlays(plays){
  return[...new Set((plays||[])
    .filter(p=>{
      const gameId=rowGame(p);
      const expectedCount=gameNumberConfig(gameId).count||BALL_COUNT;
      return(Array.isArray(p.numbers)&&p.numbers.length===expectedCount)||(Array.isArray(p.anchor_numbers)&&p.anchor_numbers.length>0);
    })
    .map(strategyIdForPlay))];
}
function eventId(){
  return Date.now().toString(36)+"-"+Math.random().toString(36).slice(2,8);
}
function strategyEvent(type,strategyId,label,detail){
  return{id:eventId(),type,strategyId,label,detail,at:new Date().toISOString()};
}
function ensureStrategyTrust(data,plays){
  const next=normalizeStrategyTrust(data);
  const records={...next.records};
  const events=[...(next.events||[])];
  strategyIdsFromPlays(plays).forEach(id=>{
    if(records[id])return;
    const label=strategyBaseLabel(id);
    const firstPlay=(plays||[]).find(p=>strategyIdForPlay(p)===id);
    records[id]={
      id,
      status:"Draft",
      baseName:label,
      version:1,
      game:firstPlay?.game||"fantasy5",
      createdAt:new Date().toISOString(),
      lockedAt:null,
      lockedDrawId:null,
      lockedDrawLabel:null,
      lockedBeforeClose:false,
      lockedNumbers:[],
      archivedAt:null,
      replacedAt:null,
      replacedBy:null,
      previousVersions:[],
      simulationOnly:true,
    };
    events.unshift(strategyEvent("Strategy Created",id,label,"Draft strategy created."));
  });
  return{records,events:events.slice(0,80)};
}
function freezeStrategyNumbers(plays,strategyId,record=null){
  const durableEntries=isPickGame(record?.game||"")?strategySnapshotPlayEntries(record):[];
  const durable=durableEntries.length?durableEntries.map(entry=>entry.numbers):strategyNumbersForRecord(strategyId,record,plays);
  if(durable.length){
    return durable.map((nums,index)=>{
      const entry=durableEntries[index];
      return{
      playId:null,
      index:index+1,
      numbers:[...nums],
      pickRules:entry?.pickRules||null,
      anchorNumbers:null,
      createdAt:record?.updatedAt||record?.createdAt||null,
    }});
  }
  return(plays||[])
    .filter(p=>strategyIdForPlay(p)===strategyId)
    .map(p=>({
      playId:p.id,
      numbers:Array.isArray(p.numbers)?[...p.numbers]:null,
      anchorNumbers:Array.isArray(p.anchor_numbers)?[...p.anchor_numbers]:null,
      createdAt:p.created_at||p.inserted_at||null,
    }));
}
function currentDrawLockId(drawStatus){
  return drawStatus?.next?.sortAt?String(drawStatus.next.sortAt):null;
}
function drawIsBeforeClose(drawStatus){
  return["Upcoming","Sales Closing Soon"].includes(drawStatus?.next?.status);
}
function drawEntryIsClosedOrLater(entry){
  return["Sales Closed","Drawing In Progress","Waiting for official results","Official results delayed","Results Ready","Archived"].includes(entry?.status);
}
function drawIsClosedOrLater(drawStatus){
  return drawEntryIsClosedOrLater(drawStatus?.next);
}
function strategyCanModify(record,drawStatus){
  if(!record||record.status!=="Locked")return true;
  const sameDraw=record.lockedDrawId&&record.lockedDrawId===currentDrawLockId(drawStatus);
  return !(sameDraw&&["Upcoming","Sales Closing Soon","Sales Closed","Drawing In Progress"].includes(drawStatus?.next?.status));
}
function lockedStrategyCounts(strategyTrust){
  const records=Object.values(strategyTrust?.records||{});
  const priorVersions=records.flatMap(r=>Array.isArray(r.previousVersions)?r.previousVersions:[]);
  const locked=records.filter(r=>r.status==="Locked").length;
  const retired=records.filter(r=>r.status==="Archived"||r.status==="Replaced").length+priorVersions.length;
  const simulation=records.filter(r=>r.status!=="Locked").length;
  const active=records.filter(r=>r.status==="Draft"||r.status==="Locked").length;
  return{locked,retired,simulation,active,total:records.length};
}
function normalizeLineup(data){
  const safe=data&&typeof data==="object"?data:{};
  const seen=new Set();
  const items=(Array.isArray(safe.items)?safe.items:[])
    .filter(item=>item&&item.id&&!seen.has(item.id)&&item.id!=="manual")
    .map(item=>{
      seen.add(item.id);
      const legacyDraw=item.drawId?{
        drawId:item.drawId,
        drawLabel:item.drawLabel||null,
        drawDate:item.drawDate||null,
        drawTime:item.drawTime||null,
      }:null;
      const draws=(Array.isArray(item.draws)?item.draws:legacyDraw?[legacyDraw]:[])
        .filter(draw=>draw&&(draw.drawId||draw.drawDate||draw.drawTime))
        .map(draw=>({
          drawId:draw.drawId||null,
          drawLabel:draw.drawLabel||null,
          drawDate:draw.drawDate||draw.draw_date||null,
          drawTime:draw.drawTime||draw.draw_time||null,
          addedAt:draw.addedAt||item.addedAt||new Date().toISOString(),
          playType:draw.playType||item.playType||"Simulation",
          status:draw.status||"upcoming",
          lockedAt:draw.lockedAt||null,
          completedAt:draw.completedAt||null,
          cancelledAt:draw.cancelledAt||null,
        }));
      return{
        id:item.id,
        playType:item.playType==="Real Play"?"Real Play":"Simulation",
        addedAt:item.addedAt||new Date().toISOString(),
        draws,
        drawId:item.drawId||draws[0]?.drawId||null,
        drawLabel:item.drawLabel||draws[0]?.drawLabel||null,
        drawDate:item.drawDate||draws[0]?.drawDate||null,
        drawTime:item.drawTime||draws[0]?.drawTime||null,
      };
    });
  return{
    items,
    snapshots:Array.isArray(safe.snapshots)?safe.snapshots:[],
  };
}
function scheduledStatusToLineupStatus(status){
  if(status==="cutoff_passed")return"cutoff passed";
  if(status==="awaiting_results")return"awaiting results";
  return status||"upcoming";
}
function scheduledDrawRowsToLineup(rows=[]){
  const itemsByStrategy=new Map();
  const snapshots=[];
  (rows||[]).forEach(row=>{
    if(!row||row.status==="cancelled")return;
    const snapshot=row.frozen_snapshot&&typeof row.frozen_snapshot==="object"?row.frozen_snapshot:{};
    const draw={
      scheduleId:row.id,
      drawId:String(Date.parse(row.draw_date+"T"+(row.draw_time==="evening"?"23:15:00":"13:45:00"))),
      drawLabel:row.draw_label||gameLabel(row.game)+" "+(row.draw_time==="evening"?"Evening":"Midday"),
      drawDate:row.draw_date,
      drawTime:row.draw_time,
      addedAt:row.scheduled_at,
      playType:row.play_type||"Simulation",
      trackingMode:normalizeTrackingMode(row.tracking_mode),
      status:scheduledStatusToLineupStatus(row.status),
      lockedAt:row.locked_at||null,
      completedAt:row.completed_at||null,
      officialDrawId:row.official_draw_id||null,
    };
    const itemBase={
      id:row.strategy_id,
      game:row.game,
      playType:row.play_type==="Real Play"?"Real Play":"Simulation",
      trackingMode:normalizeTrackingMode(row.tracking_mode),
      addedAt:row.scheduled_at,
      strategyName:row.strategy_name,
      scheduleSource:"supabase",
    };
    if(row.status==="completed"){
      snapshots.push({
        drawId:draw.drawId,
        drawLabel:draw.drawLabel,
        lockedAt:row.locked_at||row.cutoff_at||row.scheduled_at,
        completedAt:row.completed_at||row.official_result_at||null,
        officialDrawId:row.official_draw_id||null,
        items:[{
          ...itemBase,
          ...draw,
          status:"completed",
          cost:Number(snapshot.cost)||(Array.isArray(snapshot.numbers)?snapshot.numbers.length:0),
          numbers:Array.isArray(snapshot.numbers)?snapshot.numbers:[],
          anchorNumbers:Array.isArray(snapshot.anchorNumbers)?snapshot.anchorNumbers:[],
          frozenSnapshot:snapshot,
        }],
      });
      return;
    }
    const existing=itemsByStrategy.get(row.strategy_id)||{...itemBase,draws:[]};
    existing.draws.push(draw);
    itemsByStrategy.set(row.strategy_id,existing);
  });
  const items=[...itemsByStrategy.values()].map(item=>{
    const sorted=item.draws.sort((a,b)=>(Number(a.drawId)||0)-(Number(b.drawId)||0));
    return{
      ...item,
      draws:sorted,
      drawId:sorted[0]?.drawId||null,
      drawLabel:sorted[0]?.drawLabel||null,
      drawDate:sorted[0]?.drawDate||null,
      drawTime:sorted[0]?.drawTime||null,
    };
  });
  return normalizeLineup({items,snapshots});
}
function snapshotPlaysFromLineup(lineup){
  const plays=[];
  normalizeLineup(lineup).snapshots.forEach(snapshot=>{
    const drawId=String(snapshot.drawId||"");
    (snapshot.items||[]).forEach(item=>{
      (Array.isArray(item.numbers)?item.numbers:[]).forEach((play,index)=>{
        const nums=Array.isArray(play?.numbers)?play.numbers:Array.isArray(play)?play:null;
        if(!nums)return;
        plays.push({
          id:"scheduled:"+drawId+":"+item.id+":"+index,
          user_id:null,
          game:item.game||null,
          strategy:item.id,
          numbers:[...nums],
          created_at:item.addedAt||item.lockedAt||snapshot.lockedAt,
          __scheduledDrawId:drawId,
          __scheduledSnapshot:true,
          __trackingMode:normalizeTrackingMode(item.trackingMode),
        });
      });
    });
  });
  return plays;
}
function liveTrackingPlays(plays,lineup){
  const ids=new Set(normalizeLineup(lineup).items.filter(item=>normalizeTrackingMode(item.trackingMode)==="live").map(item=>item.id));
  const current=(plays||[]).filter(p=>ids.has(strategyIdForPlay(p)));
  return[...current,...snapshotPlaysFromLineup(lineup)];
}
function lineupDrawLifecycleStatus(entry,scheduledDraw,now=new Date()){
  if(scheduledDraw?.cancelledAt||scheduledDraw?.status==="cancelled")return"cancelled";
  if(entry?.status==="Results Ready"||entry?.status==="Archived")return"completed";
  if(entry?.status==="Waiting for official results"||entry?.status==="Official results delayed")return"awaiting results";
  if(entry?.status==="Sales Closed"||entry?.status==="Drawing In Progress")return"cutoff passed";
  const drawMs=Number(scheduledDraw?.drawId);
  if(Number.isFinite(drawMs)&&drawMs>0){
    if(now.getTime()>=drawMs+RESULTS_READY_MS)return"awaiting results";
    if(now.getTime()>=drawMs)return"cutoff passed";
  }
  return"upcoming";
}
function dbStatusForDrawEntry(entry,scheduledDraw,now=new Date()){
  const status=lineupDrawLifecycleStatus(entry,scheduledDraw,now);
  if(status==="cutoff passed")return"cutoff_passed";
  if(status==="awaiting results")return"awaiting_results";
  return status==="completed"||status==="cancelled"?status:"upcoming";
}
function lineupDrawIsOpenStatus(status){
  return["upcoming","cutoff passed","awaiting results"].includes(status);
}
function findScheduledDrawEntry(scheduledDraw,record,officialDraws=[],now=new Date()){
  if(!scheduledDraw)return null;
  const gameId=record?.game||"fantasy5";
  const service=getDrawStatusService([gameId],now,officialDraws);
  const entries=[...(service.entries||[]),...(service.archivedEntries||[])];
  const drawId=scheduledDraw.drawId?String(scheduledDraw.drawId):null;
  const drawDate=scheduledDraw.drawDate||scheduledDraw.draw_date||null;
  const drawTime=drawTimeKey(scheduledDraw.drawTime||scheduledDraw.draw_time||scheduledDraw.drawLabel);
  return entries.find(entry=>drawId&&String(drawLockIdForDraw(entry))===drawId)
    ||entries.find(entry=>drawDate&&entry.draw_date===drawDate&&drawTimeKey(entry.draw_time)===drawTime)
    ||null;
}
function upsertLineupSnapshot(snapshots,snapshot){
  if(!snapshot?.drawId||!(snapshot.items||[]).length)return snapshots;
  const next=[...(snapshots||[])];
  const index=next.findIndex(s=>String(s.drawId)===String(snapshot.drawId));
  if(index<0)return[snapshot,...next].slice(0,60);
  const existing=next[index];
  const byId=new Map((existing.items||[]).map(item=>[item.id,item]));
  (snapshot.items||[]).forEach(item=>byId.set(item.id,{...(byId.get(item.id)||{}),...item}));
  next[index]={...existing,...snapshot,items:[...byId.values()]};
  return next.slice(0,60);
}
function snapshotForLineupDraw(item,scheduledDraw,plays,completedAt=null){
  const drawId=scheduledDraw?.drawId||item?.drawId;
  if(!drawId)return null;
  return{
    drawId,
    drawLabel:scheduledDraw?.drawLabel||item?.drawLabel||"Scheduled draw",
    lockedAt:scheduledDraw?.lockedAt||new Date().toISOString(),
    items:[{
      ...item,
      ...scheduledDraw,
      id:item.id,
      status:completedAt?"completed":(scheduledDraw?.status||"cutoff passed"),
      completedAt:completedAt||scheduledDraw?.completedAt||null,
      cost:lineupCostForStrategy(plays,item.id),
      numbers:freezeStrategyNumbers(plays,item.id,item.record||null),
      playType:scheduledDraw?.playType||item.playType||"Simulation",
      addedAt:scheduledDraw?.addedAt||item.addedAt,
    }],
  };
}
function reconcileLineupLifecycle(lineup,records={},plays=[],officialDraws=[],now=new Date()){
  const clean=normalizeLineup(lineup);
  let changed=false;
  let snapshots=[...(clean.snapshots||[])];
  const items=[];
  clean.items.forEach(item=>{
    const record=strategyRecordForId(item.id,records,plays);
    const draws=(Array.isArray(item.draws)&&item.draws.length?item.draws:[item.drawId?{
      drawId:item.drawId,
      drawLabel:item.drawLabel,
      drawDate:item.drawDate,
      drawTime:item.drawTime,
      addedAt:item.addedAt,
      playType:item.playType,
    }:null].filter(Boolean));
    const activeDraws=[];
    draws.forEach(draw=>{
      const entry=findScheduledDrawEntry(draw,record,officialDraws,now);
      const status=lineupDrawLifecycleStatus(entry,draw,now);
      const nextDraw={
        ...draw,
        status,
        drawId:draw.drawId||drawLockIdForDraw(entry),
        drawLabel:draw.drawLabel||(entry?entry.gameLabel+" "+entry.drawLabel:null),
        drawDate:draw.drawDate||entry?.draw_date||null,
        drawTime:draw.drawTime||entry?.draw_time||null,
      };
      if(status!==draw.status)changed=true;
      if(status==="completed"){
        const completedAt=entry?.officialDraw?.official_result_at||entry?.officialDraw?.fetched_at||new Date().toISOString();
        snapshots=upsertLineupSnapshot(snapshots,snapshotForLineupDraw(item,{...nextDraw,completedAt},plays,completedAt));
        changed=true;
      }else if(lineupDrawIsOpenStatus(status)){
        activeDraws.push(nextDraw);
      }else{
        changed=true;
      }
    });
    if(activeDraws.length){
      const sorted=activeDraws.sort((a,b)=>(Number(a.drawId)||0)-(Number(b.drawId)||0)||String(a.drawDate||"").localeCompare(String(b.drawDate||""))||String(a.drawTime||"").localeCompare(String(b.drawTime||"")));
      items.push({
        ...item,
        draws:sorted,
        drawId:sorted[0]?.drawId||null,
        drawLabel:sorted[0]?.drawLabel||null,
        drawDate:sorted[0]?.drawDate||null,
        drawTime:sorted[0]?.drawTime||null,
      });
      if(sorted.length!==draws.length)changed=true;
    }else{
      changed=true;
    }
  });
  const next={...clean,items,snapshots};
  return{lineup:next,changed:changed||JSON.stringify(next)!==JSON.stringify(clean)};
}
function drawLockIdForDraw(draw){
  if(!draw)return null;
  if(draw.sortAt)return String(draw.sortAt);
  if(draw.drawAt instanceof Date&&!Number.isNaN(draw.drawAt.getTime()))return String(draw.drawAt.getTime());
  const game=rowGame(draw);
  const drawDate=draw.draw_date||String(draw.date||"").slice(0,10);
  const drawTime=drawTimeKey(draw.draw_time||draw.drawLabel||(draw.isEvening?"evening":"midday"));
  const slot=(NEXT_DRAW_SCHEDULE[game]||[]).find(s=>drawTimeKey(s.label)===drawTime);
  if(!slot||!/^\d{4}-\d{2}-\d{2}$/.test(drawDate))return null;
  return String(easternDateTime(drawDate,slot.draw).getTime());
}
function drawCloseMsForDraw(draw){
  if(!draw)return 0;
  if(draw.closeAt instanceof Date&&!Number.isNaN(draw.closeAt.getTime()))return draw.closeAt.getTime();
  const game=rowGame(draw);
  const drawDate=draw.draw_date||String(draw.date||"").slice(0,10);
  const drawTime=drawTimeKey(draw.draw_time||draw.drawLabel||(draw.isEvening?"evening":"midday"));
  const slot=(NEXT_DRAW_SCHEDULE[game]||[]).find(s=>drawTimeKey(s.label)===drawTime);
  if(!slot||!/^\d{4}-\d{2}-\d{2}$/.test(drawDate))return 0;
  return easternDateTime(drawDate,slot.close).getTime();
}
function drawOfficialMsForDraw(draw){
  const t=Date.parse(draw?.official_result_at||draw?.fetched_at||"");
  return Number.isFinite(t)?t:0;
}
function drawOccurredBeforeStrategyCreated(draw,record){
  const created=Date.parse(record?.createdAt||"");
  if(!Number.isFinite(created))return false;
  const drawMs=Number(drawLockIdForDraw(draw));
  if(Number.isFinite(drawMs)&&drawMs>0)return drawMs<created;
  const closeMs=drawCloseMsForDraw(draw);
  if(closeMs)return closeMs<created;
  const drawDate=draw?.draw_date||String(draw?.date||"").slice(0,10);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(drawDate))return false;
  return Date.parse(drawDate+"T23:59:59")<created;
}
function lineupSnapshotForDraw(lineup,draw){
  const drawId=drawLockIdForDraw(draw);
  if(!drawId)return null;
  return normalizeLineup(lineup).snapshots.find(s=>String(s.drawId)===drawId)||null;
}
function lineupEntryForDraw(strategyId,draw,lineup){
  const clean=normalizeLineup(lineup);
  const snapshot=lineupSnapshotForDraw(clean,draw);
  if(snapshot){
    const item=(snapshot.items||[]).find(entry=>entry.id===strategyId);
    return item?{...item,snapshot:true,lockedAt:item.lockedAt||snapshot.lockedAt}:null;
  }
  const item=clean.items.find(entry=>entry.id===strategyId);
  if(!item)return null;
  const drawId=drawLockIdForDraw(draw);
  const itemDraws=Array.isArray(item.draws)?item.draws:[];
  const drawMatch=itemDraws.some(d=>d.drawId&&drawId&&String(d.drawId)===String(drawId));
  if(itemDraws.length&&drawId&&!drawMatch)return null;
  const itemDrawId=item.drawId||null;
  if(!itemDraws.length&&itemDrawId&&drawId&&String(itemDrawId)!==String(drawId))return null;
  const matchedDraw=itemDraws.find(d=>d.drawId&&drawId&&String(d.drawId)===String(drawId));
  if(matchedDraw)return{...item,...matchedDraw,snapshot:false};
  return{...item,snapshot:false};
}
function wasAddedBeforeClose(iso,draw){
  const closeMs=drawCloseMsForDraw(draw);
  const t=Date.parse(iso||"");
  if(!closeMs||!Number.isFinite(t))return true;
  return t<=closeMs;
}
function playSavedAfterDraw(play,draw){
  const created=playCreatedTs(play);
  if(!created)return false;
  const closeMs=drawCloseMsForDraw(draw);
  const officialMs=drawOfficialMsForDraw(draw);
  return Boolean((closeMs&&created>closeMs)||(officialMs&&created>officialMs));
}
function classifyPlayForDraw(play,draw,lineup,strategyTrust){
  if(play?.__scheduledSnapshot){
    const drawId=String(drawLockIdForDraw(draw)||"");
    const match=drawId&&String(play.__scheduledDrawId)===drawId;
    const snapshotMode=normalizeTrackingMode(play.__trackingMode);
    return match
      ?{countsPublic:snapshotMode==="live",label:snapshotMode==="live"?"Completed live draw":"Completed Watch draw",tone:snapshotMode==="live"?"#008577":"#2E6DA4",playType:snapshotMode==="live"?"Real Play":"Simulation",trackingMode:snapshotMode,isWhatIf:snapshotMode!=="live"}
      :{countsPublic:false,label:"Different scheduled draw",tone:"#5d6675",isWhatIf:true};
  }
  const strategyId=strategyIdForPlay(play);
  const record=strategyTrust?.records?.[strategyId]||strategyTrust?.[strategyId]||null;
  const entry=lineupEntryForDraw(strategyId,draw,lineup);
  const savedAfter=playSavedAfterDraw(play,draw);
  const beforeStrategy=drawOccurredBeforeStrategyCreated(draw,record);
  const lineupBeforeClose=!!entry&&wasAddedBeforeClose(entry.addedAt||entry.lockedAt,draw);
  const mode=normalizeTrackingMode(entry?.trackingMode);
  const countsPublic=mode==="live"&&lineupBeforeClose&&!savedAfter;
  if(beforeStrategy)return{countsPublic:false,label:"Before strategy existed",tone:"#5d6675",isWhatIf:true};
  if(savedAfter)return{countsPublic:false,label:"Saved after draw",tone:"#c8860a",isSavedAfter:true,isWhatIf:true};
  if(mode==="watch")return{countsPublic:false,label:"Saved Playslip",tone:"#2E6DA4",isWhatIf:true,trackingMode:"watch"};
  if(!lineupBeforeClose)return{countsPublic:false,label:"What-if only",tone:"#2E6DA4",isWhatIf:true};
  return{countsPublic:countsPublic,label:"Live Tracking",tone:"#008577",playType:entry.playType||"Real Play",trackingMode:"live"};
}
function publicEligibilityForLineup(lineup,strategyTrust){
  return(play,draw)=>classifyPlayForDraw(play,draw,lineup,strategyTrust).countsPublic;
}
function betaCapacityModeEnabled(){
  return process.env.REACT_APP_BETA_CAPACITY_MODE==="true"||localStorage.getItem(BETA_CAPACITY_MODE_KEY)==="true";
}
function setBetaCapacityMode(enabled){
  localStorage.setItem(BETA_CAPACITY_MODE_KEY,enabled?"true":"false");
}
function betaMetricsKey(){return"lt:betaMetrics";}
function betaMetrics(){
  try{return JSON.parse(localStorage.getItem(betaMetricsKey())||'{"api":[],"slow":[],"actions":[]}');}
  catch{return{api:[],slow:[],actions:[]};}
}
function saveBetaMetrics(metrics){
  localStorage.setItem(betaMetricsKey(),JSON.stringify({
    api:(metrics.api||[]).slice(0,120),
    slow:(metrics.slow||[]).slice(0,120),
    actions:(metrics.actions||[]).slice(0,160),
  }));
}
function recordBetaMetric(type,payload){
  const metrics=betaMetrics();
  const entry={...payload,at:new Date().toISOString()};
  if(type==="api")metrics.api=[entry,...(metrics.api||[])];
  else if(type==="slow")metrics.slow=[entry,...(metrics.slow||[])];
  else metrics.actions=[entry,...(metrics.actions||[])];
  saveBetaMetrics(metrics);
}
function formatRateLimitCountdown(ms){
  const totalSeconds=Math.max(1,Math.ceil(Number(ms||0)/1000));
  const minutes=Math.floor(totalSeconds/60);
  const seconds=totalSeconds%60;
  return String(minutes).padStart(2,"0")+":"+String(seconds).padStart(2,"0");
}
function rateLimitStatus(key,limit,windowMs){
  const storageKey="lt:rate:"+key;
  const now=Date.now();
  let stamps=[];
  try{stamps=JSON.parse(localStorage.getItem(storageKey)||"[]");}catch{stamps=[];}
  stamps=stamps.filter(t=>now-Number(t)<windowMs);
  if(stamps.length>=limit){
    const retryMs=Math.max(1000,windowMs-(now-Number(stamps[0])));
    localStorage.setItem(storageKey,JSON.stringify(stamps));
    return{allowed:false,retryMs,retryText:formatRateLimitCountdown(retryMs)};
  }
  return{allowed:true,storageKey,stamps};
}
function commitRateLimitAction(key,limit,windowMs){
  const status=rateLimitStatus(key,limit,windowMs);
  if(!status.allowed)return status;
  const now=Date.now();
  const stamps=[...(status.stamps||[]),now];
  localStorage.setItem(status.storageKey,JSON.stringify(stamps));
  recordBetaMetric("actions",{key,status:"allowed"});
  return{allowed:true};
}
function rateLimitAction(key,limit,windowMs){
  return commitRateLimitAction(key,limit,windowMs);
}
async function monitoredFetch(url,options={},label="api"){
  const start=performance.now();
  try{
    const res=await fetch(url,options);
    const duration=Math.round(performance.now()-start);
    recordBetaMetric("api",{label,url:String(url),status:res.status,ok:res.ok,duration});
    if(duration>1500)recordBetaMetric("slow",{label,url:String(url),duration});
    return res;
  }catch(e){
    recordBetaMetric("api",{label,url:String(url),status:e?.name==="TimeoutError"?"timeout":e?.name==="AbortError"?"canceled":"network_error",ok:false,duration:Math.round(performance.now()-start),message:e?.name==="TimeoutError"?"Request deadline exceeded":e?.name==="AbortError"?"Intentional cancellation":e?.message||"Network error"});
    throw e;
  }
}
function lineupIdSet(lineup){
  const clean=normalizeLineup(lineup);
  const ids=[...clean.items.map(item=>item.id)];
  clean.snapshots.forEach(snapshot=>(snapshot.items||[]).forEach(item=>ids.push(item.id)));
  return new Set(ids);
}
function strategyExists(strategyId,records={},plays=[]){
  return !!records?.[strategyId]||(plays||[]).some(p=>strategyIdForPlay(p)===strategyId);
}
function removeStrategyFromLineupData(lineup,strategyId){
  const clean=normalizeLineup(lineup);
  return{
    ...clean,
    items:clean.items.filter(item=>item.id!==strategyId),
    snapshots:(clean.snapshots||[]).map(snapshot=>({
      ...snapshot,
      items:(snapshot.items||[]).filter(item=>item.id!==strategyId),
    })).filter(snapshot=>(snapshot.items||[]).length>0),
  };
}
function pruneLineupData(lineup,records={},plays=[]){
  const clean=normalizeLineup(lineup);
  const items=clean.items.filter(item=>strategyExists(item.id,records,plays));
  const snapshots=(clean.snapshots||[]).map(snapshot=>({
    ...snapshot,
    items:(snapshot.items||[]).filter(item=>strategyExists(item.id,records,plays)),
  })).filter(snapshot=>(snapshot.items||[]).length>0);
  return{...clean,items,snapshots};
}
function filterPlaysForLineup(plays,lineup){
  return liveTrackingPlays(plays,lineup);
}
function lineupCostForStrategy(plays,strategyId){
  return(plays||[]).filter(p=>strategyIdForPlay(p)===strategyId).length;
}
function lineupSummary(lineup,plays,drawStatus){
  const clean=normalizeLineup(lineup);
  const simulation=clean.items.filter(item=>item.playType!=="Real Play").length;
  const real=clean.items.length-simulation;
  const ticketCost=clean.items.reduce((sum,item)=>sum+lineupCostForStrategy(plays,item.id),0);
  const next=drawStatus?.next;
  return{
    count:clean.items.length,
    simulation,
    real,
    ticketCost,
    lockText:next?["Upcoming","Sales Closing Soon"].includes(next.status)?next.countdown:"Locked / closed":"Waiting for draw",
  };
}
function formatLockTimestamp(iso){
  if(!iso)return "";
  const d=new Date(iso);
  if(Number.isNaN(d.getTime()))return "";
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric"})+" "+d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
}

function playCreatedTs(p){
  const t=Date.parse(p.created_at||p.inserted_at||p.updated_at||"");
  return Number.isFinite(t)?t:0;
}

function fmtShortDate(iso){
  if(!iso)return "—";
  const datePart=String(iso).slice(0,10);
  const d=new Date(datePart+"T12:00:00");
  if(Number.isNaN(d.getTime()))return "—";
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
}

function fmtShortDateTimeET(iso){
  if(!iso)return "-";
  const d=new Date(iso);
  if(Number.isNaN(d.getTime()))return fmtShortDate(iso);
  return d.toLocaleString("en-US",{
    timeZone:EASTERN_TIME_ZONE,
    month:"short",
    day:"numeric",
    hour:"numeric",
    minute:"2-digit",
    timeZoneName:"short",
  }).replace(/\sEST$/," ET").replace(/\sEDT$/," ET");
}

function formatFireballDigit(value,unavailableLabel="Fireball unavailable"){
  if(value===null||value===undefined||value==="")return unavailableLabel;
  const n=Number(value);
  if(Number.isInteger(n)&&n>=0&&n<=9)return "Fireball "+n;
  return unavailableLabel;
}

function scoreStrategyDraw(stratPlays,draw,eligibleForDraw=()=>true){
  const materialized=stratPlays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT&&eligibleForDraw(p,draw,strategyIdForPlay(p)));
  const recipes=stratPlays.filter(p=>Array.isArray(p.anchor_numbers)&&p.anchor_numbers.length>0&&eligibleForDraw(p,draw,strategyIdForPlay(p)));
  const simPlayCount=materialized.length+recipes.length;
  const p5=Number(draw.prize_5match)||0;
  const p4=Number(draw.prize_4match)||0;
  const p3=Number(draw.prize_3match)||0;
  let won=0,hits3plus=0;
  for(const pl of materialized){
    const m=cntMatch(pl.numbers,draw.numbers);
    if(m>=3)hits3plus++;
    if(m===5)won+=p5;
    else if(m===4)won+=p4;
    else if(m===3)won+=p3;
  }
  for(const r of recipes){
    const dist=anchorMatchDistribution(r.anchor_numbers,draw.numbers);
    const n5=dist[5]||0,n4=dist[4]||0,n3=dist[3]||0;
    hits3plus+=n5+n4+n3;
    won+=n5*p5+n4*p4+n3*p3;
  }
  const spent=simPlayCount;
  return{won,spent,net:won-spent,hits3plus};
}

function summarizeStrategyTimeline(plays,draws,options={}){
  const eligibleForDraw=typeof options.eligibleForDraw==="function"?options.eligibleForDraw:()=>true;
  const groups={};
  for(const p of plays||[]){
    const id=strategyIdForPlay(p);
    (groups[id]=groups[id]||[]).push(p);
  }
  const out={};
  Object.entries(groups).forEach(([id,stratPlays])=>{
    let lastHitDate=null;
    let best=null;
    let worst=null;
    for(const d of draws||[]){
      const scored=scoreStrategyDraw(stratPlays,d,eligibleForDraw);
      if(scored.hits3plus>0&&!lastHitDate)lastHitDate=d.draw_date||d.date;
      const row={date:d.draw_date||d.date,net:scored.net,won:scored.won};
      if(!best||row.net>best.net)best=row;
      if(!worst||row.net<worst.net)worst=row;
    }
    out[id]={
      newest:Math.max(0,...stratPlays.map(playCreatedTs)),
      oldest:Math.min(...stratPlays.map(playCreatedTs).filter(Boolean)),
      lastHitDate,
      best,
      worst,
    };
  });
  return out;
}

function ScoreboardTab({draws,plays,strategyTrust,drawStatus,todaysLineup,onLockStrategy,onArchiveStrategy,onReplaceStrategy}){
  const navigate=useNavigate();
  const[sortBy,setSortBy]=useState("roi");
  const[windowId,setWindowId]=useState("all");
  const[poolSize,setPoolSize]=useState(1);
  const[compareIds,setCompareIds]=useState([]);
  const lineupIds=lineupIdSet(todaysLineup);
  const publicPlays=filterPlaysForLineup(plays,todaysLineup);
  const publicEligibility=publicEligibilityForLineup(todaysLineup,strategyTrust);

  const title=(
    <div className="sec-hdr" style={{alignItems:"flex-start",gap:12}}>
      <div>
        <span className="sec-title">{"\uD83C\uDFC6 Strategy Scoreboard"}</span>
        <p style={{fontSize:14,color:"#555",lineHeight:1.45,marginTop:4}}>Compare the strategies in Today's Lineup. Library-only strategies keep tracking privately but do not count toward public rankings.</p>
      </div>
      <button type="button" className="btn btn-p" onClick={()=>navigate("/strategy-builder")} style={{whiteSpace:"nowrap"}}>+ Build New Play</button>
    </div>
  );

  if(!plays||plays.length===0){
    return(
      <div>
        {title}
        <NextStepCard
          title="Next Step: Browse Strategy Library"
          text="You can explore public strategies before creating your first one."
          actions={[{label:"Browse My Playslips",onClick:()=>navigate("/my-strategies")}]}
        />
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>{"\uD83D\uDCCA"}</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>You haven't created a strategy yet.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto 18px"}}>Create one strategy now and LottoTracker will start ranking it as your tracked results come in.</p>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button type="button" className="btn btn-p" onClick={()=>navigate("/strategy-builder")}>Create Strategy</button>
            <button type="button" className="btn btn-s" onClick={()=>navigate("/strategy-builder")}>Quick Pick Strategy</button>
          </div>
        </div>
      </div>
    );
  }
  if(!lineupIds.size){
    return(
      <div>
        {title}
        <EmptyState
          icon="*"
          title="No strategies in Today's Lineup."
          text="Add strategies to Today's Lineup before they count toward public ROI, leaderboards, profile, community, or achievements."
          actions={[{label:"Open Lineup",onClick:()=>navigate("/lineup")},{label:"Create Strategy",onClick:()=>navigate("/strategy-builder")}]}
        />
      </div>
    );
  }
  if(!draws||draws.length===0){
    return(
      <div>
        {title}
        <NextStepCard
          title="Next Step: Browse Strategy Library"
          text="While results build, browse strategy ideas you may want to compare later."
          actions={[{label:"Browse My Playslips",onClick:()=>navigate("/my-strategies")}]}
        />
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>{"\uD83C\uDFC1"}</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>Results are on the way.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto"}}>Results will appear automatically after your first tracked draw.</p>
        </div>
      </div>
    );
  }

  const windowOpt=WINDOW_OPTIONS.find(w=>w.id===windowId)||WINDOW_OPTIONS[WINDOW_OPTIONS.length-1];
  const cutoff=windowOpt.days==null?null:daysAgoIso(windowOpt.days);
  const windowedDraws=cutoff==null?draws:draws.filter(d=>(d.draw_date||"")>=cutoff);
  const timeline=summarizeStrategyTimeline(publicPlays,windowedDraws,{eligibleForDraw:publicEligibility});
  const rows=windowedDraws.length===0
    ?[]
    :aggregateStrategies(publicPlays,windowedDraws,{eligibleForDraw:publicEligibility})
      .filter(r=>SHOW_ANCHORS||(r.id!=="anchor3"&&r.id!=="anchor4"))
      .map(r=>({...r,timeline:timeline[r.id]||{}}));

  const sortValue=r=>{
    if(sortBy==="profit") return r.net;
    if(sortBy==="hitRate") return r.hitRate;
    if(sortBy==="m5") return r.m5;
    if(sortBy==="m4") return r.m4;
    if(sortBy==="m3") return r.m3;
    if(sortBy==="plays") return r.playCount;
    if(sortBy==="newest") return r.timeline?.newest||0;
    if(sortBy==="oldest") return r.timeline?.oldest&&Number.isFinite(r.timeline.oldest)?r.timeline.oldest:0;
    return r.roi;
  };

  const sorted=[...rows].sort((a,b)=>{
    const va=sortValue(a),vb=sortValue(b);
    if(va==null&&vb==null) return 0;
    if(va==null) return 1;
    if(vb==null) return -1;
    if(sortBy==="oldest") return va-vb;
    return vb-va;
  });

  const toggleCompare=id=>{
    setCompareIds(prev=>{
      if(prev.includes(id)) return prev.filter(x=>x!==id);
      if(prev.length>=2) return [prev[1],id];
      return [...prev,id];
    });
  };
  const compareRows=compareIds.map(id=>rows.find(r=>r.id===id)).filter(Boolean);
  const winnerId=metric=>{
    if(compareRows.length!==2) return null;
    const va=metric==="profit"?compareRows[0].net:compareRows[0][metric];
    const vb=metric==="profit"?compareRows[1].net:compareRows[1][metric];
    if(va==null||vb==null||va===vb) return null;
    return va>vb?compareRows[0].id:compareRows[1].id;
  };

  const topBadge=sorted[0]&&sorted[0].roi!=null&&sorted[0].roi>0;
  const trustCounts=lockedStrategyCounts(strategyTrust);
  const frac=poolSize===1?0:2;
  const fmt=n=>Number(n||0).toLocaleString(undefined,{minimumFractionDigits:frac,maximumFractionDigits:frac});
  const signedMoney=n=>(n>=0?"+":"\u2212")+"$"+fmt(Math.abs(n));
  const drawSummary=(row,type)=>{
    const item=row.timeline?.[type];
    if(!item) return "\u2014";
    return signedMoney(item.net/poolSize)+" on "+fmtShortDate(item.date);
  };

  return(
    <div>
      {title}
      <NextStepCard
        title="Next Step: Browse Strategy Library"
        text="Want a new idea to compare? Browse public strategies, save one, then come back to see how your own strategies rank."
        actions={[
          {label:"Browse My Playslips",onClick:()=>navigate("/my-strategies")},
          {label:"Create Strategy",onClick:()=>navigate("/strategy-builder")},
        ]}
      />

      <div className="panel" style={{marginBottom:16,borderLeft:"5px solid #008577"}}>
        <div className="sec-hdr" style={{marginBottom:12}}>
          <span className="sec-title" style={{fontSize:20}}>Strategy Lock System</span>
          <span className="sec-badge">Trust Layer</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:12}}>
          {[
            ["Locked Strategies",trustCounts.locked],
            ["Simulation Strategies",trustCounts.simulation],
            ["Active Strategy",trustCounts.active],
            ["Retired Strategies",trustCounts.retired],
          ].map(([label,value])=>(
            <div key={label} style={{background:"#f8fbfa",border:"1px solid #dbeee9",borderRadius:10,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>{label}</div>
              <strong style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,color:"#002855",lineHeight:1}}>{value}</strong>
            </div>
          ))}
        </div>
        <div style={{fontSize:12,color:"#555",lineHeight:1.45}}>
          Locked strategies freeze their numbers for the active draw. Simulations stay unlimited and never affect ROI, achievements, leaderboards, public profiles, or community rankings.
        </div>
      </div>

      <div style={{background:"#fff",borderRadius:14,padding:"16px 20px",marginBottom:16,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:18}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Time Period</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {WINDOW_OPTIONS.map(w=>(
                <button key={w.id} type="button" className={"btn btn-sm "+(windowId===w.id?"btn-p":"btn-s")} onClick={()=>setWindowId(w.id)}>
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Pool Comparison</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {SB_POOL_SIZES.map(n=>(
                <button key={n} type="button" className={"btn btn-sm "+(poolSize===n?"btn-p":"btn-s")} onClick={()=>setPoolSize(n)}>
                  {n===1?"Solo":n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Sort By</div>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{width:"100%",border:"1.5px solid #ddd",borderRadius:8,padding:"9px 10px",fontSize:13,fontFamily:"inherit",background:"#fff"}}>
              {SCORE_SORT_OPTIONS.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{fontSize:11,color:"#888",marginTop:12,lineHeight:1.5}}>
          {poolSize===1
            ?<>Simulating solo play with the same strategy scoring used elsewhere in LottoTracker.</>
            :<>Pool comparison uses the existing pool split: each person sees <strong>1/{poolSize}</strong> of simulated cost and winnings while ROI stays comparable.</>}
        </div>
      </div>

      {windowedDraws.length===0?(
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>{"\uD83D\uDCC5"}</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>No draws in this window yet.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto"}}>Try a wider window or choose "All time" to see more strategy history.</p>
        </div>
      ):(
        <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
            <div style={{fontSize:12,color:"#555"}}>
              <strong>{sorted.length}</strong> strateg{sorted.length===1?"y":"ies"} ranked over <strong>{windowedDraws.length}</strong> draw{windowedDraws.length===1?"":"s"} &middot; {windowOpt.label}
              {poolSize>1&&<> &middot; per person in a <strong>{poolSize}-person pool</strong></>}
            </div>
            <div style={{fontSize:12,color:"#555"}}>Select two cards to compare side by side.</div>
          </div>

          {compareRows.length>0&&(
            <div style={{background:"#fff",borderRadius:14,padding:"16px 18px",marginBottom:16,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                <div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"#002855",textTransform:"uppercase"}}>Comparison Mode</div>
                  <div style={{fontSize:12,color:"#555"}}>{compareRows.length===2?"Winner highlights are shown below.":"Choose one more strategy to compare."}</div>
                </div>
                <button type="button" className="btn btn-s btn-sm" onClick={()=>setCompareIds([])}>Clear</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
                {compareRows.map(r=>{
                  const metricRows=[
                    {key:"roi",label:"ROI",value:r.roi==null?"\u2014":(r.roi>=0?"+":"")+r.roi.toFixed(0)+"%",winner:winnerId("roi")===r.id},
                    {key:"profit",label:"Profit",value:signedMoney(r.net/poolSize),winner:winnerId("profit")===r.id},
                    {key:"hitRate",label:"Hit Rate",value:r.hitRate.toFixed(1)+"%",winner:winnerId("hitRate")===r.id},
                    {key:"m5",label:"Jackpots",value:r.m5,winner:winnerId("m5")===r.id},
                  ];
                  return(
                    <div key={r.id} style={{border:"1px solid #eee",borderRadius:12,padding:14}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                        <span style={{fontSize:20}}>{r.meta.icon}</span>
                        <strong style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,color:r.meta.color,textTransform:"uppercase"}}>{r.meta.label}</strong>
                      </div>
                      <div style={{display:"grid",gap:6}}>
                        {metricRows.map(m=>(
                          <div key={m.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,borderRadius:8,padding:"7px 9px",background:m.winner?"#e9f7ef":"#f8f8f8",border:m.winner?"1px solid #9ad4ad":"1px solid transparent"}}>
                            <span style={{fontSize:12,color:"#555"}}>{m.label}</span>
                            <strong style={{fontSize:13,color:m.winner?"#2E7D32":"#1a1a1a"}}>{m.value}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {sorted.length===0?(
            <div className="empty">
              <div style={{fontSize:40,marginBottom:10}}>{"\uD83D\uDCCA"}</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>You haven't created a strategy yet.</div>
              <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",marginTop:16}}>
                <button type="button" className="btn btn-p" onClick={()=>navigate("/strategy-builder")}>Create Strategy</button>
                <button type="button" className="btn btn-s" onClick={()=>navigate("/strategy-builder")}>Quick Pick Strategy</button>
              </div>
            </div>
          ):(
            <div className="sc-grid" style={{gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))"}}>
              {sorted.map((r,i)=>{
                const positive=(r.roi??0)>=0;
                const roiColor=r.roi==null?"#555":positive?"#2E7D32":"#c8102e";
                const perSpent=r.spent/poolSize;
                const perWon=r.won/poolSize;
                const perNet=r.net/poolSize;
                const netColor=perNet>=0?"#2E7D32":"#c8102e";
                const showTrophy=i===0&&topBadge;
                const isSelected=compareIds.includes(r.id);
                const isAnchor=r.recipeCount>0&&(r.anchorCount===3||r.anchorCount===4);
                const anchorDisclaimer=isAnchor
                  ?(r.anchorCount===3
                      ?"Anchor 3 simulated at $1/draw. Real play requires $528/draw to cover all 528 combinations."
                      :"Anchor 4 simulated at $1/draw. Real play requires $32/draw to cover all 32 combinations.")
                  :null;
                const trustRecord=strategyTrust?.records?.[r.id];
                const statusMeta=strategyTrustStatusMeta(trustRecord?.status);
                const canModify=strategyCanModify(trustRecord,drawStatus);
                const canLock=drawIsBeforeClose(drawStatus)&&trustRecord?.status!=="Locked";
                return(
                  <div key={r.id} className="sc-card" style={{border:isSelected?"2px solid #008577":"2px solid transparent"}}>
                    {showTrophy&&<span className="sc-trophy">&#127942;</span>}
                    <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                      <div style={{display:"flex",alignItems:"center",gap:9}}>
                        <span style={{fontSize:24}}>{r.meta.icon}</span>
                        <div>
                          <div className="sc-name" style={{color:r.meta.color,marginBottom:0}}>{r.meta.label}</div>
                          <div style={{fontSize:11,color:"#555"}}>
                            {isAnchor
                              ?<>{r.recipeCount} anchor set{r.recipeCount===1?"":"s"} &middot; {r.totalExpansion.toLocaleString()} combos</>
                              :<>{r.playCount} play{r.playCount===1?"":"s"} tagged</>}
                          </div>
                        </div>
                      </div>
                      <button type="button" className={"btn btn-sm "+(isSelected?"btn-p":"btn-s")} onClick={()=>toggleCompare(r.id)}>{isSelected?"Selected":"Compare"}</button>
                    </div>

                    <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",marginBottom:10}}>
                      <span className="sec-badge" style={{background:statusMeta.color}}>{statusMeta.icon} {statusMeta.label}</span>
                      {trustRecord?.status==="Locked"?(
                        <span style={{fontSize:11,color:"#008577",fontWeight:900}}>
                          {trustRecord.lockedBeforeClose?"Trusted locked strategy":"Sales closed · Trusted locked strategy"}
                        </span>
                      ):(
                        <span style={{fontSize:11,color:"#777",fontWeight:800}}>What-if simulation only</span>
                      )}
                    </div>
                    {trustRecord?.lockedAt&&(
                      <div style={{fontSize:11,color:"#555",lineHeight:1.35,marginBottom:10}}>
                        Locked {formatLockTimestamp(trustRecord.lockedAt)}{trustRecord.lockedDrawLabel?" · "+trustRecord.lockedDrawLabel:""}
                      </div>
                    )}

                    <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,alignItems:"end",marginBottom:10}}>
                      <div>
                        <div className="sc-roi" style={{color:roiColor}}>
                          {r.roi==null?"\u2014":<AnimatedMetric value={(positive?"+":"")+r.roi.toFixed(0)+"%"}/>}
                        </div>
                        <div style={{fontSize:12,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1}}>ROI</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:22,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:netColor}}>{signedMoney(perNet)}</div>
                        <div style={{fontSize:11,color:"#555",textTransform:"uppercase"}}>Profit / Loss</div>
                      </div>
                    </div>

                    {anchorDisclaimer&&(
                      <div style={{background:"#fff8e6",border:"1px solid #f0d87a",borderRadius:8,padding:"8px 10px",marginBottom:10,fontSize:11,color:"#6b4a00",lineHeight:1.45}}>
                        {anchorDisclaimer}
                      </div>
                    )}

                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 12px",padding:"12px 0",borderTop:"1px solid #f0f0f0",borderBottom:"1px solid #f0f0f0",fontSize:11}}>
                      <div><span style={{color:"#555"}}>Total Simulated Spend</span><br/><strong>${fmt(perSpent)}</strong></div>
                      <div><span style={{color:"#555"}}>Total Simulated Won</span><br/><strong style={{color:"#2E7D32"}}>${fmt(perWon)}</strong></div>
                      <div><span style={{color:"#555"}}>Hit Rate</span><br/><strong>{r.hitRate.toFixed(1)}%</strong></div>
                      <div><span style={{color:"#555"}}>Last Hit Date</span><br/><strong>{fmtShortDate(r.timeline?.lastHitDate)}</strong></div>
                      <div><span style={{color:"#555"}}>Current Streak</span><br/><strong style={{color:r.streak===0?"#2E7D32":"#555"}}>{r.streak===0?"Hit on latest draw":r.streak+" draw"+(r.streak===1?"":"s")}</strong></div>
                      <div><span style={{color:"#555"}}>Play Count</span><br/><strong>{r.playCount}</strong></div>
                      <div><span style={{color:"#555"}}>Best Draw</span><br/><strong>{drawSummary(r,"best")}</strong></div>
                      <div><span style={{color:"#555"}}>Worst Draw</span><br/><strong>{drawSummary(r,"worst")}</strong></div>
                    </div>

                    <div className="sc-stats" style={{borderTop:"none",paddingTop:10}}>
                      <div><span className="sc-num" style={{color:"#D4AF37"}}>{r.m5}</span><span className="sc-lbl">Jackpots</span></div>
                      <div><span className="sc-num" style={{color:"#8B4513"}}>{r.m4}</span><span className="sc-lbl">4 Matches</span></div>
                      <div><span className="sc-num" style={{color:"#2E6DA4"}}>{r.m3}</span><span className="sc-lbl">3 Matches</span></div>
                      <div><span className="sc-num" style={{color:"#555"}}>{r.m2}</span><span className="sc-lbl">Free Tickets</span></div>
                    </div>
                    <button type="button" className="btn btn-p" style={{width:"100%",marginTop:12}} onClick={()=>navigate("/pool-simulation?playSet="+encodeURIComponent(r.id))}>
                      Pool Simulation
                    </button>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:7,marginTop:8}}>
                      {canLock&&<button type="button" className="btn btn-sm btn-p" onClick={()=>onLockStrategy(r.id,"manual")}>Lock Strategy</button>}
                      {canLock&&<button type="button" className="btn btn-sm btn-s" onClick={()=>onLockStrategy(r.id,"tonight")}>Lock For Tonight</button>}
                      {!canLock&&trustRecord?.status!=="Locked"&&drawIsClosedOrLater(drawStatus)&&<button type="button" className="btn btn-sm btn-s" onClick={()=>onLockStrategy(r.id,"auto")}>Lock Strategy</button>}
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>onReplaceStrategy(r.id)} disabled={!canModify}>Create Version</button>
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>onArchiveStrategy(r.id)} disabled={!canModify}>Archive</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{fontSize:11,color:"#888",marginTop:8,textAlign:"center"}}>
            Simulation: $1 per play per draw, pari-mutuel prize values. 2-match = $0 (free Quick Pick). LottoTracker is a simulation and tracking platform only; no tickets are sold and no money is collected.
          </div>
        </>
      )}
    </div>
  );
}

function ScoreboardTabOld({draws,plays}){
  const navigate=useNavigate();
  const[sortBy,setSortBy]=useState("roi"); // roi | won | plays
  const[windowId,setWindowId]=useState("all");
  const[poolSize,setPoolSize]=useState(1);

  // Empty states first — clearest message wins.
  if(!plays||plays.length===0){
    return(
      <div>
        <div className="sec-hdr"><span className="sec-title">Strategy ROI</span><span className="sec-badge">Simulation</span></div>
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>📊</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>You haven't created a strategy yet.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto 18px"}}>Save your first numbers, then LottoTracker will compare strategies as results come in.</p>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button type="button" className="btn btn-p" onClick={()=>navigate("/strategy-builder")}>Create Strategy</button>
            <button type="button" className="btn btn-s" onClick={()=>navigate("/my-strategies")}>Quick Pick</button>
          </div>
        </div>
      </div>
    );
  }
  if(!draws||draws.length===0){
    return(
      <div>
        <div className="sec-hdr"><span className="sec-title">Strategy ROI</span><span className="sec-badge">Simulation</span></div>
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>🏳️</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>Results are on the way.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto"}}>Results will appear automatically after your first tracked draw.</p>
        </div>
      </div>
    );
  }

  const windowOpt=WINDOW_OPTIONS.find(w=>w.id===windowId)||WINDOW_OPTIONS[WINDOW_OPTIONS.length-1];
  const cutoff=windowOpt.days==null?null:daysAgoIso(windowOpt.days);
  const windowedDraws=cutoff==null?draws:draws.filter(d=>(d.draw_date||"")>=cutoff);

  // Aggregate is computed only once window-filtered draws are in hand.
  // Empty windowedDraws skips the calc to avoid a misleading "N/A" card.
  // SHOW_ANCHORS gates the anchor3/anchor4 cards; the plays themselves are
  // still aggregated (cheap) so flipping the flag back on doesn't need any
  // additional work.
  const rows=windowedDraws.length===0
    ?[]
    :aggregateStrategies(plays,windowedDraws).filter(r=>SHOW_ANCHORS||(r.id!=="anchor3"&&r.id!=="anchor4"));

  // Sort: nulls (n/a ROI) always last; otherwise descending by the chosen metric.
  const sorted=[...rows].sort((a,b)=>{
    const key=sortBy==="won"?"won":sortBy==="plays"?"playCount":"roi";
    const va=a[key],vb=b[key];
    if(va==null&&vb==null) return 0;
    if(va==null) return 1;
    if(vb==null) return -1;
    return vb-va;
  });

  const topBadge=sorted[0]&&sorted[0].roi!=null&&sorted[0].roi>0;
  // When split across a pool, money values may be fractional cents; show 2
  // decimals to avoid hiding the split. Solo shows whole dollars like before.
  const frac=poolSize===1?0:2;
  const fmt=n=>Number(n).toLocaleString(undefined,{minimumFractionDigits:frac,maximumFractionDigits:frac});

  return(
    <div>
      <div className="sec-hdr">
        <span className="sec-title">Strategy ROI</span>
        <span className="sec-badge">Simulation</span>
      </div>

      {/* Window + pool toggles — both drive every card below. */}
      <div style={{background:"#fff",borderRadius:14,padding:"16px 20px",marginBottom:16,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
        <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
          <div style={{flex:"1 1 auto",minWidth:260}}>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Time window</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {WINDOW_OPTIONS.map(w=>(
                <button key={w.id} type="button" className={"btn btn-sm "+(windowId===w.id?"btn-p":"btn-s")} onClick={()=>setWindowId(w.id)}>
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{flex:"1 1 auto",minWidth:260}}>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Pool size</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {SB_POOL_SIZES.map(n=>(
                <button key={n} type="button" className={"btn btn-sm "+(poolSize===n?"btn-p":"btn-s")} onClick={()=>setPoolSize(n)}>
                  {n===1?"Solo":n}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{fontSize:11,color:"#888",marginTop:12,lineHeight:1.5}}>
          {poolSize===1
            ?<>Simulating solo play &mdash; 1 ticket per draw. Toggle a pool size to see per-person splits.</>
            :<>ROI is identical across pool sizes &mdash; pools scale <strong>exposure</strong>, not odds. Each person here sees <strong>1/{poolSize}</strong> of both cost and winnings.</>}
        </div>
      </div>

      {/* Empty state 3: window filter emptied the list (plays + full draws both exist). */}
      {windowedDraws.length===0?(
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>📅</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>No draws in this window yet.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto"}}>Try a wider window or choose "All time" to see more strategy history.</p>
        </div>
      ):(
        <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
            <div>
              <span style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:600,marginRight:8}}>Sort by</span>
              <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{border:"1.5px solid #ddd",borderRadius:8,padding:"5px 10px",fontSize:12,fontFamily:"inherit",background:"#fff"}}>
                <option value="roi">Simulated ROI</option>
                <option value="won">Would Have Won</option>
                <option value="plays">Play count</option>
              </select>
            </div>
            <div style={{fontSize:12,color:"#555"}}>
              <strong>{windowedDraws.length}</strong> draw{windowedDraws.length===1?"":"s"} &middot; {windowOpt.label}
              {poolSize>1&&<> &middot; per person in <strong>{poolSize}-person pool</strong></>}
            </div>
          </div>

          <div className="sc-grid">
            {sorted.map((r,i)=>{
              const positive=(r.roi??0)>=0;
              const roiColor=r.roi==null?"#555":positive?"#2E7D32":"#c8102e";
              // Per-person splits. Counts (m5/m4/m3/m2, hit rate, streak) are
              // pool-invariant — every pooler participates in every hit.
              const perSpent=r.spent/poolSize;
              const perWon=r.won/poolSize;
              const perNet=r.net/poolSize;
              const perBest=r.bestDraw/poolSize;
              const netColor=perNet>=0?"#2E7D32":"#c8102e";
              const showTrophy=i===0&&topBadge;
              // Disclaimer for anchor strategies — real-world cost is the
              // expansion size × drawCount, while the sim uses $1/draw.
              const isAnchor=r.recipeCount>0&&(r.anchorCount===3||r.anchorCount===4);
              const anchorDisclaimer=isAnchor
                ?(r.anchorCount===3
                    ?"Anchor 3 simulated at $1/draw. Real play requires $528/draw to cover all 528 combinations."
                    :"Anchor 4 simulated at $1/draw. Real play requires $32/draw to cover all 32 combinations.")
                :null;
              return(
                <div key={r.id} className="sc-card">
                  {showTrophy&&<span className="sc-trophy">&#127942;</span>}
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span style={{fontSize:18}}>{r.meta.icon}</span>
                    <div>
                      <div className="sc-name" style={{color:r.meta.color,marginBottom:0}}>{r.meta.label}</div>
                      <div style={{fontSize:11,color:"#555"}}>
                        {isAnchor
                          ?<>{r.recipeCount} anchor set{r.recipeCount===1?"":"s"} &middot; {r.totalExpansion.toLocaleString()} combos</>
                          :<>{r.playCount} play{r.playCount===1?"":"s"} tagged</>}
                      </div>
                    </div>
                  </div>
                  <div className="sc-roi" style={{color:roiColor}}>
                    {r.roi==null?"—":(positive?"+":"")+r.roi.toFixed(0)+"%"}
                  </div>
                  <div style={{fontSize:12,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Simulated ROI</div>
                  <div style={{fontSize:13,fontWeight:600,color:netColor,marginBottom:10}}>
                    {perNet>=0?"+":"\u2212"}${fmt(Math.abs(perNet))} simulated {perNet>=0?"profit":"loss"}
                  </div>
                  {anchorDisclaimer&&(
                    <div style={{background:"#fff8e6",border:"1px solid #f0d87a",borderRadius:8,padding:"8px 10px",marginBottom:10,fontSize:11,color:"#6b4a00",lineHeight:1.45}}>
                      {anchorDisclaimer}
                    </div>
                  )}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 12px",padding:"10px 0",borderTop:"1px solid #f0f0f0",borderBottom:"1px solid #f0f0f0",fontSize:11}}>
                    <div><span style={{color:"#555"}}>Simulated Spend</span><br/><strong>${fmt(perSpent)}</strong></div>
                    <div><span style={{color:"#555"}}>Would Have Won</span><br/><strong style={{color:"#2E7D32"}}>${fmt(perWon)}</strong></div>
                    <div><span style={{color:"#555"}}>Best Draw</span><br/><strong>${fmt(perBest)}</strong></div>
                    <div><span style={{color:"#555"}}>Hit Rate (2+)</span><br/><strong>{r.hitRate.toFixed(1)}%</strong></div>
                    <div style={{gridColumn:"1 / -1"}}>
                      <span style={{color:"#555"}}>Cold streak</span>{" "}
                      <strong style={{color:r.streak===0?"#2E7D32":"#555"}}>
                        {r.streak===0?"Hit on latest draw":r.streak+" draw"+(r.streak===1?"":"s")+" since last 3+"}
                      </strong>
                    </div>
                  </div>
                  <div className="sc-stats" style={{borderTop:"none",paddingTop:8}}>
                    <div><span className="sc-num" style={{color:"#D4AF37"}}>{r.m5}</span><span className="sc-lbl">Jackpots</span></div>
                    <div><span className="sc-num" style={{color:"#8B4513"}}>{r.m4}</span><span className="sc-lbl">4-Match</span></div>
                    <div><span className="sc-num" style={{color:"#2E6DA4"}}>{r.m3}</span><span className="sc-lbl">3-Match</span></div>
                    <div><span className="sc-num" style={{color:"#555"}}>{r.m2}</span><span className="sc-lbl">Free tix</span></div>
                  </div>
                  <button type="button" className="btn btn-p" style={{width:"100%",marginTop:12}} onClick={()=>navigate("/pool-simulation?playSet="+encodeURIComponent(r.id))}>
                    Run Pool Simulation
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{fontSize:11,color:"#888",marginTop:8,textAlign:"center"}}>
            Simulation: $1 per play per draw, pari-mutuel prize values. 2-match = $0 (free Quick Pick). Streak counts consecutive recent draws with no 3+ match {windowOpt.days!=null?"within this window":"across all draws"}.
          </div>
        </>
      )}
    </div>
  );
}

// ── POOL SIMULATION ────────────────────────────────────────────────
// Pool sizes in the picker (featured). Comparison table adds "solo" (1)
// as a baseline so users can see how per-person cost/winnings collapse
// as the pool scales.
const PICKER_POOL_SIZES=[5,10,25,50,100];
const COMPARISON_POOL_SIZES=[1,5,10,25,50,100];
function poolLabel(n){return n===1?"Solo":"With "+n+" friends";}

function fmtMoney(n){
  if(n==null||!Number.isFinite(Number(n))) return "\u2014";
  return "$"+Number(n).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});
}

// Score every play against a single draw. Returns group-level totals
// (pre-pool-split) — the PoolSimulationTab divides these by N below.
function calcDrawGroupResult(plays,draw){
  const empty={plays:0,spent:0,won:0,net:0,m5:0,m4:0,m3:0,m2:0,hits2plus:0,pending:false};
  if(!plays?.length||!draw) return empty;
  // Pool sim ignores recipe rows for now — they'll be expanded in Feature 2.
  const materialized=plays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT);
  if(!materialized.length) return empty;
  const p5=Number(draw.prize_5match)||0;
  const p4=Number(draw.prize_4match)||0;
  const p3=Number(draw.prize_3match)||0;
  let won=0,m5=0,m4=0,m3=0,m2=0;
  for(const pl of materialized){
    const m=cntMatch(pl.numbers,draw.numbers);
    if(m===5){m5++;won+=p5;}
    else if(m===4){m4++;won+=p4;}
    else if(m===3){m3++;won+=p3;}
    else if(m===2){m2++;} // free Quick Pick, always $0
  }
  const spent=materialized.length; // $1/play
  return{
    plays:materialized.length,
    spent,won,net:won-spent,
    m5,m4,m3,m2,
    hits2plus:m5+m4+m3+m2,
    pending:draw.prize_5match==null||draw.prize_4match==null||draw.prize_3match==null,
  };
}

function PoolSimulationTab({draws,plays}){
  const navigate=useNavigate();
  const[searchParams,setSearchParams]=useSearchParams();
  const[drawId,setDrawId]=useState(null);
  const[size,setSize]=useState(10);
  const[simGate,setSimGate]=useState({allowed:!betaCapacityModeEnabled(),message:betaCapacityModeEnabled()?"Pool Simulation is temporarily paused while beta capacity mode is active. Please try again later.":""});
  // Play Set filters all pool math and can be preselected from Scoreboard.
  const[playSet,setPlaySet]=useState(searchParams.get("playSet")||"all");
  const playSetOptions=[
    {id:"all",label:"All My Playslips"},
    ...Array.from(new Set((plays||[]).map(p=>p.strategy||"manual")))
      .filter(id=>id!=="manual")
      .map(id=>({id,label:getStrategyMeta(id).label}))
  ];

  // Keep selection valid when draws first arrive or when the chosen draw
  // disappears (e.g. admin deletes it).
  useEffect(()=>{
    if(!draws?.length){setDrawId(null);return;}
    if(!drawId||!draws.some(d=>d.id===drawId)) setDrawId(draws[0].id);
  },[draws,drawId]);
  useEffect(()=>{
    if(betaCapacityModeEnabled()){
      setSimGate({allowed:false,message:"Pool Simulation is temporarily paused while beta capacity mode is active. Please try again later."});
      return;
    }
    const limit=rateLimitAction("pool-simulation:"+((plays||[])[0]?.user_id||"local"),10,10*60*1000);
    setSimGate(limit.allowed?{allowed:true,message:""}:{allowed:false,message:"Please wait before running another simulation. Try again in "+limit.retryText+"."});
  },[]);

  if(!plays?.length){
    return(
      <div>
        <div className="sec-hdr"><span className="sec-title">Run Pool Simulation</span><span className="sec-badge">Simulation</span></div>
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>🤝</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>Pool tools are almost ready.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto 18px"}}>Join a pool or create your own when Pool Manager launches. For now, add numbers to preview pool simulations.</p>
          <button type="button" className="btn btn-p" onClick={()=>navigate("/my-strategies")}>Add My Playslips</button>
        </div>
      </div>
    );
  }
  if(!simGate.allowed){
    return(
      <div>
        <div className="sec-hdr"><span className="sec-title">Run Pool Simulation</span><span className="sec-badge">Capacity protected</span></div>
        <EmptyState
          icon="*"
          title="Simulation is paused."
          text={simGate.message||"Heavy simulations are temporarily protected during beta traffic."}
          actions={[{label:"Return Home",onClick:()=>navigate("/")},{label:"Open Scoreboard",onClick:()=>navigate("/scoreboard")}]}
        />
      </div>
    );
  }
  if(!draws?.length){
    return(
      <div>
        <div className="sec-hdr"><span className="sec-title">Run Pool Simulation</span><span className="sec-badge">Simulation</span></div>
        <div className="empty">
          <div style={{fontSize:40,marginBottom:10}}>🏳️</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>Pool results will appear soon.</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:560,margin:"0 auto"}}>Results will appear automatically after your first tracked draw. Pool Manager is still Coming Soon.</p>
        </div>
      </div>
    );
  }

  const draw=draws.find(d=>d.id===drawId)||draws[0];
  // Filter plays by the selected play set. "all" keeps current behavior;
  // future strategy IDs map to plays where p.strategy === playSet.
  const filteredPlays=playSet==="all"?plays:plays.filter(p=>p.strategy===playSet);
  const group=calcDrawGroupResult(filteredPlays,draw);
  const roi=group.spent>0?((group.won-group.spent)/group.spent)*100:null;

  // Per-person breakdown across every pool size in the comparison table.
  // ROI is constant — pooling scales both cost and winnings, so % never
  // changes. Surfaced in a footnote to set expectations.
  const rows=COMPARISON_POOL_SIZES.map(n=>({
    n,
    perCost:group.spent/n,
    perWon:group.won/n,
    perNet:(group.won-group.spent)/n,
    roi,
  }));
  const featured=rows.find(r=>r.n===size)||rows[0];

  return(
    <div>
      <div className="sec-hdr">
        <span className="sec-title">Run Pool Simulation</span>
        <span className="sec-badge">Simulation</span>
      </div>
      <div style={{fontSize:14,color:"#555",margin:"-8px 0 6px"}}>See how your Fantasy 5 strategy performs when you play solo or with a group.</div>
      <div style={{fontSize:12,color:"#555",margin:"0 0 16px"}}>Simulation only. No tickets sold. No money collected. No real lottery pool funds handled.</div>
      <div className="panel" style={{marginBottom:16,borderLeft:"5px solid #2E6DA4"}}>
        <div className="sec-title" style={{fontSize:20,marginBottom:6}}>What-if Simulation Only</div>
        <div style={{fontSize:13,color:"#555",lineHeight:1.45}}>
          Run unlimited simulations and review what a play <strong>would have won</strong>. Simulation history never affects ROI, Scoreboard rankings, achievements, leaderboards, Strategy Library, Community, or public profiles.
        </div>
      </div>

      <div className="panel" style={{marginBottom:16}}>
        {/* Strategy sits on its own row because it filters everything below
            it; draw + pool size are scoped within the chosen strategy. */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Strategy</div>
          <select value={playSet} onChange={e=>{setPlaySet(e.target.value);setSearchParams(e.target.value==="all"?{}:{playSet:e.target.value});}} style={{width:"100%",border:"1.5px solid #ddd",borderRadius:8,padding:"9px 12px",fontSize:14,fontFamily:"inherit",background:"#fff"}}>
            {playSetOptions.map(opt=><option key={opt.id} value={opt.id}>{opt.label}</option>)}
          </select>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Select draw</div>
            <select value={drawId||""} onChange={e=>setDrawId(e.target.value)} style={{width:"100%",border:"1.5px solid #ddd",borderRadius:8,padding:"9px 12px",fontSize:14,fontFamily:"inherit",background:"#fff"}}>
              {draws.map(d=><option key={d.id} value={d.id}>{d.date} · {d.isEvening?"Evening":"Midday"}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Choose Pool Size</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {PICKER_POOL_SIZES.map(n=>(
                <button key={n} type="button" className={"btn btn-sm "+(size===n?"btn-p":"btn-s")} style={{flex:1,minWidth:40,padding:"8px 6px"}} onClick={()=>setSize(n)}>
                  {poolLabel(n)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Draw summary strip */}
        <div style={{background:"#f0f8f6",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#0a4f3d",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <strong>{draw.date} · {draw.isEvening?"Evening":"Midday"}</strong>
          {(draw.numbers||[]).map((n,i)=><Ball key={i} n={n} size="sm"/>)}
          <span style={{marginLeft:"auto"}}>
            {group.hits2plus===0
              ?<span style={{color:"#888"}}>No hits on this draw</span>
              :<>
                {group.m5>0&&<span>{group.m5}× Jackpot · </span>}
                {group.m4>0&&<span>{group.m4}× 4-Match · </span>}
                {group.m3>0&&<span>{group.m3}× 3-Match · </span>}
                {group.m2>0&&<span>{group.m2}× Free tix</span>}
              </>
            }
          </span>
        </div>

        {group.pending&&(
          <div style={{background:"#fff7e6",border:"1px solid #ffc107",borderRadius:8,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#664d00"}}>
            ⚠ One or more prize tiers for this draw are blank. Winnings shown will be $0 until an admin backfills the amounts.
          </div>
        )}

        {/* Featured card */}
        <div style={{background:"#002855",color:"#fff",borderRadius:14,padding:"18px 20px",marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,.6)",textTransform:"uppercase",letterSpacing:2,marginBottom:10}}>
            {size===1?"Run Solo":"Run With "+size+" Friends"}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
            <div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:1}}>Each Person Invests</div>
              <div style={{fontSize:24,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",lineHeight:1.1}}>{fmtMoney(featured.perCost)}</div>
            </div>
            <div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:1}}>Each Person Would Have Won</div>
              <div style={{fontSize:24,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",lineHeight:1.1,color:"#D4AF37"}}>{fmtMoney(featured.perWon)}</div>
            </div>
            <div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:1}}>Profit / Loss</div>
              <div style={{fontSize:24,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",lineHeight:1.1,color:featured.perNet>=0?"#6ee7b7":"#fca5a5"}}>
                {featured.perNet>=0?"+":"\u2212"}{fmtMoney(Math.abs(featured.perNet))}
              </div>
            </div>
            <div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:1}}>Simulated ROI</div>
              <div style={{fontSize:24,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",lineHeight:1.1,color:featured.roi==null?"#aaa":(featured.roi>=0?"#6ee7b7":"#fca5a5")}}>
                {featured.roi==null?"\u2014":((featured.roi>=0?"+":"")+featured.roi.toFixed(0)+"%")}
              </div>
            </div>
          </div>
        </div>

        {/* Side-by-side comparison table */}
        <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>All pool sizes — side by side</div>
        <div style={{overflowX:"auto"}}>
          <table className="hist-tbl" style={{fontSize:12}}>
            <thead><tr>
              <th>Pool</th>
              <th>Each Person Invests</th>
              <th>Win share</th>
              <th>Profit / Loss</th>
              <th>Simulated ROI</th>
            </tr></thead>
            <tbody>
              {rows.map(r=>{
                const isFeatured=r.n===size;
                return(
                  <tr key={r.n} style={isFeatured?{background:"rgba(0,40,85,0.06)"}:{}}>
                    <td style={{fontWeight:700,color:isFeatured?"#002855":"#333"}}>
                      {poolLabel(r.n)}{isFeatured?" \u2190":""}
                    </td>
                    <td>{fmtMoney(r.perCost)}</td>
                    <td style={{color:"#2E7D32",fontWeight:600}}>{fmtMoney(r.perWon)}</td>
                    <td style={{color:r.perNet>=0?"#2E7D32":"#c8102e",fontWeight:600}}>
                      {r.perNet>=0?"+":"\u2212"}{fmtMoney(Math.abs(r.perNet))}
                    </td>
                    <td style={{color:r.roi==null?"#888":(r.roi>=0?"#2E7D32":"#c8102e"),fontWeight:700}}>
                      {r.roi==null?"\u2014":((r.roi>=0?"+":"")+r.roi.toFixed(0)+"%")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{fontSize:11,color:"#888",marginTop:8,textAlign:"center"}}>
          ROI stays constant as the pool scales — cost and winnings divide together. Pooling's real value is lowering per-person ticket budget.
        </div>
      </div>

      {/* Group totals */}
      <div className="panel">
        <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Group totals for this draw</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:12}}>
          <div><div style={{fontSize:11,color:"#555"}}>Plays</div><strong style={{fontSize:22,fontFamily:"'Barlow Condensed',sans-serif"}}>{group.plays}</strong></div>
          <div><div style={{fontSize:11,color:"#555"}}>Simulated Spend</div><strong style={{fontSize:22,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtMoney(group.spent)}</strong></div>
          <div><div style={{fontSize:11,color:"#555"}}>Would Have Won</div><strong style={{fontSize:22,fontFamily:"'Barlow Condensed',sans-serif",color:"#2E7D32"}}>{fmtMoney(group.won)}</strong></div>
          <div><div style={{fontSize:11,color:"#555"}}>Matches 2+</div><strong style={{fontSize:22,fontFamily:"'Barlow Condensed',sans-serif"}}>{group.hits2plus}</strong></div>
        </div>
      </div>
    </div>
  );
}

function HotColdTab({draws,scratch,setScratch}){
  const[period,setPeriod]=useState(30);
  // scratch / setScratch are lifted to App level so the list survives tab
  // navigation. Still NOT persisted -- reload clears (App useState default).
  // A future "Save these to Strategy N" button will be what writes to DB.
  const freq={};
  for(const d of draws.slice(0,period))for(const n of(d.numbers||[]))freq[n]=(freq[n]||0)+1;
  const all=Array.from({length:MAX_NUM},(_,i)=>i+1).map(n=>({n,c:freq[n]||0})).sort((a,b)=>b.c-a.c);
  const maxC=all[0]?.c||1;
  const hot=all.slice(0,7);const cold=all.slice(-7).reverse();
  const wf={};for(const d of draws.slice(0,7))for(const n of(d.numbers||[]))wf[n]=(wf[n]||0)+1;
  const weekHot=Array.from({length:MAX_NUM},(_,i)=>i+1).map(n=>({n,c:wf[n]||0})).sort((a,b)=>b.c-a.c).slice(0,5);

  // Quick-pick weighted toward the current window's top 10 hottest: 3 picks
  // from the top 10, 2 from the remaining 26. When period=7 and the draw
  // count is tiny, ties break by the natural 1→36 ordering that falls out
  // of the sort, which is fine — the goal is "a play that leans hot," not
  // a provably-optimal sampling.
  function quickPickHot(){
    const top10=all.slice(0,10).map(x=>x.n);
    const top10Set=new Set(top10);
    const rest=[];
    for(let i=1;i<=MAX_NUM;i++) if(!top10Set.has(i)) rest.push(i);
    const shuf=arr=>{const a=[...arr];for(let i=a.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[a[i],a[j]]=[a[j],a[i]];}return a;};
    const picks=[...shuf(top10).slice(0,3),...shuf(rest).slice(0,2)].sort((a,b)=>a-b);
    setScratch(s=>[...s,picks]);
  }

  const canQP=draws&&draws.length>0;
  return(
    <div>
      <div className="sec-hdr"><span className="sec-title">Hot / Cold Numbers</span>
        <div>{[7,14,30].map(p=><button key={p} className={"btn btn-sm "+(period===p?"btn-p":"btn-s")} style={{marginLeft:6}} onClick={()=>setPeriod(p)}>{p}d</button>)}</div>
      </div>
      <div className="panel" style={{marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:12,flexWrap:"wrap"}}>
          <div style={{fontSize:13,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1}}>This Week Hot Picks</div>
          <button type="button" className="btn btn-p btn-sm" onClick={quickPickHot} disabled={!canQP} title={canQP?"Generate a play with 3 hot + 2 random numbers (using the current "+period+"-day window)":"No draws to analyze yet"}>
            Quick Pick with Hot Numbers
          </button>
        </div>
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>{weekHot.map(({n,c})=><div key={n} style={{textAlign:"center"}}><Ball n={n}/><div style={{fontSize:10,color:"#555",marginTop:3}}>{c}x</div></div>)}</div>
      </div>
      {/* Hot-Pick Generator scratch list — in-memory only, no DB write. Clears
          on reload. A future "Save these to Strategy N" button will move any
          of these to a persistent strategy slot. */}
      {scratch.length>0&&(
        <div className="panel" style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:12,flexWrap:"wrap"}}>
            <div style={{fontSize:13,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1}}>Your Hot-Pick Generator</div>
            <button type="button" className="btn btn-s btn-sm" onClick={()=>setScratch([])}>Clear</button>
          </div>
          <div style={{fontSize:11,color:"#888",marginBottom:10}}>These plays are local to this page and clear on reload. Not saved to My Playslips.</div>
          {scratch.map((play,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderBottom:i<scratch.length-1?"1px solid #f0f0f0":"none"}}>
              <span style={{fontSize:12,color:"#999",width:36,flexShrink:0}}>#{i+1}</span>
              <div style={{display:"flex",gap:4}}>
                {play.map((n,j)=><Ball key={j} n={n} size="sm"/>)}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="hc-section">
        <div className="hc-panel"><div className="hc-hdr" style={{color:"var(--red)"}}>Hot Numbers</div>
          {hot.map(({n,c})=><div key={n} className="hc-row"><Ball n={n} size="sm"/><div className="hc-bar-wrap"><div className="hc-bar" style={{width:(c/maxC*100)+"%",background:"#c8102e"}}/></div><span className="hc-freq">{c}x</span></div>)}
        </div>
        <div className="hc-panel"><div className="hc-hdr" style={{color:"var(--teal)"}}>Cold Numbers</div>
          {cold.map(({n,c})=><div key={n} className="hc-row"><Ball n={n} size="sm"/><div className="hc-bar-wrap"><div className="hc-bar" style={{width:((c/maxC*100)+3)+"%",background:"#2E6DA4"}}/></div><span className="hc-freq">{c}x</span></div>)}
        </div>
      </div>
      <div className="panel">
        <div className="sec-title" style={{marginBottom:14}}>All Numbers — {period}-Day Frequency</div>
        <div className="hc-grid">
          {all.map(({n,c})=>{
            const t=c/maxC;
            const bg=c===0?"#e0e0e0":"rgba("+(Math.round(200-t*170))+","+(Math.round(100+t*30))+","+(Math.round(50-t*40))+","+(0.4+t*0.6)+")";
            return(<div key={n}><div className="hc-ball" style={{background:bg}}>{n}</div><div className="hc-count">{c}x</div></div>);
          })}
        </div>
      </div>
    </div>
  );
}

function LaunchReadinessTab({user,draws,drawsLoading,drawStatus,activityNotifications}){
  const[apiHealth,setApiHealth]=useState({label:"Checking",detail:"Contacting draw API...",color:"#c8860a"});
  const[pwaStatus,setPwaStatus]=useState({label:"Checking",detail:"Checking manifest and service worker...",color:"#c8860a"});
  useEffect(()=>{
    let active=true;
    monitoredFetch("/api/draws?limit=5",{cache:"no-store"},"readiness-draws")
      .then(async r=>{
        const data=await r.json().catch(()=>null);
        if(!active)return;
        setApiHealth({
          label:r.ok?"Healthy":"Needs review",
          detail:r.ok?"Draw API responded with "+(Array.isArray(data)?data.length:"valid")+" record set.":"Draw API returned HTTP "+r.status+".",
          color:r.ok?"#008577":"#c8102e",
        });
      })
      .catch(e=>{
        if(active)setApiHealth({label:"Needs review",detail:e?.message||"Draw API did not respond.",color:"#c8102e"});
      });
    return()=>{active=false;};
  },[]);
  useEffect(()=>{
    const hasManifest=!!document.querySelector('link[rel="manifest"]');
    if(!("serviceWorker" in navigator)){
      setPwaStatus({label:hasManifest?"Partial":"Needs review",detail:hasManifest?"Manifest is present. Service worker is not available in this browser.":"Manifest and service worker were not detected.",color:hasManifest?"#c8860a":"#c8102e"});
      return;
    }
    navigator.serviceWorker.getRegistration()
      .then(reg=>setPwaStatus({
        label:hasManifest&&reg?"Ready":hasManifest?"Manifest ready":"Needs review",
        detail:hasManifest&&reg?"Manifest and service worker are registered.":hasManifest?"Manifest is present. Service worker will register after browser load.":"Service worker support exists, but manifest was not found.",
        color:hasManifest&&reg?"#008577":hasManifest?"#c8860a":"#c8102e",
      }))
      .catch(()=>setPwaStatus({label:hasManifest?"Manifest ready":"Needs review",detail:"Could not inspect service worker registration.",color:hasManifest?"#c8860a":"#c8102e"}));
  },[]);
  const launchGames=launchReadyGames();
  const soonGames=GAME_CATALOG.filter(g=>g.roadmap==="soon");
  const statusRows=[
    ["Authentication status",user?.email?"Signed in as "+user.email:"Not signed in","#008577"],
    ["Draw engine status",drawsLoading?"Loading":drawStatus?.next?drawStatus.next.status:"No active draw state",drawStatus?.next?"#008577":"#c8860a"],
    ["Results engine status",draws.some(d=>Array.isArray(d.numbers)&&d.numbers.length)?"Ready with completed draws":"Waiting for completed draws",draws.some(d=>Array.isArray(d.numbers)&&d.numbers.length)?"#008577":"#c8860a"],
    ["Play Builder status","Ready","#008577"],
    ["Activity Center status",(activityNotifications?.length||0)+" notifications available","#008577"],
    ["PWA status",pwaStatus.label,pwaStatus.color],
    ["API health",apiHealth.label,apiHealth.color],
    ["Build version",APP_VERSION_LABEL+" · "+APP_BUILD_VERSION,"#008577"],
    ["Last deployment time",document.lastModified||"Unavailable","#2E6DA4"],
  ];
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Launch Readiness</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            Administrator-only public beta checklist for the current LottoTracker build.
          </p>
        </div>
        <span className="sec-badge" style={{background:"#008577"}}>{APP_VERSION_LABEL}</span>
      </div>
      <div className="launch-note" style={{marginBottom:18}}>{TRUST_COPY}</div>
      <div className="settings-grid" style={{marginBottom:18}}>
        {statusRows.map(([label,value,color])=>(
          <div key={label} className="panel" style={{marginBottom:0}}>
            <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{label}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:900,color:"#002855",lineHeight:1.05}}>{value}</div>
            <div style={{width:38,height:5,borderRadius:8,background:color,marginTop:12}}/>
          </div>
        ))}
      </div>
      <div className="settings-grid">
        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Supported Games</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {launchGames.map(g=><span key={g.id} className="dc-badge" style={{background:"#00857720",color:"#008577"}}>{g.label}</span>)}
          </div>
        </div>
        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Coming Soon Games</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {soonGames.map(g=><span key={g.id} className="dc-badge" style={{background:"#2E6DA420",color:"#2E6DA4"}}>{g.label}</span>)}
          </div>
        </div>
        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:8}}>API Health Detail</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5}}>{apiHealth.detail}</div>
          <div className="sec-title" style={{fontSize:20,margin:"16px 0 8px"}}>PWA Detail</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5}}>{pwaStatus.detail}</div>
        </div>
      </div>
    </div>
  );
}

function LaunchMonitorTab({strategyTrust,todaysLineup,onToggleCapacity}){
  const[stats,setStats]=useState({loading:true,userCount:"Checking",newToday:"Checking",strategiesToday:null,lineupLocksToday:null,warnings:[]});
  const[capacity,setCapacity]=useState(betaCapacityModeEnabled());
  const metrics=betaMetrics();
  const today=new Date().toISOString().slice(0,10);
  const strategyEvents=(strategyTrust?.events||[]).filter(e=>String(e.at||"").slice(0,10)===today);
  const localStrategiesCreatedToday=strategyEvents.filter(e=>e.type==="Strategy Created").length;
  const localLineupLocksToday=[
    ...(todaysLineup?.snapshots||[]).filter(s=>String(s.lockedAt||"").slice(0,10)===today),
    ...strategyEvents.filter(e=>e.type==="Strategy Locked"),
  ].length;
  const failedApi=(metrics.api||[]).filter(item=>item.ok===false||Number(item.status)>=400);
  const slow=(metrics.slow||[]);
  useEffect(()=>{
    let active=true;
    async function load(){
      try{
        const{data:{session}}=await supabase.auth.getSession();
        const token=session?.access_token;
        const r=await monitoredFetch("/api/admin/monitor",{headers:token?{Authorization:"Bearer "+token}:{}}, "admin-monitor");
        const data=await r.json().catch(()=>({}));
        if(!r.ok)throw new Error(data.error||"Monitor unavailable");
        if(!active)return;
        setStats({
          loading:false,
          userCount:data.user_count??"Unavailable",
          newToday:data.new_signups_today??"Unavailable",
          strategiesToday:data.strategies_created_today,
          lineupLocksToday:data.lineup_locks_today,
          warnings:data.warnings||[],
        });
      }catch(e){
        if(active)setStats({loading:false,userCount:"Unavailable",newToday:"Unavailable",strategiesToday:null,lineupLocksToday:null,warnings:[e?.message||"Monitor unavailable"]});
      }
    }
    load();
    return()=>{active=false;};
  },[today]);
  function toggleCapacity(){
    const next=!capacity;
    setCapacity(next);
    setBetaCapacityMode(next);
    if(onToggleCapacity)onToggleCapacity(next);
  }
  const cards=[
    ["User count",stats.userCount,"#002855"],
    ["New signups today",stats.newToday,"#008577"],
    ["Strategies created today",stats.strategiesToday??localStrategiesCreatedToday,"#2E6DA4"],
    ["Lineup locks today",stats.lineupLocksToday??localLineupLocksToday,"#c8860a"],
    ["Failed API calls",failedApi.length,failedApi.length?"#c8102e":"#008577"],
    ["Slow pages/actions",slow.length,slow.length?"#c8860a":"#008577"],
  ];
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Launch Monitor</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            Administrator traffic watch for the first public beta wave.
          </p>
        </div>
        <button type="button" className={"btn "+(capacity?"btn-p":"btn-s")} onClick={toggleCapacity}>
          {capacity?"Beta capacity mode ON":"Beta capacity mode OFF"}
        </button>
      </div>
      <div className="launch-note" style={{marginBottom:18}}>
        Capacity mode temporarily disables heavy simulations. Tracking, draw status, scoring, and lock logic are unchanged.
      </div>
      {!!stats.warnings?.length&&(
        <div className="auth-err" style={{marginBottom:18}}>
          {stats.warnings.slice(0,2).join(" ")}
        </div>
      )}
      <div className="settings-grid" style={{marginBottom:18}}>
        {cards.map(([label,value,color])=>(
          <div key={label} className="panel" style={{marginBottom:0}}>
            <div style={{fontSize:11,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{label}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:32,fontWeight:900,color:"#002855",lineHeight:1.05}}>{stats.loading&&String(value)==="Checking"?"Checking":value}</div>
            <div style={{width:38,height:5,borderRadius:8,background:color,marginTop:12}}/>
          </div>
        ))}
      </div>
      <div className="settings-grid">
        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Failed API Calls</div>
          {(failedApi.length?failedApi.slice(0,8):[{label:"None",status:"OK",duration:"-"}]).map((item,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:12,borderBottom:"1px solid #f0f0f0",padding:"7px 0"}}>
              <strong style={{color:"#002855"}}>{item.label||item.url}</strong>
              <span>{item.status} · {item.duration}ms</span>
            </div>
          ))}
        </div>
        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>Slow Pages / Actions</div>
          {(slow.length?slow.slice(0,8):[{label:"None",duration:"-"}]).map((item,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:12,borderBottom:"1px solid #f0f0f0",padding:"7px 0"}}>
              <strong style={{color:"#002855"}}>{item.label||item.url}</strong>
              <span>{item.duration}ms</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminTab({draws,onSaved,toast}){
  const today=new Date().toISOString().slice(0,10);
  const[date,setDate]=useState(today);
  const[drawTime,setDrawTime]=useState("evening");
  const[numStr,setNumStr]=useState("");
  const[p5,setP5]=useState("");
  const[p4,setP4]=useState("");
  const[p3,setP3]=useState("");
  const[p2,setP2]=useState("");
  const[loading,setLoading]=useState(false);
  const[result,setResult]=useState(null);
  const[err,setErr]=useState("");
  const[recalcBusy,setRecalcBusy]=useState("");  // draw_id or "all" while running
  function parse(v){return v.trim().split(/[\s,\-]+/).map(x=>parseInt(x,10)).filter(n=>!isNaN(n));}

  const anyPriceBlank=p5===""||p4===""||p3==="";

  // Live validation — recomputed every render so Save stays disabled until
  // the 5 numbers are in range and unique.
  const ns=parse(numStr);
  const isEmpty=numStr.trim()==="";
  let numberErr="";
  if(!isEmpty){
    if(ns.length!==BALL_COUNT) numberErr="Enter exactly 5 numbers — got "+ns.length;
    else if(ns.some(n=>n<1||n>MAX_NUM)) numberErr="Each number must be between 1 and 36";
    else if(new Set(ns).size!==BALL_COUNT) numberErr="Numbers must be unique";
  }
  const numbersValid=!isEmpty && !numberErr;
  const canSubmit=!loading && numbersValid && !!date;

  async function submit(){
    if(!canSubmit)return;
    setErr("");setResult(null);
    setLoading(true);
    try{
      const{data:sess}=await supabase.auth.getSession();
      const token=sess?.session?.access_token;
      if(!token){setErr("Not signed in");setLoading(false);return;}
      const sorted=[...ns].sort((a,b)=>a-b);
      const body={
        draw_date:date,
        draw_time:drawTime,
        numbers:sorted,
        prize_5match:p5===""?null:parseFloat(p5),
        prize_4match:p4===""?null:parseFloat(p4),
        prize_3match:p3===""?null:parseFloat(p3),
        prize_2match:p2===""?0:parseFloat(p2),
      };
      const r=await monitoredFetch("/api/admin/draws",{
        method:"POST",
        headers:{"content-type":"application/json",authorization:"Bearer "+token},
        body:JSON.stringify(body),
      },"admin-save-draw");
      const d=await r.json();
      if(!r.ok){setErr(d.error||"Request failed");setLoading(false);return;}
      setResult(d);
      // Confirmation toast so you can spot typos without leaving the tab
      if(toast){
        const timeLabel=drawTime==="midday"?"Midday":"Evening";
        const dateShort=new Date(date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"});
        toast("✓ Saved "+timeLabel+" "+dateShort+": "+sorted.join(" · "));
      }
      setNumStr("");setP5("");setP4("");setP3("");setP2("");
      if(onSaved)onSaved();
    }catch(e){setErr(e.message||"Network error");}
    finally{setLoading(false);}
  }

  async function recalc(drawId){
    setRecalcBusy(drawId||"all");
    try{
      const{data:sess}=await supabase.auth.getSession();
      const token=sess?.session?.access_token;
      if(!token){if(toast)toast("Not signed in");return;}
      const body=drawId?{draw_id:drawId}:{};
      const r=await monitoredFetch("/api/admin/recalc",{
        method:"POST",
        headers:{"content-type":"application/json",authorization:"Bearer "+token},
        body:JSON.stringify(body),
      },"admin-recalc");
      const d=await r.json();
      if(!r.ok){if(toast)toast("Recalc failed: "+(d.error||"unknown"));return;}
      if(toast){
        const msg=drawId
          ?"✓ Re-scored 1 draw ("+d.users_scored_rows+" rows)"
          :"✓ Re-scored "+d.draws_scored+" draw"+(d.draws_scored===1?"":"s")+" ("+d.users_scored_rows+" rows)";
        toast(msg);
      }
      if(onSaved)onSaved();
    }catch(e){if(toast)toast("Network error: "+(e.message||e));}
    finally{setRecalcBusy("");}
  }
  return(
    <div>
      <div className="sec-hdr"><span className="sec-title">Admin — Enter Draw</span><span className="sec-badge">Manual Entry</span></div>
      <div className="panel">
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Draw date</div>
            <input type="date" className="add-inp" style={{width:"100%"}} value={date} onChange={e=>setDate(e.target.value)}/>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Draw time</div>
            <div style={{display:"flex",gap:8}}>
              <button type="button" className={"btn "+(drawTime==="midday"?"btn-p":"btn-s")} style={{flex:1}} onClick={()=>setDrawTime("midday")}>Midday</button>
              <button type="button" className={"btn "+(drawTime==="evening"?"btn-p":"btn-s")} style={{flex:1}} onClick={()=>setDrawTime("evening")}>Evening</button>
            </div>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:600,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Winning numbers <span style={{color:"var(--red)"}}>*</span></div>
          <input
            className="add-inp"
            style={{width:"100%",borderColor:numberErr?"var(--red)":undefined}}
            placeholder="Enter 5 numbers 1-36, e.g. 2 5 7 21 30"
            value={numStr}
            onChange={e=>setNumStr(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&submit()}
          />
          {numberErr && <div style={{color:"var(--red)",fontSize:12,marginTop:4,fontWeight:600}}>{numberErr}</div>}
          {numbersValid && <div style={{color:"var(--teal)",fontSize:12,marginTop:4,fontWeight:600}}>✓ Ready: {[...ns].sort((a,b)=>a-b).join(" · ")}</div>}
        </div>
        <div style={{fontSize:12,color:"#555",marginBottom:8}}>Prize amounts — pari-mutuel, posted by Florida Lottery a few hours after each draw. Leave blank and backfill later via the Re-score button below.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <div style={{fontSize:11,color:"#555",marginBottom:4}}>Jackpot (5-match)</div>
            <input className="add-inp" style={{width:"100%"}} type="number" step="0.01" min="0" placeholder="e.g. 100000" value={p5} onChange={e=>setP5(e.target.value)}/>
            <div style={{fontSize:10,color:"#888",marginTop:2}}>0 = rolldown</div>
          </div>
          <div>
            <div style={{fontSize:11,color:"#555",marginBottom:4}}>4-match</div>
            <input className="add-inp" style={{width:"100%"}} type="number" step="0.01" min="0" placeholder="e.g. 107" value={p4} onChange={e=>setP4(e.target.value)}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"#555",marginBottom:4}}>3-match</div>
            <input className="add-inp" style={{width:"100%"}} type="number" step="0.01" min="0" placeholder="e.g. 10" value={p3} onChange={e=>setP3(e.target.value)}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"#555",marginBottom:4}}>2-match</div>
            <input className="add-inp" style={{width:"100%"}} type="number" step="0.01" min="0" placeholder="0 (free tix)" value={p2} onChange={e=>setP2(e.target.value)}/>
            <div style={{fontSize:10,color:"#888",marginTop:2}}>Free Quick Pick — $0</div>
          </div>
        </div>
        {anyPriceBlank&&!isEmpty&&(
          <div style={{background:"#fff7e6",border:"1px solid #ffc107",borderRadius:8,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#664d00"}}>
            ⚠ One or more prize tiers blank. Scoring will use $0 placeholders until you backfill — click <strong>Re-score</strong> below after updating.
          </div>
        )}
        {err&&<div className="auth-err">{err}</div>}
        <button className="btn btn-p" onClick={submit} disabled={!canSubmit} style={{width:"100%",opacity:canSubmit?1:0.55,cursor:canSubmit?"pointer":"not-allowed"}}>
          {loading?"Saving & scoring…":isEmpty?"Enter 5 numbers above":numberErr?"Fix errors above":"Save draw & score all plays"}
        </button>
        {result&&(
          <div style={{marginTop:14,padding:"12px 14px",background:"#f0f8f6",border:"1px solid #c8e6d9",borderRadius:10,fontSize:13,color:"#0a4f3d"}}>
            ✓ Draw saved. Scored <strong>{result.scored?.users_scored??0}</strong> user{result.scored?.users_scored===1?"":"s"} · scanned <strong>{result.scored?.plays_scanned??0}</strong> plays.
          </div>
        )}
      </div>

      {/* Recent draws — quick re-score + edit pathway */}
      <div className="sec-hdr" style={{marginTop:24}}>
        <span className="sec-title">Recent Draws</span>
        <button className="btn btn-sm btn-s" disabled={!!recalcBusy||!draws?.length} onClick={()=>recalc(null)} style={{opacity:recalcBusy?0.6:1}}>
          {recalcBusy==="all"?"Re-scoring all…":"Re-score all draws"}
        </button>
      </div>
      <div className="panel" style={{padding:0,overflow:"hidden"}}>
        {!draws?.length?(
          <div className="empty" style={{padding:"28px 20px"}}>No draws entered yet.</div>
        ):(
          <div style={{overflowX:"auto"}}>
            <table className="hist-tbl">
              <thead><tr><th>Date</th><th>Draw</th><th>Numbers</th><th>Jackpot</th><th>Prizes</th><th></th></tr></thead>
              <tbody>
                {draws.slice(0,14).map(d=>{
                  const anyBlank=d.prize_5match==null||d.prize_4match==null||d.prize_3match==null;
                  return(
                    <tr key={d.id}>
                      <td style={{whiteSpace:"nowrap",fontSize:12}}>{d.date}</td>
                      <td><span style={{fontSize:12,fontWeight:600,color:d.isEvening?"#002855":"#c8102e"}}>{d.isEvening?"Evening":"Midday"}</span></td>
                      <td><div style={{display:"flex",gap:3}}>{(d.numbers||[]).map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div></td>
                      <td style={{fontSize:12}}>{d.jackpot||"Pending"}</td>
                      <td style={{fontSize:11,color:anyBlank?"#c46a00":"#2E7D32",fontWeight:600}}>
                        {anyBlank?"Pending":"Complete"}
                      </td>
                      <td style={{textAlign:"right"}}>
                        <button className="btn btn-sm btn-s" disabled={!!recalcBusy} onClick={()=>recalc(d.id)} style={{opacity:recalcBusy===d.id?0.6:1}}>
                          {recalcBusy===d.id?"Re-scoring…":"Re-score"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{fontSize:11,color:"#888",marginTop:8}}>
        Re-score uses the draw's current prize columns to rebuild user_draw_results.total_won. Run after updating pari-mutuel amounts directly in the DB or after entering a draw that had blank tiers.
      </div>
    </div>
  );
}


function MyGamesTab({myGames,onChange,planTier,trialActive,onLimit}){
  const canUseAll=planAtLeast(planTier,"pro")||trialActive;
  function toggle(id){
    if(!canUseAll){
      if(id==="fantasy5")return;
      if(PICK_GAME_IDS.includes(id)){
        const currentPick=freePickGame(myGames);
        if(currentPick&&currentPick!==id){
          onLimit?.("pick_game");
          return;
        }
        onChange(["fantasy5",id]);
        return;
      }
      return;
    }
    const next=myGames.includes(id)?myGames.filter(x=>x!==id):[...myGames,id];
    onChange(next);
  }
  return(
    <div>
      <div className="sec-hdr"><span className="sec-title">My Games</span><span className="sec-badge">Ready for Pro</span></div>
      <div className="panel" style={{marginBottom:18}}>
        <div style={{fontSize:14,color:"#555",lineHeight:1.45,marginBottom:12}}>
          Free accounts include Fantasy 5 and one Pick game. Pro and higher unlock every launch-ready game.
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <span className="dc-badge" style={{background:"#00285520",color:"#002855"}}>Current: {planTier.toUpperCase()}</span>
          {trialActive&&<span className="dc-badge" style={{background:"#00857720",color:"#008577"}}>7-day Pro trial active</span>}
          {!canUseAll&&<span className="dc-badge" style={{background:"#c8860a20",color:"#8a5a00"}}>Free: Fantasy 5 + 1 Pick game</span>}
        </div>
      </div>
      <GameSupportRoadmap/>
      <TrustedScoringRules/>
      <div className="strat-grid">
        {GAME_CATALOG.map(g=>{
          const checked=myGames.includes(g.id);
          const meta=gameRoadmapMeta(g.id);
          const selectable=g.roadmap==="launch";
          const freeAllowed=g.id==="fantasy5"||PICK_GAME_IDS.includes(g.id);
          const locked=!selectable||(!canUseAll&&!freeAllowed);
          return(
            <div key={g.id} className="strat-card" style={{outline:checked?"3px solid #008577":"none",opacity:locked?0.65:1}}>
              <div className="strat-head" style={{background:checked?"#008577":meta.color}}>
                <div className="strat-name">{g.label}</div>
                <div className="strat-desc">{g.status} · {g.upcoming}</div>
              </div>
              <div className="strat-body">
                <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:14}}>
                  {g.roadmap==="launch"
                    ?"Launch Ready: trusted scoring labels and locked strategy tracking are enabled for this game."
                    :g.roadmap==="soon"
                      ?"Coming Soon: listed on the roadmap, but not active for trusted public scoring yet."
                      :"Research Later: LottoTracker will validate rules and data before active support."}
                </div>
                <button type="button" className={"btn "+(checked?"btn-p":"btn-s")} style={{width:"100%"}} disabled={locked} onClick={()=>toggle(g.id)}>
                  {checked?g.id==="fantasy5"&&!canUseAll?"Included":"Selected":!selectable?g.status:locked?"Paid Plan Required":PICK_GAME_IDS.includes(g.id)&&!canUseAll?"Choose Pick Game":"Add to My Games"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OwnerUsersTab(){
  const[users,setUsers]=useState([]);
  const[loading,setLoading]=useState(true);
  const[err,setErr]=useState("");
  const[busyEmail,setBusyEmail]=useState("");
  const[notice,setNotice]=useState("");
  async function load(){
    setLoading(true);
    setErr("");
    try{
      const{data:{session}}=await supabase.auth.getSession();
      const token=session?.access_token;
      const r=await monitoredFetch("/api/admin/monitor?view=users",{headers:token?{Authorization:"Bearer "+token}:{}}, "admin-users");
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Unable to load users");
      setUsers(Array.isArray(data.users)?data.users:[]);
    }catch(e){
      setErr(e?.message||"Unable to load users");
    }finally{
      setLoading(false);
    }
  }
  useEffect(()=>{load();},[]);
  async function reconcile(email){
    setBusyEmail(email);
    setErr("");
    setNotice("");
    try{
      const{data:{session}}=await supabase.auth.getSession();
      const token=session?.access_token;
      const r=await monitoredFetch("/api/admin/monitor",{
        method:"POST",
        headers:{Authorization:"Bearer "+token,"Content-Type":"application/json"},
        body:JSON.stringify({action:"reconcile-pro",email}),
      }, "admin-reconcile-pro");
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Reconcile failed");
      const product=data.product;
      const warning=product?.looks_fence?" Warning: configured Stripe product looks like Fence Plus.":"";
      setNotice((data.profile?.email||email)+" is now "+(data.profile?.plan_tier||"Pro")+"."+warning);
      load();
    }catch(e){
      setErr(e?.message||"Reconcile failed");
    }finally{
      setBusyEmail("");
    }
  }
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Owner Users</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            View soft-launch users, free/pro status, signup dates, strategy counts, and billing state.
          </p>
        </div>
        <button type="button" className={"btn btn-s "+(loading?"loading":"")} onClick={load} disabled={loading}>Refresh</button>
      </div>
      {err&&<div className="auth-err">{err}</div>}
      {notice&&<div className="launch-note" style={{marginBottom:14}}>{notice}</div>}
      <div className="panel" style={{overflowX:"auto"}}>
        <table className="hist-tbl">
          <thead>
            <tr>
              <th>Email</th>
              <th>Plan</th>
              <th>Subscription</th>
              <th>Billing</th>
              <th>Signup Date</th>
              <th>Strategies</th>
              <th>Saved Plays</th>
              <th>Activity</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading?(
              <tr><td colSpan="9">Loading users...</td></tr>
            ):users.length?users.map(row=>(
              <tr key={row.user_id}>
                <td><strong>{row.email||"No email"}</strong><br/><span style={{fontSize:11,color:"#777"}}>{String(row.user_id||"").slice(0,8)}</span></td>
                <td><span className="dc-badge" style={{background:row.plan_tier==="pro"?"#00857722":"#f0f0f0",color:row.plan_tier==="pro"?"#008577":"#555"}}>{PLAN_COPY[row.plan_tier]?.label||row.plan_tier||"Free"}</span></td>
                <td>{row.subscription_status||"free"}</td>
                <td>{row.billing_status||"free"}{row.cancel_at_period_end?" · canceling":""}</td>
                <td>{row.signup_date?new Date(row.signup_date).toLocaleDateString():"Unknown"}</td>
                <td>{row.strategies_count||0}</td>
                <td>{row.saved_plays_count||0}</td>
                <td>{row.activity_count||0}</td>
                <td><button type="button" className={"btn btn-sm btn-s "+(busyEmail===row.email?"loading":"")} onClick={()=>reconcile(row.email)} disabled={!row.email||!!busyEmail}>{busyEmail===row.email?"Checking":"Reconcile Pro"}</button></td>
              </tr>
            )):(
              <tr><td colSpan="9">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminToolsTab({user,isAdmin}){
  const[busy,setBusy]=useState("");
  const[result,setResult]=useState(null);
  const[err,setErr]=useState("");
  const[importerHealth,setImporterHealth]=useState({loading:true,games:[],error:""});
  const[deleteForm,setDeleteForm]=useState({ownerUserId:"",strategyId:"",confirmName:""});
  const[deletePreview,setDeletePreview]=useState(null);
  const[fingerprintForm,setFingerprintForm]=useState({ownerUserId:"",strategyId:"",confirmName:""});
  const[fingerprintPreview,setFingerprintPreview]=useState(null);
  async function loadImporterHealth(){
    setImporterHealth(current=>({...current,loading:true,error:""}));
    try{
      const{data:{session}}=await supabase.auth.getSession();
      const token=session?.access_token;
      const r=await monitoredFetch("/api/admin/monitor?view=importer-health",{headers:token?{Authorization:"Bearer "+token}:{}}, "importer-health");
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Importer health unavailable");
      setImporterHealth({loading:false,games:data.games||[],error:"",retryPolicy:data.retry_policy||null,generatedAt:data.generated_at||null});
    }catch(e){
      setImporterHealth({loading:false,games:[],error:e?.message||"Importer health unavailable"});
    }
  }
  useEffect(()=>{loadImporterHealth();},[]);
  async function runAction(action,extraBody={}){
    setBusy(action);
    setErr("");
    if(!String(action).startsWith("strategy-delete-"))setResult(null);
    try{
      const{data:{session}}=await supabase.auth.getSession();
      const token=session?.access_token;
      const r=await monitoredFetch("/api/admin/recalc",{
        method:"POST",
        headers:{Authorization:"Bearer "+token,"Content-Type":"application/json"},
        body:JSON.stringify({action,game:"fantasy5",...extraBody}),
      }, "admin-"+action);
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Admin action failed");
      setResult({action,data});
      if(action==="strategy-delete-preview")setDeletePreview(data);
      if(action==="strategy-delete-confirm"){
        setDeletePreview(null);
        setDeleteForm({ownerUserId:"",strategyId:"",confirmName:""});
      }
      if(action==="strategy-fingerprint-preview")setFingerprintPreview(data);
      if(action==="strategy-fingerprint-confirm"){
        setFingerprintPreview(data.after||null);
        setFingerprintForm(f=>({...f,confirmName:""}));
      }
      if(action==="check-official-results-now")loadImporterHealth();
      return data;
    }catch(e){
      setErr(e?.message||"Admin action failed");
      return null;
    }finally{
      setBusy("");
    }
  }
  function previewDelete(){
    setDeletePreview(null);
    runAction("strategy-delete-preview",{
      owner_user_id:deleteForm.ownerUserId.trim(),
      strategy_id:deleteForm.strategyId.trim(),
    });
  }
  function confirmDelete(){
    if(!deletePreview)return;
    runAction("strategy-delete-confirm",{
      owner_user_id:deletePreview.owner_user_id,
      strategy_id:deletePreview.strategy_id,
      confirm_strategy_name:deleteForm.confirmName,
    });
  }
  const deleteReady=deletePreview&&deleteForm.confirmName===deletePreview.strategy_name&&!deletePreview.block_delete;
  function previewFingerprint(){
    setFingerprintPreview(null);
    runAction("strategy-fingerprint-preview",{
      owner_user_id:fingerprintForm.ownerUserId.trim(),
      strategy_id:fingerprintForm.strategyId.trim(),
    });
  }
  function confirmFingerprint(){
    if(!fingerprintPreview)return;
    runAction("strategy-fingerprint-confirm",{
      owner_user_id:fingerprintPreview.owner_user_id,
      strategy_id:fingerprintPreview.strategy_id,
      confirm_strategy_name:fingerprintForm.confirmName,
    });
  }
  const fingerprintReady=fingerprintPreview&&fingerprintForm.confirmName===fingerprintPreview.strategy_name&&fingerprintPreview.needs_recalculation&&fingerprintPreview.ok;
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Admin Tools</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            Run protected maintenance tasks for live strategy performance. Results show counts and errors only.
          </p>
        </div>
      </div>
      <OwnerRuntimeVersion key={user?.id||"signed-out"} userId={user?.id} isAdmin={isAdmin} getSession={()=>supabase.auth.getSession()}/>
      {err&&<div className="auth-err">{err}</div>}
      <div className="panel" style={{display:"grid",gap:12}}>
        <button type="button" className={"btn btn-p "+(busy==="strategy-performance-backfill"?"loading":"")} disabled={!!busy} onClick={()=>runAction("strategy-performance-backfill")}>
          Run Strategy Performance Backfill
        </button>
        <button type="button" className={"btn btn-s "+(busy==="strategy-performance-verify"?"loading":"")} disabled={!!busy} onClick={()=>runAction("strategy-performance-verify")}>
          Verify Strategy Performance
        </button>
        <button type="button" className={"btn btn-s "+(busy==="strategy-integrity-report"?"loading":"")} disabled={!!busy} onClick={()=>runAction("strategy-integrity-report")}>
          Strategy Integrity Report
        </button>
        <button type="button" className={"btn btn-s "+(busy==="lrf-pick-diagnostic"?"loading":"")} disabled={!!busy} onClick={()=>runAction("lrf-pick-diagnostic")}>
          LRF Pick Data Diagnostic
        </button>
        <button type="button" className={"btn btn-p "+(busy==="check-official-results-now"?"loading":"")} disabled={!!busy} onClick={()=>runAction("check-official-results-now",{days:2})}>
          Check Official Results Now
        </button>
      </div>
      <div className="panel" style={{marginTop:16,display:"grid",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <div>
            <div className="sec-title" style={{fontSize:20,marginBottom:6}}>Official Importer Health</div>
            <p style={{fontSize:13,color:"#555",lineHeight:1.5,margin:0}}>
              Server-side official result checks for Fantasy 5 and Pick 2 through Pick 5.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-s" onClick={loadImporterHealth}>Refresh</button>
        </div>
        {importerHealth.error&&<div className="auth-err">{importerHealth.error}</div>}
        {importerHealth.loading?(
          <div style={{fontSize:13,color:"#555",fontWeight:800}}>Checking importer health...</div>
        ):(
          <div style={{display:"grid",gap:8}}>
            {(importerHealth.games||[]).map(row=>{
              const latest=row.latest_confirmed_draw||{};
              const target=row.current_target||{};
              const stale=!!row.stale_warning;
              return(
                <div key={row.game} style={{border:"1px solid "+(stale?"#f0c6cf":"#e8eee9"),borderRadius:10,padding:10,background:stale?"#fff7f8":"#fff",display:"grid",gap:7}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"center"}}>
                    <strong style={{color:"#002855"}}>{gameLabel(row.game)}</strong>
                    <span className="sec-badge" style={{background:stale?"#c8102e":row.current_status==="official"?"#008577":"#005bbb"}}>{stale?"Stale":row.current_status}</span>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:8,fontSize:12,color:"#555",lineHeight:1.35}}>
                    <div><strong style={{color:"#002855"}}>Current target:</strong> {target.draw_date?fmtShortDate(target.draw_date)+" "+drawFilterLabel(target.draw_time):"Unavailable"}</div>
                    <div><strong style={{color:"#002855"}}>Latest confirmed:</strong> {latest.draw_date?fmtShortDate(latest.draw_date)+" "+drawFilterLabel(latest.draw_time)+" - "+officialNumbersText(latest):"None"}</div>
                    <div><strong style={{color:"#002855"}}>Last poll:</strong> {formatOfficialTimestamp(row.last_poll)}</div>
                    <div><strong style={{color:"#002855"}}>Next poll:</strong> {formatOfficialTimestamp(row.next_poll)}</div>
                    <div><strong style={{color:"#002855"}}>Retry count:</strong> {row.retry_count??0}</div>
                    <div><strong style={{color:row.last_error?"#c8102e":"#002855"}}>Last error:</strong> {row.last_error||"None"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="panel" style={{marginTop:16,display:"grid",gap:12}}>
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:6}}>Owner Strategy Maintenance</div>
          <p style={{fontSize:13,color:"#555",lineHeight:1.5,margin:0}}>
            Deletes one exact strategy for one exact owner. Completed draw results are protected and will block deletion.
          </p>
        </div>
        <label style={{display:"grid",gap:6,fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>
          Owner user ID
          <input className="input" value={deleteForm.ownerUserId} onChange={e=>{setDeleteForm(f=>({...f,ownerUserId:e.target.value}));setDeletePreview(null);}} placeholder="Exact Supabase user ID"/>
        </label>
        <label style={{display:"grid",gap:6,fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>
          Strategy ID
          <input className="input" value={deleteForm.strategyId} onChange={e=>{setDeleteForm(f=>({...f,strategyId:e.target.value}));setDeletePreview(null);}} placeholder="Exact strategy ID"/>
        </label>
        <button type="button" className={"btn btn-s "+(busy==="strategy-delete-preview"?"loading":"")} disabled={!!busy||!deleteForm.ownerUserId.trim()||!deleteForm.strategyId.trim()} onClick={previewDelete}>
          Preview Exact Delete
        </button>
        {deletePreview&&(
          <div style={{border:"1px solid #f0c6cf",background:"#fff7f8",borderRadius:10,padding:12,display:"grid",gap:10}}>
            <div className="sec-title" style={{fontSize:18}}>Confirm Strategy Delete</div>
            <div className="settings-grid" style={{gap:8}}>
              {[
                ["Strategy name",deletePreview.strategy_name],
                ["Strategy ID",deletePreview.strategy_id],
                ["Owner",deletePreview.owner_user_id],
                ["Active versions",deletePreview.active_version_count],
                ["Total versions",deletePreview.total_version_count],
                ["Active version plays",deletePreview.active_play_count],
                ["Materialized plays",deletePreview.materialized_play_count],
                ["Scheduled rows",deletePreview.scheduled_row_count],
                ["Future scheduled rows",deletePreview.future_scheduled_row_count],
                ["Completed scheduled rows",deletePreview.completed_scheduled_row_count],
                ["Live result rows",deletePreview.live_result_count],
                ["Referral events",deletePreview.referral_event_count??"Unavailable"],
              ].map(([label,value])=>(
                <div key={label} style={{background:"#fff",border:"1px solid #eee",borderRadius:8,padding:10}}>
                  <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.7}}>{label}</div>
                  <div style={{fontSize:13,color:"#002855",fontWeight:900,wordBreak:"break-word"}}>{String(value)}</div>
                </div>
              ))}
            </div>
            {deletePreview.block_delete&&<div className="auth-err">{deletePreview.block_reason}</div>}
            <label style={{display:"grid",gap:6,fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>
              Type the strategy name exactly
              <input className="input" value={deleteForm.confirmName} onChange={e=>setDeleteForm(f=>({...f,confirmName:e.target.value}))} placeholder={deletePreview.strategy_name}/>
            </label>
            <button type="button" className={"btn "+(deleteReady?"btn-p":"btn-s")+" "+(busy==="strategy-delete-confirm"?"loading":"")} disabled={!!busy||!deleteReady} onClick={confirmDelete}>
              Delete This Exact Strategy
            </button>
          </div>
        )}
      </div>
      <div className="panel" style={{marginTop:16,display:"grid",gap:12}}>
        <div>
          <div className="sec-title" style={{fontSize:20,marginBottom:6}}>Legacy Fingerprint Maintenance</div>
          <p style={{fontSize:13,color:"#555",lineHeight:1.5,margin:0}}>
            Recalculates one active strategy fingerprint only after counts, cost, Pick rules, and materialized plays verify cleanly.
          </p>
        </div>
        <label style={{display:"grid",gap:6,fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>
          Owner user ID
          <input className="input" value={fingerprintForm.ownerUserId} onChange={e=>{setFingerprintForm(f=>({...f,ownerUserId:e.target.value}));setFingerprintPreview(null);}} placeholder="Exact Supabase user ID"/>
        </label>
        <label style={{display:"grid",gap:6,fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>
          Strategy ID
          <input className="input" value={fingerprintForm.strategyId} onChange={e=>{setFingerprintForm(f=>({...f,strategyId:e.target.value}));setFingerprintPreview(null);}} placeholder="Exact strategy ID"/>
        </label>
        <button type="button" className={"btn btn-s "+(busy==="strategy-fingerprint-preview"?"loading":"")} disabled={!!busy||!fingerprintForm.ownerUserId.trim()||!fingerprintForm.strategyId.trim()} onClick={previewFingerprint}>
          Preview Fingerprint Recalculation
        </button>
        {fingerprintPreview&&(
          <div style={{border:"1px solid #d9e8ff",background:"#f7fbff",borderRadius:10,padding:12,display:"grid",gap:10}}>
            <div className="sec-title" style={{fontSize:18}}>Confirm Fingerprint Recalculation</div>
            <div className="settings-grid" style={{gap:8}}>
              {[
                ["Strategy name",fingerprintPreview.strategy_name],
                ["Strategy ID",fingerprintPreview.strategy_id],
                ["Owner",fingerprintPreview.owner_user_id],
                ["Game",fingerprintPreview.game],
                ["Version",fingerprintPreview.version_number],
                ["Snapshot count",fingerprintPreview.snapshot_count],
                ["Unique count",fingerprintPreview.unique_count],
                ["Materialized count",fingerprintPreview.materialized_count],
                ["Estimated cost",fingerprintPreview.estimated_cost],
                ["Calculated cost",fingerprintPreview.calculated_cost],
                ["Has Pick rules",fingerprintPreview.has_pick_rules?"Yes":"No"],
                ["Needs recalculation",fingerprintPreview.needs_recalculation?"Yes":"No"],
                ["Old format likely",fingerprintPreview.old_format_likely?"Yes":"No"],
              ].map(([label,value])=>(
                <div key={label} style={{background:"#fff",border:"1px solid #eee",borderRadius:8,padding:10}}>
                  <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.7}}>{label}</div>
                  <div style={{fontSize:13,color:"#002855",fontWeight:900,wordBreak:"break-word"}}>{String(value)}</div>
                </div>
              ))}
            </div>
            {!!fingerprintPreview.problems?.length&&<div className="auth-err">{fingerprintPreview.problems.join("; ")}</div>}
            <label style={{display:"grid",gap:6,fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>
              Type the strategy name exactly
              <input className="input" value={fingerprintForm.confirmName} onChange={e=>setFingerprintForm(f=>({...f,confirmName:e.target.value}))} placeholder={fingerprintPreview.strategy_name}/>
            </label>
            <button type="button" className={"btn "+(fingerprintReady?"btn-p":"btn-s")+" "+(busy==="strategy-fingerprint-confirm"?"loading":"")} disabled={!!busy||!fingerprintReady} onClick={confirmFingerprint}>
              Recalculate This Fingerprint
            </button>
          </div>
        )}
      </div>
      {result&&(
        <div className="panel" style={{marginTop:16}}>
          <div className="sec-title" style={{fontSize:20,marginBottom:10}}>{result.action}</div>
          <pre style={{whiteSpace:"pre-wrap",wordBreak:"break-word",background:"#f8f8f8",border:"1px solid #eee",borderRadius:10,padding:12,fontSize:12,maxHeight:420,overflow:"auto"}}>
            {JSON.stringify(result.data,null,2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function UpgradePrompt({onUpgrade,onLater,billingBusy,myGames,targetPlan="pro"}){
  const tracked=normalizeGames(myGames).map(gameLabel).join(", ");
  const plan=normalizePlanTier(targetPlan);
  const planName=PLAN_COPY[plan]?.label||"Pro";
  const comingSoon=COMING_SOON_PLAN_IDS.has(plan);
  const benefits=[
    plan==="pool_organizer"?"Create pools and manage members":plan==="premium"?"Join Community and follow players":"Unlock Fantasy 5 and every Florida Pick game",
    plan==="pool_organizer"?"Track everyone's plays":"Save unlimited strategies",
    plan==="premium"?"Save public strategies":"Build as many strategies as you need",
    plan==="pool_organizer"?"Use the pool dashboard":"Keep the launch experience simple and focused",
    "Keep LottoTracker simulation-only with no tickets or money handled",
  ];
  return(
    <div className="panel" style={{padding:"32px 28px",maxWidth:760,margin:"0 auto 20px"}}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:40,fontWeight:900,color:"#002855",textTransform:"uppercase",lineHeight:1,marginBottom:10}}>
        Unlock {planName}
      </div>
      <div style={{fontSize:15,color:"#555",lineHeight:1.55,marginBottom:16}}>
        You're currently tracking {tracked}.
      </div>
      <div style={{fontSize:14,color:"#555",fontWeight:700,marginBottom:8}}>Upgrade to {planName} to:</div>
      <div style={{display:"grid",gap:8,marginBottom:20}}>
        {benefits.map(item=>(
          <div key={item} style={{display:"flex",gap:9,alignItems:"flex-start",fontSize:14,color:"#333",lineHeight:1.35}}>
            <span style={{color:"#008577",fontWeight:900}}>✓</span>
            <span>{item}</span>
          </div>
        ))}
      </div>
      <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px",fontSize:12,color:"#5a4a00",fontWeight:600,lineHeight:1.45,marginBottom:18}}>
        Lotto Tracker provides number-selection information and result tracking. No guaranteed wins. No tickets sold. No money collected. No pool funds handled. No winnings distributed.
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {comingSoon?(
          <>
            <button type="button" className="btn btn-s" disabled>{planName} Coming Soon</button>
            <button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} onClick={()=>onUpgrade?.("pro")} disabled={billingBusy}>
              {billingBusy?"Starting...":"Start Pro"}
            </button>
          </>
        ):(
          <button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} onClick={()=>onUpgrade?.("pro")} disabled={billingBusy}>
            {billingBusy?"Starting...":"Start Pro"}
          </button>
        )}
        <button type="button" className="btn btn-s" onClick={onLater}>Maybe later</button>
      </div>
    </div>
  );
}

function FreeLimitUpgradeDialog({open,reason,onUpgrade,onClose,selectedPickGame=null}){
  if(!open)return null;
  const pickLabel=selectedPickGame?gameLabel(selectedPickGame):"your Pick game";
  const copy=reason==="pick_game"
    ?{
      title:"Unlock every Pick game",
      text:selectedPickGame
        ?"Free includes Fantasy 5 plus one Pick game. You already selected "+pickLabel+". Pro unlocks the remaining Pick games."
        :"Free includes Fantasy 5 plus one Pick game. Pro unlocks Pick 2, Pick 3, Pick 4, and Pick 5 together.",
      bullets:["All Florida Pick games","Unlimited saved strategies","Unlimited Watch Results and What-If","Advanced analytics as they launch"],
    }
    :{
      title:"Save unlimited strategies",
      text:"You've reached the Free plan limit of "+FREE_MAX_STRATEGIES+" saved strategies. Start your 7-day Pro trial to unlock unlimited strategies.",
      bullets:["Unlimited saved strategies","All Florida Pick games","Unlimited Watch Results and What-If","Advanced analytics as they launch"],
    };
  return createPortal(
    <div role="dialog" aria-modal="true" style={{position:"fixed",inset:0,zIndex:1200,background:"rgba(0,20,45,.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
      <div className="panel" style={{width:"min(560px,100%)",margin:0,border:"2px solid #ffd54f",boxShadow:"0 28px 80px rgba(0,0,0,.35)"}}>
        <div className="sec-hdr" style={{marginBottom:10}}>
          <span className="sec-title" style={{fontSize:26}}>Unlock Pro</span>
          <button type="button" className="btn btn-sm btn-s" onClick={onClose}>Close</button>
        </div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:"#002855",textTransform:"uppercase",lineHeight:1,marginBottom:8}}>
          {copy.title}
        </div>
        <div style={{fontSize:15,color:"#555",lineHeight:1.5,marginBottom:14}}>{copy.text}</div>
        {reason==="pick_game"&&selectedPickGame&&(
          <div style={{background:"#eef8f7",border:"1px solid #b9dfda",borderRadius:10,padding:"10px 12px",fontSize:13,color:"#00594f",fontWeight:800,lineHeight:1.4,marginBottom:14}}>
            Your current Free Pick game: {pickLabel}
          </div>
        )}
        <div style={{display:"grid",gap:8,marginBottom:16}}>
          {copy.bullets.map(item=>(
            <div key={item} style={{display:"flex",gap:9,fontSize:14,color:"#333",fontWeight:700}}>
              <span style={{color:"#008577",fontWeight:900}}>✓</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
        <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px",fontSize:12,color:"#5a4a00",fontWeight:700,lineHeight:1.45,marginBottom:16}}>
          Start with the 7-day Pro trial. LottoTracker remains simulation-only: no tickets sold, no money handled, no winnings distributed.
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button type="button" className="btn btn-p" onClick={onUpgrade}>Start 7-Day Free Trial</button>
          <button type="button" className="btn btn-s" onClick={onClose}>{reason==="strategies"?"Keep Editing":"Keep Using Current Pick Game"}</button>
          {reason==="strategies"&&<button type="button" className="btn btn-s" onClick={onClose}>Cancel</button>}
        </div>
      </div>
    </div>,
    document.body
  );
}

function RequirePro({children,subscription,onUpgrade,onLater,billingBusy,myGames,minPlan="pro"}){
  if(subscriptionHas(subscription,minPlan))return children;
  return <UpgradePrompt onUpgrade={onUpgrade} onLater={onLater} billingBusy={billingBusy} myGames={myGames} targetPlan={minPlan}/>;
}

function referralOrigin(){
  return typeof window!=="undefined"?window.location.origin:"https://www.lottotracker.app";
}
function cleanReferralSlugDraft(value){
  return String(value||"").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"").slice(0,40);
}
function referralLinkFromSlug(slug){
  const clean=cleanReferralSlugDraft(slug);
  return clean?referralOrigin()+"/r/"+encodeURIComponent(clean):referralOrigin()+"/r";
}
function referralSlugFromPath(pathname){
  const match=String(pathname||"").match(/^\/r\/([^/?#]+)/i);
  return match?decodeURIComponent(match[1]||"").trim():"";
}
async function authenticatedJson(path,options={}){
  const{data:{session}}=await supabase.auth.getSession();
  if(options.signal?.aborted)throw Object.assign(new Error("Request canceled"),{name:"AbortError"});
  const token=session?.access_token;
  if(!token){require("./sessionReadPolicy").reportExpiredSession();throw Object.assign(new Error("Please sign in again."),{status:401});}
  const headers={...(options.headers||{}),"Content-Type":"application/json"};
  if(token)headers.Authorization="Bearer "+token;
  const r=await monitoredFetch(path,{...options,headers},options.label||"authenticated-json");
  const data=await r.json().catch(()=>({}));
  if(!r.ok){if(r.status===401){const current=await supabase.auth.getSession();if(!current.data?.session||current.data.session.access_token===token)require("./sessionReadPolicy").reportExpiredSession();}const error=new Error(data.error||"Request failed");error.status=r.status;throw error;}
  return data;
}
function useReferralProfile(user,subscription,onUpdated){
  const initial=subscription?.referral_slug||"";
  const[slug,setSlug]=useState(initial);
  const[draft,setDraft]=useState(initial);
  const[loading,setLoading]=useState(false);
  const[saving,setSaving]=useState(false);
  const[msg,setMsg]=useState("");
  const[err,setErr]=useState("");
  useEffect(()=>{
    const next=subscription?.referral_slug||"";
    if(next&&next!==slug){setSlug(next);setDraft(next);}
  },[subscription?.referral_slug]);
  useEffect(()=>{
    if(!user?.id)return;
    let cancelled=false;
    setLoading(true);
    authenticatedJson("/api/public?action=referral-profile",{label:"referral-profile"})
      .then(data=>{
        if(cancelled)return;
        const next=data.referralSlug||"";
        setSlug(next);
        setDraft(next);
        onUpdated?.(data);
      })
      .catch(e=>{if(!cancelled)setErr(e.message||"Could not load referral link.");})
      .finally(()=>{if(!cancelled)setLoading(false);});
    return()=>{cancelled=true;};
  },[user?.id]);
  async function save(){
    const clean=cleanReferralSlugDraft(draft);
    setMsg("");setErr("");
    if(clean.length<3){setErr("Choose at least 3 letters or numbers.");return false;}
    setSaving(true);
    try{
      const data=await authenticatedJson("/api/public?action=update-referral-slug",{
        method:"POST",
        body:JSON.stringify({slug:clean}),
        label:"update-referral-slug",
      });
      const next=data.referralSlug||clean;
      setSlug(next);
      setDraft(next);
      onUpdated?.(data);
      setMsg("Referral slug updated.");
      return true;
    }catch(e){
      setErr(e.message||"Could not update referral slug.");
      return false;
    }finally{
      setSaving(false);
    }
  }
  return{slug,draft,setDraft,link:referralLinkFromSlug(slug||draft),loading,saving,msg,err,setMsg,setErr,save};
}

function ReferralTab({user,subscription,onReferralUpdated}){
  const storageKey="lt:referrals:"+(user?.id||user?.email||"local");
  const[stats,setStats]=useState(()=>JSON.parse(localStorage.getItem(storageKey)||'{"copies":0,"shares":0}'));
  const[copied,setCopied]=useState(false);
  const[rateMsg,setRateMsg]=useState("");
  const referral=useReferralProfile(user,subscription,onReferralUpdated);
  const referralLink=referral.link;
  function save(next){
    setStats(next);
    localStorage.setItem(storageKey,JSON.stringify(next));
  }
  function copyLink(){
    if(!referral.slug){setRateMsg("Your referral link is still loading. Try again in a moment.");return;}
    const limit=rateLimitAction("referral-copy:"+(user?.id||user?.email||"local"),20,60*60*1000);
    if(!limit.allowed){setRateMsg("Please wait before copying another referral link. Try again in "+limit.retryText+".");return;}
    setRateMsg("");
    navigator.clipboard?.writeText(referralLink);
    save({...stats,copies:(stats.copies||0)+1,lastAction:"Copied",lastAt:new Date().toISOString()});
    setCopied(true);
    setTimeout(()=>setCopied(false),1600);
  }
  function shareLink(){
    if(!referral.slug){setRateMsg("Your referral link is still loading. Try again in a moment.");return;}
    const limit=rateLimitAction("referral-share:"+(user?.id||user?.email||"local"),12,60*60*1000);
    if(!limit.allowed){setRateMsg("Please wait before sharing again. Try again in "+limit.retryText+".");return;}
    setRateMsg("");
    save({...stats,shares:(stats.shares||0)+1,lastAction:"Shared",lastAt:new Date().toISOString()});
    if(navigator.share)navigator.share({title:"LottoTracker",text:"Check out Today’s Picks on Lotto Tracker. Create an account and try the free number preview.",url:referralLink}).catch(()=>{});
    else copyLink();
  }
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Invite Friends</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:720}}>
            Invite friends to discover Today’s Picks and follow Lotto Tracker’s strategies.
          </p>
        </div>
        <span className="sec-badge" style={{background:"#008577"}}>{APP_VERSION_LABEL}</span>
      </div>
      <div className="launch-note" style={{marginBottom:18}}>{TRUST_COPY}</div>
      <div className="panel" style={{textAlign:"center",padding:"42px 22px"}}>
        <div style={{fontSize:52,marginBottom:12}}>{"\uD83D\uDC65"}</div>
        <div className="sec-title" style={{fontSize:34,marginBottom:8}}>Invite a friend.</div>
        <div style={{fontSize:14,color:"#555",lineHeight:1.5,maxWidth:620,margin:"0 auto 18px"}}>
          Send a friend your link to create an account and try the free number preview. No referral rewards or earnings are offered.
        </div>
        <div style={{display:"grid",gap:8,maxWidth:720,margin:"0 auto 16px"}}>
          <div style={{fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>Your link name</div>
          <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
            <input value={referral.draft} onChange={e=>referral.setDraft(cleanReferralSlugDraft(e.target.value))} placeholder="your-name" style={{fontSize:14,color:"#002855",fontWeight:800,background:"#fff",border:"1px solid #ddd",borderRadius:10,padding:"10px 12px",minWidth:220,textAlign:"center"}}/>
            <button type="button" className={"btn btn-s "+(referral.saving?"loading":"")} onClick={referral.save} disabled={referral.saving||referral.loading}>{referral.saving?"Saving":"Save Link Name"}</button>
          </div>
          <div style={{fontSize:12,color:"#555",fontWeight:900,textTransform:"uppercase",letterSpacing:.8}}>Referral link</div>
          <div style={{fontSize:13,color:"#002855",fontWeight:800,wordBreak:"break-all",background:"#f8f8f8",borderRadius:10,padding:12}}>{referralLink}</div>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          <button type="button" className="btn btn-p" onClick={copyLink}>{copied?"Copied":"Copy Link"}</button>
          <button type="button" className="btn btn-s" onClick={shareLink}>Share Link</button>
        </div>
        {referral.msg&&<div style={{margin:"14px auto 0",maxWidth:520,fontSize:12,color:"#008577",fontWeight:800}}>{referral.msg}</div>}
        {referral.err&&<div className="auth-err" style={{margin:"14px auto 0",maxWidth:520}}>{referral.err}</div>}
        {rateMsg&&<div className="auth-err" style={{margin:"14px auto 0",maxWidth:520}}>{rateMsg}</div>}
      </div>
      <div className="settings-grid">
        <div className="panel"><div className="sec-title" style={{fontSize:20}}>Copy Link</div><div style={{fontSize:34,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,color:"#002855"}}>{stats.copies||0}</div></div>
        <div className="panel"><div className="sec-title" style={{fontSize:20}}>Share Link</div><div style={{fontSize:34,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,color:"#002855"}}>{stats.shares||0}</div></div>
        <div className="panel"><div className="sec-title" style={{fontSize:20}}>Invite Activity</div><div style={{fontSize:13,color:"#555",lineHeight:1.5}}>Copies and shares are counted on this device so you can see what you have sent.</div></div>
      </div>
    </div>
  );
}

function TodayLineupTab({plays,draws,strategyTrust,drawStatus,lineup,onAdd,onRemove,onMove,onPlayType,onLockStrategy}){
  const navigate=useNavigate();
  const clean=normalizeLineup(lineup);
  const idsInLineup=new Set(clean.items.map(item=>item.id));
  const strategyIds=[...new Set([...Object.keys(strategyTrust?.records||{}),...strategyIdsFromPlays(plays)])].filter(id=>id&&id!=="manual");
  const scoredDraws=(draws||[]).filter(d=>Array.isArray(d.numbers)&&d.numbers.length===BALL_COUNT);
  const rows=scoredDraws.length?aggregateStrategies(plays,scoredDraws):[];
  const rowById=Object.fromEntries(rows.map(row=>[row.id,row]));
  const summary=lineupSummary(clean,plays,drawStatus);
  const lockedForDraw=["Sales Closed","Drawing In Progress"].includes(drawStatus?.next?.status);
  const available=strategyIds.filter(id=>!idsInLineup.has(id));
  const lineupSharePayload=shareCardPayload({
    type:"lineup",
    title:"Watch My Draw on LottoTracker",
    strategyName:"Today's Lineup",
    game:drawStatus?.next?.gameLabel||"Fantasy 5",
    status:sharePoolStatus(drawStatus?.next,lockedForDraw),
    roi:summary.count+" strategies",
    drawsTracked:String(scoredDraws.length),
    createdDate:fmtShortDate(new Date().toISOString()),
    link:shortShareLink("lineup","today",clean.items.length>0),
    isPublic:clean.items.length>0,
    accent:lockedForDraw?"#002855":"#008577",
    note:"Today's lineup share card. No ticket sales or money handled.",
    watchDraw:true,
    joinDraw:true,
    drawTime:formatShareDrawTime(drawStatus?.next),
    playCount:String(summary.ticketCost),
    playTypeLabel:summary.real>0?"$1 play · self-reported real play":"$1 play · simulation",
    estimatedCost:"$"+summary.ticketCost+" estimated",
    possibleWin:sharePossibleWinLabel(drawStatus?.next?.game||drawStatus?.next?.gameLabel||"fantasy5"),
    poolMode:summary.real>0?"Group Play":"Group Simulation",
    openSpots:"5 spots available",
    suggestedBuyIn:"$20 each",
    possiblePayout:sharePossibleWinLabel(drawStatus?.next?.game||drawStatus?.next?.gameLabel||"fantasy5"),
    poolStatus:sharePoolStatus(drawStatus?.next,lockedForDraw),
    fireballNote:shareFireballNote(drawStatus?.next?.game||drawStatus?.next?.gameLabel||"fantasy5",false),
    ctaLabel:"Watch this draw on LottoTracker",
    joinCtaLabel:"Request to Join",
    organizerDisclaimer:"Organizer handles tickets and payments outside LottoTracker.",
  });
  function lifecycleMeta(id,inLineup=false){
    const status=strategyTrust?.records?.[id]?.status;
    if(status==="Locked")return{label:"Locked",color:"#008577"};
    if(status==="Archived"||status==="Replaced")return{label:"Retired",color:"#5d6675"};
    if(inLineup)return{label:"Active",color:"#2E6DA4"};
    return{label:"Draft",color:"#c8860a"};
  }
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Today's Lineup</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            Choose which saved strategies count toward public ROI, leaderboards, profile, community, and achievements for the next draw.
          </p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
          <span className="sec-badge" style={{background:lockedForDraw?"#002855":"#008577"}}>{lockedForDraw?"Locked":"Editable"}</span>
          <ShareCardButton payload={lineupSharePayload} label="Share Lineup" className="btn btn-sm btn-s"/>
        </div>
      </div>
      <div className="launch-note" style={{marginBottom:18}}>{TRUST_COPY} Library-only strategies continue tracking privately.</div>
      <div style={{fontSize:12,color:"#555",lineHeight:1.45,margin:"-8px 0 18px"}}>
        Real Play is a self-reported label only. LottoTracker does not sell tickets, collect money, manage funds, or distribute winnings.
      </div>

      <div className="sc-grid" style={{marginBottom:18}}>
        <div className="sc-card"><div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>Active strategies</div><div className="sc-roi" style={{color:"#002855"}}>{summary.count}</div></div>
        <div className="sc-card"><div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>Estimated ticket cost</div><div className="sc-roi" style={{color:"#008577"}}>${summary.ticketCost}</div></div>
        <div className="sc-card"><div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>Simulation / Real Play</div><div className="sc-roi" style={{color:"#c8860a"}}>{summary.simulation}/{summary.real}</div></div>
        <div className="sc-card"><div style={{fontSize:11,color:"#555",fontWeight:800,textTransform:"uppercase"}}>Time until lock</div><div className="sc-roi" style={{color:"#c8102e",fontSize:30}}>{summary.lockText}</div></div>
      </div>

      {!clean.items.length&&(
        <EmptyState
          icon="*"
          title="No strategies in Today's Lineup."
          text="Add saved strategies from your library. Only lineup strategies count toward public stats; library-only strategies stay private."
          actions={[{label:"Create Strategy",onClick:()=>navigate("/strategy-builder")}]}
        />
      )}

      <div style={{display:"grid",gridTemplateColumns:"minmax(0,1.2fr) minmax(280px,.8fr)",gap:18,alignItems:"start"}}>
        <div className="panel">
          <div className="sec-hdr" style={{marginBottom:12}}>
            <span className="sec-title" style={{fontSize:22}}>Lineup Order</span>
            <span className="sec-badge">{clean.items.length} selected</span>
          </div>
          <div style={{display:"grid",gap:10}}>
            {clean.items.map((item,index)=>{
              const record=strategyTrust?.records?.[item.id];
              const status=lifecycleMeta(item.id,true);
              const row=rowById[item.id];
              const cost=lineupCostForStrategy(plays,item.id);
              const strategyPlays=(plays||[]).filter(p=>strategyIdForPlay(p)===item.id&&Array.isArray(p.numbers));
              const shareRules=shareLineRulesDigest(record);
              const isSavedAfter=!!(drawStatus?.next&&strategyPlays.some(p=>playSavedAfterDraw(p,drawStatus.next)));
              const cardStatus=isSavedAfter?"Saved After Draw":sharePoolStatus(drawStatus?.next,lockedForDraw);
              const roiDisplay=isSavedAfter?"What-if only":row?.roi==null?"Pending":(row.roi>=0?"+":"")+row.roi.toFixed(0)+"%";
              const strategySharePayload=shareCardPayload({
                type:"strategy",
                title:isSavedAfter?"Watch Strategy on LottoTracker":"Watch My Draw on LottoTracker",
                strategyName:strategyDisplayName(item.id,record),
                game:gameLabel(record?.game||"fantasy5"),
                status:isSavedAfter?"What-if only":"Watching tonight's draw",
                roi:roiDisplay,
                drawsTracked:String(isSavedAfter?0:scoredDraws.length),
                createdDate:fmtShortDate(record?.createdAt),
                link:shortShareLink("strategy",item.id,record?.published||record?.status==="Locked"),
                isPublic:!!record?.published||record?.status==="Locked",
                accent:item.playType==="Real Play"?"#c8860a":"#2E6DA4",
                note:isSavedAfter?"Saved after this draw. This is What-if only and does not count toward ROI, wins, or rankings.":"Part of Today's Lineup. Simulation and tracking only.",
                watchDraw:true,
                joinDraw:false,
                isWhatIfOnly:isSavedAfter,
                drawTime:formatShareDrawTime(drawStatus?.next),
                playCount:String(shareRules?.representedPlayCount||cost),
                playType:item.playType,
                playTypeLabel:shareRules?.playTypeLabel||sharePlayTypeLabel(record?.game||"fantasy5",item.playType),
                rulesSummaryLine:shareRules?.summaryLine||"",
                rowSuffixes:shareRules?.rowSuffixes||[],
                rowFireballs:shareRules?.rowFireballs||[],
                combinationsLabel:shareRules?.combinationsLabel||"",
                estimatedCost:"$"+cost+" estimated",
                possibleWin:sharePossibleWinLabel(record?.game||"fantasy5"),
                poolMode:item.playType==="Real Play"?"Group Play":"Group Simulation",
                openSpots:"5 spots available",
                suggestedBuyIn:"$20 each",
                possiblePayout:sharePossibleWinLabel(record?.game||"fantasy5"),
                poolStatus:sharePoolStatus(drawStatus?.next,lockedForDraw),
                fireballNote:shareRules?.fireballNote||shareFireballNote(record?.game||"fantasy5",false),
                ctaLabel:isSavedAfter?"Watch Strategy":"Watch this draw on LottoTracker",
                joinCtaLabel:"Request to Join",
                organizerDisclaimer:"Organizer handles tickets and payments outside LottoTracker.",
              });
              return(
                <div key={item.id} style={{border:"1px solid #eee",borderRadius:12,padding:12,background:"#fff"}}>
                  <div style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto",gap:10,alignItems:"start"}}>
                    <div style={{fontWeight:900,color:"#777"}}>#{index+1}</div>
                    <div>
                      <div style={{fontWeight:900,color:"#002855",fontSize:16}}>{strategyDisplayName(item.id,record)}</div>
                      <div style={{display:"flex",gap:7,flexWrap:"wrap",marginTop:7}}>
                        <span className="sec-badge" style={{background:status.color}}>{status.label}</span>
                        <span className="sec-badge" style={{background:item.playType==="Real Play"?"#c8860a":"#2E6DA4"}}>{item.playType}</span>
                        <span className="sec-badge" style={{background:"#5d6675"}}>${cost} estimated</span>
                        {isSavedAfter
                          ?<span className="sec-badge" style={{background:"#c8860a"}}>What-if only</span>
                          :row?.roi!=null&&<span className="sec-badge" style={{background:row.roi>=0?"#008577":"#c8102e"}}>{(row.roi>=0?"+":"")+row.roi.toFixed(0)}% ROI</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>onMove(item.id,-1)} disabled={lockedForDraw||index===0}>Up</button>
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>onMove(item.id,1)} disabled={lockedForDraw||index===clean.items.length-1}>Down</button>
                      <ShareCardButton payload={strategySharePayload} label="Share" className="btn btn-sm btn-s"/>
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>onRemove(item.id)} disabled={lockedForDraw}>Remove</button>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginTop:10}}>
                    <label style={{fontSize:12,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
                      Play Type{" "}
                      <select value={item.playType} onChange={e=>onPlayType(item.id,e.target.value)} disabled={lockedForDraw} style={{border:"1px solid #ddd",borderRadius:8,padding:"6px 8px",marginLeft:6}}>
                        <option>Simulation</option>
                        <option>Real Play</option>
                      </select>
                    </label>
                    {drawIsBeforeClose(drawStatus)&&record?.status!=="Locked"&&(
                      <button type="button" className="btn btn-sm btn-p" onClick={()=>onLockStrategy(item.id,"tonight")}>Lock Strategy</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel">
          <div className="sec-title" style={{fontSize:22,marginBottom:12}}>Add From Library</div>
          {available.length?(
            <div style={{display:"grid",gap:9}}>
              {available.map(id=>{
                const record=strategyTrust?.records?.[id];
                const status=lifecycleMeta(id,false);
                return(
                  <div key={id} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:10,alignItems:"center",borderBottom:"1px solid #f0f0f0",paddingBottom:9}}>
                    <div>
                      <strong style={{color:"#002855"}}>{strategyDisplayName(id,record)}</strong>
                      <div style={{fontSize:11,color:status.color,fontWeight:800,textTransform:"uppercase"}}>{status.label}</div>
                    </div>
                    <button type="button" className="btn btn-sm btn-p" onClick={()=>onAdd(id)} disabled={lockedForDraw}>Add</button>
                  </div>
                );
              })}
            </div>
          ):(
            <EmptyState icon="*" title="No saved playslips available." text="Create another saved playslip to add more lineup options." actions={[{label:"Play Builder",onClick:()=>navigate("/strategy-builder")}]}/>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsTab({user,subscription,myGames,featureUnlocks,onRefresh,billingBusy,onSignOut,onFeedback,onReferralUpdated}){
  const navigate=useNavigate();
  const isPro=subscriptionHas(subscription,"pro");
  const planLabel=PLAN_COPY[subscriptionTier(subscription)]?.label||"Free";
  const selectedGames=normalizeGames(myGames).map(gameLabel);
  const[referralCopied,setReferralCopied]=useState(false);
  const[settingsMsg,setSettingsMsg]=useState("");
  const referral=useReferralProfile(user,subscription,onReferralUpdated);
  const referralLink=referral.link;
  const settingCards=[
    {
      title:"Account",
      text:"Review your public profile and account identity.",
      meta:user?.email||"Signed in",
      action:"View Profile",
      onClick:()=>navigate("/profile"),
    },
    {
      title:"Plan & Billing",
      text:"Check subscription status, trial access, billing, cancel, or restore.",
      meta:planLabel+" plan",
      action:isPro?"Manage Billing":"Start Pro",
      onClick:()=>navigate("/pro"),
    },
    {
      title:"My Games",
      text:"Control which games appear on your dashboard and daily schedule.",
      meta:selectedGames.join(", "),
      action:"Manage Games",
      onClick:()=>navigate("/my-games"),
    },
    {
      title:"Notifications",
      text:"Activity Center is ready for results, milestones, and reminders. Push notifications are not connected yet.",
      meta:"In-app only",
      action:"Open Activity",
      onClick:()=>navigate("/activity"),
    },
    {
      title:"Referrals",
      text:"Invite friends and share your LottoTracker link.",
      meta:"Referral link ready",
      action:"Invite Friends",
      onClick:()=>navigate("/referrals"),
    },
  ];
  const unlockRows=[
    ["Scoreboard",!!featureUnlocks?.scoreboard],
    ["Pool Simulation",!!featureUnlocks?.poolSimulation],
    ["Hot / Cold",!!featureUnlocks?.hotCold],
    ["History",!!featureUnlocks?.history],
    ["Pool Manager",!!featureUnlocks?.poolManager],
  ];
  function copyReferral(){
    if(!referral.slug){setSettingsMsg("Your referral link is still loading. Try again in a moment.");return;}
    const limit=rateLimitAction("settings-referral-copy:"+(user?.id||user?.email||"local"),20,60*60*1000);
    if(!limit.allowed){setSettingsMsg("Please wait before copying another referral link. Try again in "+limit.retryText+".");return;}
    setSettingsMsg("");
    navigator.clipboard?.writeText(referralLink);
    setReferralCopied(true);
    setTimeout(()=>setReferralCopied(false),1600);
  }
  function shareReferral(){
    if(!referral.slug){setSettingsMsg("Your referral link is still loading. Try again in a moment.");return;}
    const limit=rateLimitAction("settings-referral-share:"+(user?.id||user?.email||"local"),12,60*60*1000);
    if(!limit.allowed){setSettingsMsg("Please wait before sharing again. Try again in "+limit.retryText+".");return;}
    setSettingsMsg("");
    if(navigator.share){
      navigator.share({title:"LottoTracker",text:"Try LottoTracker with me.",url:referralLink}).catch(()=>{});
    }else copyReferral();
  }
  function copyAppVersion(){
    navigator.clipboard?.writeText(APP_VERSION_LABEL+" | Build "+APP_BUILD_VERSION+" | Database "+DATABASE_VERSION+" | "+(document.lastModified||"Build date unavailable"));
  }
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Settings</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            Manage your account, plan, selected games, and launch preferences from one place.
          </p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
          <span className="sec-badge" style={{background:"#008577"}}>{APP_VERSION_LABEL}</span>
          <span className="sec-badge">{planLabel}</span>
          <button type="button" className="btn btn-sm btn-p" onClick={()=>navigate("/pro")}>{isPro?"Manage Plan":"Start Pro"}</button>
        </div>
      </div>

      <div className="launch-note" style={{marginBottom:18}}>
        {TRUST_COPY} No pool funds handled.
      </div>

      <div className="settings-grid" style={{marginBottom:20}}>
        {settingCards.map(card=>(
          <div key={card.title} className="panel" style={{marginBottom:0}}>
            <div className="sec-title" style={{fontSize:20,marginBottom:8}}>{card.title}</div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:10}}>{card.text}</div>
            <div style={{fontSize:12,color:"#002855",fontWeight:800,marginBottom:14}}>{card.meta}</div>
            <button type="button" className="btn btn-p" onClick={card.onClick}>{card.action}</button>
          </div>
        ))}
      </div>

      <div className="settings-grid">
        <div className="panel">
          <div className="sec-hdr" style={{marginBottom:12}}>
            <span className="sec-title" style={{fontSize:20}}>Feature Access</span>
            <span className="sec-badge">Launch</span>
          </div>
          <div style={{display:"grid",gap:9}}>
            {unlockRows.map(([label,enabled])=>(
              <div key={label} style={{display:"flex",justifyContent:"space-between",gap:12,fontSize:13,borderBottom:"1px solid #f0f0f0",paddingBottom:8}}>
                <strong style={{color:"#002855"}}>{label}</strong>
                <span style={{color:enabled?"#008577":"#777",fontWeight:800}}>{enabled?"Unlocked":"Locked"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:8}}>Launch Checklist</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:12}}>
            Your best next steps are to confirm selected games, add numbers, and review your dashboard.
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button type="button" className="btn btn-p" onClick={()=>navigate("/")}>Open Dashboard</button>
            <button type="button" className="btn btn-s" onClick={()=>navigate("/my-strategies")}>Create Strategy</button>
            <button type="button" className={"btn btn-s "+(billingBusy?"loading":"")} onClick={onRefresh} disabled={billingBusy}>{billingBusy?"Refreshing":"Refresh Plan"}</button>
          </div>
        </div>

        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:8}}>Invite a Friend</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:12}}>
            Share your LottoTracker link with friends who want to follow draws and strategies.
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
            <input value={referral.draft} onChange={e=>referral.setDraft(cleanReferralSlugDraft(e.target.value))} placeholder="your-name" style={{fontSize:13,color:"#002855",fontWeight:800,background:"#fff",border:"1px solid #ddd",borderRadius:8,padding:"9px 10px",minWidth:180,flex:"1 1 180px"}}/>
            <button type="button" className={"btn btn-sm btn-s "+(referral.saving?"loading":"")} onClick={referral.save} disabled={referral.saving||referral.loading}>{referral.saving?"Saving":"Save Link Name"}</button>
          </div>
          <div style={{fontSize:12,color:"#002855",fontWeight:800,wordBreak:"break-all",background:"#f8f8f8",borderRadius:8,padding:10,marginBottom:12}}>{referralLink}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button type="button" className="btn btn-p" onClick={copyReferral}>{referralCopied?"Copied":"Copy Link"}</button>
            <button type="button" className="btn btn-s" onClick={shareReferral}>Share</button>
          </div>
          {referral.msg&&<div style={{fontSize:12,color:"#008577",fontWeight:800,marginTop:12}}>{referral.msg}</div>}
          {referral.err&&<div className="auth-err" style={{marginTop:12}}>{referral.err}</div>}
          {settingsMsg&&<div className="auth-err" style={{marginTop:12}}>{settingsMsg}</div>}
        </div>

        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:8}}>Feedback</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:12}}>
            Report a bug, suggest a feature, or tell us what felt confusing. Your note goes straight to the LottoTracker launch team.
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
            <button type="button" className="btn btn-sm btn-p" onClick={onFeedback}>Open Feedback Form</button>
            <a className="btn btn-sm btn-s" href={"mailto:"+SUPPORT_EMAIL+"?subject=LottoTracker%20Soft%20Launch%20Feedback"} style={{textDecoration:"none"}}>Email Support</a>
            <button type="button" className="btn btn-sm btn-s" onClick={copyAppVersion}>Copy App Version</button>
          </div>
          <div style={{background:"#fffaf0",border:"1px solid #ffd54f",borderRadius:10,padding:"9px 10px",fontSize:12,color:"#5a4a00",fontWeight:800,lineHeight:1.4}}>
            LottoTracker is in soft launch. Your feedback helps us improve the app.
          </div>
        </div>

        <div className="panel">
          <div className="sec-title" style={{fontSize:20,marginBottom:8}}>Session</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:12}}>
            Signed in as <strong>{user?.email||"your account"}</strong>.
          </div>
          <button type="button" className="btn btn-s" onClick={onSignOut}>Sign Out</button>
        </div>
      </div>
      <div className="panel" style={{marginTop:18}}>
        <div className="sec-title" style={{fontSize:20,marginBottom:10}}>App Info</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,fontSize:13,color:"#555"}}>
          <div><strong style={{color:"#002855"}}>App</strong><br/>{APP_VERSION_LABEL}</div>
          <div><strong style={{color:"#002855"}}>Last Updated</strong><br/>{document.lastModified||"Unavailable"}</div>
        </div>
      </div>
    </div>
  );
}

function ProTab({subscription,onCheckout,onPortal,onCancel,onRestore,onRefresh,billingBusy}){
  const planCards=[
    {id:"free",color:"#002855",desc:"Take a look before you join",items:["One qualifying Traveling number when available","Free account and number preview","Invite friends with your link"]},
    {id:"pro",color:"#008577",desc:"Your daily number board",items:["All issued Today’s Picks candidates","Traveling Numbers · Repeat Pair · Grid","14-Day Hold · Today’s Top Numbers","Straight and Box-family displays","Why this pick? explanations","Previous Picks: actual issued results","Share eligible verified hits","Cancel through your account"]},
    {id:"premium",color:"#2E6DA4",desc:"Planned features · Coming Soon",items:["Everything in Pro","Build your own strategies and playslips","What-If historical simulations","Saved-play result tracking","Community and strategy library"]},
    {id:"pool_organizer",color:"#c8860a",desc:"Planned features · Coming Soon",items:["Everything in Premium","Create private pools","Invite and organize members","Track group plays","Pool dashboard"]},
  ];
  const[params]=useSearchParams();
  const navigate=useNavigate();

  const activeTier=subscriptionTier(subscription);
  const activePlan=PLAN_COPY[activeTier]||PLAN_COPY.free;
  const isPaid=subscriptionHas(subscription,"pro");
  const trialUntil=subscription?.trial_ends_at?new Date(subscription.trial_ends_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):null;
  const periodEnd=subscription?.current_period_end?new Date(subscription.current_period_end).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):null;
  const checkStyle={color:"#008577",fontWeight:900,marginRight:8};
  function proCheckoutButtons(stack=false){
    const buttonStyle=stack?{width:"100%"}:undefined;
    const wrap=children=>stack?<div style={{display:"grid",gap:8}}>{children}</div>:children;
    if(isPaid){
      return wrap(
        <button type="button" className="btn btn-s" style={buttonStyle} onClick={onPortal} disabled={billingBusy}>
          Manage Billing
        </button>
      );
    }
    return wrap(
      <>
        <button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} style={buttonStyle} onClick={()=>onCheckout("pro",{start_immediately:true,skip_trial:true,trial:false,checkout_mode:"pay_now"})} disabled={billingBusy}>
          {billingBusy?"Working...":"Unlock Today’s Picks — $9.99/month"}
        </button>
      </>
    );
  }
  function planButton(card){
    if(card.id==="free")return <button type="button" className="btn btn-s" style={{width:"100%"}} disabled>{activeTier==="free"?"Current Plan":"Included"}</button>;
    const included=subscriptionHas(subscription,card.id);
    if(COMING_SOON_PLAN_IDS.has(card.id)){
      return <button type="button" className="btn btn-s" style={{width:"100%"}} disabled>{PLAN_COPY[card.id].label} Coming Soon</button>;
    }
    if(card.id==="pro")return proCheckoutButtons(true);
    return(
      <button type="button" className={"btn "+(included?"btn-s":"btn-p")+" "+(billingBusy?"loading":"")} style={{width:"100%"}} onClick={included?onPortal:()=>onCheckout(card.id)} disabled={billingBusy}>
        {billingBusy?"Working...":included?"Manage Billing":"Start "+PLAN_COPY[card.id].label}
      </button>
    );
  }
  if(params.get("checkout")==="success"){
    return <CheckoutActivation onRefresh={onRefresh} onConfirmed={()=>navigate("/",{replace:true})}/>;
  }
  return(
    <div>
      <div className="hero">
        <div style={{position:"relative",zIndex:1,maxWidth:820}}>
          <div className="hero-sub">Soft Launch Plans</div>
          <div className="hero-title">LottoTracker Plans</div>
          <p style={{color:"rgba(255,255,255,.78)",fontSize:15,lineHeight:1.55,maxWidth:720,marginBottom:20}}>
            Start with the free number preview, or unlock Today’s Picks with Pro for $9.99/month. No free trial. Premium and Pool Organizer are future plans, not available for purchase.
          </p>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {proCheckoutButtons()}
            <button type="button" className="btn btn-s" disabled>Premium Coming Soon</button>
          </div>
        </div>
      </div>

      <div className="panel" style={{border:"2px solid #008577"}}>
        <div className="sec-hdr" style={{marginBottom:10}}>
          <span className="sec-title">Subscription Status</span>
          <span className="sec-badge" style={{background:isPaid?"#008577":"#002855"}}>{activePlan.label}</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>Plan</div><strong>{activePlan.label}</strong></div>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>Subscription</div><strong>{subscription?.subscription_status||"free"}</strong></div>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>Billing</div><strong>{subscription?.billing_status||"free"}</strong></div>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>{subscription?.trial_active?"Trial ends":"Renews / expires"}</div><strong>{trialUntil||periodEnd||"Not active"}</strong></div>
        </div>
        {subscription?.cancel_at_period_end&&(
          <div style={{background:"#fff7e6",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px",marginTop:14,fontSize:13,color:"#5a4a00"}}>
            Your subscription is scheduled to cancel at period end{periodEnd?": "+periodEnd:""}.
          </div>
        )}
        {subscription?.profile_error&&(
          <div className="auth-err" style={{marginTop:14,marginBottom:0}}>Profile status warning: {subscription.profile_error}</div>
        )}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:16}}>
          {proCheckoutButtons()}
          {isPaid&&!subscription?.cancel_at_period_end&&<button type="button" className="btn btn-s" onClick={onCancel} disabled={billingBusy}>Cancel Subscription</button>}
          {isPaid&&subscription?.cancel_at_period_end&&<button type="button" className="btn btn-p" onClick={onRestore} disabled={billingBusy}>Restore Subscription</button>}
          <button type="button" className="btn btn-s" onClick={onRefresh} disabled={billingBusy}>Refresh Status</button>
        </div>
      </div>

      <div className="panel" style={{border:"2px solid #ffd54f",background:"#fffaf0"}}>
        <div style={{fontWeight:700,color:"#002855",marginBottom:6}}>Lotto Tracker provides number-selection information and result tracking. No guaranteed wins.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:12,color:"#5a4a00",fontWeight:600,textTransform:"uppercase"}}>
          <span>No tickets sold.</span>
          <span>No lottery wagers accepted.</span>
          <span>No pool funds handled.</span>
          <span>No winnings distributed.</span>
        </div>
      </div>

      <div className="sec-hdr"><span className="sec-title">Subscription Plans</span><span className="sec-badge">Only Pro checkout active</span></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:16,marginBottom:24}}>
        {planCards.map(card=>{
          const copy=PLAN_COPY[card.id];
          const active=activeTier===card.id;
          return(
            <div key={card.id} className="strat-card" style={{outline:active?"3px solid "+card.color:"none"}}>
              <div className="strat-head" style={{background:card.color}}>
                <div className="strat-name">{copy.label}</div>
                <div className="strat-desc">{card.desc}</div>
              </div>
              <div className="strat-body">
                <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:8}}>
                  <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:700,color:card.color}}>{copy.price}</span>
                  {card.id!=="free"&&<span style={{fontSize:13,color:"#555",fontWeight:600}}>/ month</span>}
                </div>
                {COMING_SOON_PLAN_IDS.has(card.id)&&!active&&<div className="sec-badge" style={{background:"#5d6675",display:"inline-block",marginBottom:10}}>Coming Soon</div>}
                <div style={{fontSize:12,color:"#555",fontWeight:700,marginBottom:12}}>{copy.short}</div>
                <div style={{display:"grid",gap:8,marginBottom:18}}>
                  {card.items.map(item=><div key={item} style={{fontSize:13,color:"#555",lineHeight:1.35}}><span style={checkStyle}>✓</span>{item}</div>)}
                </div>
                {planButton(card)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="panel">
        <div className="sec-title" style={{fontSize:22,marginBottom:8}}>Pool Organizer note</div>
        <div style={{fontSize:13,color:"#555",lineHeight:1.5}}>
          Pool Organizer helps organize private pool information, invitations, outside buy-in records, play tracking, and simulated split summaries. LottoTracker does not collect, hold, transfer, or distribute money and does not sell tickets.
        </div>
      </div>
    </div>
  );
}

function ProTabLegacy({subscription,onCheckout,onPortal,onCancel,onRestore,onRefresh,billingBusy}){
  const freeItems=[
    "Fantasy 5",
    "One Pick game",
    "Maximum 3 saved strategies",
    "Watch Results",
    "What-If",
    "Share cards",
    "7-day Pro trial",
  ];
  const proItems=[
    "All Pick games",
    "Unlimited strategies",
    "Unlimited Watch Results",
    "Unlimited What-If",
    "Advanced analytics",
  ];
  const poolItems=[
    "Private pool admin",
    "Invite members",
    "Mark participation only",
    "Share results",
    "No money collected",
  ];
  const[params,setParams]=useSearchParams();
  useEffect(()=>{
    const checkout=params.get("checkout");
    if(checkout==="success"){
      onRefresh();
      setParams({}, {replace:true});
    }
  },[params,onRefresh,setParams]);
  const checkStyle={color:"#008577",fontWeight:900,marginRight:8};
  const isPro=subscriptionHas(subscription,"pro");
  const trialUntil=subscription?.trial_ends_at?new Date(subscription.trial_ends_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):null;
  const periodEnd=subscription?.current_period_end?new Date(subscription.current_period_end).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):null;
  return(
    <div>
      <div className="hero">
        <div style={{position:"relative",zIndex:1,maxWidth:820}}>
          <div className="hero-sub">Plans</div>
          <div className="hero-title">LottoTracker Pro</div>
          <p style={{color:"rgba(255,255,255,.78)",fontSize:15,lineHeight:1.55,maxWidth:720,marginBottom:20}}>
            Track every supported game, save more plays, compare strategies, and keep your simulation history in one launch-ready workspace.
          </p>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} onClick={isPro?onPortal:onCheckout} disabled={billingBusy}>
              {billingBusy?"Working...":isPro?"Manage Billing":"Start 7-day Pro Trial"}
            </button>
            <NavLink to="/pools" className="btn btn-s" style={{textDecoration:"none"}}>Open Pool Manager</NavLink>
          </div>
        </div>
      </div>

      <div className="panel" style={{border:"2px solid #008577"}}>
        <div className="sec-hdr" style={{marginBottom:10}}>
          <span className="sec-title">Subscription Status</span>
          <span className="sec-badge" style={{background:isPro?"#008577":"#002855"}}>{isPro?"Pro":"Free"}</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>Plan</div><strong>{isPro?"Pro":"Free account"}</strong></div>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>Subscription</div><strong>{subscription?.subscription_status||"free"}</strong></div>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>Billing</div><strong>{subscription?.billing_status||"free"}</strong></div>
          <div><div style={{fontSize:11,color:"#555",textTransform:"uppercase",fontWeight:700}}>{subscription?.trial_active?"Trial ends":"Renews / expires"}</div><strong>{trialUntil||periodEnd||"Not active"}</strong></div>
        </div>
        {subscription?.cancel_at_period_end&&(
          <div style={{background:"#fff7e6",border:"1px solid #ffd54f",borderRadius:10,padding:"10px 12px",marginTop:14,fontSize:13,color:"#5a4a00"}}>
            Your Pro subscription is scheduled to cancel at period end{periodEnd?": "+periodEnd:""}.
          </div>
        )}
        {subscription?.profile_error&&(
          <div className="auth-err" style={{marginTop:14,marginBottom:0}}>Profile status warning: {subscription.profile_error}</div>
        )}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:16}}>
          {!isPro&&<button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} onClick={onCheckout} disabled={billingBusy}>Start 7-day Pro Trial</button>}
          {isPro&&<button type="button" className="btn btn-s" onClick={onPortal} disabled={billingBusy}>Billing Portal</button>}
          {isPro&&!subscription?.cancel_at_period_end&&<button type="button" className="btn btn-s" onClick={onCancel} disabled={billingBusy}>Cancel Subscription</button>}
          {isPro&&subscription?.cancel_at_period_end&&<button type="button" className="btn btn-p" onClick={onRestore} disabled={billingBusy}>Restore Subscription</button>}
          <button type="button" className="btn btn-s" onClick={onRefresh} disabled={billingBusy}>Refresh Status</button>
        </div>
      </div>

      <div className="panel" style={{border:"2px solid #ffd54f",background:"#fffaf0"}}>
        <div style={{fontWeight:700,color:"#002855",marginBottom:6}}>Lotto Tracker provides number-selection information and result tracking. No guaranteed wins.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:12,color:"#5a4a00",fontWeight:600,textTransform:"uppercase"}}>
          <span>No tickets sold.</span>
          <span>No lottery wagers accepted.</span>
          <span>No pool funds handled.</span>
          <span>No winnings distributed.</span>
        </div>
      </div>

      <div className="sec-hdr"><span className="sec-title">Free vs Pro</span><span className="sec-badge">Stripe connected</span></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16,marginBottom:24}}>
        <div className="strat-card">
          <div className="strat-head" style={{background:"#002855"}}>
            <div className="strat-name">Free</div>
            <div className="strat-desc">Start tracking with one selected game</div>
          </div>
          <div className="strat-body">
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:700,color:"#002855",marginBottom:8}}>$0</div>
            <div style={{display:"grid",gap:8,marginBottom:18}}>
              {freeItems.map(item=><div key={item} style={{fontSize:13,color:"#555",lineHeight:1.35}}><span style={checkStyle}>✓</span>{item}</div>)}
            </div>
            <button type="button" className="btn btn-s" style={{width:"100%"}} disabled>{isPro?"Available if canceled":"Current Plan"}</button>
          </div>
        </div>

        <div className="strat-card" style={{outline:"3px solid #008577"}}>
          <div className="strat-head" style={{background:"#008577"}}>
            <div className="strat-name">Pro</div>
            <div className="strat-desc">Full tracking and simulation toolkit</div>
          </div>
          <div className="strat-body">
            <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:8}}>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:700,color:"#008577"}}>$10</span>
              <span style={{fontSize:13,color:"#555",fontWeight:600}}>/ month</span>
            </div>
            <div style={{display:"grid",gap:8,marginBottom:18}}>
              {proItems.map(item=><div key={item} style={{fontSize:13,color:"#555",lineHeight:1.35}}><span style={checkStyle}>✓</span>{item}</div>)}
            </div>
            <button type="button" className={"btn btn-p "+(billingBusy?"loading":"")} style={{width:"100%"}} onClick={isPro?onPortal:onCheckout} disabled={billingBusy}>{isPro?"Manage Billing":"Start Pro Trial"}</button>
          </div>
        </div>
      </div>

      <div className="sec-hdr"><span className="sec-title">Pool Manager</span><span className="sec-badge">MVP</span></div>
      <div className="panel">
        <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:20,alignItems:"start"}}>
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:6}}>
              Pool Manager
            </div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.5,maxWidth:720,marginBottom:14}}>
              An admin layer for organizing private groups around participation and simulated results. It does not collect, hold, move, or distribute money.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:8}}>
              {poolItems.map(item=><div key={item} style={{fontSize:13,color:"#555",lineHeight:1.35}}><span style={checkStyle}>✓</span>{item}</div>)}
            </div>
          </div>
          <div style={{textAlign:"right",minWidth:150}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:700,color:"#008577"}}>MVP</div>
            <div style={{fontSize:12,color:"#555",fontWeight:600,marginBottom:12}}>simulation only</div>
            <NavLink to="/pools" className="btn btn-s" style={{textDecoration:"none"}}>Open Manager</NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}

const POOL_MANAGER_GAMES=launchReadyGames().map(({id,label})=>({id,label}));
const POOL_STATUS_OPTIONS=["Waiting on Members","Ready","Locked","Results Ready","Completed"];
const POOL_SUBMISSION_OPTIONS=["Quick Picks","Saved Numbers","Manual Numbers"];
function defaultPoolDeadline(){
  const d=new Date();
  d.setDate(d.getDate()+1);
  d.setHours(18,0,0,0);
  const pad=n=>String(n).padStart(2,"0");
  return d.getFullYear()+"-"+pad(d.getMonth()+1)+"-"+pad(d.getDate())+"T"+pad(d.getHours())+":"+pad(d.getMinutes());
}

function PoolManagerTab({draws,plays,drawStatus,strategyTrust}) {
  const navigate=useNavigate();
  const defaultPool={
    name:"Lucky 10",
    game:"fantasy5",
    type:"Private",
    maxMembers:10,
    description:"Private simulation pool for tracking participation and results.",
    deadline:defaultPoolDeadline(),
    drawSelection:draws?.[0]?.draw_date||"Next available draw",
    strategyId:"",
    status:"Waiting on Members",
  };
  const[pool,setPool]=useState(defaultPool);
  const[poolCreated,setPoolCreated]=useState(false);
  const[form,setForm]=useState(defaultPool);
  const[inviteName,setInviteName]=useState("");
  const inviteRef=useRef(null);
  const[members,setMembers]=useState([
    {id:1,username:"admin",status:"Joined",method:"Saved Numbers",numbers:"Saved plays",submitted:true},
    {id:2,username:"maria",status:"Joined",method:"Quick Picks",numbers:"04 11 18 23 35",submitted:true},
    {id:3,username:"devon",status:"Pending",method:"Manual Numbers",numbers:"",submitted:false},
  ]);

  const joined=members.filter(m=>m.status==="Joined");
  const pending=members.filter(m=>m.status==="Pending");
  const submitted=members.filter(m=>m.status==="Joined"&&m.submitted);
  const readyToPlay=joined.length>0&&submitted.length===joined.length;
  const savedPlayCount=(plays||[]).filter(p=>rowGame(p)===pool.game).length;
  const latestDraw=draws?.find(d=>rowGame(d)===pool.game)||draws?.[0]||null;
  const poolGameLabel=POOL_MANAGER_GAMES.find(g=>g.id===pool.game)?.label||gameLabel(pool.game);
  const strategyOptions=strategyIdsFromPlays(plays)
    .filter(id=>id!=="manual")
    .map(id=>({id,label:strategyDisplayName(id,strategyTrust?.records?.[id])}));

  function updateForm(key,value){
    setForm(f=>({...f,[key]:value}));
  }
  function createPool(){
    const clean={
      ...form,
      name:form.name.trim()||"New Pool",
      maxMembers:Math.max(1,Number(form.maxMembers)||1),
      status:"Waiting on Members",
    };
    setPool(clean);
    setForm(clean);
    setPoolCreated(true);
  }
  function inviteMember(){
    const username=inviteName.trim();
    if(!username)return;
    setMembers(ms=>[
      ...ms,
      {id:Date.now(),username,status:"Pending",method:"Quick Picks",numbers:"",submitted:false},
    ]);
    setInviteName("");
  }
  function updateMember(id,patch){
    setMembers(ms=>ms.map(m=>m.id===id?{...m,...patch}:m));
  }
  function quickPickFor(id){
    const seed=id+members.length+pool.name.length;
    const nums=[];
    for(let i=0;nums.length<5;i++){
      const next=((seed*7+i*11)%36)+1;
      if(!nums.includes(next))nums.push(next);
    }
    updateMember(id,{method:"Quick Picks",numbers:nums.sort((a,b)=>a-b).map(n=>String(n).padStart(2,"0")).join(" "),submitted:true});
  }
  function memberResult(m,index){
    const matches=latestDraw?.numbers?.length===5?Math.max(0,Math.min(5,(index+pool.name.length)%4)):null;
    if(m.status!=="Joined")return "Not active";
    if(!m.submitted)return "Waiting for numbers";
    if(matches==null)return "Simulation pending";
    if(matches>=3)return matches+" simulated matches";
    if(matches===2)return "Free ticket simulation";
    return "No simulated hit";
  }
  const statusTone={
    "Waiting on Members":"#c8860a",
    Ready:"#008577",
    Locked:"#002855",
    "Results Ready":"#2E6DA4",
    Completed:"#555",
  }[pool.status]||"#555";

  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Pool Manager</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:780}}>
            Manage private pool information, participation, number submissions, and simulated results. No tickets, payments, pool funds, or winnings are handled by LottoTracker.
          </p>
        </div>
        <span className="sec-badge" style={{background:statusTone}}>{pool.status}</span>
      </div>

      <div className="panel" style={{border:"2px solid #ffd54f",background:"#fffaf0"}}>
        <div style={{fontWeight:700,color:"#002855",marginBottom:6}}>Simulation only.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:12,color:"#5a4a00",fontWeight:600,textTransform:"uppercase"}}>
          <span>No money.</span>
          <span>No ticket management.</span>
          <span>No payment handling.</span>
          <span>No ticket purchasing.</span>
        </div>
      </div>

      {!poolCreated&&<EmptyState
        icon="*"
        title="No pools yet."
        text="Create your first pool to organize members, number submissions, and simulated results. No money, tickets, or payments are handled."
        actions={[{label:"Create First Pool",onClick:()=>document.querySelector("#pool-name-input")?.focus()}]}
      />}

      {drawStatus?.next&&(
        <NextStepCard
          title={drawStatus.next.status==="Results Ready"?"Ready to Score Pool":drawStatus.next.status==="Sales Closing Soon"?"Pool Reminder":"Pool Draw Status"}
          text={drawStatus.next.status==="Results Ready"
            ?"Results are ready for "+drawStatus.next.gameLabel+". Review member submissions and score the pool simulation."
            :drawStatus.next.status==="Sales Closing Soon"
              ?"Sales close soon for "+drawStatus.next.gameLabel+". Confirm members and number submissions before the window closes."
              :drawStatus.next.status==="Upcoming"
                ?"The next "+drawStatus.next.gameLabel+" draw is upcoming. Pool Manager is waiting on members and submissions."
                :"Sales are closed. Pool Manager will be ready to score when results post."}
          actions={drawStatus.next.status==="Results Ready"
            ?[{label:"Review Results",onClick:()=>setPool(p=>({...p,status:"Results Ready"}))},{label:"Open Results Center",onClick:()=>navigate("/results")}]
            :[{label:"Invite Members",onClick:()=>inviteRef.current?.focus()},{label:"Mark Ready",onClick:()=>setPool(p=>({...p,status:readyToPlay?"Ready":"Waiting on Members"}))}]}
        />
      )}

      {pool.status==="Waiting on Members"&&(
        <NextStepCard
          title="Next Step: Invite Members"
          text="This pool is waiting on members. Invite someone, then mark participation when they join."
          actions={[
            {label:"Invite Members",onClick:()=>inviteRef.current?.focus()},
            {label:"Review Pool Status",onClick:()=>setPool(p=>({...p,status:readyToPlay?"Ready":"Waiting on Members"}))},
          ]}
        />
      )}

      <div style={{display:"grid",gridTemplateColumns:"minmax(0,1.15fr) minmax(280px,.85fr)",gap:18,alignItems:"start"}}>
        <div className="panel">
          <div className="sec-hdr" style={{marginBottom:12}}>
            <span className="sec-title">Create Pool</span>
            <span className="sec-badge">{poolGameLabel}</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:12}}>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Pool Name
              <input id="pool-name-input" className="add-inp" value={form.name} onChange={e=>updateForm("name",e.target.value)} style={{width:"100%",marginTop:6}}/>
            </label>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Game
              <select className="add-inp" value={form.game} onChange={e=>updateForm("game",e.target.value)} style={{width:"100%",marginTop:6}}>
                {POOL_MANAGER_GAMES.map(g=><option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </label>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Pool Type
              <select className="add-inp" value={form.type} onChange={e=>updateForm("type",e.target.value)} style={{width:"100%",marginTop:6}}>
                <option>Private</option>
                <option>Invite Only</option>
              </select>
            </label>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Maximum Members
              <input className="add-inp" type="number" min="1" value={form.maxMembers} onChange={e=>updateForm("maxMembers",e.target.value)} style={{width:"100%",marginTop:6}}/>
            </label>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Pool Deadline
              <input className="add-inp" type="datetime-local" value={form.deadline} onChange={e=>updateForm("deadline",e.target.value)} style={{width:"100%",marginTop:6}}/>
            </label>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Draw Selection
              <select className="add-inp" value={form.drawSelection} onChange={e=>updateForm("drawSelection",e.target.value)} style={{width:"100%",marginTop:6}}>
                <option>Next available draw</option>
                {(draws||[]).slice(0,8).map(d=><option key={(d.draw_date||"draw")+"-"+d.id} value={d.draw_date}>{gameLabel(rowGame(d))} - {d.draw_date}</option>)}
              </select>
            </label>
            <label style={{fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Pool Strategy
              <select className="add-inp" value={form.strategyId} onChange={e=>updateForm("strategyId",e.target.value)} style={{width:"100%",marginTop:6}}>
                <option value="">Choose saved strategy</option>
                {strategyOptions.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
            <label style={{gridColumn:"1 / -1",fontSize:12,fontWeight:700,color:"#555",textTransform:"uppercase"}}>
              Pool Description
              <textarea className="add-inp" value={form.description} onChange={e=>updateForm("description",e.target.value)} rows={3} style={{width:"100%",marginTop:6,resize:"vertical"}}/>
            </label>
          </div>
          <button type="button" className="btn btn-p" onClick={createPool} style={{marginTop:14}}>Create Pool</button>
        </div>

        <div className="panel">
          <div className="sec-hdr" style={{marginBottom:12}}>
            <span className="sec-title">Administrator Dashboard</span>
            <span className="sec-badge" style={{background:readyToPlay?"#008577":"#c8860a"}}>{readyToPlay?"Ready To Play":"Open"}</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,fontSize:12}}>
            <div><span style={{color:"#555"}}>Members Joined</span><br/><strong style={{fontSize:22,color:"#002855"}}>{joined.length}</strong></div>
            <div><span style={{color:"#555"}}>Pending Invites</span><br/><strong style={{fontSize:22,color:"#c8860a"}}>{pending.length}</strong></div>
            <div><span style={{color:"#555"}}>Numbers Submitted</span><br/><strong style={{fontSize:22,color:"#008577"}}>{submitted.length}</strong></div>
            <div><span style={{color:"#555"}}>Maximum Members</span><br/><strong style={{fontSize:22,color:"#002855"}}>{pool.maxMembers}</strong></div>
            <div><span style={{color:"#555"}}>Saved Numbers</span><br/><strong style={{fontSize:22,color:"#002855"}}>{savedPlayCount}</strong></div>
            <div><span style={{color:"#555"}}>Pool Type</span><br/><strong style={{fontSize:22,color:"#002855"}}>{pool.type}</strong></div>
            <div style={{gridColumn:"1 / -1"}}><span style={{color:"#555"}}>Pool Strategy</span><br/><strong>{pool.strategyId?strategyDisplayName(pool.strategyId,strategyTrust?.records?.[pool.strategyId]):"Choose a saved strategy"}</strong></div>
            <div style={{gridColumn:"1 / -1"}}><span style={{color:"#555"}}>Submission Deadline</span><br/><strong>{pool.deadline||"Not set"}</strong></div>
            <div style={{gridColumn:"1 / -1"}}><span style={{color:"#555"}}>Ready To Play</span><br/><strong style={{color:readyToPlay?"#008577":"#c8860a"}}>{readyToPlay?"Yes":"Waiting on member submissions"}</strong></div>
          </div>
          <div style={{borderTop:"1px solid #f0f0f0",marginTop:14,paddingTop:14}}>
            <div style={{fontSize:11,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:1,marginBottom:7}}>Status</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {POOL_STATUS_OPTIONS.map(s=>(
                <button key={s} type="button" className={"btn btn-sm "+(pool.status===s?"btn-p":"btn-s")} onClick={()=>setPool(p=>({...p,status:s}))}>{s}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="sec-hdr" style={{marginBottom:12}}>
          <span className="sec-title">Members</span>
          <span className="sec-badge">{members.length} total</span>
        </div>
        <div className="add-row">
          <input ref={inviteRef} className="add-inp" value={inviteName} onChange={e=>setInviteName(e.target.value)} placeholder="Invite by Username"/>
          <button type="button" className="btn btn-p" onClick={inviteMember}>Invite</button>
        </div>
        <div style={{display:"grid",gap:10}}>
          {members.map(m=>(
            <div key={m.id} style={{display:"grid",gridTemplateColumns:"minmax(130px,.8fr) minmax(120px,.7fr) minmax(160px,.9fr) minmax(160px,1fr) auto",gap:10,alignItems:"center",padding:"12px 0",borderBottom:"1px solid #f0f0f0"}}>
              <div>
                <strong style={{color:"#002855"}}>@{m.username}</strong>
                <div style={{fontSize:11,color:"#555"}}>{m.submitted?"Numbers submitted":"No numbers yet"}</div>
              </div>
              <select className="add-inp" value={m.status} onChange={e=>updateMember(m.id,{status:e.target.value,submitted:e.target.value==="Joined"?m.submitted:false})} style={{padding:"8px 10px"}}>
                <option>Pending</option>
                <option>Joined</option>
                <option>Removed</option>
              </select>
              <select className="add-inp" value={m.method} onChange={e=>updateMember(m.id,{method:e.target.value})} style={{padding:"8px 10px"}}>
                {POOL_SUBMISSION_OPTIONS.map(o=><option key={o}>{o}</option>)}
              </select>
              <input className="add-inp" value={m.numbers} onChange={e=>updateMember(m.id,{numbers:e.target.value,submitted:e.target.value.trim().length>0})} placeholder={m.method==="Manual Numbers"?"Manual Numbers":"Submission details"} style={{padding:"8px 10px"}}/>
              <div style={{display:"flex",gap:6,justifyContent:"flex-end",flexWrap:"wrap"}}>
                <button type="button" className="btn btn-sm btn-s" onClick={()=>quickPickFor(m.id)}>Quick Picks</button>
                <button type="button" className="btn btn-sm btn-p" onClick={()=>updateMember(m.id,{status:"Joined",submitted:true,numbers:m.numbers||"Saved plays"})}>Mark Joined</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:14,fontSize:12,color:"#555"}}>
          <strong>Pending:</strong> {pending.map(m=>"@"+m.username).join(", ")||"None"}
          <strong>Joined:</strong> {joined.map(m=>"@"+m.username).join(", ")||"None"}
          <strong>Removed:</strong> {members.filter(m=>m.status==="Removed").map(m=>"@"+m.username).join(", ")||"None"}
        </div>
      </div>

      <div className="panel">
        <div className="sec-hdr" style={{marginBottom:12}}>
          <span className="sec-title">Results</span>
          <span className="sec-badge" style={{background:pool.status==="Results Ready"||pool.status==="Completed"?"#008577":"#002855"}}>
            {pool.status==="Results Ready"||pool.status==="Completed"?"Simulation Ready":"After Draw"}
          </span>
        </div>
        <div style={{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:12}}>
          Results shown here are simulated summaries for pool tracking only. LottoTracker does not buy tickets, manage money, or distribute winnings.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
          {joined.map((m,index)=>(
            <div key={m.id} style={{border:"1px solid #eee",borderRadius:12,padding:14,background:"#fafafa"}}>
              <div style={{fontWeight:700,color:"#002855",marginBottom:4}}>@{m.username}</div>
              <div style={{fontSize:12,color:"#555",marginBottom:8}}>{m.method} - {m.numbers||"No numbers submitted"}</div>
              <strong style={{color:"#008577"}}>{pool.status==="Results Ready"||pool.status==="Completed"?memberResult(m,index):"Waiting for draw results"}</strong>
            </div>
          ))}
        </div>
        {joined.length===0&&(
          <div className="empty" style={{padding:"20px 0"}}>Invite members and mark them joined to see simulated results after the draw.</div>
        )}
      </div>
    </div>
  );
}

const COMMUNITY_FEED=[
  {id:"john-4",icon:"\uD83C\uDF89",title:"John matched 4 numbers today.",meta:"Fantasy 5 Evening · Simulation result",user:"John",tone:"#D4AF37"},
  {id:"sarah-free",icon:"\uD83C\uDF89",title:"Sarah won a Free Ticket.",meta:"Fantasy 5 Midday · 2-match",user:"Sarah",tone:"#008577"},
  {id:"evening-ready",icon:"\uD83D\uDD25",title:"Fantasy 5 Evening results are now available.",meta:"Results posted · 8 minutes ago",user:"LottoTracker",tone:"#c8102e"},
  {id:"lucky-pick",icon:"\uD83C\uDFC6",title:"Lucky Pick Strategy is #1 today.",meta:"Strategy leaderboard · Today",user:"LottoTracker",strategy:"Lucky Pick Strategy",tone:"#8B4513"},
  {id:"tracked-127",icon:"\uD83C\uDFAF",title:"127 players tracked today's Fantasy 5 draw.",meta:"Community activity · Fantasy 5",user:"LottoTracker",tone:"#2E6DA4"},
  {id:"pool-created",icon:"\u2B50",title:"Mike created a new public pool.",meta:"Public pool · Weekend Fantasy 5",user:"Mike",tone:"#002855"},
  {id:"pick-five-roi",icon:"\uD83D\uDCC8",title:"Pick 5 Map gained +22% ROI.",meta:"Strategy movement · 30-day view",user:"Owen",strategy:"Pick 5 Map",tone:"#008577"},
  {id:"jennifer-100",icon:"\uD83C\uDF89",title:"Jennifer reached 100 tracked draws.",meta:"Milestone · Simulation tracking",user:"Jennifer",tone:"#D4AF37"},
];
const COMMUNITY_STRATEGIES=[
  {id:"lucky-pick",name:"Lucky Pick Strategy",creator:"Maya",game:"Fantasy 5",roi:"+42%",hitRate:"18.6%",trackedDraws:214,threeMatches:31,fourMatches:4,bestHit:"4 Match",followers:182,likes:426,lastUpdated:"Today",copied:96,popularity:97,tags:["Trending Strategies","Editor's Picks","Highest ROI"],games:"Fantasy 5"},
  {id:"weekend-lucky",name:"Weekend Lucky Numbers",creator:"Jennifer",game:"Mega Millions",roi:"+18%",hitRate:"14.2%",trackedDraws:100,threeMatches:14,fourMatches:1,bestHit:"4 Match",followers:144,likes:318,lastUpdated:"Today",copied:84,popularity:89,tags:["Trending Strategies","Newest"],games:"Fantasy 5, Mega Millions"},
  {id:"steady-five",name:"Steady Five",creator:"Andre",game:"Fantasy 5",roi:"+9%",hitRate:"11.8%",trackedDraws:76,threeMatches:9,fourMatches:0,bestHit:"3 Match",followers:88,likes:171,lastUpdated:"Yesterday",copied:41,popularity:74,tags:["Editor's Picks"],games:"Fantasy 5"},
  {id:"pick-two-sprint",name:"Pick 2 Sprint",creator:"Leo",game:"Pick 2",roi:"+11%",hitRate:"17.9%",trackedDraws:64,threeMatches:8,fourMatches:0,bestHit:"3 Match",followers:58,likes:128,lastUpdated:"Today",copied:30,popularity:66,tags:["Newest"],games:"Pick 2"},
  {id:"midday-builder",name:"Midday Builder",creator:"Sarah",game:"Pick 3",roi:"+27%",hitRate:"21.4%",trackedDraws:58,threeMatches:12,fourMatches:0,bestHit:"3 Match",followers:76,likes:150,lastUpdated:"Today",copied:33,popularity:72,tags:["Newest","Highest ROI"],games:"Pick 3"},
  {id:"cash-pop-pulse",name:"Cash Pop Pulse",creator:"Mike",game:"Cash Pop",roi:"+15%",hitRate:"16.1%",trackedDraws:89,threeMatches:11,fourMatches:2,bestHit:"4 Match",followers:102,likes:204,lastUpdated:"2 days ago",copied:52,popularity:80,tags:["Trending Strategies"],games:"Cash Pop"},
  {id:"power-night-watch",name:"Power Night Watch",creator:"Dana",game:"Powerball",roi:"-6%",hitRate:"8.3%",trackedDraws:142,threeMatches:10,fourMatches:1,bestHit:"4 Match",followers:117,likes:189,lastUpdated:"3 days ago",copied:47,popularity:69,tags:["Most Followed"],games:"Powerball"},
  {id:"pick-four-ladder",name:"Pick 4 Ladder",creator:"Nina",game:"Pick 4",roi:"+5%",hitRate:"10.9%",trackedDraws:121,threeMatches:13,fourMatches:2,bestHit:"4 Match",followers:93,likes:166,lastUpdated:"Last week",copied:39,popularity:68,tags:["Editor's Picks"],games:"Pick 4"},
  {id:"pick-five-map",name:"Pick 5 Map",creator:"Owen",game:"Pick 5",roi:"+22%",hitRate:"15.5%",trackedDraws:49,threeMatches:7,fourMatches:1,bestHit:"4 Match",followers:61,likes:139,lastUpdated:"Today",copied:31,popularity:71,tags:["Highest ROI","Newest"],games:"Pick 5"},
];
const PUBLIC_COMMUNITY_STRATEGIES=COMMUNITY_STRATEGIES
  .filter(strategy=>GAME_CATALOG.find(game=>game.label===strategy.game)?.roadmap==="launch")
  .map(strategy=>({...strategy,locked:true}));
const COMMUNITY_POOLS=[
  {id:"lucky-10",name:"Lucky 10",admin:"Mike",game:"Fantasy 5",draw:"Evening",members:7,seats:3,status:"Open"},
  {id:"weekend-watch",name:"Weekend Watch",admin:"Sarah",game:"Pick 4",draw:"Evening",members:18,seats:7,status:"Waiting on Members"},
  {id:"midday-five",name:"Midday Five",admin:"John",game:"Fantasy 5",draw:"Midday",members:5,seats:0,status:"Ready"},
];
const COMMUNITY_TRENDING={
  "Top Strategies Today":["Lucky Pick Strategy","Pick 5 Map","Steady Five"],
  "Top Pools":["Lucky 10","Weekend Watch","Midday Five"],
  "Most Followed Players":["Jennifer","Mike","Sarah"],
  "Biggest Winners":["John · 4 Match","Sarah · Free Ticket","Maya · +42% ROI"],
  "Most Active Discussion":["Fantasy 5 Evening results","Pool simulation tips","Favorite number streaks"],
};

function StrategyLibraryTab({drawStatus}){
  const navigate=useNavigate();
  const[game,setGame]=useState("All Games");
  const[sort,setSort]=useState("Highest ROI");
  const[spotlight,setSpotlight]=useState("Trending Strategies");
  const[previewId,setPreviewId]=useState(null);
  const[saved,setSaved]=useState([]);
  const[copied,setCopied]=useState([]);
  const[followed,setFollowed]=useState([]);
  const gameOptions=["All Games",...launchReadyGames().map(g=>g.label)];
  const sortOptions=["Highest ROI","Most Popular","Newest","Most Followed","Most Draws","Most Copied"];
  const topSections=["Trending Strategies","Editor's Picks","Newest","Highest ROI"];
  function roiValue(strategy){return Number(String(strategy.roi).replace(/[^0-9.-]/g,""))||0;}
  function isNewest(strategy){return strategy.lastUpdated==="Today";}
  function toggle(setter,id){setter(list=>list.includes(id)?list.filter(x=>x!==id):[...list,id]);}
  function topItems(label){
    let rows=[...PUBLIC_COMMUNITY_STRATEGIES];
    if(label==="Editor's Picks")rows=rows.filter(s=>s.tags.includes("Editor's Picks"));
    else if(label==="Newest")rows=rows.filter(isNewest);
    else if(label==="Highest ROI")rows=rows.sort((a,b)=>roiValue(b)-roiValue(a));
    else rows=rows.filter(s=>s.tags.includes("Trending Strategies")).sort((a,b)=>b.popularity-a.popularity);
    return rows.slice(0,3);
  }
  const filtered=PUBLIC_COMMUNITY_STRATEGIES.filter(s=>game==="All Games"||s.game===game).sort((a,b)=>{
    if(sort==="Most Popular")return b.popularity-a.popularity;
    if(sort==="Newest")return Number(isNewest(b))-Number(isNewest(a))||b.popularity-a.popularity;
    if(sort==="Most Followed")return b.followers-a.followers;
    if(sort==="Most Draws")return b.trackedDraws-a.trackedDraws;
    if(sort==="Most Copied")return b.copied-a.copied;
    return roiValue(b)-roiValue(a);
  });
  const spotlightRows=topItems(spotlight);
  const preview=PUBLIC_COMMUNITY_STRATEGIES.find(s=>s.id===previewId);
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Strategy Library</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            Browse public strategies created by other LottoTracker users. This is a community library, not your personal Scoreboard.
          </p>
        </div>
        <span className="sec-badge">Simulation only</span>
      </div>

      <div className="panel" style={{border:"2px solid #ffd54f",background:"#fffaf0"}}>
        <div style={{fontWeight:700,color:"#002855",marginBottom:6}}>Public strategies are for simulation and tracking inspiration only.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:12,color:"#5a4a00",fontWeight:600,textTransform:"uppercase"}}>
          <span>No gambling.</span>
          <span>No payments.</span>
          <span>No ticket purchases.</span>
          <span>No prize calculations changed.</span>
        </div>
      </div>

      <NextStepCard
        title={drawStatus?.next?.status==="Results Ready"?"Results Ready: Compare Ideas":"Next Step: Save a Strategy"}
        text={drawStatus?.next?.status==="Results Ready"
          ?"Results are ready. Save a strategy for inspiration, then compare your own performance from the Scoreboard."
          :drawStatus?.next?.status==="Upcoming"
            ?"Tonight's draw is coming up. Save a strategy for inspiration before reviewing your own numbers."
            :"Pick one public strategy to follow as inspiration, then compare your own results from the Scoreboard when you're ready."}
        actions={[
          {label:"Save Top Strategy",onClick:()=>setSaved(list=>list.includes(filtered[0]?.id)?list:[...list,filtered[0]?.id].filter(Boolean)),disabled:!filtered[0]},
          {label:"Open Scoreboard",onClick:()=>navigate("/scoreboard")},
        ]}
      />

      <section className="panel">
        <div className="sec-hdr" style={{alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <span className="sec-title">Browse</span>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
              Game
              <select className="add-inp" value={game} onChange={e=>setGame(e.target.value)} style={{minWidth:170,marginTop:5,padding:"8px 10px"}}>
                {gameOptions.map(option=><option key={option}>{option}</option>)}
              </select>
            </label>
            <label style={{fontSize:11,fontWeight:800,color:"#555",textTransform:"uppercase"}}>
              Sort
              <select className="add-inp" value={sort} onChange={e=>setSort(e.target.value)} style={{minWidth:170,marginTop:5,padding:"8px 10px"}}>
                {sortOptions.map(option=><option key={option}>{option}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10,marginBottom:16}}>
          {topSections.map(label=>{
            const rows=topItems(label);
            const leader=rows[0];
            return(
              <button key={label} type="button" className="panel" onClick={()=>setSpotlight(label)} style={{marginBottom:0,padding:"14px 16px",textAlign:"left",border:spotlight===label?"2px solid #008577":"1px solid #edf1f0",background:spotlight===label?"#f0f8f6":"#fff",cursor:"pointer"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#002855",textTransform:"uppercase",lineHeight:1}}>{label}</div>
                <div style={{fontSize:12,color:"#555",marginTop:7,lineHeight:1.35}}>{leader?leader.name+" by "+leader.creator:"More strategies soon"}</div>
              </button>
            );
          })}
        </div>

        <div style={{background:"#002855",borderRadius:14,padding:16,color:"#fff",marginBottom:16}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,.62)",fontWeight:900,textTransform:"uppercase",letterSpacing:2,marginBottom:10}}>{spotlight}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
            {spotlightRows.map(s=>(
              <div key={s.id} style={{border:"1px solid rgba(255,255,255,.14)",borderRadius:12,padding:12,background:"rgba(255,255,255,.06)"}}>
                <div style={{fontWeight:900,lineHeight:1.25}}>{s.name}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,.7)",marginTop:4}}>{s.game} - {s.creator}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:"#D4AF37",lineHeight:1,marginTop:8}}>{s.roi}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {preview&&(
        <div className="panel" style={{border:"2px solid #008577",background:"#f8fbfa"}}>
          <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:10}}>
            <div>
              <span className="sec-title">Preview</span>
              <div style={{fontSize:16,fontWeight:800,color:"#002855",marginTop:4}}>{preview.name}</div>
            </div>
            <button type="button" className="btn btn-sm btn-s" onClick={()=>setPreviewId(null)}>Close</button>
          </div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.5,maxWidth:860}}>
            This preview shows public strategy metadata only. Saving or copying here is local demo behavior and does not alter your plays, matching, scoring, or simulations.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginTop:14}}>
            {[
              ["Creator",preview.creator],
              ["Game",preview.game],
              ["ROI",preview.roi],
              ["Best Hit",preview.bestHit],
              ["Draws",preview.trackedDraws],
              ["Followers",preview.followers],
            ].map(([label,value])=>(
              <div key={label} style={{background:"#fff",border:"1px solid #dbeee9",borderRadius:10,padding:10}}>
                <div style={{fontSize:10,color:"#555",fontWeight:900,textTransform:"uppercase"}}>{label}</div>
                <strong style={{color:"#002855"}}>{value}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sec-hdr"><span className="sec-title">Public Strategies</span><span className="sec-badge">{filtered.length} shown</span></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
        {filtered.map(strategy=>{
          const savedOn=saved.includes(strategy.id);
          const copiedOn=copied.includes(strategy.id);
          const followOn=followed.includes(strategy.creator);
          const sharePayload=shareCardPayload({
            type:"strategy",
            title:"LottoTracker Public Strategy",
            strategyName:strategy.name,
            game:strategy.game,
            status:"Playing Today",
            roi:strategy.roi,
            drawsTracked:String(strategy.trackedDraws),
            createdDate:strategy.lastUpdated,
            link:shortShareLink("strategy",strategy.id,true),
            isPublic:true,
            accent:"#008577",
            note:"Public locked strategy metadata. Simulation only.",
          });
          return(
            <div key={strategy.id} className="sc-card">
              <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                <div>
                  <div className="sc-name" style={{color:"#002855",marginBottom:3}}>{strategy.name}</div>
                  <div style={{fontSize:12,color:"#555"}}>Creator: <strong>{strategy.creator}</strong></div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  <span className="sec-badge" style={{background:"#002855",whiteSpace:"nowrap"}}>{strategy.game}</span>
                  <span className="sec-badge" style={{background:"#008577",whiteSpace:"nowrap"}}>🔒 Locked</span>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10,fontSize:12,marginBottom:14}}>
                <div><span style={{color:"#555"}}>ROI</span><br/><strong style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,color:roiValue(strategy)>=0?"#008577":"#c8102e"}}>{strategy.roi}</strong></div>
                <div><span style={{color:"#555"}}>Draws Tracked</span><br/><strong style={{fontSize:20,color:"#002855"}}>{strategy.trackedDraws}</strong></div>
                <div><span style={{color:"#555"}}>3-Matches</span><br/><strong>{strategy.threeMatches}</strong></div>
                <div><span style={{color:"#555"}}>4-Matches</span><br/><strong>{strategy.fourMatches}</strong></div>
                <div><span style={{color:"#555"}}>Best Hit</span><br/><strong>{strategy.bestHit}</strong></div>
                <div><span style={{color:"#555"}}>Followers</span><br/><strong>{strategy.followers}</strong></div>
                <div><span style={{color:"#555"}}>Likes</span><br/><strong>{strategy.likes}</strong></div>
                <div><span style={{color:"#555"}}>Last Updated</span><br/><strong>{strategy.lastUpdated}</strong></div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <button type="button" className="btn btn-s" onClick={()=>setPreviewId(strategy.id)}>Preview</button>
                <button type="button" className={"btn "+(savedOn?"btn-p":"btn-s")} onClick={()=>toggle(setSaved,strategy.id)}>{savedOn?"Saved":"Save Strategy"}</button>
                <button type="button" className={"btn "+(copiedOn?"btn-p":"btn-s")} onClick={()=>toggle(setCopied,strategy.id)}>{copiedOn?"Copied":"Copy Strategy"}</button>
                <button type="button" className={"btn "+(followOn?"btn-p":"btn-s")} onClick={()=>toggle(setFollowed,strategy.creator)}>{followOn?"Following":"Follow Creator"}</button>
                <ShareCardButton payload={sharePayload} label="Share" className="btn btn-s"/>
              </div>
            </div>
          );
        })}
      </div>
      {!filtered.length&&(
        <EmptyState
          icon="*"
          title="No public strategies match that filter yet."
          text="Try another game or sort option. Strategy Library data is local demo content for now."
        />
      )}
    </div>
  );
}

function CommunityTab({drawStatus}){
  const navigate=useNavigate();
  const[liked,setLiked]=useState([]);
  const[commented,setCommented]=useState([]);
  const[saved,setSaved]=useState([]);
  const[followed,setFollowed]=useState([]);
  const toggle=(setter,id)=>setter(list=>list.includes(id)?list.filter(x=>x!==id):[...list,id]);
  const actionBtn=(active,label,onClick)=>(
    <button type="button" className={"btn btn-sm "+(active?"btn-p":"btn-s")} onClick={onClick}>{label}</button>
  );
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Community</span>
          <p style={{fontSize:14,color:"#555",lineHeight:1.5,marginTop:5,maxWidth:760}}>
            A daily lottery timeline for public simulation milestones, strategy movement, pool openings, and result moments. Not chat. Not social media. No gambling, payments, or ticket purchases.
          </p>
        </div>
        <span className="sec-badge">Simulation only</span>
      </div>

      <div className="panel" style={{border:"2px solid #ffd54f",background:"#fffaf0"}}>
        <div style={{fontWeight:700,color:"#002855",marginBottom:6}}>Community is informational and simulation-only.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:12,color:"#5a4a00",fontWeight:600,textTransform:"uppercase"}}>
          <span>No gambling.</span>
          <span>No payments.</span>
          <span>No ticket purchases.</span>
          <span>No winnings distributed.</span>
        </div>
        <div style={{fontSize:12,color:"#5a4a00",lineHeight:1.45,marginTop:8}}>Only strategies in Today's Lineup count toward public community stats.</div>
      </div>

      {!followed.length&&(
        <EmptyState
          icon="*"
          title="No followed creators."
          text="Follow a creator from the daily timeline to personalize your Community view."
        />
      )}

      <NextStepCard
        title={drawStatus?.next?.status==="Results Ready"?"Results Ready: Share the Moment":drawStatus?.next?.status==="Upcoming"?"Tonight's Draw":"Next Step: Check the Discussion"}
        text={drawStatus?.next?.status==="Results Ready"
          ?"Results are posted. Check the daily timeline, then share or save a strategy idea."
          :drawStatus?.next?.status==="Upcoming"
            ?drawStatus.next.gameLabel+" "+drawStatus.next.drawLabel+" is the focus today. See what the community is tracking before the draw."
            :drawStatus?.latestArchived
              ?"The previous draw has moved to history. Community discussion is now focused on the next draw cycle."
              :"See what's moving today, then save or share a strategy idea when something looks useful."}
        actions={[
          {label:"View Discussion",onClick:()=>setCommented(list=>list.includes(COMMUNITY_FEED[0].id)?list:[...list,COMMUNITY_FEED[0].id])},
          {label:"Share Strategy",onClick:()=>navigate("/my-strategies")},
        ]}
      />

      <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr)",gap:16}}>
        <section>
          <div className="sec-hdr"><span className="sec-title">Daily Timeline</span><span className="sec-badge">{COMMUNITY_FEED.length} updates</span></div>
          <div style={{display:"grid",gap:12}}>
            {COMMUNITY_FEED.map(item=>{
              const likeOn=liked.includes(item.id);
              const commentOn=commented.includes(item.id);
              const followOn=followed.includes(item.user);
              const saveOn=item.strategy&&saved.includes(item.strategy);
              return(
                <div key={item.id} className="panel" style={{marginBottom:0,padding:"16px 18px",borderLeft:"5px solid "+item.tone}}>
                  <div style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr)",gap:12,alignItems:"start"}}>
                    <div style={{width:42,height:42,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",background:item.tone+"18",fontSize:24}}>{item.icon}</div>
                    <div>
                      <div style={{fontSize:16,fontWeight:800,color:"#002855",lineHeight:1.25,marginBottom:4}}>{item.title}</div>
                      <div style={{fontSize:12,color:"#666",lineHeight:1.35,marginBottom:12}}>{item.meta}</div>
                      <div style={{display:"flex",gap:7,flexWrap:"wrap",alignItems:"center"}}>
                        {actionBtn(likeOn,likeOn?"Liked":"\uD83D\uDC4D Like",()=>toggle(setLiked,item.id))}
                        {actionBtn(commentOn,commentOn?"Commented":"\uD83D\uDCAC Comment",()=>toggle(setCommented,item.id))}
                        {item.strategy&&actionBtn(saveOn,saveOn?"Saved":"\u2B50 Save Strategy",()=>toggle(setSaved,item.strategy))}
                        {item.user!=="LottoTracker"&&actionBtn(followOn,followOn?"Following":"\uD83D\uDC65 Follow User",()=>toggle(setFollowed,item.user))}
                      </div>
                      {commentOn&&(
                        <div style={{fontSize:12,color:"#008577",fontWeight:700,marginTop:9}}>Added to the timeline discussion.</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="sec-hdr"><span className="sec-title">Strategy Cards</span><span className="sec-badge">Public</span></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:14}}>
            {PUBLIC_COMMUNITY_STRATEGIES.map(s=>{
              const saveOn=saved.includes(s.name);
              return(
                <div key={s.id} className="sc-card">
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                    <div className="sc-name" style={{color:"#002855"}}>{s.name}</div>
                    <span className="sec-badge" style={{background:"#008577"}}>🔒 Locked</span>
                  </div>
                  <div style={{fontSize:12,color:"#555",marginBottom:12}}>Creator: <strong>{s.creator}</strong></div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,fontSize:12,marginBottom:14}}>
                    <div><span style={{color:"#555"}}>ROI</span><br/><strong style={{fontSize:24,color:"#008577",fontFamily:"'Barlow Condensed',sans-serif"}}>{s.roi}</strong></div>
                    <div><span style={{color:"#555"}}>Hit Rate</span><br/><strong style={{fontSize:24,color:"#2E6DA4",fontFamily:"'Barlow Condensed',sans-serif"}}>{s.hitRate}</strong></div>
                    <div><span style={{color:"#555"}}>Tracked Draws</span><br/><strong>{s.trackedDraws}</strong></div>
                    <div><span style={{color:"#555"}}>Games Played</span><br/><strong>{s.games}</strong></div>
                  </div>
                  <button type="button" className={"btn "+(saveOn?"btn-p":"btn-s")} style={{width:"100%"}} onClick={()=>toggle(setSaved,s.name)}>
                    {saveOn?"Strategy Saved":"Save Strategy"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="sec-hdr"><span className="sec-title">Public Pools</span><span className="sec-badge">Simulation groups</span></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:14}}>
            {COMMUNITY_POOLS.map(pool=>(
              <div key={pool.id} className="sc-card">
                <div className="sc-name" style={{color:"#002855"}}>{pool.name}</div>
                <div style={{display:"grid",gap:7,fontSize:12,marginBottom:14}}>
                  <div><span style={{color:"#555"}}>Administrator</span> <strong>{pool.admin}</strong></div>
                  <div><span style={{color:"#555"}}>Game</span> <strong>{pool.game}</strong></div>
                  <div><span style={{color:"#555"}}>Draw</span> <strong>{pool.draw}</strong></div>
                  <div><span style={{color:"#555"}}>Members Joined</span> <strong>{pool.members}</strong></div>
                  <div><span style={{color:"#555"}}>Seats Remaining</span> <strong>{pool.seats}</strong></div>
                  <div><span style={{color:"#555"}}>Status</span> <strong style={{color:pool.status==="Ready"?"#008577":"#c8860a"}}>{pool.status}</strong></div>
                </div>
                <button type="button" className="btn btn-p" style={{width:"100%"}} onClick={()=>navigate("/pools")}>View Pool</button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="sec-hdr"><span className="sec-title">Trending</span><span className="sec-badge">Today</span></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
            {Object.entries(COMMUNITY_TRENDING).map(([title,items])=>(
              <div key={title} className="panel" style={{marginBottom:0,padding:"16px 18px"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:"#002855",textTransform:"uppercase",marginBottom:10}}>{title}</div>
                <div style={{display:"grid",gap:8}}>
                  {items.map((item,i)=>(
                    <div key={item} style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr)",gap:8,alignItems:"center",fontSize:13,color:"#555"}}>
                      <span style={{width:24,height:24,borderRadius:8,background:"#f0f8f6",color:"#008577",fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{i+1}</span>
                      <strong style={{color:"#002855"}}>{item}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function ActivityCenter({notifications,readIds,onMarkRead,onMarkAllRead}){
  const navigate=useNavigate();
  const[fading,setFading]=useState([]);
  const readSet=new Set(readIds);
  const unreadCount=notifications.filter(n=>!readSet.has(n.id)).length;
  const groups=["Today","Yesterday","Earlier"];
  function fadeRead(id){
    if(readSet.has(id))return;
    setFading(ids=>[...new Set([...ids,id])]);
    setTimeout(()=>{
      onMarkRead(id);
      setFading(ids=>ids.filter(x=>x!==id));
    },240);
  }
  function actionFor(n){
    if(n.route)return{label:n.route==="/my-strategies"?"Create Strategy":n.route==="/history"?"View History":n.route==="/results"?"View Results":"Open",path:n.route};
    if(n.id.includes("results")||n.title.toLowerCase().includes("results"))return{label:"View Results",path:"/results"};
    if(n.id.includes("strategy")||n.title.toLowerCase().includes("strategy"))return{label:"View Scoreboard",path:"/scoreboard"};
    if(n.id.includes("pool"))return{label:"Open Pool",path:"/pools"};
    if(n.id.includes("draw")||n.id.includes("sales"))return{label:"Open Dashboard",path:"/"};
    return{label:"View Details",path:"/profile"};
  }
  return(
    <div>
      <div className="sec-hdr" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div>
          <span className="sec-title">Activity Center</span>
          <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginTop:6}}>
            Mock notifications for draw timing, results, strategies, pools, and tracking milestones. Push notifications are not connected yet.
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <span className="sec-badge" style={{background:unreadCount?"#c8102e":"#008577"}}>{unreadCount} unread</span>
          <button type="button" className="btn btn-sm btn-s" onClick={onMarkAllRead} disabled={!unreadCount}>Mark all read</button>
        </div>
      </div>

      {groups.map(group=>{
        const rows=notifications.filter(n=>n.group===group);
        if(!rows.length)return null;
        return(
          <div key={group} style={{marginBottom:24}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:700,color:"#002855",textTransform:"uppercase",marginBottom:10}}>{group}</div>
            <div style={{display:"grid",gap:10}}>
              {rows.map(n=>{
                const isRead=readSet.has(n.id);
                const isFading=fading.includes(n.id);
                return(
                  <div key={n.id} className={"panel "+(!isRead?"activity-unread ":"")+(isFading?"activity-fading":"")} style={{marginBottom:0,padding:"16px 18px",borderLeft:"5px solid "+(isRead?"#d9d9d9":n.tone),opacity:isRead?0.72:1}}>
                    <div style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) auto auto",gap:12,alignItems:"start"}}>
                      <div style={{width:38,height:38,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",background:n.tone+"18",fontSize:20}}>{n.icon}</div>
                      <div>
                        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:3}}>
                          <div style={{fontWeight:700,color:"#002855",fontSize:15,lineHeight:1.25}}>{n.title}</div>
                          {!isRead&&<span style={{background:"#c8102e",color:"#fff",fontSize:10,fontWeight:800,textTransform:"uppercase",borderRadius:10,padding:"2px 7px"}}>Unread</span>}
                        </div>
                        <div style={{fontSize:13,color:"#555",lineHeight:1.45,marginBottom:8}}>{n.body}</div>
                        <div style={{fontSize:11,color:"#888",fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>{n.meta}</div>
                      </div>
                      <button type="button" className="btn btn-sm btn-p" onClick={()=>navigate(actionFor(n).path)} style={{whiteSpace:"nowrap"}}>
                        {actionFor(n).label}
                      </button>
                      <button type="button" className="btn btn-sm btn-s" onClick={()=>fadeRead(n.id)} disabled={isRead||isFading} style={{whiteSpace:"nowrap"}}>
                        {isRead?"Read":"Mark Read"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryTab({draws,plays,drawStatus,todaysLineup,strategyTrust}){
  const navigate=useNavigate();
  return(
    <div>
      <div className="sec-hdr"><span className="sec-title">Draw History</span><span className="sec-badge">{draws.length} draws</span></div>
      {drawStatus?.latestArchived&&(
        <NextStepCard
          title="Latest Draw Archived"
          text={drawStatus.latestArchived.gameLabel+" "+drawStatus.latestArchived.drawLabel+" has moved into history. Review your saved-play results below."}
          actions={[{label:"Open Results Center",onClick:()=>navigate("/results")},{label:"View My Playslips",onClick:()=>navigate("/my-strategies")}]}
        />
      )}
      {draws.length===0&&(
        <EmptyState
          icon="📋"
          title="Your history will build itself."
          text="Results will appear automatically after your first tracked draw."
        />
      )}
      {draws.length>0&&(
        <div className="panel" style={{padding:0,overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <table className="hist-tbl">
              <thead><tr><th>Date</th><th>Draw</th><th>Numbers</th><th>Jackpot</th><th>Your Best</th></tr></thead>
              <tbody>
                {draws.map((d,i)=>{
                  const materialized=(plays||[]).filter(pl=>Array.isArray(pl.numbers)&&pl.numbers.length===gameNumberConfig(rowGame(pl)).count);
                  const trusted=materialized.filter(pl=>rowGame(pl)===rowGame(d)&&classifyPlayForDraw(pl,d,todaysLineup,strategyTrust).countsPublic);
                  const whatIf=materialized.filter(pl=>rowGame(pl)===rowGame(d)&&!classifyPlayForDraw(pl,d,todaysLineup,strategyTrust).countsPublic);
                  const best=trusted.length?Math.max(0,...trusted.map(pl=>cntMatch(pl.numbers,d.numbers))):0;
                  const bestWhatIf=whatIf.length?Math.max(0,...whatIf.map(pl=>cntMatch(pl.numbers,d.numbers))):0;
                  const pr=getDrawPrize(d,best);
                  return(
                    <tr key={i}>
                      <td style={{whiteSpace:"nowrap",fontSize:12}}>{d.date}</td>
                      <td><span style={{fontSize:12,fontWeight:600,color:d.isEvening?"#002855":"#c8102e"}}>{d.isEvening?"Evening":"Midday"}</span></td>
                      <td><div style={{display:"flex",gap:3}}>{d.numbers.map((n,j)=><Ball key={j} n={n} size="sm"/>)}</div></td>
                      <td style={{fontSize:12}}>{d.jackpot||"Pending"}</td>
                      <td>
                        {pr
                          ?<span style={{background:pr.color+"20",color:pr.color,padding:"2px 8px",borderRadius:8,fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{best} Match &middot; {pr.display}</span>
                          :<span style={{fontSize:11,color:"#aaa"}}>{best>0?best+" match":"—"}</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App(){
  const navigate=useNavigate();
  const location=useLocation();
  const[user,setUser]=useState(null);
  const[checked,setChecked]=useState(false);
  const[appStartState,setAppStartState]=useState("restoring_auth");
  const[setupChecked,setSetupChecked]=useState(false);
  const[setupDone,setSetupDone]=useState(false);
  const[onboardingInitialStep,setOnboardingInitialStep]=useState(1);
  const[,setOnboardingStatus]=useState(null);
  const[draws,setDraws]=useState([]);
  const[drawsLoading,setDrawsLoading]=useState(false);
  const[drawLimitLoaded,setDrawLimitLoaded]=useState(0);
  const[historyLoadedGames,setHistoryLoadedGames]=useState({});
  const[plays,setPlays]=useState([]);
  // Hot/Cold scratch list: in-memory, not persisted. Lifted to App level so
  // the list survives tab navigation -- HotColdTab unmounts on route change
  // and would lose its local state otherwise. Still clears on browser reload
  // (useState default) and on the panel's Clear button.
  const[hotPickScratch,setHotPickScratch]=useState([]);
  const[strats,setStrats]=useState({});
  const[isAdmin,setIsAdmin]=useState(false);
  const[myGames,setMyGames]=useState(DEFAULT_MY_GAMES);
  const[activityReadIds,setActivityReadIds]=useState([]);
  const[subscription,setSubscription]=useState(DEFAULT_SUBSCRIPTION);
  const customerProAccess=subscriptionHas(subscription,"pro");
  const customerReader=React.useMemo(()=>require("./savedReadTransport").scopedCustomerReader(authenticatedJson,user?.id,customerProAccess),[user?.id,customerProAccess]);
  const[billingBusy,setBillingBusy]=useState(false);
  const[toastMsg,setToastMsg]=useState("");
  const[firstWinOpen,setFirstWinOpen]=useState(false);
  const[dailyStreak,setDailyStreak]=useState(1);
  const[strategyTrust,setStrategyTrust]=useState(emptyStrategyTrust);
  const[todaysLineup,setTodaysLineup]=useState(normalizeLineup(null));
  const[scheduledDraws,setScheduledDraws]=useState([]);
  const[performanceSummaries,setPerformanceSummaries]=useState([]);
  const[performanceLoading,setPerformanceLoading]=useState(false);
  const[historyLoading,setHistoryLoading]=useState(false);
  const[feedbackOpen,setFeedbackOpen]=useState(false);
  const[freeLimitDialog,setFreeLimitDialog]=useState(null);
  const officialPollRef=useRef({});
  const drawFetchRef=useRef({limit:0,promise:null,lastAt:0});
  const pendingPublicCopyRef=useRef({key:"",busy:false});
  const authUserIdRef=useRef("");
  const accountLoadSeqRef=useRef(0);
  const planTier=subscription.plan_tier||"free";
  const trialActive=!!subscription.trial_active;
  const drawData=useDrawDataService(myGames,draws);
  const drawStatus=drawData.status;
  const activityNotifications=buildActivityNotifications(MOCK_ACTIVITY,drawStatus);
  function toast(m){setToastMsg(m);setTimeout(()=>setToastMsg(""),3000);}
  function isActiveAccountLoad(userId,seq){
    return !!userId&&authUserIdRef.current===userId&&accountLoadSeqRef.current===seq;
  }
  function hasSavedNumberPlays(list=plays){
    return list.some(p=>Array.isArray(p.numbers)&&p.numbers.length===BALL_COUNT);
  }
  function showFirstWin(){setFirstWinOpen(true);}
  function closeFirstWin(path="/"){
    setFirstWinOpen(false);
    navigate(path,{replace:path==="/"});
  }
  function applyAuthSession(session,{initial=false}={}){
    const nextUser=session?.user??null;
    const nextId=nextUser?.id||"";
    const currentId=authUserIdRef.current||"";
    if(initial||nextId!==currentId){
      require("./savedReadTransport").clearSavedReads();
      accountLoadSeqRef.current+=1;
      authUserIdRef.current=nextId;
      setPlays([]);
      setStrategyTrust(emptyStrategyTrust());
      setTodaysLineup(normalizeLineup(null));
      setScheduledDraws([]);
      setPerformanceSummaries([]);
      setUser(nextUser);
      setAppStartState(nextUser?"resolving_account":"signed_out");
    }
    if(initial)setChecked(true);
  }
  function authReturnPath(){
    try{
      const params=new URLSearchParams(location.search||"");
      const fromQuery=String(params.get("returnTo")||"").trim();
      const fromStorage=String(localStorage.getItem("lt:returnToAfterAuth")||"").trim();
      const candidate=fromQuery||fromStorage;
      if(candidate&&candidate.startsWith("/")&&!candidate.startsWith("//")){
        localStorage.removeItem("lt:returnToAfterAuth");
        return candidate;
      }
    }catch{}
    return"";
  }
  function handleAuthComplete(nextUser){
    applyAuthSession({user:nextUser});
    const destination=authReturnPath();
    if(destination)navigate(destination,{replace:true});
  }
  useEffect(()=>{
    const expired=()=>{require("./savedReadTransport").clearSavedReads();applyAuthSession(null);navigate("/?auth=login",{replace:true});};
    const {SESSION_EXPIRED}=require("./sessionReadPolicy");window.addEventListener(SESSION_EXPIRED,expired);return()=>window.removeEventListener(SESSION_EXPIRED,expired);
  },[navigate]);
  useEffect(()=>{
    setAppStartState("restoring_auth");
    supabase.auth.getSession().then(({data:{session}})=>{
      applyAuthSession(session,{initial:true});
    }).catch(()=>{
      setAppStartState("error");
      setChecked(true);
    });
    const{data:l}=supabase.auth.onAuthStateChange((event,s)=>{
      if(event==="TOKEN_REFRESHED"&&s?.user?.id&&s.user.id===authUserIdRef.current)return;
      applyAuthSession(s);
    });
    return()=>l.subscription.unsubscribe();
  },[]);
  useEffect(()=>{
    let cancelled=false;
    const pathReferral=referralSlugFromPath(location.pathname);
    const params=new URLSearchParams(location.search||"");
    const queryReferral=String(params.get("ref")||"").trim();
    const raw=pathReferral||queryReferral;
    if(!raw)return undefined;
    const cleanPathReferral=pathReferral?cleanReferralSlugDraft(pathReferral):"";
    localStorage.setItem("lt:pendingReferralCode",cleanPathReferral||raw);
    if(cleanPathReferral)return undefined;
    monitoredFetch("/api/public?action=referral-resolve&ref="+encodeURIComponent(raw),{},"referral-resolve")
      .then(r=>r.json().then(data=>({ok:r.ok,data})))
      .then(({ok,data})=>{
        if(cancelled||!ok||!data?.referralSlug)return;
        localStorage.setItem("lt:pendingReferralCode",data.referralSlug);
        const nextParams=new URLSearchParams(location.search||"");
        nextParams.delete("ref");
        const qs=nextParams.toString();
        navigate((data.canonicalPath||("/r/"+encodeURIComponent(data.referralSlug)))+(qs?("?"+qs):""),{replace:true});
      })
      .catch(()=>{});
    return()=>{cancelled=true;};
  },[location.pathname,location.search,navigate]);
  useEffect(()=>{
    if(!user){
      setAppStartState(checked?"signed_out":"restoring_auth");
      setIsAdmin(false);
      setMyGames(DEFAULT_MY_GAMES);
      setActivityReadIds([]);
      setSubscription(DEFAULT_SUBSCRIPTION);
      setSetupChecked(false);
      setSetupDone(false);
      setOnboardingInitialStep(1);
      setOnboardingStatus(null);
      setFirstWinOpen(false);
      setDailyStreak(1);
      setStrategyTrust(emptyStrategyTrust());
      setTodaysLineup(normalizeLineup(null));
      setScheduledDraws([]);
      setPerformanceSummaries([]);
      setPerformanceLoading(false);
      setHistoryLoading(false);
      return;
    }
    initUser();
  },[user]);
  useEffect(()=>{
    if(!user)return;
    const pending=getPendingPublicStrategyCopy();
    if(!pending.strategyId)return;
    let cancelled=false;
    (async()=>{
      try{
        const copied=await copyPendingPublicStrategyToVault();
        if(!copied||cancelled)return;
        await onboardingRequest("complete",{step:"complete"}).catch(()=>{});
        const key=setupKey();
        if(key)localStorage.setItem(key,"true");
        setSetupDone(true);
        setSetupChecked(true);
        setAppStartState("authenticated_app");
        await loadPlays(planTier);
        await loadScheduledDraws();
        await loadPerformanceSummaries();
        navigate("/my-strategies",{replace:true});
      }catch(e){
        recordBetaMetric("api_failed",{label:"copy-public-strategy",message:e?.message||"Copy failed"});
        toast("We could not add that shared strategy. Please try again.");
      }
    })();
    return()=>{cancelled=true;};
  },[user,location.search]);
  useEffect(()=>{
    if(!user)return;
    updateStrategyTrust(prev=>ensureStrategyTrust(prev,plays));
  },[user,plays.map(p=>[p.id,p.strategy,Array.isArray(p.numbers)?p.numbers.join("-"):"",Array.isArray(p.anchor_numbers)?p.anchor_numbers.join("-"):""].join(":")).join("|")]);
  useEffect(()=>{
    if(!user)return;
    const pruned=pruneLineupData(todaysLineup,strategyTrust?.records||{},plays);
    if(JSON.stringify(pruned)!==JSON.stringify(normalizeLineup(todaysLineup))){
      updateLineup(pruned);
    }
  },[user,plays.map(p=>[p.id,p.strategy,p.game].join(":")).join("|"),Object.keys(strategyTrust?.records||{}).sort().join("|")]);
  useEffect(()=>{
    if(!user)return;
    if(scheduledDraws.length)setTodaysLineup(scheduledDrawRowsToLineup(scheduledDraws));
  },[user,scheduledDraws.map(row=>[row.id,row.status,row.strategy_id,row.draw_date,row.draw_time,row.completed_at].join(":")).join("|")]);
  useEffect(()=>{
    if(!user||!scheduledDraws.length||!draws.length)return;
    syncScheduledDrawStatuses(scheduledDraws);
  },[
    user,
    draws.map(d=>[d.id,d.game,d.draw_date,d.draw_time,d.official_result_status,d.source,Array.isArray(d.numbers)?d.numbers.join("-"):""].join(":")).join("|"),
    scheduledDraws.map(row=>[row.id,row.status,row.draw_date,row.draw_time].join(":")).join("|")
  ]);
  useEffect(()=>{
    if(!user)return;
    if(scheduledDraws.length)return;
    const reconciled=reconcileLineupLifecycle(todaysLineup,strategyTrust?.records||{},plays,draws,new Date());
    if(reconciled.changed){
      updateLineup(reconciled.lineup);
    }
  },[
    user,
    scheduledDraws.length,
    draws.map(d=>[d.id,d.game,d.draw_date,d.draw_time,d.official_result_status,d.source,Array.isArray(d.numbers)?d.numbers.join("-"):""].join(":")).join("|"),
    todaysLineup.items.map(item=>item.id+":"+((item.draws||[]).map(draw=>[draw.drawId,draw.status,draw.completedAt].join("/")).join(","))).join("|"),
    plays.map(p=>[p.id,p.strategy,p.game].join(":")).join("|"),
    Object.keys(strategyTrust?.records||{}).sort().join("|")
  ]);
  useEffect(()=>{
    if(!user)return undefined;
    const path=location.pathname;
    const start=performance.now();
    const timer=setTimeout(()=>{
      const duration=Math.round(performance.now()-start);
      if(duration>1200)recordBetaMetric("slow",{label:"page:"+path,duration});
    },1250);
    return()=>clearTimeout(timer);
  },[user,location.pathname]);
  useEffect(()=>{
    if(!user)return;
    const hasClosedLineupItem=normalizeLineup(todaysLineup).items.some(item=>{
      const record=strategyTrust?.records?.[item.id];
      return record?.status!=="Locked"&&drawEntryIsClosedOrLater(lineupDrawForStrategy(item.id,item));
    });
    if(!drawIsClosedOrLater(drawStatus)&&!hasClosedLineupItem)return;
    autoLockActiveStrategies();
  },[user,drawStatus?.next?.status,drawStatus?.next?.sortAt,plays.map(p=>p.id).join("|"),todaysLineup.items.map(item=>item.id+":"+item.playType+":"+item.drawId+":"+((item.draws||[]).map(d=>d.drawId).join(","))).join("|"),Object.values(strategyTrust?.records||{}).map(r=>r.id+":"+r.status+":"+r.lockedDrawId).join("|")]);
  useEffect(()=>{
    if(!user)return undefined;
    if(require('./customerRefresh').customerReadPage(location.pathname))return undefined;
    const activeScheduled=(scheduledDraws||[])
      .filter(row=>!["completed","cancelled"].includes(row.status))
      .filter(row=>["cutoff_passed","awaiting_results"].includes(row.status)||Date.parse((row.draw_date||"")+"T23:59:00")<=Date.now());
    const pendingEntries=activeScheduled.map(row=>{
      const entry=(drawStatus?.entries||[]).find(e=>e.game===row.game&&e.draw_date===row.draw_date&&e.draw_time===row.draw_time);
      return entry?{...entry,scheduledDrawId:row.id}:null;
    }).filter(entry=>
      entry&&["Drawing In Progress","Waiting for official results","Official results delayed"].includes(entry.status)
    ).slice(0,3);
    if(!pendingEntries.length)return undefined;
    const keys=pendingEntries.map(entry=>[entry.game,entry.draw_date,entry.draw_time].join(":"));
    keys.forEach(key=>{if(!officialPollRef.current[key])officialPollRef.current[key]={startedAt:Date.now(),attempts:0,inFlight:false};});
    let stopped=false;
    let timer=null;
    async function poll(){
      if(stopped)return;
      const activeEntries=pendingEntries.filter(entry=>{
        const key=[entry.game,entry.draw_date,entry.draw_time].join(":");
        const meta=officialPollRef.current[key]||{};
        if(meta.inFlight||Number(meta.attempts)>=OFFICIAL_POLL_MAX_ATTEMPTS)return false;
        if(Date.now()-Number(meta.startedAt||Date.now())>20*60*1000)return false;
        return true;
      });
      if(!activeEntries.length)return;
      let savedAny=false;
      try{
        const results=await Promise.allSettled(activeEntries.map(async entry=>{
          const key=[entry.game,entry.draw_date,entry.draw_time].join(":");
          const meta=officialPollRef.current[key]||{startedAt:Date.now(),attempts:0};
          officialPollRef.current[key]={...meta,inFlight:true,attempts:Number(meta.attempts||0)+1,lastAt:Date.now()};
          try{
            const r=await monitoredFetch("/api/poll-official-results",{
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body:JSON.stringify({
                game:entry.game,
                draw_date:entry.draw_date,
                draw_time:entry.draw_time,
                draw_at:entry.drawAt instanceof Date?entry.drawAt.toISOString():entry.drawAt,
              }),
            },"official-results");
            const data=await r.json().catch(()=>null);
            return !!data?.official;
          }finally{
            officialPollRef.current[key]={...(officialPollRef.current[key]||{}),inFlight:false};
          }
        }));
        savedAny=results.some(result=>result.status==="fulfilled"&&result.value===true);
        results.forEach(result=>{
          if(result.status==="rejected")recordBetaMetric("api_failed",{label:"official-results",message:result.reason?.message||String(result.reason||"unknown error")});
        });
      }catch(e){
        recordBetaMetric("api_failed",{label:"official-results",message:e.message});
        activeEntries.forEach(entry=>{
          const key=[entry.game,entry.draw_date,entry.draw_time].join(":");
          if(officialPollRef.current[key])officialPollRef.current[key].inFlight=false;
        });
      }
      if(savedAny){
        toast("Official Florida Lottery results saved.");
        fetchDraws(drawLimitLoaded||DASHBOARD_DRAW_LIMIT);
        loadScheduledDraws();
        loadPerformanceSummaries();
        return;
      }
      const delay=OFFICIAL_POLL_INTERVAL_MS+Math.floor(Math.random()*15000);
      timer=setTimeout(poll,delay);
    }
    poll();
    return()=>{stopped=true;if(timer)clearTimeout(timer);};
  },[
    user,
    location.pathname,
    scheduledDraws.map(row=>[row.id,row.game,row.draw_date,row.draw_time,row.status].join(":")).join("|"),
    (drawStatus?.entries||[]).map(e=>[e.game,e.draw_date,e.draw_time,e.status].join(":")).join("|")
  ]);
  useEffect(()=>{
    if(!user)return undefined;
    if(location.pathname!=="/results")return undefined;
    const pending=(drawStatus?.entries||[]).filter(entry=>["Drawing In Progress","Waiting for official results","Official results delayed"].includes(entry.status));
    if(!pending.length)return undefined;
    let stopped=false;
    const refresh=async()=>{
      if(stopped)return;
      await fetchDraws(Math.max(drawLimitLoaded||DASHBOARD_DRAW_LIMIT,DASHBOARD_DRAW_LIMIT));
      if(stopped)return;
      await Promise.all([loadScheduledDraws(),loadPerformanceSummaries()]);
    };
    const timer=setInterval(refresh,45000);
    return()=>{stopped=true;clearInterval(timer);};
  },[
    user,
    location.pathname,
    drawLimitLoaded,
    (drawStatus?.entries||[]).map(e=>[e.game,e.draw_date,e.draw_time,e.status].join(":")).join("|")
  ]);
  async function initUser(){
    const accountUserId=user?.id||"";
    const accountSeq=accountLoadSeqRef.current;
    setAppStartState("resolving_account");
    setSetupChecked(false);
    const localSetupHint=localStorage.getItem(setupKey())==="true";
    loadMyGames(planTier, trialActive, subscription.free_pick_game);
    loadActivityRead();
    updateDailyStreak();
    const subPromise=loadSubscriptionStatus();
    fetchDraws(DASHBOARD_DRAW_LIMIT);
    loadPlays(planTier,accountUserId,accountSeq);
    loadScheduledDraws(accountUserId,accountSeq);
    loadPerformanceSummaries(accountUserId,accountSeq);
    checkAdmin();
    await resolveAccountOnboarding(localSetupHint);
    if(!isActiveAccountLoad(accountUserId,accountSeq))return;
    subPromise.then(sub=>loadMyGames(sub.plan_tier,sub.trial_active,sub.free_pick_game)).catch(()=>{});
  }
  function onboardingStepNumber(step){
    const key=String(step||"").toLowerCase();
    if(key==="welcome")return 1;
    if(key==="choose_game")return 2;
    if(["create_strategy","strategy_name","name_strategy"].includes(key))return 3;
    if(["add_numbers","enter_numbers"].includes(key))return 4;
    if(["choose_intent","track_or_watch"].includes(key))return 5;
    return 1;
  }
  function onboardingStepKey(step){
    return ["welcome","choose_game","create_strategy","add_numbers","choose_intent"][Math.max(1,Math.min(5,Number(step)||1))-1];
  }
  async function onboardingRequest(action="status",body={}){
    const headers=await authHeaders();
    const options=action==="status"
      ?{headers}
      :{method:"POST",headers,body:JSON.stringify({action,...body})};
    const r=await monitoredFetch("/api/onboarding/status",options,"onboarding-status");
    const data=await r.json().catch(()=>({}));
    if(!r.ok)throw new Error(data.error||"Could not resolve account setup");
    setOnboardingStatus(data);
    return data;
  }
  async function resolveAccountOnboarding(localSetupHint=false){
    if(!LEGACY_ONBOARDING_ENABLED){
      setSetupDone(true);
      setSetupChecked(true);
      setAppStartState("authenticated_app");
      return null;
    }
    try{
      const data=await onboardingRequest("status");
      const shouldShow=!!data.shouldShowOnboarding;
      setSetupDone(!shouldShow);
      setOnboardingInitialStep(onboardingStepNumber(data.onboarding?.last_step));
      const key=setupKey();
      if(key)localStorage.setItem(key,shouldShow?"false":"true");
      setSetupChecked(true);
      setAppStartState(shouldShow?"onboarding":"authenticated_app");
      return data;
    }catch(e){
      recordBetaMetric("api_failed",{label:"onboarding-status",message:e.message});
      if(localSetupHint){
        setSetupDone(true);
        setSetupChecked(true);
        setAppStartState("authenticated_app");
      }else{
        setSetupChecked(true);
        setAppStartState("error");
        toast("We had trouble loading your account setup. Please refresh.");
      }
      return null;
    }
  }
  function saveOnboardingProgress(step){
    onboardingRequest("progress",{step:onboardingStepKey(step)}).catch(e=>{
      recordBetaMetric("api_failed",{label:"onboarding-progress",message:e.message});
    });
  }
  function myGamesKey(){return user?"lt:myGames:"+user.id:null;}
  function setupKey(){return user?"lt:onboardingComplete:"+user.id:null;}
  function strategyKey(){return user?"lt:firstStrategy:"+user.id:null;}
  function activityReadKey(){return user?"lt:activityRead:"+user.id:null;}
  function strategyTrustKey(){return user?"lt:strategyTrust:"+user.id:null;}
  function lineupKey(){return user?"lt:todaysLineup:"+user.id:null;}
  function streakKey(){return user?"lt:dailyStreak:"+user.id:null;}
  function todayIso(){return new Date().toISOString().slice(0,10);}
  function updateDailyStreak(){
    const key=streakKey();
    if(!key)return;
    const today=todayIso();
    try{
      const saved=JSON.parse(localStorage.getItem(key)||"null");
      const yesterday=new Date();
      yesterday.setDate(yesterday.getDate()-1);
      const yIso=yesterday.toISOString().slice(0,10);
      let next=1;
      if(saved?.lastOpen===today)next=Number(saved.count)||1;
      else if(saved?.lastOpen===yIso)next=(Number(saved.count)||0)+1;
      localStorage.setItem(key,JSON.stringify({lastOpen:today,count:next}));
      setDailyStreak(next);
    }catch{
      localStorage.setItem(key,JSON.stringify({lastOpen:today,count:1}));
      setDailyStreak(1);
    }
  }
  async function authHeaders(){
    const{data:{session}}=await supabase.auth.getSession();
    const token=session?.access_token;
    return token?{Authorization:"Bearer "+token,"Content-Type":"application/json"}:{"Content-Type":"application/json"};
  }
  function applyReferralProfile(data){
    if(!data)return;
    setSubscription(prev=>({
      ...prev,
      referral_slug:data.referralSlug||prev.referral_slug||null,
      referral_code:data.legacyReferralCode||data.referralCode||prev.referral_code||null,
      referral_slug_updated_at:data.referralSlugUpdatedAt||prev.referral_slug_updated_at||null,
    }));
  }
  async function saveFreePickEntitlement(pickGame){
    const pick=freePickGame(freeGamesForPick(pickGame));
    if(!pick||subscriptionHas(subscription,"pro")||trialActive)return;
    try{
      const headers=await authHeaders();
      await monitoredFetch("/api/public?action=signup-alert",{
        method:"POST",
        headers,
        body:JSON.stringify({selectedPickGame:pick}),
      },"free-pick-entitlement");
      setSubscription(prev=>({...prev,free_pick_game:pick}));
    }catch(e){
      recordBetaMetric("api_failed",{label:"free-pick-entitlement",message:e.message||String(e)});
    }
  }
  function getPendingPublicStrategyCopy(){
    try{
      const params=new URLSearchParams(location.search||"");
      return{
        strategyId:String(params.get("copyStrategy")||localStorage.getItem("lt:pendingPublicStrategy")||"").trim(),
        referralCode:String(params.get("ref")||localStorage.getItem("lt:pendingReferralCode")||"").trim(),
      };
    }catch{
      return{strategyId:"",referralCode:""};
    }
  }
  async function copyPendingPublicStrategyToVault(){
    const pending=getPendingPublicStrategyCopy();
    if(!pending.strategyId||!user)return false;
    const lockKey=user.id+":"+pending.strategyId;
    if(pendingPublicCopyRef.current.busy&&pendingPublicCopyRef.current.key===lockKey)return false;
    pendingPublicCopyRef.current={key:lockKey,busy:true};
    try{
      const headers=await authHeaders();
      if(!headers.Authorization)return false;
      const r=await monitoredFetch("/api/public?action=copy-public-strategy",{
        method:"POST",
        headers,
        body:JSON.stringify({strategyId:pending.strategyId,referralCode:pending.referralCode||""}),
      },"copy-public-strategy");
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Could not add shared strategy");
      localStorage.removeItem("lt:pendingPublicStrategy");
      localStorage.removeItem("lt:pendingReferralCode");
      toast(data.alreadyOwner?"That playslip is already in My Playslips.":"Shared playslip added to My Playslips.");
      return true;
    }finally{
      pendingPublicCopyRef.current={key:"",busy:false};
    }
  }
  async function createPublicStrategyShare(strategyId,meta={}){
    if(!strategyId)throw new Error("Choose a strategy to share.");
    const headers=await authHeaders();
    const r=await monitoredFetch("/api/public?action=create-strategy-share",{
      method:"POST",
      headers,
      body:JSON.stringify({strategyId,strategyName:meta.strategyName||meta.name||""}),
    },"create-strategy-share");
    const data=await r.json().catch(()=>({}));
    if(!r.ok)throw new Error(data.error||"Could not prepare this public share link.");
    return data;
  }
  async function refreshPublicStrategyShareTitle(strategyId,strategyName){
    if(!strategyId||!strategyName)return null;
    const headers=await authHeaders();
    if(!headers.Authorization)return null;
    const r=await monitoredFetch("/api/public?action=refresh-strategy-share-title",{
      method:"POST",
      headers,
      body:JSON.stringify({strategyId,strategyName}),
    },"refresh-strategy-share-title");
    const data=await r.json().catch(()=>({}));
    if(!r.ok)throw new Error(data.error||"Could not refresh this public share title.");
    return data;
  }
  async function createPublicResultShare(scheduledDrawId,resultId){
    if(!scheduledDrawId)throw new Error("Choose a completed result to share.");
    const headers=await authHeaders();
    const r=await monitoredFetch("/api/public?action=create-result-share",{
      method:"POST",
      headers,
      body:JSON.stringify({scheduledDrawId,resultId}),
    },"create-result-share");
    const data=await r.json().catch(()=>({}));
    if(!r.ok)throw new Error(data.error||"Could not prepare this public result link.");
    return data;
  }
  async function revokePublicStrategyShare(strategyId){
    if(!strategyId)throw new Error("Choose a strategy to stop sharing.");
    const headers=await authHeaders();
    const r=await monitoredFetch("/api/public?action=revoke-strategy-share",{
      method:"POST",
      headers,
      body:JSON.stringify({strategyId,revoke:true}),
    },"revoke-strategy-share");
    const data=await r.json().catch(()=>({}));
    if(!r.ok)throw new Error(data.error||"Could not stop sharing this strategy.");
    return data;
  }
  async function loadSubscriptionStatus(){
    try{
      const headers=await authHeaders();
      const r=await monitoredFetch("/api/billing/status",{headers},"billing-status");
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Unable to load subscription");
      const next={...DEFAULT_SUBSCRIPTION,...data};
      setSubscription(next);
      return next;
    }catch(e){
      const next={...DEFAULT_SUBSCRIPTION,billing_status:"unavailable"};
      setSubscription(next);
      toast("Billing status unavailable. Free limits are active.");
      return next;
    }
  }
  async function billingPost(path,body=null){
    setBillingBusy(true);
    try{
      const headers=await authHeaders();
      const r=await monitoredFetch(path,{method:"POST",headers,body:body?JSON.stringify(body):undefined},"billing-action");
      const data=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(data.error||"Billing request failed");
      if(data.url){window.location.href=data.url;return data;}
      const next={...DEFAULT_SUBSCRIPTION,...data};
      setSubscription(next);
      loadMyGames(next.plan_tier,next.trial_active,next.free_pick_game);
      loadPlays(next.plan_tier);
      return next;
    }catch(e){
      toast(e.message||"Billing request failed");
      return null;
    }finally{
      setBillingBusy(false);
    }
  }
  function startCheckout(plan="pro",options={}){
    const tier=normalizePlanTier(plan);
    if(COMING_SOON_PLAN_IDS.has(tier)){
      toast(PLAN_COPY[tier].label+" is coming soon. Pro is available now.");
      return;
    }
    billingPost("/api/billing/create-checkout-session",{plan:tier,...options});
  }
  function openBillingPortal(){billingPost("/api/billing/create-portal-session");}
  function cancelSubscription(){billingPost("/api/billing/cancel-subscription");}
  function restoreSubscription(){billingPost("/api/billing/restore-subscription");}
  async function refreshSubscription(){
    setBillingBusy(true);
    const next=await loadSubscriptionStatus();
    loadMyGames(next.plan_tier,next.trial_active,next.free_pick_game);
    loadPlays(next.plan_tier);
    setBillingBusy(false);
    return next;
  }
  function goUpgrade(plan="pro"){
    navigate("/pro");
  }
  function showFreeLimitDialog(reason){
    setFreeLimitDialog(reason||"strategies");
  }
  function closeFreeLimitDialog(){
    setFreeLimitDialog(null);
  }
  function startTrialFromLimitDialog(){
    setFreeLimitDialog(null);
    navigate("/pro");
  }
  function maybeLater(){
    navigate("/");
  }
  function loadMyGames(plan=planTier,trial=trialActive,freePick=subscription.free_pick_game){
    const key=myGamesKey();
    if(!key)return;
    try{
      const saved=JSON.parse(localStorage.getItem(key)||"null");
      const preferredFreeGames=freePick?freeGamesForPick(freePick):(saved||DEFAULT_MY_GAMES);
      const allowed=enforceGameAccess(preferredFreeGames,plan,trial);
      setMyGames(allowed);
      if((saved||DEFAULT_MY_GAMES).length!==allowed.length)localStorage.setItem(key,JSON.stringify(allowed));
    }catch{setMyGames(DEFAULT_MY_GAMES);}
  }
  function saveMyGames(next){
    const requested=normalizeGames(next);
    if(!subscriptionHas(subscription,"pro")){
      const currentPick=freePickGame(myGames);
      const requestedPicks=requested.filter(id=>PICK_GAME_IDS.includes(id));
      const newPick=requestedPicks.find(id=>id!==currentPick);
      if(requestedPicks.length>1||(currentPick&&newPick)){
        showFreeLimitDialog("pick_game");
        return;
      }
    }
    const allowed=enforceGameAccess(next,planTier,trialActive);
    setMyGames(allowed);
    const key=myGamesKey();
    if(key)localStorage.setItem(key,JSON.stringify(allowed));
    const pick=freePickGame(allowed);
    if(pick&&!subscriptionHas(subscription,"pro"))saveFreePickEntitlement(pick);
    if(allowed.length!==normalizeGames(next).length)toast("Free accounts include Fantasy 5 plus one Pick game. Pro unlocks all games.");
    else toast("My Games updated.");
  }
  function ensureGameVisibleForStrategy(gameId){
    const cleanGame=gameId||"fantasy5";
    if(!gameAllowedForPlan(cleanGame,myGames,planTier,trialActive))return;
    if(myGames.includes(cleanGame))return;
    const next=cleanGame==="fantasy5"
      ?["fantasy5",...myGames.filter(id=>id!=="fantasy5")]
      :["fantasy5",cleanGame,...myGames.filter(id=>id!=="fantasy5"&&!PICK_GAME_IDS.includes(id)&&id!==cleanGame)];
    const allowed=enforceGameAccess(next,planTier,trialActive);
    setMyGames(allowed);
    const key=myGamesKey();
    if(key)localStorage.setItem(key,JSON.stringify(allowed));
  }
  async function checkAdmin(){
    try{
      const{data,error}=await supabase.from("admin_users").select("user_id").eq("user_id",user.id).maybeSingle();
      if(data){
        setIsAdmin(true);
        return true;
      }
      if(error)recordBetaMetric("api_failed",{label:"admin-users-client",message:error.message});
      const headers=await authHeaders();
      const r=await monitoredFetch("/api/admin/monitor?view=admin-check",{headers},"admin-check");
      const monitor=await r.json().catch(()=>({}));
      const ok=!!monitor.is_admin;
      setIsAdmin(ok);
      return ok;
    }catch(e){
      recordBetaMetric("api_failed",{label:"admin-check",message:e?.message||"Admin check failed"});
      setIsAdmin(false);
      return false;
    }
  }
  async function fetchDraws(limit=DASHBOARD_DRAW_LIMIT,options={}){
    const safeLimit=Math.max(1,Math.min(FULL_DRAW_LIMIT,Number(limit)||DASHBOARD_DRAW_LIMIT));
    const gameFilter=GAME_CATALOG.some(game=>game.id===options?.game)?options.game:"";
    const requestKey=(gameFilter?("game:"+gameFilter):"all")+":"+safeLimit;
    const now=Date.now();
    if(drawFetchRef.current.promise&&drawFetchRef.current.key===requestKey)return drawFetchRef.current.promise;
    if(!gameFilter&&drawLimitLoaded>=safeLimit&&safeLimit<=DASHBOARD_DRAW_LIMIT&&now-drawFetchRef.current.lastAt<DRAW_CACHE_TTL_MS)return;
    const showLoading=!gameFilter&&(draws.length===0||safeLimit>drawLimitLoaded);
    if(showLoading)setDrawsLoading(true);
    const promise=(async()=>{
      try{
      const qs=new URLSearchParams({limit:String(safeLimit)});
      if(gameFilter)qs.set("game",gameFilter);
      const r=await monitoredFetch("/api/draws?"+qs.toString(),{},"draws");
      if(!r.ok)throw new Error();
      const d=await r.json();
      const next=Array.isArray(d)?d:[];
      setDraws(prev=>{
        const merged=gameFilter?mergeDrawRows(prev,next):next;
        return drawsFingerprint(prev)===drawsFingerprint(merged)?prev:merged;
      });
      if(!gameFilter)setDrawLimitLoaded(prev=>prev===safeLimit?prev:safeLimit);
      drawFetchRef.current.lastAt=Date.now();
      }
      catch{toast("Draw data is taking longer than expected. Try refreshing in a moment.");}
      finally{
        if(showLoading)setDrawsLoading(false);
        drawFetchRef.current.promise=null;
      }
    })();
    drawFetchRef.current={limit:safeLimit,key:requestKey,promise,lastAt:drawFetchRef.current.lastAt||0};
    return promise;
  }
  async function loadHistoricalDraws(gameId="fantasy5"){
    const gameFilter=GAME_CATALOG.some(game=>game.id===gameId)?gameId:"fantasy5";
    if(historyLoadedGames[gameFilter])return;
    setHistoryLoading(true);
    try{
      await fetchDraws(WHAT_IF_DRAW_LIMIT,{game:gameFilter});
      setHistoryLoadedGames(prev=>({...prev,[gameFilter]:true}));
    }finally{setHistoryLoading(false);}
  }
  async function loadPerformanceSummaries(accountUserId=user?.id,accountSeq=accountLoadSeqRef.current){
    if(!accountUserId)return;
    setPerformanceLoading(true);
    try{
      const{data,error}=await supabase
        .from("strategy_performance_summaries")
        .select("user_id,strategy_id,game,tracking_mode,period_key,draw_filter,total_cost,cash_won,net,roi,best_match,free_tickets,largest_prize,draws_tracked,winning_draws,trend_direction,previous_period_roi,last_completed_draw_date,last_completed_draw_time,last_official_draw_key,refreshed_at,refresh_reason")
        .eq("user_id",accountUserId);
      if(!isActiveAccountLoad(accountUserId,accountSeq))return;
      if(error){
        recordBetaMetric("api_failed",{label:"performance-summaries",message:error.message});
        setPerformanceSummaries([]);
        return;
      }
      setPerformanceSummaries(data||[]);
    }finally{
      if(isActiveAccountLoad(accountUserId,accountSeq))setPerformanceLoading(false);
    }
  }
  async function loadPlays(plan=planTier,accountUserId=user?.id,accountSeq=accountLoadSeqRef.current){
    if(!accountUserId)return;
    await loadStrategyVersions(accountUserId,accountSeq);
    if(!isActiveAccountLoad(accountUserId,accountSeq))return;
    const{data,error}=await selectAllUserPlaysForUser(accountUserId);
    if(!isActiveAccountLoad(accountUserId,accountSeq))return;
    if(!error&&data){
      const u=[];const s=new Set();
      for(const r of data){
        // Recipe rows have numbers=null and anchor_numbers populated. Legacy
        // rows stored numbers as a string sometimes; harmlessly parse.
        const ns=Array.isArray(r.numbers)?r.numbers:(r.numbers?JSON.parse(r.numbers):null);
        const anch=Array.isArray(r.anchor_numbers)?r.anchor_numbers:null;
        // Dedup key: materialized plays key on number set; recipes key on
        // (strategy, anchor set) so two different anchor saves both survive
        // while an accidental double-insert still dedups.
        const gameId=r.game||"fantasy5";
        const numsKey=ns
          ?(isPickGame(gameId)?ns.join(","):ns.slice().sort((a,b)=>a-b).join(","))
          :"";
        const k=ns
          ?`m:${gameId}:${r.strategy||""}:${numsKey}`
          :`a:${gameId}:${r.strategy}:${(anch||[]).slice().sort((a,b)=>a-b).join(",")}`;
        if(!s.has(k)){s.add(k);u.push({...r,numbers:ns,anchor_numbers:anch});}
      }
      setPlays(u);
    }
  }
  async function selectAllUserPlaysForUser(userId){
    const all=[];
    const pageSize=1000;
    for(let from=0;from<50000;from+=pageSize){
      const to=from+pageSize-1;
      const{data,error}=await supabase
        .from("user_plays")
        .select("*")
        .eq("user_id",userId)
        .order("created_at",{ascending:true})
        .range(from,to);
      if(error)return{data:all,error};
      all.push(...(data||[]));
      if(!data||data.length<pageSize)break;
    }
    return{data:all,error:null};
  }
  async function loadActiveStrategyVersion(strategyId,accountUserId=user?.id,accountSeq=accountLoadSeqRef.current){
    if(!accountUserId||!strategyId)return null;
    const{data,error}=await supabase
      .from("strategy_versions")
      .select("strategy_id,version_number,game,strategy_name,snapshot,fingerprint,play_count,estimated_cost,created_reason,created_at,updated_at,is_active")
      .eq("user_id",accountUserId)
      .eq("strategy_id",strategyId)
      .eq("is_active",true)
      .is("retired_at",null)
      .maybeSingle();
    if(!isActiveAccountLoad(accountUserId,accountSeq))return null;
    if(error){
      recordBetaMetric("api_failed",{label:"strategy-version-one",message:error.message});
      return null;
    }
    if(!data)return null;
    const snapshot=parseStrategySnapshot(data.snapshot)||{};
    const snapshotCount=strategySnapshotNumbers(snapshot).length;
    return{
      id:data.strategy_id,
      baseName:data.strategy_name||strategyBaseLabel(data.strategy_id),
      version:Number(data.version_number)||1,
      game:data.game||snapshot.game||"fantasy5",
      source:snapshot.source||"Manual",
      playSize:snapshotCount||Number(data.play_count)||0,
      pickRules:snapshot.pickRules||null,
      fingerprint:data.fingerprint||null,
      outcomeSnapshot:snapshot,
      sourceOfTruth:"strategy_versions",
      estimatedCost:Number(data.estimated_cost)||Number(snapshot.estimatedCost)||0,
      createdAt:data.created_at||null,
      updatedAt:data.updated_at||data.created_at||null,
      status:"Draft",
    };
  }
  async function loadStrategyVersions(accountUserId=user?.id,accountSeq=accountLoadSeqRef.current){
    if(!accountUserId)return;
    const{data,error}=await supabase
      .from("strategy_versions")
      .select("strategy_id,version_number,game,strategy_name,snapshot,fingerprint,play_count,estimated_cost,created_reason,created_at,updated_at,is_active")
      .eq("user_id",accountUserId)
      .eq("is_active",true)
      .is("retired_at",null);
    if(!isActiveAccountLoad(accountUserId,accountSeq))return;
    if(error){
      recordBetaMetric("api_failed",{label:"strategy-versions",message:error.message});
      return;
    }
    const versions=data||[];
    updateStrategyTrust(prev=>{
      const base=normalizeStrategyTrust(prev);
      const records={};
      versions.forEach(row=>{
        const id=row.strategy_id;
        if(!id)return;
        const snapshot=parseStrategySnapshot(row.snapshot)||{};
        const snapshotCount=strategySnapshotNumbers(snapshot).length;
        const existing=base.records[id]||{};
        records[id]={
          ...existing,
          id,
          baseName:row.strategy_name||existing.baseName||strategyBaseLabel(id),
          version:Number(row.version_number)||Number(existing.version)||1,
          game:row.game||snapshot.game||existing.game||"fantasy5",
          source:snapshot.source||existing.source||"Manual",
          playSize:snapshotCount||Number(row.play_count)||Number(existing.playSize)||0,
          pickRules:snapshot.pickRules||existing.pickRules||null,
          fingerprint:row.fingerprint||existing.fingerprint||null,
          outcomeSnapshot:snapshot,
          sourceOfTruth:"strategy_versions",
          estimatedCost:Number(row.estimated_cost)||Number(snapshot.estimatedCost)||Number(existing.estimatedCost)||0,
          createdAt:existing.createdAt||row.created_at||null,
          updatedAt:row.updated_at||existing.updatedAt||row.created_at||null,
          status:existing.status||"Draft",
        };
      });
      return{...base,records};
    });
  }
  async function addPlay(numbers){
    const firstSave=!hasSavedNumberPlays();
    const{data,error}=await supabase.from("user_plays").insert({user_id:user.id,game:"fantasy5",numbers}).select().single();
    if(!error&&data){
      setPlays(p=>[...p,{...data,numbers}]);
      toast(firstSave?"🎉 First numbers saved":"Play added!");
      if(firstSave)showFirstWin();
    }
    else if(error){toast("Error adding play: "+error.message);}
  }
  // Bulk-insert variant used by MyNumbers "Fill to N plays" (up to ~500 new
  // rows at once). Single supabase round-trip instead of N sequential inserts.
  async function addPlaysBatch(numbersArr,toastMsg){
    if(!numbersArr||numbersArr.length===0) return;
    let allowed=numbersArr;
    const rows=allowed.map(nums=>({user_id:user.id,game:"fantasy5",numbers:nums}));
    const firstSave=!hasSavedNumberPlays();
    const{data,error}=await supabase.from("user_plays").insert(rows).select();
    if(error){toast("Error adding plays: "+error.message);return;}
    if(data){
      setPlays(p=>[...p,...data]);
      toast(firstSave?"🎉 First numbers saved":(toastMsg||("Added "+data.length+" plays!")));
      if(firstSave)showFirstWin();
    }
  }
  async function deletePlay(id){
    const play=plays.find(p=>p.id===id);
    const strategyId=play?strategyIdForPlay(play):null;
    if(strategyId&&!strategyCanModify(strategyTrust.records?.[strategyId],drawStatus)){
      toast("Strategy is locked until this draw finishes.");
      return;
    }
    await supabase.from("user_plays").delete().eq("id",id);
    setPlays(p=>p.filter(x=>x.id!==id));
    toast("Removed.");
  }
  async function deleteAllPlays(){
    await supabase.from("user_plays").delete().eq("user_id",user.id);
    setPlays([]);
    toast("All plays deleted.");
  }
  function saveStrat(id,d){setStrats(s=>({...s,[id]:d}));}
  function loadActivityRead(){
    const key=activityReadKey();
    if(!key)return;
    try{
      const saved=JSON.parse(localStorage.getItem(key)||"[]");
      setActivityReadIds(Array.isArray(saved)?saved:[]);
    }catch{setActivityReadIds([]);}
  }
  function saveActivityRead(ids){
    const clean=[...new Set(ids)].filter(id=>activityNotifications.some(n=>n.id===id)||String(id).startsWith("draw-status-"));
    setActivityReadIds(clean);
    const key=activityReadKey();
    if(key)localStorage.setItem(key,JSON.stringify(clean));
  }
  function markActivityRead(id){
    saveActivityRead([...activityReadIds,id]);
  }
  function markAllActivityRead(){
    saveActivityRead(activityNotifications.map(n=>n.id));
  }
  function loadStrategyTrust(){
    const key=strategyTrustKey();
    if(!key)return;
    try{
      setStrategyTrust(normalizeStrategyTrust(JSON.parse(localStorage.getItem(key)||"null")));
    }catch{
      setStrategyTrust(emptyStrategyTrust());
    }
  }
  function updateStrategyTrust(updater){
    setStrategyTrust(prev=>{
      const next=typeof updater==="function"?normalizeStrategyTrust(updater(prev)):normalizeStrategyTrust(updater);
      const key=strategyTrustKey();
      if(key)localStorage.setItem(key,JSON.stringify(next));
      return next;
    });
  }
  function loadLineup(){
    const key=lineupKey();
    if(!key)return;
    try{
      setTodaysLineup(normalizeLineup(JSON.parse(localStorage.getItem(key)||"null")));
    }catch{
      setTodaysLineup(normalizeLineup(null));
    }
  }
  async function loadScheduledDraws(accountUserId=user?.id,accountSeq=accountLoadSeqRef.current){
    if(!accountUserId)return;
    const since=new Date();
    since.setFullYear(since.getFullYear()-1);
    const{data,error}=await supabase
      .from("scheduled_strategy_draws")
      .select("*")
      .eq("user_id",accountUserId)
      .gte("draw_date",since.toISOString().slice(0,10))
      .order("draw_date",{ascending:true})
      .order("draw_time",{ascending:true});
    if(!isActiveAccountLoad(accountUserId,accountSeq))return;
    if(error){
      toast("Live schedule is unavailable: "+error.message);
      return;
    }
    let rows=data||[];
    if(!rows.length){
      const migrated=await migrateLegacyLineupToScheduledDraws();
      if(!isActiveAccountLoad(accountUserId,accountSeq))return;
      if(migrated){
        const retry=await supabase
          .from("scheduled_strategy_draws")
          .select("*")
          .eq("user_id",accountUserId)
          .gte("draw_date",since.toISOString().slice(0,10))
          .order("draw_date",{ascending:true})
          .order("draw_time",{ascending:true});
        if(!isActiveAccountLoad(accountUserId,accountSeq))return;
        if(!retry.error)rows=retry.data||[];
      }
    }
    setScheduledDraws(rows);
    setTodaysLineup(scheduledDrawRowsToLineup(rows));
    syncScheduledDrawStatuses(rows);
  }
  async function migrateLegacyLineupToScheduledDraws(){
    const key=lineupKey();
    if(!key)return false;
    let legacy=null;
    try{legacy=normalizeLineup(JSON.parse(localStorage.getItem(key)||"null"));}catch{return false;}
    if(!legacy.items.length)return false;
    const rows=[];
    legacy.items.forEach(item=>{
      const record=strategyRecordForId(item.id,strategyTrust?.records,plays);
      const drawsForItem=Array.isArray(item.draws)&&item.draws.length?item.draws:[item.drawId?item:null].filter(Boolean);
      drawsForItem.forEach(draw=>{
        const entry=findScheduledDrawEntry(draw,record,draws,new Date());
        const status=dbStatusForDrawEntry(entry,draw,new Date());
        if(status==="completed"||status==="cancelled")return;
        const frozen=freezeStrategyNumbers(plays,item.id,record);
        const frozenCost=strategyEstimatedCost(record,frozen);
        rows.push({
          user_id:user.id,
          strategy_id:item.id,
          game:record.game||entry?.game||"fantasy5",
          draw_date:draw.drawDate||entry?.draw_date,
          draw_time:drawTimeKey(draw.drawTime||entry?.draw_time||draw.drawLabel),
          draw_label:draw.drawLabel||(entry?(entry.gameLabel+" "+entry.drawLabel):null),
          strategy_version:Number(record.version)||1,
          strategy_name:strategyDisplayName(item.id,record),
          play_type:item.playType==="Real Play"?"Real Play":"Simulation",
          tracking_mode:"live",
          status,
          scheduled_at:item.addedAt||new Date().toISOString(),
          cutoff_at:entry?.closeAt instanceof Date?entry.closeAt.toISOString():null,
          locked_at:status==="cutoff_passed"||status==="awaiting_results"?(draw.lockedAt||new Date().toISOString()):null,
          frozen_snapshot:{
            strategyId:item.id,
            strategyName:strategyDisplayName(item.id,record),
            game:record.game||entry?.game||"fantasy5",
            version:Number(record.version)||1,
            fingerprint:record.fingerprint||null,
            versionActivatedAt:record.createdAt||record.updatedAt||null,
            versionCreatedAt:record.createdAt||null,
            scheduledAt:item.addedAt||new Date().toISOString(),
            cutoffAt:entry?.closeAt instanceof Date?entry.closeAt.toISOString():null,
            drawId:draw.drawId||drawLockIdForDraw(entry),
            drawDate:draw.drawDate||entry?.draw_date,
            drawTime:drawTimeKey(draw.drawTime||entry?.draw_time||draw.drawLabel),
            playType:item.playType==="Real Play"?"Real Play":"Simulation",
            trackingMode:"live",
            cost:frozenCost,
            pickRules:record.pickRules||null,
            numbers:frozen,
          },
          updated_at:new Date().toISOString(),
        });
      });
    });
    const clean=rows.filter(row=>row.draw_date&&row.draw_time&&row.strategy_id);
    if(!clean.length)return false;
    const{error}=await supabase.from("scheduled_strategy_draws").upsert(clean,{onConflict:"user_id,strategy_id,game,draw_date,draw_time"});
    if(error){
      toast("Could not migrate active lineup: "+error.message);
      return false;
    }
    toast("Moved active lineup schedule to your account.");
    return true;
  }
  async function syncScheduledDrawStatuses(rows=scheduledDraws){
    if(!user||!Array.isArray(rows)||!rows.length)return;
    const updates=[];
    rows.forEach(row=>{
      if(["completed","cancelled"].includes(row.status))return;
      const record={game:row.game};
      const entry=findScheduledDrawEntry({
        drawId:String(Date.parse(row.draw_date+"T"+(row.draw_time==="evening"?"23:15:00":"13:45:00"))),
        drawDate:row.draw_date,
        drawTime:row.draw_time,
        drawLabel:row.draw_label,
      },record,draws,new Date());
      const nextStatus=dbStatusForDrawEntry(entry,row,new Date());
      if(nextStatus&&nextStatus!==row.status&&nextStatus!=="completed"){
        updates.push({id:row.id,status:nextStatus,locked_at:nextStatus==="cutoff_passed"||nextStatus==="awaiting_results"?(row.locked_at||new Date().toISOString()):row.locked_at});
      }
    });
    await Promise.all(updates.slice(0,20).map(update=>supabase.from("scheduled_strategy_draws").update({
      status:update.status,
      locked_at:update.locked_at,
      updated_at:new Date().toISOString(),
    }).eq("user_id",user.id).eq("id",update.id)));
    if(updates.length){
      loadScheduledDraws();
      loadPerformanceSummaries();
    }
  }
  function updateLineup(updater){
    setTodaysLineup(prev=>{
      const next=normalizeLineup(typeof updater==="function"?updater(prev):updater);
      const key=lineupKey();
      if(key)localStorage.setItem(key,JSON.stringify(next));
      return next;
    });
  }
  async function addToLineup(strategyId,options={}){
    if(!strategyId)return false;
    const trackingMode=normalizeTrackingMode(options.trackingMode||"live");
    const selectedDraws=(Array.isArray(options.draws)&&options.draws.length?options.draws:[options.draw||drawStatus?.next]).filter(Boolean);
    const openDraws=selectedDraws.filter(draw=>!drawEntryIsClosedOrLater(draw));
    if(!openDraws.length){
      const label=selectedDraws[0]?.drawLabel||"That draw";
      toast(label+" closed. Choose another open draw or keep it in My Playslips.");
      return false;
    }
    const activeVersionRecord=await loadActiveStrategyVersion(strategyId);
    const record=activeVersionRecord||strategyRecordForId(strategyId,strategyTrust?.records,plays);
    if(!strategyNumbersForRecord(strategyId,record,plays).length){
      toast("Could not load this strategy's saved version for scheduling. Please refresh before scheduling it.");
      return false;
    }
    const frozen=freezeStrategyNumbers(plays,strategyId,record);
    const frozenCost=strategyEstimatedCost(record,frozen);
    const rows=openDraws.map(draw=>{
      const scheduledAt=new Date().toISOString();
      return{
      user_id:user.id,
      strategy_id:strategyId,
      game:record.game||draw.game||"fantasy5",
      draw_date:draw.draw_date,
      draw_time:drawTimeKey(draw.draw_time||draw.drawLabel),
      draw_label:draw.gameLabel+" "+draw.drawLabel,
      strategy_version:Number(record.version)||1,
      strategy_name:strategyDisplayName(strategyId,record),
      play_type:trackingMode==="live"?"Real Play":"Simulation",
      tracking_mode:trackingMode,
      status:"upcoming",
      scheduled_at:scheduledAt,
      cutoff_at:draw.closeAt instanceof Date?draw.closeAt.toISOString():null,
      frozen_snapshot:{
        strategyId,
        strategyName:strategyDisplayName(strategyId,record),
        game:record.game||draw.game||"fantasy5",
        version:Number(record.version)||1,
        fingerprint:record.fingerprint||null,
        versionActivatedAt:record.createdAt||record.updatedAt||null,
        versionCreatedAt:record.createdAt||null,
        scheduledAt,
        cutoffAt:draw.closeAt instanceof Date?draw.closeAt.toISOString():null,
        drawId:drawLockIdForDraw(draw),
        drawDate:draw.draw_date,
        drawTime:drawTimeKey(draw.draw_time||draw.drawLabel),
        playType:trackingMode==="live"?"Real Play":"Simulation",
        trackingMode,
        cost:frozenCost,
        pickRules:record.pickRules||null,
        numbers:frozen,
      },
      updated_at:new Date().toISOString(),
    };
    });
    const{data,error}=await supabase
      .from("scheduled_strategy_draws")
      .upsert(rows,{onConflict:"user_id,strategy_id,game,draw_date,draw_time"})
      .select();
    if(error){
      toast("Could not schedule strategy: "+error.message);
      return false;
    }
    setScheduledDraws(prev=>{
      const byKey=new Map((prev||[]).map(row=>[[row.strategy_id,row.game,row.draw_date,row.draw_time].join(":"),row]));
      (data||[]).forEach(row=>byKey.set([row.strategy_id,row.game,row.draw_date,row.draw_time].join(":"),row));
      return[...byKey.values()];
    });
    toast((trackingMode==="live"?"Live tracking":"Watch mode")+" scheduled for "+(data?.length||rows.length)+" draw"+((data?.length||rows.length)===1?"":"s")+".");
    loadScheduledDraws();
    return true;
  }
  function lineupDrawForStrategy(strategyId,item){
    const record=strategyRecordForId(strategyId,strategyTrust?.records,plays);
    const gameId=record.game||"fantasy5";
    const entries=getDrawStatusService([gameId],new Date(),draws).entries;
    const itemDrawIds=(Array.isArray(item?.draws)?item.draws:[]).map(draw=>String(draw.drawId)).filter(Boolean);
    return entries.find(entry=>itemDrawIds.includes(String(drawLockIdForDraw(entry))))
      ||entries.find(entry=>item?.drawId&&drawLockIdForDraw(entry)===item.drawId)
      ||entries.find(entry=>entry.sortAt>=Date.now())
      ||entries[0]
      ||drawStatus?.next
      ||null;
  }
  function lineupItemEditable(strategyId,item){
    const record=strategyRecordForId(strategyId,strategyTrust?.records,plays);
    const gameId=record.game||"fantasy5";
    const entries=getDrawStatusService([gameId],new Date(),draws).entries;
    const itemDrawIds=(Array.isArray(item?.draws)?item.draws:[]).map(draw=>String(draw.drawId)).filter(Boolean);
    const selectedEntries=itemDrawIds.length?entries.filter(entry=>itemDrawIds.includes(String(drawLockIdForDraw(entry)))):[lineupDrawForStrategy(strategyId,item)].filter(Boolean);
    return selectedEntries.some(entry=>!drawEntryIsClosedOrLater(entry));
  }
  async function removeFromLineup(strategyId){
    const item=normalizeLineup(todaysLineup).items.find(entry=>entry.id===strategyId);
    if(item&&!lineupItemEditable(strategyId,item)){
      toast("This lineup is locked for that draw.");
      return false;
    }
    if(user){
      const{error}=await supabase
        .from("scheduled_strategy_draws")
        .update({status:"cancelled",cancelled_at:new Date().toISOString(),updated_at:new Date().toISOString()})
        .eq("user_id",user.id)
        .eq("strategy_id",strategyId)
        .in("status",["upcoming","cutoff_passed","awaiting_results"]);
      if(error){
        toast("Could not remove from today: "+error.message);
        return false;
      }
      setScheduledDraws(prev=>(prev||[]).map(row=>row.strategy_id===strategyId&&["upcoming","cutoff_passed","awaiting_results"].includes(row.status)?{...row,status:"cancelled",cancelled_at:new Date().toISOString()}:row));
      loadScheduledDraws();
    }
    updateLineup(prev=>{
      const clean=normalizeLineup(prev);
      return{...clean,items:clean.items.filter(item=>item.id!==strategyId)};
    });
    toast("Schedule cancelled.");
    return true;
  }
  function moveLineupItem(strategyId,delta){
    if(drawIsClosedOrLater(drawStatus))return;
    updateLineup(prev=>{
      const clean=normalizeLineup(prev);
      const items=[...clean.items];
      const index=items.findIndex(item=>item.id===strategyId);
      const nextIndex=index+delta;
      if(index<0||nextIndex<0||nextIndex>=items.length)return clean;
      const [item]=items.splice(index,1);
      items.splice(nextIndex,0,item);
      return{...clean,items};
    });
  }
  function setLineupPlayType(strategyId,playType){
    if(drawIsClosedOrLater(drawStatus))return;
    updateLineup(prev=>{
      const clean=normalizeLineup(prev);
      return{...clean,items:clean.items.map(item=>item.id===strategyId?{...item,playType:playType==="Real Play"?"Real Play":"Simulation"}:item)};
    });
  }
  function lockStrategy(strategyId,mode="manual"){
    if(!strategyId)return;
    const lineupItem=normalizeLineup(todaysLineup).items.find(item=>item.id===strategyId);
    const targetDraw=lineupDrawForStrategy(strategyId,lineupItem);
    if(!targetDraw)return;
    const targetDrawId=drawLockIdForDraw(targetDraw);
    const targetDrawLabel=targetDraw.gameLabel+" "+targetDraw.drawLabel;
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,plays);
      const existing=base.records[strategyId]||{};
      const label=strategyDisplayName(strategyId,existing);
      const lockedAt=new Date().toISOString();
      const lockedBeforeClose=["Upcoming","Sales Closing Soon"].includes(targetDraw.status);
      const frozen=freezeStrategyNumbers(plays,strategyId,existing);
      const records={
        ...base.records,
        [strategyId]:{
          ...existing,
          id:strategyId,
          status:"Locked",
          lockedAt,
          lockedDrawId:targetDrawId,
          lockedDrawLabel:targetDrawLabel,
          lockedBeforeClose,
          lockedNumbers:frozen,
          playType:lineupItem?.playType||existing.playType||"Simulation",
          lineupSnapshot:lineupItem?{drawId:targetDrawId,drawLabel:targetDrawLabel,lockedAt,playType:lineupItem.playType,numbers:frozen}:existing.lineupSnapshot,
          simulationOnly:(lineupItem?.playType||existing.playType)!=="Real Play",
        },
      };
      const detail=lockedBeforeClose?"Locked before draw.":"Sales closed. Strategy locked.";
      return{records,events:[strategyEvent("Strategy Locked",strategyId,label,detail),...base.events].slice(0,80)};
    });
    toast(mode==="tonight"?"Strategy locked for tonight.":"Strategy locked.");
  }
  function autoLockActiveStrategies(){
    const cleanLineup=normalizeLineup(todaysLineup);
    if(!cleanLineup.items.length)return;
    const drawId=currentDrawLockId(drawStatus);
    const drawLabel=drawStatus?.next?drawStatus.next.gameLabel+" "+drawStatus.next.drawLabel:"Current draw";
    const lockedAt=new Date().toISOString();
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,plays);
      let changed=false;
      const records={...base.records};
      const events=[...base.events];
      cleanLineup.items.forEach(item=>{
        const strategyId=item.id;
        const itemDraw=lineupDrawForStrategy(strategyId,item);
        if(!drawEntryIsClosedOrLater(itemDraw))return;
        const itemDrawId=item.drawId||drawLockIdForDraw(itemDraw)||drawId;
        const itemDrawLabel=itemDraw?(itemDraw.gameLabel+" "+itemDraw.drawLabel):drawLabel;
        const record=records[strategyId];
        if(!record||record.status==="Archived"||record.status==="Replaced")return;
        if(record.status==="Locked"&&record.lockedDrawId===itemDrawId)return;
        const label=strategyDisplayName(strategyId,record);
        const frozen=freezeStrategyNumbers(plays,strategyId,record);
        records[strategyId]={
          ...record,
          status:"Locked",
          lockedAt,
          lockedDrawId:itemDrawId,
          lockedDrawLabel:itemDrawLabel,
          lockedBeforeClose:false,
          lockedNumbers:frozen,
          playType:item.playType,
          lineupSnapshot:{drawId:itemDrawId,drawLabel:itemDrawLabel,lockedAt,playType:item.playType,numbers:frozen},
          simulationOnly:item.playType!=="Real Play",
        };
        events.unshift(strategyEvent("Strategy Locked",strategyId,label,"Sales Closed. Strategy Locked."));
        changed=true;
      });
      return changed?{records,events:events.slice(0,80)}:prev;
    });
    updateLineup(prev=>{
      const clean=normalizeLineup(prev);
      let snapshots=[...clean.snapshots];
      let changed=false;
      clean.items.forEach(item=>{
        const record=strategyRecordForId(item.id,strategyTrust?.records,plays);
        const scheduledDraws=Array.isArray(item.draws)&&item.draws.length?item.draws:[item.drawId?{
          drawId:item.drawId,
          drawLabel:item.drawLabel,
          drawDate:item.drawDate,
          drawTime:item.drawTime,
          addedAt:item.addedAt,
          playType:item.playType,
        }:null].filter(Boolean);
        scheduledDraws.forEach(draw=>{
          const entry=findScheduledDrawEntry(draw,record,draws,new Date());
          const itemDraw=entry||lineupDrawForStrategy(item.id,{...item,draws:[draw]});
          if(!drawEntryIsClosedOrLater(itemDraw))return;
          const itemDrawId=draw.drawId||drawLockIdForDraw(itemDraw)||drawId;
          if(!itemDrawId)return;
          if(snapshots.some(s=>String(s.drawId)===String(itemDrawId)&&(s.items||[]).some(snapshotItem=>snapshotItem.id===item.id)))return;
          snapshots=upsertLineupSnapshot(snapshots,snapshotForLineupDraw(item,{...draw,drawId:itemDrawId,lockedAt,status:"cutoff passed"},plays,null));
          changed=true;
        });
      });
      return changed?{...clean,snapshots:snapshots.slice(0,60)}:clean;
    });
  }
  function retireStrategy(strategyId){
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,plays);
      const record=base.records[strategyId];
      if(!record)return base;
      const label=strategyDisplayName(strategyId,record);
      const records={...base.records,[strategyId]:{...record,status:"Archived",archivedAt:new Date().toISOString(),simulationOnly:true}};
      return{records,events:[strategyEvent("Strategy Retired",strategyId,label,"Strategy archived. History stays intact."),...base.events].slice(0,80)};
    });
    toast("Strategy archived.");
  }
function createStrategyVersion(strategyId){
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,plays);
      const record=base.records[strategyId];
      if(!record)return base;
      if(!strategyCanModify(record,drawStatus))return base;
      const prior={...record,replacedAt:new Date().toISOString(),status:"Replaced"};
      const version=(Number(record.version)||1)+1;
      const records={
        ...base.records,
        [strategyId]:{
          ...record,
          status:"Draft",
          version,
          createdAt:new Date().toISOString(),
          lockedAt:null,
          lockedDrawId:null,
          lockedDrawLabel:null,
          lockedBeforeClose:false,
          lockedNumbers:[],
          archivedAt:null,
          replacedAt:new Date().toISOString(),
          previousVersions:[...(record.previousVersions||[]),prior].slice(-12),
          simulationOnly:true,
        },
      };
      const label=strategyDisplayName(strategyId,records[strategyId]);
      return{records,events:[strategyEvent("Version Created",strategyId,label,"History begins today. Older version keeps its prior record."),...base.events].slice(0,80)};
    });
    toast("New strategy version created.");
  }
  function uniqueStrategyNumbers(numbers,gameId){
    const seen=new Set();
    return(numbers||[]).filter(nums=>{
      const clean=(nums||[]).map(n=>Number(n));
      const key=(gameId||"fantasy5")+":"+clean.join("-");
      if(seen.has(key))return false;
      seen.add(key);
      return true;
    });
  }
  function playRowKey(row){
    return(row?.game||"fantasy5")+":"+((row?.numbers||[]).map(n=>Number(n)).join("-"));
  }
  function strategyPlayKeyFor(gameId,nums){
    return(gameId||"fantasy5")+":"+((nums||[]).map(n=>Number(n)).join("-"));
  }
  function prepareStrategyNumbersForSave(rawNumbers,gameId){
    const cfg=gameNumberConfig(gameId||"fantasy5");
    const seen=new Set();
    const numbers=[];
    let duplicateCount=0;
    let invalidCount=0;
    (rawNumbers||[]).forEach(nums=>{
      if(!Array.isArray(nums)||nums.length!==cfg.count){
        invalidCount++;
        return;
      }
      const clean=nums.map(n=>Number(n));
      if(clean.some(n=>!Number.isFinite(n))){
        invalidCount++;
        return;
      }
      const key=strategyPlayKeyFor(gameId,clean);
      if(seen.has(key)){
        duplicateCount++;
        return;
      }
      seen.add(key);
      numbers.push(clean);
    });
    return{numbers,duplicateCount,invalidCount,uniqueCount:numbers.length,submittedCount:(rawNumbers||[]).length};
  }
  function prepareStrategyEntriesForSave(rawEntries,gameId){
    const cfg=gameNumberConfig(gameId||"fantasy5");
    const seen=new Set();
    const entries=[];
    let duplicateCount=0;
    let invalidCount=0;
    (rawEntries||[]).forEach(entry=>{
      const nums=previewEntryNumbers(entry);
      if(!Array.isArray(nums)||nums.length!==cfg.count){
        invalidCount++;
        return;
      }
      const clean=nums.map(n=>Number(n));
      if(clean.some(n=>!Number.isFinite(n))){
        invalidCount++;
        return;
      }
      const key=strategyPlayKeyFor(gameId,clean);
      if(seen.has(key)){
        duplicateCount++;
        return;
      }
      seen.add(key);
      entries.push(isPickGame(gameId)?makePreviewEntryForGame(gameId,clean,pickLineRulesFromItem(entry)?.playType,pickLineRulesFromItem(entry)?.wager,pickLineRulesFromItem(entry)?.fireball,pickLineRulesFromItem(entry)?.multiple):clean);
    });
    return{entries,numbers:entries.map(entry=>previewEntryNumbers(entry)),duplicateCount,invalidCount,uniqueCount:entries.length,submittedCount:(rawEntries||[]).length};
  }
  function strategyVersionPayload(strategyId,form,numbers,versionNumber,reason){
    const snapshot=strategyOutcomeSnapshot({
      strategyId,
      game:form.game||"fantasy5",
      plays:numbers,
      pickRules:form.pickRules||null,
      source:form.source||"Manual",
      version:versionNumber,
    });
    const estimatedCost=isPickGame(form.game)?Number(form.pickRules?.cost)||numbers.length:numbers.length;
    return{
      user_id:user.id,
      strategy_id:strategyId,
      version_number:versionNumber,
      game:form.game||"fantasy5",
      strategy_name:(form.name||strategyBaseLabel(strategyId)).trim(),
      snapshot,
      fingerprint:strategyFingerprint(snapshot),
      play_count:numbers.length,
      estimated_cost:estimatedCost,
      created_reason:reason||"manual_version",
      is_active:false,
      created_at:new Date().toISOString(),
      updated_at:new Date().toISOString(),
    };
  }
  function strategyVersionIntegrity(payload,row=null){
    const snapshot=row?.snapshot||payload?.snapshot||{};
    const numbers=strategySnapshotNumbers(snapshot);
    const uniqueCount=new Set(numbers.map(nums=>strategyPlayKeyFor(payload?.game||row?.game||snapshot.game,nums))).size;
    const fingerprint=strategyFingerprint(snapshot);
    const rulesCost=Number(snapshot?.pickRules?.cost);
    const snapshotCost=Number(snapshot?.estimatedCost);
    const expectedCost=Number.isFinite(rulesCost)?rulesCost:(Number.isFinite(snapshotCost)?snapshotCost:numbers.length);
    return{
      snapshotCount:numbers.length,
      uniqueCount,
      playCount:Number(row?.play_count??payload?.play_count)||0,
      estimatedCost:Number(row?.estimated_cost??payload?.estimated_cost)||0,
      calculatedCost:expectedCost,
      fingerprint,
      savedFingerprint:row?.fingerprint||payload?.fingerprint||null,
      game:row?.game||payload?.game||snapshot.game||"fantasy5",
      pickRules:snapshot.pickRules||null,
    };
  }
  function assertStrategyVersionIntegrity(payload,row=null){
    const report=strategyVersionIntegrity(payload,row);
    const expectedCount=Number(payload?.play_count)||0;
    const expectedCost=Number(payload?.estimated_cost)||0;
    const problems=[];
    if(report.snapshotCount!==expectedCount)problems.push("snapshot has "+report.snapshotCount+" plays, expected "+expectedCount);
    if(report.uniqueCount!==expectedCount)problems.push("unique play count is "+report.uniqueCount+", expected "+expectedCount);
    if(report.playCount!==expectedCount)problems.push("saved play_count is "+report.playCount+", expected "+expectedCount);
    if(Math.abs(report.estimatedCost-expectedCost)>0.001)problems.push("saved cost is "+report.estimatedCost+", expected "+expectedCost);
    if(Math.abs(report.calculatedCost-expectedCost)>0.001)problems.push("calculated cost is "+report.calculatedCost+", expected "+expectedCost);
    if(report.savedFingerprint!==payload?.fingerprint)problems.push("saved fingerprint does not match expected fingerprint");
    if(report.fingerprint!==payload?.fingerprint)problems.push("snapshot fingerprint recalculation does not match");
    if(problems.length)throw new Error("Strategy integrity check failed: "+problems.join("; "));
    return report;
  }
  async function readStrategyVersionRow(strategyId,versionNumber){
    return supabase
      .from("strategy_versions")
      .select("user_id,strategy_id,version_number,game,strategy_name,snapshot,fingerprint,play_count,estimated_cost,created_reason,created_at,updated_at,is_active")
      .eq("user_id",user.id)
      .eq("strategy_id",strategyId)
      .eq("version_number",versionNumber)
      .maybeSingle();
  }
  async function selectAllUserPlaysForStrategy(userId,strategyId){
    return selectUserPlayRowsPaged(()=>supabase
      .from("user_plays")
      .select("*")
      .eq("user_id",userId)
      .eq("strategy",strategyId)
      .order("created_at",{ascending:true}));
  }
  async function finalizeVerifiedStrategyVersion(strategyId,versionNumber){
    const{error}=await supabase.rpc("finalize_strategy_version",{
      p_strategy_id:strategyId,
      p_version_number:versionNumber,
    });
    if(error)throw new Error("Could not finalize verified version: "+error.message);
  }
  async function saveVerifiedStrategyReplacement({strategyId,form,numbers,versionNumber,reason,replaceExisting=false}){
    const payload=strategyVersionPayload(strategyId,form,numbers,versionNumber,reason);
    assertStrategyVersionIntegrity(payload,payload);
    const saveQuery=replaceExisting
      ?supabase.from("strategy_versions").upsert(payload,{onConflict:"user_id,strategy_id,version_number"})
      :supabase.from("strategy_versions").insert(payload);
    const insert=await saveQuery
      .select("user_id,strategy_id,version_number,game,strategy_name,snapshot,fingerprint,play_count,estimated_cost,created_reason,created_at,updated_at,is_active")
      .maybeSingle();
    if(insert.error)throw new Error("Could not save strategy version: "+insert.error.message);
    const readBack=await readStrategyVersionRow(strategyId,versionNumber);
    if(readBack.error)throw new Error("Could not verify saved strategy version: "+readBack.error.message);
    if(!readBack.data)throw new Error("Saved strategy version was not found during verification.");
    assertStrategyVersionIntegrity(payload,readBack.data);
    await finalizeVerifiedStrategyVersion(strategyId,versionNumber);
    const materialized=await selectAllUserPlaysForStrategy(user.id,strategyId);
    if(materialized.error)throw new Error("Could not verify materialized plays: "+materialized.error.message);
    const materializedRows=(materialized.data||[]).map(row=>({...row,numbers:normalizeUserPlayNumbers(row.numbers)}));
    if(materializedRows.length!==numbers.length)throw new Error("Materialized row count mismatch after finalize.");
    const active=await supabase
      .from("strategy_versions")
      .select("user_id,strategy_id,version_number,game,strategy_name,snapshot,fingerprint,play_count,estimated_cost,created_reason,created_at,updated_at,is_active")
      .eq("user_id",user.id)
      .eq("strategy_id",strategyId)
      .eq("is_active",true)
      .maybeSingle();
    if(active.error)throw new Error("Could not verify active strategy version: "+active.error.message);
    if(Number(active.data?.version_number)!==Number(versionNumber))throw new Error("The verified version was not activated.");
    assertStrategyVersionIntegrity(payload,active.data);
    return{payload,active:active.data,materializedRows};
  }
  function versionChangeReason(previousRecord,form,oldNumbers,newNumbers){
    if(!previousRecord)return"initial_create";
    if((previousRecord.game||"fantasy5")!==(form.game||"fantasy5"))return"game_changed";
    const oldSnapshot=strategyOutcomeSnapshot({
      strategyId:previousRecord.id,
      game:previousRecord.game||"fantasy5",
      plays:oldNumbers,
      pickRules:previousRecord.pickRules||null,
      source:previousRecord.source||"Manual",
      version:Number(previousRecord.version)||1,
    });
    const newSnapshot=strategyOutcomeSnapshot({
      strategyId:previousRecord.id,
      game:form.game||"fantasy5",
      plays:newNumbers,
      pickRules:form.pickRules||null,
      source:form.source||previousRecord.source||"Manual",
      version:Number(previousRecord.version)||1,
    });
    if(strategyFingerprint(oldSnapshot)===strategyFingerprint(newSnapshot))return null;
    if(JSON.stringify(stableStrategyValue(previousRecord.pickRules||null))!==JSON.stringify(stableStrategyValue(form.pickRules||null)))return"rules_changed";
    if(oldNumbers.length!==newNumbers.length)return"play_structure_changed";
    return"numbers_changed";
  }
  function savedStrategyCount(){
    return savedStrategyIdsFromState(strategyTrust,plays).length;
  }
  async function lifetimeStrategyCount(){
    if(!user?.id)return savedStrategyCount();
    const{count,error}=await supabase
      .from("strategy_versions")
      .select("strategy_id",{count:"exact",head:true})
      .eq("user_id",user.id);
    if(error){
      recordBetaMetric("api_failed",{label:"strategy-lifetime-count",message:error.message});
      return savedStrategyCount();
    }
    return Number(count)||0;
  }
  function canCreateAnotherStrategy(){
    return subscriptionHas(subscription,"pro")||savedStrategyCount()<FREE_MAX_STRATEGIES;
  }
  function blockFreeStrategyLimit(){
    showFreeLimitDialog("strategies");
    toast("Free accounts can save up to "+FREE_MAX_STRATEGIES+" strategies. Pro unlocks unlimited strategies.");
  }
  function handleFreeLimitSaveError(message){
    const text=String(message||"");
    if(/one selected Pick game|unlock all Pick games|Fantasy 5 plus one Pick game/i.test(text)){
      showFreeLimitDialog("pick_game");
      return true;
    }
    if(/save up to 3 strategies|unlock unlimited strategies/i.test(text)){
      showFreeLimitDialog("strategies");
      return true;
    }
    return false;
  }
  async function createSavedStrategy(form,previewNumbers,lock=false,options={}){
    if(!user)return null;
    if(!gameAllowedForPlan(form?.game||"fantasy5",myGames,planTier,trialActive)){
      showFreeLimitDialog("pick_game");
      toast("Free accounts include Fantasy 5 plus one Pick game. Pro unlocks all Pick games.");
      return null;
    }
    if(!canCreateAnotherStrategy()){
      blockFreeStrategyLimit();
      return null;
    }
    const rateKey="strategy-create:"+(user.id||user.email);
    const limit=rateLimitStatus(rateKey,8,15*60*1000);
    if(!limit.allowed){
      toast("Please wait before creating another strategy. Try again in "+limit.retryText+".");
      return null;
    }
    const name=(form.name||"New Strategy").trim()||"New Strategy";
    const id=uniqueStrategyId(name,strategyTrust.records);
    const rawNumbers=(previewNumbers&&previewNumbers.length?previewNumbers:buildStrategyPreview(form.source,form.playSize,plays,draws)).slice(0,Number(form.playSize)||5);
    const prepared=prepareStrategyEntriesForSave(rawNumbers,form.game||"fantasy5");
    let numbers=prepared.entries;
    if(numbers.length>STRATEGY_MAX_PLAYS){
      numbers=numbers.slice(0,STRATEGY_MAX_PLAYS);
      toast("Saved the first "+STRATEGY_MAX_PLAYS.toLocaleString()+" unique plays. Larger pool wheels are coming later.");
    }
    if(!numbers.length){toast("Add at least one valid play before saving.");return null;}
    if(prepared.duplicateCount||prepared.invalidCount){
      toast("Import check: submitted "+prepared.submittedCount.toLocaleString()+" plays; "+numbers.length.toLocaleString()+" unique valid plays will be saved. Removed "+prepared.duplicateCount.toLocaleString()+" duplicate and "+prepared.invalidCount.toLocaleString()+" invalid play"+((prepared.duplicateCount+prepared.invalidCount)===1?".":"s."));
    }
    const countBeforeSave=await lifetimeStrategyCount();
    const saveForm={...form,pickRules:isPickGame(form.game)?pickStrategyRulesFromPreview(form.game,numbers,form.pickRules?.playType,form.pickRules?.wager,form.pickRules?.fireball,form.pickRules?.multiple):null};
    let saveResult;
    try{
      saveResult=await saveVerifiedStrategyReplacement({strategyId:id,form:{...saveForm,name},numbers,versionNumber:1,reason:"initial_create"});
    }catch(error){
      recordBetaMetric("api_failed",{label:"strategy-safe-create",message:error.message});
      if(handleFreeLimitSaveError(error.message)){
        toast("Strategy was not saved. Review your Free plan limits.");
        return null;
      }
      toast("Strategy was not saved. "+error.message);
      return null;
    }
    commitRateLimitAction(rateKey,8,15*60*1000);
    const savedRows=saveResult.materializedRows;
    setPlays(p=>[...p,...savedRows]);
    ensureGameVisibleForStrategy(saveForm.game||"fantasy5");
    const nowIso=new Date().toISOString();
    const versionPayload=saveResult.payload;
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,[...plays,...savedRows]);
      const locked=lock&&drawStatus?.next;
      const record={
        ...(base.records[id]||{}),
        id,
        status:locked?"Locked":"Draft",
        baseName:name,
        version:1,
        game:saveForm.game||"fantasy5",
        source:saveForm.source||"Manual",
        preferredDraw:saveForm.preferredDraw||"evening",
        playSize:savedRows.length,
        pickRules:saveForm.pickRules||null,
        fingerprint:versionPayload.fingerprint,
        outcomeSnapshot:versionPayload.snapshot,
        sourceOfTruth:"strategy_versions",
        estimatedCost:Number(versionPayload.estimated_cost)||savedRows.length,
        createdAt:nowIso,
        updatedAt:nowIso,
        lockedAt:locked?nowIso:null,
        lockedDrawId:locked?currentDrawLockId(drawStatus):null,
        lockedDrawLabel:locked?drawStatus.next.gameLabel+" "+drawStatus.next.drawLabel:null,
        lockedBeforeClose:locked?drawIsBeforeClose(drawStatus):false,
        lockedNumbers:locked?savedRows.map(r=>({playId:r.id,numbers:[...r.numbers],anchorNumbers:null,createdAt:r.created_at||nowIso})):[],
        simulationOnly:!locked,
        published:false,
      };
      return{
        records:{...base.records,[id]:record},
        events:[
          ...(locked?[strategyEvent("Strategy Locked",id,name,drawIsBeforeClose(drawStatus)?"Locked before draw.":"Sales closed. Strategy locked.")] : []),
          strategyEvent("Strategy Created",id,name,"Created in Play Builder."),
          ...base.events,
        ].slice(0,80),
      };
    });
    toast(lock?"Strategy saved and locked.":"Strategy saved. Integrity check: "+savedRows.length.toLocaleString()+" plays verified.");
    loadPlays(planTier);
    return options?.returnMeta?{id,firstStrategy:countBeforeSave===0,countBeforeSave}:id;
  }

  async function updateSavedStrategy(strategyId,form,previewNumbers){
    if(!user||!strategyId)return null;
    const existingRecord=strategyTrust.records?.[strategyId];
    if(!gameAllowedForPlan(form?.game||existingRecord?.game||"fantasy5",myGames,planTier,trialActive)){
      showFreeLimitDialog("pick_game");
      toast("Free accounts include Fantasy 5 plus one Pick game. Pro unlocks all Pick games.");
      return null;
    }
    if(!strategyCanModify(existingRecord,drawStatus)){
      toast("This strategy is locked until the draw finishes.");
      return null;
    }
    const oldNumbers=isPickGame(existingRecord?.game||form.game||"fantasy5")?strategySnapshotPlayEntries(existingRecord):strategyNumbersForRecord(strategyId,existingRecord,plays);
    const prepared=prepareStrategyEntriesForSave(previewNumbers||[],form.game||"fantasy5");
    let numbers=prepared.entries;
    if(!numbers.length){toast("Add at least one play before saving.");return null;}
    if(numbers.length>STRATEGY_MAX_PLAYS){
      numbers=numbers.slice(0,STRATEGY_MAX_PLAYS);
      toast("Saved the first "+STRATEGY_MAX_PLAYS.toLocaleString()+" unique plays. Larger pool wheels are coming later.");
    }
    if(prepared.duplicateCount||prepared.invalidCount){
      toast("Edit check: submitted "+prepared.submittedCount.toLocaleString()+" plays; "+numbers.length.toLocaleString()+" unique valid plays will be saved. Removed "+prepared.duplicateCount.toLocaleString()+" duplicate and "+prepared.invalidCount.toLocaleString()+" invalid play"+((prepared.duplicateCount+prepared.invalidCount)===1?".":"s."));
    }
    const saveForm={...form,pickRules:isPickGame(form.game)?pickStrategyRulesFromPreview(form.game,numbers,form.pickRules?.playType,form.pickRules?.wager,form.pickRules?.fireball,form.pickRules?.multiple):null};
    const reason=versionChangeReason({...existingRecord,id:strategyId},saveForm,oldNumbers,numbers);
    if(!reason){
      const nextName=(saveForm.name||existingRecord?.baseName||strategyBaseLabel(strategyId)).trim();
      const currentName=(existingRecord?.baseName||strategyBaseLabel(strategyId)).trim();
      if(nextName&&nextName!==currentName){
        await renameStrategy(strategyId,nextName);
        toast("Strategy renamed. No new version was needed.");
        return strategyId;
      }
      toast("No changes detected.");
      return strategyId;
    }
    const currentVersion=Number(existingRecord?.version)||1;
    const isOnboardingContinuation=!!saveForm.onboardingContinuation&&currentVersion===1;
    const nextVersion=isOnboardingContinuation?1:currentVersion+1;
    let saveResult;
    try{
      saveResult=await saveVerifiedStrategyReplacement({
        strategyId,
        form:saveForm,
        numbers,
        versionNumber:nextVersion,
        reason:isOnboardingContinuation?"onboarding_continue":reason,
        replaceExisting:isOnboardingContinuation,
      });
    }catch(error){
      recordBetaMetric("api_failed",{label:"strategy-safe-update",message:error.message});
      if(handleFreeLimitSaveError(error.message)){
        toast("Edit was not saved. Review your Free plan limits.");
        return null;
      }
      toast("Edit was not saved. Previous version is intact. "+error.message);
      return null;
    }
    const reloadedRows=saveResult.materializedRows;
    setPlays(p=>[...p.filter(play=>strategyIdForPlay(play)!==strategyId),...reloadedRows]);
    ensureGameVisibleForStrategy(saveForm.game||"fantasy5");
    const nowIso=new Date().toISOString();
    const versionPayload=saveResult.payload;
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,[...plays.filter(play=>strategyIdForPlay(play)!==strategyId),...reloadedRows]);
      const record=base.records[strategyId]||{};
      const previousVersions=isOnboardingContinuation
        ?(record.previousVersions||[])
        :[...(record.previousVersions||[]),{...record,retiredAt:nowIso,status:"Replaced"}].slice(-12);
      return{
        records:{
          ...base.records,
          [strategyId]:{
            ...record,
            id:strategyId,
            baseName:(saveForm.name||record.baseName||strategyBaseLabel(strategyId)).trim(),
            game:saveForm.game||record.game||"fantasy5",
            source:saveForm.source||record.source||"Manual",
            preferredDraw:saveForm.preferredDraw||record.preferredDraw||"evening",
            playSize:reloadedRows.length,
            pickRules:saveForm.pickRules||null,
            version:nextVersion,
            fingerprint:versionPayload.fingerprint,
            outcomeSnapshot:versionPayload.snapshot,
            sourceOfTruth:"strategy_versions",
            estimatedCost:Number(versionPayload.estimated_cost)||reloadedRows.length,
            previousVersions,
            updatedAt:nowIso,
          },
        },
        events:[strategyEvent("Version Created",strategyId,saveForm.name||strategyDisplayName(strategyId,record),"Verified edit created v"+nextVersion+"."),...base.events].slice(0,80),
      };
    });
    toast((isOnboardingContinuation?"Playslip saved as v1.":"Strategy updated.")+" Integrity check: "+oldNumbers.length.toLocaleString()+" old plays, "+numbers.length.toLocaleString()+" submitted, "+reloadedRows.length.toLocaleString()+" reloaded.");
    loadPlays(planTier);
    return strategyId;
  }
  async function renameStrategy(strategyId,nextName){
    if(!user||!strategyId)return false;
    const name=String(nextName||"").trim();
    if(!name)return false;
    const nowIso=new Date().toISOString();
    await supabase.from("strategy_versions")
      .update({strategy_name:name,updated_at:nowIso})
      .eq("user_id",user.id)
      .eq("strategy_id",strategyId)
      .eq("is_active",true);
    let publicRefreshWarning=false;
    try{
      await refreshPublicStrategyShareTitle(strategyId,name);
    }catch(error){
      publicRefreshWarning=true;
      recordBetaMetric("api_failed",{label:"refresh-strategy-share-title",message:error.message});
    }
    updateStrategyTrust(prev=>{
      const fresh=ensureStrategyTrust(prev,plays);
      const existing=fresh.records[strategyId]||{};
      const records={...fresh.records};
      records[strategyId]={...existing,id:strategyId,baseName:name,updatedAt:nowIso};
      return{records,events:[strategyEvent("Strategy Renamed",strategyId,name,"Strategy name updated."),...fresh.events].slice(0,80)};
    });
    toast(publicRefreshWarning?"Strategy renamed. Public share title refresh needs another try.":"Strategy renamed.");
    return strategyId;
  }
  async function duplicateStrategy(strategyId){
    if(!user||!strategyId)return;
    const base=ensureStrategyTrust(strategyTrust,plays);
    const record=base.records[strategyId];
    if(!record){toast("No saved strategy to duplicate.");return;}
    if(!gameAllowedForPlan(record.game||"fantasy5",myGames,planTier,trialActive)){
      showFreeLimitDialog("pick_game");
      toast("Free accounts include Fantasy 5 plus one Pick game. Pro unlocks all Pick games.");
      return;
    }
    let numbers=isPickGame(record.game||"fantasy5")?strategySnapshotPlayEntries(record):strategyNumbersForRecord(strategyId,record,plays);
    if(!numbers.length){toast("No saved plays to duplicate.");return;}
    if(!canCreateAnotherStrategy()){
      blockFreeStrategyLimit();
      return;
    }
    if(numbers.length>STRATEGY_MAX_PLAYS){
      numbers=numbers.slice(0,STRATEGY_MAX_PLAYS);
      toast("Duplicated the first "+STRATEGY_MAX_PLAYS.toLocaleString()+" unique plays.");
    }
    const name=(record.baseName||strategyBaseLabel(strategyId))+" Copy";
    const id=uniqueStrategyId(name,base.records);
    let saveResult;
    const duplicateForm={
      name,
      game:record.game||"fantasy5",
      source:record.source||"Manual",
      pickRules:isPickGame(record.game||"fantasy5")?pickStrategyRulesFromPreview(record.game||"fantasy5",numbers,record.pickRules?.playType,record.pickRules?.wager,record.pickRules?.fireball,record.pickRules?.multiple):(record.pickRules||null),
    };
    try{
      saveResult=await saveVerifiedStrategyReplacement({strategyId:id,form:duplicateForm,numbers,versionNumber:1,reason:"initial_create"});
    }catch(error){
      recordBetaMetric("api_failed",{label:"strategy-safe-duplicate",message:error.message});
      if(handleFreeLimitSaveError(error.message)){
        toast("Duplicate was not saved. Review your Free plan limits.");
        return;
      }
      toast("Duplicate was not saved. "+error.message);
      return;
    }
    const nowIso=new Date().toISOString();
    const savedRows=saveResult.materializedRows;
    if(!savedRows.length){
      toast("No duplicate plays were saved.");
      return;
    }
    setPlays(p=>[...p,...savedRows]);
    const versionPayload=saveResult.payload;
    updateStrategyTrust(prev=>{
      const refreshed=ensureStrategyTrust(prev,[...plays,...savedRows]);
      const sourceRecord=refreshed.records[strategyId]||record;
      const copied={
        ...sourceRecord,
        id,
        baseName:name,
        status:"Draft",
        version:1,
        game:sourceRecord.game||record.game||"fantasy5",
        playSize:savedRows.length,
        estimatedCost:Number(versionPayload.estimated_cost)||savedRows.length,
        createdAt:nowIso,
        updatedAt:nowIso,
        lockedAt:null,
        lockedDrawId:null,
        lockedDrawLabel:null,
        lockedBeforeClose:false,
        lockedNumbers:[],
        archivedAt:null,
        replacedAt:null,
        published:false,
        simulationOnly:true,
        previousVersions:[],
        fingerprint:versionPayload.fingerprint,
        outcomeSnapshot:versionPayload.snapshot,
        sourceOfTruth:"strategy_versions",
      };
      return{
        records:{...refreshed.records,[id]:copied},
        events:[strategyEvent("Strategy Created",id,name,"Duplicated from "+strategyDisplayName(strategyId,sourceRecord)+"."),...refreshed.events].slice(0,80),
      };
    });
    toast("Strategy duplicated. Integrity check: "+savedRows.length.toLocaleString()+" plays verified.");
  }
  async function deleteStrategy(strategyId){
    if(!strategyId)return;
    if(!window.confirm("Delete this strategy and its saved plays?"))return;
    const nowIso=new Date().toISOString();
    const versionUpdate=await supabase.from("strategy_versions")
      .update({is_active:false,retired_at:nowIso,updated_at:nowIso})
      .eq("user_id",user.id)
      .eq("strategy_id",strategyId)
      .eq("is_active",true)
      .is("retired_at",null)
      .select("id,strategy_id,strategy_name");
    if(versionUpdate.error){
      toast("Error deleting strategy: "+versionUpdate.error.message);
      return;
    }
    const retiredRows=versionUpdate.data||[];
    if(!retiredRows.length){
      toast("Strategy was already deleted or could not be found.");
      await loadPlays(planTier);
      return;
    }
    const{error}=await supabase.from("user_plays").delete().eq("user_id",user.id).eq("strategy",strategyId);
    if(error){
      await supabase.from("strategy_versions")
        .update({is_active:true,retired_at:null,updated_at:new Date().toISOString()})
        .eq("user_id",user.id)
        .eq("strategy_id",strategyId)
        .in("id",retiredRows.map(row=>row.id));
      toast("Error deleting strategy: "+error.message);
      await loadPlays(planTier);
      return;
    }
    await supabase.from("scheduled_strategy_draws")
      .update({status:"cancelled",cancelled_at:nowIso,updated_at:nowIso})
      .eq("user_id",user.id)
      .eq("strategy_id",strategyId)
      .in("status",["upcoming","cutoff_passed","awaiting_results"]);
    setScheduledDraws(prev=>(prev||[]).map(row=>row.strategy_id===strategyId&&["upcoming","cutoff_passed","awaiting_results"].includes(row.status)?{...row,status:"cancelled",cancelled_at:new Date().toISOString()}:row));
    setPlays(p=>p.filter(play=>strategyIdForPlay(play)!==strategyId));
    updateLineup(prev=>{
      return removeStrategyFromLineupData(prev,strategyId);
    });
    updateStrategyTrust(prev=>{
      const base=normalizeStrategyTrust(prev);
      const records={...base.records};
      const label=strategyDisplayName(strategyId,records[strategyId]);
      delete records[strategyId];
      return{records,events:[strategyEvent("Strategy Deleted",strategyId,label,"Strategy and saved plays deleted."),...base.events].slice(0,80)};
    });
    toast("Strategy deleted.");
  }
  function publishStrategy(strategyId){
    const current=strategyTrust.records?.[strategyId];
    if(!lineupIdSet(todaysLineup).has(strategyId)){
      toast("Add this strategy to Today's Lineup before publishing.");
      return;
    }
    if(current?.status!=="Locked"){
      toast("Only locked strategies can be published.");
      return;
    }
    updateStrategyTrust(prev=>{
      const base=ensureStrategyTrust(prev,plays);
      const record=base.records[strategyId];
      if(!record)return base;
      const label=strategyDisplayName(strategyId,record);
      const records={...base.records,[strategyId]:{...record,published:true,updatedAt:new Date().toISOString()}};
      return{records,events:[strategyEvent("Strategy Published",strategyId,label,"Published from Play Builder."),...base.events].slice(0,80)};
    });
    toast("Strategy ready for public surfaces.");
  }
  function onboardingHasActivity(data){
    const counts=data?.activity?.counts||{};
    return Object.values(counts).some(count=>Number(count)>0);
  }
  async function completeSetup(games,strategy,path,numbers){
    if(games&&typeof games==="object"&&!Array.isArray(games)){
      const setup=games;
      if(setup.strategyId){
        if(setup.intent==="live")await addToLineup(setup.strategyId,{trackingMode:"live"});
        if(setup.intent==="watch")await addToLineup(setup.strategyId,{trackingMode:"watch"});
        await onboardingRequest("first_choice",{step:"complete"}).catch(()=>{});
        await onboardingRequest("complete",{step:"complete"}).catch(()=>{});
        const key=setupKey();
        if(key)localStorage.setItem(key,"true");
        setSetupDone(true);
        setSetupChecked(true);
        setAppStartState("authenticated_app");
        const destination=setup.intent==="another"?"/strategy-builder":setup.path||"/my-strategies";
        toast("Congratulations! You built your first strategy.");
        navigate(destination,{replace:true});
        if(destination!=="/")setTimeout(()=>navigate(destination,{replace:true}),0);
        return true;
      }
      const preflight=await onboardingRequest("status").catch(()=>null);
      if(!preflight){
        toast("We could not verify your account setup. Please try again.");
        return false;
      }
      if(setup.intent==="draft"){
        const allowed=enforceGameAccess(setup.games,planTier,trialActive);
        setMyGames(allowed);
        const gKey=myGamesKey();
        if(gKey)localStorage.setItem(gKey,JSON.stringify(allowed));
        const allowedPick=freePickGame(allowed);
        if(allowedPick)await saveFreePickEntitlement(allowedPick);
        const sKey=strategyKey();
        if(sKey)localStorage.setItem(sKey,setup.source||"Manual");
        const strategyGame=gameAllowedForPlan(setup.strategyGame,allowed,planTier,trialActive)?(setup.strategyGame||freePickGame(allowed)||"fantasy5"):(freePickGame(allowed)||"fantasy5");
        const cfg=gameNumberConfig(strategyGame);
        const checkedResult=validateNumberBoxes(numberBoxValuesFromNumbers(setup.numbers||[],cfg.count),cfg);
        const checked=checkedResult.error?null:(strategyGame==="fantasy5"?[...checkedResult.numbers].sort((a,b)=>a-b):checkedResult.numbers);
        if(!checked){toast("Save valid "+gameLabel(strategyGame)+" numbers before continuing.");return false;}
        const strategyId=await createSavedStrategy({
          name:setup.strategyName||"My First Strategy",
          game:strategyGame,
          source:setup.source||"Manual",
          playSize:1,
        },[checked],false);
        if(!strategyId)return false;
        await onboardingRequest("first_strategy",{step:"choose_intent"}).catch(()=>{});
        return strategyId;
      }
      if(preflight&&(!preflight.shouldShowOnboarding||onboardingHasActivity(preflight))){
        const key=setupKey();
        if(key)localStorage.setItem(key,"true");
        setSetupDone(true);
        setSetupChecked(true);
        setAppStartState("authenticated_app");
        toast("Welcome back. Your strategies are ready.");
        navigate("/my-strategies",{replace:true});
        return true;
      }
      const allowed=enforceGameAccess(setup.games,planTier,trialActive);
      setMyGames(allowed);
      const gKey=myGamesKey();
      if(gKey)localStorage.setItem(gKey,JSON.stringify(allowed));
      const allowedPick=freePickGame(allowed);
      if(allowedPick)await saveFreePickEntitlement(allowedPick);
      const sKey=strategyKey();
      if(sKey)localStorage.setItem(sKey,setup.source||"Manual");
      const strategyGame=gameAllowedForPlan(setup.strategyGame,allowed,planTier,trialActive)?(setup.strategyGame||freePickGame(allowed)||"fantasy5"):(freePickGame(allowed)||"fantasy5");
      const cfg=gameNumberConfig(strategyGame);
      const checkedResult=validateNumberBoxes(numberBoxValuesFromNumbers(setup.numbers||[],cfg.count),cfg);
      const checked=checkedResult.error?null:(strategyGame==="fantasy5"?[...checkedResult.numbers].sort((a,b)=>a-b):checkedResult.numbers);
      if(!checked){toast("Save valid "+gameLabel(strategyGame)+" numbers before continuing.");return false;}
      const strategyId=await createSavedStrategy({
        name:setup.strategyName||"My First Strategy",
        game:strategyGame,
        source:setup.source||"Manual",
        playSize:1,
      },[checked],false);
      if(!strategyId)return false;
      await onboardingRequest("first_strategy",{step:"choose_intent"}).catch(()=>{});
      if(setup.intent==="live")await addToLineup(strategyId,{trackingMode:"live"});
      if(setup.intent==="watch")await addToLineup(strategyId,{trackingMode:"watch"});
      await onboardingRequest("first_choice",{step:"complete"}).catch(()=>{});
      await onboardingRequest("complete",{step:"complete"}).catch(()=>{});
      const key=setupKey();
      if(key)localStorage.setItem(key,"true");
      setSetupDone(true);
      setSetupChecked(true);
      setAppStartState("authenticated_app");
      const destination=setup.intent==="another"?"/strategy-builder":setup.path||"/my-strategies";
      toast("Congratulations! You built your first strategy.");
      navigate(destination,{replace:true});
      if(destination!=="/")setTimeout(()=>navigate(destination,{replace:true}),0);
      return true;
    }
    const allowed=enforceGameAccess(games,planTier,trialActive);
    setMyGames(allowed);
    const gKey=myGamesKey();
    if(gKey)localStorage.setItem(gKey,JSON.stringify(allowed));
    const sKey=strategyKey();
    if(sKey){
      if(strategy)localStorage.setItem(sKey,strategy);
      else localStorage.removeItem(sKey);
    }
    if(numbers){
      const sorted=[...numbers].sort((a,b)=>a-b);
      const{data,error}=await supabase.from("user_plays").insert({user_id:user.id,game:allowed[0]||"fantasy5",numbers:sorted}).select().single();
      if(error){toast("Error saving numbers: "+error.message);return false;}
      if(data)setPlays(p=>[...p,{...data,numbers:sorted}]);
      toast("🎉 First numbers saved");
      showFirstWin();
      return true;
    }
    const key=setupKey();
    if(key)localStorage.setItem(key,"true");
    setSetupDone(true);
    toast("Dashboard setup complete.");
    navigate(path||"/",{replace:true});
    return true;
  }
  // Persist an anchor set as a RECIPE row (numbers=null, anchor_numbers=[...]).
  // Scoring expands it to all combinations in memory at read time — see
  // aggregateStrategies on the client and _lib/match.js on the server.
  async function persistAnchorSet(strategy,anchors){
    if(!user) return{error:"Not signed in"};
    if(!strategyCanModify(strategyTrust.records?.[strategy],drawStatus)){
      return{error:"This strategy is locked until the draw finishes."};
    }
    const sorted=[...anchors].sort((a,b)=>a-b);
    const key=sorted.join(",");
    const dup=plays.find(p=>p.strategy===strategy&&Array.isArray(p.anchor_numbers)&&
      [...p.anchor_numbers].sort((a,b)=>a-b).join(",")===key);
    if(dup) return{error:"You've already saved this anchor set."};
    const{data,error}=await supabase.from("user_plays").insert({
      user_id:user.id,game:"fantasy5",numbers:null,strategy,anchor_numbers:sorted,
    }).select().single();
    if(error) return{error:error.message};
    setPlays(p=>[...p,{...data,numbers:null,anchor_numbers:sorted}]);
    if(strategyTrust.records?.[strategy]?.status==="Locked"&&strategyCanModify(strategyTrust.records?.[strategy],drawStatus)){
      createStrategyVersion(strategy);
    }
    toast("Anchor set saved to scoreboard.");
    return{data};
  }
  async function signOut(){
    authUserIdRef.current="";
    await supabase.auth.signOut();
    setUser(null);
    setPlays([]);
  }
  if(!checked)return <LoadingSkeleton kind="app"/>;
  if(location.pathname.startsWith("/s/"))return <>{user&&<><style>{CSS}{POLISH_CSS}</style><MemberHeader isAdmin={isAdmin} onFeedback={()=>setFeedbackOpen(true)} onSignOut={signOut}/></>}{user?<main className="page"><PublicSharePreview path={location.pathname} user={user}/></main>:<PublicSharePreview path={location.pathname} user={user}/>}<VersionRefreshGuard/>{user&&feedbackOpen&&<FeedbackModal user={user} onClose={()=>setFeedbackOpen(false)}/>}</>;
  if(!user&&location.pathname==='/'&&!['login','signup'].includes(new URLSearchParams(location.search).get('auth')))return <><PublicLanding/><VersionRefreshGuard/></>;
  if(!user)return(<><style>{CSS}{POLISH_CSS}</style><AuthPage onAuth={handleAuthComplete}/><VersionRefreshGuard/></>);
  if(appStartState==="error")return(<><style>{CSS}{POLISH_CSS}</style><MemberHeader isAdmin={isAdmin} onFeedback={()=>setFeedbackOpen(true)} onSignOut={signOut}/><main className="page"><ErrorFallback onReset={()=>{setAppStartState("resolving_account");initUser();}}/></main><Toast msg={toastMsg}/><VersionRefreshGuard/>{feedbackOpen&&<FeedbackModal user={user} onClose={()=>setFeedbackOpen(false)}/>}</>);
  if(!setupChecked)return <LoadingSkeleton kind="app"/>;
  if(LEGACY_ONBOARDING_ENABLED&&!setupDone)return(<><style>{CSS}{POLISH_CSS}</style><SetupWizard initialGames={myGames} planTier={planTier} user={user} initialStep={onboardingInitialStep} onProgress={saveOnboardingProgress} onComplete={completeSetup}/><Toast msg={toastMsg}/><VersionRefreshGuard/></>);
  // Routes are the source of truth for which tab is active; NavLink handles
  // its own "active" styling off the current URL. Admin tab is inserted only
  // when the caller is in admin_users — deep-linking to /admin as a non-admin
  // sends you back to home via the catch-all <Route path="*">.
  const visibleDraws=drawData.draws;
  const visiblePlays=plays.filter(p=>myGames.includes(rowGame(p)));
  const scoredVisibleDraws=visibleDraws.filter(d=>Array.isArray(d.numbers)&&d.numbers.length===gameNumberConfig(rowGame(d)).count);
  const savedNumberPlays=visiblePlays.filter(p=>Array.isArray(p.numbers)&&p.numbers.length===gameNumberConfig(rowGame(p)).count);
  const hasSavedNumbers=savedNumberPlays.length>0;
  const publicEligibility=publicEligibilityForLineup(todaysLineup,strategyTrust);
  const trackedAfterFirstSave=scoredVisibleDraws.filter(d=>
    savedNumberPlays.some(pl=>rowGame(pl)===rowGame(d)&&publicEligibility(pl,d))
  ).length;
  const hasProAccess=subscriptionHas(subscription,"pro");
  const hasPremiumAccess=subscriptionHas(subscription,"premium");
  const hasPoolAccess=subscriptionHas(subscription,"pool_organizer");
  const featureUnlocks={
    scoreboard:isAdmin,
    poolSimulation:isAdmin,
    hotCold:false,
  };
  featureUnlocks.history=featureUnlocks.poolSimulation;
  featureUnlocks.poolManager=hasPoolAccess;
  const coreTabs=[
    {path:"/",label:"DASHBOARD"},
    {path:"/results",label:"RESULTS CENTER"},
    {path:"/?view=next",label:"NEXT PICKS"},
    {path:"/my-strategies",label:"MY PLAYSLIPS"},
    {path:"/strategy-builder",label:"PLAY BUILDER"},
    {path:"/referrals",label:"REFERRALS"},
    {path:"/pro",label:hasProAccess?"PRO":"UPGRADE"},
    {path:"/profile",label:"PROFILE"},
  ];
  const proTabs=[];
  const premiumTabs=[
    {path:"/community", label:"COMMUNITY"},
    {path:"/strategy-library", label:"STRATEGY LIBRARY"},
    ...(SHOW_ANCHORS?[{path:"/anchors",label:"ANCHORS"}]:[]),
  ];
  const poolTabs=[
    {path:"/pools", label:"POOL MANAGER"},
  ];
  const advancedTabs=[
    ...coreTabs,
    ...(hasProAccess?proTabs:[]),
    ...(hasPremiumAccess?premiumTabs:[]),
    ...(hasPoolAccess?poolTabs:[]),
  ];
  const tabs=[
    {path:"/",label:"BOARD"},{path:"/strategy-scoreboard",label:"SCOREBOARD"},{path:"/quick-picks",label:"QUICK PICKS"}
  ];
  const latest=visibleDraws[0]||null;
  // `end` on the "/" NavLink prevents it from staying active on every sub-route.
  const tabClass=({isActive})=>"tab-btn"+(isActive?" active":"");
  const page=(node,defer=false)=><SafePage key={user?.id||"signed-out"} defer={defer}>{node}</SafePage>;
  return(
    <><style>{CSS}{POLISH_CSS}</style>
      <MemberHeader tabs={tabs} isAdmin={isAdmin} onFeedback={()=>setFeedbackOpen(true)} onSignOut={signOut}/>
      <main className="page">
        {false&&<div className="tabs">
          {tabs.map(t=>(
            <NavLink key={t.path} to={t.path} end={t.path==="/"} className={tabClass}><NavItemLabel label={t.label} badge={t.badge}/></NavLink>
          ))}
        </div>}
        <div className="route-fade">
          <Routes>
            <Route path="/dashboard" element={<Navigate to="/" replace/>} />
            <Route path="/board" element={<Navigate to={"/?view="+(new URLSearchParams(location.search).get("view")==="next"?"next":"last")} replace/>} />
            <Route path="/" element={page(<MemberBoard authenticatedJson={customerReader} hasProAccess={hasProAccess} onUpgrade={startCheckout} billingBusy={billingBusy}/>)} />
            <Route path="/strategy-scoreboard" element={page(<DashboardScoreboard authenticatedJson={customerReader} hasProAccess={hasProAccess}/>)} />
            <Route path="/quick-picks" element={page(<QuickPicksTab requestJson={authenticatedJson} accountId={user?.id} hasProAccess={hasProAccess} onUpgrade={()=>startCheckout("pro")} draw={rndGamePick}/>)} />
            <Route path="/todays-picks" element={<Navigate to="/?view=next" replace/>}/><Route path="/previous-picks" element={page(<TodaysPicksPage historyPage authenticatedJson={customerReader} canManagePicks={isAdmin} hasProAccess={hasProAccess} onUpgrade={goUpgrade} onFreeUpgrade={startCheckout} billingBusy={billingBusy}/>)} />
            <Route path="/pool-manager" element={<Navigate to="/pools" replace/>} />
            <Route path="/my-numbers" element={<Navigate to="/my-strategies" replace/>} />
            <Route path="/r/:referralSlug" element={<Navigate to="/" replace/>} />
            <Route path="/legacy-dashboard" element={page(hasProAccess?<LaunchDashboard authenticatedJson={customerReader} hasProAccess={hasProAccess} onUpgrade={goUpgrade}/>:<FreeCustomerExperience authenticatedJson={customerReader} onUpgrade={startCheckout} billingBusy={billingBusy}/>)} />
            <Route path="/share" element={page(<MarketingShareCenter authenticatedJson={customerReader}/>)} />
            <Route path="/marketing-share" element={isAdmin?page(<MarketingShareCenter authenticatedJson={customerReader} owner/>):<Navigate to="/share" replace/>} />
            <Route path="/results-performance" element={page(<DrawingResultsPage authenticatedJson={customerReader} hasProAccess={hasProAccess} onUpgrade={startCheckout}/>)} />
            <Route path="/results" element={page(<><a className="btn btn-s" href="/previous-picks">View All 50 Previous Picks →</a><ResultsCenterTab draws={visibleDraws} plays={visiblePlays} subscription={subscription} onUpgrade={goUpgrade} billingBusy={billingBusy} drawStatus={drawStatus} strategyTrust={strategyTrust} todaysLineup={todaysLineup} performanceSummaries={performanceSummaries} performanceLoading={performanceLoading} isAdmin={isAdmin} onCreatePublicShare={createPublicStrategyShare} onCreatePublicResultShare={createPublicResultShare}/></>,true)} />
            <Route path="/profile" element={page(<UserProfileTab user={user} onSignOut={signOut} draws={visibleDraws} plays={visiblePlays} myGames={myGames} dailyStreak={dailyStreak} featureUnlocks={featureUnlocks} strategyTrust={strategyTrust} todaysLineup={todaysLineup} subscription={subscription} performanceSummaries={performanceSummaries}/>,true)} />
            <Route path="/activity" element={isAdmin?page(<ActivityCenter notifications={activityNotifications} readIds={activityReadIds} onMarkRead={markActivityRead} onMarkAllRead={markAllActivityRead}/>):<Navigate to="/my-strategies" replace/>} />
            <Route path="/my-games" element={page(<MyGamesTab myGames={myGames} onChange={saveMyGames} planTier={planTier} trialActive={trialActive} onLimit={showFreeLimitDialog}/>)} />
            <Route path="/community" element={page(<RequirePro subscription={subscription} onUpgrade={goUpgrade} onLater={maybeLater} billingBusy={billingBusy} myGames={myGames} minPlan="premium"><CommunityTab drawStatus={drawStatus}/></RequirePro>,true)} />
            <Route path="/strategy-library" element={page(<RequirePro subscription={subscription} onUpgrade={goUpgrade} onLater={maybeLater} billingBusy={billingBusy} myGames={myGames} minPlan="premium"><StrategyLibraryTab drawStatus={drawStatus}/></RequirePro>,true)} />
            <Route path="/my-strategies" element={page(<StrategyBuilderTab plays={plays} draws={draws} drawStatus={drawStatus} strategyTrust={strategyTrust} todaysLineup={todaysLineup} scheduledDraws={scheduledDraws} performanceSummaries={performanceSummaries} onLoadHistoricalDraws={loadHistoricalDraws} historyLoading={historyLoading} historyLoaded={historyLoadedGames} onCreateStrategy={createSavedStrategy} onUpdateStrategy={updateSavedStrategy} onLockStrategy={lockStrategy} onArchiveStrategy={retireStrategy} onReplaceStrategy={createStrategyVersion} onRenameStrategy={renameStrategy} onDuplicateStrategy={duplicateStrategy} onPublishStrategy={publishStrategy} onCreatePublicShare={createPublicStrategyShare} onCreatePublicResultShare={createPublicResultShare} onRevokePublicShare={revokePublicStrategyShare} onAddToLineup={addToLineup} onRemoveFromLineup={removeFromLineup} onDeleteStrategy={deleteStrategy} onUpgrade={goUpgrade} onLimit={showFreeLimitDialog} billingBusy={billingBusy} showAdvancedTools={hasProAccess} libraryOnly={true} maxPreviewPlays={STRATEGY_MAX_PLAYS} myGames={myGames} planTier={planTier} trialActive={trialActive} user={user}/>,true)} />
            <Route path="/strategy-builder" element={page(<StrategyBuilderTab plays={plays} draws={draws} drawStatus={drawStatus} strategyTrust={strategyTrust} todaysLineup={todaysLineup} scheduledDraws={scheduledDraws} performanceSummaries={performanceSummaries} onLoadHistoricalDraws={loadHistoricalDraws} historyLoading={historyLoading} historyLoaded={historyLoadedGames} onCreateStrategy={createSavedStrategy} onUpdateStrategy={updateSavedStrategy} onLockStrategy={lockStrategy} onArchiveStrategy={retireStrategy} onReplaceStrategy={createStrategyVersion} onRenameStrategy={renameStrategy} onDuplicateStrategy={duplicateStrategy} onPublishStrategy={publishStrategy} onCreatePublicShare={createPublicStrategyShare} onCreatePublicResultShare={createPublicResultShare} onRevokePublicShare={revokePublicStrategyShare} onAddToLineup={addToLineup} onRemoveFromLineup={removeFromLineup} onDeleteStrategy={deleteStrategy} onUpgrade={goUpgrade} onLimit={showFreeLimitDialog} billingBusy={billingBusy} showAdvancedTools={hasProAccess} maxPreviewPlays={STRATEGY_MAX_PLAYS} myGames={myGames} planTier={planTier} trialActive={trialActive} user={user}/>)} />
            <Route path="/lineup" element={<Navigate to="/my-strategies" replace/>} />
            <Route path="/referrals" element={page(<ReferralTab user={user} subscription={subscription} onReferralUpdated={applyReferralProfile}/>)} />
            <Route path="/settings" element={page(<SettingsTab user={user} subscription={subscription} myGames={myGames} featureUnlocks={featureUnlocks} onRefresh={refreshSubscription} billingBusy={billingBusy} onSignOut={signOut} onFeedback={()=>setFeedbackOpen(true)} onReferralUpdated={applyReferralProfile}/>)} />
            <Route path="/pro" element={page(<ProTab subscription={subscription} onCheckout={startCheckout} onPortal={openBillingPortal} onCancel={cancelSubscription} onRestore={restoreSubscription} onRefresh={refreshSubscription} billingBusy={billingBusy}/>)} />
            <Route path="/anchors"    element={page(<RequirePro subscription={subscription} onUpgrade={goUpgrade} onLater={maybeLater} billingBusy={billingBusy} myGames={myGames} minPlan="premium"><StratsTab draws={visibleDraws} strats={strats} plays={plays} onSave={saveStrat} onPersistAnchors={persistAnchorSet} onDeleteAnchor={deletePlay} strategyTrust={strategyTrust} drawStatus={drawStatus} onLockStrategy={lockStrategy}/></RequirePro>)} />
            <Route path="/scoreboard" element={isAdmin?page(<ScoreboardTab draws={visibleDraws} plays={visiblePlays} strategyTrust={strategyTrust} drawStatus={drawStatus} todaysLineup={todaysLineup} onLockStrategy={lockStrategy} onArchiveStrategy={retireStrategy} onReplaceStrategy={createStrategyVersion}/>):<Navigate to="/my-strategies" replace/>} />
            <Route path="/pool-simulation" element={isAdmin?page(<PoolSimulationTab draws={visibleDraws} plays={visiblePlays}/>):<Navigate to="/my-strategies" replace/>} />
            <Route path="/pools" element={page(<RequirePro subscription={subscription} onUpgrade={goUpgrade} onLater={maybeLater} billingBusy={billingBusy} myGames={myGames} minPlan="pool_organizer"><PoolManagerTab draws={visibleDraws} plays={visiblePlays} drawStatus={drawStatus} strategyTrust={strategyTrust}/></RequirePro>,true)} />
            <Route path="/hot-cold"   element={<Navigate to="/my-strategies" replace/>} />
            <Route path="/history"    element={isAdmin?page(<HistoryTab draws={visibleDraws} plays={visiblePlays} drawStatus={drawStatus} todaysLineup={todaysLineup} strategyTrust={strategyTrust}/>,true):<Navigate to="/my-strategies" replace/>} />
            {isAdmin && <Route path="/owner-users" element={page(<OwnerUsersTab/>,true)} />}
            {isAdmin && <Route path="/admin/florida-pilot" element={page(<FloridaPilotViewer authenticatedJson={authenticatedJson}/>)} />}
            {isAdmin && <Route path="/admin-tools" element={page(<AdminToolsTab user={user} isAdmin={isAdmin}/>,true)} />}
            {isAdmin && <Route path="/launch-monitor" element={page(<LaunchMonitorTab strategyTrust={strategyTrust} todaysLineup={todaysLineup}/>)} />}
            {isAdmin && <Route path="/launch-readiness" element={page(<LaunchReadinessTab user={user} draws={draws} drawsLoading={drawsLoading} drawStatus={drawStatus} activityNotifications={activityNotifications}/>)} />}
            {isAdmin && <Route path="/admin" element={page(<AdminTab draws={draws} onSaved={()=>fetchDraws()} toast={toast}/>)} />}
            <Route path="*" element={<Navigate to="/" replace/>} />
          </Routes>
        </div>
        <footer style={{marginTop:28,borderTop:"1px solid #e4ded2",paddingTop:16,display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"center",fontSize:12,color:"#555"}}>
          <span>LottoTracker is in soft launch. Your feedback helps us improve the app.</span>
          <button type="button" className="btn btn-sm btn-s" onClick={()=>setFeedbackOpen(true)}>Feedback / Contact</button>
        </footer>
      </main>
      <FreeLimitUpgradeDialog
        open={!!freeLimitDialog}
        reason={freeLimitDialog}
        onUpgrade={startTrialFromLimitDialog}
        onClose={closeFreeLimitDialog}
        selectedPickGame={freePickGame(myGames)}
      />
      <Toast msg={toastMsg}/>
      <VersionRefreshGuard/>
      {feedbackOpen&&<FeedbackModal user={user} onClose={()=>setFeedbackOpen(false)}/>}
      <FirstWinExperience
        open={firstWinOpen}
        myGames={myGames}
        drawStatus={drawStatus}
        onDashboard={()=>closeFirstWin("/")}
        onAddMore={()=>closeFirstWin("/my-strategies")}
      />
    </>
  );
}







